/*!
 * Vue.js v2.6.12
 * (c) 2014-2020 Evan You
 * Released under the MIT License.
 */
/*  */

var emptyObject = Object.freeze({});

// These helpers produce better VM code in JS engines due to their
// explicitness and function inlining.
function isUndef (v) {
  return v === undefined || v === null
}

function isDef (v) {
  return v !== undefined && v !== null
}

function isTrue (v) {
  return v === true
}

function isFalse (v) {
  return v === false
}

/**
 * Check if value is primitive.
 */
function isPrimitive$1 (value) {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    // $flow-disable-line
    typeof value === 'symbol' ||
    typeof value === 'boolean'
  )
}

/**
 * Quick object check - this is primarily used to tell
 * Objects from primitive values when we know the value
 * is a JSON-compliant type.
 */
function isObject (obj) {
  return obj !== null && typeof obj === 'object'
}

/**
 * Get the raw type string of a value, e.g., [object Object].
 */
var _toString = Object.prototype.toString;

function toRawType (value) {
  return _toString.call(value).slice(8, -1)
}

/**
 * Strict object type check. Only returns true
 * for plain JavaScript objects.
 */
function isPlainObject (obj) {
  return _toString.call(obj) === '[object Object]'
}

function isRegExp (v) {
  return _toString.call(v) === '[object RegExp]'
}

/**
 * Check if val is a valid array index.
 */
function isValidArrayIndex (val) {
  var n = parseFloat(String(val));
  return n >= 0 && Math.floor(n) === n && isFinite(val)
}

function isPromise (val) {
  return (
    isDef(val) &&
    typeof val.then === 'function' &&
    typeof val.catch === 'function'
  )
}

/**
 * Convert a value to a string that is actually rendered.
 */
function toString (val) {
  return val == null
    ? ''
    : Array.isArray(val) || (isPlainObject(val) && val.toString === _toString)
      ? JSON.stringify(val, null, 2)
      : String(val)
}

/**
 * Convert an input value to a number for persistence.
 * If the conversion fails, return original string.
 */
function toNumber (val) {
  var n = parseFloat(val);
  return isNaN(n) ? val : n
}

/**
 * Make a map and return a function for checking if a key
 * is in that map.
 */
function makeMap (
  str,
  expectsLowerCase
) {
  var map = Object.create(null);
  var list = str.split(',');
  for (var i = 0; i < list.length; i++) {
    map[list[i]] = true;
  }
  return expectsLowerCase
    ? function (val) { return map[val.toLowerCase()]; }
    : function (val) { return map[val]; }
}

/**
 * Check if a tag is a built-in tag.
 */
makeMap('slot,component', true);

/**
 * Check if an attribute is a reserved attribute.
 */
var isReservedAttribute = makeMap('key,ref,slot,slot-scope,is');

/**
 * Remove an item from an array.
 */
function remove (arr, item) {
  if (arr.length) {
    var index = arr.indexOf(item);
    if (index > -1) {
      return arr.splice(index, 1)
    }
  }
}

/**
 * Check whether an object has the property.
 */
var hasOwnProperty = Object.prototype.hasOwnProperty;
function hasOwn (obj, key) {
  return hasOwnProperty.call(obj, key)
}

/**
 * Create a cached version of a pure function.
 */
function cached (fn) {
  var cache = Object.create(null);
  return (function cachedFn (str) {
    var hit = cache[str];
    return hit || (cache[str] = fn(str))
  })
}

/**
 * Camelize a hyphen-delimited string.
 */
var camelizeRE = /-(\w)/g;
var camelize = cached(function (str) {
  return str.replace(camelizeRE, function (_, c) { return c ? c.toUpperCase() : ''; })
});

/**
 * Capitalize a string.
 */
var capitalize = cached(function (str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
});

/**
 * Hyphenate a camelCase string.
 */
var hyphenateRE = /\B([A-Z])/g;
var hyphenate = cached(function (str) {
  return str.replace(hyphenateRE, '-$1').toLowerCase()
});

/**
 * Simple bind polyfill for environments that do not support it,
 * e.g., PhantomJS 1.x. Technically, we don't need this anymore
 * since native bind is now performant enough in most browsers.
 * But removing it would mean breaking code that was able to run in
 * PhantomJS 1.x, so this must be kept for backward compatibility.
 */

/* istanbul ignore next */
function polyfillBind (fn, ctx) {
  function boundFn (a) {
    var l = arguments.length;
    return l
      ? l > 1
        ? fn.apply(ctx, arguments)
        : fn.call(ctx, a)
      : fn.call(ctx)
  }

  boundFn._length = fn.length;
  return boundFn
}

function nativeBind (fn, ctx) {
  return fn.bind(ctx)
}

var bind = Function.prototype.bind
  ? nativeBind
  : polyfillBind;

/**
 * Convert an Array-like object to a real Array.
 */
function toArray (list, start) {
  start = start || 0;
  var i = list.length - start;
  var ret = new Array(i);
  while (i--) {
    ret[i] = list[i + start];
  }
  return ret
}

/**
 * Mix properties into target object.
 */
function extend (to, _from) {
  for (var key in _from) {
    to[key] = _from[key];
  }
  return to
}

/**
 * Merge an Array of Objects into a single Object.
 */
function toObject (arr) {
  var res = {};
  for (var i = 0; i < arr.length; i++) {
    if (arr[i]) {
      extend(res, arr[i]);
    }
  }
  return res
}

/* eslint-disable no-unused-vars */

/**
 * Perform no operation.
 * Stubbing args to make Flow happy without leaving useless transpiled code
 * with ...rest (https://flow.org/blog/2017/05/07/Strict-Function-Call-Arity/).
 */
function noop (a, b, c) {}

/**
 * Always return false.
 */
var no = function (a, b, c) { return false; };

/* eslint-enable no-unused-vars */

/**
 * Return the same value.
 */
var identity = function (_) { return _; };

/**
 * Check if two values are loosely equal - that is,
 * if they are plain objects, do they have the same shape?
 */
function looseEqual (a, b) {
  if (a === b) { return true }
  var isObjectA = isObject(a);
  var isObjectB = isObject(b);
  if (isObjectA && isObjectB) {
    try {
      var isArrayA = Array.isArray(a);
      var isArrayB = Array.isArray(b);
      if (isArrayA && isArrayB) {
        return a.length === b.length && a.every(function (e, i) {
          return looseEqual(e, b[i])
        })
      } else if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime()
      } else if (!isArrayA && !isArrayB) {
        var keysA = Object.keys(a);
        var keysB = Object.keys(b);
        return keysA.length === keysB.length && keysA.every(function (key) {
          return looseEqual(a[key], b[key])
        })
      } else {
        /* istanbul ignore next */
        return false
      }
    } catch (e) {
      /* istanbul ignore next */
      return false
    }
  } else if (!isObjectA && !isObjectB) {
    return String(a) === String(b)
  } else {
    return false
  }
}

/**
 * Return the first index at which a loosely equal value can be
 * found in the array (if value is a plain object, the array must
 * contain an object of the same shape), or -1 if it is not present.
 */
function looseIndexOf (arr, val) {
  for (var i = 0; i < arr.length; i++) {
    if (looseEqual(arr[i], val)) { return i }
  }
  return -1
}

/**
 * Ensure a function is called only once.
 */
function once (fn) {
  var called = false;
  return function () {
    if (!called) {
      called = true;
      fn.apply(this, arguments);
    }
  }
}

var SSR_ATTR = 'data-server-rendered';

var ASSET_TYPES = [
  'component',
  'directive',
  'filter'
];

var LIFECYCLE_HOOKS = [
  'beforeCreate',
  'created',
  'beforeMount',
  'mounted',
  'beforeUpdate',
  'updated',
  'beforeDestroy',
  'destroyed',
  'activated',
  'deactivated',
  'errorCaptured',
  'serverPrefetch'
];

/*  */



var config = ({
  /**
   * Option merge strategies (used in core/util/options)
   */
  // $flow-disable-line
  optionMergeStrategies: Object.create(null),

  /**
   * Whether to suppress warnings.
   */
  silent: false,

  /**
   * Show production mode tip message on boot?
   */
  productionTip: "production" !== 'production',

  /**
   * Whether to enable devtools
   */
  devtools: "production" !== 'production',

  /**
   * Whether to record perf
   */
  performance: false,

  /**
   * Error handler for watcher errors
   */
  errorHandler: null,

  /**
   * Warn handler for watcher warns
   */
  warnHandler: null,

  /**
   * Ignore certain custom elements
   */
  ignoredElements: [],

  /**
   * Custom user key aliases for v-on
   */
  // $flow-disable-line
  keyCodes: Object.create(null),

  /**
   * Check if a tag is reserved so that it cannot be registered as a
   * component. This is platform-dependent and may be overwritten.
   */
  isReservedTag: no,

  /**
   * Check if an attribute is reserved so that it cannot be used as a component
   * prop. This is platform-dependent and may be overwritten.
   */
  isReservedAttr: no,

  /**
   * Check if a tag is an unknown element.
   * Platform-dependent.
   */
  isUnknownElement: no,

  /**
   * Get the namespace of an element
   */
  getTagNamespace: noop,

  /**
   * Parse the real tag name for the specific platform.
   */
  parsePlatformTagName: identity,

  /**
   * Check if an attribute must be bound using property, e.g. value
   * Platform-dependent.
   */
  mustUseProp: no,

  /**
   * Perform updates asynchronously. Intended to be used by Vue Test Utils
   * This will significantly reduce performance if set to false.
   */
  async: true,

  /**
   * Exposed for legacy reasons
   */
  _lifecycleHooks: LIFECYCLE_HOOKS
});

/*  */

/**
 * unicode letters used for parsing html tags, component names and property paths.
 * using https://www.w3.org/TR/html53/semantics-scripting.html#potentialcustomelementname
 * skipping \u10000-\uEFFFF due to it freezing up PhantomJS
 */
var unicodeRegExp = /a-zA-Z\u00B7\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u037D\u037F-\u1FFF\u200C-\u200D\u203F-\u2040\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD/;

/**
 * Check if a string starts with $ or _
 */
function isReserved (str) {
  var c = (str + '').charCodeAt(0);
  return c === 0x24 || c === 0x5F
}

/**
 * Define a property.
 */
function def (obj, key, val, enumerable) {
  Object.defineProperty(obj, key, {
    value: val,
    enumerable: !!enumerable,
    writable: true,
    configurable: true
  });
}

/**
 * Parse simple path.
 */
var bailRE = new RegExp(("[^" + (unicodeRegExp.source) + ".$_\\d]"));
function parsePath (path) {
  if (bailRE.test(path)) {
    return
  }
  var segments = path.split('.');
  return function (obj) {
    for (var i = 0; i < segments.length; i++) {
      if (!obj) { return }
      obj = obj[segments[i]];
    }
    return obj
  }
}

/*  */

// can we use __proto__?
var hasProto$1 = '__proto__' in {};

// Browser environment sniffing
var inBrowser = typeof window !== 'undefined';
var inWeex = typeof WXEnvironment !== 'undefined' && !!WXEnvironment.platform;
var weexPlatform = inWeex && WXEnvironment.platform.toLowerCase();
var UA = inBrowser && window.navigator.userAgent.toLowerCase();
var isIE = UA && /msie|trident/.test(UA);
var isIE9 = UA && UA.indexOf('msie 9.0') > 0;
var isEdge = UA && UA.indexOf('edge/') > 0;
(UA && UA.indexOf('android') > 0) || (weexPlatform === 'android');
var isIOS = (UA && /iphone|ipad|ipod|ios/.test(UA)) || (weexPlatform === 'ios');
UA && /chrome\/\d+/.test(UA) && !isEdge;
UA && /phantomjs/.test(UA);
var isFF = UA && UA.match(/firefox\/(\d+)/);

// Firefox has a "watch" function on Object.prototype...
var nativeWatch = ({}).watch;

var supportsPassive = false;
if (inBrowser) {
  try {
    var opts = {};
    Object.defineProperty(opts, 'passive', ({
      get: function get () {
        /* istanbul ignore next */
        supportsPassive = true;
      }
    })); // https://github.com/facebook/flow/issues/285
    window.addEventListener('test-passive', null, opts);
  } catch (e) {}
}

// this needs to be lazy-evaled because vue may be required before
// vue-server-renderer can set VUE_ENV
var _isServer;
var isServerRendering = function () {
  if (_isServer === undefined) {
    /* istanbul ignore if */
    if (!inBrowser && !inWeex && typeof global !== 'undefined') {
      // detect presence of vue-server-renderer and avoid
      // Webpack shimming the process
      _isServer = global['process'] && global['process'].env.VUE_ENV === 'server';
    } else {
      _isServer = false;
    }
  }
  return _isServer
};

// detect devtools
var devtools = inBrowser && window.__VUE_DEVTOOLS_GLOBAL_HOOK__;

/* istanbul ignore next */
function isNative (Ctor) {
  return typeof Ctor === 'function' && /native code/.test(Ctor.toString())
}

var hasSymbol =
  typeof Symbol !== 'undefined' && isNative(Symbol) &&
  typeof Reflect !== 'undefined' && isNative(Reflect.ownKeys);

var _Set;
/* istanbul ignore if */ // $flow-disable-line
if (typeof Set !== 'undefined' && isNative(Set)) {
  // use native Set when available.
  _Set = Set;
} else {
  // a non-standard Set polyfill that only works with primitive keys.
  _Set = /*@__PURE__*/(function () {
    function Set () {
      this.set = Object.create(null);
    }
    Set.prototype.has = function has (key) {
      return this.set[key] === true
    };
    Set.prototype.add = function add (key) {
      this.set[key] = true;
    };
    Set.prototype.clear = function clear () {
      this.set = Object.create(null);
    };

    return Set;
  }());
}

/*  */

var warn = noop;

/*  */

var uid = 0;

/**
 * A dep is an observable that can have multiple
 * directives subscribing to it.
 */
var Dep = function Dep () {
  this.id = uid++;
  this.subs = [];
};

Dep.prototype.addSub = function addSub (sub) {
  this.subs.push(sub);
};

Dep.prototype.removeSub = function removeSub (sub) {
  remove(this.subs, sub);
};

Dep.prototype.depend = function depend () {
  if (Dep.target) {
    Dep.target.addDep(this);
  }
};

Dep.prototype.notify = function notify () {
  // stabilize the subscriber list first
  var subs = this.subs.slice();
  for (var i = 0, l = subs.length; i < l; i++) {
    subs[i].update();
  }
};

// The current target watcher being evaluated.
// This is globally unique because only one watcher
// can be evaluated at a time.
Dep.target = null;
var targetStack = [];

function pushTarget (target) {
  targetStack.push(target);
  Dep.target = target;
}

function popTarget () {
  targetStack.pop();
  Dep.target = targetStack[targetStack.length - 1];
}

/*  */

var VNode = function VNode (
  tag,
  data,
  children,
  text,
  elm,
  context,
  componentOptions,
  asyncFactory
) {
  this.tag = tag;
  this.data = data;
  this.children = children;
  this.text = text;
  this.elm = elm;
  this.ns = undefined;
  this.context = context;
  this.fnContext = undefined;
  this.fnOptions = undefined;
  this.fnScopeId = undefined;
  this.key = data && data.key;
  this.componentOptions = componentOptions;
  this.componentInstance = undefined;
  this.parent = undefined;
  this.raw = false;
  this.isStatic = false;
  this.isRootInsert = true;
  this.isComment = false;
  this.isCloned = false;
  this.isOnce = false;
  this.asyncFactory = asyncFactory;
  this.asyncMeta = undefined;
  this.isAsyncPlaceholder = false;
};

var prototypeAccessors = { child: { configurable: true } };

// DEPRECATED: alias for componentInstance for backwards compat.
/* istanbul ignore next */
prototypeAccessors.child.get = function () {
  return this.componentInstance
};

Object.defineProperties( VNode.prototype, prototypeAccessors );

var createEmptyVNode = function (text) {
  if ( text === void 0 ) text = '';

  var node = new VNode();
  node.text = text;
  node.isComment = true;
  return node
};

function createTextVNode (val) {
  return new VNode(undefined, undefined, undefined, String(val))
}

// optimized shallow clone
// used for static nodes and slot nodes because they may be reused across
// multiple renders, cloning them avoids errors when DOM manipulations rely
// on their elm reference.
function cloneVNode (vnode) {
  var cloned = new VNode(
    vnode.tag,
    vnode.data,
    // #7975
    // clone children array to avoid mutating original in case of cloning
    // a child.
    vnode.children && vnode.children.slice(),
    vnode.text,
    vnode.elm,
    vnode.context,
    vnode.componentOptions,
    vnode.asyncFactory
  );
  cloned.ns = vnode.ns;
  cloned.isStatic = vnode.isStatic;
  cloned.key = vnode.key;
  cloned.isComment = vnode.isComment;
  cloned.fnContext = vnode.fnContext;
  cloned.fnOptions = vnode.fnOptions;
  cloned.fnScopeId = vnode.fnScopeId;
  cloned.asyncMeta = vnode.asyncMeta;
  cloned.isCloned = true;
  return cloned
}

/*
 * not type checking this file because flow doesn't play well with
 * dynamically accessing methods on Array prototype
 */

var arrayProto = Array.prototype;
var arrayMethods = Object.create(arrayProto);

var methodsToPatch = [
  'push',
  'pop',
  'shift',
  'unshift',
  'splice',
  'sort',
  'reverse'
];

/**
 * Intercept mutating methods and emit events
 */
methodsToPatch.forEach(function (method) {
  // cache original method
  var original = arrayProto[method];
  def(arrayMethods, method, function mutator () {
    var args = [], len = arguments.length;
    while ( len-- ) args[ len ] = arguments[ len ];

    var result = original.apply(this, args);
    var ob = this.__ob__;
    var inserted;
    switch (method) {
      case 'push':
      case 'unshift':
        inserted = args;
        break
      case 'splice':
        inserted = args.slice(2);
        break
    }
    if (inserted) { ob.observeArray(inserted); }
    // notify change
    ob.dep.notify();
    return result
  });
});

/*  */

var arrayKeys = Object.getOwnPropertyNames(arrayMethods);

/**
 * In some cases we may want to disable observation inside a component's
 * update computation.
 */
var shouldObserve = true;

function toggleObserving (value) {
  shouldObserve = value;
}

/**
 * Observer class that is attached to each observed
 * object. Once attached, the observer converts the target
 * object's property keys into getter/setters that
 * collect dependencies and dispatch updates.
 */
var Observer = function Observer (value) {
  this.value = value;
  this.dep = new Dep();
  this.vmCount = 0;
  def(value, '__ob__', this);
  if (Array.isArray(value)) {
    if (hasProto$1) {
      protoAugment(value, arrayMethods);
    } else {
      copyAugment(value, arrayMethods, arrayKeys);
    }
    this.observeArray(value);
  } else {
    this.walk(value);
  }
};

/**
 * Walk through all properties and convert them into
 * getter/setters. This method should only be called when
 * value type is Object.
 */
Observer.prototype.walk = function walk (obj) {
  var keys = Object.keys(obj);
  for (var i = 0; i < keys.length; i++) {
    defineReactive$$1(obj, keys[i]);
  }
};

/**
 * Observe a list of Array items.
 */
Observer.prototype.observeArray = function observeArray (items) {
  for (var i = 0, l = items.length; i < l; i++) {
    observe(items[i]);
  }
};

// helpers

/**
 * Augment a target Object or Array by intercepting
 * the prototype chain using __proto__
 */
function protoAugment (target, src) {
  /* eslint-disable no-proto */
  target.__proto__ = src;
  /* eslint-enable no-proto */
}

/**
 * Augment a target Object or Array by defining
 * hidden properties.
 */
/* istanbul ignore next */
function copyAugment (target, src, keys) {
  for (var i = 0, l = keys.length; i < l; i++) {
    var key = keys[i];
    def(target, key, src[key]);
  }
}

/**
 * Attempt to create an observer instance for a value,
 * returns the new observer if successfully observed,
 * or the existing observer if the value already has one.
 */
function observe (value, asRootData) {
  if (!isObject(value) || value instanceof VNode) {
    return
  }
  var ob;
  if (hasOwn(value, '__ob__') && value.__ob__ instanceof Observer) {
    ob = value.__ob__;
  } else if (
    shouldObserve &&
    !isServerRendering() &&
    (Array.isArray(value) || isPlainObject(value)) &&
    Object.isExtensible(value) &&
    !value._isVue
  ) {
    ob = new Observer(value);
  }
  if (asRootData && ob) {
    ob.vmCount++;
  }
  return ob
}

/**
 * Define a reactive property on an Object.
 */
function defineReactive$$1 (
  obj,
  key,
  val,
  customSetter,
  shallow
) {
  var dep = new Dep();

  var property = Object.getOwnPropertyDescriptor(obj, key);
  if (property && property.configurable === false) {
    return
  }

  // cater for pre-defined getter/setters
  var getter = property && property.get;
  var setter = property && property.set;
  if ((!getter || setter) && arguments.length === 2) {
    val = obj[key];
  }

  var childOb = !shallow && observe(val);
  Object.defineProperty(obj, key, {
    enumerable: true,
    configurable: true,
    get: function reactiveGetter () {
      var value = getter ? getter.call(obj) : val;
      if (Dep.target) {
        dep.depend();
        if (childOb) {
          childOb.dep.depend();
          if (Array.isArray(value)) {
            dependArray(value);
          }
        }
      }
      return value
    },
    set: function reactiveSetter (newVal) {
      var value = getter ? getter.call(obj) : val;
      /* eslint-disable no-self-compare */
      if (newVal === value || (newVal !== newVal && value !== value)) {
        return
      }
      // #7981: for accessor properties without setter
      if (getter && !setter) { return }
      if (setter) {
        setter.call(obj, newVal);
      } else {
        val = newVal;
      }
      childOb = !shallow && observe(newVal);
      dep.notify();
    }
  });
}

/**
 * Set a property on an object. Adds the new property and
 * triggers change notification if the property doesn't
 * already exist.
 */
function set (target, key, val) {
  if (Array.isArray(target) && isValidArrayIndex(key)) {
    target.length = Math.max(target.length, key);
    target.splice(key, 1, val);
    return val
  }
  if (key in target && !(key in Object.prototype)) {
    target[key] = val;
    return val
  }
  var ob = (target).__ob__;
  if (target._isVue || (ob && ob.vmCount)) {
    return val
  }
  if (!ob) {
    target[key] = val;
    return val
  }
  defineReactive$$1(ob.value, key, val);
  ob.dep.notify();
  return val
}

/**
 * Delete a property and trigger change if necessary.
 */
function del (target, key) {
  if (Array.isArray(target) && isValidArrayIndex(key)) {
    target.splice(key, 1);
    return
  }
  var ob = (target).__ob__;
  if (target._isVue || (ob && ob.vmCount)) {
    return
  }
  if (!hasOwn(target, key)) {
    return
  }
  delete target[key];
  if (!ob) {
    return
  }
  ob.dep.notify();
}

/**
 * Collect dependencies on array elements when the array is touched, since
 * we cannot intercept array element access like property getters.
 */
function dependArray (value) {
  for (var e = (void 0), i = 0, l = value.length; i < l; i++) {
    e = value[i];
    e && e.__ob__ && e.__ob__.dep.depend();
    if (Array.isArray(e)) {
      dependArray(e);
    }
  }
}

/*  */

/**
 * Option overwriting strategies are functions that handle
 * how to merge a parent option value and a child option
 * value into the final value.
 */
var strats = config.optionMergeStrategies;

/**
 * Helper that recursively merges two data objects together.
 */
function mergeData (to, from) {
  if (!from) { return to }
  var key, toVal, fromVal;

  var keys = hasSymbol
    ? Reflect.ownKeys(from)
    : Object.keys(from);

  for (var i = 0; i < keys.length; i++) {
    key = keys[i];
    // in case the object is already observed...
    if (key === '__ob__') { continue }
    toVal = to[key];
    fromVal = from[key];
    if (!hasOwn(to, key)) {
      set(to, key, fromVal);
    } else if (
      toVal !== fromVal &&
      isPlainObject(toVal) &&
      isPlainObject(fromVal)
    ) {
      mergeData(toVal, fromVal);
    }
  }
  return to
}

/**
 * Data
 */
function mergeDataOrFn (
  parentVal,
  childVal,
  vm
) {
  if (!vm) {
    // in a Vue.extend merge, both should be functions
    if (!childVal) {
      return parentVal
    }
    if (!parentVal) {
      return childVal
    }
    // when parentVal & childVal are both present,
    // we need to return a function that returns the
    // merged result of both functions... no need to
    // check if parentVal is a function here because
    // it has to be a function to pass previous merges.
    return function mergedDataFn () {
      return mergeData(
        typeof childVal === 'function' ? childVal.call(this, this) : childVal,
        typeof parentVal === 'function' ? parentVal.call(this, this) : parentVal
      )
    }
  } else {
    return function mergedInstanceDataFn () {
      // instance merge
      var instanceData = typeof childVal === 'function'
        ? childVal.call(vm, vm)
        : childVal;
      var defaultData = typeof parentVal === 'function'
        ? parentVal.call(vm, vm)
        : parentVal;
      if (instanceData) {
        return mergeData(instanceData, defaultData)
      } else {
        return defaultData
      }
    }
  }
}

strats.data = function (
  parentVal,
  childVal,
  vm
) {
  if (!vm) {
    if (childVal && typeof childVal !== 'function') {

      return parentVal
    }
    return mergeDataOrFn(parentVal, childVal)
  }

  return mergeDataOrFn(parentVal, childVal, vm)
};

/**
 * Hooks and props are merged as arrays.
 */
function mergeHook (
  parentVal,
  childVal
) {
  var res = childVal
    ? parentVal
      ? parentVal.concat(childVal)
      : Array.isArray(childVal)
        ? childVal
        : [childVal]
    : parentVal;
  return res
    ? dedupeHooks(res)
    : res
}

function dedupeHooks (hooks) {
  var res = [];
  for (var i = 0; i < hooks.length; i++) {
    if (res.indexOf(hooks[i]) === -1) {
      res.push(hooks[i]);
    }
  }
  return res
}

LIFECYCLE_HOOKS.forEach(function (hook) {
  strats[hook] = mergeHook;
});

/**
 * Assets
 *
 * When a vm is present (instance creation), we need to do
 * a three-way merge between constructor options, instance
 * options and parent options.
 */
function mergeAssets (
  parentVal,
  childVal,
  vm,
  key
) {
  var res = Object.create(parentVal || null);
  if (childVal) {
    return extend(res, childVal)
  } else {
    return res
  }
}

ASSET_TYPES.forEach(function (type) {
  strats[type + 's'] = mergeAssets;
});

/**
 * Watchers.
 *
 * Watchers hashes should not overwrite one
 * another, so we merge them as arrays.
 */
strats.watch = function (
  parentVal,
  childVal,
  vm,
  key
) {
  // work around Firefox's Object.prototype.watch...
  if (parentVal === nativeWatch) { parentVal = undefined; }
  if (childVal === nativeWatch) { childVal = undefined; }
  /* istanbul ignore if */
  if (!childVal) { return Object.create(parentVal || null) }
  if (!parentVal) { return childVal }
  var ret = {};
  extend(ret, parentVal);
  for (var key$1 in childVal) {
    var parent = ret[key$1];
    var child = childVal[key$1];
    if (parent && !Array.isArray(parent)) {
      parent = [parent];
    }
    ret[key$1] = parent
      ? parent.concat(child)
      : Array.isArray(child) ? child : [child];
  }
  return ret
};

/**
 * Other object hashes.
 */
strats.props =
strats.methods =
strats.inject =
strats.computed = function (
  parentVal,
  childVal,
  vm,
  key
) {
  if (childVal && "production" !== 'production') {
    assertObjectType(key, childVal);
  }
  if (!parentVal) { return childVal }
  var ret = Object.create(null);
  extend(ret, parentVal);
  if (childVal) { extend(ret, childVal); }
  return ret
};
strats.provide = mergeDataOrFn;

/**
 * Default strategy.
 */
var defaultStrat = function (parentVal, childVal) {
  return childVal === undefined
    ? parentVal
    : childVal
};

/**
 * Ensure all props option syntax are normalized into the
 * Object-based format.
 */
function normalizeProps (options, vm) {
  var props = options.props;
  if (!props) { return }
  var res = {};
  var i, val, name;
  if (Array.isArray(props)) {
    i = props.length;
    while (i--) {
      val = props[i];
      if (typeof val === 'string') {
        name = camelize(val);
        res[name] = { type: null };
      }
    }
  } else if (isPlainObject(props)) {
    for (var key in props) {
      val = props[key];
      name = camelize(key);
      res[name] = isPlainObject(val)
        ? val
        : { type: val };
    }
  } else ;
  options.props = res;
}

/**
 * Normalize all injections into Object-based format
 */
function normalizeInject (options, vm) {
  var inject = options.inject;
  if (!inject) { return }
  var normalized = options.inject = {};
  if (Array.isArray(inject)) {
    for (var i = 0; i < inject.length; i++) {
      normalized[inject[i]] = { from: inject[i] };
    }
  } else if (isPlainObject(inject)) {
    for (var key in inject) {
      var val = inject[key];
      normalized[key] = isPlainObject(val)
        ? extend({ from: key }, val)
        : { from: val };
    }
  } else ;
}

/**
 * Normalize raw function directives into object format.
 */
function normalizeDirectives (options) {
  var dirs = options.directives;
  if (dirs) {
    for (var key in dirs) {
      var def$$1 = dirs[key];
      if (typeof def$$1 === 'function') {
        dirs[key] = { bind: def$$1, update: def$$1 };
      }
    }
  }
}

function assertObjectType (name, value, vm) {
  if (!isPlainObject(value)) {
    warn(
      "Invalid value for option \"" + name + "\": expected an Object, " +
      "but got " + (toRawType(value)) + ".");
  }
}

/**
 * Merge two option objects into a new one.
 * Core utility used in both instantiation and inheritance.
 */
function mergeOptions (
  parent,
  child,
  vm
) {

  if (typeof child === 'function') {
    child = child.options;
  }

  normalizeProps(child);
  normalizeInject(child);
  normalizeDirectives(child);

  // Apply extends and mixins on the child options,
  // but only if it is a raw options object that isn't
  // the result of another mergeOptions call.
  // Only merged options has the _base property.
  if (!child._base) {
    if (child.extends) {
      parent = mergeOptions(parent, child.extends, vm);
    }
    if (child.mixins) {
      for (var i = 0, l = child.mixins.length; i < l; i++) {
        parent = mergeOptions(parent, child.mixins[i], vm);
      }
    }
  }

  var options = {};
  var key;
  for (key in parent) {
    mergeField(key);
  }
  for (key in child) {
    if (!hasOwn(parent, key)) {
      mergeField(key);
    }
  }
  function mergeField (key) {
    var strat = strats[key] || defaultStrat;
    options[key] = strat(parent[key], child[key], vm, key);
  }
  return options
}

/**
 * Resolve an asset.
 * This function is used because child instances need access
 * to assets defined in its ancestor chain.
 */
function resolveAsset (
  options,
  type,
  id,
  warnMissing
) {
  /* istanbul ignore if */
  if (typeof id !== 'string') {
    return
  }
  var assets = options[type];
  // check local registration variations first
  if (hasOwn(assets, id)) { return assets[id] }
  var camelizedId = camelize(id);
  if (hasOwn(assets, camelizedId)) { return assets[camelizedId] }
  var PascalCaseId = capitalize(camelizedId);
  if (hasOwn(assets, PascalCaseId)) { return assets[PascalCaseId] }
  // fallback to prototype chain
  var res = assets[id] || assets[camelizedId] || assets[PascalCaseId];
  return res
}

/*  */



function validateProp (
  key,
  propOptions,
  propsData,
  vm
) {
  var prop = propOptions[key];
  var absent = !hasOwn(propsData, key);
  var value = propsData[key];
  // boolean casting
  var booleanIndex = getTypeIndex(Boolean, prop.type);
  if (booleanIndex > -1) {
    if (absent && !hasOwn(prop, 'default')) {
      value = false;
    } else if (value === '' || value === hyphenate(key)) {
      // only cast empty string / same name to boolean if
      // boolean has higher priority
      var stringIndex = getTypeIndex(String, prop.type);
      if (stringIndex < 0 || booleanIndex < stringIndex) {
        value = true;
      }
    }
  }
  // check default value
  if (value === undefined) {
    value = getPropDefaultValue(vm, prop, key);
    // since the default value is a fresh copy,
    // make sure to observe it.
    var prevShouldObserve = shouldObserve;
    toggleObserving(true);
    observe(value);
    toggleObserving(prevShouldObserve);
  }
  return value
}

/**
 * Get the default value of a prop.
 */
function getPropDefaultValue (vm, prop, key) {
  // no default, return undefined
  if (!hasOwn(prop, 'default')) {
    return undefined
  }
  var def = prop.default;
  // the raw prop value was also undefined from previous render,
  // return previous default value to avoid unnecessary watcher trigger
  if (vm && vm.$options.propsData &&
    vm.$options.propsData[key] === undefined &&
    vm._props[key] !== undefined
  ) {
    return vm._props[key]
  }
  // call factory function for non-Function types
  // a value is Function if its prototype is function even across different execution context
  return typeof def === 'function' && getType(prop.type) !== 'Function'
    ? def.call(vm)
    : def
}

/**
 * Use function string name to check built-in types,
 * because a simple equality check will fail when running
 * across different vms / iframes.
 */
function getType (fn) {
  var match = fn && fn.toString().match(/^\s*function (\w+)/);
  return match ? match[1] : ''
}

function isSameType (a, b) {
  return getType(a) === getType(b)
}

function getTypeIndex (type, expectedTypes) {
  if (!Array.isArray(expectedTypes)) {
    return isSameType(expectedTypes, type) ? 0 : -1
  }
  for (var i = 0, len = expectedTypes.length; i < len; i++) {
    if (isSameType(expectedTypes[i], type)) {
      return i
    }
  }
  return -1
}

/*  */

function handleError (err, vm, info) {
  // Deactivate deps tracking while processing error handler to avoid possible infinite rendering.
  // See: https://github.com/vuejs/vuex/issues/1505
  pushTarget();
  try {
    if (vm) {
      var cur = vm;
      while ((cur = cur.$parent)) {
        var hooks = cur.$options.errorCaptured;
        if (hooks) {
          for (var i = 0; i < hooks.length; i++) {
            try {
              var capture = hooks[i].call(cur, err, vm, info) === false;
              if (capture) { return }
            } catch (e) {
              globalHandleError(e, cur, 'errorCaptured hook');
            }
          }
        }
      }
    }
    globalHandleError(err, vm, info);
  } finally {
    popTarget();
  }
}

function invokeWithErrorHandling (
  handler,
  context,
  args,
  vm,
  info
) {
  var res;
  try {
    res = args ? handler.apply(context, args) : handler.call(context);
    if (res && !res._isVue && isPromise(res) && !res._handled) {
      res.catch(function (e) { return handleError(e, vm, info + " (Promise/async)"); });
      // issue #9511
      // avoid catch triggering multiple times when nested calls
      res._handled = true;
    }
  } catch (e) {
    handleError(e, vm, info);
  }
  return res
}

function globalHandleError (err, vm, info) {
  if (config.errorHandler) {
    try {
      return config.errorHandler.call(null, err, vm, info)
    } catch (e) {
      // if the user intentionally throws the original error in the handler,
      // do not log it twice
      if (e !== err) {
        logError(e);
      }
    }
  }
  logError(err);
}

function logError (err, vm, info) {
  /* istanbul ignore else */
  if ((inBrowser || inWeex) && typeof console !== 'undefined') {
    console.error(err);
  } else {
    throw err
  }
}

/*  */

var isUsingMicroTask = false;

var callbacks = [];
var pending = false;

function flushCallbacks () {
  pending = false;
  var copies = callbacks.slice(0);
  callbacks.length = 0;
  for (var i = 0; i < copies.length; i++) {
    copies[i]();
  }
}

// Here we have async deferring wrappers using microtasks.
// In 2.5 we used (macro) tasks (in combination with microtasks).
// However, it has subtle problems when state is changed right before repaint
// (e.g. #6813, out-in transitions).
// Also, using (macro) tasks in event handler would cause some weird behaviors
// that cannot be circumvented (e.g. #7109, #7153, #7546, #7834, #8109).
// So we now use microtasks everywhere, again.
// A major drawback of this tradeoff is that there are some scenarios
// where microtasks have too high a priority and fire in between supposedly
// sequential events (e.g. #4521, #6690, which have workarounds)
// or even between bubbling of the same event (#6566).
var timerFunc;

// The nextTick behavior leverages the microtask queue, which can be accessed
// via either native Promise.then or MutationObserver.
// MutationObserver has wider support, however it is seriously bugged in
// UIWebView in iOS >= 9.3.3 when triggered in touch event handlers. It
// completely stops working after triggering a few times... so, if native
// Promise is available, we will use it:
/* istanbul ignore next, $flow-disable-line */
if (typeof Promise !== 'undefined' && isNative(Promise)) {
  var p = Promise.resolve();
  timerFunc = function () {
    p.then(flushCallbacks);
    // In problematic UIWebViews, Promise.then doesn't completely break, but
    // it can get stuck in a weird state where callbacks are pushed into the
    // microtask queue but the queue isn't being flushed, until the browser
    // needs to do some other work, e.g. handle a timer. Therefore we can
    // "force" the microtask queue to be flushed by adding an empty timer.
    if (isIOS) { setTimeout(noop); }
  };
  isUsingMicroTask = true;
} else if (!isIE && typeof MutationObserver !== 'undefined' && (
  isNative(MutationObserver) ||
  // PhantomJS and iOS 7.x
  MutationObserver.toString() === '[object MutationObserverConstructor]'
)) {
  // Use MutationObserver where native Promise is not available,
  // e.g. PhantomJS, iOS7, Android 4.4
  // (#6466 MutationObserver is unreliable in IE11)
  var counter = 1;
  var observer = new MutationObserver(flushCallbacks);
  var textNode = document.createTextNode(String(counter));
  observer.observe(textNode, {
    characterData: true
  });
  timerFunc = function () {
    counter = (counter + 1) % 2;
    textNode.data = String(counter);
  };
  isUsingMicroTask = true;
} else if (typeof setImmediate !== 'undefined' && isNative(setImmediate)) {
  // Fallback to setImmediate.
  // Technically it leverages the (macro) task queue,
  // but it is still a better choice than setTimeout.
  timerFunc = function () {
    setImmediate(flushCallbacks);
  };
} else {
  // Fallback to setTimeout.
  timerFunc = function () {
    setTimeout(flushCallbacks, 0);
  };
}

function nextTick (cb, ctx) {
  var _resolve;
  callbacks.push(function () {
    if (cb) {
      try {
        cb.call(ctx);
      } catch (e) {
        handleError(e, ctx, 'nextTick');
      }
    } else if (_resolve) {
      _resolve(ctx);
    }
  });
  if (!pending) {
    pending = true;
    timerFunc();
  }
  // $flow-disable-line
  if (!cb && typeof Promise !== 'undefined') {
    return new Promise(function (resolve) {
      _resolve = resolve;
    })
  }
}

/*  */

var seenObjects = new _Set();

/**
 * Recursively traverse an object to evoke all converted
 * getters, so that every nested property inside the object
 * is collected as a "deep" dependency.
 */
function traverse (val) {
  _traverse(val, seenObjects);
  seenObjects.clear();
}

function _traverse (val, seen) {
  var i, keys;
  var isA = Array.isArray(val);
  if ((!isA && !isObject(val)) || Object.isFrozen(val) || val instanceof VNode) {
    return
  }
  if (val.__ob__) {
    var depId = val.__ob__.dep.id;
    if (seen.has(depId)) {
      return
    }
    seen.add(depId);
  }
  if (isA) {
    i = val.length;
    while (i--) { _traverse(val[i], seen); }
  } else {
    keys = Object.keys(val);
    i = keys.length;
    while (i--) { _traverse(val[keys[i]], seen); }
  }
}

/*  */

var normalizeEvent = cached(function (name) {
  var passive = name.charAt(0) === '&';
  name = passive ? name.slice(1) : name;
  var once$$1 = name.charAt(0) === '~'; // Prefixed last, checked first
  name = once$$1 ? name.slice(1) : name;
  var capture = name.charAt(0) === '!';
  name = capture ? name.slice(1) : name;
  return {
    name: name,
    once: once$$1,
    capture: capture,
    passive: passive
  }
});

function createFnInvoker (fns, vm) {
  function invoker () {
    var arguments$1 = arguments;

    var fns = invoker.fns;
    if (Array.isArray(fns)) {
      var cloned = fns.slice();
      for (var i = 0; i < cloned.length; i++) {
        invokeWithErrorHandling(cloned[i], null, arguments$1, vm, "v-on handler");
      }
    } else {
      // return handler return value for single handlers
      return invokeWithErrorHandling(fns, null, arguments, vm, "v-on handler")
    }
  }
  invoker.fns = fns;
  return invoker
}

function updateListeners (
  on,
  oldOn,
  add,
  remove$$1,
  createOnceHandler,
  vm
) {
  var name, cur, old, event;
  for (name in on) {
    cur = on[name];
    old = oldOn[name];
    event = normalizeEvent(name);
    if (isUndef(cur)) ; else if (isUndef(old)) {
      if (isUndef(cur.fns)) {
        cur = on[name] = createFnInvoker(cur, vm);
      }
      if (isTrue(event.once)) {
        cur = on[name] = createOnceHandler(event.name, cur, event.capture);
      }
      add(event.name, cur, event.capture, event.passive, event.params);
    } else if (cur !== old) {
      old.fns = cur;
      on[name] = old;
    }
  }
  for (name in oldOn) {
    if (isUndef(on[name])) {
      event = normalizeEvent(name);
      remove$$1(event.name, oldOn[name], event.capture);
    }
  }
}

/*  */

function mergeVNodeHook (def, hookKey, hook) {
  if (def instanceof VNode) {
    def = def.data.hook || (def.data.hook = {});
  }
  var invoker;
  var oldHook = def[hookKey];

  function wrappedHook () {
    hook.apply(this, arguments);
    // important: remove merged hook to ensure it's called only once
    // and prevent memory leak
    remove(invoker.fns, wrappedHook);
  }

  if (isUndef(oldHook)) {
    // no existing hook
    invoker = createFnInvoker([wrappedHook]);
  } else {
    /* istanbul ignore if */
    if (isDef(oldHook.fns) && isTrue(oldHook.merged)) {
      // already a merged invoker
      invoker = oldHook;
      invoker.fns.push(wrappedHook);
    } else {
      // existing plain hook
      invoker = createFnInvoker([oldHook, wrappedHook]);
    }
  }

  invoker.merged = true;
  def[hookKey] = invoker;
}

/*  */

function extractPropsFromVNodeData (
  data,
  Ctor,
  tag
) {
  // we are only extracting raw values here.
  // validation and default values are handled in the child
  // component itself.
  var propOptions = Ctor.options.props;
  if (isUndef(propOptions)) {
    return
  }
  var res = {};
  var attrs = data.attrs;
  var props = data.props;
  if (isDef(attrs) || isDef(props)) {
    for (var key in propOptions) {
      var altKey = hyphenate(key);
      checkProp(res, props, key, altKey, true) ||
      checkProp(res, attrs, key, altKey, false);
    }
  }
  return res
}

function checkProp (
  res,
  hash,
  key,
  altKey,
  preserve
) {
  if (isDef(hash)) {
    if (hasOwn(hash, key)) {
      res[key] = hash[key];
      if (!preserve) {
        delete hash[key];
      }
      return true
    } else if (hasOwn(hash, altKey)) {
      res[key] = hash[altKey];
      if (!preserve) {
        delete hash[altKey];
      }
      return true
    }
  }
  return false
}

/*  */

// The template compiler attempts to minimize the need for normalization by
// statically analyzing the template at compile time.
//
// For plain HTML markup, normalization can be completely skipped because the
// generated render function is guaranteed to return Array<VNode>. There are
// two cases where extra normalization is needed:

// 1. When the children contains components - because a functional component
// may return an Array instead of a single root. In this case, just a simple
// normalization is needed - if any child is an Array, we flatten the whole
// thing with Array.prototype.concat. It is guaranteed to be only 1-level deep
// because functional components already normalize their own children.
function simpleNormalizeChildren (children) {
  for (var i = 0; i < children.length; i++) {
    if (Array.isArray(children[i])) {
      return Array.prototype.concat.apply([], children)
    }
  }
  return children
}

// 2. When the children contains constructs that always generated nested Arrays,
// e.g. <template>, <slot>, v-for, or when the children is provided by user
// with hand-written render functions / JSX. In such cases a full normalization
// is needed to cater to all possible types of children values.
function normalizeChildren (children) {
  return isPrimitive$1(children)
    ? [createTextVNode(children)]
    : Array.isArray(children)
      ? normalizeArrayChildren(children)
      : undefined
}

function isTextNode (node) {
  return isDef(node) && isDef(node.text) && isFalse(node.isComment)
}

function normalizeArrayChildren (children, nestedIndex) {
  var res = [];
  var i, c, lastIndex, last;
  for (i = 0; i < children.length; i++) {
    c = children[i];
    if (isUndef(c) || typeof c === 'boolean') { continue }
    lastIndex = res.length - 1;
    last = res[lastIndex];
    //  nested
    if (Array.isArray(c)) {
      if (c.length > 0) {
        c = normalizeArrayChildren(c, ((nestedIndex || '') + "_" + i));
        // merge adjacent text nodes
        if (isTextNode(c[0]) && isTextNode(last)) {
          res[lastIndex] = createTextVNode(last.text + (c[0]).text);
          c.shift();
        }
        res.push.apply(res, c);
      }
    } else if (isPrimitive$1(c)) {
      if (isTextNode(last)) {
        // merge adjacent text nodes
        // this is necessary for SSR hydration because text nodes are
        // essentially merged when rendered to HTML strings
        res[lastIndex] = createTextVNode(last.text + c);
      } else if (c !== '') {
        // convert primitive to vnode
        res.push(createTextVNode(c));
      }
    } else {
      if (isTextNode(c) && isTextNode(last)) {
        // merge adjacent text nodes
        res[lastIndex] = createTextVNode(last.text + c.text);
      } else {
        // default key for nested array children (likely generated by v-for)
        if (isTrue(children._isVList) &&
          isDef(c.tag) &&
          isUndef(c.key) &&
          isDef(nestedIndex)) {
          c.key = "__vlist" + nestedIndex + "_" + i + "__";
        }
        res.push(c);
      }
    }
  }
  return res
}

/*  */

function initProvide (vm) {
  var provide = vm.$options.provide;
  if (provide) {
    vm._provided = typeof provide === 'function'
      ? provide.call(vm)
      : provide;
  }
}

function initInjections (vm) {
  var result = resolveInject(vm.$options.inject, vm);
  if (result) {
    toggleObserving(false);
    Object.keys(result).forEach(function (key) {
      /* istanbul ignore else */
      {
        defineReactive$$1(vm, key, result[key]);
      }
    });
    toggleObserving(true);
  }
}

function resolveInject (inject, vm) {
  if (inject) {
    // inject is :any because flow is not smart enough to figure out cached
    var result = Object.create(null);
    var keys = hasSymbol
      ? Reflect.ownKeys(inject)
      : Object.keys(inject);

    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      // #6574 in case the inject object is observed...
      if (key === '__ob__') { continue }
      var provideKey = inject[key].from;
      var source = vm;
      while (source) {
        if (source._provided && hasOwn(source._provided, provideKey)) {
          result[key] = source._provided[provideKey];
          break
        }
        source = source.$parent;
      }
      if (!source) {
        if ('default' in inject[key]) {
          var provideDefault = inject[key].default;
          result[key] = typeof provideDefault === 'function'
            ? provideDefault.call(vm)
            : provideDefault;
        }
      }
    }
    return result
  }
}

/*  */



/**
 * Runtime helper for resolving raw children VNodes into a slot object.
 */
function resolveSlots (
  children,
  context
) {
  if (!children || !children.length) {
    return {}
  }
  var slots = {};
  for (var i = 0, l = children.length; i < l; i++) {
    var child = children[i];
    var data = child.data;
    // remove slot attribute if the node is resolved as a Vue slot node
    if (data && data.attrs && data.attrs.slot) {
      delete data.attrs.slot;
    }
    // named slots should only be respected if the vnode was rendered in the
    // same context.
    if ((child.context === context || child.fnContext === context) &&
      data && data.slot != null
    ) {
      var name = data.slot;
      var slot = (slots[name] || (slots[name] = []));
      if (child.tag === 'template') {
        slot.push.apply(slot, child.children || []);
      } else {
        slot.push(child);
      }
    } else {
      (slots.default || (slots.default = [])).push(child);
    }
  }
  // ignore slots that contains only whitespace
  for (var name$1 in slots) {
    if (slots[name$1].every(isWhitespace)) {
      delete slots[name$1];
    }
  }
  return slots
}

function isWhitespace (node) {
  return (node.isComment && !node.asyncFactory) || node.text === ' '
}

/*  */

function normalizeScopedSlots (
  slots,
  normalSlots,
  prevSlots
) {
  var res;
  var hasNormalSlots = Object.keys(normalSlots).length > 0;
  var isStable = slots ? !!slots.$stable : !hasNormalSlots;
  var key = slots && slots.$key;
  if (!slots) {
    res = {};
  } else if (slots._normalized) {
    // fast path 1: child component re-render only, parent did not change
    return slots._normalized
  } else if (
    isStable &&
    prevSlots &&
    prevSlots !== emptyObject &&
    key === prevSlots.$key &&
    !hasNormalSlots &&
    !prevSlots.$hasNormal
  ) {
    // fast path 2: stable scoped slots w/ no normal slots to proxy,
    // only need to normalize once
    return prevSlots
  } else {
    res = {};
    for (var key$1 in slots) {
      if (slots[key$1] && key$1[0] !== '$') {
        res[key$1] = normalizeScopedSlot(normalSlots, key$1, slots[key$1]);
      }
    }
  }
  // expose normal slots on scopedSlots
  for (var key$2 in normalSlots) {
    if (!(key$2 in res)) {
      res[key$2] = proxyNormalSlot(normalSlots, key$2);
    }
  }
  // avoriaz seems to mock a non-extensible $scopedSlots object
  // and when that is passed down this would cause an error
  if (slots && Object.isExtensible(slots)) {
    (slots)._normalized = res;
  }
  def(res, '$stable', isStable);
  def(res, '$key', key);
  def(res, '$hasNormal', hasNormalSlots);
  return res
}

function normalizeScopedSlot(normalSlots, key, fn) {
  var normalized = function () {
    var res = arguments.length ? fn.apply(null, arguments) : fn({});
    res = res && typeof res === 'object' && !Array.isArray(res)
      ? [res] // single vnode
      : normalizeChildren(res);
    return res && (
      res.length === 0 ||
      (res.length === 1 && res[0].isComment) // #9658
    ) ? undefined
      : res
  };
  // this is a slot using the new v-slot syntax without scope. although it is
  // compiled as a scoped slot, render fn users would expect it to be present
  // on this.$slots because the usage is semantically a normal slot.
  if (fn.proxy) {
    Object.defineProperty(normalSlots, key, {
      get: normalized,
      enumerable: true,
      configurable: true
    });
  }
  return normalized
}

function proxyNormalSlot(slots, key) {
  return function () { return slots[key]; }
}

/*  */

/**
 * Runtime helper for rendering v-for lists.
 */
function renderList (
  val,
  render
) {
  var ret, i, l, keys, key;
  if (Array.isArray(val) || typeof val === 'string') {
    ret = new Array(val.length);
    for (i = 0, l = val.length; i < l; i++) {
      ret[i] = render(val[i], i);
    }
  } else if (typeof val === 'number') {
    ret = new Array(val);
    for (i = 0; i < val; i++) {
      ret[i] = render(i + 1, i);
    }
  } else if (isObject(val)) {
    if (hasSymbol && val[Symbol.iterator]) {
      ret = [];
      var iterator = val[Symbol.iterator]();
      var result = iterator.next();
      while (!result.done) {
        ret.push(render(result.value, ret.length));
        result = iterator.next();
      }
    } else {
      keys = Object.keys(val);
      ret = new Array(keys.length);
      for (i = 0, l = keys.length; i < l; i++) {
        key = keys[i];
        ret[i] = render(val[key], key, i);
      }
    }
  }
  if (!isDef(ret)) {
    ret = [];
  }
  (ret)._isVList = true;
  return ret
}

/*  */

/**
 * Runtime helper for rendering <slot>
 */
function renderSlot (
  name,
  fallback,
  props,
  bindObject
) {
  var scopedSlotFn = this.$scopedSlots[name];
  var nodes;
  if (scopedSlotFn) { // scoped slot
    props = props || {};
    if (bindObject) {
      props = extend(extend({}, bindObject), props);
    }
    nodes = scopedSlotFn(props) || fallback;
  } else {
    nodes = this.$slots[name] || fallback;
  }

  var target = props && props.slot;
  if (target) {
    return this.$createElement('template', { slot: target }, nodes)
  } else {
    return nodes
  }
}

/*  */

/**
 * Runtime helper for resolving filters
 */
function resolveFilter (id) {
  return resolveAsset(this.$options, 'filters', id) || identity
}

/*  */

function isKeyNotMatch (expect, actual) {
  if (Array.isArray(expect)) {
    return expect.indexOf(actual) === -1
  } else {
    return expect !== actual
  }
}

/**
 * Runtime helper for checking keyCodes from config.
 * exposed as Vue.prototype._k
 * passing in eventKeyName as last argument separately for backwards compat
 */
function checkKeyCodes (
  eventKeyCode,
  key,
  builtInKeyCode,
  eventKeyName,
  builtInKeyName
) {
  var mappedKeyCode = config.keyCodes[key] || builtInKeyCode;
  if (builtInKeyName && eventKeyName && !config.keyCodes[key]) {
    return isKeyNotMatch(builtInKeyName, eventKeyName)
  } else if (mappedKeyCode) {
    return isKeyNotMatch(mappedKeyCode, eventKeyCode)
  } else if (eventKeyName) {
    return hyphenate(eventKeyName) !== key
  }
}

/*  */

/**
 * Runtime helper for merging v-bind="object" into a VNode's data.
 */
function bindObjectProps (
  data,
  tag,
  value,
  asProp,
  isSync
) {
  if (value) {
    if (!isObject(value)) ; else {
      if (Array.isArray(value)) {
        value = toObject(value);
      }
      var hash;
      var loop = function ( key ) {
        if (
          key === 'class' ||
          key === 'style' ||
          isReservedAttribute(key)
        ) {
          hash = data;
        } else {
          var type = data.attrs && data.attrs.type;
          hash = asProp || config.mustUseProp(tag, type, key)
            ? data.domProps || (data.domProps = {})
            : data.attrs || (data.attrs = {});
        }
        var camelizedKey = camelize(key);
        var hyphenatedKey = hyphenate(key);
        if (!(camelizedKey in hash) && !(hyphenatedKey in hash)) {
          hash[key] = value[key];

          if (isSync) {
            var on = data.on || (data.on = {});
            on[("update:" + key)] = function ($event) {
              value[key] = $event;
            };
          }
        }
      };

      for (var key in value) loop( key );
    }
  }
  return data
}

/*  */

/**
 * Runtime helper for rendering static trees.
 */
function renderStatic (
  index,
  isInFor
) {
  var cached = this._staticTrees || (this._staticTrees = []);
  var tree = cached[index];
  // if has already-rendered static tree and not inside v-for,
  // we can reuse the same tree.
  if (tree && !isInFor) {
    return tree
  }
  // otherwise, render a fresh tree.
  tree = cached[index] = this.$options.staticRenderFns[index].call(
    this._renderProxy,
    null,
    this // for render fns generated for functional component templates
  );
  markStatic(tree, ("__static__" + index), false);
  return tree
}

/**
 * Runtime helper for v-once.
 * Effectively it means marking the node as static with a unique key.
 */
function markOnce (
  tree,
  index,
  key
) {
  markStatic(tree, ("__once__" + index + (key ? ("_" + key) : "")), true);
  return tree
}

function markStatic (
  tree,
  key,
  isOnce
) {
  if (Array.isArray(tree)) {
    for (var i = 0; i < tree.length; i++) {
      if (tree[i] && typeof tree[i] !== 'string') {
        markStaticNode(tree[i], (key + "_" + i), isOnce);
      }
    }
  } else {
    markStaticNode(tree, key, isOnce);
  }
}

function markStaticNode (node, key, isOnce) {
  node.isStatic = true;
  node.key = key;
  node.isOnce = isOnce;
}

/*  */

function bindObjectListeners (data, value) {
  if (value) {
    if (!isPlainObject(value)) ; else {
      var on = data.on = data.on ? extend({}, data.on) : {};
      for (var key in value) {
        var existing = on[key];
        var ours = value[key];
        on[key] = existing ? [].concat(existing, ours) : ours;
      }
    }
  }
  return data
}

/*  */

function resolveScopedSlots (
  fns, // see flow/vnode
  res,
  // the following are added in 2.6
  hasDynamicKeys,
  contentHashKey
) {
  res = res || { $stable: !hasDynamicKeys };
  for (var i = 0; i < fns.length; i++) {
    var slot = fns[i];
    if (Array.isArray(slot)) {
      resolveScopedSlots(slot, res, hasDynamicKeys);
    } else if (slot) {
      // marker for reverse proxying v-slot without scope on this.$slots
      if (slot.proxy) {
        slot.fn.proxy = true;
      }
      res[slot.key] = slot.fn;
    }
  }
  if (contentHashKey) {
    (res).$key = contentHashKey;
  }
  return res
}

/*  */

function bindDynamicKeys (baseObj, values) {
  for (var i = 0; i < values.length; i += 2) {
    var key = values[i];
    if (typeof key === 'string' && key) {
      baseObj[values[i]] = values[i + 1];
    }
  }
  return baseObj
}

// helper to dynamically append modifier runtime markers to event names.
// ensure only append when value is already string, otherwise it will be cast
// to string and cause the type check to miss.
function prependModifier (value, symbol) {
  return typeof value === 'string' ? symbol + value : value
}

/*  */

function installRenderHelpers (target) {
  target._o = markOnce;
  target._n = toNumber;
  target._s = toString;
  target._l = renderList;
  target._t = renderSlot;
  target._q = looseEqual;
  target._i = looseIndexOf;
  target._m = renderStatic;
  target._f = resolveFilter;
  target._k = checkKeyCodes;
  target._b = bindObjectProps;
  target._v = createTextVNode;
  target._e = createEmptyVNode;
  target._u = resolveScopedSlots;
  target._g = bindObjectListeners;
  target._d = bindDynamicKeys;
  target._p = prependModifier;
}

/*  */

function FunctionalRenderContext (
  data,
  props,
  children,
  parent,
  Ctor
) {
  var this$1 = this;

  var options = Ctor.options;
  // ensure the createElement function in functional components
  // gets a unique context - this is necessary for correct named slot check
  var contextVm;
  if (hasOwn(parent, '_uid')) {
    contextVm = Object.create(parent);
    // $flow-disable-line
    contextVm._original = parent;
  } else {
    // the context vm passed in is a functional context as well.
    // in this case we want to make sure we are able to get a hold to the
    // real context instance.
    contextVm = parent;
    // $flow-disable-line
    parent = parent._original;
  }
  var isCompiled = isTrue(options._compiled);
  var needNormalization = !isCompiled;

  this.data = data;
  this.props = props;
  this.children = children;
  this.parent = parent;
  this.listeners = data.on || emptyObject;
  this.injections = resolveInject(options.inject, parent);
  this.slots = function () {
    if (!this$1.$slots) {
      normalizeScopedSlots(
        data.scopedSlots,
        this$1.$slots = resolveSlots(children, parent)
      );
    }
    return this$1.$slots
  };

  Object.defineProperty(this, 'scopedSlots', ({
    enumerable: true,
    get: function get () {
      return normalizeScopedSlots(data.scopedSlots, this.slots())
    }
  }));

  // support for compiled functional template
  if (isCompiled) {
    // exposing $options for renderStatic()
    this.$options = options;
    // pre-resolve slots for renderSlot()
    this.$slots = this.slots();
    this.$scopedSlots = normalizeScopedSlots(data.scopedSlots, this.$slots);
  }

  if (options._scopeId) {
    this._c = function (a, b, c, d) {
      var vnode = createElement(contextVm, a, b, c, d, needNormalization);
      if (vnode && !Array.isArray(vnode)) {
        vnode.fnScopeId = options._scopeId;
        vnode.fnContext = parent;
      }
      return vnode
    };
  } else {
    this._c = function (a, b, c, d) { return createElement(contextVm, a, b, c, d, needNormalization); };
  }
}

installRenderHelpers(FunctionalRenderContext.prototype);

function createFunctionalComponent (
  Ctor,
  propsData,
  data,
  contextVm,
  children
) {
  var options = Ctor.options;
  var props = {};
  var propOptions = options.props;
  if (isDef(propOptions)) {
    for (var key in propOptions) {
      props[key] = validateProp(key, propOptions, propsData || emptyObject);
    }
  } else {
    if (isDef(data.attrs)) { mergeProps(props, data.attrs); }
    if (isDef(data.props)) { mergeProps(props, data.props); }
  }

  var renderContext = new FunctionalRenderContext(
    data,
    props,
    children,
    contextVm,
    Ctor
  );

  var vnode = options.render.call(null, renderContext._c, renderContext);

  if (vnode instanceof VNode) {
    return cloneAndMarkFunctionalResult(vnode, data, renderContext.parent, options)
  } else if (Array.isArray(vnode)) {
    var vnodes = normalizeChildren(vnode) || [];
    var res = new Array(vnodes.length);
    for (var i = 0; i < vnodes.length; i++) {
      res[i] = cloneAndMarkFunctionalResult(vnodes[i], data, renderContext.parent, options);
    }
    return res
  }
}

function cloneAndMarkFunctionalResult (vnode, data, contextVm, options, renderContext) {
  // #7817 clone node before setting fnContext, otherwise if the node is reused
  // (e.g. it was from a cached normal slot) the fnContext causes named slots
  // that should not be matched to match.
  var clone = cloneVNode(vnode);
  clone.fnContext = contextVm;
  clone.fnOptions = options;
  if (data.slot) {
    (clone.data || (clone.data = {})).slot = data.slot;
  }
  return clone
}

function mergeProps (to, from) {
  for (var key in from) {
    to[camelize(key)] = from[key];
  }
}

/*  */

/*  */

/*  */

/*  */

// inline hooks to be invoked on component VNodes during patch
var componentVNodeHooks = {
  init: function init (vnode, hydrating) {
    if (
      vnode.componentInstance &&
      !vnode.componentInstance._isDestroyed &&
      vnode.data.keepAlive
    ) {
      // kept-alive components, treat as a patch
      var mountedNode = vnode; // work around flow
      componentVNodeHooks.prepatch(mountedNode, mountedNode);
    } else {
      var child = vnode.componentInstance = createComponentInstanceForVnode(
        vnode,
        activeInstance
      );
      child.$mount(hydrating ? vnode.elm : undefined, hydrating);
    }
  },

  prepatch: function prepatch (oldVnode, vnode) {
    var options = vnode.componentOptions;
    var child = vnode.componentInstance = oldVnode.componentInstance;
    updateChildComponent(
      child,
      options.propsData, // updated props
      options.listeners, // updated listeners
      vnode, // new parent vnode
      options.children // new children
    );
  },

  insert: function insert (vnode) {
    var context = vnode.context;
    var componentInstance = vnode.componentInstance;
    if (!componentInstance._isMounted) {
      componentInstance._isMounted = true;
      callHook(componentInstance, 'mounted');
    }
    if (vnode.data.keepAlive) {
      if (context._isMounted) {
        // vue-router#1212
        // During updates, a kept-alive component's child components may
        // change, so directly walking the tree here may call activated hooks
        // on incorrect children. Instead we push them into a queue which will
        // be processed after the whole patch process ended.
        queueActivatedComponent(componentInstance);
      } else {
        activateChildComponent(componentInstance, true /* direct */);
      }
    }
  },

  destroy: function destroy (vnode) {
    var componentInstance = vnode.componentInstance;
    if (!componentInstance._isDestroyed) {
      if (!vnode.data.keepAlive) {
        componentInstance.$destroy();
      } else {
        deactivateChildComponent(componentInstance, true /* direct */);
      }
    }
  }
};

var hooksToMerge = Object.keys(componentVNodeHooks);

function createComponent (
  Ctor,
  data,
  context,
  children,
  tag
) {
  if (isUndef(Ctor)) {
    return
  }

  var baseCtor = context.$options._base;

  // plain options object: turn it into a constructor
  if (isObject(Ctor)) {
    Ctor = baseCtor.extend(Ctor);
  }

  // if at this stage it's not a constructor or an async component factory,
  // reject.
  if (typeof Ctor !== 'function') {
    return
  }

  // async component
  var asyncFactory;
  if (isUndef(Ctor.cid)) {
    asyncFactory = Ctor;
    Ctor = resolveAsyncComponent(asyncFactory, baseCtor);
    if (Ctor === undefined) {
      // return a placeholder node for async component, which is rendered
      // as a comment node but preserves all the raw information for the node.
      // the information will be used for async server-rendering and hydration.
      return createAsyncPlaceholder(
        asyncFactory,
        data,
        context,
        children,
        tag
      )
    }
  }

  data = data || {};

  // resolve constructor options in case global mixins are applied after
  // component constructor creation
  resolveConstructorOptions(Ctor);

  // transform component v-model data into props & events
  if (isDef(data.model)) {
    transformModel(Ctor.options, data);
  }

  // extract props
  var propsData = extractPropsFromVNodeData(data, Ctor);

  // functional component
  if (isTrue(Ctor.options.functional)) {
    return createFunctionalComponent(Ctor, propsData, data, context, children)
  }

  // extract listeners, since these needs to be treated as
  // child component listeners instead of DOM listeners
  var listeners = data.on;
  // replace with listeners with .native modifier
  // so it gets processed during parent component patch.
  data.on = data.nativeOn;

  if (isTrue(Ctor.options.abstract)) {
    // abstract components do not keep anything
    // other than props & listeners & slot

    // work around flow
    var slot = data.slot;
    data = {};
    if (slot) {
      data.slot = slot;
    }
  }

  // install component management hooks onto the placeholder node
  installComponentHooks(data);

  // return a placeholder vnode
  var name = Ctor.options.name || tag;
  var vnode = new VNode(
    ("vue-component-" + (Ctor.cid) + (name ? ("-" + name) : '')),
    data, undefined, undefined, undefined, context,
    { Ctor: Ctor, propsData: propsData, listeners: listeners, tag: tag, children: children },
    asyncFactory
  );

  return vnode
}

function createComponentInstanceForVnode (
  vnode, // we know it's MountedComponentVNode but flow doesn't
  parent // activeInstance in lifecycle state
) {
  var options = {
    _isComponent: true,
    _parentVnode: vnode,
    parent: parent
  };
  // check inline-template render functions
  var inlineTemplate = vnode.data.inlineTemplate;
  if (isDef(inlineTemplate)) {
    options.render = inlineTemplate.render;
    options.staticRenderFns = inlineTemplate.staticRenderFns;
  }
  return new vnode.componentOptions.Ctor(options)
}

function installComponentHooks (data) {
  var hooks = data.hook || (data.hook = {});
  for (var i = 0; i < hooksToMerge.length; i++) {
    var key = hooksToMerge[i];
    var existing = hooks[key];
    var toMerge = componentVNodeHooks[key];
    if (existing !== toMerge && !(existing && existing._merged)) {
      hooks[key] = existing ? mergeHook$1(toMerge, existing) : toMerge;
    }
  }
}

function mergeHook$1 (f1, f2) {
  var merged = function (a, b) {
    // flow complains about extra args which is why we use any
    f1(a, b);
    f2(a, b);
  };
  merged._merged = true;
  return merged
}

// transform component v-model info (value and callback) into
// prop and event handler respectively.
function transformModel (options, data) {
  var prop = (options.model && options.model.prop) || 'value';
  var event = (options.model && options.model.event) || 'input'
  ;(data.attrs || (data.attrs = {}))[prop] = data.model.value;
  var on = data.on || (data.on = {});
  var existing = on[event];
  var callback = data.model.callback;
  if (isDef(existing)) {
    if (
      Array.isArray(existing)
        ? existing.indexOf(callback) === -1
        : existing !== callback
    ) {
      on[event] = [callback].concat(existing);
    }
  } else {
    on[event] = callback;
  }
}

/*  */

var SIMPLE_NORMALIZE = 1;
var ALWAYS_NORMALIZE = 2;

// wrapper function for providing a more flexible interface
// without getting yelled at by flow
function createElement (
  context,
  tag,
  data,
  children,
  normalizationType,
  alwaysNormalize
) {
  if (Array.isArray(data) || isPrimitive$1(data)) {
    normalizationType = children;
    children = data;
    data = undefined;
  }
  if (isTrue(alwaysNormalize)) {
    normalizationType = ALWAYS_NORMALIZE;
  }
  return _createElement(context, tag, data, children, normalizationType)
}

function _createElement (
  context,
  tag,
  data,
  children,
  normalizationType
) {
  if (isDef(data) && isDef((data).__ob__)) {
    return createEmptyVNode()
  }
  // object syntax in v-bind
  if (isDef(data) && isDef(data.is)) {
    tag = data.is;
  }
  if (!tag) {
    // in case of component :is set to falsy value
    return createEmptyVNode()
  }
  // support single function children as default scoped slot
  if (Array.isArray(children) &&
    typeof children[0] === 'function'
  ) {
    data = data || {};
    data.scopedSlots = { default: children[0] };
    children.length = 0;
  }
  if (normalizationType === ALWAYS_NORMALIZE) {
    children = normalizeChildren(children);
  } else if (normalizationType === SIMPLE_NORMALIZE) {
    children = simpleNormalizeChildren(children);
  }
  var vnode, ns;
  if (typeof tag === 'string') {
    var Ctor;
    ns = (context.$vnode && context.$vnode.ns) || config.getTagNamespace(tag);
    if (config.isReservedTag(tag)) {
      vnode = new VNode(
        config.parsePlatformTagName(tag), data, children,
        undefined, undefined, context
      );
    } else if ((!data || !data.pre) && isDef(Ctor = resolveAsset(context.$options, 'components', tag))) {
      // component
      vnode = createComponent(Ctor, data, context, children, tag);
    } else {
      // unknown or unlisted namespaced elements
      // check at runtime because it may get assigned a namespace when its
      // parent normalizes children
      vnode = new VNode(
        tag, data, children,
        undefined, undefined, context
      );
    }
  } else {
    // direct component options / constructor
    vnode = createComponent(tag, data, context, children);
  }
  if (Array.isArray(vnode)) {
    return vnode
  } else if (isDef(vnode)) {
    if (isDef(ns)) { applyNS(vnode, ns); }
    if (isDef(data)) { registerDeepBindings(data); }
    return vnode
  } else {
    return createEmptyVNode()
  }
}

function applyNS (vnode, ns, force) {
  vnode.ns = ns;
  if (vnode.tag === 'foreignObject') {
    // use default namespace inside foreignObject
    ns = undefined;
    force = true;
  }
  if (isDef(vnode.children)) {
    for (var i = 0, l = vnode.children.length; i < l; i++) {
      var child = vnode.children[i];
      if (isDef(child.tag) && (
        isUndef(child.ns) || (isTrue(force) && child.tag !== 'svg'))) {
        applyNS(child, ns, force);
      }
    }
  }
}

// ref #5318
// necessary to ensure parent re-render when deep bindings like :style and
// :class are used on slot nodes
function registerDeepBindings (data) {
  if (isObject(data.style)) {
    traverse(data.style);
  }
  if (isObject(data.class)) {
    traverse(data.class);
  }
}

/*  */

function initRender (vm) {
  vm._vnode = null; // the root of the child tree
  vm._staticTrees = null; // v-once cached trees
  var options = vm.$options;
  var parentVnode = vm.$vnode = options._parentVnode; // the placeholder node in parent tree
  var renderContext = parentVnode && parentVnode.context;
  vm.$slots = resolveSlots(options._renderChildren, renderContext);
  vm.$scopedSlots = emptyObject;
  // bind the createElement fn to this instance
  // so that we get proper render context inside it.
  // args order: tag, data, children, normalizationType, alwaysNormalize
  // internal version is used by render functions compiled from templates
  vm._c = function (a, b, c, d) { return createElement(vm, a, b, c, d, false); };
  // normalization is always applied for the public version, used in
  // user-written render functions.
  vm.$createElement = function (a, b, c, d) { return createElement(vm, a, b, c, d, true); };

  // $attrs & $listeners are exposed for easier HOC creation.
  // they need to be reactive so that HOCs using them are always updated
  var parentData = parentVnode && parentVnode.data;

  /* istanbul ignore else */
  {
    defineReactive$$1(vm, '$attrs', parentData && parentData.attrs || emptyObject, null, true);
    defineReactive$$1(vm, '$listeners', options._parentListeners || emptyObject, null, true);
  }
}

var currentRenderingInstance = null;

function renderMixin (Vue) {
  // install runtime convenience helpers
  installRenderHelpers(Vue.prototype);

  Vue.prototype.$nextTick = function (fn) {
    return nextTick(fn, this)
  };

  Vue.prototype._render = function () {
    var vm = this;
    var ref = vm.$options;
    var render = ref.render;
    var _parentVnode = ref._parentVnode;

    if (_parentVnode) {
      vm.$scopedSlots = normalizeScopedSlots(
        _parentVnode.data.scopedSlots,
        vm.$slots,
        vm.$scopedSlots
      );
    }

    // set parent vnode. this allows render functions to have access
    // to the data on the placeholder node.
    vm.$vnode = _parentVnode;
    // render self
    var vnode;
    try {
      // There's no need to maintain a stack because all render fns are called
      // separately from one another. Nested component's render fns are called
      // when parent component is patched.
      currentRenderingInstance = vm;
      vnode = render.call(vm._renderProxy, vm.$createElement);
    } catch (e) {
      handleError(e, vm, "render");
      // return error render result,
      // or previous vnode to prevent render error causing blank component
      /* istanbul ignore else */
      {
        vnode = vm._vnode;
      }
    } finally {
      currentRenderingInstance = null;
    }
    // if the returned array contains only a single node, allow it
    if (Array.isArray(vnode) && vnode.length === 1) {
      vnode = vnode[0];
    }
    // return empty vnode in case the render function errored out
    if (!(vnode instanceof VNode)) {
      vnode = createEmptyVNode();
    }
    // set parent
    vnode.parent = _parentVnode;
    return vnode
  };
}

/*  */

function ensureCtor (comp, base) {
  if (
    comp.__esModule ||
    (hasSymbol && comp[Symbol.toStringTag] === 'Module')
  ) {
    comp = comp.default;
  }
  return isObject(comp)
    ? base.extend(comp)
    : comp
}

function createAsyncPlaceholder (
  factory,
  data,
  context,
  children,
  tag
) {
  var node = createEmptyVNode();
  node.asyncFactory = factory;
  node.asyncMeta = { data: data, context: context, children: children, tag: tag };
  return node
}

function resolveAsyncComponent (
  factory,
  baseCtor
) {
  if (isTrue(factory.error) && isDef(factory.errorComp)) {
    return factory.errorComp
  }

  if (isDef(factory.resolved)) {
    return factory.resolved
  }

  var owner = currentRenderingInstance;
  if (owner && isDef(factory.owners) && factory.owners.indexOf(owner) === -1) {
    // already pending
    factory.owners.push(owner);
  }

  if (isTrue(factory.loading) && isDef(factory.loadingComp)) {
    return factory.loadingComp
  }

  if (owner && !isDef(factory.owners)) {
    var owners = factory.owners = [owner];
    var sync = true;
    var timerLoading = null;
    var timerTimeout = null

    ;(owner).$on('hook:destroyed', function () { return remove(owners, owner); });

    var forceRender = function (renderCompleted) {
      for (var i = 0, l = owners.length; i < l; i++) {
        (owners[i]).$forceUpdate();
      }

      if (renderCompleted) {
        owners.length = 0;
        if (timerLoading !== null) {
          clearTimeout(timerLoading);
          timerLoading = null;
        }
        if (timerTimeout !== null) {
          clearTimeout(timerTimeout);
          timerTimeout = null;
        }
      }
    };

    var resolve = once(function (res) {
      // cache resolved
      factory.resolved = ensureCtor(res, baseCtor);
      // invoke callbacks only if this is not a synchronous resolve
      // (async resolves are shimmed as synchronous during SSR)
      if (!sync) {
        forceRender(true);
      } else {
        owners.length = 0;
      }
    });

    var reject = once(function (reason) {
      if (isDef(factory.errorComp)) {
        factory.error = true;
        forceRender(true);
      }
    });

    var res = factory(resolve, reject);

    if (isObject(res)) {
      if (isPromise(res)) {
        // () => Promise
        if (isUndef(factory.resolved)) {
          res.then(resolve, reject);
        }
      } else if (isPromise(res.component)) {
        res.component.then(resolve, reject);

        if (isDef(res.error)) {
          factory.errorComp = ensureCtor(res.error, baseCtor);
        }

        if (isDef(res.loading)) {
          factory.loadingComp = ensureCtor(res.loading, baseCtor);
          if (res.delay === 0) {
            factory.loading = true;
          } else {
            timerLoading = setTimeout(function () {
              timerLoading = null;
              if (isUndef(factory.resolved) && isUndef(factory.error)) {
                factory.loading = true;
                forceRender(false);
              }
            }, res.delay || 200);
          }
        }

        if (isDef(res.timeout)) {
          timerTimeout = setTimeout(function () {
            timerTimeout = null;
            if (isUndef(factory.resolved)) {
              reject(
                null
              );
            }
          }, res.timeout);
        }
      }
    }

    sync = false;
    // return in case resolved synchronously
    return factory.loading
      ? factory.loadingComp
      : factory.resolved
  }
}

/*  */

function isAsyncPlaceholder (node) {
  return node.isComment && node.asyncFactory
}

/*  */

function getFirstComponentChild (children) {
  if (Array.isArray(children)) {
    for (var i = 0; i < children.length; i++) {
      var c = children[i];
      if (isDef(c) && (isDef(c.componentOptions) || isAsyncPlaceholder(c))) {
        return c
      }
    }
  }
}

/*  */

/*  */

function initEvents (vm) {
  vm._events = Object.create(null);
  vm._hasHookEvent = false;
  // init parent attached events
  var listeners = vm.$options._parentListeners;
  if (listeners) {
    updateComponentListeners(vm, listeners);
  }
}

var target;

function add (event, fn) {
  target.$on(event, fn);
}

function remove$1 (event, fn) {
  target.$off(event, fn);
}

function createOnceHandler (event, fn) {
  var _target = target;
  return function onceHandler () {
    var res = fn.apply(null, arguments);
    if (res !== null) {
      _target.$off(event, onceHandler);
    }
  }
}

function updateComponentListeners (
  vm,
  listeners,
  oldListeners
) {
  target = vm;
  updateListeners(listeners, oldListeners || {}, add, remove$1, createOnceHandler, vm);
  target = undefined;
}

function eventsMixin (Vue) {
  var hookRE = /^hook:/;
  Vue.prototype.$on = function (event, fn) {
    var vm = this;
    if (Array.isArray(event)) {
      for (var i = 0, l = event.length; i < l; i++) {
        vm.$on(event[i], fn);
      }
    } else {
      (vm._events[event] || (vm._events[event] = [])).push(fn);
      // optimize hook:event cost by using a boolean flag marked at registration
      // instead of a hash lookup
      if (hookRE.test(event)) {
        vm._hasHookEvent = true;
      }
    }
    return vm
  };

  Vue.prototype.$once = function (event, fn) {
    var vm = this;
    function on () {
      vm.$off(event, on);
      fn.apply(vm, arguments);
    }
    on.fn = fn;
    vm.$on(event, on);
    return vm
  };

  Vue.prototype.$off = function (event, fn) {
    var vm = this;
    // all
    if (!arguments.length) {
      vm._events = Object.create(null);
      return vm
    }
    // array of events
    if (Array.isArray(event)) {
      for (var i$1 = 0, l = event.length; i$1 < l; i$1++) {
        vm.$off(event[i$1], fn);
      }
      return vm
    }
    // specific event
    var cbs = vm._events[event];
    if (!cbs) {
      return vm
    }
    if (!fn) {
      vm._events[event] = null;
      return vm
    }
    // specific handler
    var cb;
    var i = cbs.length;
    while (i--) {
      cb = cbs[i];
      if (cb === fn || cb.fn === fn) {
        cbs.splice(i, 1);
        break
      }
    }
    return vm
  };

  Vue.prototype.$emit = function (event) {
    var vm = this;
    var cbs = vm._events[event];
    if (cbs) {
      cbs = cbs.length > 1 ? toArray(cbs) : cbs;
      var args = toArray(arguments, 1);
      var info = "event handler for \"" + event + "\"";
      for (var i = 0, l = cbs.length; i < l; i++) {
        invokeWithErrorHandling(cbs[i], vm, args, vm, info);
      }
    }
    return vm
  };
}

/*  */

var activeInstance = null;

function setActiveInstance(vm) {
  var prevActiveInstance = activeInstance;
  activeInstance = vm;
  return function () {
    activeInstance = prevActiveInstance;
  }
}

function initLifecycle (vm) {
  var options = vm.$options;

  // locate first non-abstract parent
  var parent = options.parent;
  if (parent && !options.abstract) {
    while (parent.$options.abstract && parent.$parent) {
      parent = parent.$parent;
    }
    parent.$children.push(vm);
  }

  vm.$parent = parent;
  vm.$root = parent ? parent.$root : vm;

  vm.$children = [];
  vm.$refs = {};

  vm._watcher = null;
  vm._inactive = null;
  vm._directInactive = false;
  vm._isMounted = false;
  vm._isDestroyed = false;
  vm._isBeingDestroyed = false;
}

function lifecycleMixin (Vue) {
  Vue.prototype._update = function (vnode, hydrating) {
    var vm = this;
    var prevEl = vm.$el;
    var prevVnode = vm._vnode;
    var restoreActiveInstance = setActiveInstance(vm);
    vm._vnode = vnode;
    // Vue.prototype.__patch__ is injected in entry points
    // based on the rendering backend used.
    if (!prevVnode) {
      // initial render
      vm.$el = vm.__patch__(vm.$el, vnode, hydrating, false /* removeOnly */);
    } else {
      // updates
      vm.$el = vm.__patch__(prevVnode, vnode);
    }
    restoreActiveInstance();
    // update __vue__ reference
    if (prevEl) {
      prevEl.__vue__ = null;
    }
    if (vm.$el) {
      vm.$el.__vue__ = vm;
    }
    // if parent is an HOC, update its $el as well
    if (vm.$vnode && vm.$parent && vm.$vnode === vm.$parent._vnode) {
      vm.$parent.$el = vm.$el;
    }
    // updated hook is called by the scheduler to ensure that children are
    // updated in a parent's updated hook.
  };

  Vue.prototype.$forceUpdate = function () {
    var vm = this;
    if (vm._watcher) {
      vm._watcher.update();
    }
  };

  Vue.prototype.$destroy = function () {
    var vm = this;
    if (vm._isBeingDestroyed) {
      return
    }
    callHook(vm, 'beforeDestroy');
    vm._isBeingDestroyed = true;
    // remove self from parent
    var parent = vm.$parent;
    if (parent && !parent._isBeingDestroyed && !vm.$options.abstract) {
      remove(parent.$children, vm);
    }
    // teardown watchers
    if (vm._watcher) {
      vm._watcher.teardown();
    }
    var i = vm._watchers.length;
    while (i--) {
      vm._watchers[i].teardown();
    }
    // remove reference from data ob
    // frozen object may not have observer.
    if (vm._data.__ob__) {
      vm._data.__ob__.vmCount--;
    }
    // call the last hook...
    vm._isDestroyed = true;
    // invoke destroy hooks on current rendered tree
    vm.__patch__(vm._vnode, null);
    // fire destroyed hook
    callHook(vm, 'destroyed');
    // turn off all instance listeners.
    vm.$off();
    // remove __vue__ reference
    if (vm.$el) {
      vm.$el.__vue__ = null;
    }
    // release circular reference (#6759)
    if (vm.$vnode) {
      vm.$vnode.parent = null;
    }
  };
}

function mountComponent (
  vm,
  el,
  hydrating
) {
  vm.$el = el;
  if (!vm.$options.render) {
    vm.$options.render = createEmptyVNode;
  }
  callHook(vm, 'beforeMount');

  var updateComponent;
  /* istanbul ignore if */
  {
    updateComponent = function () {
      vm._update(vm._render(), hydrating);
    };
  }

  // we set this to vm._watcher inside the watcher's constructor
  // since the watcher's initial patch may call $forceUpdate (e.g. inside child
  // component's mounted hook), which relies on vm._watcher being already defined
  new Watcher(vm, updateComponent, noop, {
    before: function before () {
      if (vm._isMounted && !vm._isDestroyed) {
        callHook(vm, 'beforeUpdate');
      }
    }
  }, true /* isRenderWatcher */);
  hydrating = false;

  // manually mounted instance, call mounted on self
  // mounted is called for render-created child components in its inserted hook
  if (vm.$vnode == null) {
    vm._isMounted = true;
    callHook(vm, 'mounted');
  }
  return vm
}

function updateChildComponent (
  vm,
  propsData,
  listeners,
  parentVnode,
  renderChildren
) {

  // determine whether component has slot children
  // we need to do this before overwriting $options._renderChildren.

  // check if there are dynamic scopedSlots (hand-written or compiled but with
  // dynamic slot names). Static scoped slots compiled from template has the
  // "$stable" marker.
  var newScopedSlots = parentVnode.data.scopedSlots;
  var oldScopedSlots = vm.$scopedSlots;
  var hasDynamicScopedSlot = !!(
    (newScopedSlots && !newScopedSlots.$stable) ||
    (oldScopedSlots !== emptyObject && !oldScopedSlots.$stable) ||
    (newScopedSlots && vm.$scopedSlots.$key !== newScopedSlots.$key)
  );

  // Any static slot children from the parent may have changed during parent's
  // update. Dynamic scoped slots may also have changed. In such cases, a forced
  // update is necessary to ensure correctness.
  var needsForceUpdate = !!(
    renderChildren ||               // has new static slots
    vm.$options._renderChildren ||  // has old static slots
    hasDynamicScopedSlot
  );

  vm.$options._parentVnode = parentVnode;
  vm.$vnode = parentVnode; // update vm's placeholder node without re-render

  if (vm._vnode) { // update child tree's parent
    vm._vnode.parent = parentVnode;
  }
  vm.$options._renderChildren = renderChildren;

  // update $attrs and $listeners hash
  // these are also reactive so they may trigger child update if the child
  // used them during render
  vm.$attrs = parentVnode.data.attrs || emptyObject;
  vm.$listeners = listeners || emptyObject;

  // update props
  if (propsData && vm.$options.props) {
    toggleObserving(false);
    var props = vm._props;
    var propKeys = vm.$options._propKeys || [];
    for (var i = 0; i < propKeys.length; i++) {
      var key = propKeys[i];
      var propOptions = vm.$options.props; // wtf flow?
      props[key] = validateProp(key, propOptions, propsData, vm);
    }
    toggleObserving(true);
    // keep a copy of raw propsData
    vm.$options.propsData = propsData;
  }

  // update listeners
  listeners = listeners || emptyObject;
  var oldListeners = vm.$options._parentListeners;
  vm.$options._parentListeners = listeners;
  updateComponentListeners(vm, listeners, oldListeners);

  // resolve slots + force update if has children
  if (needsForceUpdate) {
    vm.$slots = resolveSlots(renderChildren, parentVnode.context);
    vm.$forceUpdate();
  }
}

function isInInactiveTree (vm) {
  while (vm && (vm = vm.$parent)) {
    if (vm._inactive) { return true }
  }
  return false
}

function activateChildComponent (vm, direct) {
  if (direct) {
    vm._directInactive = false;
    if (isInInactiveTree(vm)) {
      return
    }
  } else if (vm._directInactive) {
    return
  }
  if (vm._inactive || vm._inactive === null) {
    vm._inactive = false;
    for (var i = 0; i < vm.$children.length; i++) {
      activateChildComponent(vm.$children[i]);
    }
    callHook(vm, 'activated');
  }
}

function deactivateChildComponent (vm, direct) {
  if (direct) {
    vm._directInactive = true;
    if (isInInactiveTree(vm)) {
      return
    }
  }
  if (!vm._inactive) {
    vm._inactive = true;
    for (var i = 0; i < vm.$children.length; i++) {
      deactivateChildComponent(vm.$children[i]);
    }
    callHook(vm, 'deactivated');
  }
}

function callHook (vm, hook) {
  // #7573 disable dep collection when invoking lifecycle hooks
  pushTarget();
  var handlers = vm.$options[hook];
  var info = hook + " hook";
  if (handlers) {
    for (var i = 0, j = handlers.length; i < j; i++) {
      invokeWithErrorHandling(handlers[i], vm, null, vm, info);
    }
  }
  if (vm._hasHookEvent) {
    vm.$emit('hook:' + hook);
  }
  popTarget();
}

var queue = [];
var activatedChildren = [];
var has = {};
var waiting = false;
var flushing = false;
var index = 0;

/**
 * Reset the scheduler's state.
 */
function resetSchedulerState () {
  index = queue.length = activatedChildren.length = 0;
  has = {};
  waiting = flushing = false;
}

// Async edge case #6566 requires saving the timestamp when event listeners are
// attached. However, calling performance.now() has a perf overhead especially
// if the page has thousands of event listeners. Instead, we take a timestamp
// every time the scheduler flushes and use that for all event listeners
// attached during that flush.
var currentFlushTimestamp = 0;

// Async edge case fix requires storing an event listener's attach timestamp.
var getNow = Date.now;

// Determine what event timestamp the browser is using. Annoyingly, the
// timestamp can either be hi-res (relative to page load) or low-res
// (relative to UNIX epoch), so in order to compare time we have to use the
// same timestamp type when saving the flush timestamp.
// All IE versions use low-res event timestamps, and have problematic clock
// implementations (#9632)
if (inBrowser && !isIE) {
  var performance = window.performance;
  if (
    performance &&
    typeof performance.now === 'function' &&
    getNow() > document.createEvent('Event').timeStamp
  ) {
    // if the event timestamp, although evaluated AFTER the Date.now(), is
    // smaller than it, it means the event is using a hi-res timestamp,
    // and we need to use the hi-res version for event listener timestamps as
    // well.
    getNow = function () { return performance.now(); };
  }
}

/**
 * Flush both queues and run the watchers.
 */
function flushSchedulerQueue () {
  currentFlushTimestamp = getNow();
  flushing = true;
  var watcher, id;

  // Sort queue before flush.
  // This ensures that:
  // 1. Components are updated from parent to child. (because parent is always
  //    created before the child)
  // 2. A component's user watchers are run before its render watcher (because
  //    user watchers are created before the render watcher)
  // 3. If a component is destroyed during a parent component's watcher run,
  //    its watchers can be skipped.
  queue.sort(function (a, b) { return a.id - b.id; });

  // do not cache length because more watchers might be pushed
  // as we run existing watchers
  for (index = 0; index < queue.length; index++) {
    watcher = queue[index];
    if (watcher.before) {
      watcher.before();
    }
    id = watcher.id;
    has[id] = null;
    watcher.run();
  }

  // keep copies of post queues before resetting state
  var activatedQueue = activatedChildren.slice();
  var updatedQueue = queue.slice();

  resetSchedulerState();

  // call component updated and activated hooks
  callActivatedHooks(activatedQueue);
  callUpdatedHooks(updatedQueue);

  // devtool hook
  /* istanbul ignore if */
  if (devtools && config.devtools) {
    devtools.emit('flush');
  }
}

function callUpdatedHooks (queue) {
  var i = queue.length;
  while (i--) {
    var watcher = queue[i];
    var vm = watcher.vm;
    if (vm._watcher === watcher && vm._isMounted && !vm._isDestroyed) {
      callHook(vm, 'updated');
    }
  }
}

/**
 * Queue a kept-alive component that was activated during patch.
 * The queue will be processed after the entire tree has been patched.
 */
function queueActivatedComponent (vm) {
  // setting _inactive to false here so that a render function can
  // rely on checking whether it's in an inactive tree (e.g. router-view)
  vm._inactive = false;
  activatedChildren.push(vm);
}

function callActivatedHooks (queue) {
  for (var i = 0; i < queue.length; i++) {
    queue[i]._inactive = true;
    activateChildComponent(queue[i], true /* true */);
  }
}

/**
 * Push a watcher into the watcher queue.
 * Jobs with duplicate IDs will be skipped unless it's
 * pushed when the queue is being flushed.
 */
function queueWatcher (watcher) {
  var id = watcher.id;
  if (has[id] == null) {
    has[id] = true;
    if (!flushing) {
      queue.push(watcher);
    } else {
      // if already flushing, splice the watcher based on its id
      // if already past its id, it will be run next immediately.
      var i = queue.length - 1;
      while (i > index && queue[i].id > watcher.id) {
        i--;
      }
      queue.splice(i + 1, 0, watcher);
    }
    // queue the flush
    if (!waiting) {
      waiting = true;
      nextTick(flushSchedulerQueue);
    }
  }
}

/*  */



var uid$2 = 0;

/**
 * A watcher parses an expression, collects dependencies,
 * and fires callback when the expression value changes.
 * This is used for both the $watch() api and directives.
 */
var Watcher = function Watcher (
  vm,
  expOrFn,
  cb,
  options,
  isRenderWatcher
) {
  this.vm = vm;
  if (isRenderWatcher) {
    vm._watcher = this;
  }
  vm._watchers.push(this);
  // options
  if (options) {
    this.deep = !!options.deep;
    this.user = !!options.user;
    this.lazy = !!options.lazy;
    this.sync = !!options.sync;
    this.before = options.before;
  } else {
    this.deep = this.user = this.lazy = this.sync = false;
  }
  this.cb = cb;
  this.id = ++uid$2; // uid for batching
  this.active = true;
  this.dirty = this.lazy; // for lazy watchers
  this.deps = [];
  this.newDeps = [];
  this.depIds = new _Set();
  this.newDepIds = new _Set();
  this.expression = '';
  // parse expression for getter
  if (typeof expOrFn === 'function') {
    this.getter = expOrFn;
  } else {
    this.getter = parsePath(expOrFn);
    if (!this.getter) {
      this.getter = noop;
    }
  }
  this.value = this.lazy
    ? undefined
    : this.get();
};

/**
 * Evaluate the getter, and re-collect dependencies.
 */
Watcher.prototype.get = function get () {
  pushTarget(this);
  var value;
  var vm = this.vm;
  try {
    value = this.getter.call(vm, vm);
  } catch (e) {
    if (this.user) {
      handleError(e, vm, ("getter for watcher \"" + (this.expression) + "\""));
    } else {
      throw e
    }
  } finally {
    // "touch" every property so they are all tracked as
    // dependencies for deep watching
    if (this.deep) {
      traverse(value);
    }
    popTarget();
    this.cleanupDeps();
  }
  return value
};

/**
 * Add a dependency to this directive.
 */
Watcher.prototype.addDep = function addDep (dep) {
  var id = dep.id;
  if (!this.newDepIds.has(id)) {
    this.newDepIds.add(id);
    this.newDeps.push(dep);
    if (!this.depIds.has(id)) {
      dep.addSub(this);
    }
  }
};

/**
 * Clean up for dependency collection.
 */
Watcher.prototype.cleanupDeps = function cleanupDeps () {
  var i = this.deps.length;
  while (i--) {
    var dep = this.deps[i];
    if (!this.newDepIds.has(dep.id)) {
      dep.removeSub(this);
    }
  }
  var tmp = this.depIds;
  this.depIds = this.newDepIds;
  this.newDepIds = tmp;
  this.newDepIds.clear();
  tmp = this.deps;
  this.deps = this.newDeps;
  this.newDeps = tmp;
  this.newDeps.length = 0;
};

/**
 * Subscriber interface.
 * Will be called when a dependency changes.
 */
Watcher.prototype.update = function update () {
  /* istanbul ignore else */
  if (this.lazy) {
    this.dirty = true;
  } else if (this.sync) {
    this.run();
  } else {
    queueWatcher(this);
  }
};

/**
 * Scheduler job interface.
 * Will be called by the scheduler.
 */
Watcher.prototype.run = function run () {
  if (this.active) {
    var value = this.get();
    if (
      value !== this.value ||
      // Deep watchers and watchers on Object/Arrays should fire even
      // when the value is the same, because the value may
      // have mutated.
      isObject(value) ||
      this.deep
    ) {
      // set new value
      var oldValue = this.value;
      this.value = value;
      if (this.user) {
        try {
          this.cb.call(this.vm, value, oldValue);
        } catch (e) {
          handleError(e, this.vm, ("callback for watcher \"" + (this.expression) + "\""));
        }
      } else {
        this.cb.call(this.vm, value, oldValue);
      }
    }
  }
};

/**
 * Evaluate the value of the watcher.
 * This only gets called for lazy watchers.
 */
Watcher.prototype.evaluate = function evaluate () {
  this.value = this.get();
  this.dirty = false;
};

/**
 * Depend on all deps collected by this watcher.
 */
Watcher.prototype.depend = function depend () {
  var i = this.deps.length;
  while (i--) {
    this.deps[i].depend();
  }
};

/**
 * Remove self from all dependencies' subscriber list.
 */
Watcher.prototype.teardown = function teardown () {
  if (this.active) {
    // remove self from vm's watcher list
    // this is a somewhat expensive operation so we skip it
    // if the vm is being destroyed.
    if (!this.vm._isBeingDestroyed) {
      remove(this.vm._watchers, this);
    }
    var i = this.deps.length;
    while (i--) {
      this.deps[i].removeSub(this);
    }
    this.active = false;
  }
};

/*  */

var sharedPropertyDefinition = {
  enumerable: true,
  configurable: true,
  get: noop,
  set: noop
};

function proxy (target, sourceKey, key) {
  sharedPropertyDefinition.get = function proxyGetter () {
    return this[sourceKey][key]
  };
  sharedPropertyDefinition.set = function proxySetter (val) {
    this[sourceKey][key] = val;
  };
  Object.defineProperty(target, key, sharedPropertyDefinition);
}

function initState (vm) {
  vm._watchers = [];
  var opts = vm.$options;
  if (opts.props) { initProps(vm, opts.props); }
  if (opts.methods) { initMethods(vm, opts.methods); }
  if (opts.data) {
    initData(vm);
  } else {
    observe(vm._data = {}, true /* asRootData */);
  }
  if (opts.computed) { initComputed(vm, opts.computed); }
  if (opts.watch && opts.watch !== nativeWatch) {
    initWatch(vm, opts.watch);
  }
}

function initProps (vm, propsOptions) {
  var propsData = vm.$options.propsData || {};
  var props = vm._props = {};
  // cache prop keys so that future props updates can iterate using Array
  // instead of dynamic object key enumeration.
  var keys = vm.$options._propKeys = [];
  var isRoot = !vm.$parent;
  // root instance props should be converted
  if (!isRoot) {
    toggleObserving(false);
  }
  var loop = function ( key ) {
    keys.push(key);
    var value = validateProp(key, propsOptions, propsData, vm);
    /* istanbul ignore else */
    {
      defineReactive$$1(props, key, value);
    }
    // static props are already proxied on the component's prototype
    // during Vue.extend(). We only need to proxy props defined at
    // instantiation here.
    if (!(key in vm)) {
      proxy(vm, "_props", key);
    }
  };

  for (var key in propsOptions) loop( key );
  toggleObserving(true);
}

function initData (vm) {
  var data = vm.$options.data;
  data = vm._data = typeof data === 'function'
    ? getData(data, vm)
    : data || {};
  if (!isPlainObject(data)) {
    data = {};
  }
  // proxy data on instance
  var keys = Object.keys(data);
  var props = vm.$options.props;
  vm.$options.methods;
  var i = keys.length;
  while (i--) {
    var key = keys[i];
    if (props && hasOwn(props, key)) ; else if (!isReserved(key)) {
      proxy(vm, "_data", key);
    }
  }
  // observe data
  observe(data, true /* asRootData */);
}

function getData (data, vm) {
  // #7573 disable dep collection when invoking data getters
  pushTarget();
  try {
    return data.call(vm, vm)
  } catch (e) {
    handleError(e, vm, "data()");
    return {}
  } finally {
    popTarget();
  }
}

var computedWatcherOptions = { lazy: true };

function initComputed (vm, computed) {
  // $flow-disable-line
  var watchers = vm._computedWatchers = Object.create(null);
  // computed properties are just getters during SSR
  var isSSR = isServerRendering();

  for (var key in computed) {
    var userDef = computed[key];
    var getter = typeof userDef === 'function' ? userDef : userDef.get;

    if (!isSSR) {
      // create internal watcher for the computed property.
      watchers[key] = new Watcher(
        vm,
        getter || noop,
        noop,
        computedWatcherOptions
      );
    }

    // component-defined computed properties are already defined on the
    // component prototype. We only need to define computed properties defined
    // at instantiation here.
    if (!(key in vm)) {
      defineComputed(vm, key, userDef);
    }
  }
}

function defineComputed (
  target,
  key,
  userDef
) {
  var shouldCache = !isServerRendering();
  if (typeof userDef === 'function') {
    sharedPropertyDefinition.get = shouldCache
      ? createComputedGetter(key)
      : createGetterInvoker(userDef);
    sharedPropertyDefinition.set = noop;
  } else {
    sharedPropertyDefinition.get = userDef.get
      ? shouldCache && userDef.cache !== false
        ? createComputedGetter(key)
        : createGetterInvoker(userDef.get)
      : noop;
    sharedPropertyDefinition.set = userDef.set || noop;
  }
  Object.defineProperty(target, key, sharedPropertyDefinition);
}

function createComputedGetter (key) {
  return function computedGetter () {
    var watcher = this._computedWatchers && this._computedWatchers[key];
    if (watcher) {
      if (watcher.dirty) {
        watcher.evaluate();
      }
      if (Dep.target) {
        watcher.depend();
      }
      return watcher.value
    }
  }
}

function createGetterInvoker(fn) {
  return function computedGetter () {
    return fn.call(this, this)
  }
}

function initMethods (vm, methods) {
  vm.$options.props;
  for (var key in methods) {
    vm[key] = typeof methods[key] !== 'function' ? noop : bind(methods[key], vm);
  }
}

function initWatch (vm, watch) {
  for (var key in watch) {
    var handler = watch[key];
    if (Array.isArray(handler)) {
      for (var i = 0; i < handler.length; i++) {
        createWatcher(vm, key, handler[i]);
      }
    } else {
      createWatcher(vm, key, handler);
    }
  }
}

function createWatcher (
  vm,
  expOrFn,
  handler,
  options
) {
  if (isPlainObject(handler)) {
    options = handler;
    handler = handler.handler;
  }
  if (typeof handler === 'string') {
    handler = vm[handler];
  }
  return vm.$watch(expOrFn, handler, options)
}

function stateMixin (Vue) {
  // flow somehow has problems with directly declared definition object
  // when using Object.defineProperty, so we have to procedurally build up
  // the object here.
  var dataDef = {};
  dataDef.get = function () { return this._data };
  var propsDef = {};
  propsDef.get = function () { return this._props };
  Object.defineProperty(Vue.prototype, '$data', dataDef);
  Object.defineProperty(Vue.prototype, '$props', propsDef);

  Vue.prototype.$set = set;
  Vue.prototype.$delete = del;

  Vue.prototype.$watch = function (
    expOrFn,
    cb,
    options
  ) {
    var vm = this;
    if (isPlainObject(cb)) {
      return createWatcher(vm, expOrFn, cb, options)
    }
    options = options || {};
    options.user = true;
    var watcher = new Watcher(vm, expOrFn, cb, options);
    if (options.immediate) {
      try {
        cb.call(vm, watcher.value);
      } catch (error) {
        handleError(error, vm, ("callback for immediate watcher \"" + (watcher.expression) + "\""));
      }
    }
    return function unwatchFn () {
      watcher.teardown();
    }
  };
}

/*  */

var uid$3 = 0;

function initMixin (Vue) {
  Vue.prototype._init = function (options) {
    var vm = this;
    // a uid
    vm._uid = uid$3++;

    // a flag to avoid this being observed
    vm._isVue = true;
    // merge options
    if (options && options._isComponent) {
      // optimize internal component instantiation
      // since dynamic options merging is pretty slow, and none of the
      // internal component options needs special treatment.
      initInternalComponent(vm, options);
    } else {
      vm.$options = mergeOptions(
        resolveConstructorOptions(vm.constructor),
        options || {},
        vm
      );
    }
    /* istanbul ignore else */
    {
      vm._renderProxy = vm;
    }
    // expose real self
    vm._self = vm;
    initLifecycle(vm);
    initEvents(vm);
    initRender(vm);
    callHook(vm, 'beforeCreate');
    initInjections(vm); // resolve injections before data/props
    initState(vm);
    initProvide(vm); // resolve provide after data/props
    callHook(vm, 'created');

    if (vm.$options.el) {
      vm.$mount(vm.$options.el);
    }
  };
}

function initInternalComponent (vm, options) {
  var opts = vm.$options = Object.create(vm.constructor.options);
  // doing this because it's faster than dynamic enumeration.
  var parentVnode = options._parentVnode;
  opts.parent = options.parent;
  opts._parentVnode = parentVnode;

  var vnodeComponentOptions = parentVnode.componentOptions;
  opts.propsData = vnodeComponentOptions.propsData;
  opts._parentListeners = vnodeComponentOptions.listeners;
  opts._renderChildren = vnodeComponentOptions.children;
  opts._componentTag = vnodeComponentOptions.tag;

  if (options.render) {
    opts.render = options.render;
    opts.staticRenderFns = options.staticRenderFns;
  }
}

function resolveConstructorOptions (Ctor) {
  var options = Ctor.options;
  if (Ctor.super) {
    var superOptions = resolveConstructorOptions(Ctor.super);
    var cachedSuperOptions = Ctor.superOptions;
    if (superOptions !== cachedSuperOptions) {
      // super option changed,
      // need to resolve new options.
      Ctor.superOptions = superOptions;
      // check if there are any late-modified/attached options (#4976)
      var modifiedOptions = resolveModifiedOptions(Ctor);
      // update base extend options
      if (modifiedOptions) {
        extend(Ctor.extendOptions, modifiedOptions);
      }
      options = Ctor.options = mergeOptions(superOptions, Ctor.extendOptions);
      if (options.name) {
        options.components[options.name] = Ctor;
      }
    }
  }
  return options
}

function resolveModifiedOptions (Ctor) {
  var modified;
  var latest = Ctor.options;
  var sealed = Ctor.sealedOptions;
  for (var key in latest) {
    if (latest[key] !== sealed[key]) {
      if (!modified) { modified = {}; }
      modified[key] = latest[key];
    }
  }
  return modified
}

function Vue (options) {
  this._init(options);
}

initMixin(Vue);
stateMixin(Vue);
eventsMixin(Vue);
lifecycleMixin(Vue);
renderMixin(Vue);

/*  */

function initUse (Vue) {
  Vue.use = function (plugin) {
    var installedPlugins = (this._installedPlugins || (this._installedPlugins = []));
    if (installedPlugins.indexOf(plugin) > -1) {
      return this
    }

    // additional parameters
    var args = toArray(arguments, 1);
    args.unshift(this);
    if (typeof plugin.install === 'function') {
      plugin.install.apply(plugin, args);
    } else if (typeof plugin === 'function') {
      plugin.apply(null, args);
    }
    installedPlugins.push(plugin);
    return this
  };
}

/*  */

function initMixin$1 (Vue) {
  Vue.mixin = function (mixin) {
    this.options = mergeOptions(this.options, mixin);
    return this
  };
}

/*  */

function initExtend (Vue) {
  /**
   * Each instance constructor, including Vue, has a unique
   * cid. This enables us to create wrapped "child
   * constructors" for prototypal inheritance and cache them.
   */
  Vue.cid = 0;
  var cid = 1;

  /**
   * Class inheritance
   */
  Vue.extend = function (extendOptions) {
    extendOptions = extendOptions || {};
    var Super = this;
    var SuperId = Super.cid;
    var cachedCtors = extendOptions._Ctor || (extendOptions._Ctor = {});
    if (cachedCtors[SuperId]) {
      return cachedCtors[SuperId]
    }

    var name = extendOptions.name || Super.options.name;

    var Sub = function VueComponent (options) {
      this._init(options);
    };
    Sub.prototype = Object.create(Super.prototype);
    Sub.prototype.constructor = Sub;
    Sub.cid = cid++;
    Sub.options = mergeOptions(
      Super.options,
      extendOptions
    );
    Sub['super'] = Super;

    // For props and computed properties, we define the proxy getters on
    // the Vue instances at extension time, on the extended prototype. This
    // avoids Object.defineProperty calls for each instance created.
    if (Sub.options.props) {
      initProps$1(Sub);
    }
    if (Sub.options.computed) {
      initComputed$1(Sub);
    }

    // allow further extension/mixin/plugin usage
    Sub.extend = Super.extend;
    Sub.mixin = Super.mixin;
    Sub.use = Super.use;

    // create asset registers, so extended classes
    // can have their private assets too.
    ASSET_TYPES.forEach(function (type) {
      Sub[type] = Super[type];
    });
    // enable recursive self-lookup
    if (name) {
      Sub.options.components[name] = Sub;
    }

    // keep a reference to the super options at extension time.
    // later at instantiation we can check if Super's options have
    // been updated.
    Sub.superOptions = Super.options;
    Sub.extendOptions = extendOptions;
    Sub.sealedOptions = extend({}, Sub.options);

    // cache constructor
    cachedCtors[SuperId] = Sub;
    return Sub
  };
}

function initProps$1 (Comp) {
  var props = Comp.options.props;
  for (var key in props) {
    proxy(Comp.prototype, "_props", key);
  }
}

function initComputed$1 (Comp) {
  var computed = Comp.options.computed;
  for (var key in computed) {
    defineComputed(Comp.prototype, key, computed[key]);
  }
}

/*  */

function initAssetRegisters (Vue) {
  /**
   * Create asset registration methods.
   */
  ASSET_TYPES.forEach(function (type) {
    Vue[type] = function (
      id,
      definition
    ) {
      if (!definition) {
        return this.options[type + 's'][id]
      } else {
        if (type === 'component' && isPlainObject(definition)) {
          definition.name = definition.name || id;
          definition = this.options._base.extend(definition);
        }
        if (type === 'directive' && typeof definition === 'function') {
          definition = { bind: definition, update: definition };
        }
        this.options[type + 's'][id] = definition;
        return definition
      }
    };
  });
}

/*  */



function getComponentName (opts) {
  return opts && (opts.Ctor.options.name || opts.tag)
}

function matches (pattern, name) {
  if (Array.isArray(pattern)) {
    return pattern.indexOf(name) > -1
  } else if (typeof pattern === 'string') {
    return pattern.split(',').indexOf(name) > -1
  } else if (isRegExp(pattern)) {
    return pattern.test(name)
  }
  /* istanbul ignore next */
  return false
}

function pruneCache (keepAliveInstance, filter) {
  var cache = keepAliveInstance.cache;
  var keys = keepAliveInstance.keys;
  var _vnode = keepAliveInstance._vnode;
  for (var key in cache) {
    var cachedNode = cache[key];
    if (cachedNode) {
      var name = getComponentName(cachedNode.componentOptions);
      if (name && !filter(name)) {
        pruneCacheEntry(cache, key, keys, _vnode);
      }
    }
  }
}

function pruneCacheEntry (
  cache,
  key,
  keys,
  current
) {
  var cached$$1 = cache[key];
  if (cached$$1 && (!current || cached$$1.tag !== current.tag)) {
    cached$$1.componentInstance.$destroy();
  }
  cache[key] = null;
  remove(keys, key);
}

var patternTypes = [String, RegExp, Array];

var KeepAlive = {
  name: 'keep-alive',
  abstract: true,

  props: {
    include: patternTypes,
    exclude: patternTypes,
    max: [String, Number]
  },

  created: function created () {
    this.cache = Object.create(null);
    this.keys = [];
  },

  destroyed: function destroyed () {
    for (var key in this.cache) {
      pruneCacheEntry(this.cache, key, this.keys);
    }
  },

  mounted: function mounted () {
    var this$1 = this;

    this.$watch('include', function (val) {
      pruneCache(this$1, function (name) { return matches(val, name); });
    });
    this.$watch('exclude', function (val) {
      pruneCache(this$1, function (name) { return !matches(val, name); });
    });
  },

  render: function render () {
    var slot = this.$slots.default;
    var vnode = getFirstComponentChild(slot);
    var componentOptions = vnode && vnode.componentOptions;
    if (componentOptions) {
      // check pattern
      var name = getComponentName(componentOptions);
      var ref = this;
      var include = ref.include;
      var exclude = ref.exclude;
      if (
        // not included
        (include && (!name || !matches(include, name))) ||
        // excluded
        (exclude && name && matches(exclude, name))
      ) {
        return vnode
      }

      var ref$1 = this;
      var cache = ref$1.cache;
      var keys = ref$1.keys;
      var key = vnode.key == null
        // same constructor may get registered as different local components
        // so cid alone is not enough (#3269)
        ? componentOptions.Ctor.cid + (componentOptions.tag ? ("::" + (componentOptions.tag)) : '')
        : vnode.key;
      if (cache[key]) {
        vnode.componentInstance = cache[key].componentInstance;
        // make current key freshest
        remove(keys, key);
        keys.push(key);
      } else {
        cache[key] = vnode;
        keys.push(key);
        // prune oldest entry
        if (this.max && keys.length > parseInt(this.max)) {
          pruneCacheEntry(cache, keys[0], keys, this._vnode);
        }
      }

      vnode.data.keepAlive = true;
    }
    return vnode || (slot && slot[0])
  }
};

var builtInComponents = {
  KeepAlive: KeepAlive
};

/*  */

function initGlobalAPI (Vue) {
  // config
  var configDef = {};
  configDef.get = function () { return config; };
  Object.defineProperty(Vue, 'config', configDef);

  // exposed util methods.
  // NOTE: these are not considered part of the public API - avoid relying on
  // them unless you are aware of the risk.
  Vue.util = {
    warn: warn,
    extend: extend,
    mergeOptions: mergeOptions,
    defineReactive: defineReactive$$1
  };

  Vue.set = set;
  Vue.delete = del;
  Vue.nextTick = nextTick;

  // 2.6 explicit observable API
  Vue.observable = function (obj) {
    observe(obj);
    return obj
  };

  Vue.options = Object.create(null);
  ASSET_TYPES.forEach(function (type) {
    Vue.options[type + 's'] = Object.create(null);
  });

  // this is used to identify the "base" constructor to extend all plain-object
  // components with in Weex's multi-instance scenarios.
  Vue.options._base = Vue;

  extend(Vue.options.components, builtInComponents);

  initUse(Vue);
  initMixin$1(Vue);
  initExtend(Vue);
  initAssetRegisters(Vue);
}

initGlobalAPI(Vue);

Object.defineProperty(Vue.prototype, '$isServer', {
  get: isServerRendering
});

Object.defineProperty(Vue.prototype, '$ssrContext', {
  get: function get () {
    /* istanbul ignore next */
    return this.$vnode && this.$vnode.ssrContext
  }
});

// expose FunctionalRenderContext for ssr runtime helper installation
Object.defineProperty(Vue, 'FunctionalRenderContext', {
  value: FunctionalRenderContext
});

Vue.version = '2.6.12';

/*  */

// these are reserved for web because they are directly compiled away
// during template compilation
var isReservedAttr = makeMap('style,class');

// attributes that should be using props for binding
var acceptValue = makeMap('input,textarea,option,select,progress');
var mustUseProp = function (tag, type, attr) {
  return (
    (attr === 'value' && acceptValue(tag)) && type !== 'button' ||
    (attr === 'selected' && tag === 'option') ||
    (attr === 'checked' && tag === 'input') ||
    (attr === 'muted' && tag === 'video')
  )
};

var isEnumeratedAttr = makeMap('contenteditable,draggable,spellcheck');

var isValidContentEditableValue = makeMap('events,caret,typing,plaintext-only');

var convertEnumeratedValue = function (key, value) {
  return isFalsyAttrValue(value) || value === 'false'
    ? 'false'
    // allow arbitrary string value for contenteditable
    : key === 'contenteditable' && isValidContentEditableValue(value)
      ? value
      : 'true'
};

var isBooleanAttr = makeMap(
  'allowfullscreen,async,autofocus,autoplay,checked,compact,controls,declare,' +
  'default,defaultchecked,defaultmuted,defaultselected,defer,disabled,' +
  'enabled,formnovalidate,hidden,indeterminate,inert,ismap,itemscope,loop,multiple,' +
  'muted,nohref,noresize,noshade,novalidate,nowrap,open,pauseonexit,readonly,' +
  'required,reversed,scoped,seamless,selected,sortable,translate,' +
  'truespeed,typemustmatch,visible'
);

var xlinkNS = 'http://www.w3.org/1999/xlink';

var isXlink = function (name) {
  return name.charAt(5) === ':' && name.slice(0, 5) === 'xlink'
};

var getXlinkProp = function (name) {
  return isXlink(name) ? name.slice(6, name.length) : ''
};

var isFalsyAttrValue = function (val) {
  return val == null || val === false
};

/*  */

function genClassForVnode (vnode) {
  var data = vnode.data;
  var parentNode = vnode;
  var childNode = vnode;
  while (isDef(childNode.componentInstance)) {
    childNode = childNode.componentInstance._vnode;
    if (childNode && childNode.data) {
      data = mergeClassData(childNode.data, data);
    }
  }
  while (isDef(parentNode = parentNode.parent)) {
    if (parentNode && parentNode.data) {
      data = mergeClassData(data, parentNode.data);
    }
  }
  return renderClass(data.staticClass, data.class)
}

function mergeClassData (child, parent) {
  return {
    staticClass: concat(child.staticClass, parent.staticClass),
    class: isDef(child.class)
      ? [child.class, parent.class]
      : parent.class
  }
}

function renderClass (
  staticClass,
  dynamicClass
) {
  if (isDef(staticClass) || isDef(dynamicClass)) {
    return concat(staticClass, stringifyClass(dynamicClass))
  }
  /* istanbul ignore next */
  return ''
}

function concat (a, b) {
  return a ? b ? (a + ' ' + b) : a : (b || '')
}

function stringifyClass (value) {
  if (Array.isArray(value)) {
    return stringifyArray(value)
  }
  if (isObject(value)) {
    return stringifyObject(value)
  }
  if (typeof value === 'string') {
    return value
  }
  /* istanbul ignore next */
  return ''
}

function stringifyArray (value) {
  var res = '';
  var stringified;
  for (var i = 0, l = value.length; i < l; i++) {
    if (isDef(stringified = stringifyClass(value[i])) && stringified !== '') {
      if (res) { res += ' '; }
      res += stringified;
    }
  }
  return res
}

function stringifyObject (value) {
  var res = '';
  for (var key in value) {
    if (value[key]) {
      if (res) { res += ' '; }
      res += key;
    }
  }
  return res
}

/*  */

var namespaceMap = {
  svg: 'http://www.w3.org/2000/svg',
  math: 'http://www.w3.org/1998/Math/MathML'
};

var isHTMLTag = makeMap(
  'html,body,base,head,link,meta,style,title,' +
  'address,article,aside,footer,header,h1,h2,h3,h4,h5,h6,hgroup,nav,section,' +
  'div,dd,dl,dt,figcaption,figure,picture,hr,img,li,main,ol,p,pre,ul,' +
  'a,b,abbr,bdi,bdo,br,cite,code,data,dfn,em,i,kbd,mark,q,rp,rt,rtc,ruby,' +
  's,samp,small,span,strong,sub,sup,time,u,var,wbr,area,audio,map,track,video,' +
  'embed,object,param,source,canvas,script,noscript,del,ins,' +
  'caption,col,colgroup,table,thead,tbody,td,th,tr,' +
  'button,datalist,fieldset,form,input,label,legend,meter,optgroup,option,' +
  'output,progress,select,textarea,' +
  'details,dialog,menu,menuitem,summary,' +
  'content,element,shadow,template,blockquote,iframe,tfoot'
);

// this map is intentionally selective, only covering SVG elements that may
// contain child elements.
var isSVG = makeMap(
  'svg,animate,circle,clippath,cursor,defs,desc,ellipse,filter,font-face,' +
  'foreignObject,g,glyph,image,line,marker,mask,missing-glyph,path,pattern,' +
  'polygon,polyline,rect,switch,symbol,text,textpath,tspan,use,view',
  true
);

var isReservedTag = function (tag) {
  return isHTMLTag(tag) || isSVG(tag)
};

function getTagNamespace (tag) {
  if (isSVG(tag)) {
    return 'svg'
  }
  // basic support for MathML
  // note it doesn't support other MathML elements being component roots
  if (tag === 'math') {
    return 'math'
  }
}

var unknownElementCache = Object.create(null);
function isUnknownElement (tag) {
  /* istanbul ignore if */
  if (!inBrowser) {
    return true
  }
  if (isReservedTag(tag)) {
    return false
  }
  tag = tag.toLowerCase();
  /* istanbul ignore if */
  if (unknownElementCache[tag] != null) {
    return unknownElementCache[tag]
  }
  var el = document.createElement(tag);
  if (tag.indexOf('-') > -1) {
    // http://stackoverflow.com/a/28210364/1070244
    return (unknownElementCache[tag] = (
      el.constructor === window.HTMLUnknownElement ||
      el.constructor === window.HTMLElement
    ))
  } else {
    return (unknownElementCache[tag] = /HTMLUnknownElement/.test(el.toString()))
  }
}

var isTextInputType = makeMap('text,number,password,search,email,tel,url');

/*  */

/**
 * Query an element selector if it's not an element already.
 */
function query (el) {
  if (typeof el === 'string') {
    var selected = document.querySelector(el);
    if (!selected) {
      return document.createElement('div')
    }
    return selected
  } else {
    return el
  }
}

/*  */

function createElement$1 (tagName, vnode) {
  var elm = document.createElement(tagName);
  if (tagName !== 'select') {
    return elm
  }
  // false or null will remove the attribute but undefined will not
  if (vnode.data && vnode.data.attrs && vnode.data.attrs.multiple !== undefined) {
    elm.setAttribute('multiple', 'multiple');
  }
  return elm
}

function createElementNS (namespace, tagName) {
  return document.createElementNS(namespaceMap[namespace], tagName)
}

function createTextNode (text) {
  return document.createTextNode(text)
}

function createComment (text) {
  return document.createComment(text)
}

function insertBefore (parentNode, newNode, referenceNode) {
  parentNode.insertBefore(newNode, referenceNode);
}

function removeChild (node, child) {
  node.removeChild(child);
}

function appendChild (node, child) {
  node.appendChild(child);
}

function parentNode (node) {
  return node.parentNode
}

function nextSibling (node) {
  return node.nextSibling
}

function tagName (node) {
  return node.tagName
}

function setTextContent (node, text) {
  node.textContent = text;
}

function setStyleScope (node, scopeId) {
  node.setAttribute(scopeId, '');
}

var nodeOps = /*#__PURE__*/Object.freeze({
  createElement: createElement$1,
  createElementNS: createElementNS,
  createTextNode: createTextNode,
  createComment: createComment,
  insertBefore: insertBefore,
  removeChild: removeChild,
  appendChild: appendChild,
  parentNode: parentNode,
  nextSibling: nextSibling,
  tagName: tagName,
  setTextContent: setTextContent,
  setStyleScope: setStyleScope
});

/*  */

var ref = {
  create: function create (_, vnode) {
    registerRef(vnode);
  },
  update: function update (oldVnode, vnode) {
    if (oldVnode.data.ref !== vnode.data.ref) {
      registerRef(oldVnode, true);
      registerRef(vnode);
    }
  },
  destroy: function destroy (vnode) {
    registerRef(vnode, true);
  }
};

function registerRef (vnode, isRemoval) {
  var key = vnode.data.ref;
  if (!isDef(key)) { return }

  var vm = vnode.context;
  var ref = vnode.componentInstance || vnode.elm;
  var refs = vm.$refs;
  if (isRemoval) {
    if (Array.isArray(refs[key])) {
      remove(refs[key], ref);
    } else if (refs[key] === ref) {
      refs[key] = undefined;
    }
  } else {
    if (vnode.data.refInFor) {
      if (!Array.isArray(refs[key])) {
        refs[key] = [ref];
      } else if (refs[key].indexOf(ref) < 0) {
        // $flow-disable-line
        refs[key].push(ref);
      }
    } else {
      refs[key] = ref;
    }
  }
}

/**
 * Virtual DOM patching algorithm based on Snabbdom by
 * Simon Friis Vindum (@paldepind)
 * Licensed under the MIT License
 * https://github.com/paldepind/snabbdom/blob/master/LICENSE
 *
 * modified by Evan You (@yyx990803)
 *
 * Not type-checking this because this file is perf-critical and the cost
 * of making flow understand it is not worth it.
 */

var emptyNode = new VNode('', {}, []);

var hooks = ['create', 'activate', 'update', 'remove', 'destroy'];

function sameVnode (a, b) {
  return (
    a.key === b.key && (
      (
        a.tag === b.tag &&
        a.isComment === b.isComment &&
        isDef(a.data) === isDef(b.data) &&
        sameInputType(a, b)
      ) || (
        isTrue(a.isAsyncPlaceholder) &&
        a.asyncFactory === b.asyncFactory &&
        isUndef(b.asyncFactory.error)
      )
    )
  )
}

function sameInputType (a, b) {
  if (a.tag !== 'input') { return true }
  var i;
  var typeA = isDef(i = a.data) && isDef(i = i.attrs) && i.type;
  var typeB = isDef(i = b.data) && isDef(i = i.attrs) && i.type;
  return typeA === typeB || isTextInputType(typeA) && isTextInputType(typeB)
}

function createKeyToOldIdx (children, beginIdx, endIdx) {
  var i, key;
  var map = {};
  for (i = beginIdx; i <= endIdx; ++i) {
    key = children[i].key;
    if (isDef(key)) { map[key] = i; }
  }
  return map
}

function createPatchFunction (backend) {
  var i, j;
  var cbs = {};

  var modules = backend.modules;
  var nodeOps = backend.nodeOps;

  for (i = 0; i < hooks.length; ++i) {
    cbs[hooks[i]] = [];
    for (j = 0; j < modules.length; ++j) {
      if (isDef(modules[j][hooks[i]])) {
        cbs[hooks[i]].push(modules[j][hooks[i]]);
      }
    }
  }

  function emptyNodeAt (elm) {
    return new VNode(nodeOps.tagName(elm).toLowerCase(), {}, [], undefined, elm)
  }

  function createRmCb (childElm, listeners) {
    function remove$$1 () {
      if (--remove$$1.listeners === 0) {
        removeNode(childElm);
      }
    }
    remove$$1.listeners = listeners;
    return remove$$1
  }

  function removeNode (el) {
    var parent = nodeOps.parentNode(el);
    // element may have already been removed due to v-html / v-text
    if (isDef(parent)) {
      nodeOps.removeChild(parent, el);
    }
  }

  function createElm (
    vnode,
    insertedVnodeQueue,
    parentElm,
    refElm,
    nested,
    ownerArray,
    index
  ) {
    if (isDef(vnode.elm) && isDef(ownerArray)) {
      // This vnode was used in a previous render!
      // now it's used as a new node, overwriting its elm would cause
      // potential patch errors down the road when it's used as an insertion
      // reference node. Instead, we clone the node on-demand before creating
      // associated DOM element for it.
      vnode = ownerArray[index] = cloneVNode(vnode);
    }

    vnode.isRootInsert = !nested; // for transition enter check
    if (createComponent(vnode, insertedVnodeQueue, parentElm, refElm)) {
      return
    }

    var data = vnode.data;
    var children = vnode.children;
    var tag = vnode.tag;
    if (isDef(tag)) {

      vnode.elm = vnode.ns
        ? nodeOps.createElementNS(vnode.ns, tag)
        : nodeOps.createElement(tag, vnode);
      setScope(vnode);

      /* istanbul ignore if */
      {
        createChildren(vnode, children, insertedVnodeQueue);
        if (isDef(data)) {
          invokeCreateHooks(vnode, insertedVnodeQueue);
        }
        insert(parentElm, vnode.elm, refElm);
      }
    } else if (isTrue(vnode.isComment)) {
      vnode.elm = nodeOps.createComment(vnode.text);
      insert(parentElm, vnode.elm, refElm);
    } else {
      vnode.elm = nodeOps.createTextNode(vnode.text);
      insert(parentElm, vnode.elm, refElm);
    }
  }

  function createComponent (vnode, insertedVnodeQueue, parentElm, refElm) {
    var i = vnode.data;
    if (isDef(i)) {
      var isReactivated = isDef(vnode.componentInstance) && i.keepAlive;
      if (isDef(i = i.hook) && isDef(i = i.init)) {
        i(vnode, false /* hydrating */);
      }
      // after calling the init hook, if the vnode is a child component
      // it should've created a child instance and mounted it. the child
      // component also has set the placeholder vnode's elm.
      // in that case we can just return the element and be done.
      if (isDef(vnode.componentInstance)) {
        initComponent(vnode, insertedVnodeQueue);
        insert(parentElm, vnode.elm, refElm);
        if (isTrue(isReactivated)) {
          reactivateComponent(vnode, insertedVnodeQueue, parentElm, refElm);
        }
        return true
      }
    }
  }

  function initComponent (vnode, insertedVnodeQueue) {
    if (isDef(vnode.data.pendingInsert)) {
      insertedVnodeQueue.push.apply(insertedVnodeQueue, vnode.data.pendingInsert);
      vnode.data.pendingInsert = null;
    }
    vnode.elm = vnode.componentInstance.$el;
    if (isPatchable(vnode)) {
      invokeCreateHooks(vnode, insertedVnodeQueue);
      setScope(vnode);
    } else {
      // empty component root.
      // skip all element-related modules except for ref (#3455)
      registerRef(vnode);
      // make sure to invoke the insert hook
      insertedVnodeQueue.push(vnode);
    }
  }

  function reactivateComponent (vnode, insertedVnodeQueue, parentElm, refElm) {
    var i;
    // hack for #4339: a reactivated component with inner transition
    // does not trigger because the inner node's created hooks are not called
    // again. It's not ideal to involve module-specific logic in here but
    // there doesn't seem to be a better way to do it.
    var innerNode = vnode;
    while (innerNode.componentInstance) {
      innerNode = innerNode.componentInstance._vnode;
      if (isDef(i = innerNode.data) && isDef(i = i.transition)) {
        for (i = 0; i < cbs.activate.length; ++i) {
          cbs.activate[i](emptyNode, innerNode);
        }
        insertedVnodeQueue.push(innerNode);
        break
      }
    }
    // unlike a newly created component,
    // a reactivated keep-alive component doesn't insert itself
    insert(parentElm, vnode.elm, refElm);
  }

  function insert (parent, elm, ref$$1) {
    if (isDef(parent)) {
      if (isDef(ref$$1)) {
        if (nodeOps.parentNode(ref$$1) === parent) {
          nodeOps.insertBefore(parent, elm, ref$$1);
        }
      } else {
        nodeOps.appendChild(parent, elm);
      }
    }
  }

  function createChildren (vnode, children, insertedVnodeQueue) {
    if (Array.isArray(children)) {
      for (var i = 0; i < children.length; ++i) {
        createElm(children[i], insertedVnodeQueue, vnode.elm, null, true, children, i);
      }
    } else if (isPrimitive$1(vnode.text)) {
      nodeOps.appendChild(vnode.elm, nodeOps.createTextNode(String(vnode.text)));
    }
  }

  function isPatchable (vnode) {
    while (vnode.componentInstance) {
      vnode = vnode.componentInstance._vnode;
    }
    return isDef(vnode.tag)
  }

  function invokeCreateHooks (vnode, insertedVnodeQueue) {
    for (var i$1 = 0; i$1 < cbs.create.length; ++i$1) {
      cbs.create[i$1](emptyNode, vnode);
    }
    i = vnode.data.hook; // Reuse variable
    if (isDef(i)) {
      if (isDef(i.create)) { i.create(emptyNode, vnode); }
      if (isDef(i.insert)) { insertedVnodeQueue.push(vnode); }
    }
  }

  // set scope id attribute for scoped CSS.
  // this is implemented as a special case to avoid the overhead
  // of going through the normal attribute patching process.
  function setScope (vnode) {
    var i;
    if (isDef(i = vnode.fnScopeId)) {
      nodeOps.setStyleScope(vnode.elm, i);
    } else {
      var ancestor = vnode;
      while (ancestor) {
        if (isDef(i = ancestor.context) && isDef(i = i.$options._scopeId)) {
          nodeOps.setStyleScope(vnode.elm, i);
        }
        ancestor = ancestor.parent;
      }
    }
    // for slot content they should also get the scopeId from the host instance.
    if (isDef(i = activeInstance) &&
      i !== vnode.context &&
      i !== vnode.fnContext &&
      isDef(i = i.$options._scopeId)
    ) {
      nodeOps.setStyleScope(vnode.elm, i);
    }
  }

  function addVnodes (parentElm, refElm, vnodes, startIdx, endIdx, insertedVnodeQueue) {
    for (; startIdx <= endIdx; ++startIdx) {
      createElm(vnodes[startIdx], insertedVnodeQueue, parentElm, refElm, false, vnodes, startIdx);
    }
  }

  function invokeDestroyHook (vnode) {
    var i, j;
    var data = vnode.data;
    if (isDef(data)) {
      if (isDef(i = data.hook) && isDef(i = i.destroy)) { i(vnode); }
      for (i = 0; i < cbs.destroy.length; ++i) { cbs.destroy[i](vnode); }
    }
    if (isDef(i = vnode.children)) {
      for (j = 0; j < vnode.children.length; ++j) {
        invokeDestroyHook(vnode.children[j]);
      }
    }
  }

  function removeVnodes (vnodes, startIdx, endIdx) {
    for (; startIdx <= endIdx; ++startIdx) {
      var ch = vnodes[startIdx];
      if (isDef(ch)) {
        if (isDef(ch.tag)) {
          removeAndInvokeRemoveHook(ch);
          invokeDestroyHook(ch);
        } else { // Text node
          removeNode(ch.elm);
        }
      }
    }
  }

  function removeAndInvokeRemoveHook (vnode, rm) {
    if (isDef(rm) || isDef(vnode.data)) {
      var i;
      var listeners = cbs.remove.length + 1;
      if (isDef(rm)) {
        // we have a recursively passed down rm callback
        // increase the listeners count
        rm.listeners += listeners;
      } else {
        // directly removing
        rm = createRmCb(vnode.elm, listeners);
      }
      // recursively invoke hooks on child component root node
      if (isDef(i = vnode.componentInstance) && isDef(i = i._vnode) && isDef(i.data)) {
        removeAndInvokeRemoveHook(i, rm);
      }
      for (i = 0; i < cbs.remove.length; ++i) {
        cbs.remove[i](vnode, rm);
      }
      if (isDef(i = vnode.data.hook) && isDef(i = i.remove)) {
        i(vnode, rm);
      } else {
        rm();
      }
    } else {
      removeNode(vnode.elm);
    }
  }

  function updateChildren (parentElm, oldCh, newCh, insertedVnodeQueue, removeOnly) {
    var oldStartIdx = 0;
    var newStartIdx = 0;
    var oldEndIdx = oldCh.length - 1;
    var oldStartVnode = oldCh[0];
    var oldEndVnode = oldCh[oldEndIdx];
    var newEndIdx = newCh.length - 1;
    var newStartVnode = newCh[0];
    var newEndVnode = newCh[newEndIdx];
    var oldKeyToIdx, idxInOld, vnodeToMove, refElm;

    // removeOnly is a special flag used only by <transition-group>
    // to ensure removed elements stay in correct relative positions
    // during leaving transitions
    var canMove = !removeOnly;

    while (oldStartIdx <= oldEndIdx && newStartIdx <= newEndIdx) {
      if (isUndef(oldStartVnode)) {
        oldStartVnode = oldCh[++oldStartIdx]; // Vnode has been moved left
      } else if (isUndef(oldEndVnode)) {
        oldEndVnode = oldCh[--oldEndIdx];
      } else if (sameVnode(oldStartVnode, newStartVnode)) {
        patchVnode(oldStartVnode, newStartVnode, insertedVnodeQueue, newCh, newStartIdx);
        oldStartVnode = oldCh[++oldStartIdx];
        newStartVnode = newCh[++newStartIdx];
      } else if (sameVnode(oldEndVnode, newEndVnode)) {
        patchVnode(oldEndVnode, newEndVnode, insertedVnodeQueue, newCh, newEndIdx);
        oldEndVnode = oldCh[--oldEndIdx];
        newEndVnode = newCh[--newEndIdx];
      } else if (sameVnode(oldStartVnode, newEndVnode)) { // Vnode moved right
        patchVnode(oldStartVnode, newEndVnode, insertedVnodeQueue, newCh, newEndIdx);
        canMove && nodeOps.insertBefore(parentElm, oldStartVnode.elm, nodeOps.nextSibling(oldEndVnode.elm));
        oldStartVnode = oldCh[++oldStartIdx];
        newEndVnode = newCh[--newEndIdx];
      } else if (sameVnode(oldEndVnode, newStartVnode)) { // Vnode moved left
        patchVnode(oldEndVnode, newStartVnode, insertedVnodeQueue, newCh, newStartIdx);
        canMove && nodeOps.insertBefore(parentElm, oldEndVnode.elm, oldStartVnode.elm);
        oldEndVnode = oldCh[--oldEndIdx];
        newStartVnode = newCh[++newStartIdx];
      } else {
        if (isUndef(oldKeyToIdx)) { oldKeyToIdx = createKeyToOldIdx(oldCh, oldStartIdx, oldEndIdx); }
        idxInOld = isDef(newStartVnode.key)
          ? oldKeyToIdx[newStartVnode.key]
          : findIdxInOld(newStartVnode, oldCh, oldStartIdx, oldEndIdx);
        if (isUndef(idxInOld)) { // New element
          createElm(newStartVnode, insertedVnodeQueue, parentElm, oldStartVnode.elm, false, newCh, newStartIdx);
        } else {
          vnodeToMove = oldCh[idxInOld];
          if (sameVnode(vnodeToMove, newStartVnode)) {
            patchVnode(vnodeToMove, newStartVnode, insertedVnodeQueue, newCh, newStartIdx);
            oldCh[idxInOld] = undefined;
            canMove && nodeOps.insertBefore(parentElm, vnodeToMove.elm, oldStartVnode.elm);
          } else {
            // same key but different element. treat as new element
            createElm(newStartVnode, insertedVnodeQueue, parentElm, oldStartVnode.elm, false, newCh, newStartIdx);
          }
        }
        newStartVnode = newCh[++newStartIdx];
      }
    }
    if (oldStartIdx > oldEndIdx) {
      refElm = isUndef(newCh[newEndIdx + 1]) ? null : newCh[newEndIdx + 1].elm;
      addVnodes(parentElm, refElm, newCh, newStartIdx, newEndIdx, insertedVnodeQueue);
    } else if (newStartIdx > newEndIdx) {
      removeVnodes(oldCh, oldStartIdx, oldEndIdx);
    }
  }

  function findIdxInOld (node, oldCh, start, end) {
    for (var i = start; i < end; i++) {
      var c = oldCh[i];
      if (isDef(c) && sameVnode(node, c)) { return i }
    }
  }

  function patchVnode (
    oldVnode,
    vnode,
    insertedVnodeQueue,
    ownerArray,
    index,
    removeOnly
  ) {
    if (oldVnode === vnode) {
      return
    }

    if (isDef(vnode.elm) && isDef(ownerArray)) {
      // clone reused vnode
      vnode = ownerArray[index] = cloneVNode(vnode);
    }

    var elm = vnode.elm = oldVnode.elm;

    if (isTrue(oldVnode.isAsyncPlaceholder)) {
      if (isDef(vnode.asyncFactory.resolved)) {
        hydrate(oldVnode.elm, vnode, insertedVnodeQueue);
      } else {
        vnode.isAsyncPlaceholder = true;
      }
      return
    }

    // reuse element for static trees.
    // note we only do this if the vnode is cloned -
    // if the new node is not cloned it means the render functions have been
    // reset by the hot-reload-api and we need to do a proper re-render.
    if (isTrue(vnode.isStatic) &&
      isTrue(oldVnode.isStatic) &&
      vnode.key === oldVnode.key &&
      (isTrue(vnode.isCloned) || isTrue(vnode.isOnce))
    ) {
      vnode.componentInstance = oldVnode.componentInstance;
      return
    }

    var i;
    var data = vnode.data;
    if (isDef(data) && isDef(i = data.hook) && isDef(i = i.prepatch)) {
      i(oldVnode, vnode);
    }

    var oldCh = oldVnode.children;
    var ch = vnode.children;
    if (isDef(data) && isPatchable(vnode)) {
      for (i = 0; i < cbs.update.length; ++i) { cbs.update[i](oldVnode, vnode); }
      if (isDef(i = data.hook) && isDef(i = i.update)) { i(oldVnode, vnode); }
    }
    if (isUndef(vnode.text)) {
      if (isDef(oldCh) && isDef(ch)) {
        if (oldCh !== ch) { updateChildren(elm, oldCh, ch, insertedVnodeQueue, removeOnly); }
      } else if (isDef(ch)) {
        if (isDef(oldVnode.text)) { nodeOps.setTextContent(elm, ''); }
        addVnodes(elm, null, ch, 0, ch.length - 1, insertedVnodeQueue);
      } else if (isDef(oldCh)) {
        removeVnodes(oldCh, 0, oldCh.length - 1);
      } else if (isDef(oldVnode.text)) {
        nodeOps.setTextContent(elm, '');
      }
    } else if (oldVnode.text !== vnode.text) {
      nodeOps.setTextContent(elm, vnode.text);
    }
    if (isDef(data)) {
      if (isDef(i = data.hook) && isDef(i = i.postpatch)) { i(oldVnode, vnode); }
    }
  }

  function invokeInsertHook (vnode, queue, initial) {
    // delay insert hooks for component root nodes, invoke them after the
    // element is really inserted
    if (isTrue(initial) && isDef(vnode.parent)) {
      vnode.parent.data.pendingInsert = queue;
    } else {
      for (var i = 0; i < queue.length; ++i) {
        queue[i].data.hook.insert(queue[i]);
      }
    }
  }
  // list of modules that can skip create hook during hydration because they
  // are already rendered on the client or has no need for initialization
  // Note: style is excluded because it relies on initial clone for future
  // deep updates (#7063).
  var isRenderedModule = makeMap('attrs,class,staticClass,staticStyle,key');

  // Note: this is a browser-only function so we can assume elms are DOM nodes.
  function hydrate (elm, vnode, insertedVnodeQueue, inVPre) {
    var i;
    var tag = vnode.tag;
    var data = vnode.data;
    var children = vnode.children;
    inVPre = inVPre || (data && data.pre);
    vnode.elm = elm;

    if (isTrue(vnode.isComment) && isDef(vnode.asyncFactory)) {
      vnode.isAsyncPlaceholder = true;
      return true
    }
    if (isDef(data)) {
      if (isDef(i = data.hook) && isDef(i = i.init)) { i(vnode, true /* hydrating */); }
      if (isDef(i = vnode.componentInstance)) {
        // child component. it should have hydrated its own tree.
        initComponent(vnode, insertedVnodeQueue);
        return true
      }
    }
    if (isDef(tag)) {
      if (isDef(children)) {
        // empty element, allow client to pick up and populate children
        if (!elm.hasChildNodes()) {
          createChildren(vnode, children, insertedVnodeQueue);
        } else {
          // v-html and domProps: innerHTML
          if (isDef(i = data) && isDef(i = i.domProps) && isDef(i = i.innerHTML)) {
            if (i !== elm.innerHTML) {
              return false
            }
          } else {
            // iterate and compare children lists
            var childrenMatch = true;
            var childNode = elm.firstChild;
            for (var i$1 = 0; i$1 < children.length; i$1++) {
              if (!childNode || !hydrate(childNode, children[i$1], insertedVnodeQueue, inVPre)) {
                childrenMatch = false;
                break
              }
              childNode = childNode.nextSibling;
            }
            // if childNode is not null, it means the actual childNodes list is
            // longer than the virtual children list.
            if (!childrenMatch || childNode) {
              return false
            }
          }
        }
      }
      if (isDef(data)) {
        var fullInvoke = false;
        for (var key in data) {
          if (!isRenderedModule(key)) {
            fullInvoke = true;
            invokeCreateHooks(vnode, insertedVnodeQueue);
            break
          }
        }
        if (!fullInvoke && data['class']) {
          // ensure collecting deps for deep class bindings for future updates
          traverse(data['class']);
        }
      }
    } else if (elm.data !== vnode.text) {
      elm.data = vnode.text;
    }
    return true
  }

  return function patch (oldVnode, vnode, hydrating, removeOnly) {
    if (isUndef(vnode)) {
      if (isDef(oldVnode)) { invokeDestroyHook(oldVnode); }
      return
    }

    var isInitialPatch = false;
    var insertedVnodeQueue = [];

    if (isUndef(oldVnode)) {
      // empty mount (likely as component), create new root element
      isInitialPatch = true;
      createElm(vnode, insertedVnodeQueue);
    } else {
      var isRealElement = isDef(oldVnode.nodeType);
      if (!isRealElement && sameVnode(oldVnode, vnode)) {
        // patch existing root node
        patchVnode(oldVnode, vnode, insertedVnodeQueue, null, null, removeOnly);
      } else {
        if (isRealElement) {
          // mounting to a real element
          // check if this is server-rendered content and if we can perform
          // a successful hydration.
          if (oldVnode.nodeType === 1 && oldVnode.hasAttribute(SSR_ATTR)) {
            oldVnode.removeAttribute(SSR_ATTR);
            hydrating = true;
          }
          if (isTrue(hydrating)) {
            if (hydrate(oldVnode, vnode, insertedVnodeQueue)) {
              invokeInsertHook(vnode, insertedVnodeQueue, true);
              return oldVnode
            }
          }
          // either not server-rendered, or hydration failed.
          // create an empty node and replace it
          oldVnode = emptyNodeAt(oldVnode);
        }

        // replacing existing element
        var oldElm = oldVnode.elm;
        var parentElm = nodeOps.parentNode(oldElm);

        // create new node
        createElm(
          vnode,
          insertedVnodeQueue,
          // extremely rare edge case: do not insert if old element is in a
          // leaving transition. Only happens when combining transition +
          // keep-alive + HOCs. (#4590)
          oldElm._leaveCb ? null : parentElm,
          nodeOps.nextSibling(oldElm)
        );

        // update parent placeholder node element, recursively
        if (isDef(vnode.parent)) {
          var ancestor = vnode.parent;
          var patchable = isPatchable(vnode);
          while (ancestor) {
            for (var i = 0; i < cbs.destroy.length; ++i) {
              cbs.destroy[i](ancestor);
            }
            ancestor.elm = vnode.elm;
            if (patchable) {
              for (var i$1 = 0; i$1 < cbs.create.length; ++i$1) {
                cbs.create[i$1](emptyNode, ancestor);
              }
              // #6513
              // invoke insert hooks that may have been merged by create hooks.
              // e.g. for directives that uses the "inserted" hook.
              var insert = ancestor.data.hook.insert;
              if (insert.merged) {
                // start at index 1 to avoid re-invoking component mounted hook
                for (var i$2 = 1; i$2 < insert.fns.length; i$2++) {
                  insert.fns[i$2]();
                }
              }
            } else {
              registerRef(ancestor);
            }
            ancestor = ancestor.parent;
          }
        }

        // destroy old node
        if (isDef(parentElm)) {
          removeVnodes([oldVnode], 0, 0);
        } else if (isDef(oldVnode.tag)) {
          invokeDestroyHook(oldVnode);
        }
      }
    }

    invokeInsertHook(vnode, insertedVnodeQueue, isInitialPatch);
    return vnode.elm
  }
}

/*  */

var directives = {
  create: updateDirectives,
  update: updateDirectives,
  destroy: function unbindDirectives (vnode) {
    updateDirectives(vnode, emptyNode);
  }
};

function updateDirectives (oldVnode, vnode) {
  if (oldVnode.data.directives || vnode.data.directives) {
    _update(oldVnode, vnode);
  }
}

function _update (oldVnode, vnode) {
  var isCreate = oldVnode === emptyNode;
  var isDestroy = vnode === emptyNode;
  var oldDirs = normalizeDirectives$1(oldVnode.data.directives, oldVnode.context);
  var newDirs = normalizeDirectives$1(vnode.data.directives, vnode.context);

  var dirsWithInsert = [];
  var dirsWithPostpatch = [];

  var key, oldDir, dir;
  for (key in newDirs) {
    oldDir = oldDirs[key];
    dir = newDirs[key];
    if (!oldDir) {
      // new directive, bind
      callHook$1(dir, 'bind', vnode, oldVnode);
      if (dir.def && dir.def.inserted) {
        dirsWithInsert.push(dir);
      }
    } else {
      // existing directive, update
      dir.oldValue = oldDir.value;
      dir.oldArg = oldDir.arg;
      callHook$1(dir, 'update', vnode, oldVnode);
      if (dir.def && dir.def.componentUpdated) {
        dirsWithPostpatch.push(dir);
      }
    }
  }

  if (dirsWithInsert.length) {
    var callInsert = function () {
      for (var i = 0; i < dirsWithInsert.length; i++) {
        callHook$1(dirsWithInsert[i], 'inserted', vnode, oldVnode);
      }
    };
    if (isCreate) {
      mergeVNodeHook(vnode, 'insert', callInsert);
    } else {
      callInsert();
    }
  }

  if (dirsWithPostpatch.length) {
    mergeVNodeHook(vnode, 'postpatch', function () {
      for (var i = 0; i < dirsWithPostpatch.length; i++) {
        callHook$1(dirsWithPostpatch[i], 'componentUpdated', vnode, oldVnode);
      }
    });
  }

  if (!isCreate) {
    for (key in oldDirs) {
      if (!newDirs[key]) {
        // no longer present, unbind
        callHook$1(oldDirs[key], 'unbind', oldVnode, oldVnode, isDestroy);
      }
    }
  }
}

var emptyModifiers = Object.create(null);

function normalizeDirectives$1 (
  dirs,
  vm
) {
  var res = Object.create(null);
  if (!dirs) {
    // $flow-disable-line
    return res
  }
  var i, dir;
  for (i = 0; i < dirs.length; i++) {
    dir = dirs[i];
    if (!dir.modifiers) {
      // $flow-disable-line
      dir.modifiers = emptyModifiers;
    }
    res[getRawDirName(dir)] = dir;
    dir.def = resolveAsset(vm.$options, 'directives', dir.name);
  }
  // $flow-disable-line
  return res
}

function getRawDirName (dir) {
  return dir.rawName || ((dir.name) + "." + (Object.keys(dir.modifiers || {}).join('.')))
}

function callHook$1 (dir, hook, vnode, oldVnode, isDestroy) {
  var fn = dir.def && dir.def[hook];
  if (fn) {
    try {
      fn(vnode.elm, dir, vnode, oldVnode, isDestroy);
    } catch (e) {
      handleError(e, vnode.context, ("directive " + (dir.name) + " " + hook + " hook"));
    }
  }
}

var baseModules = [
  ref,
  directives
];

/*  */

function updateAttrs (oldVnode, vnode) {
  var opts = vnode.componentOptions;
  if (isDef(opts) && opts.Ctor.options.inheritAttrs === false) {
    return
  }
  if (isUndef(oldVnode.data.attrs) && isUndef(vnode.data.attrs)) {
    return
  }
  var key, cur, old;
  var elm = vnode.elm;
  var oldAttrs = oldVnode.data.attrs || {};
  var attrs = vnode.data.attrs || {};
  // clone observed objects, as the user probably wants to mutate it
  if (isDef(attrs.__ob__)) {
    attrs = vnode.data.attrs = extend({}, attrs);
  }

  for (key in attrs) {
    cur = attrs[key];
    old = oldAttrs[key];
    if (old !== cur) {
      setAttr(elm, key, cur);
    }
  }
  // #4391: in IE9, setting type can reset value for input[type=radio]
  // #6666: IE/Edge forces progress value down to 1 before setting a max
  /* istanbul ignore if */
  if ((isIE || isEdge) && attrs.value !== oldAttrs.value) {
    setAttr(elm, 'value', attrs.value);
  }
  for (key in oldAttrs) {
    if (isUndef(attrs[key])) {
      if (isXlink(key)) {
        elm.removeAttributeNS(xlinkNS, getXlinkProp(key));
      } else if (!isEnumeratedAttr(key)) {
        elm.removeAttribute(key);
      }
    }
  }
}

function setAttr (el, key, value) {
  if (el.tagName.indexOf('-') > -1) {
    baseSetAttr(el, key, value);
  } else if (isBooleanAttr(key)) {
    // set attribute for blank value
    // e.g. <option disabled>Select one</option>
    if (isFalsyAttrValue(value)) {
      el.removeAttribute(key);
    } else {
      // technically allowfullscreen is a boolean attribute for <iframe>,
      // but Flash expects a value of "true" when used on <embed> tag
      value = key === 'allowfullscreen' && el.tagName === 'EMBED'
        ? 'true'
        : key;
      el.setAttribute(key, value);
    }
  } else if (isEnumeratedAttr(key)) {
    el.setAttribute(key, convertEnumeratedValue(key, value));
  } else if (isXlink(key)) {
    if (isFalsyAttrValue(value)) {
      el.removeAttributeNS(xlinkNS, getXlinkProp(key));
    } else {
      el.setAttributeNS(xlinkNS, key, value);
    }
  } else {
    baseSetAttr(el, key, value);
  }
}

function baseSetAttr (el, key, value) {
  if (isFalsyAttrValue(value)) {
    el.removeAttribute(key);
  } else {
    // #7138: IE10 & 11 fires input event when setting placeholder on
    // <textarea>... block the first input event and remove the blocker
    // immediately.
    /* istanbul ignore if */
    if (
      isIE && !isIE9 &&
      el.tagName === 'TEXTAREA' &&
      key === 'placeholder' && value !== '' && !el.__ieph
    ) {
      var blocker = function (e) {
        e.stopImmediatePropagation();
        el.removeEventListener('input', blocker);
      };
      el.addEventListener('input', blocker);
      // $flow-disable-line
      el.__ieph = true; /* IE placeholder patched */
    }
    el.setAttribute(key, value);
  }
}

var attrs = {
  create: updateAttrs,
  update: updateAttrs
};

/*  */

function updateClass (oldVnode, vnode) {
  var el = vnode.elm;
  var data = vnode.data;
  var oldData = oldVnode.data;
  if (
    isUndef(data.staticClass) &&
    isUndef(data.class) && (
      isUndef(oldData) || (
        isUndef(oldData.staticClass) &&
        isUndef(oldData.class)
      )
    )
  ) {
    return
  }

  var cls = genClassForVnode(vnode);

  // handle transition classes
  var transitionClass = el._transitionClasses;
  if (isDef(transitionClass)) {
    cls = concat(cls, stringifyClass(transitionClass));
  }

  // set the class
  if (cls !== el._prevClass) {
    el.setAttribute('class', cls);
    el._prevClass = cls;
  }
}

var klass = {
  create: updateClass,
  update: updateClass
};

/*  */

/*  */

/*  */

/*  */

// in some cases, the event used has to be determined at runtime
// so we used some reserved tokens during compile.
var RANGE_TOKEN = '__r';
var CHECKBOX_RADIO_TOKEN = '__c';

/*  */

// normalize v-model event tokens that can only be determined at runtime.
// it's important to place the event as the first in the array because
// the whole point is ensuring the v-model callback gets called before
// user-attached handlers.
function normalizeEvents (on) {
  /* istanbul ignore if */
  if (isDef(on[RANGE_TOKEN])) {
    // IE input[type=range] only supports `change` event
    var event = isIE ? 'change' : 'input';
    on[event] = [].concat(on[RANGE_TOKEN], on[event] || []);
    delete on[RANGE_TOKEN];
  }
  // This was originally intended to fix #4521 but no longer necessary
  // after 2.5. Keeping it for backwards compat with generated code from < 2.4
  /* istanbul ignore if */
  if (isDef(on[CHECKBOX_RADIO_TOKEN])) {
    on.change = [].concat(on[CHECKBOX_RADIO_TOKEN], on.change || []);
    delete on[CHECKBOX_RADIO_TOKEN];
  }
}

var target$1;

function createOnceHandler$1 (event, handler, capture) {
  var _target = target$1; // save current target element in closure
  return function onceHandler () {
    var res = handler.apply(null, arguments);
    if (res !== null) {
      remove$2(event, onceHandler, capture, _target);
    }
  }
}

// #9446: Firefox <= 53 (in particular, ESR 52) has incorrect Event.timeStamp
// implementation and does not fire microtasks in between event propagation, so
// safe to exclude.
var useMicrotaskFix = isUsingMicroTask && !(isFF && Number(isFF[1]) <= 53);

function add$1 (
  name,
  handler,
  capture,
  passive
) {
  // async edge case #6566: inner click event triggers patch, event handler
  // attached to outer element during patch, and triggered again. This
  // happens because browsers fire microtask ticks between event propagation.
  // the solution is simple: we save the timestamp when a handler is attached,
  // and the handler would only fire if the event passed to it was fired
  // AFTER it was attached.
  if (useMicrotaskFix) {
    var attachedTimestamp = currentFlushTimestamp;
    var original = handler;
    handler = original._wrapper = function (e) {
      if (
        // no bubbling, should always fire.
        // this is just a safety net in case event.timeStamp is unreliable in
        // certain weird environments...
        e.target === e.currentTarget ||
        // event is fired after handler attachment
        e.timeStamp >= attachedTimestamp ||
        // bail for environments that have buggy event.timeStamp implementations
        // #9462 iOS 9 bug: event.timeStamp is 0 after history.pushState
        // #9681 QtWebEngine event.timeStamp is negative value
        e.timeStamp <= 0 ||
        // #9448 bail if event is fired in another document in a multi-page
        // electron/nw.js app, since event.timeStamp will be using a different
        // starting reference
        e.target.ownerDocument !== document
      ) {
        return original.apply(this, arguments)
      }
    };
  }
  target$1.addEventListener(
    name,
    handler,
    supportsPassive
      ? { capture: capture, passive: passive }
      : capture
  );
}

function remove$2 (
  name,
  handler,
  capture,
  _target
) {
  (_target || target$1).removeEventListener(
    name,
    handler._wrapper || handler,
    capture
  );
}

function updateDOMListeners (oldVnode, vnode) {
  if (isUndef(oldVnode.data.on) && isUndef(vnode.data.on)) {
    return
  }
  var on = vnode.data.on || {};
  var oldOn = oldVnode.data.on || {};
  target$1 = vnode.elm;
  normalizeEvents(on);
  updateListeners(on, oldOn, add$1, remove$2, createOnceHandler$1, vnode.context);
  target$1 = undefined;
}

var events = {
  create: updateDOMListeners,
  update: updateDOMListeners
};

/*  */

var svgContainer;

function updateDOMProps (oldVnode, vnode) {
  if (isUndef(oldVnode.data.domProps) && isUndef(vnode.data.domProps)) {
    return
  }
  var key, cur;
  var elm = vnode.elm;
  var oldProps = oldVnode.data.domProps || {};
  var props = vnode.data.domProps || {};
  // clone observed objects, as the user probably wants to mutate it
  if (isDef(props.__ob__)) {
    props = vnode.data.domProps = extend({}, props);
  }

  for (key in oldProps) {
    if (!(key in props)) {
      elm[key] = '';
    }
  }

  for (key in props) {
    cur = props[key];
    // ignore children if the node has textContent or innerHTML,
    // as these will throw away existing DOM nodes and cause removal errors
    // on subsequent patches (#3360)
    if (key === 'textContent' || key === 'innerHTML') {
      if (vnode.children) { vnode.children.length = 0; }
      if (cur === oldProps[key]) { continue }
      // #6601 work around Chrome version <= 55 bug where single textNode
      // replaced by innerHTML/textContent retains its parentNode property
      if (elm.childNodes.length === 1) {
        elm.removeChild(elm.childNodes[0]);
      }
    }

    if (key === 'value' && elm.tagName !== 'PROGRESS') {
      // store value as _value as well since
      // non-string values will be stringified
      elm._value = cur;
      // avoid resetting cursor position when value is the same
      var strCur = isUndef(cur) ? '' : String(cur);
      if (shouldUpdateValue(elm, strCur)) {
        elm.value = strCur;
      }
    } else if (key === 'innerHTML' && isSVG(elm.tagName) && isUndef(elm.innerHTML)) {
      // IE doesn't support innerHTML for SVG elements
      svgContainer = svgContainer || document.createElement('div');
      svgContainer.innerHTML = "<svg>" + cur + "</svg>";
      var svg = svgContainer.firstChild;
      while (elm.firstChild) {
        elm.removeChild(elm.firstChild);
      }
      while (svg.firstChild) {
        elm.appendChild(svg.firstChild);
      }
    } else if (
      // skip the update if old and new VDOM state is the same.
      // `value` is handled separately because the DOM value may be temporarily
      // out of sync with VDOM state due to focus, composition and modifiers.
      // This  #4521 by skipping the unnecessary `checked` update.
      cur !== oldProps[key]
    ) {
      // some property updates can throw
      // e.g. `value` on <progress> w/ non-finite value
      try {
        elm[key] = cur;
      } catch (e) {}
    }
  }
}

// check platforms/web/util/attrs.js acceptValue


function shouldUpdateValue (elm, checkVal) {
  return (!elm.composing && (
    elm.tagName === 'OPTION' ||
    isNotInFocusAndDirty(elm, checkVal) ||
    isDirtyWithModifiers(elm, checkVal)
  ))
}

function isNotInFocusAndDirty (elm, checkVal) {
  // return true when textbox (.number and .trim) loses focus and its value is
  // not equal to the updated value
  var notInFocus = true;
  // #6157
  // work around IE bug when accessing document.activeElement in an iframe
  try { notInFocus = document.activeElement !== elm; } catch (e) {}
  return notInFocus && elm.value !== checkVal
}

function isDirtyWithModifiers (elm, newVal) {
  var value = elm.value;
  var modifiers = elm._vModifiers; // injected by v-model runtime
  if (isDef(modifiers)) {
    if (modifiers.number) {
      return toNumber(value) !== toNumber(newVal)
    }
    if (modifiers.trim) {
      return value.trim() !== newVal.trim()
    }
  }
  return value !== newVal
}

var domProps = {
  create: updateDOMProps,
  update: updateDOMProps
};

/*  */

var parseStyleText = cached(function (cssText) {
  var res = {};
  var listDelimiter = /;(?![^(]*\))/g;
  var propertyDelimiter = /:(.+)/;
  cssText.split(listDelimiter).forEach(function (item) {
    if (item) {
      var tmp = item.split(propertyDelimiter);
      tmp.length > 1 && (res[tmp[0].trim()] = tmp[1].trim());
    }
  });
  return res
});

// merge static and dynamic style data on the same vnode
function normalizeStyleData (data) {
  var style = normalizeStyleBinding(data.style);
  // static style is pre-processed into an object during compilation
  // and is always a fresh object, so it's safe to merge into it
  return data.staticStyle
    ? extend(data.staticStyle, style)
    : style
}

// normalize possible array / string values into Object
function normalizeStyleBinding (bindingStyle) {
  if (Array.isArray(bindingStyle)) {
    return toObject(bindingStyle)
  }
  if (typeof bindingStyle === 'string') {
    return parseStyleText(bindingStyle)
  }
  return bindingStyle
}

/**
 * parent component style should be after child's
 * so that parent component's style could override it
 */
function getStyle (vnode, checkChild) {
  var res = {};
  var styleData;

  if (checkChild) {
    var childNode = vnode;
    while (childNode.componentInstance) {
      childNode = childNode.componentInstance._vnode;
      if (
        childNode && childNode.data &&
        (styleData = normalizeStyleData(childNode.data))
      ) {
        extend(res, styleData);
      }
    }
  }

  if ((styleData = normalizeStyleData(vnode.data))) {
    extend(res, styleData);
  }

  var parentNode = vnode;
  while ((parentNode = parentNode.parent)) {
    if (parentNode.data && (styleData = normalizeStyleData(parentNode.data))) {
      extend(res, styleData);
    }
  }
  return res
}

/*  */

var cssVarRE = /^--/;
var importantRE = /\s*!important$/;
var setProp = function (el, name, val) {
  /* istanbul ignore if */
  if (cssVarRE.test(name)) {
    el.style.setProperty(name, val);
  } else if (importantRE.test(val)) {
    el.style.setProperty(hyphenate(name), val.replace(importantRE, ''), 'important');
  } else {
    var normalizedName = normalize(name);
    if (Array.isArray(val)) {
      // Support values array created by autoprefixer, e.g.
      // {display: ["-webkit-box", "-ms-flexbox", "flex"]}
      // Set them one by one, and the browser will only set those it can recognize
      for (var i = 0, len = val.length; i < len; i++) {
        el.style[normalizedName] = val[i];
      }
    } else {
      el.style[normalizedName] = val;
    }
  }
};

var vendorNames = ['Webkit', 'Moz', 'ms'];

var emptyStyle;
var normalize = cached(function (prop) {
  emptyStyle = emptyStyle || document.createElement('div').style;
  prop = camelize(prop);
  if (prop !== 'filter' && (prop in emptyStyle)) {
    return prop
  }
  var capName = prop.charAt(0).toUpperCase() + prop.slice(1);
  for (var i = 0; i < vendorNames.length; i++) {
    var name = vendorNames[i] + capName;
    if (name in emptyStyle) {
      return name
    }
  }
});

function updateStyle (oldVnode, vnode) {
  var data = vnode.data;
  var oldData = oldVnode.data;

  if (isUndef(data.staticStyle) && isUndef(data.style) &&
    isUndef(oldData.staticStyle) && isUndef(oldData.style)
  ) {
    return
  }

  var cur, name;
  var el = vnode.elm;
  var oldStaticStyle = oldData.staticStyle;
  var oldStyleBinding = oldData.normalizedStyle || oldData.style || {};

  // if static style exists, stylebinding already merged into it when doing normalizeStyleData
  var oldStyle = oldStaticStyle || oldStyleBinding;

  var style = normalizeStyleBinding(vnode.data.style) || {};

  // store normalized style under a different key for next diff
  // make sure to clone it if it's reactive, since the user likely wants
  // to mutate it.
  vnode.data.normalizedStyle = isDef(style.__ob__)
    ? extend({}, style)
    : style;

  var newStyle = getStyle(vnode, true);

  for (name in oldStyle) {
    if (isUndef(newStyle[name])) {
      setProp(el, name, '');
    }
  }
  for (name in newStyle) {
    cur = newStyle[name];
    if (cur !== oldStyle[name]) {
      // ie9 setting to null has no effect, must use empty string
      setProp(el, name, cur == null ? '' : cur);
    }
  }
}

var style = {
  create: updateStyle,
  update: updateStyle
};

/*  */

var whitespaceRE = /\s+/;

/**
 * Add class with compatibility for SVG since classList is not supported on
 * SVG elements in IE
 */
function addClass (el, cls) {
  /* istanbul ignore if */
  if (!cls || !(cls = cls.trim())) {
    return
  }

  /* istanbul ignore else */
  if (el.classList) {
    if (cls.indexOf(' ') > -1) {
      cls.split(whitespaceRE).forEach(function (c) { return el.classList.add(c); });
    } else {
      el.classList.add(cls);
    }
  } else {
    var cur = " " + (el.getAttribute('class') || '') + " ";
    if (cur.indexOf(' ' + cls + ' ') < 0) {
      el.setAttribute('class', (cur + cls).trim());
    }
  }
}

/**
 * Remove class with compatibility for SVG since classList is not supported on
 * SVG elements in IE
 */
function removeClass (el, cls) {
  /* istanbul ignore if */
  if (!cls || !(cls = cls.trim())) {
    return
  }

  /* istanbul ignore else */
  if (el.classList) {
    if (cls.indexOf(' ') > -1) {
      cls.split(whitespaceRE).forEach(function (c) { return el.classList.remove(c); });
    } else {
      el.classList.remove(cls);
    }
    if (!el.classList.length) {
      el.removeAttribute('class');
    }
  } else {
    var cur = " " + (el.getAttribute('class') || '') + " ";
    var tar = ' ' + cls + ' ';
    while (cur.indexOf(tar) >= 0) {
      cur = cur.replace(tar, ' ');
    }
    cur = cur.trim();
    if (cur) {
      el.setAttribute('class', cur);
    } else {
      el.removeAttribute('class');
    }
  }
}

/*  */

function resolveTransition (def$$1) {
  if (!def$$1) {
    return
  }
  /* istanbul ignore else */
  if (typeof def$$1 === 'object') {
    var res = {};
    if (def$$1.css !== false) {
      extend(res, autoCssTransition(def$$1.name || 'v'));
    }
    extend(res, def$$1);
    return res
  } else if (typeof def$$1 === 'string') {
    return autoCssTransition(def$$1)
  }
}

var autoCssTransition = cached(function (name) {
  return {
    enterClass: (name + "-enter"),
    enterToClass: (name + "-enter-to"),
    enterActiveClass: (name + "-enter-active"),
    leaveClass: (name + "-leave"),
    leaveToClass: (name + "-leave-to"),
    leaveActiveClass: (name + "-leave-active")
  }
});

var hasTransition = inBrowser && !isIE9;
var TRANSITION = 'transition';
var ANIMATION = 'animation';

// Transition property/event sniffing
var transitionProp = 'transition';
var transitionEndEvent = 'transitionend';
var animationProp = 'animation';
var animationEndEvent = 'animationend';
if (hasTransition) {
  /* istanbul ignore if */
  if (window.ontransitionend === undefined &&
    window.onwebkittransitionend !== undefined
  ) {
    transitionProp = 'WebkitTransition';
    transitionEndEvent = 'webkitTransitionEnd';
  }
  if (window.onanimationend === undefined &&
    window.onwebkitanimationend !== undefined
  ) {
    animationProp = 'WebkitAnimation';
    animationEndEvent = 'webkitAnimationEnd';
  }
}

// binding to window is necessary to make hot reload work in IE in strict mode
var raf = inBrowser
  ? window.requestAnimationFrame
    ? window.requestAnimationFrame.bind(window)
    : setTimeout
  : /* istanbul ignore next */ function (fn) { return fn(); };

function nextFrame (fn) {
  raf(function () {
    raf(fn);
  });
}

function addTransitionClass (el, cls) {
  var transitionClasses = el._transitionClasses || (el._transitionClasses = []);
  if (transitionClasses.indexOf(cls) < 0) {
    transitionClasses.push(cls);
    addClass(el, cls);
  }
}

function removeTransitionClass (el, cls) {
  if (el._transitionClasses) {
    remove(el._transitionClasses, cls);
  }
  removeClass(el, cls);
}

function whenTransitionEnds (
  el,
  expectedType,
  cb
) {
  var ref = getTransitionInfo(el, expectedType);
  var type = ref.type;
  var timeout = ref.timeout;
  var propCount = ref.propCount;
  if (!type) { return cb() }
  var event = type === TRANSITION ? transitionEndEvent : animationEndEvent;
  var ended = 0;
  var end = function () {
    el.removeEventListener(event, onEnd);
    cb();
  };
  var onEnd = function (e) {
    if (e.target === el) {
      if (++ended >= propCount) {
        end();
      }
    }
  };
  setTimeout(function () {
    if (ended < propCount) {
      end();
    }
  }, timeout + 1);
  el.addEventListener(event, onEnd);
}

var transformRE = /\b(transform|all)(,|$)/;

function getTransitionInfo (el, expectedType) {
  var styles = window.getComputedStyle(el);
  // JSDOM may return undefined for transition properties
  var transitionDelays = (styles[transitionProp + 'Delay'] || '').split(', ');
  var transitionDurations = (styles[transitionProp + 'Duration'] || '').split(', ');
  var transitionTimeout = getTimeout(transitionDelays, transitionDurations);
  var animationDelays = (styles[animationProp + 'Delay'] || '').split(', ');
  var animationDurations = (styles[animationProp + 'Duration'] || '').split(', ');
  var animationTimeout = getTimeout(animationDelays, animationDurations);

  var type;
  var timeout = 0;
  var propCount = 0;
  /* istanbul ignore if */
  if (expectedType === TRANSITION) {
    if (transitionTimeout > 0) {
      type = TRANSITION;
      timeout = transitionTimeout;
      propCount = transitionDurations.length;
    }
  } else if (expectedType === ANIMATION) {
    if (animationTimeout > 0) {
      type = ANIMATION;
      timeout = animationTimeout;
      propCount = animationDurations.length;
    }
  } else {
    timeout = Math.max(transitionTimeout, animationTimeout);
    type = timeout > 0
      ? transitionTimeout > animationTimeout
        ? TRANSITION
        : ANIMATION
      : null;
    propCount = type
      ? type === TRANSITION
        ? transitionDurations.length
        : animationDurations.length
      : 0;
  }
  var hasTransform =
    type === TRANSITION &&
    transformRE.test(styles[transitionProp + 'Property']);
  return {
    type: type,
    timeout: timeout,
    propCount: propCount,
    hasTransform: hasTransform
  }
}

function getTimeout (delays, durations) {
  /* istanbul ignore next */
  while (delays.length < durations.length) {
    delays = delays.concat(delays);
  }

  return Math.max.apply(null, durations.map(function (d, i) {
    return toMs(d) + toMs(delays[i])
  }))
}

// Old versions of Chromium (below 61.0.3163.100) formats floating pointer numbers
// in a locale-dependent way, using a comma instead of a dot.
// If comma is not replaced with a dot, the input will be rounded down (i.e. acting
// as a floor function) causing unexpected behaviors
function toMs (s) {
  return Number(s.slice(0, -1).replace(',', '.')) * 1000
}

/*  */

function enter (vnode, toggleDisplay) {
  var el = vnode.elm;

  // call leave callback now
  if (isDef(el._leaveCb)) {
    el._leaveCb.cancelled = true;
    el._leaveCb();
  }

  var data = resolveTransition(vnode.data.transition);
  if (isUndef(data)) {
    return
  }

  /* istanbul ignore if */
  if (isDef(el._enterCb) || el.nodeType !== 1) {
    return
  }

  var css = data.css;
  var type = data.type;
  var enterClass = data.enterClass;
  var enterToClass = data.enterToClass;
  var enterActiveClass = data.enterActiveClass;
  var appearClass = data.appearClass;
  var appearToClass = data.appearToClass;
  var appearActiveClass = data.appearActiveClass;
  var beforeEnter = data.beforeEnter;
  var enter = data.enter;
  var afterEnter = data.afterEnter;
  var enterCancelled = data.enterCancelled;
  var beforeAppear = data.beforeAppear;
  var appear = data.appear;
  var afterAppear = data.afterAppear;
  var appearCancelled = data.appearCancelled;
  var duration = data.duration;

  // activeInstance will always be the <transition> component managing this
  // transition. One edge case to check is when the <transition> is placed
  // as the root node of a child component. In that case we need to check
  // <transition>'s parent for appear check.
  var context = activeInstance;
  var transitionNode = activeInstance.$vnode;
  while (transitionNode && transitionNode.parent) {
    context = transitionNode.context;
    transitionNode = transitionNode.parent;
  }

  var isAppear = !context._isMounted || !vnode.isRootInsert;

  if (isAppear && !appear && appear !== '') {
    return
  }

  var startClass = isAppear && appearClass
    ? appearClass
    : enterClass;
  var activeClass = isAppear && appearActiveClass
    ? appearActiveClass
    : enterActiveClass;
  var toClass = isAppear && appearToClass
    ? appearToClass
    : enterToClass;

  var beforeEnterHook = isAppear
    ? (beforeAppear || beforeEnter)
    : beforeEnter;
  var enterHook = isAppear
    ? (typeof appear === 'function' ? appear : enter)
    : enter;
  var afterEnterHook = isAppear
    ? (afterAppear || afterEnter)
    : afterEnter;
  var enterCancelledHook = isAppear
    ? (appearCancelled || enterCancelled)
    : enterCancelled;

  var explicitEnterDuration = toNumber(
    isObject(duration)
      ? duration.enter
      : duration
  );

  var expectsCSS = css !== false && !isIE9;
  var userWantsControl = getHookArgumentsLength(enterHook);

  var cb = el._enterCb = once(function () {
    if (expectsCSS) {
      removeTransitionClass(el, toClass);
      removeTransitionClass(el, activeClass);
    }
    if (cb.cancelled) {
      if (expectsCSS) {
        removeTransitionClass(el, startClass);
      }
      enterCancelledHook && enterCancelledHook(el);
    } else {
      afterEnterHook && afterEnterHook(el);
    }
    el._enterCb = null;
  });

  if (!vnode.data.show) {
    // remove pending leave element on enter by injecting an insert hook
    mergeVNodeHook(vnode, 'insert', function () {
      var parent = el.parentNode;
      var pendingNode = parent && parent._pending && parent._pending[vnode.key];
      if (pendingNode &&
        pendingNode.tag === vnode.tag &&
        pendingNode.elm._leaveCb
      ) {
        pendingNode.elm._leaveCb();
      }
      enterHook && enterHook(el, cb);
    });
  }

  // start enter transition
  beforeEnterHook && beforeEnterHook(el);
  if (expectsCSS) {
    addTransitionClass(el, startClass);
    addTransitionClass(el, activeClass);
    nextFrame(function () {
      removeTransitionClass(el, startClass);
      if (!cb.cancelled) {
        addTransitionClass(el, toClass);
        if (!userWantsControl) {
          if (isValidDuration(explicitEnterDuration)) {
            setTimeout(cb, explicitEnterDuration);
          } else {
            whenTransitionEnds(el, type, cb);
          }
        }
      }
    });
  }

  if (vnode.data.show) {
    toggleDisplay && toggleDisplay();
    enterHook && enterHook(el, cb);
  }

  if (!expectsCSS && !userWantsControl) {
    cb();
  }
}

function leave (vnode, rm) {
  var el = vnode.elm;

  // call enter callback now
  if (isDef(el._enterCb)) {
    el._enterCb.cancelled = true;
    el._enterCb();
  }

  var data = resolveTransition(vnode.data.transition);
  if (isUndef(data) || el.nodeType !== 1) {
    return rm()
  }

  /* istanbul ignore if */
  if (isDef(el._leaveCb)) {
    return
  }

  var css = data.css;
  var type = data.type;
  var leaveClass = data.leaveClass;
  var leaveToClass = data.leaveToClass;
  var leaveActiveClass = data.leaveActiveClass;
  var beforeLeave = data.beforeLeave;
  var leave = data.leave;
  var afterLeave = data.afterLeave;
  var leaveCancelled = data.leaveCancelled;
  var delayLeave = data.delayLeave;
  var duration = data.duration;

  var expectsCSS = css !== false && !isIE9;
  var userWantsControl = getHookArgumentsLength(leave);

  var explicitLeaveDuration = toNumber(
    isObject(duration)
      ? duration.leave
      : duration
  );

  var cb = el._leaveCb = once(function () {
    if (el.parentNode && el.parentNode._pending) {
      el.parentNode._pending[vnode.key] = null;
    }
    if (expectsCSS) {
      removeTransitionClass(el, leaveToClass);
      removeTransitionClass(el, leaveActiveClass);
    }
    if (cb.cancelled) {
      if (expectsCSS) {
        removeTransitionClass(el, leaveClass);
      }
      leaveCancelled && leaveCancelled(el);
    } else {
      rm();
      afterLeave && afterLeave(el);
    }
    el._leaveCb = null;
  });

  if (delayLeave) {
    delayLeave(performLeave);
  } else {
    performLeave();
  }

  function performLeave () {
    // the delayed leave may have already been cancelled
    if (cb.cancelled) {
      return
    }
    // record leaving element
    if (!vnode.data.show && el.parentNode) {
      (el.parentNode._pending || (el.parentNode._pending = {}))[(vnode.key)] = vnode;
    }
    beforeLeave && beforeLeave(el);
    if (expectsCSS) {
      addTransitionClass(el, leaveClass);
      addTransitionClass(el, leaveActiveClass);
      nextFrame(function () {
        removeTransitionClass(el, leaveClass);
        if (!cb.cancelled) {
          addTransitionClass(el, leaveToClass);
          if (!userWantsControl) {
            if (isValidDuration(explicitLeaveDuration)) {
              setTimeout(cb, explicitLeaveDuration);
            } else {
              whenTransitionEnds(el, type, cb);
            }
          }
        }
      });
    }
    leave && leave(el, cb);
    if (!expectsCSS && !userWantsControl) {
      cb();
    }
  }
}

function isValidDuration (val) {
  return typeof val === 'number' && !isNaN(val)
}

/**
 * Normalize a transition hook's argument length. The hook may be:
 * - a merged hook (invoker) with the original in .fns
 * - a wrapped component method (check ._length)
 * - a plain function (.length)
 */
function getHookArgumentsLength (fn) {
  if (isUndef(fn)) {
    return false
  }
  var invokerFns = fn.fns;
  if (isDef(invokerFns)) {
    // invoker
    return getHookArgumentsLength(
      Array.isArray(invokerFns)
        ? invokerFns[0]
        : invokerFns
    )
  } else {
    return (fn._length || fn.length) > 1
  }
}

function _enter (_, vnode) {
  if (vnode.data.show !== true) {
    enter(vnode);
  }
}

var transition = inBrowser ? {
  create: _enter,
  activate: _enter,
  remove: function remove$$1 (vnode, rm) {
    /* istanbul ignore else */
    if (vnode.data.show !== true) {
      leave(vnode, rm);
    } else {
      rm();
    }
  }
} : {};

var platformModules = [
  attrs,
  klass,
  events,
  domProps,
  style,
  transition
];

/*  */

// the directive module should be applied last, after all
// built-in modules have been applied.
var modules = platformModules.concat(baseModules);

var patch = createPatchFunction({ nodeOps: nodeOps, modules: modules });

/**
 * Not type checking this file because flow doesn't like attaching
 * properties to Elements.
 */

/* istanbul ignore if */
if (isIE9) {
  // http://www.matts411.com/post/internet-explorer-9-oninput/
  document.addEventListener('selectionchange', function () {
    var el = document.activeElement;
    if (el && el.vmodel) {
      trigger(el, 'input');
    }
  });
}

var directive = {
  inserted: function inserted (el, binding, vnode, oldVnode) {
    if (vnode.tag === 'select') {
      // #6903
      if (oldVnode.elm && !oldVnode.elm._vOptions) {
        mergeVNodeHook(vnode, 'postpatch', function () {
          directive.componentUpdated(el, binding, vnode);
        });
      } else {
        setSelected(el, binding, vnode.context);
      }
      el._vOptions = [].map.call(el.options, getValue);
    } else if (vnode.tag === 'textarea' || isTextInputType(el.type)) {
      el._vModifiers = binding.modifiers;
      if (!binding.modifiers.lazy) {
        el.addEventListener('compositionstart', onCompositionStart);
        el.addEventListener('compositionend', onCompositionEnd);
        // Safari < 10.2 & UIWebView doesn't fire compositionend when
        // switching focus before confirming composition choice
        // this also fixes the issue where some browsers e.g. iOS Chrome
        // fires "change" instead of "input" on autocomplete.
        el.addEventListener('change', onCompositionEnd);
        /* istanbul ignore if */
        if (isIE9) {
          el.vmodel = true;
        }
      }
    }
  },

  componentUpdated: function componentUpdated (el, binding, vnode) {
    if (vnode.tag === 'select') {
      setSelected(el, binding, vnode.context);
      // in case the options rendered by v-for have changed,
      // it's possible that the value is out-of-sync with the rendered options.
      // detect such cases and filter out values that no longer has a matching
      // option in the DOM.
      var prevOptions = el._vOptions;
      var curOptions = el._vOptions = [].map.call(el.options, getValue);
      if (curOptions.some(function (o, i) { return !looseEqual(o, prevOptions[i]); })) {
        // trigger change event if
        // no matching option found for at least one value
        var needReset = el.multiple
          ? binding.value.some(function (v) { return hasNoMatchingOption(v, curOptions); })
          : binding.value !== binding.oldValue && hasNoMatchingOption(binding.value, curOptions);
        if (needReset) {
          trigger(el, 'change');
        }
      }
    }
  }
};

function setSelected (el, binding, vm) {
  actuallySetSelected(el, binding);
  /* istanbul ignore if */
  if (isIE || isEdge) {
    setTimeout(function () {
      actuallySetSelected(el, binding);
    }, 0);
  }
}

function actuallySetSelected (el, binding, vm) {
  var value = binding.value;
  var isMultiple = el.multiple;
  if (isMultiple && !Array.isArray(value)) {
    return
  }
  var selected, option;
  for (var i = 0, l = el.options.length; i < l; i++) {
    option = el.options[i];
    if (isMultiple) {
      selected = looseIndexOf(value, getValue(option)) > -1;
      if (option.selected !== selected) {
        option.selected = selected;
      }
    } else {
      if (looseEqual(getValue(option), value)) {
        if (el.selectedIndex !== i) {
          el.selectedIndex = i;
        }
        return
      }
    }
  }
  if (!isMultiple) {
    el.selectedIndex = -1;
  }
}

function hasNoMatchingOption (value, options) {
  return options.every(function (o) { return !looseEqual(o, value); })
}

function getValue (option) {
  return '_value' in option
    ? option._value
    : option.value
}

function onCompositionStart (e) {
  e.target.composing = true;
}

function onCompositionEnd (e) {
  // prevent triggering an input event for no reason
  if (!e.target.composing) { return }
  e.target.composing = false;
  trigger(e.target, 'input');
}

function trigger (el, type) {
  var e = document.createEvent('HTMLEvents');
  e.initEvent(type, true, true);
  el.dispatchEvent(e);
}

/*  */

// recursively search for possible transition defined inside the component root
function locateNode (vnode) {
  return vnode.componentInstance && (!vnode.data || !vnode.data.transition)
    ? locateNode(vnode.componentInstance._vnode)
    : vnode
}

var show = {
  bind: function bind (el, ref, vnode) {
    var value = ref.value;

    vnode = locateNode(vnode);
    var transition$$1 = vnode.data && vnode.data.transition;
    var originalDisplay = el.__vOriginalDisplay =
      el.style.display === 'none' ? '' : el.style.display;
    if (value && transition$$1) {
      vnode.data.show = true;
      enter(vnode, function () {
        el.style.display = originalDisplay;
      });
    } else {
      el.style.display = value ? originalDisplay : 'none';
    }
  },

  update: function update (el, ref, vnode) {
    var value = ref.value;
    var oldValue = ref.oldValue;

    /* istanbul ignore if */
    if (!value === !oldValue) { return }
    vnode = locateNode(vnode);
    var transition$$1 = vnode.data && vnode.data.transition;
    if (transition$$1) {
      vnode.data.show = true;
      if (value) {
        enter(vnode, function () {
          el.style.display = el.__vOriginalDisplay;
        });
      } else {
        leave(vnode, function () {
          el.style.display = 'none';
        });
      }
    } else {
      el.style.display = value ? el.__vOriginalDisplay : 'none';
    }
  },

  unbind: function unbind (
    el,
    binding,
    vnode,
    oldVnode,
    isDestroy
  ) {
    if (!isDestroy) {
      el.style.display = el.__vOriginalDisplay;
    }
  }
};

var platformDirectives = {
  model: directive,
  show: show
};

/*  */

var transitionProps = {
  name: String,
  appear: Boolean,
  css: Boolean,
  mode: String,
  type: String,
  enterClass: String,
  leaveClass: String,
  enterToClass: String,
  leaveToClass: String,
  enterActiveClass: String,
  leaveActiveClass: String,
  appearClass: String,
  appearActiveClass: String,
  appearToClass: String,
  duration: [Number, String, Object]
};

// in case the child is also an abstract component, e.g. <keep-alive>
// we want to recursively retrieve the real component to be rendered
function getRealChild (vnode) {
  var compOptions = vnode && vnode.componentOptions;
  if (compOptions && compOptions.Ctor.options.abstract) {
    return getRealChild(getFirstComponentChild(compOptions.children))
  } else {
    return vnode
  }
}

function extractTransitionData (comp) {
  var data = {};
  var options = comp.$options;
  // props
  for (var key in options.propsData) {
    data[key] = comp[key];
  }
  // events.
  // extract listeners and pass them directly to the transition methods
  var listeners = options._parentListeners;
  for (var key$1 in listeners) {
    data[camelize(key$1)] = listeners[key$1];
  }
  return data
}

function placeholder (h, rawChild) {
  if (/\d-keep-alive$/.test(rawChild.tag)) {
    return h('keep-alive', {
      props: rawChild.componentOptions.propsData
    })
  }
}

function hasParentTransition (vnode) {
  while ((vnode = vnode.parent)) {
    if (vnode.data.transition) {
      return true
    }
  }
}

function isSameChild (child, oldChild) {
  return oldChild.key === child.key && oldChild.tag === child.tag
}

var isNotTextNode = function (c) { return c.tag || isAsyncPlaceholder(c); };

var isVShowDirective = function (d) { return d.name === 'show'; };

var Transition = {
  name: 'transition',
  props: transitionProps,
  abstract: true,

  render: function render (h) {
    var this$1 = this;

    var children = this.$slots.default;
    if (!children) {
      return
    }

    // filter out text nodes (possible whitespaces)
    children = children.filter(isNotTextNode);
    /* istanbul ignore if */
    if (!children.length) {
      return
    }

    var mode = this.mode;

    var rawChild = children[0];

    // if this is a component root node and the component's
    // parent container node also has transition, skip.
    if (hasParentTransition(this.$vnode)) {
      return rawChild
    }

    // apply transition data to child
    // use getRealChild() to ignore abstract components e.g. keep-alive
    var child = getRealChild(rawChild);
    /* istanbul ignore if */
    if (!child) {
      return rawChild
    }

    if (this._leaving) {
      return placeholder(h, rawChild)
    }

    // ensure a key that is unique to the vnode type and to this transition
    // component instance. This key will be used to remove pending leaving nodes
    // during entering.
    var id = "__transition-" + (this._uid) + "-";
    child.key = child.key == null
      ? child.isComment
        ? id + 'comment'
        : id + child.tag
      : isPrimitive$1(child.key)
        ? (String(child.key).indexOf(id) === 0 ? child.key : id + child.key)
        : child.key;

    var data = (child.data || (child.data = {})).transition = extractTransitionData(this);
    var oldRawChild = this._vnode;
    var oldChild = getRealChild(oldRawChild);

    // mark v-show
    // so that the transition module can hand over the control to the directive
    if (child.data.directives && child.data.directives.some(isVShowDirective)) {
      child.data.show = true;
    }

    if (
      oldChild &&
      oldChild.data &&
      !isSameChild(child, oldChild) &&
      !isAsyncPlaceholder(oldChild) &&
      // #6687 component root is a comment node
      !(oldChild.componentInstance && oldChild.componentInstance._vnode.isComment)
    ) {
      // replace old child transition data with fresh one
      // important for dynamic transitions!
      var oldData = oldChild.data.transition = extend({}, data);
      // handle transition mode
      if (mode === 'out-in') {
        // return placeholder node and queue update when leave finishes
        this._leaving = true;
        mergeVNodeHook(oldData, 'afterLeave', function () {
          this$1._leaving = false;
          this$1.$forceUpdate();
        });
        return placeholder(h, rawChild)
      } else if (mode === 'in-out') {
        if (isAsyncPlaceholder(child)) {
          return oldRawChild
        }
        var delayedLeave;
        var performLeave = function () { delayedLeave(); };
        mergeVNodeHook(data, 'afterEnter', performLeave);
        mergeVNodeHook(data, 'enterCancelled', performLeave);
        mergeVNodeHook(oldData, 'delayLeave', function (leave) { delayedLeave = leave; });
      }
    }

    return rawChild
  }
};

/*  */

var props = extend({
  tag: String,
  moveClass: String
}, transitionProps);

delete props.mode;

var TransitionGroup = {
  props: props,

  beforeMount: function beforeMount () {
    var this$1 = this;

    var update = this._update;
    this._update = function (vnode, hydrating) {
      var restoreActiveInstance = setActiveInstance(this$1);
      // force removing pass
      this$1.__patch__(
        this$1._vnode,
        this$1.kept,
        false, // hydrating
        true // removeOnly (!important, avoids unnecessary moves)
      );
      this$1._vnode = this$1.kept;
      restoreActiveInstance();
      update.call(this$1, vnode, hydrating);
    };
  },

  render: function render (h) {
    var tag = this.tag || this.$vnode.data.tag || 'span';
    var map = Object.create(null);
    var prevChildren = this.prevChildren = this.children;
    var rawChildren = this.$slots.default || [];
    var children = this.children = [];
    var transitionData = extractTransitionData(this);

    for (var i = 0; i < rawChildren.length; i++) {
      var c = rawChildren[i];
      if (c.tag) {
        if (c.key != null && String(c.key).indexOf('__vlist') !== 0) {
          children.push(c);
          map[c.key] = c
          ;(c.data || (c.data = {})).transition = transitionData;
        }
      }
    }

    if (prevChildren) {
      var kept = [];
      var removed = [];
      for (var i$1 = 0; i$1 < prevChildren.length; i$1++) {
        var c$1 = prevChildren[i$1];
        c$1.data.transition = transitionData;
        c$1.data.pos = c$1.elm.getBoundingClientRect();
        if (map[c$1.key]) {
          kept.push(c$1);
        } else {
          removed.push(c$1);
        }
      }
      this.kept = h(tag, null, kept);
      this.removed = removed;
    }

    return h(tag, null, children)
  },

  updated: function updated () {
    var children = this.prevChildren;
    var moveClass = this.moveClass || ((this.name || 'v') + '-move');
    if (!children.length || !this.hasMove(children[0].elm, moveClass)) {
      return
    }

    // we divide the work into three loops to avoid mixing DOM reads and writes
    // in each iteration - which helps prevent layout thrashing.
    children.forEach(callPendingCbs);
    children.forEach(recordPosition);
    children.forEach(applyTranslation);

    // force reflow to put everything in position
    // assign to this to avoid being removed in tree-shaking
    // $flow-disable-line
    this._reflow = document.body.offsetHeight;

    children.forEach(function (c) {
      if (c.data.moved) {
        var el = c.elm;
        var s = el.style;
        addTransitionClass(el, moveClass);
        s.transform = s.WebkitTransform = s.transitionDuration = '';
        el.addEventListener(transitionEndEvent, el._moveCb = function cb (e) {
          if (e && e.target !== el) {
            return
          }
          if (!e || /transform$/.test(e.propertyName)) {
            el.removeEventListener(transitionEndEvent, cb);
            el._moveCb = null;
            removeTransitionClass(el, moveClass);
          }
        });
      }
    });
  },

  methods: {
    hasMove: function hasMove (el, moveClass) {
      /* istanbul ignore if */
      if (!hasTransition) {
        return false
      }
      /* istanbul ignore if */
      if (this._hasMove) {
        return this._hasMove
      }
      // Detect whether an element with the move class applied has
      // CSS transitions. Since the element may be inside an entering
      // transition at this very moment, we make a clone of it and remove
      // all other transition classes applied to ensure only the move class
      // is applied.
      var clone = el.cloneNode();
      if (el._transitionClasses) {
        el._transitionClasses.forEach(function (cls) { removeClass(clone, cls); });
      }
      addClass(clone, moveClass);
      clone.style.display = 'none';
      this.$el.appendChild(clone);
      var info = getTransitionInfo(clone);
      this.$el.removeChild(clone);
      return (this._hasMove = info.hasTransform)
    }
  }
};

function callPendingCbs (c) {
  /* istanbul ignore if */
  if (c.elm._moveCb) {
    c.elm._moveCb();
  }
  /* istanbul ignore if */
  if (c.elm._enterCb) {
    c.elm._enterCb();
  }
}

function recordPosition (c) {
  c.data.newPos = c.elm.getBoundingClientRect();
}

function applyTranslation (c) {
  var oldPos = c.data.pos;
  var newPos = c.data.newPos;
  var dx = oldPos.left - newPos.left;
  var dy = oldPos.top - newPos.top;
  if (dx || dy) {
    c.data.moved = true;
    var s = c.elm.style;
    s.transform = s.WebkitTransform = "translate(" + dx + "px," + dy + "px)";
    s.transitionDuration = '0s';
  }
}

var platformComponents = {
  Transition: Transition,
  TransitionGroup: TransitionGroup
};

/*  */

// install platform specific utils
Vue.config.mustUseProp = mustUseProp;
Vue.config.isReservedTag = isReservedTag;
Vue.config.isReservedAttr = isReservedAttr;
Vue.config.getTagNamespace = getTagNamespace;
Vue.config.isUnknownElement = isUnknownElement;

// install platform runtime directives & components
extend(Vue.options.directives, platformDirectives);
extend(Vue.options.components, platformComponents);

// install platform patch function
Vue.prototype.__patch__ = inBrowser ? patch : noop;

// public mount method
Vue.prototype.$mount = function (
  el,
  hydrating
) {
  el = el && inBrowser ? query(el) : undefined;
  return mountComponent(this, el, hydrating)
};

// devtools global hook
/* istanbul ignore next */
if (inBrowser) {
  setTimeout(function () {
    if (config.devtools) {
      if (devtools) {
        devtools.emit('init', Vue);
      }
    }
  }, 0);
}

var vue_runtime_esm = /*#__PURE__*/Object.freeze({
  __proto__: null,
  'default': Vue
});

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$c = "/*! normalize.css v3.0.3 | MIT License | github.com/necolas/normalize.css */img,legend{border:0}.text-nowrap,.text-overflow{white-space:nowrap}html{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','PingFang SC','Hiragino Sans GB','Microsoft YaHei','Helvetica Neue',sans-serif,'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol';-ms-text-size-adjust:100%;-webkit-text-size-adjust:100%}body{margin:0;line-height:1.5}article,aside,details,figcaption,figure,footer,header,hgroup,main,menu,nav,section,summary{display:block}audio,canvas,progress,video{display:inline-block;vertical-align:baseline}audio:not([controls]){display:none;height:0}[hidden],template{display:none}a{background-color:transparent}a:active,a:hover{outline:0}abbr[title]{border-bottom:none;-webkit-text-decoration:underline dotted;text-decoration:underline dotted}b,optgroup,strong{font-weight:700}dfn{font-style:italic}h1{font-size:2em;margin:.67em 0}mark{background:#ff0;color:#000}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sup{top:-.5em}sub{bottom:-.25em}svg:not(:root){overflow:hidden}figure{margin:1em 40px}hr{-webkit-box-sizing:content-box;box-sizing:content-box;height:0}pre,textarea{overflow:auto}code,kbd,pre,samp{font-family:SFMono-Regular,Consolas,'Liberation Mono',Menlo,Courier,monospace;font-size:1em}button,input,optgroup,select,textarea{color:inherit;font:inherit;margin:0}button{overflow:visible}button,select{text-transform:none}button,html input[type=button],input[type=reset],input[type=submit]{-webkit-appearance:button;cursor:pointer}button[disabled],html input[disabled]{cursor:default}button::-moz-focus-inner,input::-moz-focus-inner{border:0;padding:0}input{line-height:normal}input[type=checkbox],input[type=radio]{-webkit-box-sizing:border-box;box-sizing:border-box;padding:0}input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{height:auto}input[type=search]{-webkit-appearance:textfield;-webkit-box-sizing:content-box;box-sizing:content-box}input[type=search]::-webkit-search-cancel-button,input[type=search]::-webkit-search-decoration{-webkit-appearance:none}fieldset{border:1px solid silver;margin:0 2px;padding:.35em .625em .75em}legend{padding:0}table{border-collapse:collapse;border-spacing:0}td,th{padding:0}*,:after,:before{-webkit-box-sizing:border-box;box-sizing:border-box}.fade-enter-active,.fade-leave-active{-webkit-transition:opacity .15s linear;transition:opacity .15s linear;opacity:1!important}.fade-enter-active.fade-enter,.fade-enter-active.fade-leave-active,.fade-leave-active.fade-enter,.fade-leave-active.fade-leave-active{opacity:0!important}.collapse-transition{-webkit-transition:height .2s ease-in-out,padding-top .2s ease-in-out,padding-bottom .2s ease-in-out;transition:height .2s ease-in-out,padding-top .2s ease-in-out,padding-bottom .2s ease-in-out}.by-loading-fade-enter-active,.by-loading-fade-leave-active{-webkit-transition:opacity .5s;transition:opacity .5s}.by-loading-fade-enter,.by-loading-fade-leave-to{opacity:0}.pull-right{float:right!important}.pull-left{float:left!important}.center-block{display:block;margin-left:auto;margin-right:auto}.hide{display:none!important}.show{display:block!important}.text-left{text-align:left}.text-right{text-align:right}.text-center{text-align:center}.text-justify{text-align:justify}.text-lowercase{text-transform:lowercase}.text-uppercase{text-transform:uppercase}.text-capitalize{text-transform:capitalize}.text-overflow{overflow:hidden;text-overflow:ellipsis}.font-weight-bold{font-weight:700!important}.font-weight-normal{font-weight:400!important}.font-weight-light{font-weight:300!important}.font-weight-italic{font-weight:italic!important}.inline{display:inline!important}.block{display:block!important}.inline-block{display:inline-block!important}.clearfix{zoom:1}.clearfix::after,.clearfix::before{content:'';display:table}.clearfix::after{clear:both}.flex{display:-webkit-box;display:-ms-flexbox;display:flex}.inline-flex{display:-webkit-inline-box;display:-ms-inline-flexbox;display:inline-flex}@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}";
styleInject(css_248z$c);

var css_248z$b = "@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.by-icon{display:inline-block;vertical-align:bottom;width:1em;height:1em;position:relative}.by-icon svg{position:absolute;top:0;left:0}";
styleInject(css_248z$b);

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function getAugmentedNamespace(n) {
	if (n.__esModule) return n;
	var a = Object.defineProperty({}, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

function createCommonjsModule(fn) {
  var module = { exports: {} };
	return fn(module, module.exports), module.exports;
}

var icon = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 126);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 126:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _icon = __webpack_require__(127);

var _icon2 = _interopRequireDefault(_icon);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_icon2.default.install = function (Vue) {
  Vue.component(_icon2.default.name, _icon2.default);
};

exports.default = _icon2.default;

/***/ }),

/***/ 127:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_icon_vue__ = __webpack_require__(74);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_icon_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_icon_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_icon_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_icon_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_7301a69e_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_icon_vue__ = __webpack_require__(129);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_icon_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_7301a69e_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_icon_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_7301a69e_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_icon_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 128:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = { 'angle-down': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="angle-down_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="angle-down_svg__b" fill="#fff"><use xlink:href="#angle-down_svg__a"/></mask><use opacity=".01" xlink:href="#angle-down_svg__a"/><path fill-rule="nonzero" d="M16.586 35.324a2 2 0 0 0 2.828 2.828l12-12a2 2 0 0 0 0-2.828l-12-12a2 2 0 0 0-2.828 2.828l10.586 10.586-10.586 10.586z" transform="rotate(90 24 24.738)"/></g></svg>', 'angle-left': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="angle-left_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd" transform="matrix(0 -1 -1 0 48 48)"><mask id="angle-left_svg__b" fill="#fff"><use xlink:href="#angle-left_svg__a"/></mask><use opacity=".01" xlink:href="#angle-left_svg__a"/><path fill-rule="nonzero" d="M16.586 35.324a2 2 0 0 0 2.828 2.828l12-12a2 2 0 0 0 0-2.828l-12-12a2 2 0 0 0-2.828 2.828l10.586 10.586-10.586 10.586z" transform="rotate(90 24 24.738)"/></g></svg>', 'angle-right': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="angle-right_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd" transform="rotate(-90 24 24)"><mask id="angle-right_svg__b" fill="#fff"><use xlink:href="#angle-right_svg__a"/></mask><use opacity=".01" xlink:href="#angle-right_svg__a"/><path fill-rule="nonzero" d="M16.586 35.324a2 2 0 0 0 2.828 2.828l12-12a2 2 0 0 0 0-2.828l-12-12a2 2 0 0 0-2.828 2.828l10.586 10.586-10.586 10.586z" transform="rotate(90 24 24.738)"/></g></svg>', 'bulb': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 20 20" width="100%" height="100%"><defs><path id="bulb_svg__a" d="M0 0h20v20H0z"/></defs><g fill-rule="evenodd"><mask id="bulb_svg__b" fill="#fff"><use xlink:href="#bulb_svg__a"/></mask><use opacity=".01" xlink:href="#bulb_svg__a"/><path fill-rule="nonzero" d="M7.14 8.875c0 .621.503 1.125 1.124 1.125h.361v2.14h2.25V10h.33a1.125 1.125 0 1 0 0-2.25h-2.94c-.622 0-1.126.504-1.126 1.125zm5.92 4.638v1.667a.5.5 0 0 1-.5.5h-5.5a.5.5 0 0 1-.5-.5v-1.667c-1.95-1.084-3.25-3.25-3.25-5.634 0-3.575 2.926-6.5 6.5-6.5 3.576 0 6.5 2.925 6.5 6.5 0 2.384-1.3 4.55-3.25 5.634zm-2.185-1.376c1.912-.45 3.375-2.25 3.375-4.387 0-2.475-2.025-4.5-4.5-4.5a4.513 4.513 0 0 0-4.5 4.5c0 2.137 1.463 3.825 3.375 4.387v1.464h2.25v-1.464zM12 16.75V19H7.5v-2.25H12z"/></g></svg>', 'calendar': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="calendar_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="calendar_svg__b" fill="#fff"><use xlink:href="#calendar_svg__a"/></mask><use opacity=".01" xlink:href="#calendar_svg__a"/><g fill-rule="nonzero"><path d="M30 11H18v2a2 2 0 1 1-4 0v-2H7v6h34v-6h-7v2a2 2 0 1 1-4 0v-2zm4-4h7a4 4 0 0 1 4 4v30a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V11a4 4 0 0 1 4-4h7V5a2 2 0 1 1 4 0v2h12V5a2 2 0 1 1 4 0v2zm7 14H7v20h34V21zM28 37a2 2 0 1 1 0-4h6a2 2 0 1 1 0 4h-6zm-14 0a2 2 0 1 1 0-4h6a2 2 0 1 1 0 4h-6zm14-8a2 2 0 1 1 0-4h6a2 2 0 1 1 0 4h-6zm-14 0a2 2 0 1 1 0-4h6a2 2 0 1 1 0 4h-6z"/></g></g></svg>', 'camera': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 14 14" width="100%" height="100%"><defs><path id="camera_svg__a" d="M0 0h14v14H0z"/></defs><g fill-rule="evenodd"><mask id="camera_svg__b" fill="#fff"><use xlink:href="#camera_svg__a"/></mask><use fill="#FFF" fill-opacity=".01" xlink:href="#camera_svg__a"/><path fill-rule="nonzero" d="M4.014 2.917l.714-1.428a.583.583 0 0 1 .522-.322h3.5c.221 0 .423.125.522.322l.714 1.428h1.68c.967 0 1.75.783 1.75 1.75v6.416a1.75 1.75 0 0 1-1.75 1.75H2.334a1.75 1.75 0 0 1-1.75-1.75V4.667c0-.967.784-1.75 1.75-1.75h1.681zM7 10.792a2.917 2.917 0 1 0 0-5.834 2.917 2.917 0 0 0 0 5.834zm0-1.167a1.75 1.75 0 1 1 0-3.5 1.75 1.75 0 0 1 0 3.5z"/></g></svg>', 'changelog': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="changelog_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="changelog_svg__b" fill="#fff"><use xlink:href="#changelog_svg__a"/></mask><use opacity=".01" xlink:href="#changelog_svg__a"/><g><path fill-rule="nonzero" d="M15 8h24a4 4 0 0 1 4 4v30a4 4 0 0 1-4 4H15a4 4 0 0 1-4-4V12a4 4 0 0 1 4-4zm0 4v30h24V12H15zM9 6v30a2 2 0 1 1-4 0V4a2 2 0 0 1 2-2h26a2 2 0 1 1 0 4H9zm11 18a2 2 0 1 1 0-4h14a2 2 0 1 1 0 4H20zm0 8a2 2 0 1 1 0-4h14a2 2 0 1 1 0 4H20z"/></g></g></svg>', 'check-circle': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="check-circle_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="check-circle_svg__b" fill="#fff"><use xlink:href="#check-circle_svg__a"/></mask><use fill="#FFF" opacity=".01" xlink:href="#check-circle_svg__a"/><path fill-rule="nonzero" d="M24 46C11.85 46 2 36.15 2 24S11.85 2 24 2s22 9.85 22 22-9.85 22-22 22zm-2.483-18.868l-5.103-5.103a2 2 0 0 0-2.828 2.829l6.516 6.516a2 2 0 0 0 2.829 0l11.96-11.96a2 2 0 0 0-2.829-2.828L21.517 27.132z"/></g></svg>', 'check-square': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="check-square_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="check-square_svg__b" fill="#fff"><use xlink:href="#check-square_svg__a"/></mask><use opacity=".01" xlink:href="#check-square_svg__a"/><path fill-rule="nonzero" d="M9 4.738h30a5 5 0 0 1 5 5v30a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-30a5 5 0 0 1 5-5zM22 27.91l-5.102-5.102a2 2 0 0 0-2.829 2.828l6.517 6.516a2 2 0 0 0 2.828 0l11.96-11.96a2 2 0 1 0-2.828-2.828L22 27.91z"/></g></svg>', 'check': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="check_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="check_svg__b" fill="#fff"><use xlink:href="#check_svg__a"/></mask><use fill="#FFF" fill-opacity=".01" xlink:href="#check_svg__a"/><path fill-rule="nonzero" d="M10.414 22.586a2 2 0 0 0-2.828 2.828l10 10a2 2 0 0 0 2.828 0l20-20a2 2 0 0 0-2.828-2.828L19 31.172l-8.586-8.586z"/></g></svg>', 'clock-circle': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="clock-circle_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="clock-circle_svg__b" fill="#fff"><use xlink:href="#clock-circle_svg__a"/></mask><use opacity=".01" xlink:href="#clock-circle_svg__a"/><path fill-rule="nonzero" d="M24 46C11.85 46 2 36.15 2 24S11.85 2 24 2s22 9.85 22 22-9.85 22-22 22zm0-4c9.941 0 18-8.059 18-18S33.941 6 24 6 6 14.059 6 24s8.059 18 18 18zm2.008-30v11.18l7.893 7.894a2 2 0 1 1-2.829 2.828l-8.48-8.479a2 2 0 0 1-.585-1.414l.001-12.01a2 2 0 0 1 4 .001z"/></g></svg>', 'close-circle': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="close-circle_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="close-circle_svg__b" fill="#fff"><use xlink:href="#close-circle_svg__a"/></mask><use fill="#FFF" opacity=".01" xlink:href="#close-circle_svg__a"/><path fill-rule="nonzero" d="M26.828 24l4.243-4.243a2 2 0 1 0-2.828-2.828L24 21.172l-4.243-4.243a2 2 0 1 0-2.828 2.828L21.172 24l-4.243 4.243a2 2 0 1 0 2.828 2.828L24 26.828l4.243 4.243a2 2 0 1 0 2.828-2.828L26.828 24zM24 46C11.85 46 2 36.15 2 24S11.85 2 24 2s22 9.85 22 22-9.85 22-22 22z"/></g></svg>', 'close-large': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="close-large_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="close-large_svg__b" fill="#fff"><use xlink:href="#close-large_svg__a"/></mask><use fill-opacity=".01" xlink:href="#close-large_svg__a"/><path fill-rule="nonzero" d="M24 20.172L38.586 5.586a2 2 0 0 1 2.828 2.828L26.828 23l14.586 14.586a2 2 0 0 1-2.828 2.828L24 25.828 9.414 40.414a2 2 0 0 1-2.828-2.828L21.172 23 6.586 8.414a2 2 0 1 1 2.828-2.828L24 20.172z"/></g></svg>', 'close': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="close_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="close_svg__b" fill="#fff"><use xlink:href="#close_svg__a"/></mask><use fill="#FFF" fill-opacity=".01" xlink:href="#close_svg__a"/><path fill-rule="nonzero" d="M24 21.172l8.586-8.586a2 2 0 0 1 2.828 2.828L26.828 24l8.586 8.586a2 2 0 0 1-2.828 2.828L24 26.828l-8.586 8.586a2 2 0 0 1-2.828-2.828L21.172 24l-8.586-8.586a2 2 0 0 1 2.828-2.828L24 21.172z"/></g></svg>', 'copy': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="100%" height="100%"><g fill-rule="evenodd"><path fill="#FFF" fill-opacity=".01" d="M0 0h14v14H0z"/><path fill-rule="nonzero" d="M2.917 2.917v-.875c0-.806.653-1.459 1.458-1.459h7.583c.806 0 1.459.653 1.459 1.459v7.583c0 .805-.653 1.458-1.459 1.458h-.875v.875c0 .806-.653 1.459-1.458 1.459H2.042a1.458 1.458 0 0 1-1.459-1.459V4.375c0-.805.653-1.458 1.459-1.458h.875zm8.166 7h.875c.161 0 .292-.13.292-.292V2.042a.292.292 0 0 0-.292-.292H4.375a.292.292 0 0 0-.292.292v.875h5.542c.805 0 1.458.653 1.458 1.458v5.542zM2.042 4.083a.292.292 0 0 0-.292.292v7.583c0 .161.13.292.292.292h7.583c.161 0 .292-.13.292-.292V4.375a.292.292 0 0 0-.292-.292H2.042z"/></g></svg>', 'dashboard': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="dashboard_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="dashboard_svg__b" fill="#fff"><use xlink:href="#dashboard_svg__a"/></mask><use opacity=".01" xlink:href="#dashboard_svg__a"/><g><path fill-rule="nonzero" d="M11.046 12.1l2.476 2.005a2 2 0 1 1-2.517 3.109L8.42 15.12A18.913 18.913 0 0 0 5 26c0 .43.014.856.043 1.28l3.882-.896a2 2 0 0 1 .9 3.898l-3.898.9a2.023 2.023 0 0 1-.202.036 18.951 18.951 0 0 0 4.84 8.217 2 2 0 0 1-2.828 2.828A22.93 22.93 0 0 1 1 26C1 13.297 11.297 3 24 3s23 10.297 23 23a22.93 22.93 0 0 1-6.737 16.263 2 2 0 0 1-2.828-2.828c2.3-2.3 3.959-5.122 4.84-8.217a2.023 2.023 0 0 1-.202-.037l-3.898-.9a2 2 0 0 1 .9-3.897l3.882.897a18.913 18.913 0 0 0-3.378-12.16l-2.584 2.093a2 2 0 1 1-2.517-3.109l2.476-2.005A18.931 18.931 0 0 0 26 7.104V10a2 2 0 1 1-4 0V7.104A18.931 18.931 0 0 0 11.046 12.1zm21.136 9.347c-2.27 6.397-3.9 10.055-5.21 11.412a6 6 0 0 1-8.632-8.336c1.31-1.357 4.91-3.114 11.223-5.605 1.597-.63 3.193.912 2.62 2.53zm-10.965 5.855a2 2 0 1 0 2.878 2.779c.53-.55 1.483-2.51 2.738-5.715-3.16 1.366-5.085 2.387-5.616 2.936z"/></g></g></svg>', 'delete': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="delete_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="delete_svg__b" fill="#fff"><use xlink:href="#delete_svg__a"/></mask><use opacity=".01" xlink:href="#delete_svg__a"/><g fill-rule="nonzero"><path d="M9 14H6a2 2 0 1 1 0-4h8.264l2.947-5.894A2 2 0 0 1 19 3h10a2 2 0 0 1 1.789 1.106L33.736 10H42a2 2 0 1 1 0 4h-3v29a2 2 0 0 1-2 2H11a2 2 0 0 1-2-2V14zm4 0v27h22V14H13zm5.736-4h10.528l-1.5-3h-7.528l-1.5 3zM18 21a2 2 0 1 1 4 0v12a2 2 0 1 1-4 0V21zm8 0a2 2 0 1 1 4 0v12a2 2 0 1 1-4 0V21z"/></g></g></svg>', 'double-angle-down': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="double-angle-down_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="double-angle-down_svg__b" fill="#fff"><use xlink:href="#double-angle-down_svg__a"/></mask><use opacity=".01" xlink:href="#double-angle-down_svg__a"/><path fill-rule="nonzero" d="M24 21.91l10.586-10.586a2 2 0 0 1 2.828 2.828l-12 12a2 2 0 0 1-2.828 0l-12-12a2 2 0 0 1 2.828-2.828L24 21.91zm-10.586 1.414L24 33.91l10.586-10.586a2 2 0 0 1 2.828 2.828l-12 12a2 2 0 0 1-2.828 0l-12-12a2 2 0 0 1 2.828-2.828z"/></g></svg>', 'double-angle-left': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 16 16" width="100%" height="100%"><defs><path id="double-angle-left_svg__a" d="M0 0h16v16H0z"/></defs><g fill-rule="evenodd"><mask id="double-angle-left_svg__b" fill="#fff"><use xlink:href="#double-angle-left_svg__a"/></mask><use fill="#FFF" opacity=".01" xlink:href="#double-angle-left_svg__a"/><path fill-rule="nonzero" d="M8.471 4.471L4.943 8l3.528 3.529a.667.667 0 1 1-.942.942l-4-4a.667.667 0 0 1 0-.942l4-4a.667.667 0 1 1 .942.942zm4 0L8.943 8l3.528 3.529a.667.667 0 0 1-.942.942l-4-4a.667.667 0 0 1 0-.942l4-4a.667.667 0 1 1 .942.942z"/></g></svg>', 'double-angle-right': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 16 16" width="100%" height="100%"><defs><path id="double-angle-right_svg__a" d="M0 0h16v16H0z"/></defs><g fill-rule="evenodd"><mask id="double-angle-right_svg__b" fill="#fff"><use xlink:href="#double-angle-right_svg__a"/></mask><use fill="#FFF" opacity=".01" xlink:href="#double-angle-right_svg__a"/><path fill-rule="nonzero" d="M8.471 4.471L4.943 8l3.528 3.529a.667.667 0 1 1-.942.942l-4-4a.667.667 0 0 1 0-.942l4-4a.667.667 0 1 1 .942.942zm4 0L8.943 8l3.528 3.529a.667.667 0 0 1-.942.942l-4-4a.667.667 0 0 1 0-.942l4-4a.667.667 0 1 1 .942.942z" transform="matrix(-1 0 0 1 16 0)"/></g></svg>', 'edit': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="edit_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="edit_svg__b" fill="#fff"><use xlink:href="#edit_svg__a"/></mask><use opacity=".01" xlink:href="#edit_svg__a"/><path fill-rule="nonzero" d="M5.725 43.71a2 2 0 1 1 0-4h36a2 2 0 1 1 0 4h-36zm2-17.28a2 2 0 0 1 .585-1.413l20.695-20.72a2 2 0 0 1 2.83 0l7.304 7.308a2 2 0 0 1 0 2.827L18.456 35.124a2 2 0 0 1-1.414.586H9.725a2 2 0 0 1-2-2v-7.28zm4 .828v4.452h4.488L34.897 13.02 30.42 8.54 11.725 27.258z"/></g></svg>', 'empty': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38 37" width="100%" height="100%"><path fill-rule="evenodd" d="M11.492 6.303a.805.805 0 0 1-.806-.799c0-.44.36-.798.806-.798h14.791c.446 0 .806.357.806.798 0 .442-.36.799-.806.799H11.492zm-1.186 4.112a.8.8 0 0 1-.806-.799c0-.44.36-.798.806-.798h16.806c.445 0 .806.357.806.798 0 .441-.36.799-.806.799H10.306zm-1.19 4.115a.804.804 0 0 1-.805-.798c0-.441.36-.798.806-.798H28.4c.446 0 .807.357.807.798 0 .441-.361.798-.807.798H9.117zm15.507 12.5c.592 0 1.075.478 1.078 1.068 0 .59-.482 1.068-1.078 1.068H12.95a1.072 1.072 0 0 1-1.079-1.068c0-.59.482-1.068 1.079-1.068h11.673zm10.019 6.456h-.004v-13.21a.804.804 0 0 0-.807-.799h-9.264a6.654 6.654 0 0 1-5.898 3.572 6.652 6.652 0 0 1-5.903-3.572H3.502a.802.802 0 0 0-.807.798v13.21c0 .438.361.8.807.8h30.333a.805.805 0 0 0 .807-.8zM7.976 3.572l-4.183 13.24h9.86c.575 0 1.087.361 1.274.9a3.964 3.964 0 0 0 3.742 2.673 3.964 3.964 0 0 0 3.74-2.672c.185-.54.7-.904 1.274-.904h9.861L29.361 3.576c-.136-.43-.758-.908-1.395-.908H9.367c-.567 0-1.244.438-1.391.904zM36.995 18.81c.213.445.342.94.342 1.465v13.21c0 1.911-1.572 3.468-3.502 3.468H3.502C1.572 36.953 0 35.396 0 33.486v-13.21c0-.526.129-1.018.339-1.466L5.405 2.778C5.902 1.196 7.607 0 9.371 0h18.595c1.764 0 3.468 1.196 3.966 2.781l5.063 16.029z"/></svg>', 'exclamation-circle': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="exclamation-circle_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="exclamation-circle_svg__b" fill="#fff"><use xlink:href="#exclamation-circle_svg__a"/></mask><use fill="#FFF" opacity=".01" xlink:href="#exclamation-circle_svg__a"/><path fill-rule="nonzero" d="M24 46a21.933 21.933 0 0 1-15.556-6.444A21.933 21.933 0 0 1 2 24 21.933 21.933 0 0 1 8.444 8.444 21.933 21.933 0 0 1 24 2a21.933 21.933 0 0 1 15.556 6.444A21.933 21.933 0 0 1 46 24a21.933 21.933 0 0 1-6.444 15.556A21.933 21.933 0 0 1 24 46zm0-10a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm-2-24v16a2 2 0 1 0 4 0V12a2 2 0 1 0-4 0z"/></g></svg>', 'filter': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="filter_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="filter_svg__b" fill="#fff"><use xlink:href="#filter_svg__a"/></mask><use opacity=".01" xlink:href="#filter_svg__a"/><path fill-rule="nonzero" d="M18.4 24.557v11.887a2 2 0 0 0 1.114 1.794l7.2 3.555A2 2 0 0 0 29.6 40V24.557L43.52 8.301C44.63 7.003 43.707 5 42 5H6C4.292 5 3.37 7.003 4.48 8.3L18.4 24.558zM10.345 9h27.31L26.08 22.517a2 2 0 0 0-.481 1.3v12.965l-3.2-1.58V23.818a2 2 0 0 0-.48-1.301L10.344 9z"/></g></svg>', 'full-screen': '<svg xmlns="http://www.w3.org/2000/svg" class="full-screen_svg__icon" viewBox="0 0 1024 1024" width="100%" height="100%"><path fill="#FFF" fill-opacity=".01" d="M0 0h1024v1024H0z"/><path d="M714.965 652.501l138.368 138.326V704a42.667 42.667 0 0 1 85.035-4.992l.299 4.992v192a42.667 42.667 0 0 1-37.675 42.368l-4.992.299H704a42.667 42.667 0 0 1-4.992-85.035l4.992-.299h91.136L654.635 712.832a42.667 42.667 0 1 1 60.33-60.33zm-343.296.128a42.667 42.667 0 0 1-.298 60.374l-141.824 140.33H320a42.667 42.667 0 0 1 4.992 85.035l-4.992.299H128a42.667 42.667 0 0 1-42.368-37.675L85.333 896V704a42.667 42.667 0 0 1 85.035-4.992l.299 4.992v87.552l140.629-139.221a42.667 42.667 0 0 1 60.373.298zM320 85.333a42.667 42.667 0 0 1 4.992 85.035l-4.992.299h-88.277l139.648 138.24a42.667 42.667 0 0 1 3.84 56.277l-3.542 4.01a42.667 42.667 0 0 1-56.32 3.84l-4.053-3.54-140.63-139.222V320a42.667 42.667 0 0 1-85.034 4.992L85.333 320V126.976l.043-1.28v1.28l.128-2.56.299-2.645a45.44 45.44 0 0 1 1.706-7.339l.683-1.835a42.752 42.752 0 0 1 5.248-9.642l1.195-1.536.128-.128.682-.854a43.093 43.093 0 0 1 2.219-2.474l-2.133 2.432 2.176-2.432a35.513 35.513 0 0 1 3.84-3.414l.981-.768a44.16 44.16 0 0 1 7.21-4.352l2.56-1.109a41.643 41.643 0 0 1 10.71-2.688l1.45-.17 2.134-.086L320 85.333zm576 0a42.667 42.667 0 0 1 42.368 37.675l.299 4.992v192a42.667 42.667 0 0 1-85.035 4.992l-.299-4.992v-91.179L712.832 369.365a42.667 42.667 0 0 1-56.32 3.542l-4.01-3.542a42.667 42.667 0 0 1 0-60.33l138.282-138.368H704a42.667 42.667 0 0 1-4.992-85.035l4.992-.299h192z"/></svg>', 'in': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="in_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="in_svg__b" fill="#fff"><use xlink:href="#in_svg__a"/></mask><use fill="#FFF" fill-opacity=".01" xlink:href="#in_svg__a"/><path fill-rule="nonzero" d="M20.837 21.992H42a2 2 0 1 1 0 4H20.82l5.594 5.594a2 2 0 0 1-2.828 2.828l-9-9A1.994 1.994 0 0 1 14 23.992c0-.608.271-1.152.699-1.52l8.887-8.886a2 2 0 0 1 2.828 2.828l-5.577 5.578zM8 40h16a2 2 0 1 1 0 4H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h17.992a2 2 0 1 1 0 4H8v32z"/></g></svg>', 'info-circle': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="info-circle_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="info-circle_svg__b" fill="#fff"><use xlink:href="#info-circle_svg__a"/></mask><use fill="#FFF" opacity=".01" xlink:href="#info-circle_svg__a"/><path fill-rule="nonzero" d="M26 32V20a2 2 0 0 0-2-2h-2a2 2 0 1 0 0 4v10h-1a2 2 0 1 0 0 4h6a2 2 0 1 0 0-4h-1zm-2 14a21.933 21.933 0 0 1-15.556-6.444A21.933 21.933 0 0 1 2 24 21.933 21.933 0 0 1 8.444 8.444 21.933 21.933 0 0 1 24 2a21.933 21.933 0 0 1 15.556 6.444A21.933 21.933 0 0 1 46 24a21.933 21.933 0 0 1-6.444 15.556A21.933 21.933 0 0 1 24 46zm0-34a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/></g></svg>', 'line-chart': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="line-chart_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="line-chart_svg__b" fill="#fff"><use xlink:href="#line-chart_svg__a"/></mask><use opacity=".01" xlink:href="#line-chart_svg__a"/><g fill-rule="nonzero"><path d="M8 40h34a2 2 0 1 1 0 4H6a2 2 0 0 1-2-2V6a2 2 0 1 1 4 0v34zm22.662-11.513l-8.048-7.243-6.825 13.65a2 2 0 1 1-3.578-1.788l8-16a2 2 0 0 1 3.127-.593l8.008 7.208 8.848-18.58a2 2 0 1 1 3.612 1.719l-10 21a2 2 0 0 1-3.144.627z"/></g></g></svg>', 'link': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="100%" height="100%"><g fill-rule="evenodd"><path fill="#FFF" fill-opacity=".01" d="M0 0h14v14H0z"/><path fill-rule="nonzero" d="M10.842 2.333H7.583a.583.583 0 0 1 0-1.166h4.667c.322 0 .583.26.583.583v4.667a.583.583 0 1 1-1.166 0V3.158l-3.73 3.73a.583.583 0 0 1-.824-.825l3.729-3.73zm.825 6.263a.583.583 0 1 1 1.166 0v2.779c0 .805-.653 1.458-1.458 1.458h-8.75a1.458 1.458 0 0 1-1.458-1.458v-8.75c0-.805.653-1.458 1.458-1.458H5.25a.583.583 0 0 1 0 1.166H2.625a.292.292 0 0 0-.292.292v8.75c0 .161.13.292.292.292h8.75c.161 0 .292-.13.292-.292V8.596z"/></g></svg>', 'lock': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="lock_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="lock_svg__b" fill="#fff"><use xlink:href="#lock_svg__a"/></mask><use opacity=".01" xlink:href="#lock_svg__a"/><path fill-rule="nonzero" d="M12 19.738v-6c0-6.627 5.373-12 12-12s12 5.373 12 12v6h6a2 2 0 0 1 2 2v22a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-22a2 2 0 0 1 2-2h6zm4 0h16v-6a8 8 0 0 0-16 0v6zm-8 4v18h32v-18H8zm14 6a2 2 0 0 1 4 0v6a2 2 0 1 1-4 0v-6z"/></g></svg>', 'minus-square': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="minus-square_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="minus-square_svg__b" fill="#fff"><use xlink:href="#minus-square_svg__a"/></mask><use opacity=".01" xlink:href="#minus-square_svg__a"/><path fill-rule="nonzero" d="M9 4.738h30a5 5 0 0 1 5 5v30a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-30a5 5 0 0 1 5-5zm7.004 22h16a2 2 0 1 0 0-4h-16a2 2 0 0 0 0 4z"/></g></svg>', 'minus': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="100%" height="100%"><g fill-rule="evenodd"><path fill="#FFF" d="M0 0h48v48H0z" opacity=".01"/><path fill-rule="nonzero" d="M10.5 26h28a2 2 0 1 0 0-4h-28a2 2 0 1 0 0 4z"/></g></svg>', 'more': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="more_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="more_svg__b" fill="#fff"><use xlink:href="#more_svg__a"/></mask><use fill="#FFF" fill-opacity=".01" xlink:href="#more_svg__a"/><path d="M24 39a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0-12a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0-12a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/></g></svg>', 'origin-screen': '<svg xmlns="http://www.w3.org/2000/svg" class="origin-screen_svg__icon" viewBox="0 0 1024 1024" width="100%" height="100%"><path fill="#FFF" fill-opacity=".01" d="M0 0h1024v1024H0z"/><path d="M872.533 640a42.667 42.667 0 0 1 0 85.333H787.84l138.325 138.368a42.667 42.667 0 0 1-60.33 60.331L725.333 783.531v91.136a42.667 42.667 0 0 1-37.674 42.368l-4.992.298A42.667 42.667 0 0 1 640 874.667v-192A42.667 42.667 0 0 1 682.667 640h189.866zm51.499-542.165a42.667 42.667 0 0 1 3.541 56.32l-3.541 4.01-140.544 140.502h91.179a42.667 42.667 0 0 1 42.368 37.674l.298 4.992a42.667 42.667 0 0 1-37.674 42.368l-4.992.299h-192a42.667 42.667 0 0 1-42.368-37.675l-.299-4.992v-192a42.667 42.667 0 0 1 85.035-4.992l.298 4.992v86.784L863.701 97.835a42.667 42.667 0 0 1 60.331 0zm-770.048-3.67l4.053 3.499 140.63 139.264v-87.595a42.667 42.667 0 0 1 37.674-42.368l4.992-.298a42.667 42.667 0 0 1 42.368 37.674l.299 4.992v192a42.667 42.667 0 0 1-37.675 42.368l-4.992.299h-192a42.667 42.667 0 0 1-4.992-85.035l4.992-.298h90.411L97.963 158.293a42.667 42.667 0 0 1 56.064-64.085zm221.867 563.456a44.288 44.288 0 0 1 3.2 5.035l.938 1.92 1.152 2.688.64 1.792a43.947 43.947 0 0 1 1.75 7.381l.341 3.072.043.598-.086-.598.171 3.158v192a42.667 42.667 0 0 1-85.035 4.992l-.298-4.992v-89.771L158.037 924.16a42.667 42.667 0 0 1-60.074-60.587l139.605-138.24h-88.235a42.667 42.667 0 0 1-42.368-37.674l-.298-4.992a42.667 42.667 0 0 1 37.674-42.368l4.992-.299h193.451a42.923 42.923 0 0 1 16.81 4.096l2.134 1.067a39.595 39.595 0 0 1 5.12 3.285l.939.725 3.84 3.414 2.133 2.474-2.133-2.432 2.261 2.432.768 1.024 1.237 1.536z"/></svg>', 'out': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="out_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="out_svg__b" fill="#fff"><use xlink:href="#out_svg__a"/></mask><use fill="#FFF" fill-opacity=".01" xlink:href="#out_svg__a"/><path fill-rule="nonzero" d="M43.301 22.473A1.996 1.996 0 0 1 44 23.996a1.994 1.994 0 0 1-.586 1.418l-9 9a2 2 0 0 1-2.828-2.828l5.594-5.594H16a2 2 0 1 1 0-4h21.163l-5.577-5.578a2 2 0 0 1 2.828-2.828l8.887 8.887zM8 40h16a2 2 0 1 1 0 4H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h17.992a2 2 0 1 1 0 4H8v32z"/></g></svg>', 'play-circle': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 49 49" width="100%" height="100%"><defs><path id="play-circle_svg__a" d="M0 .291h48v48H0z"/></defs><g fill-rule="evenodd" transform="translate(.904 .614)"><mask id="play-circle_svg__b" fill="#fff"><use xlink:href="#play-circle_svg__a"/></mask><use opacity=".01" xlink:href="#play-circle_svg__a"/><g fill-rule="nonzero"><path d="M24 46.386c-12.15 0-22-9.85-22-22s9.85-22 22-22 22 9.85 22 22-9.85 22-22 22zm0-4c9.941 0 18-8.06 18-18 0-9.941-8.059-18-18-18s-18 8.059-18 18c0 9.94 8.059 18 18 18zm-6-24.997c0-1.54 1.667-2.502 3-1.732l12 6.928c1.333.77 1.333 2.694 0 3.464l-12 6.928c-1.333.77-3-.192-3-1.732V17.39zm4 10.392l6-3.464-6-3.464v6.928z"/></g></g></svg>', 'plus': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="100%" height="100%"><g fill-rule="nonzero"><path fill="#FFF" d="M0 0h48v48H0z" opacity=".01"/><path d="M26.045 22H38a2 2 0 1 1 0 4H26.04l-.016 12.003a2 2 0 0 1-4-.006L22.04 26H10a2 2 0 1 1 0-4h12.045l.016-12.003a2 2 0 1 1 4 .006L26.045 22z"/></g></svg>', 'question-circle': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 14 14" width="100%" height="100%"><defs><path id="question-circle_svg__a" d="M0 0h14v14H0z"/></defs><g fill-rule="evenodd"><mask id="question-circle_svg__b" fill="#fff"><use xlink:href="#question-circle_svg__a"/></mask><path fill-rule="nonzero" d="M7 13.417a6.397 6.397 0 0 1-4.537-1.88A6.397 6.397 0 0 1 .583 7c0-1.724.685-3.342 1.88-4.537A6.397 6.397 0 0 1 7 .583c1.724 0 3.342.685 4.537 1.88A6.397 6.397 0 0 1 13.417 7a6.397 6.397 0 0 1-1.88 4.537A6.397 6.397 0 0 1 7 13.417zm0-1.167a5.23 5.23 0 0 0 3.712-1.538A5.23 5.23 0 0 0 12.25 7a5.23 5.23 0 0 0-1.538-3.712A5.23 5.23 0 0 0 7 1.75a5.23 5.23 0 0 0-3.712 1.538A5.23 5.23 0 0 0 1.75 7a5.23 5.23 0 0 0 1.538 3.712A5.23 5.23 0 0 0 7 12.25zm.583-4.74v.657a.583.583 0 0 1-1.166 0V7c0-.322.26-.583.583-.583A1.167 1.167 0 1 0 5.833 5.25a.583.583 0 0 1-1.166 0 2.333 2.333 0 1 1 2.916 2.26zM7 10.5a.583.583 0 1 0 0-1.167.583.583 0 0 0 0 1.167z"/></g></svg>', 'question-triangle': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 44 42" width="100%" height="100%"><defs><path id="question-triangle_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd" transform="translate(-2 -3)"><mask id="question-triangle_svg__b" fill="#fff"><use xlink:href="#question-triangle_svg__a"/></mask><g fill-rule="nonzero"><path d="M22.238 4.554c.755-1.406 2.771-1.405 3.525.001l19.966 37.26c.713 1.333-.252 2.945-1.763 2.945H4c-1.512 0-2.477-1.614-1.762-2.946l20-37.26zm1.76 5.178L7.343 40.76h33.282L23.998 9.732zm-2.028 12.52a2 2 0 1 1 4 0v7a2 2 0 1 1-4 0v-7zm1.996 15.035a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/></g></g></svg>', 'rotate': '<svg xmlns="http://www.w3.org/2000/svg" class="rotate_svg__icon" viewBox="0 0 1024 1024" width="100%" height="100%"><path fill="#FFF" fill-opacity=".01" d="M0 0h1024v1024H0z"/><path d="M896 469.333A42.667 42.667 0 0 1 938.667 512v384A42.667 42.667 0 0 1 896 938.667H256A42.667 42.667 0 0 1 213.333 896V512A42.667 42.667 0 0 1 256 469.333h640zm-42.667 85.334H298.667v298.666h554.666V554.667zM512 85.333a426.027 426.027 0 0 1 343.083 172.971 42.667 42.667 0 1 1-68.566 50.773A340.736 340.736 0 0 0 512 170.667c-107.52 0-206.677 50.005-273.707 136.96l-11.648 12.33L320 320a42.667 42.667 0 0 1 4.992 85.035l-4.992.298H127.53l-2.56-.128 3.03.128a43.093 43.093 0 0 1-24.32-7.594 42.667 42.667 0 0 1-4.907-3.968l3.584 2.986a42.88 42.88 0 0 1-3.2-2.645l-.384-.341a43.605 43.605 0 0 1-9.088-12.374 43.35 43.35 0 0 1-4.053-13.738l-.085-.854a42.923 42.923 0 0 1-.128-1.792l-.086-2.346v-192a42.667 42.667 0 0 1 85.035-4.992l.299 4.992v84.608l2.688-2.859A425.941 425.941 0 0 1 498.133 85.589L512 85.333z"/></svg>', 'search': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="search_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="search_svg__b" fill="#fff"><use xlink:href="#search_svg__a"/></mask><use opacity=".01" xlink:href="#search_svg__a"/><path fill-rule="nonzero" d="M33.947 36.513A18.92 18.92 0 0 1 22 40.738c-10.493 0-19-8.506-19-19 0-10.493 8.507-19 19-19s19 8.507 19 19a18.92 18.92 0 0 1-4.225 11.947l7.346 7.346a2 2 0 1 1-2.828 2.829l-7.346-7.347zM22 36.738c8.284 0 15-6.715 15-15 0-8.284-6.716-15-15-15-8.284 0-15 6.716-15 15 0 8.285 6.716 15 15 15zm7.071-23.07a2 2 0 0 1-2.828 2.828A5.974 5.974 0 0 0 22 14.738a5.974 5.974 0 0 0-4.243 1.758 2 2 0 0 1-2.828-2.829A9.973 9.973 0 0 1 22 10.738a9.973 9.973 0 0 1 7.071 2.93z"/></g></svg>', 'sort-asc': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="100%" height="100%"><defs><path id="sort-asc_svg__a" d="M0 0h48v48H0z"/></defs><g fill-rule="evenodd"><mask id="sort-asc_svg__b" fill="#fff"><use xlink:href="#sort-asc_svg__a"/></mask><use fill="#FFF" fill-opacity=".01" xlink:href="#sort-asc_svg__a"/><path fill-rule="nonzero" d="M16 11c0-1.782 2.154-2.674 3.414-1.414l12 12a2 2 0 0 1 0 2.828l-12 12C18.154 37.674 16 36.782 16 35V11z" transform="rotate(-90 24 23)"/></g></svg>', 'star-empty': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="100%" height="100%"><g fill-rule="evenodd"><path d="M11.86 5.318c-.851 1.037-2.356 2.747-2.356 2.747s.246 1.878.384 3.16c.06.654-.411.937-.945.682-1.016-.51-2.561-1.3-2.91-1.478-.353.176-1.914.953-2.94 1.462-.54.254-1.017-.028-.957-.68.14-1.281.389-3.158.389-3.158S1.003 6.346.142 5.312c-.304-.376-.11-.91.49-1.009 1.224-.23 3.12-.6 3.12-.6S4.79 1.794 5.447.624c.362-.712.611-.63.654-.61.111.04.287.178.517.61C7.265 1.795 8.29 3.71 8.29 3.71s1.874.368 3.083.6c.593.099.787.634.486 1.01z"/></g></svg>', 'star-filled': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="100%" height="100%"><g fill-rule="evenodd"><path d="M11.86 5.318c-.851 1.037-2.356 2.747-2.356 2.747s.246 1.878.384 3.16c.06.654-.411.937-.945.682-1.016-.51-2.561-1.3-2.91-1.478-.353.176-1.914.953-2.94 1.462-.54.254-1.017-.028-.957-.68.14-1.281.389-3.158.389-3.158S1.003 6.346.142 5.312c-.304-.376-.11-.91.49-1.009 1.224-.23 3.12-.6 3.12-.6S4.79 1.794 5.447.624c.362-.712.611-.63.654-.61.111.04.287.178.517.61C7.265 1.795 8.29 3.71 8.29 3.71s1.874.368 3.083.6c.593.099.787.634.486 1.01z"/></g></svg>', 'to-top': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="100%" height="100%"><g fill-rule="evenodd"><path fill-rule="nonzero" d="M15.057 14.39c.52-.52 1.365-.52 1.886 0l8 8a1.333 1.333 0 0 1-1.886 1.886L16 17.22l-7.057 7.057a1.333 1.333 0 0 1-1.886-1.885zM24 8.668a1.333 1.333 0 1 1 0 2.666H8a1.333 1.333 0 1 1 0-2.666z"/><path fill-opacity=".01" d="M0 0h32v32H0z"/></g></svg>', 'tool': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="100%" height="100%"><g fill-rule="evenodd"><path fill="#FFF" fill-opacity=".01" d="M0 0h48v48H0z"/><path fill-rule="nonzero" d="M20.521 29.713A13.004 13.004 0 0 1 5.513 11.362a2 2 0 0 1 3.205-.523l3.765 3.765 2.122-2.121-3.766-3.766a2 2 0 0 1 .523-3.204A13.004 13.004 0 0 1 29.714 20.52l13.79 13.79a2 2 0 0 1 0 2.828l-6.364 6.364a2 2 0 0 1-2.828 0l-13.79-13.79zm-9.73-6.195a9.001 9.001 0 0 0 9.57 2.048 2 2 0 0 1 2.126.456L35.726 39.26l3.535-3.536-13.239-13.239a2 2 0 0 1-.455-2.125 9.001 9.001 0 0 0-2.048-9.57 9.002 9.002 0 0 0-7.514-2.565l2.842 2.842a2 2 0 0 1 0 2.829l-4.95 4.95a2 2 0 0 1-2.828 0l-2.843-2.843a9.002 9.002 0 0 0 2.565 7.514z"/></g></svg>', 'upload': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="100%" height="100%"><g fill-rule="nonzero"><path fill-opacity=".01" d="M0 0h16v16H0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M8.667 8.575v4.092a.667.667 0 0 1-1.334 0v-4.08L6.161 9.765a.667.667 0 1 1-.945-.941l2.312-2.32c.26-.26.68-.26.942-.002l2.377 2.36a.667.667 0 0 1-.94.946l-1.24-1.232zM4.373 7.22a.664.664 0 0 1-.326.187 2.668 2.668 0 1 0 1.349 5.16.667.667 0 0 1 .363 1.283A4.002 4.002 0 0 1 3.354 6.22a4.667 4.667 0 0 1 9.295.033 4 4 0 1 1-2.497 7.595.667.667 0 0 1 .363-1.282A2.669 2.669 0 0 0 13.911 10a2.668 2.668 0 0 0-2.047-2.594.667.667 0 0 1-.507-.561.667.667 0 0 1-.024-.178 3.333 3.333 0 1 0-6.666 0c0 .23-.117.432-.294.552z"/></g></svg>', 'user': '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 24 24" width="100%" height="100%"><defs><path id="user_svg__a" d="M19.52 23.535c-.863.003-5.23.003-5.656-.007h-.558l-.89.007H1.173a.47.47 0 0 1-.472-.47v-.566c-.008-.157-.023-1.021.53-1.97.346-.594.843-1.103 1.477-1.515.77-.499 1.743-.853 2.895-1.053.026-.004.913-.125 1.813-.362 1.56-.41 1.697-.773 1.698-.777a.471.471 0 0 1 .04-.103c.012-.067.044-.323-.017-1.008-.153-1.74-1.062-2.767-1.791-3.593-.23-.26-.448-.506-.615-.748-.722-1.04-.79-2.224-.792-2.274 0-.496.236-.713.444-.905.027-.058.01-.143 0-.201-.195-1.047-.207-2.562-.043-3.534.06-1.278 1.165-2.257 1.213-2.298.164-.14.325-.286.427-.449.02-.066.008-.09 0-.111-.062-.142-.155-.355-.102-.63.032-.166.153-.462.608-.583.183-.049.379-.059.522-.066C9.6.288 10.202.263 10.812.31c4.405.337 5.923 1.98 5.985 2.05.363.433.624.959.787 1.573.05.192.083.352.102.505.154.892.129 1.809.096 2.422-.019.344-.057.705-.121 1.136-.018.117.002.14.009.149.019.023.042.047.067.074.097.103.23.244.302.47.035.114.052.236.052.385-.003.073-.07 1.257-.792 2.297a8.605 8.605 0 0 1-.615.748c-.73.826-1.638 1.854-1.792 3.593-.06.685-.029.94-.016 1.009.017.032.03.067.04.103 0 .003.138.367 1.705.778.9.237 1.78.356 1.788.358 1.185.218 2.165.582 2.935 1.083.637.415 1.133.923 1.475 1.512.542.934.52 1.78.51 1.949v.561a.47.47 0 0 1-.472.47H19.52zm-4.764-6.962zm-5.38 0z"/></defs><use fill-rule="evenodd" xlink:href="#user_svg__a"/></svg>', 'zoom-in': '<svg xmlns="http://www.w3.org/2000/svg" class="zoom-in_svg__icon" viewBox="0 0 1024 1024" width="100%" height="100%"><path fill="#FFF" fill-opacity=".01" d="M0 0h1024v1024H0z"/><path d="M448 42.667A405.333 405.333 0 0 1 853.333 448a403.627 403.627 0 0 1-90.112 254.848l156.715 156.715a42.667 42.667 0 0 1-60.373 60.373L702.848 763.221A403.627 403.627 0 0 1 448 853.333a405.333 405.333 0 0 1 0-810.666zM448 128a320 320 0 1 0 0 640 320 320 0 0 0 0-640zm0 149.333a42.667 42.667 0 0 1 42.368 37.675l.299 4.992v85.29l85.333.043a42.667 42.667 0 0 1 42.368 37.675l.299 4.992a42.667 42.667 0 0 1-37.675 42.368l-4.992.299-85.333-.043V576a42.667 42.667 0 0 1-85.035 4.992l-.299-4.992v-85.376l-85.333.043a42.667 42.667 0 0 1-42.368-37.675l-.299-4.992a42.667 42.667 0 0 1 37.675-42.368l4.992-.299 85.333-.042V320A42.667 42.667 0 0 1 448 277.333z"/></svg>', 'zoom-out': '<svg xmlns="http://www.w3.org/2000/svg" class="zoom-out_svg__icon" viewBox="0 0 1024 1024" width="100%" height="100%"><path fill="#FFF" fill-opacity=".01" d="M0 0h1024v1024H0z"/><path d="M448 42.667A405.333 405.333 0 0 1 853.333 448a403.627 403.627 0 0 1-90.112 254.848l156.715 156.715a42.667 42.667 0 0 1-60.373 60.373L702.848 763.221A403.627 403.627 0 0 1 448 853.333a405.333 405.333 0 0 1 0-810.666zM448 128a320 320 0 1 0 0 640 320 320 0 0 0 0-640zM320 405.333h256a42.667 42.667 0 0 1 4.992 85.035l-4.992.299H320a42.667 42.667 0 0 1-4.992-85.035l4.992-.299h256-256z"/></svg>' };

/***/ }),

/***/ 129:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{class:_vm.baseClass,style:({ fill: _vm.color, width: _vm.width,height:_vm.height }),domProps:{"innerHTML":_vm._s(_vm.content)},on:{"click":_vm.handleClick}})};
var staticRenderFns = [];


/***/ }),

/***/ 74:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _buiIconAddon = __webpack_require__(128);

var _buiIconAddon2 = _interopRequireDefault(_buiIconAddon);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

//
//
//
//
//
//
//
//

exports.default = {
  name: _base.PREFIX + 'icon',

  props: {
    name: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: ''
    },
    width: {
      type: String,
      default: ''
    },
    height: {
      type: String,
      default: ''
    }
  },

  data: function data() {
    return {
      CSS_PREFIX: _base.CSS_PREFIX
    };
  },

  computed: {
    baseClass: function baseClass() {
      var classArr = [_base.CSS_PREFIX + 'icon'];
      classArr.push('bui-icon-' + this.name);
      return classArr;
    },

    content: function content() {
      var iconStr = '';
      if (window.BUI && window.BUI.iconAddon && window.BUI.iconAddon[this.name]) {
        iconStr = window.BUI.iconAddon[this.name];
      } else {
        iconStr = _buiIconAddon2.default[this.name]; // || require(`!html-loader!./../svg/${this.name}.svg`);
      }
      return iconStr;
    }
  },

  methods: {
    handleClick: function handleClick(e) {
      this.$emit('click', e);
    }
  }
};

/***/ })

/******/ });
});

var _Icon = /*@__PURE__*/getDefaultExportFromCjs(icon);

var css_248z$a = "@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.by-popover-wrapper{display:inline-block}.by-popover{position:relative;z-index:1051}.by-popover .bui-popover-panel{padding:16px;background-color:#FFF;border-radius:4px;-webkit-box-shadow:0 2px 6px 0 rgba(0,0,0,.15);box-shadow:0 2px 6px 0 rgba(0,0,0,.15);color:#666}.by-popover .bui-popover-panel .bui-popover-header{font-size:14px;line-height:22px;font-weight:700;margin-bottom:8px;color:#333}.by-popover .bui-popover-panel .bui-popover-body{color:#666;font-size:12px;line-height:22px;white-space:normal}.by-popover .bui-popover-panel .bui-popover-footer{margin-top:15px;color:#666}.by-popover .bui-popover-arrow{position:absolute;width:10px;height:10px;background-color:#FFF;-webkit-transform:rotate(45deg);transform:rotate(45deg)}.by-popover.bui-popover-dark .bui-popover-arrow,.by-popover.bui-popover-dark .bui-popover-panel{background-color:#202328;color:#FFF}.by-popover.bui-popover-dark .bui-popover-body,.by-popover.bui-popover-dark .bui-popover-footer,.by-popover.bui-popover-dark .bui-popover-header{color:#FFF}.by-popover[x-placement^=top]{padding:12px 0}.by-popover[x-placement^=top] .bui-popover-arrow{bottom:7px;-webkit-box-shadow:2px 2px 3px 0 rgba(118,129,179,.15);box-shadow:2px 2px 3px 0 rgba(118,129,179,.15);border-width:0 1px 1px 0}.by-popover[x-placement^=right]{padding:0 12px}.by-popover[x-placement^=right] .bui-popover-arrow{left:7px;-webkit-box-shadow:-2px 2px 3px 0 rgba(118,129,179,.15);box-shadow:-2px 2px 3px 0 rgba(118,129,179,.15);border-width:0 0 1px 1px}.by-popover[x-placement^=bottom]{padding:12px 0}.by-popover[x-placement^=bottom] .bui-popover-arrow{top:7px;-webkit-box-shadow:-2px -2px 3px 0 rgba(118,129,179,.15);box-shadow:-2px -2px 3px 0 rgba(118,129,179,.15);border-width:1px 0 0 1px}.by-popover[x-placement^=left]{padding:0 12px}.by-popover[x-placement^=left] .bui-popover-arrow{right:7px;-webkit-box-shadow:2px -2px 3px 0 rgba(118,129,179,.15);box-shadow:2px -2px 3px 0 rgba(118,129,179,.15);border-width:1px 1px 0 0}.by-popover.bui-popover-error .bui-popover-arrow,.by-popover.bui-popover-error .bui-popover-panel{border-color:#F65656}.by-popover-rtl{direction:rtl}";
styleInject(css_248z$a);

var css_248z$9 = ".bui-popper .tooltip,.by-popover-wrapper{display:inline-block}@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.bui-popper{z-index:999}.bui-popper .bui-option-list-wrapper{border-radius:2px;margin-top:-1px;margin-bottom:2px;max-height:184px;overflow:hidden;background-color:#FFF}.bui-popper .bui-option-list-wrapper::-webkit-scrollbar-track-piece{background-color:transparent}.bui-popper .bui-option-list-wrapper::-webkit-scrollbar{width:9px;height:9px}.bui-popper .bui-option-list-wrapper::-webkit-scrollbar-thumb{background-color:#e8e8e8;border-radius:4px;background-clip:padding-box;min-height:28px}.bui-popper .bui-option-list-wrapper::-webkit-scrollbar-thumb:hover{background-color:#ddd}.by-popover{position:relative;z-index:1051}.by-popover .bui-popover-panel{padding:16px;background-color:#FFF;border-radius:4px;-webkit-box-shadow:0 2px 6px 0 rgba(0,0,0,.15);box-shadow:0 2px 6px 0 rgba(0,0,0,.15);color:#666}.by-popover .bui-popover-panel .bui-popover-header{font-size:14px;line-height:22px;font-weight:700;margin-bottom:8px;color:#333}.by-popover .bui-popover-panel .bui-popover-body{color:#666;font-size:12px;line-height:22px;white-space:normal}.by-popover .bui-popover-panel .bui-popover-footer{margin-top:15px;color:#666}.by-popover .bui-popover-arrow{position:absolute;width:10px;height:10px;background-color:#FFF;-webkit-transform:rotate(45deg);transform:rotate(45deg)}.by-popover.bui-popover-dark .bui-popover-arrow,.by-popover.bui-popover-dark .bui-popover-panel{background-color:#202328;color:#FFF}.by-popover.bui-popover-dark .bui-popover-body,.by-popover.bui-popover-dark .bui-popover-footer,.by-popover.bui-popover-dark .bui-popover-header{color:#FFF}.by-popover[x-placement^=top]{padding:12px 0}.by-popover[x-placement^=top] .bui-popover-arrow{bottom:7px;-webkit-box-shadow:2px 2px 3px 0 rgba(118,129,179,.15);box-shadow:2px 2px 3px 0 rgba(118,129,179,.15);border-width:0 1px 1px 0}.by-popover[x-placement^=right]{padding:0 12px}.by-popover[x-placement^=right] .bui-popover-arrow{left:7px;-webkit-box-shadow:-2px 2px 3px 0 rgba(118,129,179,.15);box-shadow:-2px 2px 3px 0 rgba(118,129,179,.15);border-width:0 0 1px 1px}.by-popover[x-placement^=bottom]{padding:12px 0}.by-popover[x-placement^=bottom] .bui-popover-arrow{top:7px;-webkit-box-shadow:-2px -2px 3px 0 rgba(118,129,179,.15);box-shadow:-2px -2px 3px 0 rgba(118,129,179,.15);border-width:1px 0 0 1px}.by-popover[x-placement^=left]{padding:0 12px}.by-popover[x-placement^=left] .bui-popover-arrow{right:7px;-webkit-box-shadow:2px -2px 3px 0 rgba(118,129,179,.15);box-shadow:2px -2px 3px 0 rgba(118,129,179,.15);border-width:1px 1px 0 0}.by-popover.bui-popover-error .bui-popover-arrow,.by-popover.bui-popover-error .bui-popover-panel{border-color:#F65656}.by-popover-rtl{direction:rtl}.by-tooltip .bui-popover-panel{padding:8px 16px}.bui-popper .tooltip{opacity:1;position:relative}.bui-popper .tooltip .tooltip-inner{border-radius:4px}.bui-popper .tooltip.top{margin-bottom:2px}.bui-popper .tooltip.left{margin-right:2px}.bui-popper .tooltip.bottom{margin-top:2px}.bui-popper .tooltip.right{margin-right:2px}";
styleInject(css_248z$9);

var _default$3 = {
  byted: {
    'locale': 'zh-CN',
    'pagination': {
      goto: '跳转',
      pagesize: '条/页',
      total: '共 {total} 条记录',
      placeholder: '请选择'
    },
    'select': {
      placeholder: '请选择',
      filterPlaceholder: '请输入',
      noDataText: '暂无选项',
      noMatchText: '暂无相关数据',
      hasSelect: '已选 {length} 项'
    },
    'auto-complete': {
      placeholder: '请选择',
      emptyTip: '暂无相关数据',
      loadingTip: '搜索中...',
      infiniteLoadingTip: '加载中...'
    },
    'cascader': {
      hasSelect: '已选 {length} 项',
      filterPlaceholder: '请输入',
      noDataText: '暂无选项'
    },
    'modal': {
      okText: '确定',
      cancelText: '取消'
    },
    'menu': {
      selectAll: '全选'
    },
    'select-panel': {
      clearText: '清空',
      selectAll: '全选',
      title: '已选',
      emptyText: '暂未选择',
      firstLevel: '第一层级',
      level: '第{level}层级',
      noDataText: '暂无数据'
    },
    'table': {
      noDataText: '暂无数据'
    },
    'time-picker': {
      okText: '确定'
    },
    'data-picker-new': {
      'days': ['日', '一', '二', '三', '四', '五', '六'],
      'months': ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
      'pickers': ['未来7天', '未来30天', '最近7天', '最近30天'],
      'placeholder': {
        'date': '请选择日期',
        'dateRange': '请选择日期范围'
      }
    },
    'data-picker': {
      'switchYear': '{year}年',
      'switchMonth': '{month}月',
      'days': ['日', '一', '二', '三', '四', '五', '六']
    },
    'date-picker': {
      'clearText': '清空',
      'okText': '确定',
      'datePanelLabel': '[YYYY年] [M月]',
      'days': ['日', '一', '二', '三', '四', '五', '六'],
      'months': ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
      // 'pickers': ['未来7天', '未来30天', '最近7天', '最近30天'],
      'placeholder': '请选择日期',
      'startTime': '开始时间',
      'endTime': '结束时间'
    },
    'range-picker': {
      'placeholder': ['请选择开始时间', '请选择结束时间']
    },
    'color-picker': {
      'customColor': '自定义颜色',
      'okText': '确定',
      'clearText': '清空'
    },
    'infinite-scroll': {
      loadingTip: '加载中...'
    },
    'schedule': {
      'selectedText': '已选',
      'selectableText': '可选',
      'weekText': '星期/时间',
      'noDataText': '可拖动鼠标选择时间段',
      'title': '已选择时间段',
      'clearText': '清空',
      'days': ['星期一', '星期二', '星期三', '星期四', '星期五', '星期六', '星期日']
    }
  }
};

var zhCN = /*#__PURE__*/Object.defineProperty({
	default: _default$3
}, '__esModule', {value: true});

var util = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.convertFieldsError = convertFieldsError;
exports.format = format;
exports.isEmptyValue = isEmptyValue;
exports.isEmptyObject = isEmptyObject;
exports.asyncMap = asyncMap;
exports.complementError = complementError;
exports.deepMerge = deepMerge;
/* eslint no-console:0 */

var formatRegExp = /%[sdj%]/g;

exports.warning = function warning() {};

function convertFieldsError(errors) {
  if (!errors || !errors.length) return null;
  var fields = {};
  errors.forEach(function (error) {
    var field = error.field;
    fields[field] = fields[field] || [];
    fields[field].push(error);
  });
  return fields;
}

function format() {
  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  var i = 1;
  var f = args[0];
  var len = args.length;
  if (typeof f === 'function') {
    return f.apply(null, args.slice(1));
  }
  if (typeof f === 'string') {
    var str = String(f).replace(formatRegExp, function (x) {
      if (x === '%%') {
        return '%';
      }
      if (i >= len) {
        return x;
      }
      switch (x) {
        case '%s':
          return String(args[i++]);
        case '%d':
          return Number(args[i++]);
        case '%j':
          try {
            return JSON.stringify(args[i++]);
          } catch (_) {
            return '[Circular]';
          }
          break;
        default:
          return x;
      }
    });
    for (var arg = args[i]; i < len; arg = args[++i]) {
      str += ' ' + arg;
    }
    return str;
  }
  return f;
}

function isNativeStringType(type) {
  return type === 'string' || type === 'url' || type === 'hex' || type === 'email' || type === 'pattern';
}

function isEmptyValue(value, type) {
  if (value === undefined || value === null) {
    return true;
  }
  if (type === 'array' && Array.isArray(value) && !value.length) {
    return true;
  }
  if (isNativeStringType(type) && typeof value === 'string' && !value) {
    return true;
  }
  return false;
}

function isEmptyObject(obj) {
  return Object.keys(obj).length === 0;
}

function asyncParallelArray(arr, func, callback) {
  var results = [];
  var total = 0;
  var arrLength = arr.length;

  function count(errors) {
    results.push.apply(results, errors);
    total++;
    if (total === arrLength) {
      callback(results);
    }
  }

  arr.forEach(function (a) {
    func(a, count);
  });
}

function asyncSerialArray(arr, func, callback) {
  var index = 0;
  var arrLength = arr.length;

  function next(errors) {
    if (errors && errors.length) {
      callback(errors);
      return;
    }
    var original = index;
    index = index + 1;
    if (original < arrLength) {
      func(arr[original], next);
    } else {
      callback([]);
    }
  }

  next([]);
}

function flattenObjArr(objArr) {
  var ret = [];
  Object.keys(objArr).forEach(function (k) {
    ret.push.apply(ret, objArr[k]);
  });
  return ret;
}

function asyncMap(objArr, option, func, callback) {
  if (option.first) {
    var flattenArr = flattenObjArr(objArr);
    return asyncSerialArray(flattenArr, func, callback);
  }
  var firstFields = option.firstFields || [];
  if (firstFields === true) {
    firstFields = Object.keys(objArr);
  }
  var objArrKeys = Object.keys(objArr);
  var objArrLength = objArrKeys.length;
  var total = 0;
  var results = [];
  var pending = new Promise(function (resolve, reject) {
    var next = function next(errors) {
      results.push.apply(results, errors);
      total++;
      if (total === objArrLength) {
        callback(results);
        return results.length ? reject({ errors: results, fields: convertFieldsError(results) }) : resolve();
      }
    };
    objArrKeys.forEach(function (key) {
      var arr = objArr[key];
      if (firstFields.indexOf(key) !== -1) {
        asyncSerialArray(arr, func, next);
      } else {
        asyncParallelArray(arr, func, next);
      }
    });
  });
  pending['catch'](function (e) {
    return e;
  });
  return pending;
}

function complementError(rule) {
  return function (oe) {
    if (oe && oe.message) {
      oe.field = oe.field || rule.fullField;
      return oe;
    }
    return {
      message: typeof oe === 'function' ? oe() : oe,
      field: oe.field || rule.fullField
    };
  };
}

function deepMerge(target, source) {
  if (source) {
    for (var s in source) {
      if (source.hasOwnProperty(s)) {
        var value = source[s];
        if ((typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object' && _typeof(target[s]) === 'object') {
          target[s] = _extends({}, target[s], value);
        } else {
          target[s] = value;
        }
      }
    }
  }
  return target;
}
});

var required_1$1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var util$1 = _interopRequireWildcard(util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating required fields.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function required(rule, value, source, errors, options, type) {
  if (rule.required && (!source.hasOwnProperty(rule.field) || util$1.isEmptyValue(value, type || rule.type))) {
    errors.push(util$1.format(options.messages.required, rule.fullField));
  }
}

exports['default'] = required;
});

var whitespace_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var util$1 = _interopRequireWildcard(util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating whitespace.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function whitespace(rule, value, source, errors, options) {
  if (/^\s+$/.test(value) || value === '') {
    errors.push(util$1.format(options.messages.whitespace, rule.fullField));
  }
}

exports['default'] = whitespace;
});

var type_1$1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };



var util$1 = _interopRequireWildcard(util);



var _required2 = _interopRequireDefault(required_1$1);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/* eslint max-len:0 */

var pattern = {
  // http://emailregex.com/
  email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  url: new RegExp('^(?!mailto:)(?:(?:http|https|ftp)://|//)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$', 'i'),
  hex: /^#?([a-f0-9]{6}|[a-f0-9]{3})$/i
};

var types = {
  integer: function integer(value) {
    return types.number(value) && parseInt(value, 10) === value;
  },
  float: function float(value) {
    return types.number(value) && !types.integer(value);
  },
  array: function array(value) {
    return Array.isArray(value);
  },
  regexp: function regexp(value) {
    if (value instanceof RegExp) {
      return true;
    }
    try {
      return !!new RegExp(value);
    } catch (e) {
      return false;
    }
  },
  date: function date(value) {
    return typeof value.getTime === 'function' && typeof value.getMonth === 'function' && typeof value.getYear === 'function';
  },
  number: function number(value) {
    if (isNaN(value)) {
      return false;
    }
    return typeof value === 'number';
  },
  object: function object(value) {
    return (typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object' && !types.array(value);
  },
  method: function method(value) {
    return typeof value === 'function';
  },
  email: function email(value) {
    return typeof value === 'string' && !!value.match(pattern.email) && value.length < 255;
  },
  url: function url(value) {
    return typeof value === 'string' && !!value.match(pattern.url);
  },
  hex: function hex(value) {
    return typeof value === 'string' && !!value.match(pattern.hex);
  }
};

/**
 *  Rule for validating the type of a value.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function type(rule, value, source, errors, options) {
  if (rule.required && value === undefined) {
    (0, _required2['default'])(rule, value, source, errors, options);
    return;
  }
  var custom = ['integer', 'float', 'array', 'regexp', 'object', 'method', 'email', 'number', 'date', 'url', 'hex'];
  var ruleType = rule.type;
  if (custom.indexOf(ruleType) > -1) {
    if (!types[ruleType](value)) {
      errors.push(util$1.format(options.messages.types[ruleType], rule.fullField, rule.type));
    }
    // straight typeof check
  } else if (ruleType && (typeof value === 'undefined' ? 'undefined' : _typeof(value)) !== rule.type) {
    errors.push(util$1.format(options.messages.types[ruleType], rule.fullField, rule.type));
  }
}

exports['default'] = type;
});

var range_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var util$1 = _interopRequireWildcard(util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating minimum and maximum allowed values.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function range(rule, value, source, errors, options) {
  var len = typeof rule.len === 'number';
  var min = typeof rule.min === 'number';
  var max = typeof rule.max === 'number';
  // 正则匹配码点范围从U+010000一直到U+10FFFF的文字（补充平面Supplementary Plane）
  var spRegexp = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;
  var val = value;
  var key = null;
  var num = typeof value === 'number';
  var str = typeof value === 'string';
  var arr = Array.isArray(value);
  if (num) {
    key = 'number';
  } else if (str) {
    key = 'string';
  } else if (arr) {
    key = 'array';
  }
  // if the value is not of a supported type for range validation
  // the validation rule rule should use the
  // type property to also test for a particular type
  if (!key) {
    return false;
  }
  if (arr) {
    val = value.length;
  }
  if (str) {
    // 处理码点大于U+010000的文字length属性不准确的bug，如"𠮷𠮷𠮷".lenght !== 3
    val = value.replace(spRegexp, '_').length;
  }
  if (len) {
    if (val !== rule.len) {
      errors.push(util$1.format(options.messages[key].len, rule.fullField, rule.len));
    }
  } else if (min && !max && val < rule.min) {
    errors.push(util$1.format(options.messages[key].min, rule.fullField, rule.min));
  } else if (max && !min && val > rule.max) {
    errors.push(util$1.format(options.messages[key].max, rule.fullField, rule.max));
  } else if (min && max && (val < rule.min || val > rule.max)) {
    errors.push(util$1.format(options.messages[key].range, rule.fullField, rule.min, rule.max));
  }
}

exports['default'] = range;
});

var _enum$1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var util$1 = _interopRequireWildcard(util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

var ENUM = 'enum';

/**
 *  Rule for validating a value exists in an enumerable list.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function enumerable(rule, value, source, errors, options) {
  rule[ENUM] = Array.isArray(rule[ENUM]) ? rule[ENUM] : [];
  if (rule[ENUM].indexOf(value) === -1) {
    errors.push(util$1.format(options.messages[ENUM], rule.fullField, rule[ENUM].join(', ')));
  }
}

exports['default'] = enumerable;
});

var pattern_1$1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var util$1 = _interopRequireWildcard(util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating a regular expression pattern.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function pattern(rule, value, source, errors, options) {
  if (rule.pattern) {
    if (rule.pattern instanceof RegExp) {
      // if a RegExp instance is passed, reset `lastIndex` in case its `global`
      // flag is accidentally set to `true`, which in a validation scenario
      // is not necessary and the result might be misleading
      rule.pattern.lastIndex = 0;
      if (!rule.pattern.test(value)) {
        errors.push(util$1.format(options.messages.pattern.mismatch, rule.fullField, value, rule.pattern));
      }
    } else if (typeof rule.pattern === 'string') {
      var _pattern = new RegExp(rule.pattern);
      if (!_pattern.test(value)) {
        errors.push(util$1.format(options.messages.pattern.mismatch, rule.fullField, value, rule.pattern));
      }
    }
  }
}

exports['default'] = pattern;
});

var rule = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _required2 = _interopRequireDefault(required_1$1);



var _whitespace2 = _interopRequireDefault(whitespace_1);



var _type2 = _interopRequireDefault(type_1$1);



var _range2 = _interopRequireDefault(range_1);



var _enum2 = _interopRequireDefault(_enum$1);



var _pattern2 = _interopRequireDefault(pattern_1$1);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

exports['default'] = {
  required: _required2['default'],
  whitespace: _whitespace2['default'],
  type: _type2['default'],
  range: _range2['default'],
  'enum': _enum2['default'],
  pattern: _pattern2['default']
};
});

var string_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Performs validation for string types.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function string(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value, 'string') && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options, 'string');
    if (!(0, util.isEmptyValue)(value, 'string')) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
      _rule2['default'].pattern(rule, value, source, errors, options);
      if (rule.whitespace === true) {
        _rule2['default'].whitespace(rule, value, source, errors, options);
      }
    }
  }
  callback(errors);
}

exports['default'] = string;
});

var method_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a function.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function method(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = method;
});

var number_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a number.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function number(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if (value === '') {
      value = undefined;
    }
    if ((0, util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = number;
});

var boolean_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});





var _rule2 = _interopRequireDefault(rule);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a boolean.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function boolean(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = boolean;
});

var regexp_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates the regular expression type.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function regexp(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (!(0, util.isEmptyValue)(value)) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = regexp;
});

var integer_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a number is an integer.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function integer(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = integer;
});

var float = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a number is a floating point number.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function floatFn(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = floatFn;
});

var array_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates an array.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function array(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value, 'array') && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options, 'array');
    if (!(0, util.isEmptyValue)(value, 'array')) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = array;
});

var object_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates an object.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function object(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = object;
});

var _enum = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

var ENUM = 'enum';

/**
 *  Validates an enumerable list.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function enumerable(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value) {
      _rule2['default'][ENUM](rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = enumerable;
});

var pattern_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a regular expression pattern.
 *
 *  Performs validation when a rule only contains
 *  a pattern property but is not declared as a string type.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function pattern(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value, 'string') && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (!(0, util.isEmptyValue)(value, 'string')) {
      _rule2['default'].pattern(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = pattern;
});

var date_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function date(rule, value, callback, source, options) {
  // console.log('integer rule called %j', rule);
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  // console.log('validate on %s value', value);
  if (validate) {
    if ((0, util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (!(0, util.isEmptyValue)(value)) {
      var dateObject = void 0;

      if (typeof value === 'number') {
        dateObject = new Date(value);
      } else {
        dateObject = value;
      }

      _rule2['default'].type(rule, dateObject, source, errors, options);
      if (dateObject) {
        _rule2['default'].range(rule, dateObject.getTime(), source, errors, options);
      }
    }
  }
  callback(errors);
}

exports['default'] = date;
});

var required_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };



var _rule2 = _interopRequireDefault(rule);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function required(rule, value, callback, source, options) {
  var errors = [];
  var type = Array.isArray(value) ? 'array' : typeof value === 'undefined' ? 'undefined' : _typeof(value);
  _rule2['default'].required(rule, value, source, errors, options, type);
  callback(errors);
}

exports['default'] = required;
});

var type_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _rule2 = _interopRequireDefault(rule);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function type(rule, value, callback, source, options) {
  var ruleType = rule.type;
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, util.isEmptyValue)(value, ruleType) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options, ruleType);
    if (!(0, util.isEmptyValue)(value, ruleType)) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = type;
});

var validator = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});



var _string2 = _interopRequireDefault(string_1);



var _method2 = _interopRequireDefault(method_1);



var _number2 = _interopRequireDefault(number_1);



var _boolean2 = _interopRequireDefault(boolean_1);



var _regexp2 = _interopRequireDefault(regexp_1);



var _integer2 = _interopRequireDefault(integer_1);



var _float2 = _interopRequireDefault(float);



var _array2 = _interopRequireDefault(array_1);



var _object2 = _interopRequireDefault(object_1);



var _enum2 = _interopRequireDefault(_enum);



var _pattern2 = _interopRequireDefault(pattern_1);



var _date2 = _interopRequireDefault(date_1);



var _required2 = _interopRequireDefault(required_1);



var _type2 = _interopRequireDefault(type_1);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

exports['default'] = {
  string: _string2['default'],
  method: _method2['default'],
  number: _number2['default'],
  boolean: _boolean2['default'],
  regexp: _regexp2['default'],
  integer: _integer2['default'],
  float: _float2['default'],
  array: _array2['default'],
  object: _object2['default'],
  'enum': _enum2['default'],
  pattern: _pattern2['default'],
  date: _date2['default'],
  url: _type2['default'],
  hex: _type2['default'],
  email: _type2['default'],
  required: _required2['default']
};
});

var messages_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.newMessages = newMessages;
function newMessages() {
  return {
    'default': 'Validation error on field %s',
    required: '%s is required',
    'enum': '%s must be one of %s',
    whitespace: '%s cannot be empty',
    date: {
      format: '%s date %s is invalid for format %s',
      parse: '%s date could not be parsed, %s is invalid ',
      invalid: '%s date %s is invalid'
    },
    types: {
      string: '%s is not a %s',
      method: '%s is not a %s (function)',
      array: '%s is not an %s',
      object: '%s is not an %s',
      number: '%s is not a %s',
      date: '%s is not a %s',
      boolean: '%s is not a %s',
      integer: '%s is not an %s',
      float: '%s is not a %s',
      regexp: '%s is not a valid %s',
      email: '%s is not a valid %s',
      url: '%s is not a valid %s',
      hex: '%s is not a valid %s'
    },
    string: {
      len: '%s must be exactly %s characters',
      min: '%s must be at least %s characters',
      max: '%s cannot be longer than %s characters',
      range: '%s must be between %s and %s characters'
    },
    number: {
      len: '%s must equal %s',
      min: '%s cannot be less than %s',
      max: '%s cannot be greater than %s',
      range: '%s must be between %s and %s'
    },
    array: {
      len: '%s must be exactly %s in length',
      min: '%s cannot be less than %s in length',
      max: '%s cannot be greater than %s in length',
      range: '%s must be between %s and %s in length'
    },
    pattern: {
      mismatch: '%s value %s does not match pattern %s'
    },
    clone: function clone() {
      var cloned = JSON.parse(JSON.stringify(this));
      cloned.clone = this.clone;
      return cloned;
    }
  };
}

exports.messages = newMessages();
});

var es$1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };





var _validator2 = _interopRequireDefault(validator);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Encapsulates a validation schema.
 *
 *  @param descriptor An object declaring validation rules
 *  for this schema.
 */
function Schema(descriptor) {
  this.rules = null;
  this._messages = messages_1.messages;
  this.define(descriptor);
}

Schema.prototype = {
  messages: function messages(_messages) {
    if (_messages) {
      this._messages = (0, util.deepMerge)((0, messages_1.newMessages)(), _messages);
    }
    return this._messages;
  },
  define: function define(rules) {
    if (!rules) {
      throw new Error('Cannot configure a schema with no rules');
    }
    if ((typeof rules === 'undefined' ? 'undefined' : _typeof(rules)) !== 'object' || Array.isArray(rules)) {
      throw new Error('Rules must be an object');
    }
    this.rules = {};
    var z = void 0;
    var item = void 0;
    for (z in rules) {
      if (rules.hasOwnProperty(z)) {
        item = rules[z];
        this.rules[z] = Array.isArray(item) ? item : [item];
      }
    }
  },
  validate: function validate(source_) {
    var _this = this;

    var o = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var oc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {};

    var source = source_;
    var options = o;
    var callback = oc;
    if (typeof options === 'function') {
      callback = options;
      options = {};
    }
    if (!this.rules || Object.keys(this.rules).length === 0) {
      if (callback) {
        callback();
      }
      return Promise.resolve();
    }

    function complete(results) {
      var i = void 0;
      var errors = [];
      var fields = {};

      function add(e) {
        if (Array.isArray(e)) {
          var _errors;

          errors = (_errors = errors).concat.apply(_errors, e);
        } else {
          errors.push(e);
        }
      }

      for (i = 0; i < results.length; i++) {
        add(results[i]);
      }
      if (!errors.length) {
        errors = null;
        fields = null;
      } else {
        fields = (0, util.convertFieldsError)(errors);
      }
      callback(errors, fields);
    }

    if (options.messages) {
      var messages = this.messages();
      if (messages === messages_1.messages) {
        messages = (0, messages_1.newMessages)();
      }
      (0, util.deepMerge)(messages, options.messages);
      options.messages = messages;
    } else {
      options.messages = this.messages();
    }
    var arr = void 0;
    var value = void 0;
    var series = {};
    var keys = options.keys || Object.keys(this.rules);
    keys.forEach(function (z) {
      arr = _this.rules[z];
      value = source[z];
      arr.forEach(function (r) {
        var rule = r;
        if (typeof rule.transform === 'function') {
          if (source === source_) {
            source = _extends({}, source);
          }
          value = source[z] = rule.transform(value);
        }
        if (typeof rule === 'function') {
          rule = {
            validator: rule
          };
        } else {
          rule = _extends({}, rule);
        }
        rule.validator = _this.getValidationMethod(rule);
        rule.field = z;
        rule.fullField = rule.fullField || z;
        rule.type = _this.getType(rule);
        if (!rule.validator) {
          return;
        }
        series[z] = series[z] || [];
        series[z].push({
          rule: rule,
          value: value,
          source: source,
          field: z
        });
      });
    });
    var errorFields = {};
    return (0, util.asyncMap)(series, options, function (data, doIt) {
      var rule = data.rule;
      var deep = (rule.type === 'object' || rule.type === 'array') && (_typeof(rule.fields) === 'object' || _typeof(rule.defaultField) === 'object');
      deep = deep && (rule.required || !rule.required && data.value);
      rule.field = data.field;

      function addFullfield(key, schema) {
        return _extends({}, schema, {
          fullField: rule.fullField + '.' + key
        });
      }

      function cb() {
        var e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

        var errors = e;
        if (!Array.isArray(errors)) {
          errors = [errors];
        }
        if (!options.suppressWarning && errors.length) {
          Schema.warning('async-validator:', errors);
        }
        if (errors.length && rule.message) {
          errors = [].concat(rule.message);
        }

        errors = errors.map((0, util.complementError)(rule));

        if (options.first && errors.length) {
          errorFields[rule.field] = 1;
          return doIt(errors);
        }
        if (!deep) {
          doIt(errors);
        } else {
          // if rule is required but the target object
          // does not exist fail at the rule level and don't
          // go deeper
          if (rule.required && !data.value) {
            if (rule.message) {
              errors = [].concat(rule.message).map((0, util.complementError)(rule));
            } else if (options.error) {
              errors = [options.error(rule, (0, util.format)(options.messages.required, rule.field))];
            } else {
              errors = [];
            }
            return doIt(errors);
          }

          var fieldsSchema = {};
          if (rule.defaultField) {
            for (var k in data.value) {
              if (data.value.hasOwnProperty(k)) {
                fieldsSchema[k] = rule.defaultField;
              }
            }
          }
          fieldsSchema = _extends({}, fieldsSchema, data.rule.fields);
          for (var f in fieldsSchema) {
            if (fieldsSchema.hasOwnProperty(f)) {
              var fieldSchema = Array.isArray(fieldsSchema[f]) ? fieldsSchema[f] : [fieldsSchema[f]];
              fieldsSchema[f] = fieldSchema.map(addFullfield.bind(null, f));
            }
          }
          var schema = new Schema(fieldsSchema);
          schema.messages(options.messages);
          if (data.rule.options) {
            data.rule.options.messages = options.messages;
            data.rule.options.error = options.error;
          }
          schema.validate(data.value, data.rule.options || options, function (errs) {
            var finalErrors = [];
            if (errors && errors.length) {
              finalErrors.push.apply(finalErrors, errors);
            }
            if (errs && errs.length) {
              finalErrors.push.apply(finalErrors, errs);
            }
            doIt(finalErrors.length ? finalErrors : null);
          });
        }
      }

      var res = void 0;
      if (rule.asyncValidator) {
        res = rule.asyncValidator(rule, data.value, cb, data.source, options);
      } else if (rule.validator) {
        res = rule.validator(rule, data.value, cb, data.source, options);
        if (res === true) {
          cb();
        } else if (res === false) {
          cb(rule.message || rule.field + ' fails');
        } else if (res instanceof Array) {
          cb(res);
        } else if (res instanceof Error) {
          cb(res.message);
        }
      }
      if (res && res.then) {
        res.then(function () {
          return cb();
        }, function (e) {
          return cb(e);
        });
      }
    }, function (results) {
      complete(results);
    });
  },
  getType: function getType(rule) {
    if (rule.type === undefined && rule.pattern instanceof RegExp) {
      rule.type = 'pattern';
    }
    if (typeof rule.validator !== 'function' && rule.type && !_validator2['default'].hasOwnProperty(rule.type)) {
      throw new Error((0, util.format)('Unknown rule type %s', rule.type));
    }
    return rule.type || 'string';
  },
  getValidationMethod: function getValidationMethod(rule) {
    if (typeof rule.validator === 'function') {
      return rule.validator;
    }
    var keys = Object.keys(rule);
    var messageIndex = keys.indexOf('message');
    if (messageIndex !== -1) {
      keys.splice(messageIndex, 1);
    }
    if (keys.length === 1 && keys[0] === 'required') {
      return _validator2['default'].required;
    }
    return _validator2['default'][this.getType(rule)] || false;
  }
};

Schema.register = function register(type, validator) {
  if (typeof validator !== 'function') {
    throw new Error('Cannot register a validator by type, validator is not a function');
  }
  _validator2['default'][type] = validator;
};

Schema.warning = util.warning;

Schema.messages = messages_1.messages;

exports['default'] = Schema;
});

var _typeof$1 = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; /**
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               * Any basic methods here .
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               * Copyright 2017 ByteDance, Inc.
                                                                                                                                                                                                                                                                               * Licensed under MIT
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               */

/* global window module*/




var _asyncValidator2 = _interopRequireDefault$2(es$1);

function _interopRequireDefault$2(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var RE_NARGS = /(%|)\{([0-9a-zA-Z_]+)\}/g;

var utils = {
  /**
   *
   * @param {object} source
   * @return {object}
   * Deep copy of any object .
   *
   *
   *
   *
   */
  copy: function copy(source) {
    var str = void 0;var newobj = void 0;
    try {
      newobj = source.constructor === Array ? [] : {};
    } catch (e) {
      return;
    }
    try {
      str = JSON.stringify(source);
      newobj = JSON.parse(str);
    } catch (e) {
      for (var i in source) {
        if (_typeof$1(source[i]) === 'object') {
          newobj[i] = this.copy(source[i]);
        } else {
          source[i];
        }
      }
    }
    return newobj;
  },
  // 深克隆
  deepCopy: function deepCopy(source) {
    // 如果source是对象
    if ((typeof source === 'undefined' ? 'undefined' : _typeof$1(source)) == 'object' && source !== null) {
      // 有必要区分数组和普通对象
      var o = Object.prototype.toString.call(source) == '[object Array]' ? [] : {};
      // 遍历source的自有属性
      for (var key in source) {
        // 如果key是source的自有属性
        if (source.hasOwnProperty(key)) {
          o[key] = this.deepCopy(source[key]);
          // arguments.callee调的是当前的deepCopy函数
        }
      }
      return o;
    } else {
      // 如果source是原始类型的值，直接返回
      return source;
    }
  },

  /**
   * 获取 url 参数
   *
   */
  urlParams: function urlParams(href) {
    href = href || window.location.search;
    var data = {};

    var reg = /([^?=&]+)(=([^&]*))?/g;
    if (href) {
      href.replace(reg, function ($0, $1, $2, $3) {
        data[$1] = $3;
      });
    }
    return data;
  },

  Validator: _asyncValidator2.default,

  /**
   * 获取指定长度的随机字符串
   * @param  {} length=0
   */
  getRandomStr: function getRandomStr() {
    var length = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;

    var chars = 'asdfghjklzxcvbnmqwertyuiopASDFGHJKLZXCVBNMQWERTYUIOP1234567890';
    var str = '';
    for (var i = 0; i < length; i++) {
      str += chars[Math.floor(Math.random() * chars.length)];
    }
    return str;
  },
  hasOwn: function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  },

  // 补零
  addZero: function addZero(number) {
    return ('0' + number).substr(-2, 2);
  },

  /**
   * template
   *
   * @param {String} string
   * @param {Array} ...args
   * @return {String}
   */
  template: function template(string) {
    for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    if (args.length === 1 && _typeof$1(args[0]) === 'object') {
      args = args[0];
    }

    if (!args || !args.hasOwnProperty) {
      args = {};
    }

    if (typeof string !== 'string') {
      return string;
    }

    return string.replace(RE_NARGS, function (match, prefix, i, index) {
      var result = void 0;

      if (string[index - 1] === '{' && string[index + match.length] === '}') {
        return i;
      } else {
        result = Object.prototype.hasOwnProperty.call(args, i) ? args[i] : null;
        if (result === null || result === undefined) {
          return '';
        }

        return result;
      }
    });
  }
};

var isMergeableObject = function isMergeableObject(value) {
	return isNonNullObject(value)
		&& !isSpecial(value)
};

function isNonNullObject(value) {
	return !!value && typeof value === 'object'
}

function isSpecial(value) {
	var stringValue = Object.prototype.toString.call(value);

	return stringValue === '[object RegExp]'
		|| stringValue === '[object Date]'
		|| isReactElement(value)
}

// see https://github.com/facebook/react/blob/b5ac963fb791d1298e7f396236383bc955f916c1/src/isomorphic/classic/element/ReactElement.js#L21-L25
var canUseSymbol = typeof Symbol === 'function' && Symbol.for;
var REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for('react.element') : 0xeac7;

function isReactElement(value) {
	return value.$$typeof === REACT_ELEMENT_TYPE
}

function emptyTarget(val) {
	return Array.isArray(val) ? [] : {}
}

function cloneUnlessOtherwiseSpecified(value, options) {
	return (options.clone !== false && options.isMergeableObject(value))
		? deepmerge(emptyTarget(value), value, options)
		: value
}

function defaultArrayMerge(target, source, options) {
	return target.concat(source).map(function(element) {
		return cloneUnlessOtherwiseSpecified(element, options)
	})
}

function getMergeFunction(key, options) {
	if (!options.customMerge) {
		return deepmerge
	}
	var customMerge = options.customMerge(key);
	return typeof customMerge === 'function' ? customMerge : deepmerge
}

function getEnumerableOwnPropertySymbols(target) {
	return Object.getOwnPropertySymbols
		? Object.getOwnPropertySymbols(target).filter(function(symbol) {
			return target.propertyIsEnumerable(symbol)
		})
		: []
}

function getKeys(target) {
	return Object.keys(target).concat(getEnumerableOwnPropertySymbols(target))
}

function propertyIsOnObject(object, property) {
	try {
		return property in object
	} catch(_) {
		return false
	}
}

// Protects from prototype poisoning and unexpected merging up the prototype chain.
function propertyIsUnsafe(target, key) {
	return propertyIsOnObject(target, key) // Properties are safe to merge if they don't exist in the target yet,
		&& !(Object.hasOwnProperty.call(target, key) // unsafe if they exist up the prototype chain,
			&& Object.propertyIsEnumerable.call(target, key)) // and also unsafe if they're nonenumerable.
}

function mergeObject(target, source, options) {
	var destination = {};
	if (options.isMergeableObject(target)) {
		getKeys(target).forEach(function(key) {
			destination[key] = cloneUnlessOtherwiseSpecified(target[key], options);
		});
	}
	getKeys(source).forEach(function(key) {
		if (propertyIsUnsafe(target, key)) {
			return
		}

		if (propertyIsOnObject(target, key) && options.isMergeableObject(source[key])) {
			destination[key] = getMergeFunction(key, options)(target[key], source[key], options);
		} else {
			destination[key] = cloneUnlessOtherwiseSpecified(source[key], options);
		}
	});
	return destination
}

function deepmerge(target, source, options) {
	options = options || {};
	options.arrayMerge = options.arrayMerge || defaultArrayMerge;
	options.isMergeableObject = options.isMergeableObject || isMergeableObject;
	// cloneUnlessOtherwiseSpecified is added to `options` so that custom arrayMerge()
	// implementations can use it. The caller may not replace it.
	options.cloneUnlessOtherwiseSpecified = cloneUnlessOtherwiseSpecified;

	var sourceIsArray = Array.isArray(source);
	var targetIsArray = Array.isArray(target);
	var sourceAndTargetTypesMatch = sourceIsArray === targetIsArray;

	if (!sourceAndTargetTypesMatch) {
		return cloneUnlessOtherwiseSpecified(source, options)
	} else if (sourceIsArray) {
		return options.arrayMerge(target, source, options)
	} else {
		return mergeObject(target, source, options)
	}
}

deepmerge.all = function deepmergeAll(array, options) {
	if (!Array.isArray(array)) {
		throw new Error('first argument should be an array')
	}

	return array.reduce(function(prev, next) {
		return deepmerge(prev, next, options)
	}, {})
};

var deepmerge_1 = deepmerge;

var cjs = deepmerge_1;

var require$$0 = /*@__PURE__*/getAugmentedNamespace(vue_runtime_esm);

var _bytedMessage;



var _zhCN2 = _interopRequireDefault$1(zhCN);



var _vue2 = _interopRequireDefault$1(require$$0);





var _deepmerge2 = _interopRequireDefault$1(cjs);

function _interopRequireDefault$1(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var proto = void 0;
var merged = void 0;

var DEFAULT_LANG = 'zh-CN';

var bytedMessage = (_bytedMessage = {}, _bytedMessage[DEFAULT_LANG] = _zhCN2.default, _bytedMessage);

var TRANSLATION_ABSENT = 'Translation is not registered correctly,\n                            you can call Locale.use() to install it.';

function isUndefined(o) {
  var toString = Object.prototype.toString;
  return toString.call(o) === '[object Undefined]';
}
// https://github.com/TehShrike/deepmerge
var deepMergeOptions = {
  arrayMerge: function arrayMerge(destinationArray, sourceArray, options) {
    return destinationArray;
  }
};

var locale$1 = {
  name: 'locale',
  install: function install() {
    if (locale$1.installed) return;
    proto = _vue2.default.prototype;

    // When _Vue is a subclass returned by Vue.extend, the util.defineReactive method does not exist on _Vue
    var defineReactive = _vue2.default.util && _vue2.default.util.defineReactive;

    defineReactive(proto, '$bytedLang', DEFAULT_LANG);
    locale$1.installed = true;
  },
  use: function use(lang, messages) {
    proto['$bytedLang'] = lang;
    locale$1.register(lang, messages);
  },
  register: function register(lang, messages) {
    if (!(lang in bytedMessage)) {
      bytedMessage[[lang]] = messages;
      return;
    }
    bytedMessage[[lang]] = (0, _deepmerge2.default)(bytedMessage[[lang]], messages, deepMergeOptions);
  },
  i18nHandler: function i18nHandler() {
    var currentMessage = bytedMessage[proto.$bytedLang];
    var vuei18n = Object.getPrototypeOf(this || _vue2.default).$t;
    if (typeof vuei18n === 'function' && !!_vue2.default.locale) {
      if (!merged) {
        merged = true;
        _vue2.default.locale(_vue2.default.config.lang, (0, _deepmerge2.default)(currentMessage, _vue2.default.locale(_vue2.default.config.lang) || {}, { clone: true }, deepMergeOptions));
      }

      for (var _len = arguments.length, arg = Array(_len), _key = 0; _key < _len; _key++) {
        arg[_key] = arguments[_key];
      }

      return vuei18n.apply(this, arg);
    }
  },
  i18n: function i18n(fn) {
    locale$1.i18nHandler = fn || locale$1.i18nHandler;
  },
  getMessage: function getMessage(lang) {
    var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    var options = arguments[2];

    var currentMessage = bytedMessage[lang];
    if (isUndefined(currentMessage)) {
      console.error(TRANSLATION_ABSENT);
      return '';
    }
    var value = locale$1.i18nHandler.call(this, path, options);
    if (value !== null && value !== undefined) return value;

    var array = path.split('.');
    var current = currentMessage;
    for (var i = 0, j = array.length; i < j; i++) {
      var property = array[i];
      value = current[property];
      if (i === j - 1) return (0, utils.template)(value, options);
      if (!value) return '';
      current = value;
    }
    return '';
  }
};

var locale_1 = locale$1;

/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

var base = Base;

var _locale2 = _interopRequireDefault(locale_1);



function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

// Arabic 阿拉伯语、Farsi 波斯语、Hebrew 希伯来语
var supportRTL = ['ar', 'fa-ir', 'he'];

var _default$2 = {
  computed: {
    t: function t() {
      var _this = this;

      var lang = this.$bytedLang;
      return function (path, options) {
        return _locale2.default.getMessage.call(_this, lang, path, options);
      };
    },
    isRTL: function isRTL() {
      var locale = this.t(base.LOCALE_PREFIX + '.locale');
      return supportRTL.indexOf(locale) > -1 || false;
    }
  },
  beforeCreate: function beforeCreate() {
    _locale2.default.install();
  }
};

var locale = /*#__PURE__*/Object.defineProperty({
	default: _default$2
}, '__esModule', {value: true});

var popper = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 395);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 18:
/***/ (function(module, exports) {

var g;

// This works in non-strict mode
g = (function() {
	return this;
})();

try {
	// This works if eval is allowed (see CSP)
	g = g || Function("return this")() || (1,eval)("this");
} catch(e) {
	// This works if the window reference is available
	if(typeof window === "object")
		g = window;
}

// g can still be undefined, but nothing to do about it...
// We return undefined, instead of nothing here, so it's
// easier to handle this case. if(!global) { ...}

module.exports = g;


/***/ }),

/***/ 19:
/***/ (function(module, exports) {

module.exports = require$$0;

/***/ }),

/***/ 194:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; //
//
//
//
//
//
//
//
//

var _dom = __webpack_require__(8);

var _helper = __webpack_require__(4);

var _base = __webpack_require__(1);

var _popupManager = __webpack_require__(22);

var _popupManager2 = _interopRequireDefault(_popupManager);

var _resizeObserverPolyfill = __webpack_require__(53);

var _resizeObserverPolyfill2 = _interopRequireDefault(_resizeObserverPolyfill);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var Popper = __webpack_require__(397).default;
exports.default = {
  name: _base.PREFIX + 'popper',
  props: {
    reference: {
      type: [Object, HTMLElement], // 可以直接传原生对象，也可以传 VNode
      default: function _default() {
        return {};
      }
    },
    placement: {
      type: String,
      default: 'bottom-start'
    },
    // 增加popper options后下掉modifier
    modifiers: {
      type: Object,
      default: function _default() {
        return null;
      }
    },
    // popper options 配置
    popperOptions: {
      type: Object,
      default: function _default() {
        return null;
      }
    },
    appendToBody: {
      type: [Boolean, HTMLElement],
      default: false
    },
    className: {
      type: String,
      default: ''
    },
    fitPopperResize: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      popper: null,
      width: '',
      zIndex: '',
      defaultOptions: {
        modifiers: {
          flip: {
            enabled: false
          },
          hide: {
            enabled: false
          },
          preventOverflow: {
            enabled: false
          }
        }
      }
    };
  },

  computed: {
    styles: function styles() {
      var style = {};
      if (this.width) style.width = this.width + 'px';
      if (this.appendToBody) {
        style.zIndex = this.zIndex;
      }
      return style;
    },
    referenceEle: function referenceEle() {
      if (this.reference instanceof HTMLElement) {
        return this.reference;
      } else if (this.reference) {
        return this.reference.$el;
      }
      return null;
    }
  },
  mounted: function mounted() {
    this.$on('bui.popper.update', this.update);
    this.$on('bui.popper.destroy', this.destroy);
    this.$on('bui.popper.scheduleUpdate', this.scheduleUpdate);
    this.addPopperEventListener();
  },
  created: function created() {},
  beforeDestroy: function beforeDestroy() {
    if (this.popperRefRo) {
      this.popperRefRo.unobserve(this.$refs.popperRef);
      this.popperRefRo = null;
    }
    if (this.popper) {
      this.popper.destroy();
    }
    var parentElement = this.$el.parentNode;
    if (parentElement && this.$el) {
      parentElement.removeChild(this.$el);
    }
  },

  methods: {
    // popper事件监听
    addPopperEventListener: function addPopperEventListener() {
      var _this = this;

      // 实时调整popper框的位置
      if (this.fitPopperResize) {
        this.$nextTick(function () {
          if (!_this.$refs.popperRef) {
            return;
          }
          _this.popperRefRo = new _resizeObserverPolyfill2.default(function () {
            if (_this.popper) {
              _this.popper.update();
            }
          });
          _this.popperRefRo.observe(_this.$refs.popperRef);
        });
      }
    },
    handleEle: function handleEle() {
      var dom = void 0;
      // boolean值为true将popper append到body上  传入值为dom对象时则在指定对象添加
      if (typeof this.appendToBody === 'boolean' && this.appendToBody) {
        dom = document.body;
      } else if (this.appendToBody instanceof HTMLElement) {
        dom = this.appendToBody;
      }
      if (dom && this.$el.parentNode && this.$el.parentNode === dom) {
        return;
      }
      dom && dom.appendChild(this.$el);
    },
    getPopperOptions: function getPopperOptions() {
      var _this2 = this;

      var modifiers = this.defaultOptions.modifiers;
      var currentOptions = this.defaultOptions;
      currentOptions.placement = this.placement;
      if (this.popperOptions) {
        modifiers = _extends({}, modifiers, this.popperOptions.modifiers);
        currentOptions = _extends({}, currentOptions, this.popperOptions);
      }
      if (this.modifiers) {
        modifiers = _extends({}, modifiers, this.modifiers);
      }
      if (!currentOptions.onCreate) {
        currentOptions.onCreate = function (data) {
          _this2.$nextTick(data.instance.update());
        };
      }
      return _extends(currentOptions, { modifiers: modifiers });
    },
    update: function update() {
      this.handleEle();
      if (this.popper) {
        this.popper.destroy();
      }
      if (!this.referenceEle) {
        return;
      }
      var options = this.getPopperOptions();

      this.popper = new Popper(this.referenceEle, this.$el, options);
      // 修改当前popperzIndex
      this.zIndex = this.appendToBody ? _popupManager2.default.nextZIndex() : '';
      // 特殊处理，当用于select时，宽度与被挂载的select保持一致
      if ((0, _helper.isOneOf)(this.$parent.$options.name, [_base.PREFIX + 'select'])) {
        this.width = parseInt((0, _dom.getStyle)(this.$parent.$el, 'width'));
      }
    },
    scheduleUpdate: function scheduleUpdate() {
      this.popper && this.popper.scheduleUpdate();
    },
    destroy: function destroy() {
      if (this.popper) {
        this.popper.destroy();
        this.popper = null;
      }
    }
  }
};

/***/ }),

/***/ 22:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _vue = __webpack_require__(19);

var _vue2 = _interopRequireDefault(_vue);

var _dom = __webpack_require__(8);

var _base = __webpack_require__(1);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

// 激活态初始zIndex
if (typeof _vue2.default.prototype.$bytedZIndex === 'undefined') {
  _vue2.default.prototype.$bytedZIndex = 2000;
}
// 遮罩层
var backdrop = document.createElement('div');
(0, _dom.addClass)(backdrop, _base.CSS_PREFIX + 'modal-backdrop fade');

var PopupManager = {
  // 弹出层实例
  instances: {},
  // 存储当前显示的弹出层的栈，顺序为点击显示的顺序
  stack: [],
  backdrop: backdrop,
  // zindex 增加
  nextZIndex: function nextZIndex() {
    return _vue2.default.prototype.$bytedZIndex++;
  },

  // 弹出层示例注册
  register: function register(id, instance) {
    PopupManager.instances[id] = instance;
  },

  // 弹出层示例销毁
  destroy: function destroy(id) {
    PopupManager.instances[id] = null;
    delete PopupManager.instances[id];
  },

  // getOpenInstances(currentId) {
  //   return Object.keys(PopupManager.instances).filter((key) => {
  //     return PopupManager.instances[key].value && +key !== currentId;
  //   }).map((key) => PopupManager.instances[key]);
  // },
  stackPush: function stackPush(_ref) {
    var id = _ref.id,
        zIndex = _ref.zIndex,
        $el = _ref.$el,
        instance = _ref.instance;

    this.stack.push({
      id: id,
      zIndex: zIndex,
      $el: $el,
      instance: instance
    });
  },
  stackPop: function stackPop(id) {
    if (this.stack.length && this.stack[this.stack.length - 1].id === id) {
      this.stack.pop();
    } else {
      var idIndex = this.stack.findIndex(function (item) {
        return item.id === id;
      });
      if (idIndex !== -1) {
        this.stack.splice(idIndex, 1);
      }
    }
  },

  // 删除遮罩的情况：没有展开的弹出框或者展开的弹出框内不需要显示遮罩
  isRemoveBackup: function isRemoveBackup() {
    var _this = this;

    return !this.stack.length || this.stack.every(function (item) {
      return !_this.instances[item.id].maskable;
    });
  },

  // 获取堆栈中需首个需要显示遮罩
  getFirstBackupStack: function getFirstBackupStack() {
    var index = this.stack.length - 1;
    while (index >= 0) {
      var id = this.stack[index].id;
      if (this.instances[id].maskable) {
        return this.stack[index];
      }
      index--;
    }
    return null;
  }
};
exports.default = PopupManager;

/***/ }),

/***/ 395:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _popper = __webpack_require__(396);

var _popper2 = _interopRequireDefault(_popper);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_popper2.default.install = function (Vue) {
  Vue.component(_popper2.default.name, _popper2.default);
};

exports.default = _popper2.default;

/***/ }),

/***/ 396:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popper_vue__ = __webpack_require__(194);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popper_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popper_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popper_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popper_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_307d2e38_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_popper_vue__ = __webpack_require__(398);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popper_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_307d2e38_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_popper_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_307d2e38_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_popper_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 397:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* WEBPACK VAR INJECTION */(function(global) {/**!
 * @fileOverview Kickass library to create and place poppers near their reference elements.
 * @version 1.16.1
 * @license
 * Copyright (c) 2016 Federico Zivolo and contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined' && typeof navigator !== 'undefined';

var timeoutDuration = function () {
  var longerTimeoutBrowsers = ['Edge', 'Trident', 'Firefox'];
  for (var i = 0; i < longerTimeoutBrowsers.length; i += 1) {
    if (isBrowser && navigator.userAgent.indexOf(longerTimeoutBrowsers[i]) >= 0) {
      return 1;
    }
  }
  return 0;
}();

function microtaskDebounce(fn) {
  var called = false;
  return function () {
    if (called) {
      return;
    }
    called = true;
    window.Promise.resolve().then(function () {
      called = false;
      fn();
    });
  };
}

function taskDebounce(fn) {
  var scheduled = false;
  return function () {
    if (!scheduled) {
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        fn();
      }, timeoutDuration);
    }
  };
}

var supportsMicroTasks = isBrowser && window.Promise;

/**
* Create a debounced version of a method, that's asynchronously deferred
* but called in the minimum time possible.
*
* @method
* @memberof Popper.Utils
* @argument {Function} fn
* @returns {Function}
*/
var debounce = supportsMicroTasks ? microtaskDebounce : taskDebounce;

/**
 * Check if the given variable is a function
 * @method
 * @memberof Popper.Utils
 * @argument {Any} functionToCheck - variable to check
 * @returns {Boolean} answer to: is a function?
 */
function isFunction(functionToCheck) {
  var getType = {};
  return functionToCheck && getType.toString.call(functionToCheck) === '[object Function]';
}

/**
 * Get CSS computed property of the given element
 * @method
 * @memberof Popper.Utils
 * @argument {Eement} element
 * @argument {String} property
 */
function getStyleComputedProperty(element, property) {
  if (element.nodeType !== 1) {
    return [];
  }
  // NOTE: 1 DOM access here
  var window = element.ownerDocument.defaultView;
  var css = window.getComputedStyle(element, null);
  return property ? css[property] : css;
}

/**
 * Returns the parentNode or the host of the element
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Element} parent
 */
function getParentNode(element) {
  if (element.nodeName === 'HTML') {
    return element;
  }
  return element.parentNode || element.host;
}

/**
 * Returns the scrolling parent of the given element
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Element} scroll parent
 */
function getScrollParent(element) {
  // Return body, `getScroll` will take care to get the correct `scrollTop` from it
  if (!element) {
    return document.body;
  }

  switch (element.nodeName) {
    case 'HTML':
    case 'BODY':
      return element.ownerDocument.body;
    case '#document':
      return element.body;
  }

  // Firefox want us to check `-x` and `-y` variations as well

  var _getStyleComputedProp = getStyleComputedProperty(element),
      overflow = _getStyleComputedProp.overflow,
      overflowX = _getStyleComputedProp.overflowX,
      overflowY = _getStyleComputedProp.overflowY;

  if (/(auto|scroll|overlay)/.test(overflow + overflowY + overflowX)) {
    return element;
  }

  return getScrollParent(getParentNode(element));
}

/**
 * Returns the reference node of the reference object, or the reference object itself.
 * @method
 * @memberof Popper.Utils
 * @param {Element|Object} reference - the reference element (the popper will be relative to this)
 * @returns {Element} parent
 */
function getReferenceNode(reference) {
  return reference && reference.referenceNode ? reference.referenceNode : reference;
}

var isIE11 = isBrowser && !!(window.MSInputMethodContext && document.documentMode);
var isIE10 = isBrowser && /MSIE 10/.test(navigator.userAgent);

/**
 * Determines if the browser is Internet Explorer
 * @method
 * @memberof Popper.Utils
 * @param {Number} version to check
 * @returns {Boolean} isIE
 */
function isIE(version) {
  if (version === 11) {
    return isIE11;
  }
  if (version === 10) {
    return isIE10;
  }
  return isIE11 || isIE10;
}

/**
 * Returns the offset parent of the given element
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Element} offset parent
 */
function getOffsetParent(element) {
  if (!element) {
    return document.documentElement;
  }

  var noOffsetParent = isIE(10) ? document.body : null;

  // NOTE: 1 DOM access here
  var offsetParent = element.offsetParent || null;
  // Skip hidden elements which don't have an offsetParent
  while (offsetParent === noOffsetParent && element.nextElementSibling) {
    offsetParent = (element = element.nextElementSibling).offsetParent;
  }

  var nodeName = offsetParent && offsetParent.nodeName;

  if (!nodeName || nodeName === 'BODY' || nodeName === 'HTML') {
    return element ? element.ownerDocument.documentElement : document.documentElement;
  }

  // .offsetParent will return the closest TH, TD or TABLE in case
  // no offsetParent is present, I hate this job...
  if (['TH', 'TD', 'TABLE'].indexOf(offsetParent.nodeName) !== -1 && getStyleComputedProperty(offsetParent, 'position') === 'static') {
    return getOffsetParent(offsetParent);
  }

  return offsetParent;
}

function isOffsetContainer(element) {
  var nodeName = element.nodeName;

  if (nodeName === 'BODY') {
    return false;
  }
  return nodeName === 'HTML' || getOffsetParent(element.firstElementChild) === element;
}

/**
 * Finds the root node (document, shadowDOM root) of the given element
 * @method
 * @memberof Popper.Utils
 * @argument {Element} node
 * @returns {Element} root node
 */
function getRoot(node) {
  if (node.parentNode !== null) {
    return getRoot(node.parentNode);
  }

  return node;
}

/**
 * Finds the offset parent common to the two provided nodes
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element1
 * @argument {Element} element2
 * @returns {Element} common offset parent
 */
function findCommonOffsetParent(element1, element2) {
  // This check is needed to avoid errors in case one of the elements isn't defined for any reason
  if (!element1 || !element1.nodeType || !element2 || !element2.nodeType) {
    return document.documentElement;
  }

  // Here we make sure to give as "start" the element that comes first in the DOM
  var order = element1.compareDocumentPosition(element2) & Node.DOCUMENT_POSITION_FOLLOWING;
  var start = order ? element1 : element2;
  var end = order ? element2 : element1;

  // Get common ancestor container
  var range = document.createRange();
  range.setStart(start, 0);
  range.setEnd(end, 0);
  var commonAncestorContainer = range.commonAncestorContainer;

  // Both nodes are inside #document

  if (element1 !== commonAncestorContainer && element2 !== commonAncestorContainer || start.contains(end)) {
    if (isOffsetContainer(commonAncestorContainer)) {
      return commonAncestorContainer;
    }

    return getOffsetParent(commonAncestorContainer);
  }

  // one of the nodes is inside shadowDOM, find which one
  var element1root = getRoot(element1);
  if (element1root.host) {
    return findCommonOffsetParent(element1root.host, element2);
  } else {
    return findCommonOffsetParent(element1, getRoot(element2).host);
  }
}

/**
 * Gets the scroll value of the given element in the given side (top and left)
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @argument {String} side `top` or `left`
 * @returns {number} amount of scrolled pixels
 */
function getScroll(element) {
  var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'top';

  var upperSide = side === 'top' ? 'scrollTop' : 'scrollLeft';
  var nodeName = element.nodeName;

  if (nodeName === 'BODY' || nodeName === 'HTML') {
    var html = element.ownerDocument.documentElement;
    var scrollingElement = element.ownerDocument.scrollingElement || html;
    return scrollingElement[upperSide];
  }

  return element[upperSide];
}

/*
 * Sum or subtract the element scroll values (left and top) from a given rect object
 * @method
 * @memberof Popper.Utils
 * @param {Object} rect - Rect object you want to change
 * @param {HTMLElement} element - The element from the function reads the scroll values
 * @param {Boolean} subtract - set to true if you want to subtract the scroll values
 * @return {Object} rect - The modifier rect object
 */
function includeScroll(rect, element) {
  var subtract = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  var scrollTop = getScroll(element, 'top');
  var scrollLeft = getScroll(element, 'left');
  var modifier = subtract ? -1 : 1;
  rect.top += scrollTop * modifier;
  rect.bottom += scrollTop * modifier;
  rect.left += scrollLeft * modifier;
  rect.right += scrollLeft * modifier;
  return rect;
}

/*
 * Helper to detect borders of a given element
 * @method
 * @memberof Popper.Utils
 * @param {CSSStyleDeclaration} styles
 * Result of `getStyleComputedProperty` on the given element
 * @param {String} axis - `x` or `y`
 * @return {number} borders - The borders size of the given axis
 */

function getBordersSize(styles, axis) {
  var sideA = axis === 'x' ? 'Left' : 'Top';
  var sideB = sideA === 'Left' ? 'Right' : 'Bottom';

  return parseFloat(styles['border' + sideA + 'Width']) + parseFloat(styles['border' + sideB + 'Width']);
}

function getSize(axis, body, html, computedStyle) {
  return Math.max(body['offset' + axis], body['scroll' + axis], html['client' + axis], html['offset' + axis], html['scroll' + axis], isIE(10) ? parseInt(html['offset' + axis]) + parseInt(computedStyle['margin' + (axis === 'Height' ? 'Top' : 'Left')]) + parseInt(computedStyle['margin' + (axis === 'Height' ? 'Bottom' : 'Right')]) : 0);
}

function getWindowSizes(document) {
  var body = document.body;
  var html = document.documentElement;
  var computedStyle = isIE(10) && getComputedStyle(html);

  return {
    height: getSize('Height', body, html, computedStyle),
    width: getSize('Width', body, html, computedStyle)
  };
}

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();





var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

/**
 * Given element offsets, generate an output similar to getBoundingClientRect
 * @method
 * @memberof Popper.Utils
 * @argument {Object} offsets
 * @returns {Object} ClientRect like output
 */
function getClientRect(offsets) {
  return _extends({}, offsets, {
    right: offsets.left + offsets.width,
    bottom: offsets.top + offsets.height
  });
}

/**
 * Get bounding client rect of given element
 * @method
 * @memberof Popper.Utils
 * @param {HTMLElement} element
 * @return {Object} client rect
 */
function getBoundingClientRect(element) {
  var rect = {};

  // IE10 10 FIX: Please, don't ask, the element isn't
  // considered in DOM in some circumstances...
  // This isn't reproducible in IE10 compatibility mode of IE11
  try {
    if (isIE(10)) {
      rect = element.getBoundingClientRect();
      var scrollTop = getScroll(element, 'top');
      var scrollLeft = getScroll(element, 'left');
      rect.top += scrollTop;
      rect.left += scrollLeft;
      rect.bottom += scrollTop;
      rect.right += scrollLeft;
    } else {
      rect = element.getBoundingClientRect();
    }
  } catch (e) {}

  var result = {
    left: rect.left,
    top: rect.top,
    width: rect.right - rect.left,
    height: rect.bottom - rect.top
  };

  // subtract scrollbar size from sizes
  var sizes = element.nodeName === 'HTML' ? getWindowSizes(element.ownerDocument) : {};
  var width = sizes.width || element.clientWidth || result.width;
  var height = sizes.height || element.clientHeight || result.height;

  var horizScrollbar = element.offsetWidth - width;
  var vertScrollbar = element.offsetHeight - height;

  // if an hypothetical scrollbar is detected, we must be sure it's not a `border`
  // we make this check conditional for performance reasons
  if (horizScrollbar || vertScrollbar) {
    var styles = getStyleComputedProperty(element);
    horizScrollbar -= getBordersSize(styles, 'x');
    vertScrollbar -= getBordersSize(styles, 'y');

    result.width -= horizScrollbar;
    result.height -= vertScrollbar;
  }

  return getClientRect(result);
}

function getOffsetRectRelativeToArbitraryNode(children, parent) {
  var fixedPosition = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  var isIE10 = isIE(10);
  var isHTML = parent.nodeName === 'HTML';
  var childrenRect = getBoundingClientRect(children);
  var parentRect = getBoundingClientRect(parent);
  var scrollParent = getScrollParent(children);

  var styles = getStyleComputedProperty(parent);
  var borderTopWidth = parseFloat(styles.borderTopWidth);
  var borderLeftWidth = parseFloat(styles.borderLeftWidth);

  // In cases where the parent is fixed, we must ignore negative scroll in offset calc
  if (fixedPosition && isHTML) {
    parentRect.top = Math.max(parentRect.top, 0);
    parentRect.left = Math.max(parentRect.left, 0);
  }
  var offsets = getClientRect({
    top: childrenRect.top - parentRect.top - borderTopWidth,
    left: childrenRect.left - parentRect.left - borderLeftWidth,
    width: childrenRect.width,
    height: childrenRect.height
  });
  offsets.marginTop = 0;
  offsets.marginLeft = 0;

  // Subtract margins of documentElement in case it's being used as parent
  // we do this only on HTML because it's the only element that behaves
  // differently when margins are applied to it. The margins are included in
  // the box of the documentElement, in the other cases not.
  if (!isIE10 && isHTML) {
    var marginTop = parseFloat(styles.marginTop);
    var marginLeft = parseFloat(styles.marginLeft);

    offsets.top -= borderTopWidth - marginTop;
    offsets.bottom -= borderTopWidth - marginTop;
    offsets.left -= borderLeftWidth - marginLeft;
    offsets.right -= borderLeftWidth - marginLeft;

    // Attach marginTop and marginLeft because in some circumstances we may need them
    offsets.marginTop = marginTop;
    offsets.marginLeft = marginLeft;
  }

  if (isIE10 && !fixedPosition ? parent.contains(scrollParent) : parent === scrollParent && scrollParent.nodeName !== 'BODY') {
    offsets = includeScroll(offsets, parent);
  }

  return offsets;
}

function getViewportOffsetRectRelativeToArtbitraryNode(element) {
  var excludeScroll = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  var html = element.ownerDocument.documentElement;
  var relativeOffset = getOffsetRectRelativeToArbitraryNode(element, html);
  var width = Math.max(html.clientWidth, window.innerWidth || 0);
  var height = Math.max(html.clientHeight, window.innerHeight || 0);

  var scrollTop = !excludeScroll ? getScroll(html) : 0;
  var scrollLeft = !excludeScroll ? getScroll(html, 'left') : 0;

  var offset = {
    top: scrollTop - relativeOffset.top + relativeOffset.marginTop,
    left: scrollLeft - relativeOffset.left + relativeOffset.marginLeft,
    width: width,
    height: height
  };

  return getClientRect(offset);
}

/**
 * Check if the given element is fixed or is inside a fixed parent
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @argument {Element} customContainer
 * @returns {Boolean} answer to "isFixed?"
 */
function isFixed(element) {
  var nodeName = element.nodeName;
  if (nodeName === 'BODY' || nodeName === 'HTML') {
    return false;
  }
  if (getStyleComputedProperty(element, 'position') === 'fixed') {
    return true;
  }
  var parentNode = getParentNode(element);
  if (!parentNode) {
    return false;
  }
  return isFixed(parentNode);
}

/**
 * Finds the first parent of an element that has a transformed property defined
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Element} first transformed parent or documentElement
 */

function getFixedPositionOffsetParent(element) {
  // This check is needed to avoid errors in case one of the elements isn't defined for any reason
  if (!element || !element.parentElement || isIE()) {
    return document.documentElement;
  }
  var el = element.parentElement;
  while (el && getStyleComputedProperty(el, 'transform') === 'none') {
    el = el.parentElement;
  }
  return el || document.documentElement;
}

/**
 * Computed the boundaries limits and return them
 * @method
 * @memberof Popper.Utils
 * @param {HTMLElement} popper
 * @param {HTMLElement} reference
 * @param {number} padding
 * @param {HTMLElement} boundariesElement - Element used to define the boundaries
 * @param {Boolean} fixedPosition - Is in fixed position mode
 * @returns {Object} Coordinates of the boundaries
 */
function getBoundaries(popper, reference, padding, boundariesElement) {
  var fixedPosition = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;

  // NOTE: 1 DOM access here

  var boundaries = { top: 0, left: 0 };
  var offsetParent = fixedPosition ? getFixedPositionOffsetParent(popper) : findCommonOffsetParent(popper, getReferenceNode(reference));

  // Handle viewport case
  if (boundariesElement === 'viewport') {
    boundaries = getViewportOffsetRectRelativeToArtbitraryNode(offsetParent, fixedPosition);
  } else {
    // Handle other cases based on DOM element used as boundaries
    var boundariesNode = void 0;
    if (boundariesElement === 'scrollParent') {
      boundariesNode = getScrollParent(getParentNode(reference));
      if (boundariesNode.nodeName === 'BODY') {
        boundariesNode = popper.ownerDocument.documentElement;
      }
    } else if (boundariesElement === 'window') {
      boundariesNode = popper.ownerDocument.documentElement;
    } else {
      boundariesNode = boundariesElement;
    }

    var offsets = getOffsetRectRelativeToArbitraryNode(boundariesNode, offsetParent, fixedPosition);

    // In case of HTML, we need a different computation
    if (boundariesNode.nodeName === 'HTML' && !isFixed(offsetParent)) {
      var _getWindowSizes = getWindowSizes(popper.ownerDocument),
          height = _getWindowSizes.height,
          width = _getWindowSizes.width;

      boundaries.top += offsets.top - offsets.marginTop;
      boundaries.bottom = height + offsets.top;
      boundaries.left += offsets.left - offsets.marginLeft;
      boundaries.right = width + offsets.left;
    } else {
      // for all the other DOM elements, this one is good
      boundaries = offsets;
    }
  }

  // Add paddings
  padding = padding || 0;
  var isPaddingNumber = typeof padding === 'number';
  boundaries.left += isPaddingNumber ? padding : padding.left || 0;
  boundaries.top += isPaddingNumber ? padding : padding.top || 0;
  boundaries.right -= isPaddingNumber ? padding : padding.right || 0;
  boundaries.bottom -= isPaddingNumber ? padding : padding.bottom || 0;

  return boundaries;
}

function getArea(_ref) {
  var width = _ref.width,
      height = _ref.height;

  return width * height;
}

/**
 * Utility used to transform the `auto` placement to the placement with more
 * available space.
 * @method
 * @memberof Popper.Utils
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function computeAutoPlacement(placement, refRect, popper, reference, boundariesElement) {
  var padding = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 0;

  if (placement.indexOf('auto') === -1) {
    return placement;
  }

  var boundaries = getBoundaries(popper, reference, padding, boundariesElement);

  var rects = {
    top: {
      width: boundaries.width,
      height: refRect.top - boundaries.top
    },
    right: {
      width: boundaries.right - refRect.right,
      height: boundaries.height
    },
    bottom: {
      width: boundaries.width,
      height: boundaries.bottom - refRect.bottom
    },
    left: {
      width: refRect.left - boundaries.left,
      height: boundaries.height
    }
  };

  var sortedAreas = Object.keys(rects).map(function (key) {
    return _extends({
      key: key
    }, rects[key], {
      area: getArea(rects[key])
    });
  }).sort(function (a, b) {
    return b.area - a.area;
  });

  var filteredAreas = sortedAreas.filter(function (_ref2) {
    var width = _ref2.width,
        height = _ref2.height;
    return width >= popper.clientWidth && height >= popper.clientHeight;
  });

  var computedPlacement = filteredAreas.length > 0 ? filteredAreas[0].key : sortedAreas[0].key;

  var variation = placement.split('-')[1];

  return computedPlacement + (variation ? '-' + variation : '');
}

/**
 * Get offsets to the reference element
 * @method
 * @memberof Popper.Utils
 * @param {Object} state
 * @param {Element} popper - the popper element
 * @param {Element} reference - the reference element (the popper will be relative to this)
 * @param {Element} fixedPosition - is in fixed position mode
 * @returns {Object} An object containing the offsets which will be applied to the popper
 */
function getReferenceOffsets(state, popper, reference) {
  var fixedPosition = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;

  var commonOffsetParent = fixedPosition ? getFixedPositionOffsetParent(popper) : findCommonOffsetParent(popper, getReferenceNode(reference));
  return getOffsetRectRelativeToArbitraryNode(reference, commonOffsetParent, fixedPosition);
}

/**
 * Get the outer sizes of the given element (offset size + margins)
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Object} object containing width and height properties
 */
function getOuterSizes(element) {
  var window = element.ownerDocument.defaultView;
  var styles = window.getComputedStyle(element);
  var x = parseFloat(styles.marginTop || 0) + parseFloat(styles.marginBottom || 0);
  var y = parseFloat(styles.marginLeft || 0) + parseFloat(styles.marginRight || 0);
  var result = {
    width: element.offsetWidth + y,
    height: element.offsetHeight + x
  };
  return result;
}

/**
 * Get the opposite placement of the given one
 * @method
 * @memberof Popper.Utils
 * @argument {String} placement
 * @returns {String} flipped placement
 */
function getOppositePlacement(placement) {
  var hash = { left: 'right', right: 'left', bottom: 'top', top: 'bottom' };
  return placement.replace(/left|right|bottom|top/g, function (matched) {
    return hash[matched];
  });
}

/**
 * Get offsets to the popper
 * @method
 * @memberof Popper.Utils
 * @param {Object} position - CSS position the Popper will get applied
 * @param {HTMLElement} popper - the popper element
 * @param {Object} referenceOffsets - the reference offsets (the popper will be relative to this)
 * @param {String} placement - one of the valid placement options
 * @returns {Object} popperOffsets - An object containing the offsets which will be applied to the popper
 */
function getPopperOffsets(popper, referenceOffsets, placement) {
  placement = placement.split('-')[0];

  // Get popper node sizes
  var popperRect = getOuterSizes(popper);

  // Add position, width and height to our offsets object
  var popperOffsets = {
    width: popperRect.width,
    height: popperRect.height
  };

  // depending by the popper placement we have to compute its offsets slightly differently
  var isHoriz = ['right', 'left'].indexOf(placement) !== -1;
  var mainSide = isHoriz ? 'top' : 'left';
  var secondarySide = isHoriz ? 'left' : 'top';
  var measurement = isHoriz ? 'height' : 'width';
  var secondaryMeasurement = !isHoriz ? 'height' : 'width';

  popperOffsets[mainSide] = referenceOffsets[mainSide] + referenceOffsets[measurement] / 2 - popperRect[measurement] / 2;
  if (placement === secondarySide) {
    popperOffsets[secondarySide] = referenceOffsets[secondarySide] - popperRect[secondaryMeasurement];
  } else {
    popperOffsets[secondarySide] = referenceOffsets[getOppositePlacement(secondarySide)];
  }

  return popperOffsets;
}

/**
 * Mimics the `find` method of Array
 * @method
 * @memberof Popper.Utils
 * @argument {Array} arr
 * @argument prop
 * @argument value
 * @returns index or -1
 */
function find(arr, check) {
  // use native find if supported
  if (Array.prototype.find) {
    return arr.find(check);
  }

  // use `filter` to obtain the same behavior of `find`
  return arr.filter(check)[0];
}

/**
 * Return the index of the matching object
 * @method
 * @memberof Popper.Utils
 * @argument {Array} arr
 * @argument prop
 * @argument value
 * @returns index or -1
 */
function findIndex(arr, prop, value) {
  // use native findIndex if supported
  if (Array.prototype.findIndex) {
    return arr.findIndex(function (cur) {
      return cur[prop] === value;
    });
  }

  // use `find` + `indexOf` if `findIndex` isn't supported
  var match = find(arr, function (obj) {
    return obj[prop] === value;
  });
  return arr.indexOf(match);
}

/**
 * Loop trough the list of modifiers and run them in order,
 * each of them will then edit the data object.
 * @method
 * @memberof Popper.Utils
 * @param {dataObject} data
 * @param {Array} modifiers
 * @param {String} ends - Optional modifier name used as stopper
 * @returns {dataObject}
 */
function runModifiers(modifiers, data, ends) {
  var modifiersToRun = ends === undefined ? modifiers : modifiers.slice(0, findIndex(modifiers, 'name', ends));

  modifiersToRun.forEach(function (modifier) {
    if (modifier['function']) {
      // eslint-disable-line dot-notation
      console.warn('`modifier.function` is deprecated, use `modifier.fn`!');
    }
    var fn = modifier['function'] || modifier.fn; // eslint-disable-line dot-notation
    if (modifier.enabled && isFunction(fn)) {
      // Add properties to offsets to make them a complete clientRect object
      // we do this before each modifier to make sure the previous one doesn't
      // mess with these values
      data.offsets.popper = getClientRect(data.offsets.popper);
      data.offsets.reference = getClientRect(data.offsets.reference);

      data = fn(data, modifier);
    }
  });

  return data;
}

/**
 * Updates the position of the popper, computing the new offsets and applying
 * the new style.<br />
 * Prefer `scheduleUpdate` over `update` because of performance reasons.
 * @method
 * @memberof Popper
 */
function update() {
  // if popper is destroyed, don't perform any further update
  if (this.state.isDestroyed) {
    return;
  }

  var data = {
    instance: this,
    styles: {},
    arrowStyles: {},
    attributes: {},
    flipped: false,
    offsets: {}
  };

  // compute reference element offsets
  data.offsets.reference = getReferenceOffsets(this.state, this.popper, this.reference, this.options.positionFixed);

  // compute auto placement, store placement inside the data object,
  // modifiers will be able to edit `placement` if needed
  // and refer to originalPlacement to know the original value
  data.placement = computeAutoPlacement(this.options.placement, data.offsets.reference, this.popper, this.reference, this.options.modifiers.flip.boundariesElement, this.options.modifiers.flip.padding);

  // store the computed placement inside `originalPlacement`
  data.originalPlacement = data.placement;

  data.positionFixed = this.options.positionFixed;

  // compute the popper offsets
  data.offsets.popper = getPopperOffsets(this.popper, data.offsets.reference, data.placement);

  data.offsets.popper.position = this.options.positionFixed ? 'fixed' : 'absolute';

  // run the modifiers
  data = runModifiers(this.modifiers, data);

  // the first `update` will call `onCreate` callback
  // the other ones will call `onUpdate` callback
  if (!this.state.isCreated) {
    this.state.isCreated = true;
    this.options.onCreate(data);
  } else {
    this.options.onUpdate(data);
  }
}

/**
 * Helper used to know if the given modifier is enabled.
 * @method
 * @memberof Popper.Utils
 * @returns {Boolean}
 */
function isModifierEnabled(modifiers, modifierName) {
  return modifiers.some(function (_ref) {
    var name = _ref.name,
        enabled = _ref.enabled;
    return enabled && name === modifierName;
  });
}

/**
 * Get the prefixed supported property name
 * @method
 * @memberof Popper.Utils
 * @argument {String} property (camelCase)
 * @returns {String} prefixed property (camelCase or PascalCase, depending on the vendor prefix)
 */
function getSupportedPropertyName(property) {
  var prefixes = [false, 'ms', 'Webkit', 'Moz', 'O'];
  var upperProp = property.charAt(0).toUpperCase() + property.slice(1);

  for (var i = 0; i < prefixes.length; i++) {
    var prefix = prefixes[i];
    var toCheck = prefix ? '' + prefix + upperProp : property;
    if (typeof document.body.style[toCheck] !== 'undefined') {
      return toCheck;
    }
  }
  return null;
}

/**
 * Destroys the popper.
 * @method
 * @memberof Popper
 */
function destroy() {
  this.state.isDestroyed = true;

  // touch DOM only if `applyStyle` modifier is enabled
  if (isModifierEnabled(this.modifiers, 'applyStyle')) {
    this.popper.removeAttribute('x-placement');
    this.popper.style.position = '';
    this.popper.style.top = '';
    this.popper.style.left = '';
    this.popper.style.right = '';
    this.popper.style.bottom = '';
    this.popper.style.willChange = '';
    this.popper.style[getSupportedPropertyName('transform')] = '';
  }

  this.disableEventListeners();

  // remove the popper if user explicitly asked for the deletion on destroy
  // do not use `remove` because IE11 doesn't support it
  if (this.options.removeOnDestroy) {
    this.popper.parentNode.removeChild(this.popper);
  }
  return this;
}

/**
 * Get the window associated with the element
 * @argument {Element} element
 * @returns {Window}
 */
function getWindow(element) {
  var ownerDocument = element.ownerDocument;
  return ownerDocument ? ownerDocument.defaultView : window;
}

function attachToScrollParents(scrollParent, event, callback, scrollParents) {
  var isBody = scrollParent.nodeName === 'BODY';
  var target = isBody ? scrollParent.ownerDocument.defaultView : scrollParent;
  target.addEventListener(event, callback, { passive: true });

  if (!isBody) {
    attachToScrollParents(getScrollParent(target.parentNode), event, callback, scrollParents);
  }
  scrollParents.push(target);
}

/**
 * Setup needed event listeners used to update the popper position
 * @method
 * @memberof Popper.Utils
 * @private
 */
function setupEventListeners(reference, options, state, updateBound) {
  // Resize event listener on window
  state.updateBound = updateBound;
  getWindow(reference).addEventListener('resize', state.updateBound, { passive: true });

  // Scroll event listener on scroll parents
  var scrollElement = getScrollParent(reference);
  attachToScrollParents(scrollElement, 'scroll', state.updateBound, state.scrollParents);
  state.scrollElement = scrollElement;
  state.eventsEnabled = true;

  return state;
}

/**
 * It will add resize/scroll events and start recalculating
 * position of the popper element when they are triggered.
 * @method
 * @memberof Popper
 */
function enableEventListeners() {
  if (!this.state.eventsEnabled) {
    this.state = setupEventListeners(this.reference, this.options, this.state, this.scheduleUpdate);
  }
}

/**
 * Remove event listeners used to update the popper position
 * @method
 * @memberof Popper.Utils
 * @private
 */
function removeEventListeners(reference, state) {
  // Remove resize event listener on window
  getWindow(reference).removeEventListener('resize', state.updateBound);

  // Remove scroll event listener on scroll parents
  state.scrollParents.forEach(function (target) {
    target.removeEventListener('scroll', state.updateBound);
  });

  // Reset state
  state.updateBound = null;
  state.scrollParents = [];
  state.scrollElement = null;
  state.eventsEnabled = false;
  return state;
}

/**
 * It will remove resize/scroll events and won't recalculate popper position
 * when they are triggered. It also won't trigger `onUpdate` callback anymore,
 * unless you call `update` method manually.
 * @method
 * @memberof Popper
 */
function disableEventListeners() {
  if (this.state.eventsEnabled) {
    cancelAnimationFrame(this.scheduleUpdate);
    this.state = removeEventListeners(this.reference, this.state);
  }
}

/**
 * Tells if a given input is a number
 * @method
 * @memberof Popper.Utils
 * @param {*} input to check
 * @return {Boolean}
 */
function isNumeric(n) {
  return n !== '' && !isNaN(parseFloat(n)) && isFinite(n);
}

/**
 * Set the style to the given popper
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element - Element to apply the style to
 * @argument {Object} styles
 * Object with a list of properties and values which will be applied to the element
 */
function setStyles(element, styles) {
  Object.keys(styles).forEach(function (prop) {
    var unit = '';
    // add unit if the value is numeric and is one of the following
    if (['width', 'height', 'top', 'right', 'bottom', 'left'].indexOf(prop) !== -1 && isNumeric(styles[prop])) {
      unit = 'px';
    }
    element.style[prop] = styles[prop] + unit;
  });
}

/**
 * Set the attributes to the given popper
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element - Element to apply the attributes to
 * @argument {Object} styles
 * Object with a list of properties and values which will be applied to the element
 */
function setAttributes(element, attributes) {
  Object.keys(attributes).forEach(function (prop) {
    var value = attributes[prop];
    if (value !== false) {
      element.setAttribute(prop, attributes[prop]);
    } else {
      element.removeAttribute(prop);
    }
  });
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} data.styles - List of style properties - values to apply to popper element
 * @argument {Object} data.attributes - List of attribute properties - values to apply to popper element
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The same data object
 */
function applyStyle(data) {
  // any property present in `data.styles` will be applied to the popper,
  // in this way we can make the 3rd party modifiers add custom styles to it
  // Be aware, modifiers could override the properties defined in the previous
  // lines of this modifier!
  setStyles(data.instance.popper, data.styles);

  // any property present in `data.attributes` will be applied to the popper,
  // they will be set as HTML attributes of the element
  setAttributes(data.instance.popper, data.attributes);

  // if arrowElement is defined and arrowStyles has some properties
  if (data.arrowElement && Object.keys(data.arrowStyles).length) {
    setStyles(data.arrowElement, data.arrowStyles);
  }

  return data;
}

/**
 * Set the x-placement attribute before everything else because it could be used
 * to add margins to the popper margins needs to be calculated to get the
 * correct popper offsets.
 * @method
 * @memberof Popper.modifiers
 * @param {HTMLElement} reference - The reference element used to position the popper
 * @param {HTMLElement} popper - The HTML element used as popper
 * @param {Object} options - Popper.js options
 */
function applyStyleOnLoad(reference, popper, options, modifierOptions, state) {
  // compute reference element offsets
  var referenceOffsets = getReferenceOffsets(state, popper, reference, options.positionFixed);

  // compute auto placement, store placement inside the data object,
  // modifiers will be able to edit `placement` if needed
  // and refer to originalPlacement to know the original value
  var placement = computeAutoPlacement(options.placement, referenceOffsets, popper, reference, options.modifiers.flip.boundariesElement, options.modifiers.flip.padding);

  popper.setAttribute('x-placement', placement);

  // Apply `position` to popper before anything else because
  // without the position applied we can't guarantee correct computations
  setStyles(popper, { position: options.positionFixed ? 'fixed' : 'absolute' });

  return options;
}

/**
 * @function
 * @memberof Popper.Utils
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Boolean} shouldRound - If the offsets should be rounded at all
 * @returns {Object} The popper's position offsets rounded
 *
 * The tale of pixel-perfect positioning. It's still not 100% perfect, but as
 * good as it can be within reason.
 * Discussion here: https://github.com/FezVrasta/popper.js/pull/715
 *
 * Low DPI screens cause a popper to be blurry if not using full pixels (Safari
 * as well on High DPI screens).
 *
 * Firefox prefers no rounding for positioning and does not have blurriness on
 * high DPI screens.
 *
 * Only horizontal placement and left/right values need to be considered.
 */
function getRoundedOffsets(data, shouldRound) {
  var _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;
  var round = Math.round,
      floor = Math.floor;

  var noRound = function noRound(v) {
    return v;
  };

  var referenceWidth = round(reference.width);
  var popperWidth = round(popper.width);

  var isVertical = ['left', 'right'].indexOf(data.placement) !== -1;
  var isVariation = data.placement.indexOf('-') !== -1;
  var sameWidthParity = referenceWidth % 2 === popperWidth % 2;
  var bothOddWidth = referenceWidth % 2 === 1 && popperWidth % 2 === 1;

  var horizontalToInteger = !shouldRound ? noRound : isVertical || isVariation || sameWidthParity ? round : floor;
  var verticalToInteger = !shouldRound ? noRound : round;

  return {
    left: horizontalToInteger(bothOddWidth && !isVariation && shouldRound ? popper.left - 1 : popper.left),
    top: verticalToInteger(popper.top),
    bottom: verticalToInteger(popper.bottom),
    right: horizontalToInteger(popper.right)
  };
}

var isFirefox = isBrowser && /Firefox/i.test(navigator.userAgent);

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function computeStyle(data, options) {
  var x = options.x,
      y = options.y;
  var popper = data.offsets.popper;

  // Remove this legacy support in Popper.js v2

  var legacyGpuAccelerationOption = find(data.instance.modifiers, function (modifier) {
    return modifier.name === 'applyStyle';
  }).gpuAcceleration;
  if (legacyGpuAccelerationOption !== undefined) {
    console.warn('WARNING: `gpuAcceleration` option moved to `computeStyle` modifier and will not be supported in future versions of Popper.js!');
  }
  var gpuAcceleration = legacyGpuAccelerationOption !== undefined ? legacyGpuAccelerationOption : options.gpuAcceleration;

  var offsetParent = getOffsetParent(data.instance.popper);
  var offsetParentRect = getBoundingClientRect(offsetParent);

  // Styles
  var styles = {
    position: popper.position
  };

  var offsets = getRoundedOffsets(data, window.devicePixelRatio < 2 || !isFirefox);

  var sideA = x === 'bottom' ? 'top' : 'bottom';
  var sideB = y === 'right' ? 'left' : 'right';

  // if gpuAcceleration is set to `true` and transform is supported,
  //  we use `translate3d` to apply the position to the popper we
  // automatically use the supported prefixed version if needed
  var prefixedProperty = getSupportedPropertyName('transform');

  // now, let's make a step back and look at this code closely (wtf?)
  // If the content of the popper grows once it's been positioned, it
  // may happen that the popper gets misplaced because of the new content
  // overflowing its reference element
  // To avoid this problem, we provide two options (x and y), which allow
  // the consumer to define the offset origin.
  // If we position a popper on top of a reference element, we can set
  // `x` to `top` to make the popper grow towards its top instead of
  // its bottom.
  var left = void 0,
      top = void 0;
  if (sideA === 'bottom') {
    // when offsetParent is <html> the positioning is relative to the bottom of the screen (excluding the scrollbar)
    // and not the bottom of the html element
    if (offsetParent.nodeName === 'HTML') {
      top = -offsetParent.clientHeight + offsets.bottom;
    } else {
      top = -offsetParentRect.height + offsets.bottom;
    }
  } else {
    top = offsets.top;
  }
  if (sideB === 'right') {
    if (offsetParent.nodeName === 'HTML') {
      left = -offsetParent.clientWidth + offsets.right;
    } else {
      left = -offsetParentRect.width + offsets.right;
    }
  } else {
    left = offsets.left;
  }
  if (gpuAcceleration && prefixedProperty) {
    styles[prefixedProperty] = 'translate3d(' + left + 'px, ' + top + 'px, 0)';
    styles[sideA] = 0;
    styles[sideB] = 0;
    styles.willChange = 'transform';
  } else {
    // othwerise, we use the standard `top`, `left`, `bottom` and `right` properties
    var invertTop = sideA === 'bottom' ? -1 : 1;
    var invertLeft = sideB === 'right' ? -1 : 1;
    styles[sideA] = top * invertTop;
    styles[sideB] = left * invertLeft;
    styles.willChange = sideA + ', ' + sideB;
  }

  // Attributes
  var attributes = {
    'x-placement': data.placement
  };

  // Update `data` attributes, styles and arrowStyles
  data.attributes = _extends({}, attributes, data.attributes);
  data.styles = _extends({}, styles, data.styles);
  data.arrowStyles = _extends({}, data.offsets.arrow, data.arrowStyles);

  return data;
}

/**
 * Helper used to know if the given modifier depends from another one.<br />
 * It checks if the needed modifier is listed and enabled.
 * @method
 * @memberof Popper.Utils
 * @param {Array} modifiers - list of modifiers
 * @param {String} requestingName - name of requesting modifier
 * @param {String} requestedName - name of requested modifier
 * @returns {Boolean}
 */
function isModifierRequired(modifiers, requestingName, requestedName) {
  var requesting = find(modifiers, function (_ref) {
    var name = _ref.name;
    return name === requestingName;
  });

  var isRequired = !!requesting && modifiers.some(function (modifier) {
    return modifier.name === requestedName && modifier.enabled && modifier.order < requesting.order;
  });

  if (!isRequired) {
    var _requesting = '`' + requestingName + '`';
    var requested = '`' + requestedName + '`';
    console.warn(requested + ' modifier is required by ' + _requesting + ' modifier in order to work, be sure to include it before ' + _requesting + '!');
  }
  return isRequired;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function arrow(data, options) {
  var _data$offsets$arrow;

  // arrow depends on keepTogether in order to work
  if (!isModifierRequired(data.instance.modifiers, 'arrow', 'keepTogether')) {
    return data;
  }

  var arrowElement = options.element;

  // if arrowElement is a string, suppose it's a CSS selector
  if (typeof arrowElement === 'string') {
    arrowElement = data.instance.popper.querySelector(arrowElement);

    // if arrowElement is not found, don't run the modifier
    if (!arrowElement) {
      return data;
    }
  } else {
    // if the arrowElement isn't a query selector we must check that the
    // provided DOM node is child of its popper node
    if (!data.instance.popper.contains(arrowElement)) {
      console.warn('WARNING: `arrow.element` must be child of its popper element!');
      return data;
    }
  }

  var placement = data.placement.split('-')[0];
  var _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;

  var isVertical = ['left', 'right'].indexOf(placement) !== -1;

  var len = isVertical ? 'height' : 'width';
  var sideCapitalized = isVertical ? 'Top' : 'Left';
  var side = sideCapitalized.toLowerCase();
  var altSide = isVertical ? 'left' : 'top';
  var opSide = isVertical ? 'bottom' : 'right';
  var arrowElementSize = getOuterSizes(arrowElement)[len];

  //
  // extends keepTogether behavior making sure the popper and its
  // reference have enough pixels in conjunction
  //

  // top/left side
  if (reference[opSide] - arrowElementSize < popper[side]) {
    data.offsets.popper[side] -= popper[side] - (reference[opSide] - arrowElementSize);
  }
  // bottom/right side
  if (reference[side] + arrowElementSize > popper[opSide]) {
    data.offsets.popper[side] += reference[side] + arrowElementSize - popper[opSide];
  }
  data.offsets.popper = getClientRect(data.offsets.popper);

  // compute center of the popper
  var center = reference[side] + reference[len] / 2 - arrowElementSize / 2;

  // Compute the sideValue using the updated popper offsets
  // take popper margin in account because we don't have this info available
  var css = getStyleComputedProperty(data.instance.popper);
  var popperMarginSide = parseFloat(css['margin' + sideCapitalized]);
  var popperBorderSide = parseFloat(css['border' + sideCapitalized + 'Width']);
  var sideValue = center - data.offsets.popper[side] - popperMarginSide - popperBorderSide;

  // prevent arrowElement from being placed not contiguously to its popper
  sideValue = Math.max(Math.min(popper[len] - arrowElementSize, sideValue), 0);

  data.arrowElement = arrowElement;
  data.offsets.arrow = (_data$offsets$arrow = {}, defineProperty(_data$offsets$arrow, side, Math.round(sideValue)), defineProperty(_data$offsets$arrow, altSide, ''), _data$offsets$arrow);

  return data;
}

/**
 * Get the opposite placement variation of the given one
 * @method
 * @memberof Popper.Utils
 * @argument {String} placement variation
 * @returns {String} flipped placement variation
 */
function getOppositeVariation(variation) {
  if (variation === 'end') {
    return 'start';
  } else if (variation === 'start') {
    return 'end';
  }
  return variation;
}

/**
 * List of accepted placements to use as values of the `placement` option.<br />
 * Valid placements are:
 * - `auto`
 * - `top`
 * - `right`
 * - `bottom`
 * - `left`
 *
 * Each placement can have a variation from this list:
 * - `-start`
 * - `-end`
 *
 * Variations are interpreted easily if you think of them as the left to right
 * written languages. Horizontally (`top` and `bottom`), `start` is left and `end`
 * is right.<br />
 * Vertically (`left` and `right`), `start` is top and `end` is bottom.
 *
 * Some valid examples are:
 * - `top-end` (on top of reference, right aligned)
 * - `right-start` (on right of reference, top aligned)
 * - `bottom` (on bottom, centered)
 * - `auto-end` (on the side with more space available, alignment depends by placement)
 *
 * @static
 * @type {Array}
 * @enum {String}
 * @readonly
 * @method placements
 * @memberof Popper
 */
var placements = ['auto-start', 'auto', 'auto-end', 'top-start', 'top', 'top-end', 'right-start', 'right', 'right-end', 'bottom-end', 'bottom', 'bottom-start', 'left-end', 'left', 'left-start'];

// Get rid of `auto` `auto-start` and `auto-end`
var validPlacements = placements.slice(3);

/**
 * Given an initial placement, returns all the subsequent placements
 * clockwise (or counter-clockwise).
 *
 * @method
 * @memberof Popper.Utils
 * @argument {String} placement - A valid placement (it accepts variations)
 * @argument {Boolean} counter - Set to true to walk the placements counterclockwise
 * @returns {Array} placements including their variations
 */
function clockwise(placement) {
  var counter = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  var index = validPlacements.indexOf(placement);
  var arr = validPlacements.slice(index + 1).concat(validPlacements.slice(0, index));
  return counter ? arr.reverse() : arr;
}

var BEHAVIORS = {
  FLIP: 'flip',
  CLOCKWISE: 'clockwise',
  COUNTERCLOCKWISE: 'counterclockwise'
};

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function flip(data, options) {
  // if `inner` modifier is enabled, we can't use the `flip` modifier
  if (isModifierEnabled(data.instance.modifiers, 'inner')) {
    return data;
  }

  if (data.flipped && data.placement === data.originalPlacement) {
    // seems like flip is trying to loop, probably there's not enough space on any of the flippable sides
    return data;
  }

  var boundaries = getBoundaries(data.instance.popper, data.instance.reference, options.padding, options.boundariesElement, data.positionFixed);

  var placement = data.placement.split('-')[0];
  var placementOpposite = getOppositePlacement(placement);
  var variation = data.placement.split('-')[1] || '';

  var flipOrder = [];

  switch (options.behavior) {
    case BEHAVIORS.FLIP:
      flipOrder = [placement, placementOpposite];
      break;
    case BEHAVIORS.CLOCKWISE:
      flipOrder = clockwise(placement);
      break;
    case BEHAVIORS.COUNTERCLOCKWISE:
      flipOrder = clockwise(placement, true);
      break;
    default:
      flipOrder = options.behavior;
  }

  flipOrder.forEach(function (step, index) {
    if (placement !== step || flipOrder.length === index + 1) {
      return data;
    }

    placement = data.placement.split('-')[0];
    placementOpposite = getOppositePlacement(placement);

    var popperOffsets = data.offsets.popper;
    var refOffsets = data.offsets.reference;

    // using floor because the reference offsets may contain decimals we are not going to consider here
    var floor = Math.floor;
    var overlapsRef = placement === 'left' && floor(popperOffsets.right) > floor(refOffsets.left) || placement === 'right' && floor(popperOffsets.left) < floor(refOffsets.right) || placement === 'top' && floor(popperOffsets.bottom) > floor(refOffsets.top) || placement === 'bottom' && floor(popperOffsets.top) < floor(refOffsets.bottom);

    var overflowsLeft = floor(popperOffsets.left) < floor(boundaries.left);
    var overflowsRight = floor(popperOffsets.right) > floor(boundaries.right);
    var overflowsTop = floor(popperOffsets.top) < floor(boundaries.top);
    var overflowsBottom = floor(popperOffsets.bottom) > floor(boundaries.bottom);

    var overflowsBoundaries = placement === 'left' && overflowsLeft || placement === 'right' && overflowsRight || placement === 'top' && overflowsTop || placement === 'bottom' && overflowsBottom;

    // flip the variation if required
    var isVertical = ['top', 'bottom'].indexOf(placement) !== -1;

    // flips variation if reference element overflows boundaries
    var flippedVariationByRef = !!options.flipVariations && (isVertical && variation === 'start' && overflowsLeft || isVertical && variation === 'end' && overflowsRight || !isVertical && variation === 'start' && overflowsTop || !isVertical && variation === 'end' && overflowsBottom);

    // flips variation if popper content overflows boundaries
    var flippedVariationByContent = !!options.flipVariationsByContent && (isVertical && variation === 'start' && overflowsRight || isVertical && variation === 'end' && overflowsLeft || !isVertical && variation === 'start' && overflowsBottom || !isVertical && variation === 'end' && overflowsTop);

    var flippedVariation = flippedVariationByRef || flippedVariationByContent;

    if (overlapsRef || overflowsBoundaries || flippedVariation) {
      // this boolean to detect any flip loop
      data.flipped = true;

      if (overlapsRef || overflowsBoundaries) {
        placement = flipOrder[index + 1];
      }

      if (flippedVariation) {
        variation = getOppositeVariation(variation);
      }

      data.placement = placement + (variation ? '-' + variation : '');

      // this object contains `position`, we want to preserve it along with
      // any additional property we may add in the future
      data.offsets.popper = _extends({}, data.offsets.popper, getPopperOffsets(data.instance.popper, data.offsets.reference, data.placement));

      data = runModifiers(data.instance.modifiers, data, 'flip');
    }
  });
  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function keepTogether(data) {
  var _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;

  var placement = data.placement.split('-')[0];
  var floor = Math.floor;
  var isVertical = ['top', 'bottom'].indexOf(placement) !== -1;
  var side = isVertical ? 'right' : 'bottom';
  var opSide = isVertical ? 'left' : 'top';
  var measurement = isVertical ? 'width' : 'height';

  if (popper[side] < floor(reference[opSide])) {
    data.offsets.popper[opSide] = floor(reference[opSide]) - popper[measurement];
  }
  if (popper[opSide] > floor(reference[side])) {
    data.offsets.popper[opSide] = floor(reference[side]);
  }

  return data;
}

/**
 * Converts a string containing value + unit into a px value number
 * @function
 * @memberof {modifiers~offset}
 * @private
 * @argument {String} str - Value + unit string
 * @argument {String} measurement - `height` or `width`
 * @argument {Object} popperOffsets
 * @argument {Object} referenceOffsets
 * @returns {Number|String}
 * Value in pixels, or original string if no values were extracted
 */
function toValue(str, measurement, popperOffsets, referenceOffsets) {
  // separate value from unit
  var split = str.match(/((?:\-|\+)?\d*\.?\d*)(.*)/);
  var value = +split[1];
  var unit = split[2];

  // If it's not a number it's an operator, I guess
  if (!value) {
    return str;
  }

  if (unit.indexOf('%') === 0) {
    var element = void 0;
    switch (unit) {
      case '%p':
        element = popperOffsets;
        break;
      case '%':
      case '%r':
      default:
        element = referenceOffsets;
    }

    var rect = getClientRect(element);
    return rect[measurement] / 100 * value;
  } else if (unit === 'vh' || unit === 'vw') {
    // if is a vh or vw, we calculate the size based on the viewport
    var size = void 0;
    if (unit === 'vh') {
      size = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    } else {
      size = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    }
    return size / 100 * value;
  } else {
    // if is an explicit pixel unit, we get rid of the unit and keep the value
    // if is an implicit unit, it's px, and we return just the value
    return value;
  }
}

/**
 * Parse an `offset` string to extrapolate `x` and `y` numeric offsets.
 * @function
 * @memberof {modifiers~offset}
 * @private
 * @argument {String} offset
 * @argument {Object} popperOffsets
 * @argument {Object} referenceOffsets
 * @argument {String} basePlacement
 * @returns {Array} a two cells array with x and y offsets in numbers
 */
function parseOffset(offset, popperOffsets, referenceOffsets, basePlacement) {
  var offsets = [0, 0];

  // Use height if placement is left or right and index is 0 otherwise use width
  // in this way the first offset will use an axis and the second one
  // will use the other one
  var useHeight = ['right', 'left'].indexOf(basePlacement) !== -1;

  // Split the offset string to obtain a list of values and operands
  // The regex addresses values with the plus or minus sign in front (+10, -20, etc)
  var fragments = offset.split(/(\+|\-)/).map(function (frag) {
    return frag.trim();
  });

  // Detect if the offset string contains a pair of values or a single one
  // they could be separated by comma or space
  var divider = fragments.indexOf(find(fragments, function (frag) {
    return frag.search(/,|\s/) !== -1;
  }));

  if (fragments[divider] && fragments[divider].indexOf(',') === -1) {
    console.warn('Offsets separated by white space(s) are deprecated, use a comma (,) instead.');
  }

  // If divider is found, we divide the list of values and operands to divide
  // them by ofset X and Y.
  var splitRegex = /\s*,\s*|\s+/;
  var ops = divider !== -1 ? [fragments.slice(0, divider).concat([fragments[divider].split(splitRegex)[0]]), [fragments[divider].split(splitRegex)[1]].concat(fragments.slice(divider + 1))] : [fragments];

  // Convert the values with units to absolute pixels to allow our computations
  ops = ops.map(function (op, index) {
    // Most of the units rely on the orientation of the popper
    var measurement = (index === 1 ? !useHeight : useHeight) ? 'height' : 'width';
    var mergeWithPrevious = false;
    return op
    // This aggregates any `+` or `-` sign that aren't considered operators
    // e.g.: 10 + +5 => [10, +, +5]
    .reduce(function (a, b) {
      if (a[a.length - 1] === '' && ['+', '-'].indexOf(b) !== -1) {
        a[a.length - 1] = b;
        mergeWithPrevious = true;
        return a;
      } else if (mergeWithPrevious) {
        a[a.length - 1] += b;
        mergeWithPrevious = false;
        return a;
      } else {
        return a.concat(b);
      }
    }, [])
    // Here we convert the string values into number values (in px)
    .map(function (str) {
      return toValue(str, measurement, popperOffsets, referenceOffsets);
    });
  });

  // Loop trough the offsets arrays and execute the operations
  ops.forEach(function (op, index) {
    op.forEach(function (frag, index2) {
      if (isNumeric(frag)) {
        offsets[index] += frag * (op[index2 - 1] === '-' ? -1 : 1);
      }
    });
  });
  return offsets;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @argument {Number|String} options.offset=0
 * The offset value as described in the modifier description
 * @returns {Object} The data object, properly modified
 */
function offset(data, _ref) {
  var offset = _ref.offset;
  var placement = data.placement,
      _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;

  var basePlacement = placement.split('-')[0];

  var offsets = void 0;
  if (isNumeric(+offset)) {
    offsets = [+offset, 0];
  } else {
    offsets = parseOffset(offset, popper, reference, basePlacement);
  }

  if (basePlacement === 'left') {
    popper.top += offsets[0];
    popper.left -= offsets[1];
  } else if (basePlacement === 'right') {
    popper.top += offsets[0];
    popper.left += offsets[1];
  } else if (basePlacement === 'top') {
    popper.left += offsets[0];
    popper.top -= offsets[1];
  } else if (basePlacement === 'bottom') {
    popper.left += offsets[0];
    popper.top += offsets[1];
  }

  data.popper = popper;
  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function preventOverflow(data, options) {
  var boundariesElement = options.boundariesElement || getOffsetParent(data.instance.popper);

  // If offsetParent is the reference element, we really want to
  // go one step up and use the next offsetParent as reference to
  // avoid to make this modifier completely useless and look like broken
  if (data.instance.reference === boundariesElement) {
    boundariesElement = getOffsetParent(boundariesElement);
  }

  // NOTE: DOM access here
  // resets the popper's position so that the document size can be calculated excluding
  // the size of the popper element itself
  var transformProp = getSupportedPropertyName('transform');
  var popperStyles = data.instance.popper.style; // assignment to help minification
  var top = popperStyles.top,
      left = popperStyles.left,
      transform = popperStyles[transformProp];

  popperStyles.top = '';
  popperStyles.left = '';
  popperStyles[transformProp] = '';

  var boundaries = getBoundaries(data.instance.popper, data.instance.reference, options.padding, boundariesElement, data.positionFixed);

  // NOTE: DOM access here
  // restores the original style properties after the offsets have been computed
  popperStyles.top = top;
  popperStyles.left = left;
  popperStyles[transformProp] = transform;

  options.boundaries = boundaries;

  var order = options.priority;
  var popper = data.offsets.popper;

  var check = {
    primary: function primary(placement) {
      var value = popper[placement];
      if (popper[placement] < boundaries[placement] && !options.escapeWithReference) {
        value = Math.max(popper[placement], boundaries[placement]);
      }
      return defineProperty({}, placement, value);
    },
    secondary: function secondary(placement) {
      var mainSide = placement === 'right' ? 'left' : 'top';
      var value = popper[mainSide];
      if (popper[placement] > boundaries[placement] && !options.escapeWithReference) {
        value = Math.min(popper[mainSide], boundaries[placement] - (placement === 'right' ? popper.width : popper.height));
      }
      return defineProperty({}, mainSide, value);
    }
  };

  order.forEach(function (placement) {
    var side = ['left', 'top'].indexOf(placement) !== -1 ? 'primary' : 'secondary';
    popper = _extends({}, popper, check[side](placement));
  });

  data.offsets.popper = popper;

  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function shift(data) {
  var placement = data.placement;
  var basePlacement = placement.split('-')[0];
  var shiftvariation = placement.split('-')[1];

  // if shift shiftvariation is specified, run the modifier
  if (shiftvariation) {
    var _data$offsets = data.offsets,
        reference = _data$offsets.reference,
        popper = _data$offsets.popper;

    var isVertical = ['bottom', 'top'].indexOf(basePlacement) !== -1;
    var side = isVertical ? 'left' : 'top';
    var measurement = isVertical ? 'width' : 'height';

    var shiftOffsets = {
      start: defineProperty({}, side, reference[side]),
      end: defineProperty({}, side, reference[side] + reference[measurement] - popper[measurement])
    };

    data.offsets.popper = _extends({}, popper, shiftOffsets[shiftvariation]);
  }

  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function hide(data) {
  if (!isModifierRequired(data.instance.modifiers, 'hide', 'preventOverflow')) {
    return data;
  }

  var refRect = data.offsets.reference;
  var bound = find(data.instance.modifiers, function (modifier) {
    return modifier.name === 'preventOverflow';
  }).boundaries;

  if (refRect.bottom < bound.top || refRect.left > bound.right || refRect.top > bound.bottom || refRect.right < bound.left) {
    // Avoid unnecessary DOM access if visibility hasn't changed
    if (data.hide === true) {
      return data;
    }

    data.hide = true;
    data.attributes['x-out-of-boundaries'] = '';
  } else {
    // Avoid unnecessary DOM access if visibility hasn't changed
    if (data.hide === false) {
      return data;
    }

    data.hide = false;
    data.attributes['x-out-of-boundaries'] = false;
  }

  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function inner(data) {
  var placement = data.placement;
  var basePlacement = placement.split('-')[0];
  var _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;

  var isHoriz = ['left', 'right'].indexOf(basePlacement) !== -1;

  var subtractLength = ['top', 'left'].indexOf(basePlacement) === -1;

  popper[isHoriz ? 'left' : 'top'] = reference[basePlacement] - (subtractLength ? popper[isHoriz ? 'width' : 'height'] : 0);

  data.placement = getOppositePlacement(placement);
  data.offsets.popper = getClientRect(popper);

  return data;
}

/**
 * Modifier function, each modifier can have a function of this type assigned
 * to its `fn` property.<br />
 * These functions will be called on each update, this means that you must
 * make sure they are performant enough to avoid performance bottlenecks.
 *
 * @function ModifierFn
 * @argument {dataObject} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {dataObject} The data object, properly modified
 */

/**
 * Modifiers are plugins used to alter the behavior of your poppers.<br />
 * Popper.js uses a set of 9 modifiers to provide all the basic functionalities
 * needed by the library.
 *
 * Usually you don't want to override the `order`, `fn` and `onLoad` props.
 * All the other properties are configurations that could be tweaked.
 * @namespace modifiers
 */
var modifiers = {
  /**
   * Modifier used to shift the popper on the start or end of its reference
   * element.<br />
   * It will read the variation of the `placement` property.<br />
   * It can be one either `-end` or `-start`.
   * @memberof modifiers
   * @inner
   */
  shift: {
    /** @prop {number} order=100 - Index used to define the order of execution */
    order: 100,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: shift
  },

  /**
   * The `offset` modifier can shift your popper on both its axis.
   *
   * It accepts the following units:
   * - `px` or unit-less, interpreted as pixels
   * - `%` or `%r`, percentage relative to the length of the reference element
   * - `%p`, percentage relative to the length of the popper element
   * - `vw`, CSS viewport width unit
   * - `vh`, CSS viewport height unit
   *
   * For length is intended the main axis relative to the placement of the popper.<br />
   * This means that if the placement is `top` or `bottom`, the length will be the
   * `width`. In case of `left` or `right`, it will be the `height`.
   *
   * You can provide a single value (as `Number` or `String`), or a pair of values
   * as `String` divided by a comma or one (or more) white spaces.<br />
   * The latter is a deprecated method because it leads to confusion and will be
   * removed in v2.<br />
   * Additionally, it accepts additions and subtractions between different units.
   * Note that multiplications and divisions aren't supported.
   *
   * Valid examples are:
   * ```
   * 10
   * '10%'
   * '10, 10'
   * '10%, 10'
   * '10 + 10%'
   * '10 - 5vh + 3%'
   * '-10px + 5vh, 5px - 6%'
   * ```
   * > **NB**: If you desire to apply offsets to your poppers in a way that may make them overlap
   * > with their reference element, unfortunately, you will have to disable the `flip` modifier.
   * > You can read more on this at this [issue](https://github.com/FezVrasta/popper.js/issues/373).
   *
   * @memberof modifiers
   * @inner
   */
  offset: {
    /** @prop {number} order=200 - Index used to define the order of execution */
    order: 200,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: offset,
    /** @prop {Number|String} offset=0
     * The offset value as described in the modifier description
     */
    offset: 0
  },

  /**
   * Modifier used to prevent the popper from being positioned outside the boundary.
   *
   * A scenario exists where the reference itself is not within the boundaries.<br />
   * We can say it has "escaped the boundaries" — or just "escaped".<br />
   * In this case we need to decide whether the popper should either:
   *
   * - detach from the reference and remain "trapped" in the boundaries, or
   * - if it should ignore the boundary and "escape with its reference"
   *
   * When `escapeWithReference` is set to`true` and reference is completely
   * outside its boundaries, the popper will overflow (or completely leave)
   * the boundaries in order to remain attached to the edge of the reference.
   *
   * @memberof modifiers
   * @inner
   */
  preventOverflow: {
    /** @prop {number} order=300 - Index used to define the order of execution */
    order: 300,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: preventOverflow,
    /**
     * @prop {Array} [priority=['left','right','top','bottom']]
     * Popper will try to prevent overflow following these priorities by default,
     * then, it could overflow on the left and on top of the `boundariesElement`
     */
    priority: ['left', 'right', 'top', 'bottom'],
    /**
     * @prop {number} padding=5
     * Amount of pixel used to define a minimum distance between the boundaries
     * and the popper. This makes sure the popper always has a little padding
     * between the edges of its container
     */
    padding: 5,
    /**
     * @prop {String|HTMLElement} boundariesElement='scrollParent'
     * Boundaries used by the modifier. Can be `scrollParent`, `window`,
     * `viewport` or any DOM element.
     */
    boundariesElement: 'scrollParent'
  },

  /**
   * Modifier used to make sure the reference and its popper stay near each other
   * without leaving any gap between the two. Especially useful when the arrow is
   * enabled and you want to ensure that it points to its reference element.
   * It cares only about the first axis. You can still have poppers with margin
   * between the popper and its reference element.
   * @memberof modifiers
   * @inner
   */
  keepTogether: {
    /** @prop {number} order=400 - Index used to define the order of execution */
    order: 400,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: keepTogether
  },

  /**
   * This modifier is used to move the `arrowElement` of the popper to make
   * sure it is positioned between the reference element and its popper element.
   * It will read the outer size of the `arrowElement` node to detect how many
   * pixels of conjunction are needed.
   *
   * It has no effect if no `arrowElement` is provided.
   * @memberof modifiers
   * @inner
   */
  arrow: {
    /** @prop {number} order=500 - Index used to define the order of execution */
    order: 500,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: arrow,
    /** @prop {String|HTMLElement} element='[x-arrow]' - Selector or node used as arrow */
    element: '[x-arrow]'
  },

  /**
   * Modifier used to flip the popper's placement when it starts to overlap its
   * reference element.
   *
   * Requires the `preventOverflow` modifier before it in order to work.
   *
   * **NOTE:** this modifier will interrupt the current update cycle and will
   * restart it if it detects the need to flip the placement.
   * @memberof modifiers
   * @inner
   */
  flip: {
    /** @prop {number} order=600 - Index used to define the order of execution */
    order: 600,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: flip,
    /**
     * @prop {String|Array} behavior='flip'
     * The behavior used to change the popper's placement. It can be one of
     * `flip`, `clockwise`, `counterclockwise` or an array with a list of valid
     * placements (with optional variations)
     */
    behavior: 'flip',
    /**
     * @prop {number} padding=5
     * The popper will flip if it hits the edges of the `boundariesElement`
     */
    padding: 5,
    /**
     * @prop {String|HTMLElement} boundariesElement='viewport'
     * The element which will define the boundaries of the popper position.
     * The popper will never be placed outside of the defined boundaries
     * (except if `keepTogether` is enabled)
     */
    boundariesElement: 'viewport',
    /**
     * @prop {Boolean} flipVariations=false
     * The popper will switch placement variation between `-start` and `-end` when
     * the reference element overlaps its boundaries.
     *
     * The original placement should have a set variation.
     */
    flipVariations: false,
    /**
     * @prop {Boolean} flipVariationsByContent=false
     * The popper will switch placement variation between `-start` and `-end` when
     * the popper element overlaps its reference boundaries.
     *
     * The original placement should have a set variation.
     */
    flipVariationsByContent: false
  },

  /**
   * Modifier used to make the popper flow toward the inner of the reference element.
   * By default, when this modifier is disabled, the popper will be placed outside
   * the reference element.
   * @memberof modifiers
   * @inner
   */
  inner: {
    /** @prop {number} order=700 - Index used to define the order of execution */
    order: 700,
    /** @prop {Boolean} enabled=false - Whether the modifier is enabled or not */
    enabled: false,
    /** @prop {ModifierFn} */
    fn: inner
  },

  /**
   * Modifier used to hide the popper when its reference element is outside of the
   * popper boundaries. It will set a `x-out-of-boundaries` attribute which can
   * be used to hide with a CSS selector the popper when its reference is
   * out of boundaries.
   *
   * Requires the `preventOverflow` modifier before it in order to work.
   * @memberof modifiers
   * @inner
   */
  hide: {
    /** @prop {number} order=800 - Index used to define the order of execution */
    order: 800,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: hide
  },

  /**
   * Computes the style that will be applied to the popper element to gets
   * properly positioned.
   *
   * Note that this modifier will not touch the DOM, it just prepares the styles
   * so that `applyStyle` modifier can apply it. This separation is useful
   * in case you need to replace `applyStyle` with a custom implementation.
   *
   * This modifier has `850` as `order` value to maintain backward compatibility
   * with previous versions of Popper.js. Expect the modifiers ordering method
   * to change in future major versions of the library.
   *
   * @memberof modifiers
   * @inner
   */
  computeStyle: {
    /** @prop {number} order=850 - Index used to define the order of execution */
    order: 850,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: computeStyle,
    /**
     * @prop {Boolean} gpuAcceleration=true
     * If true, it uses the CSS 3D transformation to position the popper.
     * Otherwise, it will use the `top` and `left` properties
     */
    gpuAcceleration: true,
    /**
     * @prop {string} [x='bottom']
     * Where to anchor the X axis (`bottom` or `top`). AKA X offset origin.
     * Change this if your popper should grow in a direction different from `bottom`
     */
    x: 'bottom',
    /**
     * @prop {string} [x='left']
     * Where to anchor the Y axis (`left` or `right`). AKA Y offset origin.
     * Change this if your popper should grow in a direction different from `right`
     */
    y: 'right'
  },

  /**
   * Applies the computed styles to the popper element.
   *
   * All the DOM manipulations are limited to this modifier. This is useful in case
   * you want to integrate Popper.js inside a framework or view library and you
   * want to delegate all the DOM manipulations to it.
   *
   * Note that if you disable this modifier, you must make sure the popper element
   * has its position set to `absolute` before Popper.js can do its work!
   *
   * Just disable this modifier and define your own to achieve the desired effect.
   *
   * @memberof modifiers
   * @inner
   */
  applyStyle: {
    /** @prop {number} order=900 - Index used to define the order of execution */
    order: 900,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: applyStyle,
    /** @prop {Function} */
    onLoad: applyStyleOnLoad,
    /**
     * @deprecated since version 1.10.0, the property moved to `computeStyle` modifier
     * @prop {Boolean} gpuAcceleration=true
     * If true, it uses the CSS 3D transformation to position the popper.
     * Otherwise, it will use the `top` and `left` properties
     */
    gpuAcceleration: undefined
  }
};

/**
 * The `dataObject` is an object containing all the information used by Popper.js.
 * This object is passed to modifiers and to the `onCreate` and `onUpdate` callbacks.
 * @name dataObject
 * @property {Object} data.instance The Popper.js instance
 * @property {String} data.placement Placement applied to popper
 * @property {String} data.originalPlacement Placement originally defined on init
 * @property {Boolean} data.flipped True if popper has been flipped by flip modifier
 * @property {Boolean} data.hide True if the reference element is out of boundaries, useful to know when to hide the popper
 * @property {HTMLElement} data.arrowElement Node used as arrow by arrow modifier
 * @property {Object} data.styles Any CSS property defined here will be applied to the popper. It expects the JavaScript nomenclature (eg. `marginBottom`)
 * @property {Object} data.arrowStyles Any CSS property defined here will be applied to the popper arrow. It expects the JavaScript nomenclature (eg. `marginBottom`)
 * @property {Object} data.boundaries Offsets of the popper boundaries
 * @property {Object} data.offsets The measurements of popper, reference and arrow elements
 * @property {Object} data.offsets.popper `top`, `left`, `width`, `height` values
 * @property {Object} data.offsets.reference `top`, `left`, `width`, `height` values
 * @property {Object} data.offsets.arrow] `top` and `left` offsets, only one of them will be different from 0
 */

/**
 * Default options provided to Popper.js constructor.<br />
 * These can be overridden using the `options` argument of Popper.js.<br />
 * To override an option, simply pass an object with the same
 * structure of the `options` object, as the 3rd argument. For example:
 * ```
 * new Popper(ref, pop, {
 *   modifiers: {
 *     preventOverflow: { enabled: false }
 *   }
 * })
 * ```
 * @type {Object}
 * @static
 * @memberof Popper
 */
var Defaults = {
  /**
   * Popper's placement.
   * @prop {Popper.placements} placement='bottom'
   */
  placement: 'bottom',

  /**
   * Set this to true if you want popper to position it self in 'fixed' mode
   * @prop {Boolean} positionFixed=false
   */
  positionFixed: false,

  /**
   * Whether events (resize, scroll) are initially enabled.
   * @prop {Boolean} eventsEnabled=true
   */
  eventsEnabled: true,

  /**
   * Set to true if you want to automatically remove the popper when
   * you call the `destroy` method.
   * @prop {Boolean} removeOnDestroy=false
   */
  removeOnDestroy: false,

  /**
   * Callback called when the popper is created.<br />
   * By default, it is set to no-op.<br />
   * Access Popper.js instance with `data.instance`.
   * @prop {onCreate}
   */
  onCreate: function onCreate() {},

  /**
   * Callback called when the popper is updated. This callback is not called
   * on the initialization/creation of the popper, but only on subsequent
   * updates.<br />
   * By default, it is set to no-op.<br />
   * Access Popper.js instance with `data.instance`.
   * @prop {onUpdate}
   */
  onUpdate: function onUpdate() {},

  /**
   * List of modifiers used to modify the offsets before they are applied to the popper.
   * They provide most of the functionalities of Popper.js.
   * @prop {modifiers}
   */
  modifiers: modifiers
};

/**
 * @callback onCreate
 * @param {dataObject} data
 */

/**
 * @callback onUpdate
 * @param {dataObject} data
 */

// Utils
// Methods
var Popper = function () {
  /**
   * Creates a new Popper.js instance.
   * @class Popper
   * @param {Element|referenceObject} reference - The reference element used to position the popper
   * @param {Element} popper - The HTML / XML element used as the popper
   * @param {Object} options - Your custom options to override the ones defined in [Defaults](#defaults)
   * @return {Object} instance - The generated Popper.js instance
   */
  function Popper(reference, popper) {
    var _this = this;

    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    classCallCheck(this, Popper);

    this.scheduleUpdate = function () {
      return requestAnimationFrame(_this.update);
    };

    // make update() debounced, so that it only runs at most once-per-tick
    this.update = debounce(this.update.bind(this));

    // with {} we create a new object with the options inside it
    this.options = _extends({}, Popper.Defaults, options);

    // init state
    this.state = {
      isDestroyed: false,
      isCreated: false,
      scrollParents: []
    };

    // get reference and popper elements (allow jQuery wrappers)
    this.reference = reference && reference.jquery ? reference[0] : reference;
    this.popper = popper && popper.jquery ? popper[0] : popper;

    // Deep merge modifiers options
    this.options.modifiers = {};
    Object.keys(_extends({}, Popper.Defaults.modifiers, options.modifiers)).forEach(function (name) {
      _this.options.modifiers[name] = _extends({}, Popper.Defaults.modifiers[name] || {}, options.modifiers ? options.modifiers[name] : {});
    });

    // Refactoring modifiers' list (Object => Array)
    this.modifiers = Object.keys(this.options.modifiers).map(function (name) {
      return _extends({
        name: name
      }, _this.options.modifiers[name]);
    })
    // sort the modifiers by order
    .sort(function (a, b) {
      return a.order - b.order;
    });

    // modifiers have the ability to execute arbitrary code when Popper.js get inited
    // such code is executed in the same order of its modifier
    // they could add new properties to their options configuration
    // BE AWARE: don't add options to `options.modifiers.name` but to `modifierOptions`!
    this.modifiers.forEach(function (modifierOptions) {
      if (modifierOptions.enabled && isFunction(modifierOptions.onLoad)) {
        modifierOptions.onLoad(_this.reference, _this.popper, _this.options, modifierOptions, _this.state);
      }
    });

    // fire the first update to position the popper in the right place
    this.update();

    var eventsEnabled = this.options.eventsEnabled;
    if (eventsEnabled) {
      // setup event listeners, they will take care of update the position in specific situations
      this.enableEventListeners();
    }

    this.state.eventsEnabled = eventsEnabled;
  }

  // We can't use class properties because they don't get listed in the
  // class prototype and break stuff like Sinon stubs


  createClass(Popper, [{
    key: 'update',
    value: function update$$1() {
      return update.call(this);
    }
  }, {
    key: 'destroy',
    value: function destroy$$1() {
      return destroy.call(this);
    }
  }, {
    key: 'enableEventListeners',
    value: function enableEventListeners$$1() {
      return enableEventListeners.call(this);
    }
  }, {
    key: 'disableEventListeners',
    value: function disableEventListeners$$1() {
      return disableEventListeners.call(this);
    }

    /**
     * Schedules an update. It will run on the next UI update available.
     * @method scheduleUpdate
     * @memberof Popper
     */


    /**
     * Collection of utilities useful when writing custom modifiers.
     * Starting from version 1.7, this method is available only if you
     * include `popper-utils.js` before `popper.js`.
     *
     * **DEPRECATION**: This way to access PopperUtils is deprecated
     * and will be removed in v2! Use the PopperUtils module directly instead.
     * Due to the high instability of the methods contained in Utils, we can't
     * guarantee them to follow semver. Use them at your own risk!
     * @static
     * @private
     * @type {Object}
     * @deprecated since version 1.8
     * @member Utils
     * @memberof Popper
     */

  }]);
  return Popper;
}();

/**
 * The `referenceObject` is an object that provides an interface compatible with Popper.js
 * and lets you use it as replacement of a real DOM node.<br />
 * You can use this method to position a popper relatively to a set of coordinates
 * in case you don't have a DOM node to use as reference.
 *
 * ```
 * new Popper(referenceObject, popperNode);
 * ```
 *
 * NB: This feature isn't supported in Internet Explorer 10.
 * @name referenceObject
 * @property {Function} data.getBoundingClientRect
 * A function that returns a set of coordinates compatible with the native `getBoundingClientRect` method.
 * @property {number} data.clientWidth
 * An ES6 getter that will return the width of the virtual reference element.
 * @property {number} data.clientHeight
 * An ES6 getter that will return the height of the virtual reference element.
 */


Popper.Utils = (typeof window !== 'undefined' ? window : global).PopperUtils;
Popper.placements = placements;
Popper.Defaults = Defaults;

/* harmony default export */ __webpack_exports__["default"] = (Popper);
//# sourceMappingURL=popper.js.map

/* WEBPACK VAR INJECTION */}.call(__webpack_exports__, __webpack_require__(18)));

/***/ }),

/***/ 398:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"popperRef",staticClass:"bui-popper",class:_vm.className,style:(_vm.styles)},[_vm._t("default")],2)};
var staticRenderFns = [];


/***/ }),

/***/ 4:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isOneOf = isOneOf;
function isOneOf(value, validList) {
  for (var i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true;
    }
  }
  return false;
}

/***/ }),

/***/ 53:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* WEBPACK VAR INJECTION */(function(global) {/**
 * A collection of shims that provide minimal functionality of the ES6 collections.
 *
 * These implementations are not meant to be used outside of the ResizeObserver
 * modules as they cover only a limited range of use cases.
 */
/* eslint-disable require-jsdoc, valid-jsdoc */
var MapShim = (function () {
    if (typeof Map !== 'undefined') {
        return Map;
    }
    /**
     * Returns index in provided array that matches the specified key.
     *
     * @param {Array<Array>} arr
     * @param {*} key
     * @returns {number}
     */
    function getIndex(arr, key) {
        var result = -1;
        arr.some(function (entry, index) {
            if (entry[0] === key) {
                result = index;
                return true;
            }
            return false;
        });
        return result;
    }
    return /** @class */ (function () {
        function class_1() {
            this.__entries__ = [];
        }
        Object.defineProperty(class_1.prototype, "size", {
            /**
             * @returns {boolean}
             */
            get: function () {
                return this.__entries__.length;
            },
            enumerable: true,
            configurable: true
        });
        /**
         * @param {*} key
         * @returns {*}
         */
        class_1.prototype.get = function (key) {
            var index = getIndex(this.__entries__, key);
            var entry = this.__entries__[index];
            return entry && entry[1];
        };
        /**
         * @param {*} key
         * @param {*} value
         * @returns {void}
         */
        class_1.prototype.set = function (key, value) {
            var index = getIndex(this.__entries__, key);
            if (~index) {
                this.__entries__[index][1] = value;
            }
            else {
                this.__entries__.push([key, value]);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.delete = function (key) {
            var entries = this.__entries__;
            var index = getIndex(entries, key);
            if (~index) {
                entries.splice(index, 1);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.has = function (key) {
            return !!~getIndex(this.__entries__, key);
        };
        /**
         * @returns {void}
         */
        class_1.prototype.clear = function () {
            this.__entries__.splice(0);
        };
        /**
         * @param {Function} callback
         * @param {*} [ctx=null]
         * @returns {void}
         */
        class_1.prototype.forEach = function (callback, ctx) {
            if (ctx === void 0) { ctx = null; }
            for (var _i = 0, _a = this.__entries__; _i < _a.length; _i++) {
                var entry = _a[_i];
                callback.call(ctx, entry[1], entry[0]);
            }
        };
        return class_1;
    }());
})();

/**
 * Detects whether window and document objects are available in current environment.
 */
var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined' && window.document === document;

// Returns global object of a current environment.
var global$1 = (function () {
    if (typeof global !== 'undefined' && global.Math === Math) {
        return global;
    }
    if (typeof self !== 'undefined' && self.Math === Math) {
        return self;
    }
    if (typeof window !== 'undefined' && window.Math === Math) {
        return window;
    }
    // eslint-disable-next-line no-new-func
    return Function('return this')();
})();

/**
 * A shim for the requestAnimationFrame which falls back to the setTimeout if
 * first one is not supported.
 *
 * @returns {number} Requests' identifier.
 */
var requestAnimationFrame$1 = (function () {
    if (typeof requestAnimationFrame === 'function') {
        // It's required to use a bounded function because IE sometimes throws
        // an "Invalid calling object" error if rAF is invoked without the global
        // object on the left hand side.
        return requestAnimationFrame.bind(global$1);
    }
    return function (callback) { return setTimeout(function () { return callback(Date.now()); }, 1000 / 60); };
})();

// Defines minimum timeout before adding a trailing call.
var trailingTimeout = 2;
/**
 * Creates a wrapper function which ensures that provided callback will be
 * invoked only once during the specified delay period.
 *
 * @param {Function} callback - Function to be invoked after the delay period.
 * @param {number} delay - Delay after which to invoke callback.
 * @returns {Function}
 */
function throttle (callback, delay) {
    var leadingCall = false, trailingCall = false, lastCallTime = 0;
    /**
     * Invokes the original callback function and schedules new invocation if
     * the "proxy" was called during current request.
     *
     * @returns {void}
     */
    function resolvePending() {
        if (leadingCall) {
            leadingCall = false;
            callback();
        }
        if (trailingCall) {
            proxy();
        }
    }
    /**
     * Callback invoked after the specified delay. It will further postpone
     * invocation of the original function delegating it to the
     * requestAnimationFrame.
     *
     * @returns {void}
     */
    function timeoutCallback() {
        requestAnimationFrame$1(resolvePending);
    }
    /**
     * Schedules invocation of the original function.
     *
     * @returns {void}
     */
    function proxy() {
        var timeStamp = Date.now();
        if (leadingCall) {
            // Reject immediately following calls.
            if (timeStamp - lastCallTime < trailingTimeout) {
                return;
            }
            // Schedule new call to be in invoked when the pending one is resolved.
            // This is important for "transitions" which never actually start
            // immediately so there is a chance that we might miss one if change
            // happens amids the pending invocation.
            trailingCall = true;
        }
        else {
            leadingCall = true;
            trailingCall = false;
            setTimeout(timeoutCallback, delay);
        }
        lastCallTime = timeStamp;
    }
    return proxy;
}

// Minimum delay before invoking the update of observers.
var REFRESH_DELAY = 20;
// A list of substrings of CSS properties used to find transition events that
// might affect dimensions of observed elements.
var transitionKeys = ['top', 'right', 'bottom', 'left', 'width', 'height', 'size', 'weight'];
// Check if MutationObserver is available.
var mutationObserverSupported = typeof MutationObserver !== 'undefined';
/**
 * Singleton controller class which handles updates of ResizeObserver instances.
 */
var ResizeObserverController = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserverController.
     *
     * @private
     */
    function ResizeObserverController() {
        /**
         * Indicates whether DOM listeners have been added.
         *
         * @private {boolean}
         */
        this.connected_ = false;
        /**
         * Tells that controller has subscribed for Mutation Events.
         *
         * @private {boolean}
         */
        this.mutationEventsAdded_ = false;
        /**
         * Keeps reference to the instance of MutationObserver.
         *
         * @private {MutationObserver}
         */
        this.mutationsObserver_ = null;
        /**
         * A list of connected observers.
         *
         * @private {Array<ResizeObserverSPI>}
         */
        this.observers_ = [];
        this.onTransitionEnd_ = this.onTransitionEnd_.bind(this);
        this.refresh = throttle(this.refresh.bind(this), REFRESH_DELAY);
    }
    /**
     * Adds observer to observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be added.
     * @returns {void}
     */
    ResizeObserverController.prototype.addObserver = function (observer) {
        if (!~this.observers_.indexOf(observer)) {
            this.observers_.push(observer);
        }
        // Add listeners if they haven't been added yet.
        if (!this.connected_) {
            this.connect_();
        }
    };
    /**
     * Removes observer from observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be removed.
     * @returns {void}
     */
    ResizeObserverController.prototype.removeObserver = function (observer) {
        var observers = this.observers_;
        var index = observers.indexOf(observer);
        // Remove observer if it's present in registry.
        if (~index) {
            observers.splice(index, 1);
        }
        // Remove listeners if controller has no connected observers.
        if (!observers.length && this.connected_) {
            this.disconnect_();
        }
    };
    /**
     * Invokes the update of observers. It will continue running updates insofar
     * it detects changes.
     *
     * @returns {void}
     */
    ResizeObserverController.prototype.refresh = function () {
        var changesDetected = this.updateObservers_();
        // Continue running updates if changes have been detected as there might
        // be future ones caused by CSS transitions.
        if (changesDetected) {
            this.refresh();
        }
    };
    /**
     * Updates every observer from observers list and notifies them of queued
     * entries.
     *
     * @private
     * @returns {boolean} Returns "true" if any observer has detected changes in
     *      dimensions of it's elements.
     */
    ResizeObserverController.prototype.updateObservers_ = function () {
        // Collect observers that have active observations.
        var activeObservers = this.observers_.filter(function (observer) {
            return observer.gatherActive(), observer.hasActive();
        });
        // Deliver notifications in a separate cycle in order to avoid any
        // collisions between observers, e.g. when multiple instances of
        // ResizeObserver are tracking the same element and the callback of one
        // of them changes content dimensions of the observed target. Sometimes
        // this may result in notifications being blocked for the rest of observers.
        activeObservers.forEach(function (observer) { return observer.broadcastActive(); });
        return activeObservers.length > 0;
    };
    /**
     * Initializes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.connect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already added.
        if (!isBrowser || this.connected_) {
            return;
        }
        // Subscription to the "Transitionend" event is used as a workaround for
        // delayed transitions. This way it's possible to capture at least the
        // final state of an element.
        document.addEventListener('transitionend', this.onTransitionEnd_);
        window.addEventListener('resize', this.refresh);
        if (mutationObserverSupported) {
            this.mutationsObserver_ = new MutationObserver(this.refresh);
            this.mutationsObserver_.observe(document, {
                attributes: true,
                childList: true,
                characterData: true,
                subtree: true
            });
        }
        else {
            document.addEventListener('DOMSubtreeModified', this.refresh);
            this.mutationEventsAdded_ = true;
        }
        this.connected_ = true;
    };
    /**
     * Removes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.disconnect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already removed.
        if (!isBrowser || !this.connected_) {
            return;
        }
        document.removeEventListener('transitionend', this.onTransitionEnd_);
        window.removeEventListener('resize', this.refresh);
        if (this.mutationsObserver_) {
            this.mutationsObserver_.disconnect();
        }
        if (this.mutationEventsAdded_) {
            document.removeEventListener('DOMSubtreeModified', this.refresh);
        }
        this.mutationsObserver_ = null;
        this.mutationEventsAdded_ = false;
        this.connected_ = false;
    };
    /**
     * "Transitionend" event handler.
     *
     * @private
     * @param {TransitionEvent} event
     * @returns {void}
     */
    ResizeObserverController.prototype.onTransitionEnd_ = function (_a) {
        var _b = _a.propertyName, propertyName = _b === void 0 ? '' : _b;
        // Detect whether transition may affect dimensions of an element.
        var isReflowProperty = transitionKeys.some(function (key) {
            return !!~propertyName.indexOf(key);
        });
        if (isReflowProperty) {
            this.refresh();
        }
    };
    /**
     * Returns instance of the ResizeObserverController.
     *
     * @returns {ResizeObserverController}
     */
    ResizeObserverController.getInstance = function () {
        if (!this.instance_) {
            this.instance_ = new ResizeObserverController();
        }
        return this.instance_;
    };
    /**
     * Holds reference to the controller's instance.
     *
     * @private {ResizeObserverController}
     */
    ResizeObserverController.instance_ = null;
    return ResizeObserverController;
}());

/**
 * Defines non-writable/enumerable properties of the provided target object.
 *
 * @param {Object} target - Object for which to define properties.
 * @param {Object} props - Properties to be defined.
 * @returns {Object} Target object.
 */
var defineConfigurable = (function (target, props) {
    for (var _i = 0, _a = Object.keys(props); _i < _a.length; _i++) {
        var key = _a[_i];
        Object.defineProperty(target, key, {
            value: props[key],
            enumerable: false,
            writable: false,
            configurable: true
        });
    }
    return target;
});

/**
 * Returns the global object associated with provided element.
 *
 * @param {Object} target
 * @returns {Object}
 */
var getWindowOf = (function (target) {
    // Assume that the element is an instance of Node, which means that it
    // has the "ownerDocument" property from which we can retrieve a
    // corresponding global object.
    var ownerGlobal = target && target.ownerDocument && target.ownerDocument.defaultView;
    // Return the local global object if it's not possible extract one from
    // provided element.
    return ownerGlobal || global$1;
});

// Placeholder of an empty content rectangle.
var emptyRect = createRectInit(0, 0, 0, 0);
/**
 * Converts provided string to a number.
 *
 * @param {number|string} value
 * @returns {number}
 */
function toFloat(value) {
    return parseFloat(value) || 0;
}
/**
 * Extracts borders size from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @param {...string} positions - Borders positions (top, right, ...)
 * @returns {number}
 */
function getBordersSize(styles) {
    var positions = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        positions[_i - 1] = arguments[_i];
    }
    return positions.reduce(function (size, position) {
        var value = styles['border-' + position + '-width'];
        return size + toFloat(value);
    }, 0);
}
/**
 * Extracts paddings sizes from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @returns {Object} Paddings box.
 */
function getPaddings(styles) {
    var positions = ['top', 'right', 'bottom', 'left'];
    var paddings = {};
    for (var _i = 0, positions_1 = positions; _i < positions_1.length; _i++) {
        var position = positions_1[_i];
        var value = styles['padding-' + position];
        paddings[position] = toFloat(value);
    }
    return paddings;
}
/**
 * Calculates content rectangle of provided SVG element.
 *
 * @param {SVGGraphicsElement} target - Element content rectangle of which needs
 *      to be calculated.
 * @returns {DOMRectInit}
 */
function getSVGContentRect(target) {
    var bbox = target.getBBox();
    return createRectInit(0, 0, bbox.width, bbox.height);
}
/**
 * Calculates content rectangle of provided HTMLElement.
 *
 * @param {HTMLElement} target - Element for which to calculate the content rectangle.
 * @returns {DOMRectInit}
 */
function getHTMLElementContentRect(target) {
    // Client width & height properties can't be
    // used exclusively as they provide rounded values.
    var clientWidth = target.clientWidth, clientHeight = target.clientHeight;
    // By this condition we can catch all non-replaced inline, hidden and
    // detached elements. Though elements with width & height properties less
    // than 0.5 will be discarded as well.
    //
    // Without it we would need to implement separate methods for each of
    // those cases and it's not possible to perform a precise and performance
    // effective test for hidden elements. E.g. even jQuery's ':visible' filter
    // gives wrong results for elements with width & height less than 0.5.
    if (!clientWidth && !clientHeight) {
        return emptyRect;
    }
    var styles = getWindowOf(target).getComputedStyle(target);
    var paddings = getPaddings(styles);
    var horizPad = paddings.left + paddings.right;
    var vertPad = paddings.top + paddings.bottom;
    // Computed styles of width & height are being used because they are the
    // only dimensions available to JS that contain non-rounded values. It could
    // be possible to utilize the getBoundingClientRect if only it's data wasn't
    // affected by CSS transformations let alone paddings, borders and scroll bars.
    var width = toFloat(styles.width), height = toFloat(styles.height);
    // Width & height include paddings and borders when the 'border-box' box
    // model is applied (except for IE).
    if (styles.boxSizing === 'border-box') {
        // Following conditions are required to handle Internet Explorer which
        // doesn't include paddings and borders to computed CSS dimensions.
        //
        // We can say that if CSS dimensions + paddings are equal to the "client"
        // properties then it's either IE, and thus we don't need to subtract
        // anything, or an element merely doesn't have paddings/borders styles.
        if (Math.round(width + horizPad) !== clientWidth) {
            width -= getBordersSize(styles, 'left', 'right') + horizPad;
        }
        if (Math.round(height + vertPad) !== clientHeight) {
            height -= getBordersSize(styles, 'top', 'bottom') + vertPad;
        }
    }
    // Following steps can't be applied to the document's root element as its
    // client[Width/Height] properties represent viewport area of the window.
    // Besides, it's as well not necessary as the <html> itself neither has
    // rendered scroll bars nor it can be clipped.
    if (!isDocumentElement(target)) {
        // In some browsers (only in Firefox, actually) CSS width & height
        // include scroll bars size which can be removed at this step as scroll
        // bars are the only difference between rounded dimensions + paddings
        // and "client" properties, though that is not always true in Chrome.
        var vertScrollbar = Math.round(width + horizPad) - clientWidth;
        var horizScrollbar = Math.round(height + vertPad) - clientHeight;
        // Chrome has a rather weird rounding of "client" properties.
        // E.g. for an element with content width of 314.2px it sometimes gives
        // the client width of 315px and for the width of 314.7px it may give
        // 314px. And it doesn't happen all the time. So just ignore this delta
        // as a non-relevant.
        if (Math.abs(vertScrollbar) !== 1) {
            width -= vertScrollbar;
        }
        if (Math.abs(horizScrollbar) !== 1) {
            height -= horizScrollbar;
        }
    }
    return createRectInit(paddings.left, paddings.top, width, height);
}
/**
 * Checks whether provided element is an instance of the SVGGraphicsElement.
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
var isSVGGraphicsElement = (function () {
    // Some browsers, namely IE and Edge, don't have the SVGGraphicsElement
    // interface.
    if (typeof SVGGraphicsElement !== 'undefined') {
        return function (target) { return target instanceof getWindowOf(target).SVGGraphicsElement; };
    }
    // If it's so, then check that element is at least an instance of the
    // SVGElement and that it has the "getBBox" method.
    // eslint-disable-next-line no-extra-parens
    return function (target) { return (target instanceof getWindowOf(target).SVGElement &&
        typeof target.getBBox === 'function'); };
})();
/**
 * Checks whether provided element is a document element (<html>).
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
function isDocumentElement(target) {
    return target === getWindowOf(target).document.documentElement;
}
/**
 * Calculates an appropriate content rectangle for provided html or svg element.
 *
 * @param {Element} target - Element content rectangle of which needs to be calculated.
 * @returns {DOMRectInit}
 */
function getContentRect(target) {
    if (!isBrowser) {
        return emptyRect;
    }
    if (isSVGGraphicsElement(target)) {
        return getSVGContentRect(target);
    }
    return getHTMLElementContentRect(target);
}
/**
 * Creates rectangle with an interface of the DOMRectReadOnly.
 * Spec: https://drafts.fxtf.org/geometry/#domrectreadonly
 *
 * @param {DOMRectInit} rectInit - Object with rectangle's x/y coordinates and dimensions.
 * @returns {DOMRectReadOnly}
 */
function createReadOnlyRect(_a) {
    var x = _a.x, y = _a.y, width = _a.width, height = _a.height;
    // If DOMRectReadOnly is available use it as a prototype for the rectangle.
    var Constr = typeof DOMRectReadOnly !== 'undefined' ? DOMRectReadOnly : Object;
    var rect = Object.create(Constr.prototype);
    // Rectangle's properties are not writable and non-enumerable.
    defineConfigurable(rect, {
        x: x, y: y, width: width, height: height,
        top: y,
        right: x + width,
        bottom: height + y,
        left: x
    });
    return rect;
}
/**
 * Creates DOMRectInit object based on the provided dimensions and the x/y coordinates.
 * Spec: https://drafts.fxtf.org/geometry/#dictdef-domrectinit
 *
 * @param {number} x - X coordinate.
 * @param {number} y - Y coordinate.
 * @param {number} width - Rectangle's width.
 * @param {number} height - Rectangle's height.
 * @returns {DOMRectInit}
 */
function createRectInit(x, y, width, height) {
    return { x: x, y: y, width: width, height: height };
}

/**
 * Class that is responsible for computations of the content rectangle of
 * provided DOM element and for keeping track of it's changes.
 */
var ResizeObservation = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObservation.
     *
     * @param {Element} target - Element to be observed.
     */
    function ResizeObservation(target) {
        /**
         * Broadcasted width of content rectangle.
         *
         * @type {number}
         */
        this.broadcastWidth = 0;
        /**
         * Broadcasted height of content rectangle.
         *
         * @type {number}
         */
        this.broadcastHeight = 0;
        /**
         * Reference to the last observed content rectangle.
         *
         * @private {DOMRectInit}
         */
        this.contentRect_ = createRectInit(0, 0, 0, 0);
        this.target = target;
    }
    /**
     * Updates content rectangle and tells whether it's width or height properties
     * have changed since the last broadcast.
     *
     * @returns {boolean}
     */
    ResizeObservation.prototype.isActive = function () {
        var rect = getContentRect(this.target);
        this.contentRect_ = rect;
        return (rect.width !== this.broadcastWidth ||
            rect.height !== this.broadcastHeight);
    };
    /**
     * Updates 'broadcastWidth' and 'broadcastHeight' properties with a data
     * from the corresponding properties of the last observed content rectangle.
     *
     * @returns {DOMRectInit} Last observed content rectangle.
     */
    ResizeObservation.prototype.broadcastRect = function () {
        var rect = this.contentRect_;
        this.broadcastWidth = rect.width;
        this.broadcastHeight = rect.height;
        return rect;
    };
    return ResizeObservation;
}());

var ResizeObserverEntry = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObserverEntry.
     *
     * @param {Element} target - Element that is being observed.
     * @param {DOMRectInit} rectInit - Data of the element's content rectangle.
     */
    function ResizeObserverEntry(target, rectInit) {
        var contentRect = createReadOnlyRect(rectInit);
        // According to the specification following properties are not writable
        // and are also not enumerable in the native implementation.
        //
        // Property accessors are not being used as they'd require to define a
        // private WeakMap storage which may cause memory leaks in browsers that
        // don't support this type of collections.
        defineConfigurable(this, { target: target, contentRect: contentRect });
    }
    return ResizeObserverEntry;
}());

var ResizeObserverSPI = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback function that is invoked
     *      when one of the observed elements changes it's content dimensions.
     * @param {ResizeObserverController} controller - Controller instance which
     *      is responsible for the updates of observer.
     * @param {ResizeObserver} callbackCtx - Reference to the public
     *      ResizeObserver instance which will be passed to callback function.
     */
    function ResizeObserverSPI(callback, controller, callbackCtx) {
        /**
         * Collection of resize observations that have detected changes in dimensions
         * of elements.
         *
         * @private {Array<ResizeObservation>}
         */
        this.activeObservations_ = [];
        /**
         * Registry of the ResizeObservation instances.
         *
         * @private {Map<Element, ResizeObservation>}
         */
        this.observations_ = new MapShim();
        if (typeof callback !== 'function') {
            throw new TypeError('The callback provided as parameter 1 is not a function.');
        }
        this.callback_ = callback;
        this.controller_ = controller;
        this.callbackCtx_ = callbackCtx;
    }
    /**
     * Starts observing provided element.
     *
     * @param {Element} target - Element to be observed.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.observe = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is already being observed.
        if (observations.has(target)) {
            return;
        }
        observations.set(target, new ResizeObservation(target));
        this.controller_.addObserver(this);
        // Force the update of observations.
        this.controller_.refresh();
    };
    /**
     * Stops observing provided element.
     *
     * @param {Element} target - Element to stop observing.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.unobserve = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is not being observed.
        if (!observations.has(target)) {
            return;
        }
        observations.delete(target);
        if (!observations.size) {
            this.controller_.removeObserver(this);
        }
    };
    /**
     * Stops observing all elements.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.disconnect = function () {
        this.clearActive();
        this.observations_.clear();
        this.controller_.removeObserver(this);
    };
    /**
     * Collects observation instances the associated element of which has changed
     * it's content rectangle.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.gatherActive = function () {
        var _this = this;
        this.clearActive();
        this.observations_.forEach(function (observation) {
            if (observation.isActive()) {
                _this.activeObservations_.push(observation);
            }
        });
    };
    /**
     * Invokes initial callback function with a list of ResizeObserverEntry
     * instances collected from active resize observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.broadcastActive = function () {
        // Do nothing if observer doesn't have active observations.
        if (!this.hasActive()) {
            return;
        }
        var ctx = this.callbackCtx_;
        // Create ResizeObserverEntry instance for every active observation.
        var entries = this.activeObservations_.map(function (observation) {
            return new ResizeObserverEntry(observation.target, observation.broadcastRect());
        });
        this.callback_.call(ctx, entries, ctx);
        this.clearActive();
    };
    /**
     * Clears the collection of active observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.clearActive = function () {
        this.activeObservations_.splice(0);
    };
    /**
     * Tells whether observer has active observations.
     *
     * @returns {boolean}
     */
    ResizeObserverSPI.prototype.hasActive = function () {
        return this.activeObservations_.length > 0;
    };
    return ResizeObserverSPI;
}());

// Registry of internal observers. If WeakMap is not available use current shim
// for the Map collection as it has all required methods and because WeakMap
// can't be fully polyfilled anyway.
var observers = typeof WeakMap !== 'undefined' ? new WeakMap() : new MapShim();
/**
 * ResizeObserver API. Encapsulates the ResizeObserver SPI implementation
 * exposing only those methods and properties that are defined in the spec.
 */
var ResizeObserver = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback that is invoked when
     *      dimensions of the observed elements change.
     */
    function ResizeObserver(callback) {
        if (!(this instanceof ResizeObserver)) {
            throw new TypeError('Cannot call a class as a function.');
        }
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        var controller = ResizeObserverController.getInstance();
        var observer = new ResizeObserverSPI(callback, controller, this);
        observers.set(this, observer);
    }
    return ResizeObserver;
}());
// Expose public methods of ResizeObserver.
[
    'observe',
    'unobserve',
    'disconnect'
].forEach(function (method) {
    ResizeObserver.prototype[method] = function () {
        var _a;
        return (_a = observers.get(this))[method].apply(_a, arguments);
    };
});

var index = (function () {
    // Export existing implementation if available.
    if (typeof global$1.ResizeObserver !== 'undefined') {
        return global$1.ResizeObserver;
    }
    return ResizeObserver;
})();

/* harmony default export */ __webpack_exports__["default"] = (index);

/* WEBPACK VAR INJECTION */}.call(__webpack_exports__, __webpack_require__(18)));

/***/ }),

/***/ 8:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.hasClass = hasClass;
exports.addClass = addClass;
exports.removeClass = removeClass;
exports.setStyle = setStyle;
exports.insertAfter = insertAfter;
exports.scrollTop = scrollTop;
exports.contains = contains;
var SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
var MOZ_HACK_REGEXP = /^moz([A-Z])/;
var ieVersion = Number(document.documentMode);

/* istanbul ignore next */
var trim = function trim(string) {
  return (string || '').replace(/^[\s\uFEFF]+|[\s\uFEFF]+$/g, '');
};
/* istanbul ignore next */
var camelCase = function camelCase(name) {
  return name.replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
    return offset ? letter.toUpperCase() : letter;
  }).replace(MOZ_HACK_REGEXP, 'Moz$1');
};

/* istanbul ignore next */
var on = exports.on = function () {
  if (document.addEventListener) {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.addEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.attachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
var off = exports.off = function () {
  if (document.removeEventListener) {
    return function (element, event, handler) {
      if (element && event) {
        element.removeEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event) {
        element.detachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
exports.once = function once(el, event, fn) {
  var listener = function listener() {
    if (fn) {
      for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      fn.apply(this, args);
    }
    off(el, event, listener);
  };
  on(el, event, listener);
};

/* istanbul ignore next */
function hasClass(el, cls) {
  if (!el || !cls) return false;
  if (cls.indexOf(' ') !== -1) {
    throw new Error('className should not contain space.');
  }
  if (el.classList) {
    return el.classList.contains(cls);
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
  }
}
/* istanbul ignore next */
function addClass(el, cls) {
  if (!el) return;
  var curClass = el.className;
  var classes = (cls || '').split(' ');

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.add(clsName);
    } else {
      if (!hasClass(el, clsName)) {
        curClass += ' ' + clsName;
      }
    }
  }
  if (!el.classList) {
    el.className = curClass;
  }
}
/* istanbul ignore next */
function removeClass(el, cls) {
  if (!el || !cls) return;
  var classes = cls.split(' ');
  var curClass = ' ' + el.className + ' ';

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.remove(clsName);
    } else {
      if (hasClass(el, clsName)) {
        curClass = curClass.replace(' ' + clsName + ' ', ' ');
      }
    }
  }
  if (!el.classList) {
    el.className = trim(curClass);
  }
}
/* istanbul ignore next */
exports.getStyle = ieVersion < 9 ? function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'styleFloat';
  }
  try {
    switch (styleName) {
      case 'opacity':
        try {
          return element.filters.item('alpha').opacity / 100;
        } catch (e) {
          return 1.0;
        }
      default:
        return element.style[styleName] || element.currentStyle ? element.currentStyle[styleName] : null;
    }
  } catch (e) {
    return element.style[styleName];
  }
} : function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'cssFloat';
  }
  try {
    var computed = document.defaultView.getComputedStyle(element, '');
    return element.style[styleName] || computed ? computed[styleName] : null;
  } catch (e) {
    return element.style[styleName];
  }
};

/* istanbul ignore next */
function setStyle(element, styleName, value) {
  if (!element || !styleName) return;

  if ((typeof styleName === 'undefined' ? 'undefined' : _typeof(styleName)) === 'object') {
    for (var prop in styleName) {
      if (styleName.hasOwnProperty(prop)) {
        setStyle(element, prop, styleName[prop]);
      }
    }
  } else {
    styleName = camelCase(styleName);
    if (styleName === 'opacity' && ieVersion < 9) {
      element.style.filter = isNaN(value) ? '' : 'alpha(opacity=' + value * 100 + ')';
    } else {
      element.style[styleName] = value;
    }
  }
}// 选中DOM滚动到可视区域
exports.scrollIntoView = function scrollIntoView(container, selected) {
  var direction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'vertical';

  // direction: 方向 vertival || horizontal
  if (!selected) {
    if (direction === 'horizontal') {
      container.scrollLeft = 0;
      return;
    }
    container.scrollTop = 0;
    return;
  }

  var offsetParents = [];
  var pointer = selected.offsetParent;
  while (pointer && container !== pointer && container.contains(pointer)) {
    offsetParents.push(pointer);
    pointer = pointer.offsetParent;
  }
  if (direction === 'horizontal') {
    // 水平方向滚动计算
    var left = selected.offsetLeft + offsetParents.reduce(function (prev, curr) {
      return prev + curr.offsetLeft;
    }, 0);
    var right = left + selected.offsetWidth;
    var viewRectLeft = container.scrollLeft;
    var viewRectRight = viewRectLeft + container.clientWidth;

    if (left < viewRectLeft) {
      container.scrollLeft = left;
    } else if (right > viewRectRight) {
      container.scrollLeft = right - container.clientWidth;
    }
    return;
  }
  var top = selected.offsetTop + offsetParents.reduce(function (prev, curr) {
    return prev + curr.offsetTop;
  }, 0);
  var bottom = top + selected.offsetHeight;
  var viewRectTop = container.scrollTop;
  var viewRectBottom = viewRectTop + container.clientHeight;

  if (top < viewRectTop) {
    container.scrollTop = top;
  } else if (bottom > viewRectBottom) {
    container.scrollTop = bottom - container.clientHeight;
  }
};
// 计算滚动条宽度
var scrollBarWidth = void 0;
exports.getScrollBarWidth = function getScrollBarWidth() {
  if (typeof scrollBarWidth !== 'undefined') return scrollBarWidth;
  var outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  outer.style.padding = 0;
  document.body.appendChild(outer);
  scrollBarWidth = outer.offsetHeight;
  document.body.removeChild(outer);
  return scrollBarWidth;
};

function insertAfter(el, refenerceEle) {
  var parent = refenerceEle.parentNode;
  // 如果最后的节点是目标元素，则直接添加
  if (parent.lastChild === refenerceEle) {
    parent.appendChild(el);
  } else {
    // 如果不是，则插入在目标元素的下一个兄弟节点 的前面
    parent.insertBefore(el, refenerceEle.nextSibling);
  }
}
exports.removeDom = function removeDom(el) {
  if (!el) return;
  if (el.remove) {
    el.remove();
    return;
  }
  var parent = el.parentNode;
  if (parent && parent.removeChild) {
    parent.removeChild(el);
  }
};
// scrollTop animation
function scrollTop(el) {
  var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var to = arguments[2];
  var duration = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 500;

  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame = window.webkitRequestAnimationFrame || window.mozRequestAnimationFrame || window.msRequestAnimationFrame || function (callback) {
      return window.setTimeout(callback, 1000 / 60);
    };
  }
  var difference = Math.abs(from - to);
  var step = Math.ceil(difference / duration * 50);

  function scroll(start, end, step) {
    if (start === end) return;

    var d = start + step > end ? end : start + step;
    if (start > end) {
      d = start - step < end ? end : start - step;
    }

    if (el === window) {
      window.scrollTo(d, d);
    } else {
      el.scrollTop = d;
    }
    window.requestAnimationFrame(function () {
      return scroll(d, end, step);
    });
  }
  scroll(from, to, step);
}

function contains(el, parent) {
  if (!el || !parent) return false;
  while (el = el.parentNode) {
    if (el === parent) return true;
  }
  return false;
}
/***/ })

/******/ });
});

var popover = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 391);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 193:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; //
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var _base = __webpack_require__(1);

var _dom = __webpack_require__(8);

var _helper = __webpack_require__(4);

var _popper = __webpack_require__(9);

var _popper2 = _interopRequireDefault(_popper);

var _locale = __webpack_require__(3);

var _locale2 = _interopRequireDefault(_locale);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

exports.default = {
  name: _base.PREFIX + 'popover',

  components: {
    BytedPopper: _popper2.default
  },

  directives: {
    clickoutside: {
      bind: function bind(el, binding, vnode) {
        var handler = _typeof(binding.value) === 'object' ? binding.value.handler : binding.value;
        function documentHandler(e) {
          if (el && el.contains && el.contains(e.target)) {
            return false;
          }
          if (handler || binding.expression) {
            handler(e);
          }
        }
        var modifiers = binding.modifiers || {};
        var capture = modifiers.capture || binding.value.capture || false;

        el.__vueClickOutside__ = documentHandler;
        el.__vueCapture__ = capture;
        document.addEventListener('click', documentHandler, capture);
      },
      unbind: function unbind(el, binding) {
        document.removeEventListener('click', el.__vueClickOutside__, el.__vueCapture__);
        delete el.__vueClickOutside__;
        delete el.__vueCapture__;
      }
    }
  },
  mixins: [_locale2.default],
  props: {
    placement: {
      type: String,
      default: 'top'
    },
    value: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    trigger: {
      type: String,
      default: 'click',
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['click', 'hover', 'manual']);
      }
    },
    offset: {
      type: [String, Number],
      default: 0
    },
    width: {
      type: [String, Number],
      default: '410px'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // 统一命名为showDelay，之后版本会下掉该属性
    openDelay: {
      type: Number,
      default: 0,
      validator: function validator(value) {
        if (value) {
          console.warn('[BUI Warning][Popover] open-delay should change to show-delay ');
        }
        return true;
      }
    },
    showDelay: {
      type: Number,
      default: 0
    },
    hideDelay: {
      type: Number,
      default: 200
    },

    transfer: {
      type: [Boolean, HTMLElement],
      default: false
    },
    status: {
      type: String,
      default: '',
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['', 'error']);
      }
    },
    // 鼠标是否可进入到 popper 中
    enterable: {
      type: Boolean,
      default: true
    },
    popperOptions: {
      type: Object,
      default: function _default() {
        return {
          modifiers: {
            flip: {
              enabled: false
            },
            hide: {
              enabled: false
            },
            preventOverflow: {
              enabled: false
            }
          }
        };
      }
    },
    // 是否监听popper内容变化自动更新弹框位置
    fitPopperResize: {
      type: Boolean,
      default: false
    },
    popperClass: {
      type: [String, Array],
      default: ''
    },
    arrow: {
      type: Boolean,
      default: true
    },
    useClickOutsideCapture: {
      type: Boolean,
      default: false
    }
  },

  data: function data() {
    return {
      popoverShow: false,
      referenceEle: null, // 定位参照物对象
      timer: null, // 内部用定时器
      bindEvtInterval: null
    };
  },

  computed: {
    wrapperClass: function wrapperClass(param) {
      var classArr = [_base.CSS_PREFIX + 'popover-wrapper'];
      return classArr;
    },
    baseClass: function baseClass() {
      var classArr = [_base.CSS_PREFIX + 'popover'];
      if (this.status) {
        classArr.push('bui-popover-' + this.status);
      }

      if (this.popperClass) {
        if (typeof this.popperClass === 'string') {
          classArr.push(this.popperClass);
        }
        if (Array.isArray(this.popperClass)) {
          classArr = classArr.concat(this.popperClass);
        }
      }
      if (this.isRTL) {
        classArr.push(_base.CSS_PREFIX + 'popover-rtl');
      }
      return classArr;
    },
    popperStyle: function popperStyle() {
      return {
        width: this.width
      };
    },
    popper: function popper() {
      return this.$refs['popper'];
    },
    popperMergeOptions: function popperMergeOptions() {
      // 兼容offset
      var modifiers = _extends({}, this.popperOptions.modifiers, {
        offset: {
          offset: this.offset
        }
      });
      // 兼容placement
      var placement = this.placement;
      return _extends({}, this.popperOptions, { placement: placement, modifiers: modifiers });
    }
  },
  watch: {
    value: function value(newVal) {
      if (this.trigger === 'manual') {
        this.popoverShow = newVal;
      }
    },
    popoverShow: function popoverShow(newVal) {
      if (this.disabled) {
        return;
      }
      if (this.trigger === 'manual') {
        this.$emit('input', newVal);
      }
      this.$emit('visible-change', newVal);
      if (newVal) {
        this.popper.update();
      } else {
        this.popper.destroy();
      }
    },
    disabled: function disabled(newVal) {
      if (newVal) {
        this.popoverShow = false;
      }
    }
  },
  mounted: function mounted() {
    var _this = this;

    // 处理 referenceEle
    if (this.$slots['reference'] && this.$slots['reference'][0]) {
      this.referenceEle = this.$slots['reference'][0].elm;
    }
    if (this.referenceEle) {
      this.bindRefenerceEvent();
    } else {
      // f**k vue
      // f**k vue again
      // 用 2s 延时来兜底
      var count = 0;
      this.bindEvtInterval = window.setInterval(function () {
        if (_this.referenceEle) {
          window.clearInterval(_this.bindEvtInterval);
          _this.bindRefenerceEvent();
          _this.bindEvtInterval = null;
        }
        // 防止出现定时器反复执行的情况
        count++;
        if (count > 10) {
          window.clearInterval(_this.bindEvtInterval);
          _this.bindEvtInterval = null;
        }
      }, 2000);
    }
    // 处理使用 manual 模式下,默认为 true 情况下
    // 需要保证在 refenerce 已经存在的情况下,进行赋值
    if (this.trigger === 'manual') {
      this.$nextTick(function () {
        _this.popoverShow = _this.value;
      });
    }
  },
  destroyed: function destroyed() {
    this.unbindRefenerceEvent();
  },

  methods: {
    // 绑定参照物处理事件
    bindRefenerceEvent: function bindRefenerceEvent() {
      var referenceEle = this.referenceEle;
      if (!referenceEle || !this.popper) {
        // console.warning('定位参照物不存在，请使用 v-popover:xxx 指定定位参照物');
        return false;
      }
      if (this.trigger === 'click') {
        (0, _dom.on)(referenceEle, 'click', this.togglePop);
      } else if (this.trigger === 'hover') {
        (0, _dom.on)(referenceEle, 'mouseenter', this.handleMouseEnter);
        (0, _dom.on)(this.popper.$el, 'mouseenter', this.handleMouseEnter);
        (0, _dom.on)(referenceEle, 'mouseleave', this.handleMouseLeave);
        (0, _dom.on)(this.popper.$el, 'mouseleave', this.handleMouseLeave);
      }
    },
    handleMouseEnter: function handleMouseEnter(e) {
      var _this2 = this;

      if (e.target === this.popper.$el && !this.enterable) {
        return;
      }
      clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        _this2.showPop();
      }, this.showDelay || this.openDelay);
    },
    handleMouseLeave: function handleMouseLeave() {
      var _this3 = this;

      clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        _this3.hidePop();
      }, this.hideDelay);
    },

    // 切换
    togglePop: function togglePop() {
      this.popoverShow ? this.hidePop() : this.showPop();
    },

    // @expose-api 隐藏
    hidePop: function hidePop() {
      if (this.disabled || !this.popoverShow) {
        return false;
      }
      this.popoverShow = false;
    },

    // 显示
    showPop: function showPop() {
      if (this.disabled) {
        return false;
      }
      this.popoverShow = true;
    },
    unbindRefenerceEvent: function unbindRefenerceEvent() {
      var reference = this.referenceEle;
      clearTimeout(this.timer);
      // 解绑事件
      (0, _dom.off)(reference, 'click', this.doToggle);
      (0, _dom.off)(reference, 'mouseleave', this.handleMouseLeave);
      (0, _dom.off)(reference, 'mouseenter', this.handleMouseEnter);
    },
    isInPopover: function isInPopover(el) {
      do {
        if ((0, _dom.hasClass)(el, 'bui-popper')) {
          return el;
        }
      } while (el = el.parentNode);
      return false;
    },
    handleClickoutside: function handleClickoutside(ev) {
      if (this.trigger !== 'click') return false;

      var popperEl = this.isInPopover(ev.target);
      if (
      // 1. The target element is contained in the popper
      (0, _dom.contains)(ev.target, this.$refs.popper && this.$refs.popper.$el) ||
      // 2. The `reference` element of the nested popper is contained in the parent popper
      popperEl && popperEl.__vue__ && (0, _dom.contains)(popperEl.__vue__.reference, this.$refs.popper.$el)) {
        return false;
      }

      this.hidePop();
    }
  }
};

/***/ }),

/***/ 3:
/***/ (function(module, exports) {

module.exports = locale;

/***/ }),

/***/ 391:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _popover = __webpack_require__(392);

var _popover2 = _interopRequireDefault(_popover);

var _directive = __webpack_require__(394);

var _directive2 = _interopRequireDefault(_directive);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_popover2.default.install = function (Vue) {
  Vue.directive('popover', _directive2.default);
  Vue.component(_popover2.default.name, _popover2.default);
};

exports.default = _popover2.default;

/***/ }),

/***/ 392:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popover_vue__ = __webpack_require__(193);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popover_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popover_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popover_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popover_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_daf770ee_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_popover_vue__ = __webpack_require__(393);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_popover_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_daf770ee_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_popover_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_daf770ee_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_popover_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 393:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{directives:[{name:"clickoutside",rawName:"v-clickoutside",value:({handler: _vm.handleClickoutside, capture: _vm.useClickOutsideCapture}),expression:"{handler: handleClickoutside, capture: useClickOutsideCapture}"}],class:_vm.wrapperClass},[_c('byted-popper',{directives:[{name:"show",rawName:"v-show",value:(!_vm.disabled && _vm.popoverShow),expression:"!disabled && popoverShow"}],ref:"popper",class:_vm.baseClass,style:(_vm.popperStyle),attrs:{"reference":_vm.referenceEle,"placement":_vm.placement,"popper-options":_vm.popperMergeOptions,"append-to-body":_vm.transfer,"fit-popper-resize":_vm.fitPopperResize}},[(_vm.arrow)?_c('div',{staticClass:"bui-popover-arrow",attrs:{"x-arrow":""}}):_vm._e(),_vm._v(" "),_c('div',{staticClass:"bui-popover-panel"},[(_vm.$slots.header || _vm.title)?_c('div',{staticClass:"bui-popover-header"},[(_vm.$slots.header)?_vm._t("header"):[_vm._v(_vm._s(_vm.title))]],2):_vm._e(),_vm._v(" "),_c('div',{staticClass:"bui-popover-body"},[(_vm.$slots.body)?_vm._t("body"):_vm._e()],2),_vm._v(" "),(_vm.$slots.footer)?_c('div',{staticClass:"bui-popover-footer"},[_vm._t("footer")],2):_vm._e(),_vm._v(" "),_vm._t("default")],2)]),_vm._v(" "),_vm._t("reference")],2)};
var staticRenderFns = [];


/***/ }),

/***/ 394:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
// 将被参考定位的元素的 el 传到对应的 popover 组件里
var getReference = function getReference(el, binding, vnode) {
  var vm = vnode.context;
  if (vm.$refs[binding.arg]) {
    // f**k vue https://github.com/vuejs/vue/issues/5561
    vm.$refs[binding.arg].referenceEle = el;
  } else {
    // 此处避免 popper 和 reference 的顺序导致的问题
    vm.$nextTick(function () {
      vm.$refs[binding.arg].referenceEle = el;
    });
  }
};
exports.default = {
  bind: function bind(el, binding, vnode) {
    getReference(el, binding, vnode);
  },
  inserted: function inserted(el, binding, vnode) {
    getReference(el, binding, vnode);
  }
};

/***/ }),

/***/ 4:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isOneOf = isOneOf;
function isOneOf(value, validList) {
  for (var i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true;
    }
  }
  return false;
}

/***/ }),

/***/ 8:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.hasClass = hasClass;
exports.addClass = addClass;
exports.removeClass = removeClass;
exports.setStyle = setStyle;
exports.insertAfter = insertAfter;
exports.scrollTop = scrollTop;
exports.contains = contains;
var SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
var MOZ_HACK_REGEXP = /^moz([A-Z])/;
var ieVersion = Number(document.documentMode);

/* istanbul ignore next */
var trim = function trim(string) {
  return (string || '').replace(/^[\s\uFEFF]+|[\s\uFEFF]+$/g, '');
};
/* istanbul ignore next */
var camelCase = function camelCase(name) {
  return name.replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
    return offset ? letter.toUpperCase() : letter;
  }).replace(MOZ_HACK_REGEXP, 'Moz$1');
};

/* istanbul ignore next */
var on = exports.on = function () {
  if (document.addEventListener) {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.addEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.attachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
var off = exports.off = function () {
  if (document.removeEventListener) {
    return function (element, event, handler) {
      if (element && event) {
        element.removeEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event) {
        element.detachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
exports.once = function once(el, event, fn) {
  var listener = function listener() {
    if (fn) {
      for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      fn.apply(this, args);
    }
    off(el, event, listener);
  };
  on(el, event, listener);
};

/* istanbul ignore next */
function hasClass(el, cls) {
  if (!el || !cls) return false;
  if (cls.indexOf(' ') !== -1) {
    throw new Error('className should not contain space.');
  }
  if (el.classList) {
    return el.classList.contains(cls);
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
  }
}
/* istanbul ignore next */
function addClass(el, cls) {
  if (!el) return;
  var curClass = el.className;
  var classes = (cls || '').split(' ');

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.add(clsName);
    } else {
      if (!hasClass(el, clsName)) {
        curClass += ' ' + clsName;
      }
    }
  }
  if (!el.classList) {
    el.className = curClass;
  }
}
/* istanbul ignore next */
function removeClass(el, cls) {
  if (!el || !cls) return;
  var classes = cls.split(' ');
  var curClass = ' ' + el.className + ' ';

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.remove(clsName);
    } else {
      if (hasClass(el, clsName)) {
        curClass = curClass.replace(' ' + clsName + ' ', ' ');
      }
    }
  }
  if (!el.classList) {
    el.className = trim(curClass);
  }
}
/* istanbul ignore next */
exports.getStyle = ieVersion < 9 ? function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'styleFloat';
  }
  try {
    switch (styleName) {
      case 'opacity':
        try {
          return element.filters.item('alpha').opacity / 100;
        } catch (e) {
          return 1.0;
        }
      default:
        return element.style[styleName] || element.currentStyle ? element.currentStyle[styleName] : null;
    }
  } catch (e) {
    return element.style[styleName];
  }
} : function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'cssFloat';
  }
  try {
    var computed = document.defaultView.getComputedStyle(element, '');
    return element.style[styleName] || computed ? computed[styleName] : null;
  } catch (e) {
    return element.style[styleName];
  }
};

/* istanbul ignore next */
function setStyle(element, styleName, value) {
  if (!element || !styleName) return;

  if ((typeof styleName === 'undefined' ? 'undefined' : _typeof(styleName)) === 'object') {
    for (var prop in styleName) {
      if (styleName.hasOwnProperty(prop)) {
        setStyle(element, prop, styleName[prop]);
      }
    }
  } else {
    styleName = camelCase(styleName);
    if (styleName === 'opacity' && ieVersion < 9) {
      element.style.filter = isNaN(value) ? '' : 'alpha(opacity=' + value * 100 + ')';
    } else {
      element.style[styleName] = value;
    }
  }
}// 选中DOM滚动到可视区域
exports.scrollIntoView = function scrollIntoView(container, selected) {
  var direction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'vertical';

  // direction: 方向 vertival || horizontal
  if (!selected) {
    if (direction === 'horizontal') {
      container.scrollLeft = 0;
      return;
    }
    container.scrollTop = 0;
    return;
  }

  var offsetParents = [];
  var pointer = selected.offsetParent;
  while (pointer && container !== pointer && container.contains(pointer)) {
    offsetParents.push(pointer);
    pointer = pointer.offsetParent;
  }
  if (direction === 'horizontal') {
    // 水平方向滚动计算
    var left = selected.offsetLeft + offsetParents.reduce(function (prev, curr) {
      return prev + curr.offsetLeft;
    }, 0);
    var right = left + selected.offsetWidth;
    var viewRectLeft = container.scrollLeft;
    var viewRectRight = viewRectLeft + container.clientWidth;

    if (left < viewRectLeft) {
      container.scrollLeft = left;
    } else if (right > viewRectRight) {
      container.scrollLeft = right - container.clientWidth;
    }
    return;
  }
  var top = selected.offsetTop + offsetParents.reduce(function (prev, curr) {
    return prev + curr.offsetTop;
  }, 0);
  var bottom = top + selected.offsetHeight;
  var viewRectTop = container.scrollTop;
  var viewRectBottom = viewRectTop + container.clientHeight;

  if (top < viewRectTop) {
    container.scrollTop = top;
  } else if (bottom > viewRectBottom) {
    container.scrollTop = bottom - container.clientHeight;
  }
};
// 计算滚动条宽度
var scrollBarWidth = void 0;
exports.getScrollBarWidth = function getScrollBarWidth() {
  if (typeof scrollBarWidth !== 'undefined') return scrollBarWidth;
  var outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  outer.style.padding = 0;
  document.body.appendChild(outer);
  scrollBarWidth = outer.offsetHeight;
  document.body.removeChild(outer);
  return scrollBarWidth;
};

function insertAfter(el, refenerceEle) {
  var parent = refenerceEle.parentNode;
  // 如果最后的节点是目标元素，则直接添加
  if (parent.lastChild === refenerceEle) {
    parent.appendChild(el);
  } else {
    // 如果不是，则插入在目标元素的下一个兄弟节点 的前面
    parent.insertBefore(el, refenerceEle.nextSibling);
  }
}
exports.removeDom = function removeDom(el) {
  if (!el) return;
  if (el.remove) {
    el.remove();
    return;
  }
  var parent = el.parentNode;
  if (parent && parent.removeChild) {
    parent.removeChild(el);
  }
};
// scrollTop animation
function scrollTop(el) {
  var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var to = arguments[2];
  var duration = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 500;

  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame = window.webkitRequestAnimationFrame || window.mozRequestAnimationFrame || window.msRequestAnimationFrame || function (callback) {
      return window.setTimeout(callback, 1000 / 60);
    };
  }
  var difference = Math.abs(from - to);
  var step = Math.ceil(difference / duration * 50);

  function scroll(start, end, step) {
    if (start === end) return;

    var d = start + step > end ? end : start + step;
    if (start > end) {
      d = start - step < end ? end : start - step;
    }

    if (el === window) {
      window.scrollTo(d, d);
    } else {
      el.scrollTop = d;
    }
    window.requestAnimationFrame(function () {
      return scroll(d, end, step);
    });
  }
  scroll(from, to, step);
}

function contains(el, parent) {
  if (!el || !parent) return false;
  while (el = el.parentNode) {
    if (el === parent) return true;
  }
  return false;
}
/***/ }),

/***/ 9:
/***/ (function(module, exports) {

module.exports = popper;

/***/ })

/******/ });
});

var dom = createCommonjsModule(function (module, exports) {

exports.__esModule = true;

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.hasClass = hasClass;
exports.addClass = addClass;
exports.removeClass = removeClass;
exports.setStyle = setStyle;
exports.insertAfter = insertAfter;
exports.scrollTop = scrollTop;
exports.contains = contains;
var SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
var MOZ_HACK_REGEXP = /^moz([A-Z])/;
var ieVersion = Number(document.documentMode);

/* istanbul ignore next */
var trim = function trim(string) {
  return (string || '').replace(/^[\s\uFEFF]+|[\s\uFEFF]+$/g, '');
};
/* istanbul ignore next */
var camelCase = function camelCase(name) {
  return name.replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
    return offset ? letter.toUpperCase() : letter;
  }).replace(MOZ_HACK_REGEXP, 'Moz$1');
};

/* istanbul ignore next */
var on = exports.on = function () {
  if (document.addEventListener) {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.addEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.attachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
var off = exports.off = function () {
  if (document.removeEventListener) {
    return function (element, event, handler) {
      if (element && event) {
        element.removeEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event) {
        element.detachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
exports.once = function once(el, event, fn) {
  var listener = function listener() {
    if (fn) {
      for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      fn.apply(this, args);
    }
    off(el, event, listener);
  };
  on(el, event, listener);
};

/* istanbul ignore next */
function hasClass(el, cls) {
  if (!el || !cls) return false;
  if (cls.indexOf(' ') !== -1) {
    throw new Error('className should not contain space.');
  }
  if (el.classList) {
    return el.classList.contains(cls);
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
  }
}
/* istanbul ignore next */
function addClass(el, cls) {
  if (!el) return;
  var curClass = el.className;
  var classes = (cls || '').split(' ');

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.add(clsName);
    } else {
      if (!hasClass(el, clsName)) {
        curClass += ' ' + clsName;
      }
    }
  }
  if (!el.classList) {
    el.className = curClass;
  }
}
/* istanbul ignore next */
function removeClass(el, cls) {
  if (!el || !cls) return;
  var classes = cls.split(' ');
  var curClass = ' ' + el.className + ' ';

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.remove(clsName);
    } else {
      if (hasClass(el, clsName)) {
        curClass = curClass.replace(' ' + clsName + ' ', ' ');
      }
    }
  }
  if (!el.classList) {
    el.className = trim(curClass);
  }
}
/* istanbul ignore next */
exports.getStyle = ieVersion < 9 ? function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'styleFloat';
  }
  try {
    switch (styleName) {
      case 'opacity':
        try {
          return element.filters.item('alpha').opacity / 100;
        } catch (e) {
          return 1.0;
        }
      default:
        return element.style[styleName] || element.currentStyle ? element.currentStyle[styleName] : null;
    }
  } catch (e) {
    return element.style[styleName];
  }
} : function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'cssFloat';
  }
  try {
    var computed = document.defaultView.getComputedStyle(element, '');
    return element.style[styleName] || computed ? computed[styleName] : null;
  } catch (e) {
    return element.style[styleName];
  }
};

/* istanbul ignore next */
function setStyle(element, styleName, value) {
  if (!element || !styleName) return;

  if ((typeof styleName === 'undefined' ? 'undefined' : _typeof(styleName)) === 'object') {
    for (var prop in styleName) {
      if (styleName.hasOwnProperty(prop)) {
        setStyle(element, prop, styleName[prop]);
      }
    }
  } else {
    styleName = camelCase(styleName);
    if (styleName === 'opacity' && ieVersion < 9) {
      element.style.filter = isNaN(value) ? '' : 'alpha(opacity=' + value * 100 + ')';
    } else {
      element.style[styleName] = value;
    }
  }
}// 选中DOM滚动到可视区域
exports.scrollIntoView = function scrollIntoView(container, selected) {
  var direction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'vertical';

  // direction: 方向 vertival || horizontal
  if (!selected) {
    if (direction === 'horizontal') {
      container.scrollLeft = 0;
      return;
    }
    container.scrollTop = 0;
    return;
  }

  var offsetParents = [];
  var pointer = selected.offsetParent;
  while (pointer && container !== pointer && container.contains(pointer)) {
    offsetParents.push(pointer);
    pointer = pointer.offsetParent;
  }
  if (direction === 'horizontal') {
    // 水平方向滚动计算
    var left = selected.offsetLeft + offsetParents.reduce(function (prev, curr) {
      return prev + curr.offsetLeft;
    }, 0);
    var right = left + selected.offsetWidth;
    var viewRectLeft = container.scrollLeft;
    var viewRectRight = viewRectLeft + container.clientWidth;

    if (left < viewRectLeft) {
      container.scrollLeft = left;
    } else if (right > viewRectRight) {
      container.scrollLeft = right - container.clientWidth;
    }
    return;
  }
  var top = selected.offsetTop + offsetParents.reduce(function (prev, curr) {
    return prev + curr.offsetTop;
  }, 0);
  var bottom = top + selected.offsetHeight;
  var viewRectTop = container.scrollTop;
  var viewRectBottom = viewRectTop + container.clientHeight;

  if (top < viewRectTop) {
    container.scrollTop = top;
  } else if (bottom > viewRectBottom) {
    container.scrollTop = bottom - container.clientHeight;
  }
};
// 计算滚动条宽度
var scrollBarWidth = void 0;
exports.getScrollBarWidth = function getScrollBarWidth() {
  if (typeof scrollBarWidth !== 'undefined') return scrollBarWidth;
  var outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  outer.style.padding = 0;
  document.body.appendChild(outer);
  scrollBarWidth = outer.offsetHeight;
  document.body.removeChild(outer);
  return scrollBarWidth;
};

function insertAfter(el, refenerceEle) {
  var parent = refenerceEle.parentNode;
  // 如果最后的节点是目标元素，则直接添加
  if (parent.lastChild === refenerceEle) {
    parent.appendChild(el);
  } else {
    // 如果不是，则插入在目标元素的下一个兄弟节点 的前面
    parent.insertBefore(el, refenerceEle.nextSibling);
  }
}
exports.removeDom = function removeDom(el) {
  if (!el) return;
  if (el.remove) {
    el.remove();
    return;
  }
  var parent = el.parentNode;
  if (parent && parent.removeChild) {
    parent.removeChild(el);
  }
};
// scrollTop animation
function scrollTop(el) {
  var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var to = arguments[2];
  var duration = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 500;

  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame = window.webkitRequestAnimationFrame || window.mozRequestAnimationFrame || window.msRequestAnimationFrame || function (callback) {
      return window.setTimeout(callback, 1000 / 60);
    };
  }
  var difference = Math.abs(from - to);
  var step = Math.ceil(difference / duration * 50);

  function scroll(start, end, step) {
    if (start === end) return;

    var d = start + step > end ? end : start + step;
    if (start > end) {
      d = start - step < end ? end : start - step;
    }

    if (el === window) {
      window.scrollTo(d, d);
    } else {
      el.scrollTop = d;
    }
    window.requestAnimationFrame(function () {
      return scroll(d, end, step);
    });
  }
  scroll(from, to, step);
}

function contains(el, parent) {
  if (!el || !parent) return false;
  while (el = el.parentNode) {
    if (el === parent) return true;
  }
  return false;
}});

var tooltip = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 491);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 134:
/***/ (function(module, exports) {

module.exports = popover;

/***/ }),

/***/ 228:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _dom = __webpack_require__(86);

var _popover = __webpack_require__(134);

var _popover2 = _interopRequireDefault(_popover);

var _helper = __webpack_require__(4);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

// import BytedPopper from 'components/popper';
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

exports.default = {
  name: _base.PREFIX + 'tooltip',
  components: {
    BytedPopover: _popover2.default
  },
  props: {
    placement: {
      type: String,
      default: 'top'
    },
    offset: {
      type: [String, Number],
      default: 0
    },
    width: {
      type: [String, Number],
      default: '246px'
    },
    transfer: {
      type: [Boolean, HTMLElement],
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    openDelay: {
      type: Number,
      default: 0
    },
    showDelay: {
      type: Number,
      default: 0
    },
    hideDelay: {
      type: Number,
      default: 200
    },
    popperOptions: {
      type: Object,
      default: function _default() {
        return {
          modifiers: {
            flip: {
              enabled: false
            },
            hide: {
              enabled: false
            },
            preventOverflow: {
              enabled: false
            }
          }
        };
      }
    },
    // dark || light
    theme: {
      type: String,
      default: 'light',
      validator: function validator(val) {
        return (0, _helper.isOneOf)(val, ['light', 'dark']);
      }
    },
    // 是否监听popper内容变化自动更新弹框位置
    fitPopperResize: {
      type: Boolean,
      default: false
    },
    popperClass: {
      type: [String, Array],
      default: ''
    }
  },
  computed: {
    tooltipPopperClass: function tooltipPopperClass() {
      var classArr = [this.theme === 'dark' ? 'bui-popover-dark' : ''];
      if (this.popperClass) {
        if (typeof this.popperClass === 'string') {
          classArr.push(this.popperClass);
        }
        if (Array.isArray(this.popperClass)) {
          classArr = classArr.concat(this.popperClass);
        }
      }
      return classArr;
    }
  },
  mounted: function mounted() {
    var _this = this;

    this.$nextTick(function () {
      _this.initPopperClass();
    });
  },

  methods: {
    initPopperClass: function initPopperClass() {
      if (this.$refs.popover && this.$refs.popover.popper) {
        var popEle = this.$refs.popover.popper.$el;
        (0, _dom.addClass)(popEle, _base.CSS_PREFIX + 'tooltip');
      }
    }
  }
};

/***/ }),

/***/ 4:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isOneOf = isOneOf;
function isOneOf(value, validList) {
  for (var i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true;
    }
  }
  return false;
}

/***/ }),

/***/ 491:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _tooltip = __webpack_require__(492);

var _tooltip2 = _interopRequireDefault(_tooltip);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_tooltip2.default.install = function (Vue) {
  Vue.component(_tooltip2.default.name, _tooltip2.default);
  // Vue.component(Popover.name, Popover);
};

exports.default = _tooltip2.default;

/***/ }),

/***/ 492:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tooltip_vue__ = __webpack_require__(228);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tooltip_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tooltip_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tooltip_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tooltip_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_7bb65b46_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_tooltip_vue__ = __webpack_require__(493);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tooltip_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_7bb65b46_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_tooltip_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_7bb65b46_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_tooltip_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 493:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('byted-popover',{ref:"popover",attrs:{"placement":_vm.placement,"transfer":_vm.transfer,"width":_vm.width,"offset":_vm.offset,"enterable":false,"disabled":_vm.disabled,"show-delay":_vm.openDelay || _vm.showDelay,"hide-delay":_vm.hideDelay,"popper-options":_vm.popperOptions,"fit-popper-resize":_vm.fitPopperResize,"popper-class":_vm.tooltipPopperClass,"trigger":"hover"}},[_c('template',{slot:"body"},[_vm._t("default")],2),_vm._v(" "),_c('template',{slot:"reference"},[_vm._t("reference")],2)],2)};
var staticRenderFns = [];


/***/ }),

/***/ 86:
/***/ (function(module, exports) {

module.exports = dom;

/***/ })

/******/ });
});

var _Tooltip = /*@__PURE__*/getDefaultExportFromCjs(tooltip);

var css_248z$8 = ".by-input .bui-input,.by-input-group .bui-input-group-addon{position:relative;padding:5px 12px;font-size:14px;line-height:22px;font-weight:400}@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.by-input{position:relative;width:100%}.by-input .bui-input{margin:0;list-style:none;background-image:none;width:100%;display:inline-block;border-radius:4px;color:#333;background-color:#FFF;border-width:1px;border-style:solid;border-color:#E0E0E0;outline:0;-webkit-box-sizing:border-box;box-sizing:border-box}.by-input-group .bui-input-group-btn:first-child .by-button,.by-input-group .bui-input-group-btn:first-child .by-select{margin-right:-1px}.by-input .bui-input::-moz-placeholder{color:#C1C1C1;opacity:1}.by-input .bui-input:-ms-input-placeholder{color:#C1C1C1}.by-input .bui-input::-webkit-input-placeholder{color:#C1C1C1}.by-input .bui-input:hover{border-color:#338AFF;z-index:2;background-color:#FFF}.by-input .bui-input.active,.by-input .bui-input:active{border-color:#0672ff;z-index:2;background-color:#FFF}.by-input .bui-input:focus{border-color:#338AFF;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2);z-index:2;background-color:#FFF}.by-input .bui-input[readonly]{cursor:pointer}.by-input .bui-input-disabled,.by-input .bui-input[disabled]{border-color:#F0F0F0;color:#C1C1C1;cursor:not-allowed;opacity:1;background-color:#FAFAFA}.by-input .bui-input-disabled:focus,.by-input .bui-input-disabled:hover,.by-input .bui-input[disabled]:focus,.by-input .bui-input[disabled]:hover{border-color:#F0F0F0;-webkit-box-shadow:none;box-shadow:none}.by-input .bui-input-disabled::-moz-placeholder,.by-input .bui-input[disabled]::-moz-placeholder{color:#C1C1C1;opacity:1}.by-input .bui-input-disabled:-ms-input-placeholder,.by-input .bui-input[disabled]:-ms-input-placeholder{color:#C1C1C1}.by-input .bui-input-disabled::-webkit-input-placeholder,.by-input .bui-input[disabled]::-webkit-input-placeholder{color:#C1C1C1}.by-input .bui-input-filled{background-color:#F0F0F0;border-color:#F0F0F0}.by-input .bui-input-filled:hover:not([disabled]){border-color:#EBEBEB;z-index:2;background-color:#EBEBEB}.by-input .bui-input-filled.active,.by-input .bui-input-filled:active:not([disabled]){border-color:#E0E0E0;z-index:2;background-color:#E0E0E0}.by-input .bui-input-filled:focus:not([disabled]){border-color:#338AFF;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2);z-index:2;background-color:#F0F0F0}.by-input-group{position:relative;display:table;border-collapse:separate;width:100%}.by-input-group .bui-input,.by-input-group .bui-input-group-addon,.by-input-group .bui-input-group-btn{display:table-cell}.by-input-group .bui-input-group-addon{border:1px solid #E0E0E0;border-radius:4px;color:#333;background-color:#F0F0F0;white-space:nowrap;width:1px}.by-input-group .bui-input-group-addon:last-child,.by-input-group .bui-input-group-btn:last-child .by-button,.by-input-group .bui-input-group-btn:last-child .by-select .bui-input,.by-input-group .bui-input-group-btn:last-child .by-select .bui-select-wrapper-multiple,.by-input-prepend .bui-input{border-top-left-radius:0;border-bottom-left-radius:0}.by-input-append .bui-input,.by-input-group .bui-input-group-addon:first-child,.by-input-group .bui-input-group-btn:first-child .by-button,.by-input-group .bui-input-group-btn:first-child .by-select .bui-input,.by-input-group .bui-input-group-btn:first-child .by-select .bui-select-wrapper-multiple{border-top-right-radius:0;border-bottom-right-radius:0}.by-input-group .bui-input-group-addon:first-child{border-right:0}.by-input-group .bui-input-group-addon:last-child{border-left:0}.by-input-group .bui-input-group-btn{width:1px;white-space:nowrap;font-size:0;vertical-align:middle}.by-input-group .bui-input-group-btn:last-child .by-button,.by-input-group .bui-input-group-btn:last-child .by-select{margin-left:-1px}.by-input-xs{line-height:20px;font-size:12px}.by-input-xs .bui-input,.by-input-xs .bui-input-group-addon{font-size:12px;line-height:20px;padding:2px 12px}.by-input-sm .bui-input,.by-input-sm .bui-input-group-addon,.by-input-small .bui-input,.by-input-small .bui-input-group-addon{font-size:12px;line-height:20px;padding:4px 12px}.by-input-large .bui-input,.by-input-large .bui-input-group-addon,.by-input-lg .bui-input,.by-input-lg .bui-input-group-addon{font-size:14px;line-height:22px;padding:7px 12px}.by-input-xl .bui-input,.by-input-xl .bui-input-group-addon{font-size:14px;line-height:22px;padding:9px 12px}.by-input-suffix .bui-input{padding-right:30px}.by-input .bui-input-fix-icon{position:absolute;font-size:14px;top:50%;-webkit-transform:translateY(-50%);transform:translateY(-50%);line-height:0;z-index:3}.by-input-error .bui-input{border-color:#F65656;z-index:2;background-color:#FFF}.by-input-error .bui-input:hover:not([disabled]){border-color:#F65656;background-color:#FFF}.by-input-error .bui-input.active,.by-input-error .bui-input:active:not([disabled]),.by-input-error .bui-input:focus:not([disabled]){border-color:#F65656;-webkit-box-shadow:0 0 0 2px rgba(246,86,86,.2);box-shadow:0 0 0 2px rgba(246,86,86,.2);background-color:#FFF}.by-input-error .bui-input-filled{border-color:#feecec;z-index:2;background-color:#feecec}.by-input-error .bui-input-filled:hover:not([disabled]){border-color:#fccece;background-color:#fccece}.by-input-error .bui-input-filled.active,.by-input-error .bui-input-filled:active:not([disabled]),.by-input-error .bui-input-filled:focus:not([disabled]){border-color:#F65656;-webkit-box-shadow:0 0 0 2px rgba(246,86,86,.2);box-shadow:0 0 0 2px rgba(246,86,86,.2);background-color:#feecec}.by-input .bui-input-with-prefix{padding-left:32px}.by-input .bui-input-with-suffix{padding-right:32px}.by-input .bui-input-prefix{left:12px}.by-input .bui-input-prefix .by-icon{fill:#C1C1C1}.by-input .bui-input-suffix{right:12px}.by-input .bui-input-suffix .by-icon{fill:#C1C1C1}.by-input .bui-input-suffix .bui-input-clear-icon{fill:#999}.by-input-rtl{direction:rtl}.by-input-rtl .bui-input{border-radius:4px;padding-left:12px;padding-right:12px}.by-input-rtl.by-input-prepend .bui-input{border-top-right-radius:0;border-bottom-right-radius:0}.by-input-rtl.by-input-append .bui-input{border-top-left-radius:0;border-bottom-left-radius:0}.by-input-rtl .bui-input-with-prefix{padding-right:32px}.by-input-rtl .bui-input-with-suffix{padding-left:32px}.by-input-rtl .bui-input-prefix{left:auto;right:12px}.by-input-rtl .bui-input-suffix{left:12px;right:auto}.by-input-rtl.by-input-group .bui-input-group-addon:first-child{border-radius:0 4px 4px 0;border-left:0;border-right:1px solid #E0E0E0}.by-input-rtl.by-input-group .bui-input-group-addon:last-child{border-radius:4px 0 0 4px;border-right:0;border-left:1px solid #E0E0E0}.by-input-rtl.by-input-group .bui-input-group-btn:first-child .by-button{margin-right:0;margin-left:-1px;border-radius:0 4px 4px 0}.by-input-rtl.by-input-group .bui-input-group-btn:first-child .by-select{margin-right:0;margin-left:-1px}.by-input-rtl.by-input-group .bui-input-group-btn:first-child .by-select .bui-input,.by-input-rtl.by-input-group .bui-input-group-btn:first-child .by-select .bui-select-wrapper-multiple{border-radius:0 4px 4px 0}.by-input-rtl.by-input-group .bui-input-group-btn:last-child .by-button{margin-right:-1px;margin-left:0;border-radius:4px 0 0 4px}.by-input-rtl.by-input-group .bui-input-group-btn:last-child .by-select{margin-right:-1px;margin-left:0}.by-input-rtl.by-input-group .bui-input-group-btn:last-child .by-select .bui-input,.by-input-rtl.by-input-group .bui-input-group-btn:last-child .by-select .bui-select-wrapper-multiple{border-radius:4px 0 0 4px}";
styleInject(css_248z$8);

function _broadcast(componentName, eventName, params) {
  this.$children.forEach(function (child) {
    var name = child.$options.name;
    if (name === componentName) {
      child.$emit.apply(child, [eventName].concat(params));
    } else {
      _broadcast.apply(child, [componentName, eventName].concat(params));
    }
  });
}

var _default$1 = {
  methods: {
    dispatch: function dispatch(componentName, eventName, params) {
      var parent = this.$parent || this.$root;
      var name = parent.$options.name;
      while (parent && (!name || name !== componentName)) {
        parent = parent.$parent;
        if (parent) {
          name = parent.$options.name;
        }
      }
      if (parent) {
        var _parent;

        (_parent = parent).$emit.apply(_parent, [eventName].concat(params));
      }
    },
    broadcast: function broadcast(componentName, eventName, params) {
      _broadcast.call(this, componentName, eventName, params);
    }
  }
};

var emitter = /*#__PURE__*/Object.defineProperty({
	default: _default$1
}, '__esModule', {value: true});

var input = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 336);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 171:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _emitter = __webpack_require__(6);

var _emitter2 = _interopRequireDefault(_emitter);

var _locale = __webpack_require__(3);

var _locale2 = _interopRequireDefault(_locale);

var _helper = __webpack_require__(4);

var _dom = __webpack_require__(8);

var _icon = __webpack_require__(7);

var _icon2 = _interopRequireDefault(_icon);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

exports.default = {
  name: _base.PREFIX + 'input',
  components: {
    BytedIcon: _icon2.default
  },
  mixins: [_emitter2.default, _locale2.default],
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    type: { // 原生 input 的type
      type: String,
      default: 'text'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    // 输入的最小长度
    minlength: {
      type: Number,
      default: 0
    },
    // 输入的最大长度
    maxlength: {
      type: Number,
      default: Infinity
    },
    clearable: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['md', 'small', 'xs', 'sm', 'lg', 'xl']);
      }
    },
    status: {
      type: String,
      default: '',
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['', 'error']);
      }
    },
    prefix: {
      type: String,
      default: ''
    },
    suffix: {
      type: String,
      default: ''
    },
    // 内部使用，暂时不对外,禁止 dispatch form-item.change
    validateEvent: {
      type: Boolean,
      default: true
    },
    filled: {
      type: Boolean,
      default: false
    }
  },

  data: function data() {
    return {
      slotType: {
        prepend: '',
        append: ''
      },
      focused: false,
      hovering: false
    };
  },

  computed: {
    baseClass: function baseClass() {
      var baseClass = _base.CSS_PREFIX + 'input';
      var classArr = [baseClass, baseClass + '-' + this.size];
      if (this.clearable || this.hasSuffix) {
        classArr.push(baseClass + '-suffix');
      }
      if (this.hasPrefix) {
        classArr.push(baseClass + '-prefix');
      }
      if (this.hasPrepend || this.hasAppend) {
        classArr.push(baseClass + '-group');
      }
      if (this.hasPrepend) {
        classArr.push(baseClass + '-prepend');
      }
      if (this.hasAppend) {
        classArr.push(baseClass + '-append');
      }
      if (this.filled) {
        classArr.push(baseClass + '-filled');
      }
      if (this.status) {
        classArr.push(baseClass + '-' + this.status);
      }
      if (this.isRTL) {
        classArr.push(baseClass + '-rtl');
      }
      return classArr;
    },
    inputClasses: function inputClasses() {
      var baseClass = 'bui-input';
      var classArr = [baseClass];
      if (this.hasPrefix) {
        classArr.push(baseClass + '-with-prefix');
      }
      if (this.hasSuffix) {
        classArr.push(baseClass + '-with-suffix');
      }
      if (this.filled) {
        classArr.push(baseClass + '-filled');
      }
      return classArr;
    },

    hasPrepend: function hasPrepend() {
      var slot = this._slotContents || this.$slots || {};
      return slot['prepend'] != null;
    },
    hasAppend: function hasAppend() {
      var slot = this._slotContents || this.$slots || {};
      return slot['append'] != null;
    },
    inputGroupAddonClass: function inputGroupAddonClass() {
      return {
        prepend: this.addonClass('prepend'),
        append: this.addonClass('append')
      };
    },
    showClear: function showClear() {
      return this.clearable && !this.disabled && this.value !== '' && (this.focused || this.hovering);
    },
    hasPrefix: function hasPrefix() {
      return this.prefix !== '' || this.$slots.prefix !== undefined;
    },
    hasSuffix: function hasSuffix() {
      return this.suffix !== '' || this.$slots.suffix !== undefined;
    }
  },
  mounted: function mounted() {
    this.checkSlotType();
    this.updateIconOffset();
  },
  updated: function updated() {
    this.updateIconOffset();
  },

  methods: {
    checkSlotType: function checkSlotType() {
      this.slotType = {
        prepend: this.checkTag('prepend'),
        append: this.checkTag('append')
      };
    },
    checkTag: function checkTag(slotName) {
      var slot = this.$slots[slotName];
      var tag = '';
      if (slot && slot[0] && slot[0].tag) {
        var originTag = slot[0].tag;
        if (originTag.indexOf('select') !== -1) {
          tag = 'select';
        }
        if (originTag.indexOf('button') !== -1) {
          tag = 'button';
        }
      }
      return tag;
    },
    addonClass: function addonClass(name) {
      var classArr = [];
      if (this.slotType[name] === 'button' || this.slotType[name] === 'select') {
        classArr.push('bui-input-group-btn');
      } else {
        classArr.push('bui-input-group-addon');
      }
      return classArr;
    },
    handleInput: function handleInput(e) {
      if (e && e.target) {
        this.emitOnInputChange(e.target.value, e);
      }
    },
    handleClick: function handleClick(e) {
      this.$emit('click', e);
    },
    handleBlur: function handleBlur(e) {
      this.focused = false;
      this.$emit('blur', e);
      if (this.validateEvent) {
        this.dispatch(_base.PREFIX + 'form-item', 'bui.form.blur', [this.value]);
      }
    },
    handleFocus: function handleFocus(e) {
      this.focused = true;
      this.$emit('focus', e);
    },
    handleKeydown: function handleKeydown(e) {
      this.$emit('keydown', e);
    },
    handleKeyup: function handleKeyup(e) {
      this.$emit('keyup', e);
    },
    focus: function focus() {
      this.$refs['input'].focus();
    },
    clearInput: function clearInput() {
      this.$emit('clear', '');
      this.emitOnInputChange('');
    },
    emitOnInputChange: function emitOnInputChange(newVal) {
      var e = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

      this.$emit('input', newVal, e);
      this.$emit('change', newVal, e);
      if (this.validateEvent) {
        this.dispatch(_base.PREFIX + 'form-item', 'bui.form.change', [this.value]);
      }
    },
    handleInputChange: function handleInputChange(e) {
      // 原生change事件
      this.$emit('input-change', e);
    },
    calcIconOffset: function calcIconOffset(place) {
      var elList = [].slice.call(this.$el.querySelectorAll('.bui-input-' + place) || []);
      if (!elList.length) return;
      var el = null;
      var i = 0;
      while (i < elList.length) {
        if (elList[i].parentNode === this.$el) {
          el = elList[i];
          break;
        }
        i++;
      }
      if (!el) return;
      var pendantMap = {
        suffix: 'append',
        prefix: 'prepend'
      };

      var pendant = pendantMap[place];
      if (this.$slots[pendant]) {
        var attr = place === 'suffix' ? 'right' : 'left';
        attr = this.isRTL ? attr === 'right' ? 'left' : 'right' : attr;
        (0, _dom.setStyle)(el, attr, this.$refs[pendant].offsetWidth + 12 + 'px');
      } else {
        el.removeAttribute('style');
      }
    },
    updateIconOffset: function updateIconOffset() {
      // 更新 prefix 及 suffix icon位置
      if (this.hasPrefix) {
        this.calcIconOffset('prefix');
      }
      if (this.hasSuffix || this.showClear) {
        this.calcIconOffset('suffix');
      }
    }
  }
};

/***/ }),

/***/ 3:
/***/ (function(module, exports) {

module.exports = locale;

/***/ }),

/***/ 336:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _input = __webpack_require__(337);

var _input2 = _interopRequireDefault(_input);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_input2.default.install = function (Vue) {
  Vue.component(_input2.default.name, _input2.default);
};

exports.default = _input2.default;

/***/ }),

/***/ 337:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_input_vue__ = __webpack_require__(171);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_input_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_input_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_input_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_input_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_5c1d9730_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_input_vue__ = __webpack_require__(338);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_input_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_5c1d9730_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_input_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_5c1d9730_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_input_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 338:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{class:_vm.baseClass,on:{"mouseenter":function($event){_vm.hovering = true;},"mouseleave":function($event){_vm.hovering = false;}}},[(_vm.hasPrepend)?_c('div',{ref:"prepend",class:_vm.inputGroupAddonClass.prepend},[_vm._t("prepend")],2):_vm._e(),_vm._v(" "),_c('input',{ref:"input",class:_vm.inputClasses,attrs:{"type":_vm.type,"placeholder":_vm.placeholder,"disabled":_vm.disabled,"readonly":_vm.readonly,"minlength":_vm.minlength,"maxlength":_vm.maxlength},domProps:{"value":_vm.value},on:{"input":_vm.handleInput,"click":_vm.handleClick,"blur":_vm.handleBlur,"focus":_vm.handleFocus,"keydown":_vm.handleKeydown,"keyup":_vm.handleKeyup,"change":_vm.handleInputChange}}),_vm._v(" "),(_vm.showClear || _vm.hasSuffix)?_c('span',{staticClass:"bui-input-suffix bui-input-fix-icon"},[(!_vm.showClear && _vm.hasSuffix)?[_vm._t("suffix",[_c('byted-icon',{staticClass:"suffix-icon",attrs:{"name":_vm.suffix}})])]:_vm._e(),_vm._v(" "),(_vm.showClear)?_c('byted-icon',{staticClass:"bui-input-clear-icon",attrs:{"name":"close-circle"},on:{"click":function($event){$event.stopPropagation();return _vm.clearInput($event)}}}):_vm._e()],2):_vm._e(),_vm._v(" "),(_vm.hasAppend)?_c('div',{ref:"append",class:_vm.inputGroupAddonClass.append},[_vm._t("append")],2):_vm._e(),_vm._v(" "),(_vm.hasPrefix)?_c('span',{staticClass:"bui-input-prefix bui-input-fix-icon"},[_vm._t("prefix",[_c('byted-icon',{staticClass:"prefix-icon",attrs:{"name":_vm.prefix}})])],2):_vm._e()])};
var staticRenderFns = [];


/***/ }),

/***/ 4:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isOneOf = isOneOf;
function isOneOf(value, validList) {
  for (var i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true;
    }
  }
  return false;
}

/***/ }),

/***/ 6:
/***/ (function(module, exports) {

module.exports = emitter;

/***/ }),

/***/ 7:
/***/ (function(module, exports) {

module.exports = icon;

/***/ }),

/***/ 8:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.hasClass = hasClass;
exports.addClass = addClass;
exports.removeClass = removeClass;
exports.setStyle = setStyle;
exports.insertAfter = insertAfter;
exports.scrollTop = scrollTop;
exports.contains = contains;
var SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
var MOZ_HACK_REGEXP = /^moz([A-Z])/;
var ieVersion = Number(document.documentMode);

/* istanbul ignore next */
var trim = function trim(string) {
  return (string || '').replace(/^[\s\uFEFF]+|[\s\uFEFF]+$/g, '');
};
/* istanbul ignore next */
var camelCase = function camelCase(name) {
  return name.replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
    return offset ? letter.toUpperCase() : letter;
  }).replace(MOZ_HACK_REGEXP, 'Moz$1');
};

/* istanbul ignore next */
var on = exports.on = function () {
  if (document.addEventListener) {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.addEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.attachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
var off = exports.off = function () {
  if (document.removeEventListener) {
    return function (element, event, handler) {
      if (element && event) {
        element.removeEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event) {
        element.detachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
exports.once = function once(el, event, fn) {
  var listener = function listener() {
    if (fn) {
      for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      fn.apply(this, args);
    }
    off(el, event, listener);
  };
  on(el, event, listener);
};

/* istanbul ignore next */
function hasClass(el, cls) {
  if (!el || !cls) return false;
  if (cls.indexOf(' ') !== -1) {
    throw new Error('className should not contain space.');
  }
  if (el.classList) {
    return el.classList.contains(cls);
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
  }
}
/* istanbul ignore next */
function addClass(el, cls) {
  if (!el) return;
  var curClass = el.className;
  var classes = (cls || '').split(' ');

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.add(clsName);
    } else {
      if (!hasClass(el, clsName)) {
        curClass += ' ' + clsName;
      }
    }
  }
  if (!el.classList) {
    el.className = curClass;
  }
}
/* istanbul ignore next */
function removeClass(el, cls) {
  if (!el || !cls) return;
  var classes = cls.split(' ');
  var curClass = ' ' + el.className + ' ';

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.remove(clsName);
    } else {
      if (hasClass(el, clsName)) {
        curClass = curClass.replace(' ' + clsName + ' ', ' ');
      }
    }
  }
  if (!el.classList) {
    el.className = trim(curClass);
  }
}
/* istanbul ignore next */
exports.getStyle = ieVersion < 9 ? function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'styleFloat';
  }
  try {
    switch (styleName) {
      case 'opacity':
        try {
          return element.filters.item('alpha').opacity / 100;
        } catch (e) {
          return 1.0;
        }
      default:
        return element.style[styleName] || element.currentStyle ? element.currentStyle[styleName] : null;
    }
  } catch (e) {
    return element.style[styleName];
  }
} : function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'cssFloat';
  }
  try {
    var computed = document.defaultView.getComputedStyle(element, '');
    return element.style[styleName] || computed ? computed[styleName] : null;
  } catch (e) {
    return element.style[styleName];
  }
};

/* istanbul ignore next */
function setStyle(element, styleName, value) {
  if (!element || !styleName) return;

  if ((typeof styleName === 'undefined' ? 'undefined' : _typeof(styleName)) === 'object') {
    for (var prop in styleName) {
      if (styleName.hasOwnProperty(prop)) {
        setStyle(element, prop, styleName[prop]);
      }
    }
  } else {
    styleName = camelCase(styleName);
    if (styleName === 'opacity' && ieVersion < 9) {
      element.style.filter = isNaN(value) ? '' : 'alpha(opacity=' + value * 100 + ')';
    } else {
      element.style[styleName] = value;
    }
  }
}// 选中DOM滚动到可视区域
exports.scrollIntoView = function scrollIntoView(container, selected) {
  var direction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'vertical';

  // direction: 方向 vertival || horizontal
  if (!selected) {
    if (direction === 'horizontal') {
      container.scrollLeft = 0;
      return;
    }
    container.scrollTop = 0;
    return;
  }

  var offsetParents = [];
  var pointer = selected.offsetParent;
  while (pointer && container !== pointer && container.contains(pointer)) {
    offsetParents.push(pointer);
    pointer = pointer.offsetParent;
  }
  if (direction === 'horizontal') {
    // 水平方向滚动计算
    var left = selected.offsetLeft + offsetParents.reduce(function (prev, curr) {
      return prev + curr.offsetLeft;
    }, 0);
    var right = left + selected.offsetWidth;
    var viewRectLeft = container.scrollLeft;
    var viewRectRight = viewRectLeft + container.clientWidth;

    if (left < viewRectLeft) {
      container.scrollLeft = left;
    } else if (right > viewRectRight) {
      container.scrollLeft = right - container.clientWidth;
    }
    return;
  }
  var top = selected.offsetTop + offsetParents.reduce(function (prev, curr) {
    return prev + curr.offsetTop;
  }, 0);
  var bottom = top + selected.offsetHeight;
  var viewRectTop = container.scrollTop;
  var viewRectBottom = viewRectTop + container.clientHeight;

  if (top < viewRectTop) {
    container.scrollTop = top;
  } else if (bottom > viewRectBottom) {
    container.scrollTop = bottom - container.clientHeight;
  }
};
// 计算滚动条宽度
var scrollBarWidth = void 0;
exports.getScrollBarWidth = function getScrollBarWidth() {
  if (typeof scrollBarWidth !== 'undefined') return scrollBarWidth;
  var outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  outer.style.padding = 0;
  document.body.appendChild(outer);
  scrollBarWidth = outer.offsetHeight;
  document.body.removeChild(outer);
  return scrollBarWidth;
};

function insertAfter(el, refenerceEle) {
  var parent = refenerceEle.parentNode;
  // 如果最后的节点是目标元素，则直接添加
  if (parent.lastChild === refenerceEle) {
    parent.appendChild(el);
  } else {
    // 如果不是，则插入在目标元素的下一个兄弟节点 的前面
    parent.insertBefore(el, refenerceEle.nextSibling);
  }
}
exports.removeDom = function removeDom(el) {
  if (!el) return;
  if (el.remove) {
    el.remove();
    return;
  }
  var parent = el.parentNode;
  if (parent && parent.removeChild) {
    parent.removeChild(el);
  }
};
// scrollTop animation
function scrollTop(el) {
  var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var to = arguments[2];
  var duration = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 500;

  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame = window.webkitRequestAnimationFrame || window.mozRequestAnimationFrame || window.msRequestAnimationFrame || function (callback) {
      return window.setTimeout(callback, 1000 / 60);
    };
  }
  var difference = Math.abs(from - to);
  var step = Math.ceil(difference / duration * 50);

  function scroll(start, end, step) {
    if (start === end) return;

    var d = start + step > end ? end : start + step;
    if (start > end) {
      d = start - step < end ? end : start - step;
    }

    if (el === window) {
      window.scrollTo(d, d);
    } else {
      el.scrollTop = d;
    }
    window.requestAnimationFrame(function () {
      return scroll(d, end, step);
    });
  }
  scroll(from, to, step);
}

function contains(el, parent) {
  if (!el || !parent) return false;
  while (el = el.parentNode) {
    if (el === parent) return true;
  }
  return false;
}
/***/ })

/******/ });
});

var _Input = /*@__PURE__*/getDefaultExportFromCjs(input);

var css_248z$7 = "@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}";
styleInject(css_248z$7);

var formItem = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 326);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 14:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating required fields.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function required(rule, value, source, errors, options, type) {
  if (rule.required && (!source.hasOwnProperty(rule.field) || util.isEmptyValue(value, type || rule.type))) {
    errors.push(util.format(options.messages.required, rule.fullField));
  }
}

exports['default'] = required;

/***/ }),

/***/ 168:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _emitter = __webpack_require__(6);

var _emitter2 = _interopRequireDefault(_emitter);

var _locale = __webpack_require__(3);

var _locale2 = _interopRequireDefault(_locale);

var _asyncValidator = __webpack_require__(23);

var _asyncValidator2 = _interopRequireDefault(_asyncValidator);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

exports.default = {

  name: _base.PREFIX + 'form-item',

  mixins: [_emitter2.default, _locale2.default],
  props: {
    // 标签文本
    label: {
      type: String,
      default: ''
    },
    // 表单的 model 中的字段，校验与重置实现使用
    prop: {
      type: String,
      default: ''
    },
    // 此表单项的校验规则
    rules: {
      type: [Object, Array],
      default: function _default() {
        return [];
      }
    },
    // 是否显示校验错误提示
    showMessage: {
      type: Boolean,
      default: true
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      initialValue: null,
      validateState: '', // doing,success,error
      validateMessage: '',
      validateDisabled: false
    };
  },


  computed: {
    baseClass: function baseClass() {
      var classArr = [_base.CSS_PREFIX + 'form-item'];
      if (this.validateMessage.length !== 0) {
        classArr.push(_base.CSS_PREFIX + 'form-item-error');
      }
      if (this.required) {
        classArr.push(_base.CSS_PREFIX + 'form-item-required');
      }
      if (this.tooltip) {
        classArr.push(_base.CSS_PREFIX + 'form-item-tooltip');
      }
      if (this.isRTL) {
        classArr.push(_base.CSS_PREFIX + 'form-item-rtl');
      }
      return classArr;
    },
    hasLabel: function hasLabel() {
      return this.label || this.$slots.label;
    },
    hasLabelCol: function hasLabelCol() {
      return this.form.labelWidth && Number(this.form.labelWidth) > 0 && Number(this.form.labelWidth) < 24;
    },
    parentLabelWidth: function parentLabelWidth() {
      return this.form.labelWidth;
    },
    isHor: function isHor() {
      return this.form.layoutType === 'horizontal';
    },
    labelWidthClass: function labelWidthClass() {
      var classArr = [];
      if (this.isHor && this.hasLabelCol) {
        classArr.push(_base.CSS_PREFIX + 'col-span-' + this.parentLabelWidth);
      }
      if (this.form.labelAlign) {
        classArr.push('bui-form-item-label-' + this.form.labelAlign);
      }
      return classArr;
    },
    inputClass: function inputClass() {
      var classArr = [];
      if (this.hasLabelCol && this.isHor) {
        this.addLabelColClass(classArr);
      }
      if (this.validateMessage.length !== 0) {
        classArr.push(_base.CSS_PREFIX + 'form-item-error-input');
      }
      return classArr;
    },
    messageClass: function messageClass() {
      var classArr = [];
      if (this.hasLabelCol) {
        this.addLabelColClass(classArr);
      }
      if (this.hasLabelCol) {
        classArr.push(_base.CSS_PREFIX + 'col-offset-' + this.parentLabelWidth);
      }
      return classArr;
    },
    labelStyle: function labelStyle() {
      if (!this.hasLabelCol) {
        return {
          width: this.form.labelWidth
        };
      }
      return {};
    },
    inputStyle: function inputStyle() {
      if (!this.hasLabelCol) {
        var property = {};
        property[this.isRTL ? 'marginRight' : 'marginLeft'] = this.form.labelWidth;
        return property;
      }
      return {};
    },

    // 表单项的值
    itemValue: {
      cache: false,
      get: function get() {
        var model = this.form.model;
        if (!model || !this.prop) {
          return;
        }

        var path = this.prop;
        var pathArr = path.split('.');
        var obj = model;
        for (var i = 0, len = pathArr.length; i < len; i++) {
          obj = obj[pathArr[i]];
        }
        return obj;
      }
    },
    // 表单父组件
    form: function form() {
      var parent = this.$parent;
      while (parent.$options.name !== _base.PREFIX + 'form') {
        parent = parent.$parent;
      }
      return parent;
    }
  },
  mounted: function mounted() {
    if (this.prop) {
      this.dispatch(_base.PREFIX + 'form', 'bui.formItem.addItem', [this]);
      this.initialValue = this.itemValue;
      var rules = this.getRules();
      if (rules.length !== 0) {
        // 接受从表单组件传来的事件
        this.$on('bui.form.blur', this.onItemBlur);
        this.$on('bui.form.change', this.onItemChange);
      }
    }
  },
  beforeDestroy: function beforeDestroy() {
    if (this.prop) {
      this.dispatch(_base.PREFIX + 'form', 'bui.formItem.removeItem', [this]);
    }
  },

  methods: {
    addLabelColClass: function addLabelColClass(classArr) {
      var width = 24 - this.parentLabelWidth;
      classArr.push(_base.CSS_PREFIX + 'col-span-' + width);
    },
    onItemBlur: function onItemBlur() {
      this.validate('blur');
    },
    onItemChange: function onItemChange() {
      this.form.$emit('change', this.prop, this.itemValue);
      if (this.validateDisabled) {
        this.validateDisabled = false;
        return;
      }
      this.validate('change');
    },
    validate: function validate(trigger, cb) {
      var _this = this;

      // trigger 只有 blur change
      cb = cb || function () {};
      var rules = this.filterRuleByTrigger(trigger); // 依据触发情况，筛选触发条件
      if (!rules || rules.length === 0) {
        cb(true);
        return true;
      }
      this.validateState = 'doing';
      var ruleDesc = {};
      ruleDesc[this.prop] = rules;
      var validator = new _asyncValidator2.default(ruleDesc);
      var model = {};
      model[this.prop] = this.itemValue;
      validator.validate(model, { firstFields: true }, function (errors, item) {
        var valid = !errors;
        _this.validateState = !errors ? 'success' : 'error';
        _this.validateMessage = errors ? errors[0].message : '';
        cb(valid, _this.validateMessage, _this.prop); // 给回调函数返回三个参数 （是否通过,报错信息,字段名）
      });
    },
    getRules: function getRules() {
      // 深拷贝一份rules
      return [].concat(this.rules || []);
    },

    // 依据触发条件，筛选校验规则
    filterRuleByTrigger: function filterRuleByTrigger(trigger) {
      var rules = this.getRules();
      return rules.filter(function (rule) {
        return !rule.trigger || rule.trigger.indexOf(trigger) !== -1;
      });
    },

    // 清除校验结果
    resetValidation: function resetValidation() {
      this.validateState = '';
      this.validateMessage = '';
      this.validateDisabled = false;
    },
    reset: function reset() {
      this.resetValidation();

      var model = this.form.model;
      var value = this.itemValue;

      if (Array.isArray(value) && value.length > 0) {
        model[this.prop] = [].concat(this.initialValue); // 对象就深拷贝
      } else if (typeof value !== 'undefined') {
        model[this.prop] = this.initialValue;
      }
    }
  }
};

/***/ }),

/***/ 2:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.convertFieldsError = convertFieldsError;
exports.format = format;
exports.isEmptyValue = isEmptyValue;
exports.isEmptyObject = isEmptyObject;
exports.asyncMap = asyncMap;
exports.complementError = complementError;
exports.deepMerge = deepMerge;
/* eslint no-console:0 */

var formatRegExp = /%[sdj%]/g;

exports.warning = function warning() {};

// don't print warning message when in production env or node runtime
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  exports.warning = function warning(type, errors) {
    if (typeof console !== 'undefined' && console.warn) {
      if (errors.every(function (e) {
        return typeof e === 'string';
      })) {
        console.warn(type, errors);
      }
    }
  };
}

function convertFieldsError(errors) {
  if (!errors || !errors.length) return null;
  var fields = {};
  errors.forEach(function (error) {
    var field = error.field;
    fields[field] = fields[field] || [];
    fields[field].push(error);
  });
  return fields;
}

function format() {
  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  var i = 1;
  var f = args[0];
  var len = args.length;
  if (typeof f === 'function') {
    return f.apply(null, args.slice(1));
  }
  if (typeof f === 'string') {
    var str = String(f).replace(formatRegExp, function (x) {
      if (x === '%%') {
        return '%';
      }
      if (i >= len) {
        return x;
      }
      switch (x) {
        case '%s':
          return String(args[i++]);
        case '%d':
          return Number(args[i++]);
        case '%j':
          try {
            return JSON.stringify(args[i++]);
          } catch (_) {
            return '[Circular]';
          }
          break;
        default:
          return x;
      }
    });
    for (var arg = args[i]; i < len; arg = args[++i]) {
      str += ' ' + arg;
    }
    return str;
  }
  return f;
}

function isNativeStringType(type) {
  return type === 'string' || type === 'url' || type === 'hex' || type === 'email' || type === 'pattern';
}

function isEmptyValue(value, type) {
  if (value === undefined || value === null) {
    return true;
  }
  if (type === 'array' && Array.isArray(value) && !value.length) {
    return true;
  }
  if (isNativeStringType(type) && typeof value === 'string' && !value) {
    return true;
  }
  return false;
}

function isEmptyObject(obj) {
  return Object.keys(obj).length === 0;
}

function asyncParallelArray(arr, func, callback) {
  var results = [];
  var total = 0;
  var arrLength = arr.length;

  function count(errors) {
    results.push.apply(results, errors);
    total++;
    if (total === arrLength) {
      callback(results);
    }
  }

  arr.forEach(function (a) {
    func(a, count);
  });
}

function asyncSerialArray(arr, func, callback) {
  var index = 0;
  var arrLength = arr.length;

  function next(errors) {
    if (errors && errors.length) {
      callback(errors);
      return;
    }
    var original = index;
    index = index + 1;
    if (original < arrLength) {
      func(arr[original], next);
    } else {
      callback([]);
    }
  }

  next([]);
}

function flattenObjArr(objArr) {
  var ret = [];
  Object.keys(objArr).forEach(function (k) {
    ret.push.apply(ret, objArr[k]);
  });
  return ret;
}

function asyncMap(objArr, option, func, callback) {
  if (option.first) {
    var flattenArr = flattenObjArr(objArr);
    return asyncSerialArray(flattenArr, func, callback);
  }
  var firstFields = option.firstFields || [];
  if (firstFields === true) {
    firstFields = Object.keys(objArr);
  }
  var objArrKeys = Object.keys(objArr);
  var objArrLength = objArrKeys.length;
  var total = 0;
  var results = [];
  var pending = new Promise(function (resolve, reject) {
    var next = function next(errors) {
      results.push.apply(results, errors);
      total++;
      if (total === objArrLength) {
        callback(results);
        return results.length ? reject({ errors: results, fields: convertFieldsError(results) }) : resolve();
      }
    };
    objArrKeys.forEach(function (key) {
      var arr = objArr[key];
      if (firstFields.indexOf(key) !== -1) {
        asyncSerialArray(arr, func, next);
      } else {
        asyncParallelArray(arr, func, next);
      }
    });
  });
  pending['catch'](function (e) {
    return e;
  });
  return pending;
}

function complementError(rule) {
  return function (oe) {
    if (oe && oe.message) {
      oe.field = oe.field || rule.fullField;
      return oe;
    }
    return {
      message: typeof oe === 'function' ? oe() : oe,
      field: oe.field || rule.fullField
    };
  };
}

function deepMerge(target, source) {
  if (source) {
    for (var s in source) {
      if (source.hasOwnProperty(s)) {
        var value = source[s];
        if ((typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object' && _typeof(target[s]) === 'object') {
          target[s] = _extends({}, target[s], value);
        } else {
          target[s] = value;
        }
      }
    }
  }
  return target;
}

/***/ }),

/***/ 23:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _util = __webpack_require__(2);

var _validator = __webpack_require__(24);

var _validator2 = _interopRequireDefault(_validator);

var _messages2 = __webpack_require__(44);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Encapsulates a validation schema.
 *
 *  @param descriptor An object declaring validation rules
 *  for this schema.
 */
function Schema(descriptor) {
  this.rules = null;
  this._messages = _messages2.messages;
  this.define(descriptor);
}

Schema.prototype = {
  messages: function messages(_messages) {
    if (_messages) {
      this._messages = (0, _util.deepMerge)((0, _messages2.newMessages)(), _messages);
    }
    return this._messages;
  },
  define: function define(rules) {
    if (!rules) {
      throw new Error('Cannot configure a schema with no rules');
    }
    if ((typeof rules === 'undefined' ? 'undefined' : _typeof(rules)) !== 'object' || Array.isArray(rules)) {
      throw new Error('Rules must be an object');
    }
    this.rules = {};
    var z = void 0;
    var item = void 0;
    for (z in rules) {
      if (rules.hasOwnProperty(z)) {
        item = rules[z];
        this.rules[z] = Array.isArray(item) ? item : [item];
      }
    }
  },
  validate: function validate(source_) {
    var _this = this;

    var o = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var oc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {};

    var source = source_;
    var options = o;
    var callback = oc;
    if (typeof options === 'function') {
      callback = options;
      options = {};
    }
    if (!this.rules || Object.keys(this.rules).length === 0) {
      if (callback) {
        callback();
      }
      return Promise.resolve();
    }

    function complete(results) {
      var i = void 0;
      var errors = [];
      var fields = {};

      function add(e) {
        if (Array.isArray(e)) {
          var _errors;

          errors = (_errors = errors).concat.apply(_errors, e);
        } else {
          errors.push(e);
        }
      }

      for (i = 0; i < results.length; i++) {
        add(results[i]);
      }
      if (!errors.length) {
        errors = null;
        fields = null;
      } else {
        fields = (0, _util.convertFieldsError)(errors);
      }
      callback(errors, fields);
    }

    if (options.messages) {
      var messages = this.messages();
      if (messages === _messages2.messages) {
        messages = (0, _messages2.newMessages)();
      }
      (0, _util.deepMerge)(messages, options.messages);
      options.messages = messages;
    } else {
      options.messages = this.messages();
    }
    var arr = void 0;
    var value = void 0;
    var series = {};
    var keys = options.keys || Object.keys(this.rules);
    keys.forEach(function (z) {
      arr = _this.rules[z];
      value = source[z];
      arr.forEach(function (r) {
        var rule = r;
        if (typeof rule.transform === 'function') {
          if (source === source_) {
            source = _extends({}, source);
          }
          value = source[z] = rule.transform(value);
        }
        if (typeof rule === 'function') {
          rule = {
            validator: rule
          };
        } else {
          rule = _extends({}, rule);
        }
        rule.validator = _this.getValidationMethod(rule);
        rule.field = z;
        rule.fullField = rule.fullField || z;
        rule.type = _this.getType(rule);
        if (!rule.validator) {
          return;
        }
        series[z] = series[z] || [];
        series[z].push({
          rule: rule,
          value: value,
          source: source,
          field: z
        });
      });
    });
    var errorFields = {};
    return (0, _util.asyncMap)(series, options, function (data, doIt) {
      var rule = data.rule;
      var deep = (rule.type === 'object' || rule.type === 'array') && (_typeof(rule.fields) === 'object' || _typeof(rule.defaultField) === 'object');
      deep = deep && (rule.required || !rule.required && data.value);
      rule.field = data.field;

      function addFullfield(key, schema) {
        return _extends({}, schema, {
          fullField: rule.fullField + '.' + key
        });
      }

      function cb() {
        var e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

        var errors = e;
        if (!Array.isArray(errors)) {
          errors = [errors];
        }
        if (!options.suppressWarning && errors.length) {
          Schema.warning('async-validator:', errors);
        }
        if (errors.length && rule.message) {
          errors = [].concat(rule.message);
        }

        errors = errors.map((0, _util.complementError)(rule));

        if (options.first && errors.length) {
          errorFields[rule.field] = 1;
          return doIt(errors);
        }
        if (!deep) {
          doIt(errors);
        } else {
          // if rule is required but the target object
          // does not exist fail at the rule level and don't
          // go deeper
          if (rule.required && !data.value) {
            if (rule.message) {
              errors = [].concat(rule.message).map((0, _util.complementError)(rule));
            } else if (options.error) {
              errors = [options.error(rule, (0, _util.format)(options.messages.required, rule.field))];
            } else {
              errors = [];
            }
            return doIt(errors);
          }

          var fieldsSchema = {};
          if (rule.defaultField) {
            for (var k in data.value) {
              if (data.value.hasOwnProperty(k)) {
                fieldsSchema[k] = rule.defaultField;
              }
            }
          }
          fieldsSchema = _extends({}, fieldsSchema, data.rule.fields);
          for (var f in fieldsSchema) {
            if (fieldsSchema.hasOwnProperty(f)) {
              var fieldSchema = Array.isArray(fieldsSchema[f]) ? fieldsSchema[f] : [fieldsSchema[f]];
              fieldsSchema[f] = fieldSchema.map(addFullfield.bind(null, f));
            }
          }
          var schema = new Schema(fieldsSchema);
          schema.messages(options.messages);
          if (data.rule.options) {
            data.rule.options.messages = options.messages;
            data.rule.options.error = options.error;
          }
          schema.validate(data.value, data.rule.options || options, function (errs) {
            var finalErrors = [];
            if (errors && errors.length) {
              finalErrors.push.apply(finalErrors, errors);
            }
            if (errs && errs.length) {
              finalErrors.push.apply(finalErrors, errs);
            }
            doIt(finalErrors.length ? finalErrors : null);
          });
        }
      }

      var res = void 0;
      if (rule.asyncValidator) {
        res = rule.asyncValidator(rule, data.value, cb, data.source, options);
      } else if (rule.validator) {
        res = rule.validator(rule, data.value, cb, data.source, options);
        if (res === true) {
          cb();
        } else if (res === false) {
          cb(rule.message || rule.field + ' fails');
        } else if (res instanceof Array) {
          cb(res);
        } else if (res instanceof Error) {
          cb(res.message);
        }
      }
      if (res && res.then) {
        res.then(function () {
          return cb();
        }, function (e) {
          return cb(e);
        });
      }
    }, function (results) {
      complete(results);
    });
  },
  getType: function getType(rule) {
    if (rule.type === undefined && rule.pattern instanceof RegExp) {
      rule.type = 'pattern';
    }
    if (typeof rule.validator !== 'function' && rule.type && !_validator2['default'].hasOwnProperty(rule.type)) {
      throw new Error((0, _util.format)('Unknown rule type %s', rule.type));
    }
    return rule.type || 'string';
  },
  getValidationMethod: function getValidationMethod(rule) {
    if (typeof rule.validator === 'function') {
      return rule.validator;
    }
    var keys = Object.keys(rule);
    var messageIndex = keys.indexOf('message');
    if (messageIndex !== -1) {
      keys.splice(messageIndex, 1);
    }
    if (keys.length === 1 && keys[0] === 'required') {
      return _validator2['default'].required;
    }
    return _validator2['default'][this.getType(rule)] || false;
  }
};

Schema.register = function register(type, validator) {
  if (typeof validator !== 'function') {
    throw new Error('Cannot register a validator by type, validator is not a function');
  }
  _validator2['default'][type] = validator;
};

Schema.warning = _util.warning;

Schema.messages = _messages2.messages;

exports['default'] = Schema;

/***/ }),

/***/ 24:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _string = __webpack_require__(25);

var _string2 = _interopRequireDefault(_string);

var _method = __webpack_require__(31);

var _method2 = _interopRequireDefault(_method);

var _number = __webpack_require__(32);

var _number2 = _interopRequireDefault(_number);

var _boolean = __webpack_require__(33);

var _boolean2 = _interopRequireDefault(_boolean);

var _regexp = __webpack_require__(34);

var _regexp2 = _interopRequireDefault(_regexp);

var _integer = __webpack_require__(35);

var _integer2 = _interopRequireDefault(_integer);

var _float = __webpack_require__(36);

var _float2 = _interopRequireDefault(_float);

var _array = __webpack_require__(37);

var _array2 = _interopRequireDefault(_array);

var _object = __webpack_require__(38);

var _object2 = _interopRequireDefault(_object);

var _enum = __webpack_require__(39);

var _enum2 = _interopRequireDefault(_enum);

var _pattern = __webpack_require__(40);

var _pattern2 = _interopRequireDefault(_pattern);

var _date = __webpack_require__(41);

var _date2 = _interopRequireDefault(_date);

var _required = __webpack_require__(42);

var _required2 = _interopRequireDefault(_required);

var _type = __webpack_require__(43);

var _type2 = _interopRequireDefault(_type);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

exports['default'] = {
  string: _string2['default'],
  method: _method2['default'],
  number: _number2['default'],
  boolean: _boolean2['default'],
  regexp: _regexp2['default'],
  integer: _integer2['default'],
  float: _float2['default'],
  array: _array2['default'],
  object: _object2['default'],
  'enum': _enum2['default'],
  pattern: _pattern2['default'],
  date: _date2['default'],
  url: _type2['default'],
  hex: _type2['default'],
  email: _type2['default'],
  required: _required2['default']
};

/***/ }),

/***/ 25:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Performs validation for string types.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function string(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value, 'string') && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options, 'string');
    if (!(0, _util.isEmptyValue)(value, 'string')) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
      _rule2['default'].pattern(rule, value, source, errors, options);
      if (rule.whitespace === true) {
        _rule2['default'].whitespace(rule, value, source, errors, options);
      }
    }
  }
  callback(errors);
}

exports['default'] = string;

/***/ }),

/***/ 26:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating whitespace.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function whitespace(rule, value, source, errors, options) {
  if (/^\s+$/.test(value) || value === '') {
    errors.push(util.format(options.messages.whitespace, rule.fullField));
  }
}

exports['default'] = whitespace;

/***/ }),

/***/ 27:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

var _required = __webpack_require__(14);

var _required2 = _interopRequireDefault(_required);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/* eslint max-len:0 */

var pattern = {
  // http://emailregex.com/
  email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  url: new RegExp('^(?!mailto:)(?:(?:http|https|ftp)://|//)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$', 'i'),
  hex: /^#?([a-f0-9]{6}|[a-f0-9]{3})$/i
};

var types = {
  integer: function integer(value) {
    return types.number(value) && parseInt(value, 10) === value;
  },
  float: function float(value) {
    return types.number(value) && !types.integer(value);
  },
  array: function array(value) {
    return Array.isArray(value);
  },
  regexp: function regexp(value) {
    if (value instanceof RegExp) {
      return true;
    }
    try {
      return !!new RegExp(value);
    } catch (e) {
      return false;
    }
  },
  date: function date(value) {
    return typeof value.getTime === 'function' && typeof value.getMonth === 'function' && typeof value.getYear === 'function';
  },
  number: function number(value) {
    if (isNaN(value)) {
      return false;
    }
    return typeof value === 'number';
  },
  object: function object(value) {
    return (typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object' && !types.array(value);
  },
  method: function method(value) {
    return typeof value === 'function';
  },
  email: function email(value) {
    return typeof value === 'string' && !!value.match(pattern.email) && value.length < 255;
  },
  url: function url(value) {
    return typeof value === 'string' && !!value.match(pattern.url);
  },
  hex: function hex(value) {
    return typeof value === 'string' && !!value.match(pattern.hex);
  }
};

/**
 *  Rule for validating the type of a value.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function type(rule, value, source, errors, options) {
  if (rule.required && value === undefined) {
    (0, _required2['default'])(rule, value, source, errors, options);
    return;
  }
  var custom = ['integer', 'float', 'array', 'regexp', 'object', 'method', 'email', 'number', 'date', 'url', 'hex'];
  var ruleType = rule.type;
  if (custom.indexOf(ruleType) > -1) {
    if (!types[ruleType](value)) {
      errors.push(util.format(options.messages.types[ruleType], rule.fullField, rule.type));
    }
    // straight typeof check
  } else if (ruleType && (typeof value === 'undefined' ? 'undefined' : _typeof(value)) !== rule.type) {
    errors.push(util.format(options.messages.types[ruleType], rule.fullField, rule.type));
  }
}

exports['default'] = type;

/***/ }),

/***/ 28:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating minimum and maximum allowed values.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function range(rule, value, source, errors, options) {
  var len = typeof rule.len === 'number';
  var min = typeof rule.min === 'number';
  var max = typeof rule.max === 'number';
  // 正则匹配码点范围从U+010000一直到U+10FFFF的文字（补充平面Supplementary Plane）
  var spRegexp = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;
  var val = value;
  var key = null;
  var num = typeof value === 'number';
  var str = typeof value === 'string';
  var arr = Array.isArray(value);
  if (num) {
    key = 'number';
  } else if (str) {
    key = 'string';
  } else if (arr) {
    key = 'array';
  }
  // if the value is not of a supported type for range validation
  // the validation rule rule should use the
  // type property to also test for a particular type
  if (!key) {
    return false;
  }
  if (arr) {
    val = value.length;
  }
  if (str) {
    // 处理码点大于U+010000的文字length属性不准确的bug，如"𠮷𠮷𠮷".lenght !== 3
    val = value.replace(spRegexp, '_').length;
  }
  if (len) {
    if (val !== rule.len) {
      errors.push(util.format(options.messages[key].len, rule.fullField, rule.len));
    }
  } else if (min && !max && val < rule.min) {
    errors.push(util.format(options.messages[key].min, rule.fullField, rule.min));
  } else if (max && !min && val > rule.max) {
    errors.push(util.format(options.messages[key].max, rule.fullField, rule.max));
  } else if (min && max && (val < rule.min || val > rule.max)) {
    errors.push(util.format(options.messages[key].range, rule.fullField, rule.min, rule.max));
  }
}

exports['default'] = range;

/***/ }),

/***/ 29:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

var ENUM = 'enum';

/**
 *  Rule for validating a value exists in an enumerable list.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function enumerable(rule, value, source, errors, options) {
  rule[ENUM] = Array.isArray(rule[ENUM]) ? rule[ENUM] : [];
  if (rule[ENUM].indexOf(value) === -1) {
    errors.push(util.format(options.messages[ENUM], rule.fullField, rule[ENUM].join(', ')));
  }
}

exports['default'] = enumerable;

/***/ }),

/***/ 3:
/***/ (function(module, exports) {

module.exports = locale;

/***/ }),

/***/ 30:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating a regular expression pattern.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function pattern(rule, value, source, errors, options) {
  if (rule.pattern) {
    if (rule.pattern instanceof RegExp) {
      // if a RegExp instance is passed, reset `lastIndex` in case its `global`
      // flag is accidentally set to `true`, which in a validation scenario
      // is not necessary and the result might be misleading
      rule.pattern.lastIndex = 0;
      if (!rule.pattern.test(value)) {
        errors.push(util.format(options.messages.pattern.mismatch, rule.fullField, value, rule.pattern));
      }
    } else if (typeof rule.pattern === 'string') {
      var _pattern = new RegExp(rule.pattern);
      if (!_pattern.test(value)) {
        errors.push(util.format(options.messages.pattern.mismatch, rule.fullField, value, rule.pattern));
      }
    }
  }
}

exports['default'] = pattern;

/***/ }),

/***/ 31:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a function.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function method(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = method;

/***/ }),

/***/ 32:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a number.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function number(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if (value === '') {
      value = undefined;
    }
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = number;

/***/ }),

/***/ 326:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _formItem = __webpack_require__(327);

var _formItem2 = _interopRequireDefault(_formItem);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_formItem2.default.install = function (Vue) {
  Vue.component(_formItem2.default.name, _formItem2.default);
};

exports.default = _formItem2.default;

/***/ }),

/***/ 327:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_item_vue__ = __webpack_require__(168);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_item_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_item_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_item_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_item_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_38de1155_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_form_item_vue__ = __webpack_require__(328);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_item_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_38de1155_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_form_item_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_38de1155_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_form_item_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 328:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{class:_vm.baseClass},[(_vm.hasLabel)?_c('label',{staticClass:"bui-form-item-label",class:_vm.labelWidthClass,style:(_vm.labelStyle)},[_vm._t("label",[_c('span',{staticClass:"bui-form-item-label-text"},[_vm._v(_vm._s(_vm.label))])])],2):_vm._e(),_vm._v(" "),(_vm.isHor)?[_c('div',{class:_vm.inputClass,style:(_vm.inputStyle)},[_vm._t("default")],2)]:[_c('div',{class:_vm.inputClass},[_vm._t("default")],2)],_vm._v(" "),(_vm.showMessage && _vm.validateMessage.length!==0)?_c('span',{staticClass:"bui-form-item-help",class:_vm.messageClass,style:(_vm.inputStyle)},[_vm._v("\n    "+_vm._s(_vm.validateMessage)+"\n  ")]):_vm._e()],2)};
var staticRenderFns = [];


/***/ }),

/***/ 33:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a boolean.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function boolean(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = boolean;

/***/ }),

/***/ 34:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates the regular expression type.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function regexp(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (!(0, _util.isEmptyValue)(value)) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = regexp;

/***/ }),

/***/ 35:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a number is an integer.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function integer(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = integer;

/***/ }),

/***/ 36:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a number is a floating point number.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function floatFn(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = floatFn;

/***/ }),

/***/ 37:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates an array.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function array(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value, 'array') && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options, 'array');
    if (!(0, _util.isEmptyValue)(value, 'array')) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = array;

/***/ }),

/***/ 38:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates an object.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function object(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = object;

/***/ }),

/***/ 39:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

var ENUM = 'enum';

/**
 *  Validates an enumerable list.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function enumerable(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value) {
      _rule2['default'][ENUM](rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = enumerable;

/***/ }),

/***/ 40:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a regular expression pattern.
 *
 *  Performs validation when a rule only contains
 *  a pattern property but is not declared as a string type.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function pattern(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value, 'string') && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (!(0, _util.isEmptyValue)(value, 'string')) {
      _rule2['default'].pattern(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = pattern;

/***/ }),

/***/ 41:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function date(rule, value, callback, source, options) {
  // console.log('integer rule called %j', rule);
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  // console.log('validate on %s value', value);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (!(0, _util.isEmptyValue)(value)) {
      var dateObject = void 0;

      if (typeof value === 'number') {
        dateObject = new Date(value);
      } else {
        dateObject = value;
      }

      _rule2['default'].type(rule, dateObject, source, errors, options);
      if (dateObject) {
        _rule2['default'].range(rule, dateObject.getTime(), source, errors, options);
      }
    }
  }
  callback(errors);
}

exports['default'] = date;

/***/ }),

/***/ 42:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function required(rule, value, callback, source, options) {
  var errors = [];
  var type = Array.isArray(value) ? 'array' : typeof value === 'undefined' ? 'undefined' : _typeof(value);
  _rule2['default'].required(rule, value, source, errors, options, type);
  callback(errors);
}

exports['default'] = required;

/***/ }),

/***/ 43:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function type(rule, value, callback, source, options) {
  var ruleType = rule.type;
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value, ruleType) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options, ruleType);
    if (!(0, _util.isEmptyValue)(value, ruleType)) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = type;

/***/ }),

/***/ 44:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.newMessages = newMessages;
function newMessages() {
  return {
    'default': 'Validation error on field %s',
    required: '%s is required',
    'enum': '%s must be one of %s',
    whitespace: '%s cannot be empty',
    date: {
      format: '%s date %s is invalid for format %s',
      parse: '%s date could not be parsed, %s is invalid ',
      invalid: '%s date %s is invalid'
    },
    types: {
      string: '%s is not a %s',
      method: '%s is not a %s (function)',
      array: '%s is not an %s',
      object: '%s is not an %s',
      number: '%s is not a %s',
      date: '%s is not a %s',
      boolean: '%s is not a %s',
      integer: '%s is not an %s',
      float: '%s is not a %s',
      regexp: '%s is not a valid %s',
      email: '%s is not a valid %s',
      url: '%s is not a valid %s',
      hex: '%s is not a valid %s'
    },
    string: {
      len: '%s must be exactly %s characters',
      min: '%s must be at least %s characters',
      max: '%s cannot be longer than %s characters',
      range: '%s must be between %s and %s characters'
    },
    number: {
      len: '%s must equal %s',
      min: '%s cannot be less than %s',
      max: '%s cannot be greater than %s',
      range: '%s must be between %s and %s'
    },
    array: {
      len: '%s must be exactly %s in length',
      min: '%s cannot be less than %s in length',
      max: '%s cannot be greater than %s in length',
      range: '%s must be between %s and %s in length'
    },
    pattern: {
      mismatch: '%s value %s does not match pattern %s'
    },
    clone: function clone() {
      var cloned = JSON.parse(JSON.stringify(this));
      cloned.clone = this.clone;
      return cloned;
    }
  };
}

exports.messages = newMessages();

/***/ }),

/***/ 5:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _required = __webpack_require__(14);

var _required2 = _interopRequireDefault(_required);

var _whitespace = __webpack_require__(26);

var _whitespace2 = _interopRequireDefault(_whitespace);

var _type = __webpack_require__(27);

var _type2 = _interopRequireDefault(_type);

var _range = __webpack_require__(28);

var _range2 = _interopRequireDefault(_range);

var _enum = __webpack_require__(29);

var _enum2 = _interopRequireDefault(_enum);

var _pattern = __webpack_require__(30);

var _pattern2 = _interopRequireDefault(_pattern);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

exports['default'] = {
  required: _required2['default'],
  whitespace: _whitespace2['default'],
  type: _type2['default'],
  range: _range2['default'],
  'enum': _enum2['default'],
  pattern: _pattern2['default']
};

/***/ }),

/***/ 6:
/***/ (function(module, exports) {

module.exports = emitter;

/***/ })

/******/ });
});

var _FormItem = /*@__PURE__*/getDefaultExportFromCjs(formItem);

var css_248z$6 = ".by-form-horizontal .by-form-item-tooltip .bui-form-item-label-tooltip-icon,.by-form-vertical .by-form-item-tooltip .bui-form-item-label-tooltip-icon{fill:#D6D6D6;vertical-align:-2px}@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.by-form{position:relative}.by-form-vertical .bui-form-item-label{display:block;margin-bottom:8px;line-height:22px;color:#333}.by-form-vertical .by-form-item{margin-bottom:32px;position:relative}.by-form-vertical .by-form-item-error-input>:not(form):not(.by-form-item) .bui-input,.by-form-vertical .by-form-item-error-input>:not(form):not(.by-form-item) .bui-textarea{border-color:#F65656;z-index:2;background-color:#FFF}.by-form-vertical .by-form-item-error-input>:not(form):not(.by-form-item) .bui-input:focus:not([disabled]),.by-form-vertical .by-form-item-error-input>:not(form):not(.by-form-item) .bui-textarea:focus:not([disabled]){-webkit-box-shadow:0 0 0 2px rgba(246,86,86,.2);box-shadow:0 0 0 2px rgba(246,86,86,.2)}.by-form-vertical .by-form-item-error-input>:not(form):not(.by-form-item) .bui-multiple-input-wrapper{border-color:#F65656}.by-form-vertical .by-form-item-error-input>:not(form):not(.by-form-item) .bui-multiple-input-wrapper-focused{-webkit-box-shadow:0 0 0 2px rgba(246,86,86,.2);box-shadow:0 0 0 2px rgba(246,86,86,.2)}.by-form-vertical .by-form-item-required>.bui-form-item-label{display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-align:center;-ms-flex-align:center;align-items:center}.by-form-vertical .by-form-item-required>.bui-form-item-label::after{content:'';display:block;width:4px;height:4px;background-color:#F65656;border-radius:50%;margin-left:8px}.by-form-vertical .bui-form-item-help{position:absolute;top:100%;left:0;margin-left:0!important;font-size:12px;line-height:20px;color:#F65656;margin-top:4px}.by-form-horizontal .by-form-item{margin-left:0;margin-right:0;height:auto;zoom:1;margin-bottom:32px;position:relative}.by-form-horizontal .by-form-item::after,.by-form-horizontal .by-form-item::before{content:'';display:table}.by-form-horizontal .by-form-item::after{clear:both}.by-form-horizontal .by-form-item .bui-form-item-label{float:left;position:relative;padding:6px 8px 6px 0;font-size:14px;line-height:22px;color:#333}.by-form-horizontal .by-form-item .bui-form-item-label-text{display:inline-block}.by-form-horizontal .by-form-item .bui-form-item-label-right{text-align:right}.by-form-horizontal .by-form-item .bui-form-item-label-left{text-align:left}.by-form-horizontal .by-form-item .bui-form-item-help{position:absolute;top:100%;left:0;font-size:12px;line-height:20px;color:#F65656;margin-top:4px}.by-form-horizontal .by-form-item-required>.bui-form-item-label{padding-right:20px}.by-form-horizontal .by-form-item-required>.bui-form-item-label::after{content:'';display:block;width:4px;height:4px;background-color:#F65656;border-radius:50%;position:absolute;top:15px;right:8px}.by-form-horizontal .by-form-item-error-input>:not(form):not(.by-form-item) .bui-input,.by-form-horizontal .by-form-item-error-input>:not(form):not(.by-form-item) .bui-textarea{border-color:#F65656;z-index:2;background-color:#FFF}.by-form-horizontal .by-form-item-error-input>:not(form):not(.by-form-item) .bui-input:focus:not([disabled]),.by-form-horizontal .by-form-item-error-input>:not(form):not(.by-form-item) .bui-textarea:focus:not([disabled]){-webkit-box-shadow:0 0 0 2px rgba(246,86,86,.2);box-shadow:0 0 0 2px rgba(246,86,86,.2)}.by-form-horizontal .by-form-item-error-input>:not(form):not(.by-form-item) .bui-multiple-input-wrapper{border-color:#F65656}.by-form-horizontal .by-form-item-error-input>:not(form):not(.by-form-item) .bui-multiple-input-wrapper-focused{-webkit-box-shadow:0 0 0 2px rgba(246,86,86,.2);box-shadow:0 0 0 2px rgba(246,86,86,.2)}.by-form-has-required .by-form-item .bui-form-item-label{padding:6px 20px 6px 0}.help-block{float:left;margin-top:7px;margin-bottom:0;font-size:12px;color:#F65656}.by-form-rtl{direction:rtl}.by-form-rtl.by-form-horizontal .bui-form-item-label{float:right;padding-left:20px;padding-right:0}.by-form-rtl.by-form-horizontal .bui-form-item-label::after{left:8px;right:auto}.by-form-item-rtl{direction:rtl}";
styleInject(css_248z$6);

var form = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 323);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 167:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _helper = __webpack_require__(4);

var _locale = __webpack_require__(3);

var _locale2 = _interopRequireDefault(_locale);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

exports.default = {

  name: _base.PREFIX + 'form',
  mixins: [_locale2.default],
  props: {
    labelWidth: {
      type: [Number, String],
      default: 4
    },
    labelAlign: {
      type: String,
      default: 'right',
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['right', 'left']);
      }
    },
    layoutType: {
      type: String,
      default: 'horizontal',
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['horizontal', 'vertical']);
      }
    },
    model: {
      type: Object,
      default: function _default() {
        return {};
      }
    }
  },
  data: function data() {
    return {
      // form下属的form-item集合
      formItems: []
    };
  },

  computed: {
    baseClass: function baseClass() {
      if (this.type === 'basic') {
        return;
      }
      // layoutType  inline/horizontal/""
      var clsArr = [_base.CSS_PREFIX + 'form', _base.CSS_PREFIX + 'form-' + this.layoutType];
      if (this.hasRequired) {
        clsArr.push(_base.CSS_PREFIX + 'form-has-required');
      }
      if (this.isRTL) {
        clsArr.push(_base.CSS_PREFIX + 'form-rtl');
      }
      return clsArr;
    },
    hasRequired: function hasRequired() {
      return this.formItems.some(function (item) {
        return item.required === true;
      });
    }
  },
  created: function created() {
    var _this = this;

    // 监听 formItem 的增加
    this.$on('bui.formItem.addItem', function (item) {
      if (item) _this.formItems.push(item);
      return false;
    });
    // 监听 formItem 的减少
    this.$on('bui.formItem.removeItem', function (item) {
      if (item.prop) _this.formItems.splice(_this.formItems.indexOf(item), 1);
      return false;
    });
  },

  methods: {
    reset: function reset() {
      this.formItems.forEach(function (item) {
        item.reset();
      });
    },

    // 表单验证
    validate: function validate(cb) {
      var _this2 = this;

      var valid = true;
      var count = 0;
      var errorArr = [];
      var propArr = [];
      this.formItems.forEach(function (item, index) {
        item.validate('', function (itemValid, errorObj, prop) {
          // 调用form-item的validate方法校验
          if (!itemValid) {
            errorArr.push(errorObj);
            propArr.push(prop);
            valid = false;
          }
          if (typeof cb === 'function' && ++count === _this2.formItems.length) {
            cb(valid, errorArr, propArr); // 返回整体的表单校验结果，以及单个item的校验错误的数组和错误字段名
          }
          // this.$emit('validate', [itemValid, errorObj, prop]);
        });
      });
    },

    // 清除校验结果
    resetValidation: function resetValidation() {
      this.formItems.forEach(function (item) {
        item.resetValidation();
      });
    },
    validateField: function validateField(prop, cb) {
      if (!prop) return;

      var item = this.formItems.find(function (item) {
        return item.prop === prop;
      });
      if (!item) {
        console.warn('[BUI Warning] Form validateField prop is invalid');
        return;
      }
      item.validate('', function (itemValid, errorObj, prop) {
        cb(itemValid, errorObj, prop);
      });
    }
  }
}; //
//
//
//
//
//
//
//

/***/ }),

/***/ 3:
/***/ (function(module, exports) {

module.exports = locale;

/***/ }),

/***/ 323:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _form = __webpack_require__(324);

var _form2 = _interopRequireDefault(_form);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_form2.default.install = function (Vue) {
  Vue.component(_form2.default.name, _form2.default);
};

exports.default = _form2.default;

/***/ }),

/***/ 324:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_vue__ = __webpack_require__(167);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_43ea8112_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_form_vue__ = __webpack_require__(325);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_form_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_43ea8112_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_form_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_43ea8112_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_form_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 325:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('form',{class:_vm.baseClass,on:{"submit":function($event){$event.preventDefault();}}},[_vm._t("default")],2)};
var staticRenderFns = [];


/***/ }),

/***/ 4:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isOneOf = isOneOf;
function isOneOf(value, validList) {
  for (var i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true;
    }
  }
  return false;
}

/***/ })

/******/ });
});

var _Form = /*@__PURE__*/getDefaultExportFromCjs(form);

var css_248z$5 = "@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.by-checkbox{display:inline-block;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;padding-left:0;line-height:22px;vertical-align:bottom}.by-checkbox label{cursor:pointer;margin-bottom:0;line-height:0}.by-checkbox+.by-checkbox{margin-left:32px}.by-checkbox .bui-checkbox-input-wrapper{display:inline-block;background-color:transparent;vertical-align:top;position:relative;height:16px;width:16px;border:1px solid #E0E0E0;border-radius:2px;margin-top:3px;margin-right:8px}.by-checkbox .bui-checkbox-input-wrapper:hover,.by-checkbox.secondary .bui-checkbox-input-wrapper:hover{border-color:#338AFF}.by-checkbox .bui-checkbox-input-wrapper .bui-icon-check{position:absolute;top:50%;left:50%;-webkit-transform:translate(-50%,-50%);transform:translate(-50%,-50%);opacity:0;fill:#FFF}.by-checkbox.checked .bui-checkbox-input-wrapper,.by-checkbox.checked.secondary .bui-checkbox-input-wrapper,.by-checkbox.indeterminate .bui-checkbox-input-wrapper{border:1px solid #338AFF;background-color:#338AFF}.by-checkbox.empty .bui-checkbox-input-wrapper{margin-right:0}.by-checkbox .bui-checkbox-input-label{display:inline-block;font-size:14px;line-height:22px;font-weight:400;color:#333}.by-checkbox input.bui-checkbox-input{display:none}.by-checkbox.checked .bui-checkbox-input-wrapper .bui-icon-check{opacity:1}.by-checkbox.disabled.indeterminate .bui-checkbox-input-wrapper .bui-icon-check,.by-checkbox.indeterminate .bui-checkbox-input-wrapper .bui-icon-check{opacity:0}.by-checkbox.indeterminate .bui-checkbox-input-wrapper:after{content:'';position:absolute;height:2px;width:10px;border-radius:2px;background:#FFF;top:6px;left:2px}.by-checkbox.indeterminate.secondary .bui-checkbox-input-wrapper{border:1px solid #338AFF;background-color:#338AFF}.by-checkbox.disabled{color:#C1C1C1}.by-checkbox.disabled .bui-checkbox-input-wrapper{border-color:#F0F0F0;background-color:#FAFAFA}.by-checkbox.disabled.checked .bui-checkbox-input-wrapper,.by-checkbox.disabled.checked .secondary .bui-checkbox-input-wrapper,.by-checkbox.disabled.indeterminate .bui-checkbox-input-wrapper{background-color:#a1caff;border-color:#a1caff}.by-checkbox.disabled .bui-icon-check{cursor:not-allowed}.by-checkbox.disabled.checked .bui-checkbox-input-wrapper .bui-icon-check{fill:#FFF}.by-checkbox.disabled.indeterminate .bui-checkbox-input-wrapper:after{content:'';position:absolute;height:2px;width:10px;border-radius:2px;background:#ebf3ff;top:6px;left:2px}.by-checkbox.disabled.indeterminate.secondary .bui-checkbox-input-wrapper{border-color:#a1caff;background-color:#a1caff}.by-checkbox.disabled.indeterminate.secondary .bui-checkbox-input-wrapper:after{background:#ebf3ff}.by-checkbox-rtl{direction:rtl}.by-checkbox-rtl+.by-checkbox{margin-right:32px;margin-left:0}.by-checkbox-rtl .bui-checkbox-input-wrapper{margin-right:0;margin-left:8px}";
styleInject(css_248z$5);

var css_248z$4 = "@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.bui-popper{z-index:999}.bui-popper .bui-option-list-wrapper{border-radius:2px;margin-top:-1px;margin-bottom:2px;max-height:184px;overflow:hidden;background-color:#FFF}.bui-popper .bui-option-list-wrapper::-webkit-scrollbar-track-piece{background-color:transparent}.bui-popper .bui-option-list-wrapper::-webkit-scrollbar{width:9px;height:9px}.bui-popper .bui-option-list-wrapper::-webkit-scrollbar-thumb{background-color:#e8e8e8;border-radius:4px;background-clip:padding-box;min-height:28px}.bui-popper .bui-option-list-wrapper::-webkit-scrollbar-thumb:hover{background-color:#ddd}";
styleInject(css_248z$4);

var css_248z$3 = ".by-tag-default,.by-tag-default:hover{background-color:#F0F0F0;border-color:#F0F0F0}.by-tag-default .bui-tag-close,.by-tag-default:hover .bui-tag-close,.by-tag-light .bui-tag-close,.by-tag-light:hover .bui-tag-close{fill:#999}@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.by-tag{display:inline-block;font-size:14px;line-height:22px;padding:1px 8px;border-radius:4px;border-width:1px;border-style:solid;position:relative;background-clip:padding-box}.by-tag-default{color:#333}.by-tag-default:hover{color:#666}.by-tag-light,.by-tag-light:hover{background-color:#FFF;border-color:#FFF}.by-tag-light{color:#333}.by-tag-light:hover{color:#666}.by-tag-danger .bui-tag-close,.by-tag-danger:hover .bui-tag-close,.by-tag-info .bui-tag-close,.by-tag-info:hover .bui-tag-close,.by-tag-primary .bui-tag-close,.by-tag-primary:hover .bui-tag-close,.by-tag-success .bui-tag-close,.by-tag-success:hover .bui-tag-close,.by-tag-warning .bui-tag-close,.by-tag-warning:hover .bui-tag-close{fill:#FFF}.by-tag-info{background-color:#338AFF;color:#FFF;border-color:#338AFF}.by-tag-info:hover{background-color:#58a0ff;color:#FFF;border-color:#58a0ff}.by-tag-primary{background-color:#338AFF;color:#FFF;border-color:#338AFF}.by-tag-primary:hover{background-color:#58a0ff;color:#FFF;border-color:#58a0ff}.by-tag-success{background-color:#6ABF40;color:#FFF;border-color:#6ABF40}.by-tag-success:hover{background-color:#86cb63;color:#FFF;border-color:#86cb63}.by-tag-warning{background-color:#FFA900;color:#FFF;border-color:#FFA900}.by-tag-warning:hover{background-color:#ffba2f;color:#FFF;border-color:#ffba2f}.by-tag-danger{background-color:#F65656;color:#FFF;border-color:#F65656}.by-tag-danger:hover{background-color:#f87474;color:#FFF;border-color:#f87474}.by-tag-disabled{cursor:not-allowed}.by-tag-disabled.by-tag-default{background-color:#FAFAFA;color:#C1C1C1;border-color:#FAFAFA}.by-tag-disabled.by-tag-info,.by-tag-disabled.by-tag-primary{background-color:#a1caff;border-color:#a1caff;color:#FFF}.by-tag-disabled.by-tag-default .bui-tag-close{fill:#999}.by-tag-disabled.by-tag-danger .bui-tag-close,.by-tag-disabled.by-tag-info .bui-tag-close,.by-tag-disabled.by-tag-primary .bui-tag-close,.by-tag-disabled.by-tag-success .bui-tag-close,.by-tag-disabled.by-tag-warning .bui-tag-close{fill:#FFF}.by-tag-disabled.by-tag-success{background-color:#bce2a9;color:#FFF;border-color:#bce2a9}.by-tag-disabled.by-tag-warning{background-color:#ffd98d;color:#FFF;border-color:#ffd98d}.by-tag-disabled.by-tag-danger{background-color:#fbb0b0;color:#FFF;border-color:#fbb0b0}.by-tag-disabled.by-tag-light{background-color:#FAFAFA;color:#C1C1C1;border-color:#F0F0F0}.by-tag-disabled.by-tag-light .bui-tag-close{fill:#C1C1C1}.by-tag-plain.by-tag-default .bui-tag-close,.by-tag-plain.by-tag-default:hover .bui-tag-close{fill:#999}.by-tag-disabled.by-tag-closable .bui-tag-close{cursor:not-allowed}.by-tag-sm{padding:0 8px;font-size:12px;line-height:20px}.by-tag-lg{padding:3px 8px}.by-tag-closable{padding-right:25px}.by-tag-closable .bui-tag-close{position:absolute;top:50%;-webkit-transform:translateY(-50%);transform:translateY(-50%);right:8px;cursor:pointer}.by-tag-closable.by-tag-sm .bui-tag-close{width:14px;height:14px}.by-tag-closable.by-tag-lg .bui-tag-close,.by-tag-closable.by-tag-md .bui-tag-close{width:16px;height:16px}.by-tag-plain.by-tag-default{background-color:#F5F5F5;color:#333;border-color:#E0E0E0}.by-tag-plain.by-tag-default:hover{background-color:#F5F5F5;color:#666;border-color:#E0E0E0}.by-tag-plain.by-tag-info,.by-tag-plain.by-tag-info:hover,.by-tag-plain.by-tag-primary,.by-tag-plain.by-tag-primary:hover{background-color:#ebf3ff;border-color:#a1caff}.by-tag-plain.by-tag-info{color:#338AFF}.by-tag-plain.by-tag-info .bui-tag-close{fill:#338AFF}.by-tag-plain.by-tag-info:hover{color:#58a0ff}.by-tag-plain.by-tag-info:hover .bui-tag-close{fill:#58a0ff}.by-tag-plain.by-tag-primary{color:#338AFF}.by-tag-plain.by-tag-primary .bui-tag-close{fill:#338AFF}.by-tag-plain.by-tag-primary:hover{color:#58a0ff}.by-tag-plain.by-tag-primary:hover .bui-tag-close{fill:#58a0ff}.by-tag-plain.by-tag-success{background-color:#f3faf0;color:#6ABF40;border-color:#bce2a9}.by-tag-plain.by-tag-success .bui-tag-close{fill:#6ABF40}.by-tag-plain.by-tag-success:hover{background-color:#f3faf0;color:#86cb63;border-color:#bce2a9}.by-tag-plain.by-tag-success:hover .bui-tag-close{fill:#86cb63}.by-tag-plain.by-tag-warning{background-color:#fff8eb;color:#FFA900;border-color:#ffd98d}.by-tag-plain.by-tag-warning .bui-tag-close{fill:#FFA900}.by-tag-plain.by-tag-warning:hover{background-color:#fff8eb;color:#ffba2f;border-color:#ffd98d}.by-tag-plain.by-tag-warning:hover .bui-tag-close{fill:#ffba2f}.by-tag-plain.by-tag-danger{background-color:#feecec;color:#F65656;border-color:#fbb0b0}.by-tag-plain.by-tag-danger .bui-tag-close{fill:#F65656}.by-tag-plain.by-tag-danger:hover{background-color:#feecec;color:#f87474;border-color:#fbb0b0}.by-tag-plain.by-tag-danger:hover .bui-tag-close{fill:#f87474}.by-tag-plain.by-tag-disabled.by-tag-default{background-color:#FAFAFA;color:#C1C1C1;border-color:#F0F0F0}.by-tag-plain.by-tag-disabled.by-tag-info,.by-tag-plain.by-tag-disabled.by-tag-primary{background-color:#ebf3ff;color:#a1caff;border-color:#a1caff}.by-tag-plain.by-tag-disabled.by-tag-default .bui-tag-close{fill:#C1C1C1}.by-tag-plain.by-tag-disabled.by-tag-info .bui-tag-close,.by-tag-plain.by-tag-disabled.by-tag-primary .bui-tag-close{fill:#a1caff}.by-tag-plain.by-tag-disabled.by-tag-success{background-color:#f3faf0;color:#bce2a9;border-color:#bce2a9}.by-tag-plain.by-tag-disabled.by-tag-success .bui-tag-close{fill:#bce2a9}.by-tag-plain.by-tag-disabled.by-tag-warning{background-color:#fff8eb;color:#ffd98d;border-color:#ffd98d}.by-tag-plain.by-tag-disabled.by-tag-warning .bui-tag-close{fill:#ffd98d}.by-tag-plain.by-tag-disabled.by-tag-danger{background-color:#feecec;color:#fbb0b0;border-color:#fbb0b0}.by-tag-plain.by-tag-disabled.by-tag-danger .bui-tag-close{fill:#fbb0b0}.by-tag-plain.by-tag-disabled.bui-tag-closable .bui-tag-close{cursor:not-allowed}.by-tag-rtl{direction:rtl}.by-tag-rtl.by-tag-closable{padding-left:25px;padding-right:8px}.by-tag-rtl.by-tag-closable .bui-tag-close{left:8px;right:auto}";
styleInject(css_248z$3);

var css_248z$2 = ".by-cascader,.by-cascader .bui-select-wrapper,.by-select,.by-select .bui-select-wrapper{width:100%;position:relative;cursor:pointer}@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.by-cascader,.by-select{display:inline-block;vertical-align:bottom;border-radius:4px}.by-cascader .bui-select-wrapper-multiple,.by-select .bui-select-wrapper-multiple{display:-webkit-box;display:-ms-flexbox;display:flex;-ms-flex-wrap:wrap;flex-wrap:wrap;-webkit-box-align:center;-ms-flex-align:center;align-items:center;border:1px solid #E0E0E0;border-radius:4px;padding-right:32px;padding-left:8px;position:relative}.by-cascader .bui-select-wrapper-multiple:hover,.by-select .bui-select-wrapper-multiple:hover{border-color:#338AFF;z-index:2}.by-cascader .bui-select-wrapper-multiple.bui-select-wrapper-absolute,.by-select .bui-select-wrapper-multiple.bui-select-wrapper-absolute{position:absolute;top:0;left:0;right:0;padding-left:8px;padding-right:32px;border:1px solid #0672ff;background-color:#FFF;border-radius:4px;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2);margin:0;z-index:4;max-height:102px;overflow-y:auto}.by-cascader .bui-select-wrapper-multiple.bui-select-wrapper-absolute::-webkit-scrollbar-track-piece,.by-select .bui-select-wrapper-multiple.bui-select-wrapper-absolute::-webkit-scrollbar-track-piece{background-color:transparent}.by-cascader .bui-select-wrapper-multiple.bui-select-wrapper-absolute::-webkit-scrollbar,.by-select .bui-select-wrapper-multiple.bui-select-wrapper-absolute::-webkit-scrollbar{width:9px;height:9px}.by-cascader .bui-select-wrapper-multiple.bui-select-wrapper-absolute::-webkit-scrollbar-thumb,.by-select .bui-select-wrapper-multiple.bui-select-wrapper-absolute::-webkit-scrollbar-thumb{background-color:#e8e8e8;border-radius:4px;background-clip:padding-box;min-height:28px}.by-cascader .bui-select-wrapper-multiple.bui-select-wrapper-absolute::-webkit-scrollbar-thumb:hover,.by-select .bui-select-wrapper-multiple.bui-select-wrapper-absolute::-webkit-scrollbar-thumb:hover{background-color:#ddd}.by-cascader .bui-select-wrapper-multiple.bui-select-wrapper-absolute .bui-select-tag,.by-select .bui-select-wrapper-multiple.bui-select-wrapper-absolute .bui-select-tag{max-width:100%}.by-cascader .bui-select-wrapper-multiple .bui-select-tag,.by-select .bui-select-wrapper-multiple .bui-select-tag{margin-bottom:3px;margin-left:4px;margin-top:3px;max-width:calc(100% - 45px);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.by-cascader .bui-select-wrapper-multiple .bui-selected-label,.by-select .bui-select-wrapper-multiple .bui-selected-label{color:#333;margin-left:4px;background-color:#F0F0F0;text-align:center;border-radius:13px;padding:0 6px;position:absolute;top:50%;-webkit-transform:translateY(-50%);transform:translateY(-50%);right:32px;font-size:14px}.by-cascader .bui-select-wrapper-multiple .bui-selected-label-ellipsis,.by-select .bui-select-wrapper-multiple .bui-selected-label-ellipsis{display:inline-block}.by-cascader .bui-select-wrapper-multiple .bui-selected-label-ellipsis span,.by-select .bui-select-wrapper-multiple .bui-selected-label-ellipsis span{width:2px;height:2px;margin:1px;background:#999;display:inline-block;vertical-align:2px;border-radius:50%}.by-cascader .bui-select-wrapper-multiple .bui-select-filter,.by-select .bui-select-wrapper-multiple .bui-select-filter{display:inline-block;width:100%;border:none;outline:0;padding-top:6px;padding-bottom:6px;padding-left:4px;font-size:14px}.by-cascader .bui-select-wrapper-multiple-collapse,.by-select .bui-select-wrapper-multiple-collapse{height:34px;overflow:hidden}.by-cascader .bui-select-wrapper .bui-input,.by-select .bui-select-wrapper .bui-input{width:100%;position:relative;display:inline-block;padding:5px 32px 5px 12px;border-radius:4px;color:#333;font-size:14px;line-height:22px;font-weight:400;background-color:#FFF;border-width:1px;border-style:solid;border-color:#E0E0E0;outline:0;-webkit-box-sizing:border-box;box-sizing:border-box}.by-cascader .bui-select-wrapper .bui-input::-moz-placeholder,.by-select .bui-select-wrapper .bui-input::-moz-placeholder{color:#C1C1C1;opacity:1}.by-cascader .bui-select-wrapper .bui-input:-ms-input-placeholder,.by-select .bui-select-wrapper .bui-input:-ms-input-placeholder{color:#C1C1C1}.by-cascader .bui-select-wrapper .bui-input::-webkit-input-placeholder,.by-select .bui-select-wrapper .bui-input::-webkit-input-placeholder{color:#C1C1C1}.by-cascader .bui-select-wrapper .bui-input:hover,.by-select .bui-select-wrapper .bui-input:hover{border-color:#338AFF;z-index:2;background-color:#FFF}.by-cascader .bui-select-wrapper .bui-input.active,.by-cascader .bui-select-wrapper .bui-input:active,.by-select .bui-select-wrapper .bui-input.active,.by-select .bui-select-wrapper .bui-input:active{border-color:#0672ff;z-index:2;background-color:#FFF}.by-cascader .bui-select-wrapper .bui-input:focus,.by-select .bui-select-wrapper .bui-input:focus{border-color:#338AFF;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2);z-index:2;background-color:#FFF}.by-cascader .bui-select-wrapper .bui-input[readonly],.by-select .bui-select-wrapper .bui-input[readonly]{cursor:pointer}.by-cascader .bui-select-wrapper .bui-input-disabled,.by-cascader .bui-select-wrapper .bui-input[disabled],.by-select .bui-select-wrapper .bui-input-disabled,.by-select .bui-select-wrapper .bui-input[disabled]{border-color:#F0F0F0;color:#C1C1C1;cursor:not-allowed;opacity:1;background-color:#FAFAFA}.by-cascader .bui-select-wrapper .bui-input-disabled:focus,.by-cascader .bui-select-wrapper .bui-input-disabled:hover,.by-cascader .bui-select-wrapper .bui-input[disabled]:focus,.by-cascader .bui-select-wrapper .bui-input[disabled]:hover,.by-select .bui-select-wrapper .bui-input-disabled:focus,.by-select .bui-select-wrapper .bui-input-disabled:hover,.by-select .bui-select-wrapper .bui-input[disabled]:focus,.by-select .bui-select-wrapper .bui-input[disabled]:hover{border-color:#F0F0F0;-webkit-box-shadow:none;box-shadow:none}.by-cascader .bui-select-wrapper .bui-input-disabled::-moz-placeholder,.by-cascader .bui-select-wrapper .bui-input[disabled]::-moz-placeholder,.by-select .bui-select-wrapper .bui-input-disabled::-moz-placeholder,.by-select .bui-select-wrapper .bui-input[disabled]::-moz-placeholder{color:#C1C1C1;opacity:1}.by-cascader .bui-select-wrapper .bui-input-disabled:-ms-input-placeholder,.by-cascader .bui-select-wrapper .bui-input[disabled]:-ms-input-placeholder,.by-select .bui-select-wrapper .bui-input-disabled:-ms-input-placeholder,.by-select .bui-select-wrapper .bui-input[disabled]:-ms-input-placeholder{color:#C1C1C1}.by-cascader .bui-select-wrapper .bui-input-disabled::-webkit-input-placeholder,.by-cascader .bui-select-wrapper .bui-input[disabled]::-webkit-input-placeholder,.by-select .bui-select-wrapper .bui-input-disabled::-webkit-input-placeholder,.by-select .bui-select-wrapper .bui-input[disabled]::-webkit-input-placeholder{color:#C1C1C1}.by-cascader .bui-select-wrapper .bui-input-filled,.by-select .bui-select-wrapper .bui-input-filled{background-color:#F0F0F0;border-color:#F0F0F0}.by-cascader .bui-select-wrapper .bui-input-filled:hover:not([disabled]),.by-select .bui-select-wrapper .bui-input-filled:hover:not([disabled]){border-color:#EBEBEB;z-index:2;background-color:#EBEBEB}.by-cascader .bui-select-wrapper .bui-input-filled.active,.by-cascader .bui-select-wrapper .bui-input-filled:active:not([disabled]),.by-select .bui-select-wrapper .bui-input-filled.active,.by-select .bui-select-wrapper .bui-input-filled:active:not([disabled]){border-color:#E0E0E0;z-index:2;background-color:#E0E0E0}.by-cascader .bui-select-wrapper .bui-input-filled:focus:not([disabled]),.by-select .bui-select-wrapper .bui-input-filled:focus:not([disabled]){border-color:#338AFF;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2);z-index:2;background-color:#F0F0F0}.by-cascader .bui-select-wrapper.active .bui-input[readonly],.by-cascader .bui-select-wrapper.active .bui-select-wrapper-multiple,.by-select .bui-select-wrapper.active .bui-input[readonly],.by-select .bui-select-wrapper.active .bui-select-wrapper-multiple{z-index:3;border-color:#338AFF;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2)}.by-cascader .bui-select-wrapper-plain,.by-select .bui-select-wrapper-plain{line-height:0}.by-cascader .bui-select-icon,.by-select .bui-select-icon{fill:#999;position:absolute;right:12px;top:50%;height:14px;width:14px;-webkit-transform:rotate(0);transform:rotate(0);-webkit-transition:-webkit-transform .2s ease;transition:-webkit-transform .2s ease;transition:transform .2s ease;transition:transform .2s ease, -webkit-transform .2s ease;transition:transform .2s ease,-webkit-transform .2s ease;margin-top:-7px;z-index:2;cursor:pointer}.by-cascader .bui-select-icon.open,.by-select .bui-select-icon.open{z-index:3;-webkit-transform:rotate(-180deg);transform:rotate(-180deg)}.by-cascader .bui-select-icon.bui-select-clear-icon,.by-select .bui-select-icon.bui-select-clear-icon{z-index:3}.by-cascader-custom .bui-select-wrapper,.by-select-custom .bui-select-wrapper{border:1px solid #E0E0E0;padding:5px 32px 5px 12px;border-radius:4px;min-height:34px}.by-cascader-custom .bui-select-wrapper.active,.by-select-custom .bui-select-wrapper.active{border-color:#338AFF;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2)}.by-cascader-custom .bui-select-wrapper:hover,.by-select-custom .bui-select-wrapper:hover{border-color:#338AFF}.by-cascader-multiple .bui-select-wrapper,.by-select-multiple .bui-select-wrapper{min-height:34px}.by-cascader-multiple.by-cascader-sm .bui-select-wrapper,.by-cascader-multiple.by-select-sm .bui-select-wrapper,.by-select-multiple.by-cascader-sm .bui-select-wrapper,.by-select-multiple.by-select-sm .bui-select-wrapper{min-height:30px}.by-cascader-multiple.by-cascader-sm .bui-select-wrapper .bui-select-wrapper-multiple-collapse,.by-cascader-multiple.by-select-sm .bui-select-wrapper .bui-select-wrapper-multiple-collapse,.by-select-multiple.by-cascader-sm .bui-select-wrapper .bui-select-wrapper-multiple-collapse,.by-select-multiple.by-select-sm .bui-select-wrapper .bui-select-wrapper-multiple-collapse{height:30px}.by-cascader-sm .bui-select-icon,.by-cascader-small .bui-select-icon,.by-cascader-xs .bui-select-icon,.by-select-sm .bui-select-icon,.by-select-small .bui-select-icon,.by-select-xs .bui-select-icon{height:12px;width:12px;margin-top:-6px}.by-cascader .bui-selected-label,.by-select .bui-selected-label{font-size:14px;line-height:26px;min-width:26px}.by-cascader-xs .bui-select-wrapper .bui-input,.by-select-xs .bui-select-wrapper .bui-input{font-size:12px;line-height:20px;padding:2px 32px 2px 12px}.by-cascader-xs .bui-selected-label,.by-select-xs .bui-selected-label{font-size:12px;line-height:22px;min-width:22px}.by-cascader-sm .bui-select-wrapper .bui-input,.by-cascader-small .bui-select-wrapper .bui-input,.by-select-sm .bui-select-wrapper .bui-input,.by-select-small .bui-select-wrapper .bui-input{font-size:12px;line-height:20px;padding:4px 32px 4px 12px}.by-cascader-sm .bui-selected-label,.by-cascader-small .bui-selected-label,.by-select-sm .bui-selected-label,.by-select-small .bui-selected-label{font-size:12px;line-height:22px;min-width:22px}.by-cascader-large .bui-select-wrapper .bui-input,.by-cascader-lg .bui-select-wrapper .bui-input,.by-select-large .bui-select-wrapper .bui-input,.by-select-lg .bui-select-wrapper .bui-input{font-size:14px;line-height:22px;padding:7px 32px 7px 12px}.by-cascader-large .bui-selected-label,.by-cascader-lg .bui-selected-label,.by-select-large .bui-selected-label,.by-select-lg .bui-selected-label{font-size:16px;line-height:26px;min-width:26px}.by-cascader-xl .bui-select-wrapper .bui-input,.by-select-xl .bui-select-wrapper .bui-input{font-size:14px;line-height:22px;padding:9px 32px 9px 12px}.by-cascader-xl .bui-selected-label,.by-select-xl .bui-selected-label{font-size:18px;line-height:26px;min-width:26px}.by-cascader-error .bui-select-wrapper .bui-input,.by-select-error .bui-select-wrapper .bui-input{border-color:#F65656;z-index:2;background-color:#FFF}.by-cascader-error.by-select-custom .bui-select-wrapper,.by-cascader-error.by-select-multiple .bui-select-wrapper-multiple,.by-select-error.by-select-custom .bui-select-wrapper,.by-select-error.by-select-multiple .bui-select-wrapper-multiple{border-color:#F65656}.by-cascader-plain .bui-select-wrapper,.by-select-plain .bui-select-wrapper{font-size:0}.by-cascader-plain .bui-select-wrapper .bui-input,.by-select-plain .bui-select-wrapper .bui-input{border-color:transparent!important;padding-left:0;padding-right:0;width:auto;max-width:calc(100% - 22px);overflow:hidden;white-space:nowrap}.by-cascader-plain .bui-select-wrapper .bui-input.active,.by-cascader-plain .bui-select-wrapper .bui-input:active,.by-cascader-plain .bui-select-wrapper .bui-input:focus,.by-cascader-plain .bui-select-wrapper .bui-input:hover,.by-select-plain .bui-select-wrapper .bui-input.active,.by-select-plain .bui-select-wrapper .bui-input:active,.by-select-plain .bui-select-wrapper .bui-input:focus,.by-select-plain .bui-select-wrapper .bui-input:hover{color:#338AFF;border-color:transparent!important;-webkit-box-shadow:none!important;box-shadow:none!important}.by-cascader-plain .bui-select-wrapper .bui-input-disabled,.by-cascader-plain .bui-select-wrapper .bui-input[disabled],.by-select-plain .bui-select-wrapper .bui-input-disabled,.by-select-plain .bui-select-wrapper .bui-input[disabled]{color:#C1C1C1;border-color:transparent!important;background-color:transparent!important}.by-cascader-plain .bui-select-wrapper.active .bui-input,.by-cascader-plain .bui-select-wrapper:focus .bui-input,.by-cascader-plain .bui-select-wrapper:hover .bui-input,.by-select-plain .bui-select-wrapper.active .bui-input,.by-select-plain .bui-select-wrapper:focus .bui-input,.by-select-plain .bui-select-wrapper:hover .bui-input{color:#338AFF}.by-cascader-plain .bui-select-wrapper.active .bui-select-icon-plain,.by-cascader-plain .bui-select-wrapper:focus .bui-select-icon-plain,.by-cascader-plain .bui-select-wrapper:hover .bui-select-icon-plain,.by-select-plain .bui-select-wrapper.active .bui-select-icon-plain,.by-select-plain .bui-select-wrapper:focus .bui-select-icon-plain,.by-select-plain .bui-select-wrapper:hover .bui-select-icon-plain{fill:#338AFF}.by-cascader-plain .bui-select-icon-plain,.by-select-plain .bui-select-icon-plain{width:14px;height:14px;margin-bottom:10px;fill:#999;margin-left:8px}.by-cascader-plain .bui-select-icon-plain.open,.by-select-plain .bui-select-icon-plain.open{fill:#338AFF;-webkit-transform:rotate(-180deg);transform:rotate(-180deg)}.by-cascader-plain.by-select-sm .bui-select-icon-plain,.by-cascader-plain.by-select-small .bui-select-icon-plain,.by-select-plain.by-select-sm .bui-select-icon-plain,.by-select-plain.by-select-small .bui-select-icon-plain{width:12px;height:12px;margin-bottom:9px}.by-cascader-plain.by-select-custom .bui-select-wrapper,.by-select-plain.by-select-custom .bui-select-wrapper{border:none;padding:0}.by-cascader-plain.by-select-custom .bui-select-wrapper.active,.by-cascader-plain.by-select-custom .bui-select-wrapper:hover,.by-select-plain.by-select-custom .bui-select-wrapper.active,.by-select-plain.by-select-custom .bui-select-wrapper:hover{-webkit-box-shadow:none;box-shadow:none}.by-cascader-plain.by-select-custom .bui-select-icon,.by-select-plain.by-select-custom .bui-select-icon{display:none}.by-cascader-disabled,.by-select-disabled{cursor:not-allowed}.by-cascader-disabled.by-cascader-multiple .bui-select-wrapper-multiple,.by-cascader-disabled.by-select-custom .bui-select-wrapper,.by-cascader-disabled.by-select-multiple .bui-select-wrapper-multiple,.by-select-disabled.by-cascader-multiple .bui-select-wrapper-multiple,.by-select-disabled.by-select-custom .bui-select-wrapper,.by-select-disabled.by-select-multiple .bui-select-wrapper-multiple{cursor:not-allowed;background-color:#FAFAFA;border-color:#F0F0F0}.by-cascader-disabled.by-cascader-multiple .bui-select-wrapper-multiple:hover,.by-cascader-disabled.by-select-custom .bui-select-wrapper:hover,.by-cascader-disabled.by-select-multiple .bui-select-wrapper-multiple:hover,.by-select-disabled.by-cascader-multiple .bui-select-wrapper-multiple:hover,.by-select-disabled.by-select-custom .bui-select-wrapper:hover,.by-select-disabled.by-select-multiple .bui-select-wrapper-multiple:hover{background-color:#FAFAFA;border-color:#F0F0F0;z-index:0}.by-cascader-disabled.by-cascader-multiple .bui-select-wrapper-multiple .bui-selected-label,.by-cascader-disabled.by-select-custom .bui-select-wrapper .bui-selected-label,.by-cascader-disabled.by-select-multiple .bui-select-wrapper-multiple .bui-selected-label,.by-select-disabled.by-cascader-multiple .bui-select-wrapper-multiple .bui-selected-label,.by-select-disabled.by-select-custom .bui-select-wrapper .bui-selected-label,.by-select-disabled.by-select-multiple .bui-select-wrapper-multiple .bui-selected-label{color:#C1C1C1;background-color:#FAFAFA;border:1px solid #F0F0F0}.by-cascader-disabled.by-cascader-multiple .bui-select-wrapper-multiple .bui-selected-label-ellipsis *,.by-cascader-disabled.by-select-custom .bui-select-wrapper .bui-selected-label-ellipsis *,.by-cascader-disabled.by-select-multiple .bui-select-wrapper-multiple .bui-selected-label-ellipsis *,.by-select-disabled.by-cascader-multiple .bui-select-wrapper-multiple .bui-selected-label-ellipsis *,.by-select-disabled.by-select-custom .bui-select-wrapper .bui-selected-label-ellipsis *,.by-select-disabled.by-select-multiple .bui-select-wrapper-multiple .bui-selected-label-ellipsis *{background-color:#C1C1C1}.by-cascader-disabled .bui-selected-label,.by-select-disabled .bui-selected-label{line-height:24px}.by-cascader-disabled.by-cascader-sm .bui-selected-label,.by-cascader-disabled.by-select-sm .bui-selected-label,.by-select-disabled.by-cascader-sm .bui-selected-label,.by-select-disabled.by-select-sm .bui-selected-label{line-height:20px}.by-cascader-disabled .bui-select-icon,.by-cascader-disabled .bui-select-icon-plain,.by-select-disabled .bui-select-icon,.by-select-disabled .bui-select-icon-plain{cursor:not-allowed;fill:#D6D6D6}.by-cascader-disabled.by-select-plain .bui-select-wrapper,.by-select-disabled.by-select-plain .bui-select-wrapper{cursor:not-allowed}.by-cascader-disabled.by-select-plain .bui-select-wrapper .bui-input,.by-select-disabled.by-select-plain .bui-select-wrapper .bui-input{background-color:transparent!important;color:#C1C1C1}.by-cascader-disabled.by-select-plain .bui-select-wrapper .bui-input:focus,.by-cascader-disabled.by-select-plain .bui-select-wrapper .bui-input:hover,.by-select-disabled.by-select-plain .bui-select-wrapper .bui-input:focus,.by-select-disabled.by-select-plain .bui-select-wrapper .bui-input:hover{border-color:transparent}.by-cascader-disabled.by-select-plain .bui-select-wrapper.active .bui-select-icon-plain,.by-cascader-disabled.by-select-plain .bui-select-wrapper:focus .bui-select-icon-plain,.by-cascader-disabled.by-select-plain .bui-select-wrapper:hover .bui-select-icon-plain,.by-select-disabled.by-select-plain .bui-select-wrapper.active .bui-select-icon-plain,.by-select-disabled.by-select-plain .bui-select-wrapper:focus .bui-select-icon-plain,.by-select-disabled.by-select-plain .bui-select-wrapper:hover .bui-select-icon-plain{cursor:not-allowed;fill:#F0F0F0}.by-cascader-filled .bui-select-wrapper-multiple,.by-select-filled .bui-select-wrapper-multiple{background-color:#F0F0F0;border-color:#F0F0F0}.by-cascader-filled .bui-select-wrapper-multiple:hover,.by-select-filled .bui-select-wrapper-multiple:hover{background-color:#EBEBEB;border-color:#EBEBEB}.by-cascader-filled .bui-select-wrapper-multiple.active,.by-cascader-filled .bui-select-wrapper-multiple:active,.by-select-filled .bui-select-wrapper-multiple.active,.by-select-filled .bui-select-wrapper-multiple:active{border-color:#E0E0E0;background-color:#E0E0E0}.by-cascader-filled .bui-select-wrapper-multiple:focus,.by-select-filled .bui-select-wrapper-multiple:focus{border-color:#338AFF;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2);background-color:#F0F0F0}.by-cascader-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute,.by-select-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute{background-color:#F0F0F0;border-color:#F0F0F0}.by-cascader-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute:hover,.by-select-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute:hover{background-color:#EBEBEB;border-color:#EBEBEB}.by-cascader-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute.active,.by-cascader-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute:active,.by-select-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute.active,.by-select-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute:active{border-color:#E0E0E0;background-color:#E0E0E0}.by-cascader-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute:focus,.by-select-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute:focus{border-color:#338AFF;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2);background-color:#F0F0F0}.by-cascader-filled .bui-select-wrapper .bui-selected-label,.by-select-filled .bui-select-wrapper .bui-selected-label{color:#333;background-color:#FFF}.by-cascader-active.by-cascader-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute,.by-cascader-active.by-select-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute,.by-select-active.by-cascader-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute,.by-select-active.by-select-filled .bui-select-wrapper-multiple.bui-select-wrapper-absolute{border-color:#338AFF;-webkit-box-shadow:0 0 0 2px rgba(51,138,255,.2);box-shadow:0 0 0 2px rgba(51,138,255,.2);background-color:#F0F0F0}.bui-select-popper{margin-top:4px;margin-bottom:4px;-webkit-box-shadow:0 2px 6px 0 rgba(0,0,0,.15);box-shadow:0 2px 6px 0 rgba(0,0,0,.15);border-radius:4px;z-index:999;background-color:#FFF;overflow:auto}.bui-select-popper .bui-select-option-wrapper{max-height:192px;overflow-y:auto;overflow-x:hidden}.bui-select-popper .bui-select-option-wrapper::-webkit-scrollbar-track-piece{background-color:transparent}.bui-select-popper .bui-select-option-wrapper::-webkit-scrollbar{width:9px;height:9px}.bui-select-popper .bui-select-option-wrapper::-webkit-scrollbar-thumb{background-color:#e8e8e8;border-radius:4px;background-clip:padding-box;min-height:28px}.bui-select-popper .bui-select-option-wrapper::-webkit-scrollbar-thumb:hover{background-color:#ddd}.bui-select-popper .bui-select-option .bui-option-match-none{padding:62px 12px;color:#C1C1C1;text-align:center;font-size:12px;line-height:20px}.bui-select-popper .bui-select-option .bui-option-match-none .bui-icon-empty{fill:#D6D6D6;width:37px;height:37px;display:block;margin:0 auto 10px}.bui-select-popper .bui-select-option{border-radius:4px}.bui-select-popper .bui-select-option .bui-option-filter{border-style:solid;border-color:transparent transparent #E0E0E0;border-width:0 0 1px}.bui-select-popper .bui-select-option .bui-option-filter .by-input .bui-input{border-color:transparent;outline:0}.bui-select-popper .bui-select-option .bui-option-filter .by-input .bui-input:active,.bui-select-popper .bui-select-option .bui-option-filter .by-input .bui-input:focus{border-color:transparent;-webkit-box-shadow:none;box-shadow:none}.bui-select-popper .bui-select-option .bui-option-filter .by-input .bui-input-group-addon{border-color:transparent;background-color:transparent}.bui-select-popper .bui-select-option .bui-option-filter .by-input .bui-input-group-addon::before{content:\"\";position:absolute;top:8px;bottom:8px;left:0;width:1px;background-color:#EBEBEB}.by-option{position:relative;padding:6px 12px;font-size:14px;line-height:22px;color:#333;cursor:pointer}.by-option:last-child{margin-bottom:6px}.by-option:hover{background-color:#F0F0F0}.by-option .bui-option-checkbox{display:none;position:absolute;top:50%;left:12px;margin-top:-11px}.by-option-ingroup{padding:6px 28px}.by-option-ingroup:last-child{margin-bottom:0}.by-option-selected{color:#338AFF}.by-option-disabled{color:#C1C1C1;cursor:not-allowed}.by-option-disabled:hover{background-color:transparent}.by-option-sm,.by-option-small,.by-option-xs{font-size:12px;line-height:20px;padding:5px 12px}.by-option-multiple{padding-left:36px}.by-option-multiple .bui-option-checkbox{display:inline-block}.by-option-multiple.by-option-ingroup{padding-left:50px}.by-option-multiple.by-option-ingroup .bui-option-checkbox{left:28px}.by-optgroup .bui-optgroup-label{font-size:14px;padding:6px 0 6px 5px;margin:0;color:#999}.by-optgroup-sm .bui-optgroup-label,.by-optgroup-small .bui-optgroup-label,.by-optgroup-xs .bui-optgroup-label{font-size:12px;padding:5px}.by-select-rtl{direction:rtl}.by-select-rtl .bui-select-icon{right:auto;left:12px}.by-select-rtl .bui-select-wrapper .bui-input{padding-right:12px;padding-left:32px}.by-select-rtl .bui-select-wrapper-multiple,.by-select-rtl .bui-select-wrapper-multiple.bui-select-wrapper-absolute{padding-left:32px;padding-right:8px}.by-select-rtl .bui-select-wrapper .bui-selected-label{right:auto;left:32px}.by-option-rtl{direction:rtl}.by-option-rtl .bui-option-checkbox{left:auto;right:12px}.by-option-rtl.by-option-multiple{padding-left:12px;padding-right:36px}.by-option-rtl.by-option-multiple.by-option-ingroup{padding-right:50px}.by-option-rtl.by-option-multiple.by-option-ingroup .bui-option-checkbox{right:28px}.by-optgroup-rtl{direction:rtl}";
styleInject(css_248z$2);

var _default = {
  bind: function bind(el, binding, vnode) {
    function documentHandler(e) {
      if (el && el.contains && el.contains(e.target)) {
        return false;
      }
      if (binding.value || binding.expression) {
        binding.value(e);
      }
    }
    var modifiers = binding.modifiers || {};
    var capture = modifiers.capture || false;

    el.__vueClickOutside__ = documentHandler;
    el.__vueCapture__ = capture;
    document.addEventListener('click', documentHandler, capture);
  },
  update: function update() {},
  unbind: function unbind(el, binding) {
    document.removeEventListener('click', el.__vueClickOutside__, el.__vueCapture__);
    delete el.__vueClickOutside__;
    delete el.__vueCapture__;
  }
};

var clickoutside = /*#__PURE__*/Object.defineProperty({
	default: _default
}, '__esModule', {value: true});

var checkbox = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 93);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 3:
/***/ (function(module, exports) {

module.exports = locale;

/***/ }),

/***/ 54:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _locale = __webpack_require__(3);

var _locale2 = _interopRequireDefault(_locale);

var _base = __webpack_require__(1);

var _emitter = __webpack_require__(6);

var _emitter2 = _interopRequireDefault(_emitter);

var _icon = __webpack_require__(7);

var _icon2 = _interopRequireDefault(_icon);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

exports.default = {
  name: _base.PREFIX + 'checkbox',

  components: {
    BytedIcon: _icon2.default
  },

  mixins: [_emitter2.default, _locale2.default],

  props: {
    value: {
      type: [Boolean, Array],
      default: function _default() {
        return [];
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    val: {
      type: [Boolean, String, Number],
      default: false
    },
    indeterminate: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: '',
      validator: function validator(value) {
        return ['secondary', ''].indexOf(value) > -1;
      }
    },
    // 内部使用，暂时不对外,禁止 dispatch form-item.change
    validateEvent: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    baseClass: function baseClass() {
      var classArr = [_base.CSS_PREFIX + 'checkbox'];
      if (this.indeterminate) {
        classArr.push('indeterminate');
      }
      if (this.innerChecked) {
        classArr.push('checked');
      }
      if (this.isDisabled) {
        classArr.push('disabled');
      }
      if (Object.keys(this.$slots).length === 0) {
        classArr.push('empty');
      }
      if (this.isRTL) {
        classArr.push(_base.CSS_PREFIX + 'checkbox-rtl');
      }
      if (this.type) {
        classArr.push(this.type);
      }
      return classArr;
    },
    checkboxGroup: function checkboxGroup() {
      var parent = this.$parent;
      while (parent) {
        if (parent.$options.name === _base.PREFIX + 'checkbox-group') {
          return parent;
        }
        parent = parent.$parent;
      }
      return null;
    },
    isGroup: function isGroup() {
      return this.checkboxGroup !== null;
    },
    isDisabled: function isDisabled() {
      return this.isGroup && this.checkboxGroup.disabled || this.disabled;
    },
    innerChecked: function innerChecked() {
      var isGroup = this.isGroup,
          checkboxGroup = this.checkboxGroup,
          val = this.val,
          value = this.value;

      if (isGroup) {
        return checkboxGroup.value.indexOf(val) !== -1;
      }
      if (Array.isArray(value)) {
        return value.indexOf(val) !== -1;
      }
      return !!value;
    }
  },
  watch: {
    value: function value(newValue) {
      if (!this.isGroup && this.validateEvent) {
        this.dispatch(_base.PREFIX + 'form-item', 'bui.form.change', [newValue]);
      }
    }
  },
  mounted: function mounted() {},

  methods: {
    handleChange: function handleChange(e) {
      if (this.isDisabled) {
        return false;
      }
      var checked = e.target.checked;
      if (Array.isArray(this.value)) {
        // 多个 checkbox 共一个 v-model
        var valueCopy = this.value.slice(0);
        if (checked) {
          valueCopy.push(this.val);
        } else {
          valueCopy.splice(valueCopy.indexOf(this.val), 1);
        }
        this.$emit('input', valueCopy);
        this.$emit('change', valueCopy);
      } else {
        // 单个checkbox
        this.$emit('input', checked);
        this.$emit('change', checked);
      }
      if (this.isGroup) {
        this.dispatch(_base.PREFIX + 'checkbox-group', 'bui.checkbox.change', {
          val: this.val,
          checked: checked
        });
      }
    },
    isArray: function isArray(arr) {
      return Object.prototype.toString.call(arr) === '[object Array]';
    }
  }
};

/***/ }),

/***/ 6:
/***/ (function(module, exports) {

module.exports = emitter;

/***/ }),

/***/ 7:
/***/ (function(module, exports) {

module.exports = icon;

/***/ }),

/***/ 93:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _checkbox = __webpack_require__(94);

var _checkbox2 = _interopRequireDefault(_checkbox);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_checkbox2.default.install = function (Vue) {
  Vue.component(_checkbox2.default.name, _checkbox2.default);
};

exports.default = _checkbox2.default;

/***/ }),

/***/ 94:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_checkbox_vue__ = __webpack_require__(54);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_checkbox_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_checkbox_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_checkbox_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_checkbox_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_51d65e06_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_checkbox_vue__ = __webpack_require__(95);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_checkbox_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_51d65e06_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_checkbox_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_51d65e06_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_checkbox_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 95:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{class:_vm.baseClass},[_c('label',[_c('div',{staticClass:"bui-checkbox-input-wrapper"},[_c('input',{staticClass:"bui-checkbox-input",attrs:{"type":"checkbox"},domProps:{"value":_vm.val,"checked":_vm.innerChecked},on:{"change":_vm.handleChange}}),_vm._v(" "),_c('byted-icon',{attrs:{"name":"check","width":"14px","height":"14px"}})],1),_c('span',{staticClass:"bui-checkbox-input-label"},[_vm._t("default")],2)])])};
var staticRenderFns = [];


/***/ })

/******/ });
});

var tag = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 482);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 225:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _helper = __webpack_require__(4);

var _icon = __webpack_require__(7);

var _icon2 = _interopRequireDefault(_icon);

var _locale = __webpack_require__(3);

var _locale2 = _interopRequireDefault(_locale);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

//
//
//
//
//
//
//
//
//
//
//
//

exports.default = {
  name: _base.PREFIX + 'tag',
  components: {
    BytedIcon: _icon2.default
  },
  mixins: [_locale2.default],
  props: {
    text: {
      type: String,
      default: ''
    },
    closable: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'default',
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['default', 'success', 'primary', 'info', 'warning', 'danger', 'light']);
      }
    },
    color: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['sm', 'md', 'lg']);
      }
    },
    plain: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    basicClass: function basicClass() {
      var classArr = [_base.CSS_PREFIX + 'tag', _base.CSS_PREFIX + 'tag-' + this.type, _base.CSS_PREFIX + 'tag-' + this.size];
      if (this.closable) {
        classArr.push(_base.CSS_PREFIX + 'tag-closable');
      }
      if (this.disabled) {
        classArr.push(_base.CSS_PREFIX + 'tag-disabled');
      }
      if (this.plain) {
        classArr.push(_base.CSS_PREFIX + 'tag-plain');
      }
      if (this.isRTL) {
        classArr.push(_base.CSS_PREFIX + 'tag-rtl');
      }
      if (this.filled) {
        classArr.push(_base.CSS_PREFIX + 'tag-filled');
      }
      return classArr;
    }
  },

  methods: {
    handleClose: function handleClose(event) {
      if (this.disabled) return;
      this.$emit('close', event);
    }
  }
};

/***/ }),

/***/ 3:
/***/ (function(module, exports) {

module.exports = locale;

/***/ }),

/***/ 4:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isOneOf = isOneOf;
function isOneOf(value, validList) {
  for (var i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true;
    }
  }
  return false;
}

/***/ }),

/***/ 482:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _tag = __webpack_require__(483);

var _tag2 = _interopRequireDefault(_tag);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_tag2.default.install = function (Vue) {
  Vue.component(_tag2.default.name, _tag2.default);
};

exports.default = _tag2.default;

/***/ }),

/***/ 483:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tag_vue__ = __webpack_require__(225);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tag_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tag_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tag_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tag_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_99d288ac_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_tag_vue__ = __webpack_require__(484);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_tag_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_99d288ac_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_tag_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_99d288ac_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_tag_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 484:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('span',{class:_vm.basicClass,style:({backgroundColor: _vm.color})},[_vm._t("default"),_vm._v(" "),(_vm.closable)?_c('byted-icon',{staticClass:"bui-tag-close",attrs:{"name":"close"},on:{"click":function($event){$event.stopPropagation();return _vm.handleClose($event)}}}):_vm._e()],2)};
var staticRenderFns = [];


/***/ }),

/***/ 7:
/***/ (function(module, exports) {

module.exports = icon;

/***/ })

/******/ });
});

var select = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 426);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 10:
/***/ (function(module, exports) {

module.exports = clickoutside;

/***/ }),

/***/ 132:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_option_vue__ = __webpack_require__(75);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_option_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_option_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_option_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_option_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_2462dbbc_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_option_vue__ = __webpack_require__(133);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_option_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_2462dbbc_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_option_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_2462dbbc_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_option_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 133:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.visible),expression:"visible"}],class:_vm.baseClass,on:{"click":_vm.handleClick}},[(_vm.select.multiple)?_c('byted-checkbox',{staticClass:"bui-option-checkbox",attrs:{"value":_vm.isSelected,"disabled":_vm.finallyDisabled,"validate-event":false},on:{"change":_vm.handleCheckChange},nativeOn:{"click":function($event){$event.stopPropagation();}}}):_vm._e(),_vm._v(" "),_c('div',[_vm._t("default",[_vm._v(_vm._s(_vm.label))])],2)],1)};
var staticRenderFns = [];


/***/ }),

/***/ 14:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating required fields.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function required(rule, value, source, errors, options, type) {
  if (rule.required && (!source.hasOwnProperty(rule.field) || util.isEmptyValue(value, type || rule.type))) {
    errors.push(util.format(options.messages.required, rule.fullField));
  }
}

exports['default'] = required;

/***/ }),

/***/ 15:
/***/ (function(module, exports) {

module.exports = input;

/***/ }),

/***/ 16:
/***/ (function(module, exports) {

module.exports = checkbox;

/***/ }),

/***/ 18:
/***/ (function(module, exports) {

var g;

// This works in non-strict mode
g = (function() {
	return this;
})();

try {
	// This works if eval is allowed (see CSP)
	g = g || Function("return this")() || (1,eval)("this");
} catch(e) {
	// This works if the window reference is available
	if(typeof window === "object")
		g = window;
}

// g can still be undefined, but nothing to do about it...
// We return undefined, instead of nothing here, so it's
// easier to handle this case. if(!global) { ...}

module.exports = g;


/***/ }),

/***/ 2:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.convertFieldsError = convertFieldsError;
exports.format = format;
exports.isEmptyValue = isEmptyValue;
exports.isEmptyObject = isEmptyObject;
exports.asyncMap = asyncMap;
exports.complementError = complementError;
exports.deepMerge = deepMerge;
/* eslint no-console:0 */

var formatRegExp = /%[sdj%]/g;

exports.warning = function warning() {};

// don't print warning message when in production env or node runtime
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  exports.warning = function warning(type, errors) {
    if (typeof console !== 'undefined' && console.warn) {
      if (errors.every(function (e) {
        return typeof e === 'string';
      })) {
        console.warn(type, errors);
      }
    }
  };
}

function convertFieldsError(errors) {
  if (!errors || !errors.length) return null;
  var fields = {};
  errors.forEach(function (error) {
    var field = error.field;
    fields[field] = fields[field] || [];
    fields[field].push(error);
  });
  return fields;
}

function format() {
  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  var i = 1;
  var f = args[0];
  var len = args.length;
  if (typeof f === 'function') {
    return f.apply(null, args.slice(1));
  }
  if (typeof f === 'string') {
    var str = String(f).replace(formatRegExp, function (x) {
      if (x === '%%') {
        return '%';
      }
      if (i >= len) {
        return x;
      }
      switch (x) {
        case '%s':
          return String(args[i++]);
        case '%d':
          return Number(args[i++]);
        case '%j':
          try {
            return JSON.stringify(args[i++]);
          } catch (_) {
            return '[Circular]';
          }
          break;
        default:
          return x;
      }
    });
    for (var arg = args[i]; i < len; arg = args[++i]) {
      str += ' ' + arg;
    }
    return str;
  }
  return f;
}

function isNativeStringType(type) {
  return type === 'string' || type === 'url' || type === 'hex' || type === 'email' || type === 'pattern';
}

function isEmptyValue(value, type) {
  if (value === undefined || value === null) {
    return true;
  }
  if (type === 'array' && Array.isArray(value) && !value.length) {
    return true;
  }
  if (isNativeStringType(type) && typeof value === 'string' && !value) {
    return true;
  }
  return false;
}

function isEmptyObject(obj) {
  return Object.keys(obj).length === 0;
}

function asyncParallelArray(arr, func, callback) {
  var results = [];
  var total = 0;
  var arrLength = arr.length;

  function count(errors) {
    results.push.apply(results, errors);
    total++;
    if (total === arrLength) {
      callback(results);
    }
  }

  arr.forEach(function (a) {
    func(a, count);
  });
}

function asyncSerialArray(arr, func, callback) {
  var index = 0;
  var arrLength = arr.length;

  function next(errors) {
    if (errors && errors.length) {
      callback(errors);
      return;
    }
    var original = index;
    index = index + 1;
    if (original < arrLength) {
      func(arr[original], next);
    } else {
      callback([]);
    }
  }

  next([]);
}

function flattenObjArr(objArr) {
  var ret = [];
  Object.keys(objArr).forEach(function (k) {
    ret.push.apply(ret, objArr[k]);
  });
  return ret;
}

function asyncMap(objArr, option, func, callback) {
  if (option.first) {
    var flattenArr = flattenObjArr(objArr);
    return asyncSerialArray(flattenArr, func, callback);
  }
  var firstFields = option.firstFields || [];
  if (firstFields === true) {
    firstFields = Object.keys(objArr);
  }
  var objArrKeys = Object.keys(objArr);
  var objArrLength = objArrKeys.length;
  var total = 0;
  var results = [];
  var pending = new Promise(function (resolve, reject) {
    var next = function next(errors) {
      results.push.apply(results, errors);
      total++;
      if (total === objArrLength) {
        callback(results);
        return results.length ? reject({ errors: results, fields: convertFieldsError(results) }) : resolve();
      }
    };
    objArrKeys.forEach(function (key) {
      var arr = objArr[key];
      if (firstFields.indexOf(key) !== -1) {
        asyncSerialArray(arr, func, next);
      } else {
        asyncParallelArray(arr, func, next);
      }
    });
  });
  pending['catch'](function (e) {
    return e;
  });
  return pending;
}

function complementError(rule) {
  return function (oe) {
    if (oe && oe.message) {
      oe.field = oe.field || rule.fullField;
      return oe;
    }
    return {
      message: typeof oe === 'function' ? oe() : oe,
      field: oe.field || rule.fullField
    };
  };
}

function deepMerge(target, source) {
  if (source) {
    for (var s in source) {
      if (source.hasOwnProperty(s)) {
        var value = source[s];
        if ((typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object' && _typeof(target[s]) === 'object') {
          target[s] = _extends({}, target[s], value);
        } else {
          target[s] = value;
        }
      }
    }
  }
  return target;
}

/***/ }),

/***/ 202:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _name$directives$prov;

var _base = __webpack_require__(1);

var _helper = __webpack_require__(4);

var _emitter = __webpack_require__(6);

var _emitter2 = _interopRequireDefault(_emitter);

var _tag = __webpack_require__(92);

var _tag2 = _interopRequireDefault(_tag);

var _icon = __webpack_require__(7);

var _icon2 = _interopRequireDefault(_icon);

var _input = __webpack_require__(15);

var _input2 = _interopRequireDefault(_input);

var _popper = __webpack_require__(9);

var _popper2 = _interopRequireDefault(_popper);

var _locale = __webpack_require__(3);

var _locale2 = _interopRequireDefault(_locale);

var _clickoutside = __webpack_require__(10);

var _clickoutside2 = _interopRequireDefault(_clickoutside);

var _dom = __webpack_require__(8);

var _resizeObserverPolyfill = __webpack_require__(53);

var _resizeObserverPolyfill2 = _interopRequireDefault(_resizeObserverPolyfill);

var _utils = __webpack_require__(45);

var _utils2 = _interopRequireDefault(_utils);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; } //
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

exports.default = (_name$directives$prov = {
  name: _base.PREFIX + 'select',
  directives: { clickoutside: _clickoutside2.default },

  provide: function provide() {
    return {
      select: this,
      optgroup: null
    };
  },


  components: {
    BytedTag: _tag2.default,
    BytedIcon: _icon2.default,
    BytedInput: _input2.default,
    BytedPopper: _popper2.default,
    TagExpand: {
      functional: true,
      props: {
        content: Object
      },
      render: function render(h, ctx) {
        return ctx.props.content;
      }
    }
  }

}, _defineProperty(_name$directives$prov, 'directives', { clickoutside: _clickoutside2.default }), _defineProperty(_name$directives$prov, 'mixins', [_emitter2.default, _locale2.default]), _defineProperty(_name$directives$prov, 'props', {
  // 是否多选
  multiple: {
    type: Boolean,
    default: false
  },
  // 选中值
  value: {
    type: [String, Number, Array],
    required: true
  },
  // 默认填充文案
  placeholder: {
    type: String,
    default: ''
  },
  // 是否禁用
  disabled: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  },
  transfer: {
    type: Boolean,
    default: false
  },
  // 选中项是否要折叠
  collapseTags: {
    type: Boolean,
    default: false
  },
  // 是否要过滤选项
  filterable: {
    type: Boolean,
    default: false
  },
  filterPlaceholder: {
    type: String,
    default: ''
  },
  noDataText: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'md',
    validator: function validator(value) {
      return (0, _helper.isOneOf)(value, ['md', 'small', 'large', 'xs', 'sm', 'lg', 'xl']);
    }
  },
  // 搜索条件无匹配时显示的文字
  noMatchText: {
    type: [String, Function],
    default: ''
  },
  plain: {
    type: Boolean,
    default: false
  },
  status: {
    type: String,
    default: '',
    validator: function validator(value) {
      return (0, _helper.isOneOf)(value, ['', 'error']);
    }
  },
  align: {
    type: String,
    default: '',
    validator: function validator(value) {
      return (0, _helper.isOneOf)(value, ['left', 'center', 'right', '']);
    }
  },
  popperOptions: {
    type: Object,
    default: function _default() {
      return {
        modifiers: {
          flip: {
            enabled: true
          },
          hide: {
            enabled: false
          },
          preventOverflow: {
            enabled: false
          }
        }
      };
    }
  },
  popperClass: {
    type: String,
    default: ''
  },
  filterMethod: {
    type: Function,
    default: null
  },
  filled: {
    type: Boolean,
    default: false
  }

}), _defineProperty(_name$directives$prov, 'data', function data() {
  return {
    // popper show
    optionShow: false,
    // options array
    optionsArr: [],
    // popper reference
    popperRef: null,
    popperRefWidth: 0,
    // filter word
    filterLabel: '',
    selectMultipleCollapseWrapperId: 'bui-select-wrapper-multiple-collapse_' + this._uid,
    hovering: false,
    selected: [],
    // 该变量提供给业务方二次开发使用，用于渲染显示label标签，类型为function
    renderLabel: null,
    groupsArr: []
  };
}), _defineProperty(_name$directives$prov, 'computed', {
  _placeholder: function _placeholder() {
    return this.placeholder || this.t(_base.LOCALE_PREFIX + '.select.placeholder');
  },
  _filterPlaceholder: function _filterPlaceholder() {
    return this.filterPlaceholder || this.t(_base.LOCALE_PREFIX + '.select.filterPlaceholder');
  },
  _noDataText: function _noDataText() {
    return this.noDataText || this.t(_base.LOCALE_PREFIX + '.select.noDataText');
  },
  _noMatchText: function _noMatchText() {
    return this.noMatchText || this.t(_base.LOCALE_PREFIX + '.select.noMatchText');
  },
  _hasSelect: function _hasSelect() {
    if (this.selected.length > 99) {
      return '99+';
    }
    return this.selected.length;
  },
  baseClass: function baseClass() {
    var baseClass = _base.CSS_PREFIX + 'select';
    var classArr = [baseClass];
    if (this.multiple) {
      classArr.push(baseClass + '-multiple');
    }
    if (this.disabled) {
      classArr.push(baseClass + '-disabled');
    }
    var slot = this._slotContents || this.$slots || {};
    var hasCustomInput = slot['input'] != null;
    if (hasCustomInput) {
      classArr.push(baseClass + '-custom');
    }
    if (!this.multiple) {
      classArr.push(baseClass + '-' + this.size);
    } else {
      classArr.push(baseClass + '-' + this.multipleSize);
    }
    if (this.plain && !this.filled) {
      classArr.push(baseClass + '-plain');
    }
    if (this.filled) {
      classArr.push(baseClass + '-filled');
    }
    if (this.optionShow && !this.disabled) {
      classArr.push(baseClass + '-active');
    }
    if (this.status) {
      classArr.push(baseClass + '-' + this.status);
    }
    if (this.isRTL) {
      classArr.push(baseClass + '-rtl');
    }
    return classArr;
  },
  multipleSize: function multipleSize() {
    if (this.size === 'sm' || this.size === 'md') {
      return this.size;
    }
    return 'md';
  },
  wrapperClass: function wrapperClass() {
    return {
      'active': this.optionShow && !this.collapseTags
    };
  },
  isInputShow: function isInputShow() {
    if (this.multiple && Array.isArray(this.value) && this.value.length) {
      return false;
    }
    // 无边框 不显示input
    if (this.plain) {
      return false;
    }
    return true;
  },
  inputStyle: function inputStyle() {
    return {
      textAlign: this.align
    };
  },
  multipleClass: function multipleClass() {
    return {
      'bui-select-wrapper-absolute': this.multiple && this.optionShow && this.collapseTags,
      'bui-select-wrapper-multiple-collapse': this.isMultipeCollapseTagsShow
    };
  },
  isMultipleWrapperShow: function isMultipleWrapperShow() {
    return this.multiple && this.selected.length;
  },
  isMultipeCollapseTagsShow: function isMultipeCollapseTagsShow() {
    return this.multiple && this.collapseTags && this.selected.length && !this.optionShow;
  },
  collapseTagsStyle: function collapseTagsStyle() {
    var style = {};
    if (this.multiple && this.selected.length && this.isMultipeCollapseTagsShow) {
      // labelWidth + marginRight + basePaddingRight
      var space = this.selected.length > 99 ? 38 + 4 + 32 : 26 + 4 + 32;
      style[this.isRTL ? 'paddingLeft' : 'paddingRight'] = space + 'px';
    }
    return style;
  },
  showClear: function showClear() {
    var hasValue = void 0;
    if (this.multiple) {
      hasValue = Array.isArray(this.value) && this.value.length > 0;
    } else {
      hasValue = this.value !== undefined && this.value !== null && this.value !== '';
    }
    return this.clearable && !this.disabled && hasValue && this.hovering;
  },

  // 记录过滤项的个数
  filteredOptionsCount: function filteredOptionsCount() {
    return this.optionsArr.filter(function (option) {
      return option.visible;
    }).length;
  },
  inputClass: function inputClass() {
    var baseClass = 'bui-input';
    var classArr = [baseClass];
    if (this.filled) {
      classArr.push(baseClass + '-filled');
    }
    return classArr;
  },
  tagType: function tagType() {
    return this.filled ? 'light' : 'default';
  }
}), _defineProperty(_name$directives$prov, 'watch', {
  value: function value(newVal) {
    this.setSelected();
  },
  optionShow: function optionShow(newVal) {
    newVal ? this.broadcast(_base.PREFIX + 'popper', 'bui.popper.update') : this.broadcast(_base.PREFIX + 'popper', 'bui.popper.destroy');
    this.$emit('visible-change', newVal);
  },
  optionsArr: function optionsArr() {
    this.setSelected();
  },
  filterLabel: function filterLabel(val) {
    this.$emit('change:filter', val);
  }
}), _defineProperty(_name$directives$prov, 'created', function created() {
  if (this.multiple && !Array.isArray(this.value)) {
    console.error('SELECT ERROR: 请设置value值为数组');
  }
}), _defineProperty(_name$directives$prov, 'mounted', function mounted() {
  var _this = this;

  this.$nextTick(function () {
    if (!_this.$refs.popperRef) {
      return;
    }
    _this.popperRef = _this.$refs.popperRef;
    _this.popperRefRo = new _resizeObserverPolyfill2.default(function () {
      _this.popperRefWidth = _this.$refs.popperRef && _this.$refs.popperRef.clientWidth || 0;
    });
    _this.popperRefRo.observe(_this.$refs.popperRef);
  });
  this.setSelected();
  // 监测DOM变化，更新popper
  this.bindEvent();
}), _defineProperty(_name$directives$prov, 'beforeDestroy', function beforeDestroy() {
  try {
    if (this.RO) {
      this.RO.unobserve(this.$refs.multiple);
      this.RO = null;
    }
    if (this.popperRefRo) {
      this.popperRefRo.unobserve(this.$refs.popperRef);
      this.popperRefRo = null;
    }
  } catch (e) {
    this.RO = null;
    this.popperRefRo = null;
  }
}), _defineProperty(_name$directives$prov, 'methods', {
  togglePopperShow: function togglePopperShow(value) {
    this.changeOptionShow(value);
  },
  bindEvent: function bindEvent() {
    var _this2 = this;

    if (this.multiple) {
      this.$nextTick(function () {
        if (!_this2.$refs.multiple) {
          return;
        }
        _this2.RO = new _resizeObserverPolyfill2.default(_this2.handleResize);
        _this2.RO.observe(_this2.$refs.multiple);
      });
    }
  },

  // 修改popper reference,在collapseTags为true时，this.$refs.multiple为绝对定位
  changePopperRef: function changePopperRef() {
    if (this.multiple && this.collapseTags && this.selected.length && this.$refs.multiple) {
      this.popperRef = this.$refs.multiple;
      !this.optionShow && (this.$refs.multiple.scrollTop = 0);
    } else {
      this.popperRef = this.$refs.popperRef;
    }
  },

  // 获取选中项的label
  getSelectedLabel: function getSelectedLabel() {
    var option = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

    if (option) {
      return option.currentLabel;
    }
    return '';
  },
  handleWrapMouseEnter: function handleWrapMouseEnter() {
    this.hovering = true;
  },
  handleWrapMouseLeave: function handleWrapMouseLeave() {
    this.hovering = false;
  },

  // 点击箭头关闭弹框
  handleIconClick: function handleIconClick() {
    if (this.disabled) return false;
    this.changeOptionShow(!this.optionShow);
  },

  // 点击清空按钮
  handleClearClick: function handleClearClick() {
    var value = this.multiple ? [] : '';
    this.$emit('input', value);
    this.emitChange(value);
    this.$emit('clear', value);
    this.changeOptionShow(false);
  },
  changeOptionShow: function changeOptionShow(open) {
    this.optionShow = open;
    this.changePopperRef();
    this.handlePopperPositionUpdate();
  },
  handlePopperPositionUpdate: function handlePopperPositionUpdate() {
    var _this3 = this;

    if (!this.optionShow) {
      return;
    }
    if (this.isMultipleWrapperShow) {
      setTimeout(function () {
        _this3.handleResize();
      }, 0);
    } else {
      this.handleResize();
    }
  },
  clickoutsideHandle: function clickoutsideHandle(e) {
    if (!this.optionShow) {
      return;
    }
    if (!this.multiple && !this.filterable) {
      this.changeOptionShow(false);
      return;
    }
    var target = e.target;
    var parent = target.parentNode;
    var targetId = target.getAttribute('data-id');
    var parentId = parent && parent.getAttribute ? parent.getAttribute('data-id') : '';
    // 此处select-wrapper-multiple display:none
    if (targetId === this.selectMultipleCollapseWrapperId) {
      return;
    }
    while (parent && !(0, _dom.hasClass)(parent, 'bui-select-popper') && parentId !== this.selectMultipleCollapseWrapperId) {
      parent = parent.parentNode;
      parentId = parent && parent.getAttribute ? parent.getAttribute('data-id') : '';
    }
    if (!parent) {
      this.changeOptionShow(false);
      this.filterLabel = '';
      this.handleQueryChange('');
    }
  },

  // select wrapper click
  handleSelectClick: function handleSelectClick() {
    if (this.disabled) return;
    this.changeOptionShow(!this.optionShow);
  },
  setFilterFocus: function setFilterFocus() {
    this.filterable && this.$refs.filter && this.$refs.filter.focus();
  },

  // 销毁option
  handleOptionDestroy: function handleOptionDestroy(option) {
    // option销毁后，修改option的数组
    var index = this.optionsArr.indexOf(option);
    if (index > -1) {
      this.optionsArr.splice(index, 1);
    }
  },

  // 处理option选中
  handleOptionClick: function handleOptionClick(option) {
    var value = void 0;
    if (this.multiple) {
      value = this.value.slice();
      var optionIndex = this.value.indexOf(option.value);
      optionIndex > -1 ? value.splice(optionIndex, 1) : value.push(option.value);
      this.$emit('input', value);
      this.emitChange(value);
    } else {
      value = [option.value];
      this.$emit('input', option.value);
      this.emitChange(option.value);
      this.changeOptionShow(false);
    }
    this.dispatch(_base.PREFIX + 'form-item', 'bui.form.change', value);
  },
  emitChange: function emitChange(val) {
    if (!this.valueEquals(this.value, val)) {
      this.$emit('change', val);
    }
  },

  // 比较值是否完全相等
  valueEquals: function valueEquals(a, b) {
    if (a === b) return true;
    if (!(a instanceof Array)) return false;
    if (!(b instanceof Array)) return false;
    if (a.length !== b.length) return false;
    for (var i = 0; i !== a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  },

  // 设置option的选中项
  setSelected: function setSelected() {
    var _this4 = this;

    var oldSelected = this.selected;
    var selected = [];
    var curValue = this.multiple ? this.value || [] : [this.value];

    curValue.forEach(function (value, index) {
      var option = _this4.optionsArr.filter(function (item) {
        return item.value === value;
      });
      var oldOption = oldSelected.filter(function (item) {
        return item.value === value;
      });
      // 外部返回label值，暂时支持基本类型，option > old option, 兜底label显示value
      if (_this4.renderLabel && typeof _this4.renderLabel === 'function') {
        selected.push({ value: value, currentLabel: _this4.renderLabel(value) });
      } else if (option.length) {
        selected = selected.concat(option);
      } else if (oldOption.length) {
        selected = selected.concat(oldOption);
      } else if (value !== '') {
        selected.push({ value: value, currentLabel: value });
      }
      if (selected[index]) selected[index]['key'] = _utils2.default.getRandomStr(6);
    });
    this.selected = selected;

    this.changePopperRef();
  },

  // remove tag
  handleTagRemove: function handleTagRemove(curVal) {
    var value = this.value.slice();
    value.splice(value.indexOf(curVal), 1);
    this.$emit('input', value);
    this.emitChange(value);
    this.dispatch(_base.PREFIX + 'form-item', 'bui.form.change', value);
  },

  // 词搜索
  handleQueryChange: function handleQueryChange(query) {
    if (!this.filterable) {
      return;
    }
    if (this.filterMethod && typeof this.filterMethod === 'function') {
      this.filterMethod(query);
    } else {
      // 初始化过滤选项值
      this.broadcast(_base.PREFIX + 'option', 'queryChange', query.trim());
      this.broadcast(_base.PREFIX + 'optgroup', 'queryChange');
    }
  },

  // 过滤input事件
  handleFilterInput: function handleFilterInput(value) {
    this.handleQueryChange(value);
  },
  handleFilterClear: function handleFilterClear(value) {
    this.handleQueryChange(value);
  },

  // input blur
  handleInputBlur: function handleInputBlur(e) {
    this.$emit('blur', e);
  },

  // input focus
  handleInputFocus: function handleInputFocus(e) {
    this.$emit('focus', e);
  },

  // this.$refs.multiple resize,更新popper
  handleResize: function handleResize() {
    if (this.optionShow) {
      this.broadcast(_base.PREFIX + 'popper', 'bui.popper.update');
    }
  },

  // 销毁optgroup
  handleOptGroupDestroy: function handleOptGroupDestroy(optgroup) {
    var index = this.groupsArr.indexOf(optgroup);
    if (index > -1) {
      this.groupsArr.splice(optgroup, 1);
    }
  }
}), _name$directives$prov);

/***/ }),

/***/ 203:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _locale = __webpack_require__(3);

var _locale2 = _interopRequireDefault(_locale);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

//
//
//
//
//
//
//
//
//

exports.default = {
  name: _base.PREFIX + 'optgroup',

  inject: ['select'],
  mixins: [_locale2.default],
  provide: function provide() {
    return {
      optgroup: this
    };
  },


  props: {
    label: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data: function data() {
    return {
      visible: true,
      optionsArr: []
    };
  },


  computed: {
    baseClass: function baseClass() {
      var baseCls = _base.CSS_PREFIX + 'optgroup';
      var classArr = [baseCls];
      classArr.push(baseCls + '-' + this.select.size);
      if (this.isRTL) {
        classArr.push(baseCls + '-rtl');
      }
      return classArr;
    }
  },
  created: function created() {
    this.select.groupsArr.push(this);
    this.$on('queryChange', this.handleOptGroupVisible);
  },
  beforeDestroy: function beforeDestroy() {
    this.select.handleOptGroupDestroy(this);
  },

  methods: {
    // 销毁option
    handleOptionDestroy: function handleOptionDestroy(option) {
      // option销毁后，修改option的数组
      var index = this.optionsArr.indexOf(option);
      if (index > -1) {
        this.optionsArr.splice(index, 1);
      }
    },
    handleOptGroupVisible: function handleOptGroupVisible() {
      if (this.optionsArr.length === 0) {
        return this.visible = true;
      }
      this.visible = this.optionsArr.some(function (option) {
        return option.visible;
      });
    }
  }
};

/***/ }),

/***/ 23:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _util = __webpack_require__(2);

var _validator = __webpack_require__(24);

var _validator2 = _interopRequireDefault(_validator);

var _messages2 = __webpack_require__(44);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Encapsulates a validation schema.
 *
 *  @param descriptor An object declaring validation rules
 *  for this schema.
 */
function Schema(descriptor) {
  this.rules = null;
  this._messages = _messages2.messages;
  this.define(descriptor);
}

Schema.prototype = {
  messages: function messages(_messages) {
    if (_messages) {
      this._messages = (0, _util.deepMerge)((0, _messages2.newMessages)(), _messages);
    }
    return this._messages;
  },
  define: function define(rules) {
    if (!rules) {
      throw new Error('Cannot configure a schema with no rules');
    }
    if ((typeof rules === 'undefined' ? 'undefined' : _typeof(rules)) !== 'object' || Array.isArray(rules)) {
      throw new Error('Rules must be an object');
    }
    this.rules = {};
    var z = void 0;
    var item = void 0;
    for (z in rules) {
      if (rules.hasOwnProperty(z)) {
        item = rules[z];
        this.rules[z] = Array.isArray(item) ? item : [item];
      }
    }
  },
  validate: function validate(source_) {
    var _this = this;

    var o = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var oc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {};

    var source = source_;
    var options = o;
    var callback = oc;
    if (typeof options === 'function') {
      callback = options;
      options = {};
    }
    if (!this.rules || Object.keys(this.rules).length === 0) {
      if (callback) {
        callback();
      }
      return Promise.resolve();
    }

    function complete(results) {
      var i = void 0;
      var errors = [];
      var fields = {};

      function add(e) {
        if (Array.isArray(e)) {
          var _errors;

          errors = (_errors = errors).concat.apply(_errors, e);
        } else {
          errors.push(e);
        }
      }

      for (i = 0; i < results.length; i++) {
        add(results[i]);
      }
      if (!errors.length) {
        errors = null;
        fields = null;
      } else {
        fields = (0, _util.convertFieldsError)(errors);
      }
      callback(errors, fields);
    }

    if (options.messages) {
      var messages = this.messages();
      if (messages === _messages2.messages) {
        messages = (0, _messages2.newMessages)();
      }
      (0, _util.deepMerge)(messages, options.messages);
      options.messages = messages;
    } else {
      options.messages = this.messages();
    }
    var arr = void 0;
    var value = void 0;
    var series = {};
    var keys = options.keys || Object.keys(this.rules);
    keys.forEach(function (z) {
      arr = _this.rules[z];
      value = source[z];
      arr.forEach(function (r) {
        var rule = r;
        if (typeof rule.transform === 'function') {
          if (source === source_) {
            source = _extends({}, source);
          }
          value = source[z] = rule.transform(value);
        }
        if (typeof rule === 'function') {
          rule = {
            validator: rule
          };
        } else {
          rule = _extends({}, rule);
        }
        rule.validator = _this.getValidationMethod(rule);
        rule.field = z;
        rule.fullField = rule.fullField || z;
        rule.type = _this.getType(rule);
        if (!rule.validator) {
          return;
        }
        series[z] = series[z] || [];
        series[z].push({
          rule: rule,
          value: value,
          source: source,
          field: z
        });
      });
    });
    var errorFields = {};
    return (0, _util.asyncMap)(series, options, function (data, doIt) {
      var rule = data.rule;
      var deep = (rule.type === 'object' || rule.type === 'array') && (_typeof(rule.fields) === 'object' || _typeof(rule.defaultField) === 'object');
      deep = deep && (rule.required || !rule.required && data.value);
      rule.field = data.field;

      function addFullfield(key, schema) {
        return _extends({}, schema, {
          fullField: rule.fullField + '.' + key
        });
      }

      function cb() {
        var e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

        var errors = e;
        if (!Array.isArray(errors)) {
          errors = [errors];
        }
        if (!options.suppressWarning && errors.length) {
          Schema.warning('async-validator:', errors);
        }
        if (errors.length && rule.message) {
          errors = [].concat(rule.message);
        }

        errors = errors.map((0, _util.complementError)(rule));

        if (options.first && errors.length) {
          errorFields[rule.field] = 1;
          return doIt(errors);
        }
        if (!deep) {
          doIt(errors);
        } else {
          // if rule is required but the target object
          // does not exist fail at the rule level and don't
          // go deeper
          if (rule.required && !data.value) {
            if (rule.message) {
              errors = [].concat(rule.message).map((0, _util.complementError)(rule));
            } else if (options.error) {
              errors = [options.error(rule, (0, _util.format)(options.messages.required, rule.field))];
            } else {
              errors = [];
            }
            return doIt(errors);
          }

          var fieldsSchema = {};
          if (rule.defaultField) {
            for (var k in data.value) {
              if (data.value.hasOwnProperty(k)) {
                fieldsSchema[k] = rule.defaultField;
              }
            }
          }
          fieldsSchema = _extends({}, fieldsSchema, data.rule.fields);
          for (var f in fieldsSchema) {
            if (fieldsSchema.hasOwnProperty(f)) {
              var fieldSchema = Array.isArray(fieldsSchema[f]) ? fieldsSchema[f] : [fieldsSchema[f]];
              fieldsSchema[f] = fieldSchema.map(addFullfield.bind(null, f));
            }
          }
          var schema = new Schema(fieldsSchema);
          schema.messages(options.messages);
          if (data.rule.options) {
            data.rule.options.messages = options.messages;
            data.rule.options.error = options.error;
          }
          schema.validate(data.value, data.rule.options || options, function (errs) {
            var finalErrors = [];
            if (errors && errors.length) {
              finalErrors.push.apply(finalErrors, errors);
            }
            if (errs && errs.length) {
              finalErrors.push.apply(finalErrors, errs);
            }
            doIt(finalErrors.length ? finalErrors : null);
          });
        }
      }

      var res = void 0;
      if (rule.asyncValidator) {
        res = rule.asyncValidator(rule, data.value, cb, data.source, options);
      } else if (rule.validator) {
        res = rule.validator(rule, data.value, cb, data.source, options);
        if (res === true) {
          cb();
        } else if (res === false) {
          cb(rule.message || rule.field + ' fails');
        } else if (res instanceof Array) {
          cb(res);
        } else if (res instanceof Error) {
          cb(res.message);
        }
      }
      if (res && res.then) {
        res.then(function () {
          return cb();
        }, function (e) {
          return cb(e);
        });
      }
    }, function (results) {
      complete(results);
    });
  },
  getType: function getType(rule) {
    if (rule.type === undefined && rule.pattern instanceof RegExp) {
      rule.type = 'pattern';
    }
    if (typeof rule.validator !== 'function' && rule.type && !_validator2['default'].hasOwnProperty(rule.type)) {
      throw new Error((0, _util.format)('Unknown rule type %s', rule.type));
    }
    return rule.type || 'string';
  },
  getValidationMethod: function getValidationMethod(rule) {
    if (typeof rule.validator === 'function') {
      return rule.validator;
    }
    var keys = Object.keys(rule);
    var messageIndex = keys.indexOf('message');
    if (messageIndex !== -1) {
      keys.splice(messageIndex, 1);
    }
    if (keys.length === 1 && keys[0] === 'required') {
      return _validator2['default'].required;
    }
    return _validator2['default'][this.getType(rule)] || false;
  }
};

Schema.register = function register(type, validator) {
  if (typeof validator !== 'function') {
    throw new Error('Cannot register a validator by type, validator is not a function');
  }
  _validator2['default'][type] = validator;
};

Schema.warning = _util.warning;

Schema.messages = _messages2.messages;

exports['default'] = Schema;

/***/ }),

/***/ 24:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _string = __webpack_require__(25);

var _string2 = _interopRequireDefault(_string);

var _method = __webpack_require__(31);

var _method2 = _interopRequireDefault(_method);

var _number = __webpack_require__(32);

var _number2 = _interopRequireDefault(_number);

var _boolean = __webpack_require__(33);

var _boolean2 = _interopRequireDefault(_boolean);

var _regexp = __webpack_require__(34);

var _regexp2 = _interopRequireDefault(_regexp);

var _integer = __webpack_require__(35);

var _integer2 = _interopRequireDefault(_integer);

var _float = __webpack_require__(36);

var _float2 = _interopRequireDefault(_float);

var _array = __webpack_require__(37);

var _array2 = _interopRequireDefault(_array);

var _object = __webpack_require__(38);

var _object2 = _interopRequireDefault(_object);

var _enum = __webpack_require__(39);

var _enum2 = _interopRequireDefault(_enum);

var _pattern = __webpack_require__(40);

var _pattern2 = _interopRequireDefault(_pattern);

var _date = __webpack_require__(41);

var _date2 = _interopRequireDefault(_date);

var _required = __webpack_require__(42);

var _required2 = _interopRequireDefault(_required);

var _type = __webpack_require__(43);

var _type2 = _interopRequireDefault(_type);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

exports['default'] = {
  string: _string2['default'],
  method: _method2['default'],
  number: _number2['default'],
  boolean: _boolean2['default'],
  regexp: _regexp2['default'],
  integer: _integer2['default'],
  float: _float2['default'],
  array: _array2['default'],
  object: _object2['default'],
  'enum': _enum2['default'],
  pattern: _pattern2['default'],
  date: _date2['default'],
  url: _type2['default'],
  hex: _type2['default'],
  email: _type2['default'],
  required: _required2['default']
};

/***/ }),

/***/ 25:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Performs validation for string types.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function string(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value, 'string') && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options, 'string');
    if (!(0, _util.isEmptyValue)(value, 'string')) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
      _rule2['default'].pattern(rule, value, source, errors, options);
      if (rule.whitespace === true) {
        _rule2['default'].whitespace(rule, value, source, errors, options);
      }
    }
  }
  callback(errors);
}

exports['default'] = string;

/***/ }),

/***/ 26:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating whitespace.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function whitespace(rule, value, source, errors, options) {
  if (/^\s+$/.test(value) || value === '') {
    errors.push(util.format(options.messages.whitespace, rule.fullField));
  }
}

exports['default'] = whitespace;

/***/ }),

/***/ 27:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

var _required = __webpack_require__(14);

var _required2 = _interopRequireDefault(_required);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/* eslint max-len:0 */

var pattern = {
  // http://emailregex.com/
  email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  url: new RegExp('^(?!mailto:)(?:(?:http|https|ftp)://|//)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$', 'i'),
  hex: /^#?([a-f0-9]{6}|[a-f0-9]{3})$/i
};

var types = {
  integer: function integer(value) {
    return types.number(value) && parseInt(value, 10) === value;
  },
  float: function float(value) {
    return types.number(value) && !types.integer(value);
  },
  array: function array(value) {
    return Array.isArray(value);
  },
  regexp: function regexp(value) {
    if (value instanceof RegExp) {
      return true;
    }
    try {
      return !!new RegExp(value);
    } catch (e) {
      return false;
    }
  },
  date: function date(value) {
    return typeof value.getTime === 'function' && typeof value.getMonth === 'function' && typeof value.getYear === 'function';
  },
  number: function number(value) {
    if (isNaN(value)) {
      return false;
    }
    return typeof value === 'number';
  },
  object: function object(value) {
    return (typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object' && !types.array(value);
  },
  method: function method(value) {
    return typeof value === 'function';
  },
  email: function email(value) {
    return typeof value === 'string' && !!value.match(pattern.email) && value.length < 255;
  },
  url: function url(value) {
    return typeof value === 'string' && !!value.match(pattern.url);
  },
  hex: function hex(value) {
    return typeof value === 'string' && !!value.match(pattern.hex);
  }
};

/**
 *  Rule for validating the type of a value.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function type(rule, value, source, errors, options) {
  if (rule.required && value === undefined) {
    (0, _required2['default'])(rule, value, source, errors, options);
    return;
  }
  var custom = ['integer', 'float', 'array', 'regexp', 'object', 'method', 'email', 'number', 'date', 'url', 'hex'];
  var ruleType = rule.type;
  if (custom.indexOf(ruleType) > -1) {
    if (!types[ruleType](value)) {
      errors.push(util.format(options.messages.types[ruleType], rule.fullField, rule.type));
    }
    // straight typeof check
  } else if (ruleType && (typeof value === 'undefined' ? 'undefined' : _typeof(value)) !== rule.type) {
    errors.push(util.format(options.messages.types[ruleType], rule.fullField, rule.type));
  }
}

exports['default'] = type;

/***/ }),

/***/ 28:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating minimum and maximum allowed values.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function range(rule, value, source, errors, options) {
  var len = typeof rule.len === 'number';
  var min = typeof rule.min === 'number';
  var max = typeof rule.max === 'number';
  // 正则匹配码点范围从U+010000一直到U+10FFFF的文字（补充平面Supplementary Plane）
  var spRegexp = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;
  var val = value;
  var key = null;
  var num = typeof value === 'number';
  var str = typeof value === 'string';
  var arr = Array.isArray(value);
  if (num) {
    key = 'number';
  } else if (str) {
    key = 'string';
  } else if (arr) {
    key = 'array';
  }
  // if the value is not of a supported type for range validation
  // the validation rule rule should use the
  // type property to also test for a particular type
  if (!key) {
    return false;
  }
  if (arr) {
    val = value.length;
  }
  if (str) {
    // 处理码点大于U+010000的文字length属性不准确的bug，如"𠮷𠮷𠮷".lenght !== 3
    val = value.replace(spRegexp, '_').length;
  }
  if (len) {
    if (val !== rule.len) {
      errors.push(util.format(options.messages[key].len, rule.fullField, rule.len));
    }
  } else if (min && !max && val < rule.min) {
    errors.push(util.format(options.messages[key].min, rule.fullField, rule.min));
  } else if (max && !min && val > rule.max) {
    errors.push(util.format(options.messages[key].max, rule.fullField, rule.max));
  } else if (min && max && (val < rule.min || val > rule.max)) {
    errors.push(util.format(options.messages[key].range, rule.fullField, rule.min, rule.max));
  }
}

exports['default'] = range;

/***/ }),

/***/ 29:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

var ENUM = 'enum';

/**
 *  Rule for validating a value exists in an enumerable list.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function enumerable(rule, value, source, errors, options) {
  rule[ENUM] = Array.isArray(rule[ENUM]) ? rule[ENUM] : [];
  if (rule[ENUM].indexOf(value) === -1) {
    errors.push(util.format(options.messages[ENUM], rule.fullField, rule[ENUM].join(', ')));
  }
}

exports['default'] = enumerable;

/***/ }),

/***/ 3:
/***/ (function(module, exports) {

module.exports = locale;

/***/ }),

/***/ 30:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var util = _interopRequireWildcard(_util);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj['default'] = obj; return newObj; } }

/**
 *  Rule for validating a regular expression pattern.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param source The source object being validated.
 *  @param errors An array of errors that this rule may add
 *  validation errors to.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function pattern(rule, value, source, errors, options) {
  if (rule.pattern) {
    if (rule.pattern instanceof RegExp) {
      // if a RegExp instance is passed, reset `lastIndex` in case its `global`
      // flag is accidentally set to `true`, which in a validation scenario
      // is not necessary and the result might be misleading
      rule.pattern.lastIndex = 0;
      if (!rule.pattern.test(value)) {
        errors.push(util.format(options.messages.pattern.mismatch, rule.fullField, value, rule.pattern));
      }
    } else if (typeof rule.pattern === 'string') {
      var _pattern = new RegExp(rule.pattern);
      if (!_pattern.test(value)) {
        errors.push(util.format(options.messages.pattern.mismatch, rule.fullField, value, rule.pattern));
      }
    }
  }
}

exports['default'] = pattern;

/***/ }),

/***/ 31:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a function.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function method(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = method;

/***/ }),

/***/ 32:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a number.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function number(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if (value === '') {
      value = undefined;
    }
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = number;

/***/ }),

/***/ 33:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _util = __webpack_require__(2);

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a boolean.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function boolean(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = boolean;

/***/ }),

/***/ 34:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates the regular expression type.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function regexp(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (!(0, _util.isEmptyValue)(value)) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = regexp;

/***/ }),

/***/ 35:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a number is an integer.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function integer(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = integer;

/***/ }),

/***/ 36:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a number is a floating point number.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function floatFn(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = floatFn;

/***/ }),

/***/ 37:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates an array.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function array(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value, 'array') && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options, 'array');
    if (!(0, _util.isEmptyValue)(value, 'array')) {
      _rule2['default'].type(rule, value, source, errors, options);
      _rule2['default'].range(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = array;

/***/ }),

/***/ 38:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates an object.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function object(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value !== undefined) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = object;

/***/ }),

/***/ 39:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

var ENUM = 'enum';

/**
 *  Validates an enumerable list.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function enumerable(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (value) {
      _rule2['default'][ENUM](rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = enumerable;

/***/ }),

/***/ 4:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isOneOf = isOneOf;
function isOneOf(value, validList) {
  for (var i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true;
    }
  }
  return false;
}

/***/ }),

/***/ 40:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

/**
 *  Validates a regular expression pattern.
 *
 *  Performs validation when a rule only contains
 *  a pattern property but is not declared as a string type.
 *
 *  @param rule The validation rule.
 *  @param value The value of the field on the source object.
 *  @param callback The callback function.
 *  @param source The source object being validated.
 *  @param options The validation options.
 *  @param options.messages The validation messages.
 */
function pattern(rule, value, callback, source, options) {
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value, 'string') && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (!(0, _util.isEmptyValue)(value, 'string')) {
      _rule2['default'].pattern(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = pattern;

/***/ }),

/***/ 41:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function date(rule, value, callback, source, options) {
  // console.log('integer rule called %j', rule);
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  // console.log('validate on %s value', value);
  if (validate) {
    if ((0, _util.isEmptyValue)(value) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options);
    if (!(0, _util.isEmptyValue)(value)) {
      var dateObject = void 0;

      if (typeof value === 'number') {
        dateObject = new Date(value);
      } else {
        dateObject = value;
      }

      _rule2['default'].type(rule, dateObject, source, errors, options);
      if (dateObject) {
        _rule2['default'].range(rule, dateObject.getTime(), source, errors, options);
      }
    }
  }
  callback(errors);
}

exports['default'] = date;

/***/ }),

/***/ 42:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function required(rule, value, callback, source, options) {
  var errors = [];
  var type = Array.isArray(value) ? 'array' : typeof value === 'undefined' ? 'undefined' : _typeof(value);
  _rule2['default'].required(rule, value, source, errors, options, type);
  callback(errors);
}

exports['default'] = required;

/***/ }),

/***/ 426:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _select = __webpack_require__(427);

var _select2 = _interopRequireDefault(_select);

var _option = __webpack_require__(132);

var _option2 = _interopRequireDefault(_option);

var _optgroup = __webpack_require__(429);

var _optgroup2 = _interopRequireDefault(_optgroup);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_select2.default.Option = _option2.default;

_select2.default.install = function (Vue) {
  Vue.component(_option2.default.name, _option2.default);
  Vue.component(_select2.default.name, _select2.default);
  Vue.component(_optgroup2.default.name, _optgroup2.default);
};

exports.default = _select2.default;

/***/ }),

/***/ 427:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_select_vue__ = __webpack_require__(202);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_select_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_select_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_select_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_select_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_78f05426_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_select_vue__ = __webpack_require__(428);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_select_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_78f05426_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_select_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_78f05426_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_select_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 428:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{directives:[{name:"clickoutside",rawName:"v-clickoutside",value:(_vm.clickoutsideHandle),expression:"clickoutsideHandle"}],class:_vm.baseClass},[_c('div',{ref:"popperRef",staticClass:"bui-select-wrapper",class:_vm.wrapperClass,on:{"click":_vm.handleSelectClick,"mouseenter":_vm.handleWrapMouseEnter,"mouseleave":_vm.handleWrapMouseLeave}},[_vm._t("input",[_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.isMultipleWrapperShow),expression:"isMultipleWrapperShow"}],ref:"multiple",staticClass:"bui-select-wrapper-multiple",class:_vm.multipleClass,style:(_vm.collapseTagsStyle)},[_vm._l((_vm.selected),function(item){return _c('byted-tag',{key:item.key,staticClass:"bui-select-tag",attrs:{"disabled":_vm.disabled,"plain":_vm.disabled,"size":_vm.multipleSize,"type":_vm.tagType,"closable":""},on:{"close":function($event){return _vm.handleTagRemove(item.value)}}},[(typeof _vm.getSelectedLabel(item) === 'object')?[_c('tag-expand',{attrs:{"content":_vm.getSelectedLabel(item)}})]:[_vm._v("\n            "+_vm._s(_vm.getSelectedLabel(item))+"\n          ")]],2)}),_vm._v(" "),(_vm.isMultipeCollapseTagsShow)?_c('span',{staticClass:"bui-selected-label"},[_vm._v("\n          "+_vm._s(_vm._hasSelect)+"\n        ")]):_vm._e()],2),_vm._v(" "),(_vm.isInputShow)?_c('input',{ref:"input",class:_vm.inputClass,style:(_vm.inputStyle),attrs:{"placeholder":_vm._placeholder,"disabled":_vm.disabled,"readonly":""},domProps:{"value":_vm.getSelectedLabel(_vm.selected[0])},on:{"blur":_vm.handleInputBlur,"focus":_vm.handleInputFocus}}):_vm._e(),_vm._v(" "),(_vm.plain)?_c('div',{staticClass:"bui-select-wrapper-plain",style:(_vm.inputStyle)},[_c('span',{staticClass:"bui-input"},[_vm._v(_vm._s(_vm.getSelectedLabel(_vm.selected[0]) || _vm._placeholder))]),_vm._v(" "),_c('byted-icon',{staticClass:"bui-select-icon-plain",class:{open:_vm.optionShow},attrs:{"name":"angle-down"},on:{"click":function($event){$event.stopPropagation();return _vm.handleIconClick($event)}}})],1):_vm._e()]),_vm._v(" "),_c('byted-icon',{directives:[{name:"show",rawName:"v-show",value:(!_vm.plain && !_vm.showClear),expression:"!plain && !showClear"}],staticClass:"bui-select-icon",class:{open:_vm.optionShow},attrs:{"name":"angle-down"},on:{"click":function($event){$event.stopPropagation();return _vm.handleIconClick($event)}}}),_vm._v(" "),(!_vm.plain && _vm.showClear)?_c('byted-icon',{staticClass:"bui-select-icon bui-select-clear-icon",attrs:{"name":"close-circle"},on:{"click":function($event){$event.stopPropagation();return _vm.handleClearClick($event)}}}):_vm._e()],2),_vm._v(" "),_c('byted-popper',{directives:[{name:"show",rawName:"v-show",value:(_vm.optionShow),expression:"optionShow"}],ref:"popper",staticClass:"bui-select-popper",class:_vm.popperClass,attrs:{"reference":_vm.popperRef,"append-to-body":_vm.transfer,"popper-options":_vm.popperOptions,"placement":"bottom-start"}},[_c('div',{staticClass:"bui-select-option"},[(_vm.filterable)?_c('div',{staticClass:"bui-option-filter"},[_c('byted-input',{ref:"filter",attrs:{"size":_vm.size,"validate-event":false,"placeholder":_vm._filterPlaceholder,"prefix":"search","clearable":""},on:{"input":_vm.handleFilterInput,"clear":_vm.handleFilterClear},model:{value:(_vm.filterLabel),callback:function ($$v) {_vm.filterLabel=$$v;},expression:"filterLabel"}},[_c('template',{slot:"append"},[_vm._t("filter-append")],2)],2)],1):_vm._e(),_vm._v(" "),_c('div',{staticClass:"bui-select-option-wrapper"},[_vm._t("default")],2),_vm._v(" "),(_vm.optionsArr.length && _vm.filteredOptionsCount <= 0)?_c('div',{staticClass:"bui-option-match-none"},[_c('byted-icon',{attrs:{"name":"empty"}}),_vm._v("\n        "+_vm._s(_vm._noMatchText)+"\n      ")],1):_vm._e(),_vm._v(" "),(!_vm.optionsArr.length)?[_vm._t("empty",[_c('div',{staticClass:"bui-option-match-none"},[_c('byted-icon',{attrs:{"name":"empty"}}),_vm._v("\n            "+_vm._s(_vm._noDataText)+"\n          ")],1)])]:_vm._e(),_vm._v(" "),_vm._t("footer")],2)])],1)};
var staticRenderFns = [];


/***/ }),

/***/ 429:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_optgroup_vue__ = __webpack_require__(203);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_optgroup_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_optgroup_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_optgroup_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_optgroup_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_48381ee8_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_optgroup_vue__ = __webpack_require__(430);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_optgroup_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_48381ee8_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_optgroup_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_48381ee8_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_optgroup_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 43:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _rule = __webpack_require__(5);

var _rule2 = _interopRequireDefault(_rule);

var _util = __webpack_require__(2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

function type(rule, value, callback, source, options) {
  var ruleType = rule.type;
  var errors = [];
  var validate = rule.required || !rule.required && source.hasOwnProperty(rule.field);
  if (validate) {
    if ((0, _util.isEmptyValue)(value, ruleType) && !rule.required) {
      return callback();
    }
    _rule2['default'].required(rule, value, source, errors, options, ruleType);
    if (!(0, _util.isEmptyValue)(value, ruleType)) {
      _rule2['default'].type(rule, value, source, errors, options);
    }
  }
  callback(errors);
}

exports['default'] = type;

/***/ }),

/***/ 430:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.visible),expression:"visible"}],class:_vm.baseClass},[_c('p',{staticClass:"bui-optgroup-label"},[_vm._v(_vm._s(_vm.label))]),_vm._v(" "),_vm._t("default")],2)};
var staticRenderFns = [];


/***/ }),

/***/ 44:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.newMessages = newMessages;
function newMessages() {
  return {
    'default': 'Validation error on field %s',
    required: '%s is required',
    'enum': '%s must be one of %s',
    whitespace: '%s cannot be empty',
    date: {
      format: '%s date %s is invalid for format %s',
      parse: '%s date could not be parsed, %s is invalid ',
      invalid: '%s date %s is invalid'
    },
    types: {
      string: '%s is not a %s',
      method: '%s is not a %s (function)',
      array: '%s is not an %s',
      object: '%s is not an %s',
      number: '%s is not a %s',
      date: '%s is not a %s',
      boolean: '%s is not a %s',
      integer: '%s is not an %s',
      float: '%s is not a %s',
      regexp: '%s is not a valid %s',
      email: '%s is not a valid %s',
      url: '%s is not a valid %s',
      hex: '%s is not a valid %s'
    },
    string: {
      len: '%s must be exactly %s characters',
      min: '%s must be at least %s characters',
      max: '%s cannot be longer than %s characters',
      range: '%s must be between %s and %s characters'
    },
    number: {
      len: '%s must equal %s',
      min: '%s cannot be less than %s',
      max: '%s cannot be greater than %s',
      range: '%s must be between %s and %s'
    },
    array: {
      len: '%s must be exactly %s in length',
      min: '%s cannot be less than %s in length',
      max: '%s cannot be greater than %s in length',
      range: '%s must be between %s and %s in length'
    },
    pattern: {
      mismatch: '%s value %s does not match pattern %s'
    },
    clone: function clone() {
      var cloned = JSON.parse(JSON.stringify(this));
      cloned.clone = this.clone;
      return cloned;
    }
  };
}

exports.messages = newMessages();

/***/ }),

/***/ 45:
/***/ (function(module, exports, __webpack_require__) {


var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; /**
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               * Any basic methods here .
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               * Copyright 2017 ByteDance, Inc.
                                                                                                                                                                                                                                                                               * Licensed under MIT
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               */

/* global window module*/


var _asyncValidator = __webpack_require__(23);

var _asyncValidator2 = _interopRequireDefault(_asyncValidator);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var RE_NARGS = /(%|)\{([0-9a-zA-Z_]+)\}/g;

module.exports = {
  /**
   *
   * @param {object} source
   * @return {object}
   * Deep copy of any object .
   *
   *
   *
   *
   */
  copy: function copy(source) {
    var str = void 0;var newobj = void 0;
    try {
      newobj = source.constructor === Array ? [] : {};
    } catch (e) {
      return;
    }
    try {
      str = JSON.stringify(source);
      newobj = JSON.parse(str);
    } catch (e) {
      for (var i in source) {
        if (_typeof(source[i]) === 'object') {
          newobj[i] = this.copy(source[i]);
        } else {
          source[i];
        }
      }
    }
    return newobj;
  },
  // 深克隆
  deepCopy: function deepCopy(source) {
    // 如果source是对象
    if ((typeof source === 'undefined' ? 'undefined' : _typeof(source)) == 'object' && source !== null) {
      // 有必要区分数组和普通对象
      var o = Object.prototype.toString.call(source) == '[object Array]' ? [] : {};
      // 遍历source的自有属性
      for (var key in source) {
        // 如果key是source的自有属性
        if (source.hasOwnProperty(key)) {
          o[key] = this.deepCopy(source[key]);
          // arguments.callee调的是当前的deepCopy函数
        }
      }
      return o;
    } else {
      // 如果source是原始类型的值，直接返回
      return source;
    }
  },

  /**
   * 获取 url 参数
   *
   */
  urlParams: function urlParams(href) {
    href = href || window.location.search;
    var data = {};

    var reg = /([^?=&]+)(=([^&]*))?/g;
    if (href) {
      href.replace(reg, function ($0, $1, $2, $3) {
        data[$1] = $3;
      });
    }
    return data;
  },

  Validator: _asyncValidator2.default,

  /**
   * 获取指定长度的随机字符串
   * @param  {} length=0
   */
  getRandomStr: function getRandomStr() {
    var length = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;

    var chars = 'asdfghjklzxcvbnmqwertyuiopASDFGHJKLZXCVBNMQWERTYUIOP1234567890';
    var str = '';
    for (var i = 0; i < length; i++) {
      str += chars[Math.floor(Math.random() * chars.length)];
    }
    return str;
  },
  hasOwn: function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  },

  // 补零
  addZero: function addZero(number) {
    return ('0' + number).substr(-2, 2);
  },

  /**
   * template
   *
   * @param {String} string
   * @param {Array} ...args
   * @return {String}
   */
  template: function template(string) {
    for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    if (args.length === 1 && _typeof(args[0]) === 'object') {
      args = args[0];
    }

    if (!args || !args.hasOwnProperty) {
      args = {};
    }

    if (typeof string !== 'string') {
      return string;
    }

    return string.replace(RE_NARGS, function (match, prefix, i, index) {
      var result = void 0;

      if (string[index - 1] === '{' && string[index + match.length] === '}') {
        return i;
      } else {
        result = Object.prototype.hasOwnProperty.call(args, i) ? args[i] : null;
        if (result === null || result === undefined) {
          return '';
        }

        return result;
      }
    });
  }
};

/***/ }),

/***/ 5:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _required = __webpack_require__(14);

var _required2 = _interopRequireDefault(_required);

var _whitespace = __webpack_require__(26);

var _whitespace2 = _interopRequireDefault(_whitespace);

var _type = __webpack_require__(27);

var _type2 = _interopRequireDefault(_type);

var _range = __webpack_require__(28);

var _range2 = _interopRequireDefault(_range);

var _enum = __webpack_require__(29);

var _enum2 = _interopRequireDefault(_enum);

var _pattern = __webpack_require__(30);

var _pattern2 = _interopRequireDefault(_pattern);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

exports['default'] = {
  required: _required2['default'],
  whitespace: _whitespace2['default'],
  type: _type2['default'],
  range: _range2['default'],
  'enum': _enum2['default'],
  pattern: _pattern2['default']
};

/***/ }),

/***/ 53:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* WEBPACK VAR INJECTION */(function(global) {/**
 * A collection of shims that provide minimal functionality of the ES6 collections.
 *
 * These implementations are not meant to be used outside of the ResizeObserver
 * modules as they cover only a limited range of use cases.
 */
/* eslint-disable require-jsdoc, valid-jsdoc */
var MapShim = (function () {
    if (typeof Map !== 'undefined') {
        return Map;
    }
    /**
     * Returns index in provided array that matches the specified key.
     *
     * @param {Array<Array>} arr
     * @param {*} key
     * @returns {number}
     */
    function getIndex(arr, key) {
        var result = -1;
        arr.some(function (entry, index) {
            if (entry[0] === key) {
                result = index;
                return true;
            }
            return false;
        });
        return result;
    }
    return /** @class */ (function () {
        function class_1() {
            this.__entries__ = [];
        }
        Object.defineProperty(class_1.prototype, "size", {
            /**
             * @returns {boolean}
             */
            get: function () {
                return this.__entries__.length;
            },
            enumerable: true,
            configurable: true
        });
        /**
         * @param {*} key
         * @returns {*}
         */
        class_1.prototype.get = function (key) {
            var index = getIndex(this.__entries__, key);
            var entry = this.__entries__[index];
            return entry && entry[1];
        };
        /**
         * @param {*} key
         * @param {*} value
         * @returns {void}
         */
        class_1.prototype.set = function (key, value) {
            var index = getIndex(this.__entries__, key);
            if (~index) {
                this.__entries__[index][1] = value;
            }
            else {
                this.__entries__.push([key, value]);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.delete = function (key) {
            var entries = this.__entries__;
            var index = getIndex(entries, key);
            if (~index) {
                entries.splice(index, 1);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.has = function (key) {
            return !!~getIndex(this.__entries__, key);
        };
        /**
         * @returns {void}
         */
        class_1.prototype.clear = function () {
            this.__entries__.splice(0);
        };
        /**
         * @param {Function} callback
         * @param {*} [ctx=null]
         * @returns {void}
         */
        class_1.prototype.forEach = function (callback, ctx) {
            if (ctx === void 0) { ctx = null; }
            for (var _i = 0, _a = this.__entries__; _i < _a.length; _i++) {
                var entry = _a[_i];
                callback.call(ctx, entry[1], entry[0]);
            }
        };
        return class_1;
    }());
})();

/**
 * Detects whether window and document objects are available in current environment.
 */
var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined' && window.document === document;

// Returns global object of a current environment.
var global$1 = (function () {
    if (typeof global !== 'undefined' && global.Math === Math) {
        return global;
    }
    if (typeof self !== 'undefined' && self.Math === Math) {
        return self;
    }
    if (typeof window !== 'undefined' && window.Math === Math) {
        return window;
    }
    // eslint-disable-next-line no-new-func
    return Function('return this')();
})();

/**
 * A shim for the requestAnimationFrame which falls back to the setTimeout if
 * first one is not supported.
 *
 * @returns {number} Requests' identifier.
 */
var requestAnimationFrame$1 = (function () {
    if (typeof requestAnimationFrame === 'function') {
        // It's required to use a bounded function because IE sometimes throws
        // an "Invalid calling object" error if rAF is invoked without the global
        // object on the left hand side.
        return requestAnimationFrame.bind(global$1);
    }
    return function (callback) { return setTimeout(function () { return callback(Date.now()); }, 1000 / 60); };
})();

// Defines minimum timeout before adding a trailing call.
var trailingTimeout = 2;
/**
 * Creates a wrapper function which ensures that provided callback will be
 * invoked only once during the specified delay period.
 *
 * @param {Function} callback - Function to be invoked after the delay period.
 * @param {number} delay - Delay after which to invoke callback.
 * @returns {Function}
 */
function throttle (callback, delay) {
    var leadingCall = false, trailingCall = false, lastCallTime = 0;
    /**
     * Invokes the original callback function and schedules new invocation if
     * the "proxy" was called during current request.
     *
     * @returns {void}
     */
    function resolvePending() {
        if (leadingCall) {
            leadingCall = false;
            callback();
        }
        if (trailingCall) {
            proxy();
        }
    }
    /**
     * Callback invoked after the specified delay. It will further postpone
     * invocation of the original function delegating it to the
     * requestAnimationFrame.
     *
     * @returns {void}
     */
    function timeoutCallback() {
        requestAnimationFrame$1(resolvePending);
    }
    /**
     * Schedules invocation of the original function.
     *
     * @returns {void}
     */
    function proxy() {
        var timeStamp = Date.now();
        if (leadingCall) {
            // Reject immediately following calls.
            if (timeStamp - lastCallTime < trailingTimeout) {
                return;
            }
            // Schedule new call to be in invoked when the pending one is resolved.
            // This is important for "transitions" which never actually start
            // immediately so there is a chance that we might miss one if change
            // happens amids the pending invocation.
            trailingCall = true;
        }
        else {
            leadingCall = true;
            trailingCall = false;
            setTimeout(timeoutCallback, delay);
        }
        lastCallTime = timeStamp;
    }
    return proxy;
}

// Minimum delay before invoking the update of observers.
var REFRESH_DELAY = 20;
// A list of substrings of CSS properties used to find transition events that
// might affect dimensions of observed elements.
var transitionKeys = ['top', 'right', 'bottom', 'left', 'width', 'height', 'size', 'weight'];
// Check if MutationObserver is available.
var mutationObserverSupported = typeof MutationObserver !== 'undefined';
/**
 * Singleton controller class which handles updates of ResizeObserver instances.
 */
var ResizeObserverController = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserverController.
     *
     * @private
     */
    function ResizeObserverController() {
        /**
         * Indicates whether DOM listeners have been added.
         *
         * @private {boolean}
         */
        this.connected_ = false;
        /**
         * Tells that controller has subscribed for Mutation Events.
         *
         * @private {boolean}
         */
        this.mutationEventsAdded_ = false;
        /**
         * Keeps reference to the instance of MutationObserver.
         *
         * @private {MutationObserver}
         */
        this.mutationsObserver_ = null;
        /**
         * A list of connected observers.
         *
         * @private {Array<ResizeObserverSPI>}
         */
        this.observers_ = [];
        this.onTransitionEnd_ = this.onTransitionEnd_.bind(this);
        this.refresh = throttle(this.refresh.bind(this), REFRESH_DELAY);
    }
    /**
     * Adds observer to observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be added.
     * @returns {void}
     */
    ResizeObserverController.prototype.addObserver = function (observer) {
        if (!~this.observers_.indexOf(observer)) {
            this.observers_.push(observer);
        }
        // Add listeners if they haven't been added yet.
        if (!this.connected_) {
            this.connect_();
        }
    };
    /**
     * Removes observer from observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be removed.
     * @returns {void}
     */
    ResizeObserverController.prototype.removeObserver = function (observer) {
        var observers = this.observers_;
        var index = observers.indexOf(observer);
        // Remove observer if it's present in registry.
        if (~index) {
            observers.splice(index, 1);
        }
        // Remove listeners if controller has no connected observers.
        if (!observers.length && this.connected_) {
            this.disconnect_();
        }
    };
    /**
     * Invokes the update of observers. It will continue running updates insofar
     * it detects changes.
     *
     * @returns {void}
     */
    ResizeObserverController.prototype.refresh = function () {
        var changesDetected = this.updateObservers_();
        // Continue running updates if changes have been detected as there might
        // be future ones caused by CSS transitions.
        if (changesDetected) {
            this.refresh();
        }
    };
    /**
     * Updates every observer from observers list and notifies them of queued
     * entries.
     *
     * @private
     * @returns {boolean} Returns "true" if any observer has detected changes in
     *      dimensions of it's elements.
     */
    ResizeObserverController.prototype.updateObservers_ = function () {
        // Collect observers that have active observations.
        var activeObservers = this.observers_.filter(function (observer) {
            return observer.gatherActive(), observer.hasActive();
        });
        // Deliver notifications in a separate cycle in order to avoid any
        // collisions between observers, e.g. when multiple instances of
        // ResizeObserver are tracking the same element and the callback of one
        // of them changes content dimensions of the observed target. Sometimes
        // this may result in notifications being blocked for the rest of observers.
        activeObservers.forEach(function (observer) { return observer.broadcastActive(); });
        return activeObservers.length > 0;
    };
    /**
     * Initializes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.connect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already added.
        if (!isBrowser || this.connected_) {
            return;
        }
        // Subscription to the "Transitionend" event is used as a workaround for
        // delayed transitions. This way it's possible to capture at least the
        // final state of an element.
        document.addEventListener('transitionend', this.onTransitionEnd_);
        window.addEventListener('resize', this.refresh);
        if (mutationObserverSupported) {
            this.mutationsObserver_ = new MutationObserver(this.refresh);
            this.mutationsObserver_.observe(document, {
                attributes: true,
                childList: true,
                characterData: true,
                subtree: true
            });
        }
        else {
            document.addEventListener('DOMSubtreeModified', this.refresh);
            this.mutationEventsAdded_ = true;
        }
        this.connected_ = true;
    };
    /**
     * Removes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.disconnect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already removed.
        if (!isBrowser || !this.connected_) {
            return;
        }
        document.removeEventListener('transitionend', this.onTransitionEnd_);
        window.removeEventListener('resize', this.refresh);
        if (this.mutationsObserver_) {
            this.mutationsObserver_.disconnect();
        }
        if (this.mutationEventsAdded_) {
            document.removeEventListener('DOMSubtreeModified', this.refresh);
        }
        this.mutationsObserver_ = null;
        this.mutationEventsAdded_ = false;
        this.connected_ = false;
    };
    /**
     * "Transitionend" event handler.
     *
     * @private
     * @param {TransitionEvent} event
     * @returns {void}
     */
    ResizeObserverController.prototype.onTransitionEnd_ = function (_a) {
        var _b = _a.propertyName, propertyName = _b === void 0 ? '' : _b;
        // Detect whether transition may affect dimensions of an element.
        var isReflowProperty = transitionKeys.some(function (key) {
            return !!~propertyName.indexOf(key);
        });
        if (isReflowProperty) {
            this.refresh();
        }
    };
    /**
     * Returns instance of the ResizeObserverController.
     *
     * @returns {ResizeObserverController}
     */
    ResizeObserverController.getInstance = function () {
        if (!this.instance_) {
            this.instance_ = new ResizeObserverController();
        }
        return this.instance_;
    };
    /**
     * Holds reference to the controller's instance.
     *
     * @private {ResizeObserverController}
     */
    ResizeObserverController.instance_ = null;
    return ResizeObserverController;
}());

/**
 * Defines non-writable/enumerable properties of the provided target object.
 *
 * @param {Object} target - Object for which to define properties.
 * @param {Object} props - Properties to be defined.
 * @returns {Object} Target object.
 */
var defineConfigurable = (function (target, props) {
    for (var _i = 0, _a = Object.keys(props); _i < _a.length; _i++) {
        var key = _a[_i];
        Object.defineProperty(target, key, {
            value: props[key],
            enumerable: false,
            writable: false,
            configurable: true
        });
    }
    return target;
});

/**
 * Returns the global object associated with provided element.
 *
 * @param {Object} target
 * @returns {Object}
 */
var getWindowOf = (function (target) {
    // Assume that the element is an instance of Node, which means that it
    // has the "ownerDocument" property from which we can retrieve a
    // corresponding global object.
    var ownerGlobal = target && target.ownerDocument && target.ownerDocument.defaultView;
    // Return the local global object if it's not possible extract one from
    // provided element.
    return ownerGlobal || global$1;
});

// Placeholder of an empty content rectangle.
var emptyRect = createRectInit(0, 0, 0, 0);
/**
 * Converts provided string to a number.
 *
 * @param {number|string} value
 * @returns {number}
 */
function toFloat(value) {
    return parseFloat(value) || 0;
}
/**
 * Extracts borders size from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @param {...string} positions - Borders positions (top, right, ...)
 * @returns {number}
 */
function getBordersSize(styles) {
    var positions = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        positions[_i - 1] = arguments[_i];
    }
    return positions.reduce(function (size, position) {
        var value = styles['border-' + position + '-width'];
        return size + toFloat(value);
    }, 0);
}
/**
 * Extracts paddings sizes from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @returns {Object} Paddings box.
 */
function getPaddings(styles) {
    var positions = ['top', 'right', 'bottom', 'left'];
    var paddings = {};
    for (var _i = 0, positions_1 = positions; _i < positions_1.length; _i++) {
        var position = positions_1[_i];
        var value = styles['padding-' + position];
        paddings[position] = toFloat(value);
    }
    return paddings;
}
/**
 * Calculates content rectangle of provided SVG element.
 *
 * @param {SVGGraphicsElement} target - Element content rectangle of which needs
 *      to be calculated.
 * @returns {DOMRectInit}
 */
function getSVGContentRect(target) {
    var bbox = target.getBBox();
    return createRectInit(0, 0, bbox.width, bbox.height);
}
/**
 * Calculates content rectangle of provided HTMLElement.
 *
 * @param {HTMLElement} target - Element for which to calculate the content rectangle.
 * @returns {DOMRectInit}
 */
function getHTMLElementContentRect(target) {
    // Client width & height properties can't be
    // used exclusively as they provide rounded values.
    var clientWidth = target.clientWidth, clientHeight = target.clientHeight;
    // By this condition we can catch all non-replaced inline, hidden and
    // detached elements. Though elements with width & height properties less
    // than 0.5 will be discarded as well.
    //
    // Without it we would need to implement separate methods for each of
    // those cases and it's not possible to perform a precise and performance
    // effective test for hidden elements. E.g. even jQuery's ':visible' filter
    // gives wrong results for elements with width & height less than 0.5.
    if (!clientWidth && !clientHeight) {
        return emptyRect;
    }
    var styles = getWindowOf(target).getComputedStyle(target);
    var paddings = getPaddings(styles);
    var horizPad = paddings.left + paddings.right;
    var vertPad = paddings.top + paddings.bottom;
    // Computed styles of width & height are being used because they are the
    // only dimensions available to JS that contain non-rounded values. It could
    // be possible to utilize the getBoundingClientRect if only it's data wasn't
    // affected by CSS transformations let alone paddings, borders and scroll bars.
    var width = toFloat(styles.width), height = toFloat(styles.height);
    // Width & height include paddings and borders when the 'border-box' box
    // model is applied (except for IE).
    if (styles.boxSizing === 'border-box') {
        // Following conditions are required to handle Internet Explorer which
        // doesn't include paddings and borders to computed CSS dimensions.
        //
        // We can say that if CSS dimensions + paddings are equal to the "client"
        // properties then it's either IE, and thus we don't need to subtract
        // anything, or an element merely doesn't have paddings/borders styles.
        if (Math.round(width + horizPad) !== clientWidth) {
            width -= getBordersSize(styles, 'left', 'right') + horizPad;
        }
        if (Math.round(height + vertPad) !== clientHeight) {
            height -= getBordersSize(styles, 'top', 'bottom') + vertPad;
        }
    }
    // Following steps can't be applied to the document's root element as its
    // client[Width/Height] properties represent viewport area of the window.
    // Besides, it's as well not necessary as the <html> itself neither has
    // rendered scroll bars nor it can be clipped.
    if (!isDocumentElement(target)) {
        // In some browsers (only in Firefox, actually) CSS width & height
        // include scroll bars size which can be removed at this step as scroll
        // bars are the only difference between rounded dimensions + paddings
        // and "client" properties, though that is not always true in Chrome.
        var vertScrollbar = Math.round(width + horizPad) - clientWidth;
        var horizScrollbar = Math.round(height + vertPad) - clientHeight;
        // Chrome has a rather weird rounding of "client" properties.
        // E.g. for an element with content width of 314.2px it sometimes gives
        // the client width of 315px and for the width of 314.7px it may give
        // 314px. And it doesn't happen all the time. So just ignore this delta
        // as a non-relevant.
        if (Math.abs(vertScrollbar) !== 1) {
            width -= vertScrollbar;
        }
        if (Math.abs(horizScrollbar) !== 1) {
            height -= horizScrollbar;
        }
    }
    return createRectInit(paddings.left, paddings.top, width, height);
}
/**
 * Checks whether provided element is an instance of the SVGGraphicsElement.
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
var isSVGGraphicsElement = (function () {
    // Some browsers, namely IE and Edge, don't have the SVGGraphicsElement
    // interface.
    if (typeof SVGGraphicsElement !== 'undefined') {
        return function (target) { return target instanceof getWindowOf(target).SVGGraphicsElement; };
    }
    // If it's so, then check that element is at least an instance of the
    // SVGElement and that it has the "getBBox" method.
    // eslint-disable-next-line no-extra-parens
    return function (target) { return (target instanceof getWindowOf(target).SVGElement &&
        typeof target.getBBox === 'function'); };
})();
/**
 * Checks whether provided element is a document element (<html>).
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
function isDocumentElement(target) {
    return target === getWindowOf(target).document.documentElement;
}
/**
 * Calculates an appropriate content rectangle for provided html or svg element.
 *
 * @param {Element} target - Element content rectangle of which needs to be calculated.
 * @returns {DOMRectInit}
 */
function getContentRect(target) {
    if (!isBrowser) {
        return emptyRect;
    }
    if (isSVGGraphicsElement(target)) {
        return getSVGContentRect(target);
    }
    return getHTMLElementContentRect(target);
}
/**
 * Creates rectangle with an interface of the DOMRectReadOnly.
 * Spec: https://drafts.fxtf.org/geometry/#domrectreadonly
 *
 * @param {DOMRectInit} rectInit - Object with rectangle's x/y coordinates and dimensions.
 * @returns {DOMRectReadOnly}
 */
function createReadOnlyRect(_a) {
    var x = _a.x, y = _a.y, width = _a.width, height = _a.height;
    // If DOMRectReadOnly is available use it as a prototype for the rectangle.
    var Constr = typeof DOMRectReadOnly !== 'undefined' ? DOMRectReadOnly : Object;
    var rect = Object.create(Constr.prototype);
    // Rectangle's properties are not writable and non-enumerable.
    defineConfigurable(rect, {
        x: x, y: y, width: width, height: height,
        top: y,
        right: x + width,
        bottom: height + y,
        left: x
    });
    return rect;
}
/**
 * Creates DOMRectInit object based on the provided dimensions and the x/y coordinates.
 * Spec: https://drafts.fxtf.org/geometry/#dictdef-domrectinit
 *
 * @param {number} x - X coordinate.
 * @param {number} y - Y coordinate.
 * @param {number} width - Rectangle's width.
 * @param {number} height - Rectangle's height.
 * @returns {DOMRectInit}
 */
function createRectInit(x, y, width, height) {
    return { x: x, y: y, width: width, height: height };
}

/**
 * Class that is responsible for computations of the content rectangle of
 * provided DOM element and for keeping track of it's changes.
 */
var ResizeObservation = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObservation.
     *
     * @param {Element} target - Element to be observed.
     */
    function ResizeObservation(target) {
        /**
         * Broadcasted width of content rectangle.
         *
         * @type {number}
         */
        this.broadcastWidth = 0;
        /**
         * Broadcasted height of content rectangle.
         *
         * @type {number}
         */
        this.broadcastHeight = 0;
        /**
         * Reference to the last observed content rectangle.
         *
         * @private {DOMRectInit}
         */
        this.contentRect_ = createRectInit(0, 0, 0, 0);
        this.target = target;
    }
    /**
     * Updates content rectangle and tells whether it's width or height properties
     * have changed since the last broadcast.
     *
     * @returns {boolean}
     */
    ResizeObservation.prototype.isActive = function () {
        var rect = getContentRect(this.target);
        this.contentRect_ = rect;
        return (rect.width !== this.broadcastWidth ||
            rect.height !== this.broadcastHeight);
    };
    /**
     * Updates 'broadcastWidth' and 'broadcastHeight' properties with a data
     * from the corresponding properties of the last observed content rectangle.
     *
     * @returns {DOMRectInit} Last observed content rectangle.
     */
    ResizeObservation.prototype.broadcastRect = function () {
        var rect = this.contentRect_;
        this.broadcastWidth = rect.width;
        this.broadcastHeight = rect.height;
        return rect;
    };
    return ResizeObservation;
}());

var ResizeObserverEntry = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObserverEntry.
     *
     * @param {Element} target - Element that is being observed.
     * @param {DOMRectInit} rectInit - Data of the element's content rectangle.
     */
    function ResizeObserverEntry(target, rectInit) {
        var contentRect = createReadOnlyRect(rectInit);
        // According to the specification following properties are not writable
        // and are also not enumerable in the native implementation.
        //
        // Property accessors are not being used as they'd require to define a
        // private WeakMap storage which may cause memory leaks in browsers that
        // don't support this type of collections.
        defineConfigurable(this, { target: target, contentRect: contentRect });
    }
    return ResizeObserverEntry;
}());

var ResizeObserverSPI = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback function that is invoked
     *      when one of the observed elements changes it's content dimensions.
     * @param {ResizeObserverController} controller - Controller instance which
     *      is responsible for the updates of observer.
     * @param {ResizeObserver} callbackCtx - Reference to the public
     *      ResizeObserver instance which will be passed to callback function.
     */
    function ResizeObserverSPI(callback, controller, callbackCtx) {
        /**
         * Collection of resize observations that have detected changes in dimensions
         * of elements.
         *
         * @private {Array<ResizeObservation>}
         */
        this.activeObservations_ = [];
        /**
         * Registry of the ResizeObservation instances.
         *
         * @private {Map<Element, ResizeObservation>}
         */
        this.observations_ = new MapShim();
        if (typeof callback !== 'function') {
            throw new TypeError('The callback provided as parameter 1 is not a function.');
        }
        this.callback_ = callback;
        this.controller_ = controller;
        this.callbackCtx_ = callbackCtx;
    }
    /**
     * Starts observing provided element.
     *
     * @param {Element} target - Element to be observed.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.observe = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is already being observed.
        if (observations.has(target)) {
            return;
        }
        observations.set(target, new ResizeObservation(target));
        this.controller_.addObserver(this);
        // Force the update of observations.
        this.controller_.refresh();
    };
    /**
     * Stops observing provided element.
     *
     * @param {Element} target - Element to stop observing.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.unobserve = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is not being observed.
        if (!observations.has(target)) {
            return;
        }
        observations.delete(target);
        if (!observations.size) {
            this.controller_.removeObserver(this);
        }
    };
    /**
     * Stops observing all elements.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.disconnect = function () {
        this.clearActive();
        this.observations_.clear();
        this.controller_.removeObserver(this);
    };
    /**
     * Collects observation instances the associated element of which has changed
     * it's content rectangle.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.gatherActive = function () {
        var _this = this;
        this.clearActive();
        this.observations_.forEach(function (observation) {
            if (observation.isActive()) {
                _this.activeObservations_.push(observation);
            }
        });
    };
    /**
     * Invokes initial callback function with a list of ResizeObserverEntry
     * instances collected from active resize observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.broadcastActive = function () {
        // Do nothing if observer doesn't have active observations.
        if (!this.hasActive()) {
            return;
        }
        var ctx = this.callbackCtx_;
        // Create ResizeObserverEntry instance for every active observation.
        var entries = this.activeObservations_.map(function (observation) {
            return new ResizeObserverEntry(observation.target, observation.broadcastRect());
        });
        this.callback_.call(ctx, entries, ctx);
        this.clearActive();
    };
    /**
     * Clears the collection of active observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.clearActive = function () {
        this.activeObservations_.splice(0);
    };
    /**
     * Tells whether observer has active observations.
     *
     * @returns {boolean}
     */
    ResizeObserverSPI.prototype.hasActive = function () {
        return this.activeObservations_.length > 0;
    };
    return ResizeObserverSPI;
}());

// Registry of internal observers. If WeakMap is not available use current shim
// for the Map collection as it has all required methods and because WeakMap
// can't be fully polyfilled anyway.
var observers = typeof WeakMap !== 'undefined' ? new WeakMap() : new MapShim();
/**
 * ResizeObserver API. Encapsulates the ResizeObserver SPI implementation
 * exposing only those methods and properties that are defined in the spec.
 */
var ResizeObserver = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback that is invoked when
     *      dimensions of the observed elements change.
     */
    function ResizeObserver(callback) {
        if (!(this instanceof ResizeObserver)) {
            throw new TypeError('Cannot call a class as a function.');
        }
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        var controller = ResizeObserverController.getInstance();
        var observer = new ResizeObserverSPI(callback, controller, this);
        observers.set(this, observer);
    }
    return ResizeObserver;
}());
// Expose public methods of ResizeObserver.
[
    'observe',
    'unobserve',
    'disconnect'
].forEach(function (method) {
    ResizeObserver.prototype[method] = function () {
        var _a;
        return (_a = observers.get(this))[method].apply(_a, arguments);
    };
});

var index = (function () {
    // Export existing implementation if available.
    if (typeof global$1.ResizeObserver !== 'undefined') {
        return global$1.ResizeObserver;
    }
    return ResizeObserver;
})();

/* harmony default export */ __webpack_exports__["default"] = (index);

/* WEBPACK VAR INJECTION */}.call(__webpack_exports__, __webpack_require__(18)));

/***/ }),

/***/ 6:
/***/ (function(module, exports) {

module.exports = emitter;

/***/ }),

/***/ 7:
/***/ (function(module, exports) {

module.exports = icon;

/***/ }),

/***/ 75:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _emitter = __webpack_require__(6);

var _emitter2 = _interopRequireDefault(_emitter);

var _locale = __webpack_require__(3);

var _locale2 = _interopRequireDefault(_locale);

var _checkbox = __webpack_require__(16);

var _checkbox2 = _interopRequireDefault(_checkbox);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

exports.default = {
  name: _base.PREFIX + 'option',
  components: {
    BytedCheckbox: _checkbox2.default
  },

  mixins: [_emitter2.default, _locale2.default],

  inject: ['select', 'optgroup'],

  props: {
    value: {
      type: [String, Number, Array],
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    }
  },

  data: function data() {
    return {
      visible: true,
      innerDisable: this.disabled
    };
  },


  computed: {
    baseClass: function baseClass() {
      var baseCls = _base.CSS_PREFIX + 'option';
      var classArr = [baseCls];
      if (this.isSelected) {
        classArr.push(baseCls + '-selected');
      }
      if (this.finallyDisabled) {
        classArr.push(baseCls + '-disabled');
      }
      if (this.select.multiple) {
        classArr.push(baseCls + '-multiple');
      }
      if (this.isInGroup) {
        classArr.push(baseCls + '-ingroup');
      }
      if (this.isRTL) {
        classArr.push(baseCls + '-rtl');
      }
      classArr.push(baseCls + '-' + this.select.size);
      return classArr;
    },
    currentLabel: function currentLabel() {
      var defaultSlot = this.$slots.default || [{}];
      return this.label || defaultSlot && defaultSlot[0].text && defaultSlot[0].text.trim() || this.value;
    },
    isSelected: function isSelected() {
      return Array.isArray(this.select.value) ? this.select.value.includes(this.value) : this.select.value === this.value;
    },
    isInGroup: function isInGroup() {
      return !!this.optgroup;
    },
    isGroupDisable: function isGroupDisable() {
      if (!this.isInGroup) return false;
      return this.optgroup.disabled;
    },
    finallyDisabled: function finallyDisabled() {
      return this.innerDisable || this.isGroupDisable;
    }
  },

  watch: {
    disabled: function disabled(val) {
      this.innerDisable = val;
    }
  },

  created: function created() {
    this.select && this.select.optionsArr.push(this);
    this.optgroup && this.optgroup.optionsArr.push(this);
    this.$on('queryChange', this.handleQueryChange);
  },
  beforeDestroy: function beforeDestroy() {
    // 移除卸载的option实例
    this.select && this.select.handleOptionDestroy(this);
    this.optgroup && this.optgroup.handleOptionDestroy(this);
  },


  methods: {
    handleClick: function handleClick() {
      if (this.finallyDisabled) {
        return;
      }
      this.select.handleOptionClick(this); // 调用select上的函数
    },
    handleCheckChange: function handleCheckChange() {
      this.handleClick();
    },
    handleQueryChange: function handleQueryChange(query) {
      // query 里如果有正则中的特殊字符，需要先将这些字符转义
      var parsedQuery = String(query).replace(/(\^|\(|\)|\[|\]|\$|\*|\+|\.|\?|\\|\{|\}|\|)/g, '\\$1');
      this.visible = new RegExp(parsedQuery, 'i').test(this.currentLabel);
    }
  }
};

/***/ }),

/***/ 8:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.hasClass = hasClass;
exports.addClass = addClass;
exports.removeClass = removeClass;
exports.setStyle = setStyle;
exports.insertAfter = insertAfter;
exports.scrollTop = scrollTop;
exports.contains = contains;
var SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
var MOZ_HACK_REGEXP = /^moz([A-Z])/;
var ieVersion = Number(document.documentMode);

/* istanbul ignore next */
var trim = function trim(string) {
  return (string || '').replace(/^[\s\uFEFF]+|[\s\uFEFF]+$/g, '');
};
/* istanbul ignore next */
var camelCase = function camelCase(name) {
  return name.replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
    return offset ? letter.toUpperCase() : letter;
  }).replace(MOZ_HACK_REGEXP, 'Moz$1');
};

/* istanbul ignore next */
var on = exports.on = function () {
  if (document.addEventListener) {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.addEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.attachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
var off = exports.off = function () {
  if (document.removeEventListener) {
    return function (element, event, handler) {
      if (element && event) {
        element.removeEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event) {
        element.detachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
exports.once = function once(el, event, fn) {
  var listener = function listener() {
    if (fn) {
      for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      fn.apply(this, args);
    }
    off(el, event, listener);
  };
  on(el, event, listener);
};

/* istanbul ignore next */
function hasClass(el, cls) {
  if (!el || !cls) return false;
  if (cls.indexOf(' ') !== -1) {
    throw new Error('className should not contain space.');
  }
  if (el.classList) {
    return el.classList.contains(cls);
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
  }
}
/* istanbul ignore next */
function addClass(el, cls) {
  if (!el) return;
  var curClass = el.className;
  var classes = (cls || '').split(' ');

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.add(clsName);
    } else {
      if (!hasClass(el, clsName)) {
        curClass += ' ' + clsName;
      }
    }
  }
  if (!el.classList) {
    el.className = curClass;
  }
}
/* istanbul ignore next */
function removeClass(el, cls) {
  if (!el || !cls) return;
  var classes = cls.split(' ');
  var curClass = ' ' + el.className + ' ';

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.remove(clsName);
    } else {
      if (hasClass(el, clsName)) {
        curClass = curClass.replace(' ' + clsName + ' ', ' ');
      }
    }
  }
  if (!el.classList) {
    el.className = trim(curClass);
  }
}
/* istanbul ignore next */
exports.getStyle = ieVersion < 9 ? function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'styleFloat';
  }
  try {
    switch (styleName) {
      case 'opacity':
        try {
          return element.filters.item('alpha').opacity / 100;
        } catch (e) {
          return 1.0;
        }
      default:
        return element.style[styleName] || element.currentStyle ? element.currentStyle[styleName] : null;
    }
  } catch (e) {
    return element.style[styleName];
  }
} : function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'cssFloat';
  }
  try {
    var computed = document.defaultView.getComputedStyle(element, '');
    return element.style[styleName] || computed ? computed[styleName] : null;
  } catch (e) {
    return element.style[styleName];
  }
};

/* istanbul ignore next */
function setStyle(element, styleName, value) {
  if (!element || !styleName) return;

  if ((typeof styleName === 'undefined' ? 'undefined' : _typeof(styleName)) === 'object') {
    for (var prop in styleName) {
      if (styleName.hasOwnProperty(prop)) {
        setStyle(element, prop, styleName[prop]);
      }
    }
  } else {
    styleName = camelCase(styleName);
    if (styleName === 'opacity' && ieVersion < 9) {
      element.style.filter = isNaN(value) ? '' : 'alpha(opacity=' + value * 100 + ')';
    } else {
      element.style[styleName] = value;
    }
  }
}// 选中DOM滚动到可视区域
exports.scrollIntoView = function scrollIntoView(container, selected) {
  var direction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'vertical';

  // direction: 方向 vertival || horizontal
  if (!selected) {
    if (direction === 'horizontal') {
      container.scrollLeft = 0;
      return;
    }
    container.scrollTop = 0;
    return;
  }

  var offsetParents = [];
  var pointer = selected.offsetParent;
  while (pointer && container !== pointer && container.contains(pointer)) {
    offsetParents.push(pointer);
    pointer = pointer.offsetParent;
  }
  if (direction === 'horizontal') {
    // 水平方向滚动计算
    var left = selected.offsetLeft + offsetParents.reduce(function (prev, curr) {
      return prev + curr.offsetLeft;
    }, 0);
    var right = left + selected.offsetWidth;
    var viewRectLeft = container.scrollLeft;
    var viewRectRight = viewRectLeft + container.clientWidth;

    if (left < viewRectLeft) {
      container.scrollLeft = left;
    } else if (right > viewRectRight) {
      container.scrollLeft = right - container.clientWidth;
    }
    return;
  }
  var top = selected.offsetTop + offsetParents.reduce(function (prev, curr) {
    return prev + curr.offsetTop;
  }, 0);
  var bottom = top + selected.offsetHeight;
  var viewRectTop = container.scrollTop;
  var viewRectBottom = viewRectTop + container.clientHeight;

  if (top < viewRectTop) {
    container.scrollTop = top;
  } else if (bottom > viewRectBottom) {
    container.scrollTop = bottom - container.clientHeight;
  }
};
// 计算滚动条宽度
var scrollBarWidth = void 0;
exports.getScrollBarWidth = function getScrollBarWidth() {
  if (typeof scrollBarWidth !== 'undefined') return scrollBarWidth;
  var outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  outer.style.padding = 0;
  document.body.appendChild(outer);
  scrollBarWidth = outer.offsetHeight;
  document.body.removeChild(outer);
  return scrollBarWidth;
};

function insertAfter(el, refenerceEle) {
  var parent = refenerceEle.parentNode;
  // 如果最后的节点是目标元素，则直接添加
  if (parent.lastChild === refenerceEle) {
    parent.appendChild(el);
  } else {
    // 如果不是，则插入在目标元素的下一个兄弟节点 的前面
    parent.insertBefore(el, refenerceEle.nextSibling);
  }
}
exports.removeDom = function removeDom(el) {
  if (!el) return;
  if (el.remove) {
    el.remove();
    return;
  }
  var parent = el.parentNode;
  if (parent && parent.removeChild) {
    parent.removeChild(el);
  }
};
// scrollTop animation
function scrollTop(el) {
  var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var to = arguments[2];
  var duration = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 500;

  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame = window.webkitRequestAnimationFrame || window.mozRequestAnimationFrame || window.msRequestAnimationFrame || function (callback) {
      return window.setTimeout(callback, 1000 / 60);
    };
  }
  var difference = Math.abs(from - to);
  var step = Math.ceil(difference / duration * 50);

  function scroll(start, end, step) {
    if (start === end) return;

    var d = start + step > end ? end : start + step;
    if (start > end) {
      d = start - step < end ? end : start - step;
    }

    if (el === window) {
      window.scrollTo(d, d);
    } else {
      el.scrollTop = d;
    }
    window.requestAnimationFrame(function () {
      return scroll(d, end, step);
    });
  }
  scroll(from, to, step);
}

function contains(el, parent) {
  if (!el || !parent) return false;
  while (el = el.parentNode) {
    if (el === parent) return true;
  }
  return false;
}
/***/ }),

/***/ 9:
/***/ (function(module, exports) {

module.exports = popper;

/***/ }),

/***/ 92:
/***/ (function(module, exports) {

module.exports = tag;

/***/ })

/******/ });
});

var _Select = /*@__PURE__*/getDefaultExportFromCjs(select);

var css_248z$1 = ".bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default,.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.by-loading{display:inline-block}.by-loading .bui-loading-icon{display:-webkit-box;display:-ms-flexbox;display:flex;width:28px;height:28px}.by-loading .bui-loading-icon .bui-icon-loading{width:100%;height:100%}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.by-loading .bui-loading-icon .bui-icon-loading .bui-loading-group{stroke:#338AFF}.by-loading .bui-loading-icon-sm,.by-loading .bui-loading-icon-small{height:14px;width:14px}.by-loading .bui-loading-icon-md,.by-loading .bui-loading-icon-normal{height:18px;width:18px}.by-loading .bui-loading-icon-xs{height:12px;width:12px}.by-loading .bui-loading-icon-lg{height:36px;width:36px}.by-loading .bui-loading-icon-xl{height:56px;width:56px}.by-loading-v{position:absolute;top:0;right:0;bottom:0;left:0;z-index:996;background-color:rgba(244,244,244,.9)}.by-loading-v .bui-loading-wrapper{position:absolute;top:50%;left:50%;-webkit-transform:translate(-50%,-50%);transform:translate(-50%,-50%);text-align:center}.by-loading-v .bui-loading-icon{margin:auto}.by-loading-v .bui-loading-text{color:#338bff;margin-top:2px}.by-loading-v .bui-loading-text-sm,.by-loading-v .bui-loading-text-xs{font-size:12px;line-height:20px}.by-loading-v .bui-loading-text-md{font-size:14px;line-height:22px}.by-loading-v .bui-loading-text-lg{font-size:16px;line-height:24px;margin-top:4px}.by-loading-v .bui-loading-text-xl{font-size:18px;line-height:26px;margin-top:6px}";
styleInject(css_248z$1);

var css_248z = ".bui-btn.disabled .bui-icon svg,.bui-btn.disabled .by-icon svg,.bui-btn.disabled.focus .bui-icon svg,.bui-btn.disabled.focus .by-icon svg,.bui-btn.disabled:focus .bui-icon svg,.bui-btn.disabled:focus .by-icon svg,.bui-btn.disabled:hover .bui-icon svg,.bui-btn.disabled:hover .by-icon svg,.bui-btn[disabled] .bui-icon svg,.bui-btn[disabled] .by-icon svg,.bui-btn[disabled].focus .bui-icon svg,.bui-btn[disabled].focus .by-icon svg,.bui-btn[disabled]:focus .bui-icon svg,.bui-btn[disabled]:focus .by-icon svg,.bui-btn[disabled]:hover .bui-icon svg,.bui-btn[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn .bui-icon svg,fieldset[disabled] .bui-btn .by-icon svg,fieldset[disabled] .bui-btn.focus .bui-icon svg,fieldset[disabled] .bui-btn.focus .by-icon svg,fieldset[disabled] .bui-btn:focus .bui-icon svg,fieldset[disabled] .bui-btn:focus .by-icon svg,fieldset[disabled] .bui-btn:hover .bui-icon svg,fieldset[disabled] .bui-btn:hover .by-icon svg{fill:#C1C1C1}@-webkit-keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}@keyframes bui-loading-opacity{0%{opacity:1}100%{opacity:0}}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(8){-webkit-animation:bui-loading-opacity .8s linear -.04ms infinite;animation:bui-loading-opacity .8s linear -.04ms infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(7){-webkit-animation:bui-loading-opacity .8s linear -.11432s infinite;animation:bui-loading-opacity .8s linear -.11432s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(6){-webkit-animation:bui-loading-opacity .8s linear -.2286s infinite;animation:bui-loading-opacity .8s linear -.2286s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(5){-webkit-animation:bui-loading-opacity .8s linear -.34288s infinite;animation:bui-loading-opacity .8s linear -.34288s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(4){-webkit-animation:bui-loading-opacity .8s linear -.45716s infinite;animation:bui-loading-opacity .8s linear -.45716s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(3){-webkit-animation:bui-loading-opacity .8s linear -.57144s infinite;animation:bui-loading-opacity .8s linear -.57144s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(2){-webkit-animation:bui-loading-opacity .8s linear -.68572s infinite;animation:bui-loading-opacity .8s linear -.68572s infinite}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(1){-webkit-animation:bui-loading-opacity .8s linear -.8s infinite;animation:bui-loading-opacity .8s linear -.8s infinite}.bui-loading-opacity-animation .bui-loading-group .bui-loading-group-default{stroke:#58a0ff}.bui-btn.disabled .by-icon .bui-loading-group,.bui-btn.disabled.focus .by-icon .bui-loading-group,.bui-btn.disabled:focus .by-icon .bui-loading-group,.bui-btn.disabled:hover .by-icon .bui-loading-group,.bui-btn[disabled] .by-icon .bui-loading-group,.bui-btn[disabled].focus .by-icon .bui-loading-group,.bui-btn[disabled]:focus .by-icon .bui-loading-group,.bui-btn[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn:hover .by-icon .bui-loading-group{stroke:#C1C1C1}.bui-loading-opacity-animation .bui-loading-path-item:nth-child(0){-webkit-animation:bui-loading-opacity .8s linear -.91428s infinite;animation:bui-loading-opacity .8s linear -.91428s infinite}.bui-btn{position:relative;display:inline-block;font-weight:400;text-align:center;white-space:nowrap;vertical-align:middle;-ms-touch-action:manipulation;touch-action:manipulation;cursor:pointer;background-image:none;border:1px solid transparent;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;min-width:100px;padding:5px 16px;font-size:14px;line-height:22px;border-radius:4px}.bui-btn-sm,.bui-btn-xs{font-size:12px;line-height:20px}.bui-btn .by-loading .bui-loading-icon{height:14px;width:14px}.bui-btn-sm .by-loading .bui-loading-icon,.bui-btn-xs .by-loading .bui-loading-icon{height:12px;width:12px}.bui-btn.focus,.bui-btn:focus,.bui-btn:hover{outline:0;text-decoration:none;z-index:2}.bui-btn.active,.bui-btn:active{background-image:none;outline:0;z-index:3}.bui-btn.active.focus,.bui-btn.active:focus,.bui-btn:active.focus,.bui-btn:active:focus{outline:0}.bui-btn.disabled,.bui-btn[disabled],fieldset[disabled] .bui-btn{cursor:not-allowed;color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn.disabled.focus,.bui-btn.disabled:focus,.bui-btn.disabled:hover,.bui-btn[disabled].focus,.bui-btn[disabled]:focus,.bui-btn[disabled]:hover,fieldset[disabled] .bui-btn.focus,fieldset[disabled] .bui-btn:focus,fieldset[disabled] .bui-btn:hover{color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}a.bui-btn.disabled,fieldset[disabled] a.bui-btn{pointer-events:none}.bui-btn-xs{min-width:60px;padding:2px 8px;border-radius:4px}.bui-btn-sm{min-width:80px;padding:4px 16px;border-radius:4px}.bui-btn-lg,.bui-btn-xl{font-size:14px;line-height:22px;border-radius:4px}.bui-btn-lg .by-loading .bui-loading-icon,.bui-btn-xl .by-loading .bui-loading-icon{height:14px;width:14px}.bui-btn-lg{min-width:120px;padding:7px 16px}.bui-btn-xl{min-width:140px;padding:9px 16px}.bui-btn-loading{display:-webkit-inline-box;display:-ms-inline-flexbox;display:inline-flex;-webkit-box-align:center;-ms-flex-align:center;align-items:center;-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center}.bui-btn-loading .bui-loading-icon{margin-right:4px}.bui-btn-default.bui-btn-plain{color:#333;background-color:#FAFAFA;border-color:#E0E0E0}.bui-btn-default.bui-btn-plain .bui-icon svg,.bui-btn-default.bui-btn-plain .by-icon svg{fill:#333}.bui-btn-default.bui-btn-plain.focus .bui-icon svg,.bui-btn-default.bui-btn-plain.focus .by-icon svg,.bui-btn-default.bui-btn-plain:focus .bui-icon svg,.bui-btn-default.bui-btn-plain:focus .by-icon svg,.bui-btn-default.bui-btn-plain:hover .bui-icon svg,.bui-btn-default.bui-btn-plain:hover .by-icon svg{fill:#338AFF}.bui-btn-default.bui-btn-plain .by-icon .bui-loading-group{stroke:#333}.bui-btn-default.bui-btn-plain.focus .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain:focus .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#338AFF}.bui-btn-default.bui-btn-plain:hover{color:#338AFF;background-color:#FAFAFA;border-color:#338AFF}.bui-btn-default.bui-btn-plain.focus,.bui-btn-default.bui-btn-plain:focus{color:#338AFF;background-color:#ebf3ff;border-color:#338AFF}.bui-btn-default.bui-btn-plain.active,.bui-btn-default.bui-btn-plain:active{color:#0672ff;background-color:#FAFAFA;border-color:#0672ff}.bui-btn-default.bui-btn-plain.active .bui-icon svg,.bui-btn-default.bui-btn-plain.active .by-icon svg,.bui-btn-default.bui-btn-plain:active .bui-icon svg,.bui-btn-default.bui-btn-plain:active .by-icon svg{fill:#0672ff}.bui-btn-default.bui-btn-plain.disabled .bui-icon svg,.bui-btn-default.bui-btn-plain.disabled .by-icon svg,.bui-btn-default.bui-btn-plain.disabled.focus .bui-icon svg,.bui-btn-default.bui-btn-plain.disabled.focus .by-icon svg,.bui-btn-default.bui-btn-plain.disabled:focus .bui-icon svg,.bui-btn-default.bui-btn-plain.disabled:focus .by-icon svg,.bui-btn-default.bui-btn-plain.disabled:hover .bui-icon svg,.bui-btn-default.bui-btn-plain.disabled:hover .by-icon svg,.bui-btn-default.bui-btn-plain[disabled] .bui-icon svg,.bui-btn-default.bui-btn-plain[disabled] .by-icon svg,.bui-btn-default.bui-btn-plain[disabled].focus .bui-icon svg,.bui-btn-default.bui-btn-plain[disabled].focus .by-icon svg,.bui-btn-default.bui-btn-plain[disabled]:focus .bui-icon svg,.bui-btn-default.bui-btn-plain[disabled]:focus .by-icon svg,.bui-btn-default.bui-btn-plain[disabled]:hover .bui-icon svg,.bui-btn-default.bui-btn-plain[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn-default.bui-btn-plain .bui-icon svg,fieldset[disabled] .bui-btn-default.bui-btn-plain .by-icon svg,fieldset[disabled] .bui-btn-default.bui-btn-plain.focus .bui-icon svg,fieldset[disabled] .bui-btn-default.bui-btn-plain.focus .by-icon svg,fieldset[disabled] .bui-btn-default.bui-btn-plain:focus .bui-icon svg,fieldset[disabled] .bui-btn-default.bui-btn-plain:focus .by-icon svg,fieldset[disabled] .bui-btn-default.bui-btn-plain:hover .bui-icon svg,fieldset[disabled] .bui-btn-default.bui-btn-plain:hover .by-icon svg{fill:#C1C1C1}.bui-btn-default.bui-btn-plain.active .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain:active .by-icon .bui-loading-group{stroke:#0672ff}.bui-btn-default.bui-btn-plain.disabled .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain.disabled.focus .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain.disabled:focus .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain.disabled:hover .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain[disabled] .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain[disabled].focus .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain[disabled]:focus .by-icon .bui-loading-group,.bui-btn-default.bui-btn-plain[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn-default.bui-btn-plain .by-icon .bui-loading-group,fieldset[disabled] .bui-btn-default.bui-btn-plain.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn-default.bui-btn-plain:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn-default.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#C1C1C1}.bui-btn-default.bui-btn-plain.disabled,.bui-btn-default.bui-btn-plain[disabled],fieldset[disabled] .bui-btn-default.bui-btn-plain{cursor:not-allowed;color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn-default.bui-btn-plain.disabled.focus,.bui-btn-default.bui-btn-plain.disabled:focus,.bui-btn-default.bui-btn-plain.disabled:hover,.bui-btn-default.bui-btn-plain[disabled].focus,.bui-btn-default.bui-btn-plain[disabled]:focus,.bui-btn-default.bui-btn-plain[disabled]:hover,fieldset[disabled] .bui-btn-default.bui-btn-plain.focus,fieldset[disabled] .bui-btn-default.bui-btn-plain:focus,fieldset[disabled] .bui-btn-default.bui-btn-plain:hover{color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn-default .bui-icon svg,.bui-btn-default .by-icon svg,.bui-btn-default.active .bui-icon svg,.bui-btn-default.active .by-icon svg,.bui-btn-default.focus .bui-icon svg,.bui-btn-default.focus .by-icon svg,.bui-btn-default:active .bui-icon svg,.bui-btn-default:active .by-icon svg,.bui-btn-default:focus .bui-icon svg,.bui-btn-default:focus .by-icon svg,.bui-btn-default:hover .bui-icon svg,.bui-btn-default:hover .by-icon svg{fill:#333}.bui-btn-default .by-icon .bui-loading-group,.bui-btn-default.active .by-icon .bui-loading-group,.bui-btn-default.focus .by-icon .bui-loading-group,.bui-btn-default:active .by-icon .bui-loading-group,.bui-btn-default:focus .by-icon .bui-loading-group,.bui-btn-default:hover .by-icon .bui-loading-group{stroke:#333}.bui-btn-default{color:#333;background-color:#F0F0F0;border-color:#F0F0F0}.bui-btn-default.focus,.bui-btn-default:focus,.bui-btn-default:hover{background-color:#EBEBEB;border-color:#EBEBEB;color:#333}.bui-btn-default.active,.bui-btn-default:active{color:#333;background-color:#E0E0E0;border-color:#E0E0E0}.bui-btn-default.disabled .bui-icon svg,.bui-btn-default.disabled .by-icon svg,.bui-btn-default.disabled.focus .bui-icon svg,.bui-btn-default.disabled.focus .by-icon svg,.bui-btn-default.disabled:focus .bui-icon svg,.bui-btn-default.disabled:focus .by-icon svg,.bui-btn-default.disabled:hover .bui-icon svg,.bui-btn-default.disabled:hover .by-icon svg,.bui-btn-default[disabled] .bui-icon svg,.bui-btn-default[disabled] .by-icon svg,.bui-btn-default[disabled].focus .bui-icon svg,.bui-btn-default[disabled].focus .by-icon svg,.bui-btn-default[disabled]:focus .bui-icon svg,.bui-btn-default[disabled]:focus .by-icon svg,.bui-btn-default[disabled]:hover .bui-icon svg,.bui-btn-default[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn-default .bui-icon svg,fieldset[disabled] .bui-btn-default .by-icon svg,fieldset[disabled] .bui-btn-default.focus .bui-icon svg,fieldset[disabled] .bui-btn-default.focus .by-icon svg,fieldset[disabled] .bui-btn-default:focus .bui-icon svg,fieldset[disabled] .bui-btn-default:focus .by-icon svg,fieldset[disabled] .bui-btn-default:hover .bui-icon svg,fieldset[disabled] .bui-btn-default:hover .by-icon svg{fill:#C1C1C1}.bui-btn-default.disabled .by-icon .bui-loading-group,.bui-btn-default.disabled.focus .by-icon .bui-loading-group,.bui-btn-default.disabled:focus .by-icon .bui-loading-group,.bui-btn-default.disabled:hover .by-icon .bui-loading-group,.bui-btn-default[disabled] .by-icon .bui-loading-group,.bui-btn-default[disabled].focus .by-icon .bui-loading-group,.bui-btn-default[disabled]:focus .by-icon .bui-loading-group,.bui-btn-default[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn-default .by-icon .bui-loading-group,fieldset[disabled] .bui-btn-default.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn-default:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn-default:hover .by-icon .bui-loading-group{stroke:#C1C1C1}.bui-btn-default.disabled,.bui-btn-default[disabled],fieldset[disabled] .bui-btn-default{cursor:not-allowed;color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn-default.disabled.focus,.bui-btn-default.disabled:focus,.bui-btn-default.disabled:hover,.bui-btn-default[disabled].focus,.bui-btn-default[disabled]:focus,.bui-btn-default[disabled]:hover,fieldset[disabled] .bui-btn-default.focus,fieldset[disabled] .bui-btn-default:focus,fieldset[disabled] .bui-btn-default:hover{color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn.bui-btn-primary .bui-icon svg,.bui-btn.bui-btn-primary .by-icon svg,.bui-btn.bui-btn-primary.active .bui-icon svg,.bui-btn.bui-btn-primary.active .by-icon svg,.bui-btn.bui-btn-primary.active.focus .bui-icon svg,.bui-btn.bui-btn-primary.active.focus .by-icon svg,.bui-btn.bui-btn-primary.active:focus .bui-icon svg,.bui-btn.bui-btn-primary.active:focus .by-icon svg,.bui-btn.bui-btn-primary.active:hover .bui-icon svg,.bui-btn.bui-btn-primary.active:hover .by-icon svg,.bui-btn.bui-btn-primary.focus .bui-icon svg,.bui-btn.bui-btn-primary.focus .by-icon svg,.bui-btn.bui-btn-primary:active .bui-icon svg,.bui-btn.bui-btn-primary:active .by-icon svg,.bui-btn.bui-btn-primary:active.focus .bui-icon svg,.bui-btn.bui-btn-primary:active.focus .by-icon svg,.bui-btn.bui-btn-primary:active:focus .bui-icon svg,.bui-btn.bui-btn-primary:active:focus .by-icon svg,.bui-btn.bui-btn-primary:active:hover .bui-icon svg,.bui-btn.bui-btn-primary:active:hover .by-icon svg,.bui-btn.bui-btn-primary:focus .bui-icon svg,.bui-btn.bui-btn-primary:focus .by-icon svg,.bui-btn.bui-btn-primary:hover .bui-icon svg,.bui-btn.bui-btn-primary:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary.focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary.focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary:focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary:focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary:hover .by-icon svg{fill:#FFF}.bui-btn.bui-btn-primary .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary:active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-primary .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-primary.focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-primary:focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-primary:hover .by-icon .bui-loading-group{stroke:#FFF}.bui-btn.bui-btn-primary{color:#FFF;background-color:#338AFF;border-color:#338AFF}.bui-btn.bui-btn-primary.active,.bui-btn.bui-btn-primary.active.focus,.bui-btn.bui-btn-primary.active:focus,.bui-btn.bui-btn-primary.active:hover,.bui-btn.bui-btn-primary:active,.bui-btn.bui-btn-primary:active.focus,.bui-btn.bui-btn-primary:active:focus,.bui-btn.bui-btn-primary:active:hover,.open>.dropdown-toggle.bui-btn.bui-btn-primary,.open>.dropdown-toggle.bui-btn.bui-btn-primary.focus,.open>.dropdown-toggle.bui-btn.bui-btn-primary:focus,.open>.dropdown-toggle.bui-btn.bui-btn-primary:hover{color:#FFF;background-color:#0672ff;border-color:#0672ff}.bui-btn.bui-btn-primary.focus,.bui-btn.bui-btn-primary:focus,.bui-btn.bui-btn-primary:hover{color:#FFF;background-color:#58a0ff;border-color:#58a0ff}.bui-btn.bui-btn-primary.bui-btn-loading,.bui-btn.bui-btn-primary.bui-btn-loading.focus,.bui-btn.bui-btn-primary.bui-btn-loading:active,.bui-btn.bui-btn-primary.bui-btn-loading:focus,.bui-btn.bui-btn-primary.bui-btn-loading:hover,.bui-btn.bui-btn-primary.disabled,.bui-btn.bui-btn-primary.disabled.focus,.bui-btn.bui-btn-primary.disabled:active,.bui-btn.bui-btn-primary.disabled:focus,.bui-btn.bui-btn-primary.disabled:hover,.bui-btn.bui-btn-primary[disabled],.bui-btn.bui-btn-primary[disabled].focus,.bui-btn.bui-btn-primary[disabled]:active,.bui-btn.bui-btn-primary[disabled]:focus,.bui-btn.bui-btn-primary[disabled]:hover,fieldset[disabled] .bui-btn.bui-btn-primary,fieldset[disabled] .bui-btn.bui-btn-primary.focus,fieldset[disabled] .bui-btn.bui-btn-primary:active,fieldset[disabled] .bui-btn.bui-btn-primary:focus,fieldset[disabled] .bui-btn.bui-btn-primary:hover{color:#ebf3ff;background-color:#a1caff;border-color:#a1caff}.bui-btn.bui-btn-primary.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-primary.disabled .bui-icon svg,.bui-btn.bui-btn-primary.disabled .by-icon svg,.bui-btn.bui-btn-primary.disabled.focus .bui-icon svg,.bui-btn.bui-btn-primary.disabled.focus .by-icon svg,.bui-btn.bui-btn-primary.disabled:active .bui-icon svg,.bui-btn.bui-btn-primary.disabled:active .by-icon svg,.bui-btn.bui-btn-primary.disabled:focus .bui-icon svg,.bui-btn.bui-btn-primary.disabled:focus .by-icon svg,.bui-btn.bui-btn-primary.disabled:hover .bui-icon svg,.bui-btn.bui-btn-primary.disabled:hover .by-icon svg,.bui-btn.bui-btn-primary[disabled] .bui-icon svg,.bui-btn.bui-btn-primary[disabled] .by-icon svg,.bui-btn.bui-btn-primary[disabled].focus .bui-icon svg,.bui-btn.bui-btn-primary[disabled].focus .by-icon svg,.bui-btn.bui-btn-primary[disabled]:active .bui-icon svg,.bui-btn.bui-btn-primary[disabled]:active .by-icon svg,.bui-btn.bui-btn-primary[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-primary[disabled]:focus .by-icon svg,.bui-btn.bui-btn-primary[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-primary[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary.focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary.focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary:active .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary:active .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary:focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary:focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary:hover .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-primary:hover .by-icon svg{fill:#ebf3ff}.bui-btn.bui-btn-primary.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-primary[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-primary .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-primary.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-primary:active .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-primary:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-primary:hover .by-icon .bui-loading-group{stroke:#ebf3ff}.bui-btn.bui-btn-primary.bui-btn-plain{color:#333;background-color:#FFF;border-color:#E0E0E0}.bui-btn.bui-btn-primary.bui-btn-plain .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain .by-icon svg{fill:#333}.bui-btn.bui-btn-primary.bui-btn-plain .by-icon .bui-loading-group{stroke:#333}.bui-btn.bui-btn-primary.bui-btn-plain.active,.bui-btn.bui-btn-primary.bui-btn-plain.active:hover,.bui-btn.bui-btn-primary.bui-btn-plain.focus,.bui-btn.bui-btn-primary.bui-btn-plain.focus:hover,.bui-btn.bui-btn-primary.bui-btn-plain:focus,.bui-btn.bui-btn-primary.bui-btn-plain:focus:hover,.open>.dropdown-toggle.bui-btn.bui-btn-primary.bui-btn-plain,.open>.dropdown-toggle.bui-btn.bui-btn-primary.bui-btn-plain:hover{color:#338AFF;background-color:#ebf3ff;border-color:#338AFF}.bui-btn.bui-btn-primary.bui-btn-plain.active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.active:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.active:hover .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.focus:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.focus:hover .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:focus:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:focus:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary.bui-btn-plain .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary.bui-btn-plain .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary.bui-btn-plain:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-primary.bui-btn-plain:hover .by-icon svg{fill:#338AFF}.bui-btn.bui-btn-primary.bui-btn-plain.active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.focus:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain:focus:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-primary.bui-btn-plain .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-primary.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#338AFF}.bui-btn.bui-btn-primary.bui-btn-plain:hover{color:#58a0ff;background-color:#FFF;border-color:#58a0ff}.bui-btn.bui-btn-primary.bui-btn-plain:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:hover .by-icon svg{fill:#58a0ff}.bui-btn.bui-btn-primary.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#58a0ff}.bui-btn.bui-btn-primary.bui-btn-plain:active,.bui-btn.bui-btn-primary.bui-btn-plain:active.focus,.bui-btn.bui-btn-primary.bui-btn-plain:active:focus,.bui-btn.bui-btn-primary.bui-btn-plain:active:hover{color:#0672ff;background-color:#FFF;border-color:#0672ff}.bui-btn.bui-btn-primary.bui-btn-plain:active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:active.focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:active.focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:active:focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:active:focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:active:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain:active:hover .by-icon svg{fill:#0672ff}.bui-btn.bui-btn-primary.bui-btn-plain:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain:active:hover .by-icon .bui-loading-group{stroke:#0672ff}.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-primary.bui-btn-plain.disabled,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]{cursor:not-allowed}.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.focus,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:active,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:focus,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:hover,.bui-btn.bui-btn-primary.bui-btn-plain.disabled,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.focus,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:active,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:focus,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:hover,.bui-btn.bui-btn-primary.bui-btn-plain[disabled],.bui-btn.bui-btn-primary.bui-btn-plain[disabled].focus,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:active,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:focus,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:hover{color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:hover .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled] .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled] .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:hover .by-icon svg{fill:#C1C1C1}.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled]:hover .by-icon .bui-loading-group{stroke:#C1C1C1}.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active.focus,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:active,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:focus,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:hover,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active.focus,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:active,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:focus,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:hover,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active.focus,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:active,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:focus,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:hover{color:#a1caff;background-color:#ebf3ff;border-color:#a1caff}.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active.focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active.focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:hover .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active.focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active.focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:hover .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active.focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active.focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:active .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:active .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:focus .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:focus .by-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:hover .bui-icon svg,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:hover .by-icon svg{fill:#a1caff}.bui-btn.bui-btn-secondary .bui-icon svg,.bui-btn.bui-btn-secondary .by-icon svg,.bui-btn.bui-btn-secondary.active .bui-icon svg,.bui-btn.bui-btn-secondary.active .by-icon svg,.bui-btn.bui-btn-secondary.active.focus .bui-icon svg,.bui-btn.bui-btn-secondary.active.focus .by-icon svg,.bui-btn.bui-btn-secondary.active:focus .bui-icon svg,.bui-btn.bui-btn-secondary.active:focus .by-icon svg,.bui-btn.bui-btn-secondary.active:hover .bui-icon svg,.bui-btn.bui-btn-secondary.active:hover .by-icon svg,.bui-btn.bui-btn-secondary.focus .bui-icon svg,.bui-btn.bui-btn-secondary.focus .by-icon svg,.bui-btn.bui-btn-secondary:active .bui-icon svg,.bui-btn.bui-btn-secondary:active .by-icon svg,.bui-btn.bui-btn-secondary:active.focus .bui-icon svg,.bui-btn.bui-btn-secondary:active.focus .by-icon svg,.bui-btn.bui-btn-secondary:active:focus .bui-icon svg,.bui-btn.bui-btn-secondary:active:focus .by-icon svg,.bui-btn.bui-btn-secondary:active:hover .bui-icon svg,.bui-btn.bui-btn-secondary:active:hover .by-icon svg,.bui-btn.bui-btn-secondary:focus .bui-icon svg,.bui-btn.bui-btn-secondary:focus .by-icon svg,.bui-btn.bui-btn-secondary:hover .bui-icon svg,.bui-btn.bui-btn-secondary:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary:focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary:focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary:hover .by-icon svg{fill:#FFF}.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.bui-btn-loading.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain.disabled.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-primary.bui-btn-plain[disabled].active:hover .by-icon .bui-loading-group{stroke:#a1caff}.bui-btn.bui-btn-secondary .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary:active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-secondary .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-secondary:focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-secondary:hover .by-icon .bui-loading-group{stroke:#FFF}.bui-btn.bui-btn-secondary{color:#FFF;background-color:#338AFF;border-color:#338AFF}.bui-btn.bui-btn-secondary.active,.bui-btn.bui-btn-secondary.active.focus,.bui-btn.bui-btn-secondary.active:focus,.bui-btn.bui-btn-secondary.active:hover,.bui-btn.bui-btn-secondary:active,.bui-btn.bui-btn-secondary:active.focus,.bui-btn.bui-btn-secondary:active:focus,.bui-btn.bui-btn-secondary:active:hover,.open>.dropdown-toggle.bui-btn.bui-btn-secondary,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.focus,.open>.dropdown-toggle.bui-btn.bui-btn-secondary:focus,.open>.dropdown-toggle.bui-btn.bui-btn-secondary:hover{color:#FFF;background-color:#0672ff;border-color:#0672ff}.bui-btn.bui-btn-secondary.focus,.bui-btn.bui-btn-secondary:focus,.bui-btn.bui-btn-secondary:hover{color:#FFF;background-color:#58a0ff;border-color:#58a0ff}.bui-btn.bui-btn-secondary.bui-btn-loading,.bui-btn.bui-btn-secondary.bui-btn-loading.focus,.bui-btn.bui-btn-secondary.bui-btn-loading:active,.bui-btn.bui-btn-secondary.bui-btn-loading:focus,.bui-btn.bui-btn-secondary.bui-btn-loading:hover,.bui-btn.bui-btn-secondary.disabled,.bui-btn.bui-btn-secondary.disabled.focus,.bui-btn.bui-btn-secondary.disabled:active,.bui-btn.bui-btn-secondary.disabled:focus,.bui-btn.bui-btn-secondary.disabled:hover,.bui-btn.bui-btn-secondary[disabled],.bui-btn.bui-btn-secondary[disabled].focus,.bui-btn.bui-btn-secondary[disabled]:active,.bui-btn.bui-btn-secondary[disabled]:focus,.bui-btn.bui-btn-secondary[disabled]:hover,fieldset[disabled] .bui-btn.bui-btn-secondary,fieldset[disabled] .bui-btn.bui-btn-secondary.focus,fieldset[disabled] .bui-btn.bui-btn-secondary:active,fieldset[disabled] .bui-btn.bui-btn-secondary:focus,fieldset[disabled] .bui-btn.bui-btn-secondary:hover{color:#ebf3ff;background-color:#a1caff;border-color:#a1caff}.bui-btn.bui-btn-secondary.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-secondary.disabled .bui-icon svg,.bui-btn.bui-btn-secondary.disabled .by-icon svg,.bui-btn.bui-btn-secondary.disabled.focus .bui-icon svg,.bui-btn.bui-btn-secondary.disabled.focus .by-icon svg,.bui-btn.bui-btn-secondary.disabled:active .bui-icon svg,.bui-btn.bui-btn-secondary.disabled:active .by-icon svg,.bui-btn.bui-btn-secondary.disabled:focus .bui-icon svg,.bui-btn.bui-btn-secondary.disabled:focus .by-icon svg,.bui-btn.bui-btn-secondary.disabled:hover .bui-icon svg,.bui-btn.bui-btn-secondary.disabled:hover .by-icon svg,.bui-btn.bui-btn-secondary[disabled] .bui-icon svg,.bui-btn.bui-btn-secondary[disabled] .by-icon svg,.bui-btn.bui-btn-secondary[disabled].focus .bui-icon svg,.bui-btn.bui-btn-secondary[disabled].focus .by-icon svg,.bui-btn.bui-btn-secondary[disabled]:active .bui-icon svg,.bui-btn.bui-btn-secondary[disabled]:active .by-icon svg,.bui-btn.bui-btn-secondary[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-secondary[disabled]:focus .by-icon svg,.bui-btn.bui-btn-secondary[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-secondary[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary.focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary.focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary:active .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary:active .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary:focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary:focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary:hover .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-secondary:hover .by-icon svg{fill:#ebf3ff}.bui-btn.bui-btn-secondary.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-secondary .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-secondary.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-secondary:active .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-secondary:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-secondary:hover .by-icon .bui-loading-group{stroke:#ebf3ff}.bui-btn.bui-btn-secondary.bui-btn-plain{color:#333;background-color:#FFF;border-color:#E0E0E0}.bui-btn.bui-btn-secondary.bui-btn-plain .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain .by-icon svg{fill:#333}.bui-btn.bui-btn-secondary.bui-btn-plain .by-icon .bui-loading-group{stroke:#333}.bui-btn.bui-btn-secondary.bui-btn-plain.active,.bui-btn.bui-btn-secondary.bui-btn-plain.active:hover,.bui-btn.bui-btn-secondary.bui-btn-plain.focus,.bui-btn.bui-btn-secondary.bui-btn-plain.focus:hover,.bui-btn.bui-btn-secondary.bui-btn-plain:focus,.bui-btn.bui-btn-secondary.bui-btn-plain:focus:hover,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.bui-btn-plain,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.bui-btn-plain:hover{color:#338AFF;background-color:#ebf3ff;border-color:#338AFF}.bui-btn.bui-btn-secondary.bui-btn-plain.active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.active:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.active:hover .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.focus:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.focus:hover .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:focus:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:focus:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.bui-btn-plain .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.bui-btn-plain .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.bui-btn-plain:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.bui-btn-plain:hover .by-icon svg{fill:#338AFF}.bui-btn.bui-btn-secondary.bui-btn-plain.active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.focus:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain:focus:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.bui-btn-plain .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-secondary.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#338AFF}.bui-btn.bui-btn-secondary.bui-btn-plain:hover{color:#58a0ff;background-color:#FFF;border-color:#58a0ff}.bui-btn.bui-btn-secondary.bui-btn-plain:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:hover .by-icon svg{fill:#58a0ff}.bui-btn.bui-btn-secondary.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#58a0ff}.bui-btn.bui-btn-secondary.bui-btn-plain:active,.bui-btn.bui-btn-secondary.bui-btn-plain:active.focus,.bui-btn.bui-btn-secondary.bui-btn-plain:active:focus,.bui-btn.bui-btn-secondary.bui-btn-plain:active:hover{color:#0672ff;background-color:#FFF;border-color:#0672ff}.bui-btn.bui-btn-secondary.bui-btn-plain:active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:active.focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:active.focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:active:focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:active:focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:active:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain:active:hover .by-icon svg{fill:#0672ff}.bui-btn.bui-btn-secondary.bui-btn-plain:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain:active:hover .by-icon .bui-loading-group{stroke:#0672ff}.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]{cursor:not-allowed}.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.focus,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:active,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:focus,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:hover,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.focus,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:active,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:focus,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:hover,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled],.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].focus,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:active,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:focus,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:hover{color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:hover .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled] .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled] .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:hover .by-icon svg{fill:#C1C1C1}.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled]:hover .by-icon .bui-loading-group{stroke:#C1C1C1}.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active.focus,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:active,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:focus,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:hover,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active.focus,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:active,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:focus,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:hover,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active.focus,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:active,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:focus,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:hover{color:#a1caff;background-color:#ebf3ff;border-color:#a1caff}.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active.focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active.focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:hover .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active.focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active.focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:hover .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active.focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active.focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:active .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:active .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:focus .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:focus .by-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:hover .bui-icon svg,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:hover .by-icon svg{fill:#a1caff}.bui-btn.bui-btn-info .bui-icon svg,.bui-btn.bui-btn-info .by-icon svg,.bui-btn.bui-btn-info.active .bui-icon svg,.bui-btn.bui-btn-info.active .by-icon svg,.bui-btn.bui-btn-info.active.focus .bui-icon svg,.bui-btn.bui-btn-info.active.focus .by-icon svg,.bui-btn.bui-btn-info.active:focus .bui-icon svg,.bui-btn.bui-btn-info.active:focus .by-icon svg,.bui-btn.bui-btn-info.active:hover .bui-icon svg,.bui-btn.bui-btn-info.active:hover .by-icon svg,.bui-btn.bui-btn-info.focus .bui-icon svg,.bui-btn.bui-btn-info.focus .by-icon svg,.bui-btn.bui-btn-info:active .bui-icon svg,.bui-btn.bui-btn-info:active .by-icon svg,.bui-btn.bui-btn-info:active.focus .bui-icon svg,.bui-btn.bui-btn-info:active.focus .by-icon svg,.bui-btn.bui-btn-info:active:focus .bui-icon svg,.bui-btn.bui-btn-info:active:focus .by-icon svg,.bui-btn.bui-btn-info:active:hover .bui-icon svg,.bui-btn.bui-btn-info:active:hover .by-icon svg,.bui-btn.bui-btn-info:focus .bui-icon svg,.bui-btn.bui-btn-info:focus .by-icon svg,.bui-btn.bui-btn-info:hover .bui-icon svg,.bui-btn.bui-btn-info:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info.focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info.focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info:focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info:focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info:hover .by-icon svg{fill:#FFF}.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.bui-btn-loading.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain.disabled.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-secondary.bui-btn-plain[disabled].active:hover .by-icon .bui-loading-group{stroke:#a1caff}.bui-btn.bui-btn-info .by-icon .bui-loading-group,.bui-btn.bui-btn-info.active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info:active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-info .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-info.focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-info:focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-info:hover .by-icon .bui-loading-group{stroke:#FFF}.bui-btn.bui-btn-info{color:#FFF;background-color:#338AFF;border-color:#338AFF}.bui-btn.bui-btn-info.active,.bui-btn.bui-btn-info.active.focus,.bui-btn.bui-btn-info.active:focus,.bui-btn.bui-btn-info.active:hover,.bui-btn.bui-btn-info:active,.bui-btn.bui-btn-info:active.focus,.bui-btn.bui-btn-info:active:focus,.bui-btn.bui-btn-info:active:hover,.open>.dropdown-toggle.bui-btn.bui-btn-info,.open>.dropdown-toggle.bui-btn.bui-btn-info.focus,.open>.dropdown-toggle.bui-btn.bui-btn-info:focus,.open>.dropdown-toggle.bui-btn.bui-btn-info:hover{color:#FFF;background-color:#0672ff;border-color:#0672ff}.bui-btn.bui-btn-info.focus,.bui-btn.bui-btn-info:focus,.bui-btn.bui-btn-info:hover{color:#FFF;background-color:#58a0ff;border-color:#58a0ff}.bui-btn.bui-btn-info.bui-btn-loading,.bui-btn.bui-btn-info.bui-btn-loading.focus,.bui-btn.bui-btn-info.bui-btn-loading:active,.bui-btn.bui-btn-info.bui-btn-loading:focus,.bui-btn.bui-btn-info.bui-btn-loading:hover,.bui-btn.bui-btn-info.disabled,.bui-btn.bui-btn-info.disabled.focus,.bui-btn.bui-btn-info.disabled:active,.bui-btn.bui-btn-info.disabled:focus,.bui-btn.bui-btn-info.disabled:hover,.bui-btn.bui-btn-info[disabled],.bui-btn.bui-btn-info[disabled].focus,.bui-btn.bui-btn-info[disabled]:active,.bui-btn.bui-btn-info[disabled]:focus,.bui-btn.bui-btn-info[disabled]:hover,fieldset[disabled] .bui-btn.bui-btn-info,fieldset[disabled] .bui-btn.bui-btn-info.focus,fieldset[disabled] .bui-btn.bui-btn-info:active,fieldset[disabled] .bui-btn.bui-btn-info:focus,fieldset[disabled] .bui-btn.bui-btn-info:hover{color:#ebf3ff;background-color:#a1caff;border-color:#a1caff}.bui-btn.bui-btn-info.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-info.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-info.disabled .bui-icon svg,.bui-btn.bui-btn-info.disabled .by-icon svg,.bui-btn.bui-btn-info.disabled.focus .bui-icon svg,.bui-btn.bui-btn-info.disabled.focus .by-icon svg,.bui-btn.bui-btn-info.disabled:active .bui-icon svg,.bui-btn.bui-btn-info.disabled:active .by-icon svg,.bui-btn.bui-btn-info.disabled:focus .bui-icon svg,.bui-btn.bui-btn-info.disabled:focus .by-icon svg,.bui-btn.bui-btn-info.disabled:hover .bui-icon svg,.bui-btn.bui-btn-info.disabled:hover .by-icon svg,.bui-btn.bui-btn-info[disabled] .bui-icon svg,.bui-btn.bui-btn-info[disabled] .by-icon svg,.bui-btn.bui-btn-info[disabled].focus .bui-icon svg,.bui-btn.bui-btn-info[disabled].focus .by-icon svg,.bui-btn.bui-btn-info[disabled]:active .bui-icon svg,.bui-btn.bui-btn-info[disabled]:active .by-icon svg,.bui-btn.bui-btn-info[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-info[disabled]:focus .by-icon svg,.bui-btn.bui-btn-info[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-info[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-info .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-info .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-info.focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-info.focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-info:active .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-info:active .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-info:focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-info:focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-info:hover .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-info:hover .by-icon svg{fill:#ebf3ff}.bui-btn.bui-btn-info.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-info.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-info[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-info .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-info.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-info:active .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-info:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-info:hover .by-icon .bui-loading-group{stroke:#ebf3ff}.bui-btn.bui-btn-info.bui-btn-plain{color:#333;background-color:#FFF;border-color:#E0E0E0}.bui-btn.bui-btn-info.bui-btn-plain .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain .by-icon svg{fill:#333}.bui-btn.bui-btn-info.bui-btn-plain .by-icon .bui-loading-group{stroke:#333}.bui-btn.bui-btn-info.bui-btn-plain.active,.bui-btn.bui-btn-info.bui-btn-plain.active:hover,.bui-btn.bui-btn-info.bui-btn-plain.focus,.bui-btn.bui-btn-info.bui-btn-plain.focus:hover,.bui-btn.bui-btn-info.bui-btn-plain:focus,.bui-btn.bui-btn-info.bui-btn-plain:focus:hover,.open>.dropdown-toggle.bui-btn.bui-btn-info.bui-btn-plain,.open>.dropdown-toggle.bui-btn.bui-btn-info.bui-btn-plain:hover{color:#338AFF;background-color:#ebf3ff;border-color:#338AFF}.bui-btn.bui-btn-info.bui-btn-plain.active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.active:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.active:hover .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.focus:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.focus:hover .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:focus:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:focus:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info.bui-btn-plain .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info.bui-btn-plain .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info.bui-btn-plain:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-info.bui-btn-plain:hover .by-icon svg{fill:#338AFF}.bui-btn.bui-btn-info.bui-btn-plain.active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.focus:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain:focus:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-info.bui-btn-plain .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-info.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#338AFF}.bui-btn.bui-btn-info.bui-btn-plain:hover{color:#58a0ff;background-color:#FFF;border-color:#58a0ff}.bui-btn.bui-btn-info.bui-btn-plain:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:hover .by-icon svg{fill:#58a0ff}.bui-btn.bui-btn-info.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#58a0ff}.bui-btn.bui-btn-info.bui-btn-plain:active,.bui-btn.bui-btn-info.bui-btn-plain:active.focus,.bui-btn.bui-btn-info.bui-btn-plain:active:focus,.bui-btn.bui-btn-info.bui-btn-plain:active:hover{color:#0672ff;background-color:#FFF;border-color:#0672ff}.bui-btn.bui-btn-info.bui-btn-plain:active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:active.focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:active.focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:active:focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:active:focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:active:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain:active:hover .by-icon svg{fill:#0672ff}.bui-btn.bui-btn-info.bui-btn-plain:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain:active:hover .by-icon .bui-loading-group{stroke:#0672ff}.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-info.bui-btn-plain.disabled,.bui-btn.bui-btn-info.bui-btn-plain[disabled]{cursor:not-allowed}.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.focus,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:active,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:focus,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:hover,.bui-btn.bui-btn-info.bui-btn-plain.disabled,.bui-btn.bui-btn-info.bui-btn-plain.disabled.focus,.bui-btn.bui-btn-info.bui-btn-plain.disabled:active,.bui-btn.bui-btn-info.bui-btn-plain.disabled:focus,.bui-btn.bui-btn-info.bui-btn-plain.disabled:hover,.bui-btn.bui-btn-info.bui-btn-plain[disabled],.bui-btn.bui-btn-info.bui-btn-plain[disabled].focus,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:active,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:focus,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:hover{color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled:active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled:active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled:focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled:focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled:hover .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled] .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled] .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:hover .by-icon svg{fill:#C1C1C1}.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled]:hover .by-icon .bui-loading-group{stroke:#C1C1C1}.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active.focus,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:active,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:focus,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:hover,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active.focus,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:active,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:focus,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:hover,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active.focus,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:active,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:focus,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:hover{color:#a1caff;background-color:#ebf3ff;border-color:#a1caff}.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active.focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active.focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:hover .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active.focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active.focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:hover .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active.focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active.focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:active .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:active .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:focus .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:focus .by-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:hover .bui-icon svg,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:hover .by-icon svg{fill:#a1caff}.bui-btn.bui-btn-success .bui-icon svg,.bui-btn.bui-btn-success .by-icon svg,.bui-btn.bui-btn-success.active .bui-icon svg,.bui-btn.bui-btn-success.active .by-icon svg,.bui-btn.bui-btn-success.active.focus .bui-icon svg,.bui-btn.bui-btn-success.active.focus .by-icon svg,.bui-btn.bui-btn-success.active:focus .bui-icon svg,.bui-btn.bui-btn-success.active:focus .by-icon svg,.bui-btn.bui-btn-success.active:hover .bui-icon svg,.bui-btn.bui-btn-success.active:hover .by-icon svg,.bui-btn.bui-btn-success.focus .bui-icon svg,.bui-btn.bui-btn-success.focus .by-icon svg,.bui-btn.bui-btn-success:active .bui-icon svg,.bui-btn.bui-btn-success:active .by-icon svg,.bui-btn.bui-btn-success:active.focus .bui-icon svg,.bui-btn.bui-btn-success:active.focus .by-icon svg,.bui-btn.bui-btn-success:active:focus .bui-icon svg,.bui-btn.bui-btn-success:active:focus .by-icon svg,.bui-btn.bui-btn-success:active:hover .bui-icon svg,.bui-btn.bui-btn-success:active:hover .by-icon svg,.bui-btn.bui-btn-success:focus .bui-icon svg,.bui-btn.bui-btn-success:focus .by-icon svg,.bui-btn.bui-btn-success:hover .bui-icon svg,.bui-btn.bui-btn-success:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success.focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success.focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success:focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success:focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success:hover .by-icon svg{fill:#FFF}.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.bui-btn-loading.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain.disabled.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-info.bui-btn-plain[disabled].active:hover .by-icon .bui-loading-group{stroke:#a1caff}.bui-btn.bui-btn-success .by-icon .bui-loading-group,.bui-btn.bui-btn-success.active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success:active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-success .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-success.focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-success:focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-success:hover .by-icon .bui-loading-group{stroke:#FFF}.bui-btn.bui-btn-success{color:#FFF;background-color:#6ABF40;border-color:#6ABF40}.bui-btn.bui-btn-success.active,.bui-btn.bui-btn-success.active.focus,.bui-btn.bui-btn-success.active:focus,.bui-btn.bui-btn-success.active:hover,.bui-btn.bui-btn-success:active,.bui-btn.bui-btn-success:active.focus,.bui-btn.bui-btn-success:active:focus,.bui-btn.bui-btn-success:active:hover,.open>.dropdown-toggle.bui-btn.bui-btn-success,.open>.dropdown-toggle.bui-btn.bui-btn-success.focus,.open>.dropdown-toggle.bui-btn.bui-btn-success:focus,.open>.dropdown-toggle.bui-btn.bui-btn-success:hover{color:#FFF;background-color:#5ca537;border-color:#5ca537}.bui-btn.bui-btn-success.focus,.bui-btn.bui-btn-success:focus,.bui-btn.bui-btn-success:hover{color:#FFF;background-color:#86cb63;border-color:#86cb63}.bui-btn.bui-btn-success.bui-btn-loading,.bui-btn.bui-btn-success.bui-btn-loading.focus,.bui-btn.bui-btn-success.bui-btn-loading:active,.bui-btn.bui-btn-success.bui-btn-loading:focus,.bui-btn.bui-btn-success.bui-btn-loading:hover,.bui-btn.bui-btn-success.disabled,.bui-btn.bui-btn-success.disabled.focus,.bui-btn.bui-btn-success.disabled:active,.bui-btn.bui-btn-success.disabled:focus,.bui-btn.bui-btn-success.disabled:hover,.bui-btn.bui-btn-success[disabled],.bui-btn.bui-btn-success[disabled].focus,.bui-btn.bui-btn-success[disabled]:active,.bui-btn.bui-btn-success[disabled]:focus,.bui-btn.bui-btn-success[disabled]:hover,fieldset[disabled] .bui-btn.bui-btn-success,fieldset[disabled] .bui-btn.bui-btn-success.focus,fieldset[disabled] .bui-btn.bui-btn-success:active,fieldset[disabled] .bui-btn.bui-btn-success:focus,fieldset[disabled] .bui-btn.bui-btn-success:hover{color:#f3faf0;background-color:#bce2a9;border-color:#bce2a9}.bui-btn.bui-btn-success.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-success.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-success.disabled .bui-icon svg,.bui-btn.bui-btn-success.disabled .by-icon svg,.bui-btn.bui-btn-success.disabled.focus .bui-icon svg,.bui-btn.bui-btn-success.disabled.focus .by-icon svg,.bui-btn.bui-btn-success.disabled:active .bui-icon svg,.bui-btn.bui-btn-success.disabled:active .by-icon svg,.bui-btn.bui-btn-success.disabled:focus .bui-icon svg,.bui-btn.bui-btn-success.disabled:focus .by-icon svg,.bui-btn.bui-btn-success.disabled:hover .bui-icon svg,.bui-btn.bui-btn-success.disabled:hover .by-icon svg,.bui-btn.bui-btn-success[disabled] .bui-icon svg,.bui-btn.bui-btn-success[disabled] .by-icon svg,.bui-btn.bui-btn-success[disabled].focus .bui-icon svg,.bui-btn.bui-btn-success[disabled].focus .by-icon svg,.bui-btn.bui-btn-success[disabled]:active .bui-icon svg,.bui-btn.bui-btn-success[disabled]:active .by-icon svg,.bui-btn.bui-btn-success[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-success[disabled]:focus .by-icon svg,.bui-btn.bui-btn-success[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-success[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-success .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-success .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-success.focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-success.focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-success:active .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-success:active .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-success:focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-success:focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-success:hover .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-success:hover .by-icon svg{fill:#f3faf0}.bui-btn.bui-btn-success.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-success.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-success[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-success .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-success.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-success:active .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-success:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-success:hover .by-icon .bui-loading-group{stroke:#f3faf0}.bui-btn.bui-btn-success.bui-btn-plain{color:#333;background-color:#FFF;border-color:#E0E0E0}.bui-btn.bui-btn-success.bui-btn-plain .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain .by-icon svg{fill:#333}.bui-btn.bui-btn-success.bui-btn-plain .by-icon .bui-loading-group{stroke:#333}.bui-btn.bui-btn-success.bui-btn-plain.active,.bui-btn.bui-btn-success.bui-btn-plain.active:hover,.bui-btn.bui-btn-success.bui-btn-plain.focus,.bui-btn.bui-btn-success.bui-btn-plain.focus:hover,.bui-btn.bui-btn-success.bui-btn-plain:focus,.bui-btn.bui-btn-success.bui-btn-plain:focus:hover,.open>.dropdown-toggle.bui-btn.bui-btn-success.bui-btn-plain,.open>.dropdown-toggle.bui-btn.bui-btn-success.bui-btn-plain:hover{color:#6ABF40;background-color:#f3faf0;border-color:#6ABF40}.bui-btn.bui-btn-success.bui-btn-plain.active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.active:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.active:hover .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.focus:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.focus:hover .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:focus:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:focus:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success.bui-btn-plain .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success.bui-btn-plain .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success.bui-btn-plain:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-success.bui-btn-plain:hover .by-icon svg{fill:#6ABF40}.bui-btn.bui-btn-success.bui-btn-plain.active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.focus:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain:focus:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-success.bui-btn-plain .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-success.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#6ABF40}.bui-btn.bui-btn-success.bui-btn-plain:hover{color:#86cb63;background-color:#FFF;border-color:#86cb63}.bui-btn.bui-btn-success.bui-btn-plain:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:hover .by-icon svg{fill:#86cb63}.bui-btn.bui-btn-success.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#86cb63}.bui-btn.bui-btn-success.bui-btn-plain:active,.bui-btn.bui-btn-success.bui-btn-plain:active.focus,.bui-btn.bui-btn-success.bui-btn-plain:active:focus,.bui-btn.bui-btn-success.bui-btn-plain:active:hover{color:#5ca537;background-color:#FFF;border-color:#5ca537}.bui-btn.bui-btn-success.bui-btn-plain:active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:active.focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:active.focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:active:focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:active:focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:active:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain:active:hover .by-icon svg{fill:#5ca537}.bui-btn.bui-btn-success.bui-btn-plain:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain:active:hover .by-icon .bui-loading-group{stroke:#5ca537}.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-success.bui-btn-plain.disabled,.bui-btn.bui-btn-success.bui-btn-plain[disabled]{cursor:not-allowed}.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.focus,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:active,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:focus,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:hover,.bui-btn.bui-btn-success.bui-btn-plain.disabled,.bui-btn.bui-btn-success.bui-btn-plain.disabled.focus,.bui-btn.bui-btn-success.bui-btn-plain.disabled:active,.bui-btn.bui-btn-success.bui-btn-plain.disabled:focus,.bui-btn.bui-btn-success.bui-btn-plain.disabled:hover,.bui-btn.bui-btn-success.bui-btn-plain[disabled],.bui-btn.bui-btn-success.bui-btn-plain[disabled].focus,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:active,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:focus,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:hover{color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled:active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled:active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled:focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled:focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled:hover .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled] .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled] .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:hover .by-icon svg{fill:#C1C1C1}.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled]:hover .by-icon .bui-loading-group{stroke:#C1C1C1}.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active.focus,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:active,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:focus,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:hover,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active.focus,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:active,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:focus,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:hover,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active.focus,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:active,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:focus,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:hover{color:#bce2a9;background-color:#f3faf0;border-color:#bce2a9}.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active.focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active.focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:hover .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active.focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active.focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:hover .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active.focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active.focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:active .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:active .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:focus .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:focus .by-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:hover .bui-icon svg,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:hover .by-icon svg{fill:#bce2a9}.bui-btn.bui-btn-warning .bui-icon svg,.bui-btn.bui-btn-warning .by-icon svg,.bui-btn.bui-btn-warning.active .bui-icon svg,.bui-btn.bui-btn-warning.active .by-icon svg,.bui-btn.bui-btn-warning.active.focus .bui-icon svg,.bui-btn.bui-btn-warning.active.focus .by-icon svg,.bui-btn.bui-btn-warning.active:focus .bui-icon svg,.bui-btn.bui-btn-warning.active:focus .by-icon svg,.bui-btn.bui-btn-warning.active:hover .bui-icon svg,.bui-btn.bui-btn-warning.active:hover .by-icon svg,.bui-btn.bui-btn-warning.focus .bui-icon svg,.bui-btn.bui-btn-warning.focus .by-icon svg,.bui-btn.bui-btn-warning:active .bui-icon svg,.bui-btn.bui-btn-warning:active .by-icon svg,.bui-btn.bui-btn-warning:active.focus .bui-icon svg,.bui-btn.bui-btn-warning:active.focus .by-icon svg,.bui-btn.bui-btn-warning:active:focus .bui-icon svg,.bui-btn.bui-btn-warning:active:focus .by-icon svg,.bui-btn.bui-btn-warning:active:hover .bui-icon svg,.bui-btn.bui-btn-warning:active:hover .by-icon svg,.bui-btn.bui-btn-warning:focus .bui-icon svg,.bui-btn.bui-btn-warning:focus .by-icon svg,.bui-btn.bui-btn-warning:hover .bui-icon svg,.bui-btn.bui-btn-warning:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning.focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning.focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning:focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning:focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning:hover .by-icon svg{fill:#FFF}.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.bui-btn-loading.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain.disabled.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-success.bui-btn-plain[disabled].active:hover .by-icon .bui-loading-group{stroke:#bce2a9}.bui-btn.bui-btn-warning .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning:active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-warning .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-warning.focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-warning:focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-warning:hover .by-icon .bui-loading-group{stroke:#FFF}.bui-btn.bui-btn-warning{color:#FFF;background-color:#FFA900;border-color:#FFA900}.bui-btn.bui-btn-warning.active,.bui-btn.bui-btn-warning.active.focus,.bui-btn.bui-btn-warning.active:focus,.bui-btn.bui-btn-warning.active:hover,.bui-btn.bui-btn-warning:active,.bui-btn.bui-btn-warning:active.focus,.bui-btn.bui-btn-warning:active:focus,.bui-btn.bui-btn-warning:active:hover,.open>.dropdown-toggle.bui-btn.bui-btn-warning,.open>.dropdown-toggle.bui-btn.bui-btn-warning.focus,.open>.dropdown-toggle.bui-btn.bui-btn-warning:focus,.open>.dropdown-toggle.bui-btn.bui-btn-warning:hover{color:#FFF;background-color:#dc9300;border-color:#dc9300}.bui-btn.bui-btn-warning.focus,.bui-btn.bui-btn-warning:focus,.bui-btn.bui-btn-warning:hover{color:#FFF;background-color:#ffba2f;border-color:#ffba2f}.bui-btn.bui-btn-warning.bui-btn-loading,.bui-btn.bui-btn-warning.bui-btn-loading.focus,.bui-btn.bui-btn-warning.bui-btn-loading:active,.bui-btn.bui-btn-warning.bui-btn-loading:focus,.bui-btn.bui-btn-warning.bui-btn-loading:hover,.bui-btn.bui-btn-warning.disabled,.bui-btn.bui-btn-warning.disabled.focus,.bui-btn.bui-btn-warning.disabled:active,.bui-btn.bui-btn-warning.disabled:focus,.bui-btn.bui-btn-warning.disabled:hover,.bui-btn.bui-btn-warning[disabled],.bui-btn.bui-btn-warning[disabled].focus,.bui-btn.bui-btn-warning[disabled]:active,.bui-btn.bui-btn-warning[disabled]:focus,.bui-btn.bui-btn-warning[disabled]:hover,fieldset[disabled] .bui-btn.bui-btn-warning,fieldset[disabled] .bui-btn.bui-btn-warning.focus,fieldset[disabled] .bui-btn.bui-btn-warning:active,fieldset[disabled] .bui-btn.bui-btn-warning:focus,fieldset[disabled] .bui-btn.bui-btn-warning:hover{color:#fff8eb;background-color:#ffd98d;border-color:#ffd98d}.bui-btn.bui-btn-warning.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-warning.disabled .bui-icon svg,.bui-btn.bui-btn-warning.disabled .by-icon svg,.bui-btn.bui-btn-warning.disabled.focus .bui-icon svg,.bui-btn.bui-btn-warning.disabled.focus .by-icon svg,.bui-btn.bui-btn-warning.disabled:active .bui-icon svg,.bui-btn.bui-btn-warning.disabled:active .by-icon svg,.bui-btn.bui-btn-warning.disabled:focus .bui-icon svg,.bui-btn.bui-btn-warning.disabled:focus .by-icon svg,.bui-btn.bui-btn-warning.disabled:hover .bui-icon svg,.bui-btn.bui-btn-warning.disabled:hover .by-icon svg,.bui-btn.bui-btn-warning[disabled] .bui-icon svg,.bui-btn.bui-btn-warning[disabled] .by-icon svg,.bui-btn.bui-btn-warning[disabled].focus .bui-icon svg,.bui-btn.bui-btn-warning[disabled].focus .by-icon svg,.bui-btn.bui-btn-warning[disabled]:active .bui-icon svg,.bui-btn.bui-btn-warning[disabled]:active .by-icon svg,.bui-btn.bui-btn-warning[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-warning[disabled]:focus .by-icon svg,.bui-btn.bui-btn-warning[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-warning[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning.focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning.focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning:active .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning:active .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning:focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning:focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning:hover .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-warning:hover .by-icon svg{fill:#fff8eb}.bui-btn.bui-btn-warning.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-warning[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-warning .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-warning.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-warning:active .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-warning:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-warning:hover .by-icon .bui-loading-group{stroke:#fff8eb}.bui-btn.bui-btn-warning.bui-btn-plain{color:#333;background-color:#FFF;border-color:#E0E0E0}.bui-btn.bui-btn-warning.bui-btn-plain .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain .by-icon svg{fill:#333}.bui-btn.bui-btn-warning.bui-btn-plain .by-icon .bui-loading-group{stroke:#333}.bui-btn.bui-btn-warning.bui-btn-plain.active,.bui-btn.bui-btn-warning.bui-btn-plain.active:hover,.bui-btn.bui-btn-warning.bui-btn-plain.focus,.bui-btn.bui-btn-warning.bui-btn-plain.focus:hover,.bui-btn.bui-btn-warning.bui-btn-plain:focus,.bui-btn.bui-btn-warning.bui-btn-plain:focus:hover,.open>.dropdown-toggle.bui-btn.bui-btn-warning.bui-btn-plain,.open>.dropdown-toggle.bui-btn.bui-btn-warning.bui-btn-plain:hover{color:#FFA900;background-color:#fff8eb;border-color:#FFA900}.bui-btn.bui-btn-warning.bui-btn-plain.active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.active:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.active:hover .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.focus:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.focus:hover .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:focus:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:focus:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning.bui-btn-plain .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning.bui-btn-plain .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning.bui-btn-plain:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-warning.bui-btn-plain:hover .by-icon svg{fill:#FFA900}.bui-btn.bui-btn-warning.bui-btn-plain.active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.focus:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain:focus:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-warning.bui-btn-plain .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-warning.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#FFA900}.bui-btn.bui-btn-warning.bui-btn-plain:hover{color:#ffba2f;background-color:#FFF;border-color:#ffba2f}.bui-btn.bui-btn-warning.bui-btn-plain:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:hover .by-icon svg{fill:#ffba2f}.bui-btn.bui-btn-warning.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#ffba2f}.bui-btn.bui-btn-warning.bui-btn-plain:active,.bui-btn.bui-btn-warning.bui-btn-plain:active.focus,.bui-btn.bui-btn-warning.bui-btn-plain:active:focus,.bui-btn.bui-btn-warning.bui-btn-plain:active:hover{color:#dc9300;background-color:#FFF;border-color:#dc9300}.bui-btn.bui-btn-warning.bui-btn-plain:active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:active.focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:active.focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:active:focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:active:focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:active:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain:active:hover .by-icon svg{fill:#dc9300}.bui-btn.bui-btn-warning.bui-btn-plain:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain:active:hover .by-icon .bui-loading-group{stroke:#dc9300}.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-warning.bui-btn-plain.disabled,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]{cursor:not-allowed}.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.focus,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:active,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:focus,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:hover,.bui-btn.bui-btn-warning.bui-btn-plain.disabled,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.focus,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:active,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:focus,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:hover,.bui-btn.bui-btn-warning.bui-btn-plain[disabled],.bui-btn.bui-btn-warning.bui-btn-plain[disabled].focus,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:active,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:focus,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:hover{color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:hover .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled] .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled] .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:hover .by-icon svg{fill:#C1C1C1}.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled]:hover .by-icon .bui-loading-group{stroke:#C1C1C1}.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active.focus,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:active,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:focus,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:hover,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active.focus,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:active,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:focus,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:hover,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active.focus,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:active,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:focus,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:hover{color:#ffd98d;background-color:#fff8eb;border-color:#ffd98d}.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active.focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active.focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:hover .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active.focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active.focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:hover .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active.focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active.focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:active .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:active .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:focus .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:focus .by-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:hover .bui-icon svg,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:hover .by-icon svg{fill:#ffd98d}.bui-btn.bui-btn-danger .bui-icon svg,.bui-btn.bui-btn-danger .by-icon svg,.bui-btn.bui-btn-danger.active .bui-icon svg,.bui-btn.bui-btn-danger.active .by-icon svg,.bui-btn.bui-btn-danger.active.focus .bui-icon svg,.bui-btn.bui-btn-danger.active.focus .by-icon svg,.bui-btn.bui-btn-danger.active:focus .bui-icon svg,.bui-btn.bui-btn-danger.active:focus .by-icon svg,.bui-btn.bui-btn-danger.active:hover .bui-icon svg,.bui-btn.bui-btn-danger.active:hover .by-icon svg,.bui-btn.bui-btn-danger.focus .bui-icon svg,.bui-btn.bui-btn-danger.focus .by-icon svg,.bui-btn.bui-btn-danger:active .bui-icon svg,.bui-btn.bui-btn-danger:active .by-icon svg,.bui-btn.bui-btn-danger:active.focus .bui-icon svg,.bui-btn.bui-btn-danger:active.focus .by-icon svg,.bui-btn.bui-btn-danger:active:focus .bui-icon svg,.bui-btn.bui-btn-danger:active:focus .by-icon svg,.bui-btn.bui-btn-danger:active:hover .bui-icon svg,.bui-btn.bui-btn-danger:active:hover .by-icon svg,.bui-btn.bui-btn-danger:focus .bui-icon svg,.bui-btn.bui-btn-danger:focus .by-icon svg,.bui-btn.bui-btn-danger:hover .bui-icon svg,.bui-btn.bui-btn-danger:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger.focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger.focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger:focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger:focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger:hover .by-icon svg{fill:#FFF}.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.bui-btn-loading.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain.disabled.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-warning.bui-btn-plain[disabled].active:hover .by-icon .bui-loading-group{stroke:#ffd98d}.bui-btn.bui-btn-danger .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger:active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-danger .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-danger.focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-danger:focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-danger:hover .by-icon .bui-loading-group{stroke:#FFF}.bui-btn.bui-btn-danger{color:#FFF;background-color:#F65656;border-color:#F65656}.bui-btn.bui-btn-danger.active,.bui-btn.bui-btn-danger.active.focus,.bui-btn.bui-btn-danger.active:focus,.bui-btn.bui-btn-danger.active:hover,.bui-btn.bui-btn-danger:active,.bui-btn.bui-btn-danger:active.focus,.bui-btn.bui-btn-danger:active:focus,.bui-btn.bui-btn-danger:active:hover,.open>.dropdown-toggle.bui-btn.bui-btn-danger,.open>.dropdown-toggle.bui-btn.bui-btn-danger.focus,.open>.dropdown-toggle.bui-btn.bui-btn-danger:focus,.open>.dropdown-toggle.bui-btn.bui-btn-danger:hover{color:#FFF;background-color:#f32626;border-color:#f32626}.bui-btn.bui-btn-danger.focus,.bui-btn.bui-btn-danger:focus,.bui-btn.bui-btn-danger:hover{color:#FFF;background-color:#f87474;border-color:#f87474}.bui-btn.bui-btn-danger.bui-btn-loading,.bui-btn.bui-btn-danger.bui-btn-loading.focus,.bui-btn.bui-btn-danger.bui-btn-loading:active,.bui-btn.bui-btn-danger.bui-btn-loading:focus,.bui-btn.bui-btn-danger.bui-btn-loading:hover,.bui-btn.bui-btn-danger.disabled,.bui-btn.bui-btn-danger.disabled.focus,.bui-btn.bui-btn-danger.disabled:active,.bui-btn.bui-btn-danger.disabled:focus,.bui-btn.bui-btn-danger.disabled:hover,.bui-btn.bui-btn-danger[disabled],.bui-btn.bui-btn-danger[disabled].focus,.bui-btn.bui-btn-danger[disabled]:active,.bui-btn.bui-btn-danger[disabled]:focus,.bui-btn.bui-btn-danger[disabled]:hover,fieldset[disabled] .bui-btn.bui-btn-danger,fieldset[disabled] .bui-btn.bui-btn-danger.focus,fieldset[disabled] .bui-btn.bui-btn-danger:active,fieldset[disabled] .bui-btn.bui-btn-danger:focus,fieldset[disabled] .bui-btn.bui-btn-danger:hover{color:#feecec;background-color:#fbb0b0;border-color:#fbb0b0}.bui-btn.bui-btn-danger.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-danger.disabled .bui-icon svg,.bui-btn.bui-btn-danger.disabled .by-icon svg,.bui-btn.bui-btn-danger.disabled.focus .bui-icon svg,.bui-btn.bui-btn-danger.disabled.focus .by-icon svg,.bui-btn.bui-btn-danger.disabled:active .bui-icon svg,.bui-btn.bui-btn-danger.disabled:active .by-icon svg,.bui-btn.bui-btn-danger.disabled:focus .bui-icon svg,.bui-btn.bui-btn-danger.disabled:focus .by-icon svg,.bui-btn.bui-btn-danger.disabled:hover .bui-icon svg,.bui-btn.bui-btn-danger.disabled:hover .by-icon svg,.bui-btn.bui-btn-danger[disabled] .bui-icon svg,.bui-btn.bui-btn-danger[disabled] .by-icon svg,.bui-btn.bui-btn-danger[disabled].focus .bui-icon svg,.bui-btn.bui-btn-danger[disabled].focus .by-icon svg,.bui-btn.bui-btn-danger[disabled]:active .bui-icon svg,.bui-btn.bui-btn-danger[disabled]:active .by-icon svg,.bui-btn.bui-btn-danger[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-danger[disabled]:focus .by-icon svg,.bui-btn.bui-btn-danger[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-danger[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger.focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger.focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger:active .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger:active .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger:focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger:focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger:hover .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-danger:hover .by-icon svg{fill:#feecec}.bui-btn.bui-btn-danger.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-danger[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-danger .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-danger.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-danger:active .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-danger:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-danger:hover .by-icon .bui-loading-group{stroke:#feecec}.bui-btn.bui-btn-danger.bui-btn-plain{color:#333;background-color:#FFF;border-color:#E0E0E0}.bui-btn.bui-btn-danger.bui-btn-plain .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain .by-icon svg{fill:#333}.bui-btn.bui-btn-danger.bui-btn-plain .by-icon .bui-loading-group{stroke:#333}.bui-btn.bui-btn-danger.bui-btn-plain.active,.bui-btn.bui-btn-danger.bui-btn-plain.active:hover,.bui-btn.bui-btn-danger.bui-btn-plain.focus,.bui-btn.bui-btn-danger.bui-btn-plain.focus:hover,.bui-btn.bui-btn-danger.bui-btn-plain:focus,.bui-btn.bui-btn-danger.bui-btn-plain:focus:hover,.open>.dropdown-toggle.bui-btn.bui-btn-danger.bui-btn-plain,.open>.dropdown-toggle.bui-btn.bui-btn-danger.bui-btn-plain:hover{color:#F65656;background-color:#feecec;border-color:#F65656}.bui-btn.bui-btn-danger.bui-btn-plain.active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.active:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.active:hover .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.focus:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.focus:hover .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:focus:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:focus:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger.bui-btn-plain .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger.bui-btn-plain .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger.bui-btn-plain:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-danger.bui-btn-plain:hover .by-icon svg{fill:#F65656}.bui-btn.bui-btn-danger.bui-btn-plain.active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.focus:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain:focus:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-danger.bui-btn-plain .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-danger.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#F65656}.bui-btn.bui-btn-danger.bui-btn-plain:hover{color:#f87474;background-color:#FFF;border-color:#f87474}.bui-btn.bui-btn-danger.bui-btn-plain:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:hover .by-icon svg{fill:#f87474}.bui-btn.bui-btn-danger.bui-btn-plain:hover .by-icon .bui-loading-group{stroke:#f87474}.bui-btn.bui-btn-danger.bui-btn-plain:active,.bui-btn.bui-btn-danger.bui-btn-plain:active.focus,.bui-btn.bui-btn-danger.bui-btn-plain:active:focus,.bui-btn.bui-btn-danger.bui-btn-plain:active:hover{color:#f32626;background-color:#FFF;border-color:#f32626}.bui-btn.bui-btn-danger.bui-btn-plain:active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:active.focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:active.focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:active:focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:active:focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:active:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain:active:hover .by-icon svg{fill:#f32626}.bui-btn.bui-btn-danger.bui-btn-plain:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain:active:hover .by-icon .bui-loading-group{stroke:#f32626}.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-danger.bui-btn-plain.disabled,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]{cursor:not-allowed}.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.focus,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:active,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:focus,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:hover,.bui-btn.bui-btn-danger.bui-btn-plain.disabled,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.focus,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:active,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:focus,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:hover,.bui-btn.bui-btn-danger.bui-btn-plain[disabled],.bui-btn.bui-btn-danger.bui-btn-plain[disabled].focus,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:active,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:focus,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:hover{color:#C1C1C1;background-color:#FAFAFA;border-color:#F0F0F0}.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:hover .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled] .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled] .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:hover .by-icon svg{fill:#C1C1C1}.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled]:hover .by-icon .bui-loading-group{stroke:#C1C1C1}.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active.focus,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:active,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:focus,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:hover,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active.focus,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:active,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:focus,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:hover,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active.focus,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:active,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:focus,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:hover{color:#fbb0b0;background-color:#feecec;border-color:#fbb0b0}.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active.focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active.focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:hover .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active.focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active.focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:hover .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active.focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active.focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:active .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:active .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:focus .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:focus .by-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:hover .bui-icon svg,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:hover .by-icon svg{fill:#fbb0b0}.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.bui-btn-loading.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain.disabled.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:active .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-danger.bui-btn-plain[disabled].active:hover .by-icon .bui-loading-group{stroke:#fbb0b0}.bui-btn.bui-btn-text{color:#338AFF;background-color:transparent;border-color:transparent}.bui-btn.bui-btn-text.focus,.bui-btn.bui-btn-text:focus,.bui-btn.bui-btn-text:hover{color:#58a0ff;background-color:transparent;border-color:transparent}.bui-btn.bui-btn-text .bui-icon svg,.bui-btn.bui-btn-text .by-icon svg{fill:#338AFF}.bui-btn.bui-btn-text.focus .bui-icon svg,.bui-btn.bui-btn-text.focus .by-icon svg,.bui-btn.bui-btn-text:focus .bui-icon svg,.bui-btn.bui-btn-text:focus .by-icon svg,.bui-btn.bui-btn-text:hover .bui-icon svg,.bui-btn.bui-btn-text:hover .by-icon svg{fill:#58a0ff}.bui-btn.bui-btn-text .by-icon .bui-loading-group{stroke:#338AFF}.bui-btn.bui-btn-text.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text:hover .by-icon .bui-loading-group{stroke:#58a0ff}.bui-btn.bui-btn-text.active,.bui-btn.bui-btn-text.active.focus,.bui-btn.bui-btn-text.active:focus,.bui-btn.bui-btn-text.active:hover,.bui-btn.bui-btn-text:active,.bui-btn.bui-btn-text:active.focus,.bui-btn.bui-btn-text:active:focus,.bui-btn.bui-btn-text:active:hover,.open>.dropdown-toggle.bui-btn.bui-btn-text,.open>.dropdown-toggle.bui-btn.bui-btn-text.focus,.open>.dropdown-toggle.bui-btn.bui-btn-text:focus,.open>.dropdown-toggle.bui-btn.bui-btn-text:hover{color:#0672ff;background-color:transparent;border-color:transparent}.bui-btn.bui-btn-text.bui-btn-loading,.bui-btn.bui-btn-text.bui-btn-loading.focus,.bui-btn.bui-btn-text.bui-btn-loading:focus,.bui-btn.bui-btn-text.bui-btn-loading:hover,.bui-btn.bui-btn-text.disabled,.bui-btn.bui-btn-text.disabled.focus,.bui-btn.bui-btn-text.disabled:focus,.bui-btn.bui-btn-text.disabled:hover,.bui-btn.bui-btn-text[disabled],.bui-btn.bui-btn-text[disabled].focus,.bui-btn.bui-btn-text[disabled]:focus,.bui-btn.bui-btn-text[disabled]:hover,fieldset[disabled] .bui-btn.bui-btn-text,fieldset[disabled] .bui-btn.bui-btn-text.focus,fieldset[disabled] .bui-btn.bui-btn-text:focus,fieldset[disabled] .bui-btn.bui-btn-text:hover{color:#a1caff;background-color:transparent;border-color:transparent}.bui-btn.bui-btn-text.active .bui-icon svg,.bui-btn.bui-btn-text.active .by-icon svg,.bui-btn.bui-btn-text.active.focus .bui-icon svg,.bui-btn.bui-btn-text.active.focus .by-icon svg,.bui-btn.bui-btn-text.active:focus .bui-icon svg,.bui-btn.bui-btn-text.active:focus .by-icon svg,.bui-btn.bui-btn-text.active:hover .bui-icon svg,.bui-btn.bui-btn-text.active:hover .by-icon svg,.bui-btn.bui-btn-text:active .bui-icon svg,.bui-btn.bui-btn-text:active .by-icon svg,.bui-btn.bui-btn-text:active.focus .bui-icon svg,.bui-btn.bui-btn-text:active.focus .by-icon svg,.bui-btn.bui-btn-text:active:focus .bui-icon svg,.bui-btn.bui-btn-text:active:focus .by-icon svg,.bui-btn.bui-btn-text:active:hover .bui-icon svg,.bui-btn.bui-btn-text:active:hover .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-text .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-text .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-text.focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-text.focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-text:focus .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-text:focus .by-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-text:hover .bui-icon svg,.open>.dropdown-toggle.bui-btn.bui-btn-text:hover .by-icon svg{fill:#0672ff}.bui-btn.bui-btn-text.bui-btn-loading .bui-icon svg,.bui-btn.bui-btn-text.bui-btn-loading .by-icon svg,.bui-btn.bui-btn-text.bui-btn-loading.focus .bui-icon svg,.bui-btn.bui-btn-text.bui-btn-loading.focus .by-icon svg,.bui-btn.bui-btn-text.bui-btn-loading:focus .bui-icon svg,.bui-btn.bui-btn-text.bui-btn-loading:focus .by-icon svg,.bui-btn.bui-btn-text.bui-btn-loading:hover .bui-icon svg,.bui-btn.bui-btn-text.bui-btn-loading:hover .by-icon svg,.bui-btn.bui-btn-text.disabled .bui-icon svg,.bui-btn.bui-btn-text.disabled .by-icon svg,.bui-btn.bui-btn-text.disabled.focus .bui-icon svg,.bui-btn.bui-btn-text.disabled.focus .by-icon svg,.bui-btn.bui-btn-text.disabled:focus .bui-icon svg,.bui-btn.bui-btn-text.disabled:focus .by-icon svg,.bui-btn.bui-btn-text.disabled:hover .bui-icon svg,.bui-btn.bui-btn-text.disabled:hover .by-icon svg,.bui-btn.bui-btn-text[disabled] .bui-icon svg,.bui-btn.bui-btn-text[disabled] .by-icon svg,.bui-btn.bui-btn-text[disabled].focus .bui-icon svg,.bui-btn.bui-btn-text[disabled].focus .by-icon svg,.bui-btn.bui-btn-text[disabled]:focus .bui-icon svg,.bui-btn.bui-btn-text[disabled]:focus .by-icon svg,.bui-btn.bui-btn-text[disabled]:hover .bui-icon svg,.bui-btn.bui-btn-text[disabled]:hover .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-text .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-text .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-text.focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-text.focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-text:focus .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-text:focus .by-icon svg,fieldset[disabled] .bui-btn.bui-btn-text:hover .bui-icon svg,fieldset[disabled] .bui-btn.bui-btn-text:hover .by-icon svg{fill:#a1caff}.bui-btn.bui-btn-text.active .by-icon .bui-loading-group,.bui-btn.bui-btn-text.active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text.active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text.active:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-text:active .by-icon .bui-loading-group,.bui-btn.bui-btn-text:active.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text:active:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text:active:hover .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-text .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-text.focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-text:focus .by-icon .bui-loading-group,.open>.dropdown-toggle.bui-btn.bui-btn-text:hover .by-icon .bui-loading-group{stroke:#0672ff}.bui-btn.bui-btn-text.bui-btn-loading .by-icon .bui-loading-group,.bui-btn.bui-btn-text.bui-btn-loading.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text.bui-btn-loading:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text.bui-btn-loading:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-text.disabled .by-icon .bui-loading-group,.bui-btn.bui-btn-text.disabled.focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text.disabled:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text.disabled:hover .by-icon .bui-loading-group,.bui-btn.bui-btn-text[disabled] .by-icon .bui-loading-group,.bui-btn.bui-btn-text[disabled].focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text[disabled]:focus .by-icon .bui-loading-group,.bui-btn.bui-btn-text[disabled]:hover .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-text .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-text.focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-text:focus .by-icon .bui-loading-group,fieldset[disabled] .bui-btn.bui-btn-text:hover .by-icon .bui-loading-group{stroke:#a1caff}";
styleInject(css_248z);

var loading = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 357);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 130:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

exports.default = {
  props: {
    color: {
      type: String,
      default: ''
    }
  },
  render: function render(h) {
    return h(
      'div',
      { 'class': _base.CSS_PREFIX + 'icon bui-icon-loading' },
      [h(
        'svg',
        {
          attrs: { xmlns: 'http://www.w3.org/2000/svg', fill: 'none', viewBox: '0 0 24 24' }
        },
        [h(
          'g',
          { 'class': 'bui-loading-group', attrs: { stroke: this.color, 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2.04' }
          },
          [h('path', { 'class': 'bui-loading-path-item', attrs: { d: 'M11.932 2.4v3.813', opacity: '1' }
          }), h('path', { 'class': 'bui-loading-path-item', attrs: { d: 'M15.973 7.89l2.698-2.697', opacity: '0.852' }
          }), h('path', { 'class': 'bui-loading-path-item', attrs: { d: 'M17.651 11.932h3.813', opacity: '0.71' }
          }), h('path', { 'class': 'bui-loading-path-item', attrs: { d: 'M15.973 15.974l2.698 2.697', opacity: '0.568' }
          }), h('path', { 'class': 'bui-loading-path-item', attrs: { d: 'M11.932 17.65v3.814', opacity: '0.426' }
          }), h('path', { 'class': 'bui-loading-path-item', attrs: { d: 'M5.193 18.671l2.697-2.697', opacity: '0.284' }
          }), h('path', { 'class': 'bui-loading-path-item', attrs: { d: 'M2.4 11.932h3.813', opacity: '0.142' }
          }), h('path', { 'class': 'bui-loading-path-item', attrs: { d: 'M5.193 5.193L7.89 7.89', opacity: '0' }
          })]
        )]
      )]
    );
  }
};

/***/ }),

/***/ 179:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_loading_vue__ = __webpack_require__(180);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_loading_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_loading_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_loading_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_loading_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_0a29b251_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_loading_vue__ = __webpack_require__(359);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_loading_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_0a29b251_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_loading_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_0a29b251_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_loading_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 180:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _dom = __webpack_require__(8);

var _loadingIcon = __webpack_require__(130);

var _loadingIcon2 = _interopRequireDefault(_loadingIcon);

var _helper = __webpack_require__(4);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; } //
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

exports.default = {
  name: _base.PREFIX + 'loading',
  iconName: _base.PREFIX + 'loading-icon',
  components: {
    LoadingIcon: _loadingIcon2.default
  },
  props: {
    show: {
      type: Boolean,
      default: true
    },
    parent: {
      type: Object,
      default: function _default() {
        return null;
      }
    },
    size: {
      type: String,
      default: 'normal', // normal, small
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['normal', 'default', 'small', 'xl', 'lg', 'md', 'sm', 'xs']);
      }
    },
    color: {
      type: String,
      default: '#598FE6'
    }
  },
  data: function data() {
    return {
      parentStyle: {
        overflow: '',
        position: ''
      },
      inDirective: false,
      CSS_PREFIX: _base.CSS_PREFIX,
      text: ''
    };
  },

  computed: {
    baseClass: function baseClass() {
      var _ref;

      return _ref = {}, _defineProperty(_ref, _base.CSS_PREFIX + 'loading', true), _defineProperty(_ref, _base.CSS_PREFIX + 'loading-v', this.inDirective), _ref;
    },
    baseIconClass: function baseIconClass() {
      var baseIconClass = 'bui-loading-icon';
      var baseIconArr = [baseIconClass];
      baseIconArr.push(baseIconClass + '-' + this.size);
      return baseIconArr;
    },
    textClass: function textClass() {
      return ['bui-loading-text', 'bui-loading-text-' + this.size];
    }
  },
  methods: {
    showLoading: function showLoading() {
      var _this = this;

      this.$nextTick(function () {
        if (!_this.inDirective || !_this.parent) return;
        _this.parentStyle.position = _this.parent.style.position;
        _this.parentStyle.overflow = _this.parent.style.overflow;

        var parentPosition = (0, _dom.getStyle)(_this.parent, 'position');
        if (parentPosition === 'static') {
          (0, _dom.setStyle)(_this.parent, 'position', 'relative');
        }
        (0, _dom.setStyle)(_this.parent, 'overflow', 'hidden');
      });
    },
    leaveCancelled: function leaveCancelled() {
      this.hideLoading();
    },
    hideLoading: function hideLoading() {
      (0, _dom.setStyle)(this.parent, 'position', this.parentStyle.position);
      (0, _dom.setStyle)(this.parent, 'overflow', this.parentStyle.overflow);
    }
  }
};

/***/ }),

/***/ 357:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _installLoading = __webpack_require__(358);

var _installLoading2 = _interopRequireDefault(_installLoading);

var _loading = __webpack_require__(179);

var _loading2 = _interopRequireDefault(_loading);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_loading2.default.install = function (Vue) {
  Vue.component(_loading2.default.iconName, _loading2.default);
  (0, _installLoading2.default)(Vue);
};

exports.default = _loading2.default;

/***/ }),

/***/ 358:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

exports.default = function (Vue) {
  Vue.directive(_base.PREFIX + 'loading', {
    name: _base.PREFIX + 'loading',
    bind: function bind(el, _ref, vnode) {
      var value = _ref.value,
          arg = _ref.arg;

      var loadingText = el.getAttribute('bui-loading-text');
      var subLoading = _extends({}, _loading2.default, { props: _extends({}, _loading2.default.props) });
      subLoading.props.show = {
        type: Boolean,
        default: value
      };
      var context = new Vue(subLoading).$mount();
      context.parent = el;
      context.inDirective = true;
      context.text = vnode && vnode.context && vnode.context[loadingText] || loadingText;
      // context.parentStyle.position = context.parent.style.position;
      // context.parentStyle.overflow = context.parent.style.overflow;
      if (arg) {
        context.size = arg;
      }

      if (context.show) {
        context.showLoading();
      }
      el.appendChild(context.$el);
      el[elKey] = context; // 存储起来
    },
    update: function update(el, _ref2) {
      var value = _ref2.value,
          arg = _ref2.arg;

      var context = el[elKey];
      if (!context) {
        return;
      }
      context.show = value;
      if (arg) {
        context.size = arg;
      }
    },
    unbind: function unbind(el) {
      var context = el[elKey];
      if (!context) {
        return;
      }
      delete el[elKey];
    }
  });
};

var _base = __webpack_require__(1);

var _loading = __webpack_require__(179);

var _loading2 = _interopRequireDefault(_loading);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var elKey = 'buiLoading'; // 存储在原生node上的信息key字段名

/**
 * @param {Object} Vue Vue实例
 */

/***/ }),

/***/ 359:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('transition',{attrs:{"name":(_vm.CSS_PREFIX + "loading-fade")},on:{"enter":_vm.showLoading,"leave-cancelled":_vm.leaveCancelled,"after-leave":_vm.hideLoading}},[_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.show),expression:"show"}],class:_vm.baseClass},[(!_vm.inDirective)?_c('div',{class:_vm.baseIconClass},[_c('loading-icon')],1):_c('div',{staticClass:"bui-loading-wrapper"},[_c('div',{class:_vm.baseIconClass},[_c('loading-icon')],1),_vm._v(" "),(_vm.inDirective && _vm.text)?_c('div',{class:_vm.textClass},[_vm._v("\n        "+_vm._s(_vm.text)+"\n      ")]):_vm._e()])])])};
var staticRenderFns = [];


/***/ }),

/***/ 4:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isOneOf = isOneOf;
function isOneOf(value, validList) {
  for (var i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true;
    }
  }
  return false;
}

/***/ }),

/***/ 8:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

exports.hasClass = hasClass;
exports.addClass = addClass;
exports.removeClass = removeClass;
exports.setStyle = setStyle;
exports.insertAfter = insertAfter;
exports.scrollTop = scrollTop;
exports.contains = contains;
var SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;
var MOZ_HACK_REGEXP = /^moz([A-Z])/;
var ieVersion = Number(document.documentMode);

/* istanbul ignore next */
var trim = function trim(string) {
  return (string || '').replace(/^[\s\uFEFF]+|[\s\uFEFF]+$/g, '');
};
/* istanbul ignore next */
var camelCase = function camelCase(name) {
  return name.replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
    return offset ? letter.toUpperCase() : letter;
  }).replace(MOZ_HACK_REGEXP, 'Moz$1');
};

/* istanbul ignore next */
var on = exports.on = function () {
  if (document.addEventListener) {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.addEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event && handler) {
        element.attachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
var off = exports.off = function () {
  if (document.removeEventListener) {
    return function (element, event, handler) {
      if (element && event) {
        element.removeEventListener(event, handler, false);
      }
    };
  } else {
    return function (element, event, handler) {
      if (element && event) {
        element.detachEvent('on' + event, handler);
      }
    };
  }
}();

/* istanbul ignore next */
exports.once = function once(el, event, fn) {
  var listener = function listener() {
    if (fn) {
      for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      fn.apply(this, args);
    }
    off(el, event, listener);
  };
  on(el, event, listener);
};

/* istanbul ignore next */
function hasClass(el, cls) {
  if (!el || !cls) return false;
  if (cls.indexOf(' ') !== -1) {
    throw new Error('className should not contain space.');
  }
  if (el.classList) {
    return el.classList.contains(cls);
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
  }
}
/* istanbul ignore next */
function addClass(el, cls) {
  if (!el) return;
  var curClass = el.className;
  var classes = (cls || '').split(' ');

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.add(clsName);
    } else {
      if (!hasClass(el, clsName)) {
        curClass += ' ' + clsName;
      }
    }
  }
  if (!el.classList) {
    el.className = curClass;
  }
}
/* istanbul ignore next */
function removeClass(el, cls) {
  if (!el || !cls) return;
  var classes = cls.split(' ');
  var curClass = ' ' + el.className + ' ';

  for (var i = 0, j = classes.length; i < j; i++) {
    var clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.remove(clsName);
    } else {
      if (hasClass(el, clsName)) {
        curClass = curClass.replace(' ' + clsName + ' ', ' ');
      }
    }
  }
  if (!el.classList) {
    el.className = trim(curClass);
  }
}
/* istanbul ignore next */
exports.getStyle = ieVersion < 9 ? function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'styleFloat';
  }
  try {
    switch (styleName) {
      case 'opacity':
        try {
          return element.filters.item('alpha').opacity / 100;
        } catch (e) {
          return 1.0;
        }
      default:
        return element.style[styleName] || element.currentStyle ? element.currentStyle[styleName] : null;
    }
  } catch (e) {
    return element.style[styleName];
  }
} : function (element, styleName) {
  if (!element || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'cssFloat';
  }
  try {
    var computed = document.defaultView.getComputedStyle(element, '');
    return element.style[styleName] || computed ? computed[styleName] : null;
  } catch (e) {
    return element.style[styleName];
  }
};

/* istanbul ignore next */
function setStyle(element, styleName, value) {
  if (!element || !styleName) return;

  if ((typeof styleName === 'undefined' ? 'undefined' : _typeof(styleName)) === 'object') {
    for (var prop in styleName) {
      if (styleName.hasOwnProperty(prop)) {
        setStyle(element, prop, styleName[prop]);
      }
    }
  } else {
    styleName = camelCase(styleName);
    if (styleName === 'opacity' && ieVersion < 9) {
      element.style.filter = isNaN(value) ? '' : 'alpha(opacity=' + value * 100 + ')';
    } else {
      element.style[styleName] = value;
    }
  }
}// 选中DOM滚动到可视区域
exports.scrollIntoView = function scrollIntoView(container, selected) {
  var direction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'vertical';

  // direction: 方向 vertival || horizontal
  if (!selected) {
    if (direction === 'horizontal') {
      container.scrollLeft = 0;
      return;
    }
    container.scrollTop = 0;
    return;
  }

  var offsetParents = [];
  var pointer = selected.offsetParent;
  while (pointer && container !== pointer && container.contains(pointer)) {
    offsetParents.push(pointer);
    pointer = pointer.offsetParent;
  }
  if (direction === 'horizontal') {
    // 水平方向滚动计算
    var left = selected.offsetLeft + offsetParents.reduce(function (prev, curr) {
      return prev + curr.offsetLeft;
    }, 0);
    var right = left + selected.offsetWidth;
    var viewRectLeft = container.scrollLeft;
    var viewRectRight = viewRectLeft + container.clientWidth;

    if (left < viewRectLeft) {
      container.scrollLeft = left;
    } else if (right > viewRectRight) {
      container.scrollLeft = right - container.clientWidth;
    }
    return;
  }
  var top = selected.offsetTop + offsetParents.reduce(function (prev, curr) {
    return prev + curr.offsetTop;
  }, 0);
  var bottom = top + selected.offsetHeight;
  var viewRectTop = container.scrollTop;
  var viewRectBottom = viewRectTop + container.clientHeight;

  if (top < viewRectTop) {
    container.scrollTop = top;
  } else if (bottom > viewRectBottom) {
    container.scrollTop = bottom - container.clientHeight;
  }
};
// 计算滚动条宽度
var scrollBarWidth = void 0;
exports.getScrollBarWidth = function getScrollBarWidth() {
  if (typeof scrollBarWidth !== 'undefined') return scrollBarWidth;
  var outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  outer.style.padding = 0;
  document.body.appendChild(outer);
  scrollBarWidth = outer.offsetHeight;
  document.body.removeChild(outer);
  return scrollBarWidth;
};

function insertAfter(el, refenerceEle) {
  var parent = refenerceEle.parentNode;
  // 如果最后的节点是目标元素，则直接添加
  if (parent.lastChild === refenerceEle) {
    parent.appendChild(el);
  } else {
    // 如果不是，则插入在目标元素的下一个兄弟节点 的前面
    parent.insertBefore(el, refenerceEle.nextSibling);
  }
}
exports.removeDom = function removeDom(el) {
  if (!el) return;
  if (el.remove) {
    el.remove();
    return;
  }
  var parent = el.parentNode;
  if (parent && parent.removeChild) {
    parent.removeChild(el);
  }
};
// scrollTop animation
function scrollTop(el) {
  var from = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var to = arguments[2];
  var duration = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 500;

  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame = window.webkitRequestAnimationFrame || window.mozRequestAnimationFrame || window.msRequestAnimationFrame || function (callback) {
      return window.setTimeout(callback, 1000 / 60);
    };
  }
  var difference = Math.abs(from - to);
  var step = Math.ceil(difference / duration * 50);

  function scroll(start, end, step) {
    if (start === end) return;

    var d = start + step > end ? end : start + step;
    if (start > end) {
      d = start - step < end ? end : start - step;
    }

    if (el === window) {
      window.scrollTo(d, d);
    } else {
      el.scrollTop = d;
    }
    window.requestAnimationFrame(function () {
      return scroll(d, end, step);
    });
  }
  scroll(from, to, step);
}

function contains(el, parent) {
  if (!el || !parent) return false;
  while (el = el.parentNode) {
    if (el === parent) return true;
  }
  return false;
}
/***/ })

/******/ });
});

var button = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
/******/ 			});
/******/ 		}
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 252);
/******/ })
/************************************************************************/
/******/ ({

/***/ 0:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (immutable) */ __webpack_exports__["a"] = normalizeComponent;
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  scriptExports = scriptExports || {};

  // ES6 modules interop
  var type = typeof scriptExports.default;
  if (type === 'object' || type === 'function') {
    scriptExports = scriptExports.default;
  }

  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () { injectStyles.call(this, this.$root.$options.shadowRoot); }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functioal component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ 1:
/***/ (function(module, exports, __webpack_require__) {


/**
 *
 *
 * Base configration of the entire Components .
 *
 *
 * Copyright 2017 ByteDance, Inc.
 * Licensed under MIT
 *
 */

/* global module*/
var Base = {

  // Prefix for each component .
  PREFIX: 'byted-',
  CSS_PREFIX: 'by-',
  LOCALE_PREFIX: 'byted'
};

module.exports = Base;

/***/ }),

/***/ 13:
/***/ (function(module, exports) {

module.exports = loading;

/***/ }),

/***/ 252:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _button = __webpack_require__(90);

var _button2 = _interopRequireDefault(_button);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

_button2.default.install = function (Vue) {
  Vue.component(_button2.default.name, _button2.default);
};

exports.default = _button2.default;

/***/ }),

/***/ 4:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isOneOf = isOneOf;
function isOneOf(value, validList) {
  for (var i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true;
    }
  }
  return false;
}

/***/ }),

/***/ 52:
/***/ (function(module, exports, __webpack_require__) {


Object.defineProperty(exports, "__esModule", {
  value: true
});

var _base = __webpack_require__(1);

var _helper = __webpack_require__(4);

var _loading = __webpack_require__(13);

var _loading2 = _interopRequireDefault(_loading);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; } //
//
//
//
//
//
//
//
//
//
//
//
//
//
//

exports.default = {
  name: _base.PREFIX + 'button',
  components: _defineProperty({}, _loading2.default.iconName, _loading2.default),
  props: {
    type: {
      type: String,
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['text', 'default', 'info', 'success', 'warning', 'danger', 'primary', 'secondary']);
      },

      default: 'default'
    },

    size: {
      type: String,
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['default', 'small', 'large', 'xl', 'lg', 'md', 'sm', 'xs']);
      },

      default: 'md'
    },

    plain: {
      type: Boolean,
      default: undefined
    },

    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    tag: {
      type: String,
      default: 'button'
    },
    htmlType: {
      type: String,
      default: 'button',
      validator: function validator(value) {
        return (0, _helper.isOneOf)(value, ['submit', 'reset', 'button', 'menu']);
      }
    }
  },

  computed: {
    baseClass: function baseClass() {
      var classArr = ['bui-btn', 'bui-btn-' + this.type, _base.CSS_PREFIX + 'button'];
      var sizeObj = {
        small: 'bui-btn-sm',
        large: 'bui-btn-lg',
        xl: 'bui-btn-xl',
        lg: 'bui-btn-lg',
        md: 'bui-btn-md',
        sm: 'bui-btn-sm',
        xs: 'bui-btn-xs',
        default: 'bui-btn-md'
      };
      if (this.size) {
        classArr.push(sizeObj[this.size]);
      }
      if (this.plain) {
        classArr.push('bui-btn-plain');
      }
      if (this.loading) {
        classArr.push('bui-btn-loading');
      }
      // button default 兼容面性和现有样式，plain = underfined && type=default，默认为线性样式
      if (this.plain === undefined && this.type === 'default') {
        classArr.push('bui-btn-plain');
      }
      return classArr;
    },
    typeAttr: function typeAttr() {
      return this.tag === 'button' ? { type: this.htmlType } : {};
    }
  },

  methods: {
    handleClick: function handleClick(evt) {
      if (this.loading) return;
      if (!this.disabled) {
        this.$emit('click', evt);
      }
    }
  }
};

/***/ }),

/***/ 90:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
Object.defineProperty(__webpack_exports__, "__esModule", { value: true });
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_button_vue__ = __webpack_require__(52);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_button_vue___default = __webpack_require__.n(__WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_button_vue__);
/* harmony namespace reexport (unknown) */ for(var __WEBPACK_IMPORT_KEY__ in __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_button_vue__) if(__WEBPACK_IMPORT_KEY__ !== 'default') (function(key) { __webpack_require__.d(__webpack_exports__, key, function() { return __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_button_vue__[key]; }); }(__WEBPACK_IMPORT_KEY__));
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_32714b89_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_button_vue__ = __webpack_require__(91);
/* harmony import */ var __WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__ = __webpack_require__(0);
/* script */


/* template */

/* template functional */
var __vue_template_functional__ = false;
/* styles */
var __vue_styles__ = null;
/* scopeId */
var __vue_scopeId__ = null;
/* moduleIdentifier (server only) */
var __vue_module_identifier__ = null;

var Component = Object(__WEBPACK_IMPORTED_MODULE_2__node_modules_vue_loader_lib_runtime_component_normalizer__["a" /* default */])(
  __WEBPACK_IMPORTED_MODULE_0__babel_loader_node_modules_vue_loader_lib_selector_type_script_index_0_button_vue___default.a,
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_32714b89_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_button_vue__["a" /* render */],
  __WEBPACK_IMPORTED_MODULE_1__node_modules_vue_loader_lib_template_compiler_index_id_data_v_32714b89_hasScoped_false_optionsId_0_buble_transforms_node_modules_vue_loader_lib_selector_type_template_index_0_button_vue__["b" /* staticRenderFns */],
  __vue_template_functional__,
  __vue_styles__,
  __vue_scopeId__,
  __vue_module_identifier__
);

/* harmony default export */ __webpack_exports__["default"] = (Component.exports);


/***/ }),

/***/ 91:
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return render; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return staticRenderFns; });
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c(_vm.$props.tag,_vm._b({tag:"component",class:_vm.baseClass,attrs:{"disabled":_vm.disabled},on:{"click":_vm.handleClick}},'component',_vm.typeAttr,false),[(_vm.loading)?_c('byted-loading-icon',{attrs:{"size":_vm.size}}):_vm._e(),_vm._v(" "),_vm._t("default")],2)};
var staticRenderFns = [];


/***/ })

/******/ });
});

var _Button = /*@__PURE__*/getDefaultExportFromCjs(button);

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

/**
  * vue-class-component v7.2.6
  * (c) 2015-present Evan You
  * @license MIT
  */

function _typeof(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

// The rational behind the verbose Reflect-feature check below is the fact that there are polyfills
// which add an implementation for Reflect.defineMetadata but not for Reflect.getOwnMetadataKeys.
// Without this check consumers will encounter hard to track down runtime errors.
function reflectionIsSupported() {
  return typeof Reflect !== 'undefined' && Reflect.defineMetadata && Reflect.getOwnMetadataKeys;
}
function copyReflectionMetadata(to, from) {
  forwardMetadata(to, from);
  Object.getOwnPropertyNames(from.prototype).forEach(function (key) {
    forwardMetadata(to.prototype, from.prototype, key);
  });
  Object.getOwnPropertyNames(from).forEach(function (key) {
    forwardMetadata(to, from, key);
  });
}

function forwardMetadata(to, from, propertyKey) {
  var metaKeys = propertyKey ? Reflect.getOwnMetadataKeys(from, propertyKey) : Reflect.getOwnMetadataKeys(from);
  metaKeys.forEach(function (metaKey) {
    var metadata = propertyKey ? Reflect.getOwnMetadata(metaKey, from, propertyKey) : Reflect.getOwnMetadata(metaKey, from);

    if (propertyKey) {
      Reflect.defineMetadata(metaKey, metadata, to, propertyKey);
    } else {
      Reflect.defineMetadata(metaKey, metadata, to);
    }
  });
}

var fakeArray = {
  __proto__: []
};
var hasProto = fakeArray instanceof Array;
function createDecorator(factory) {
  return function (target, key, index) {
    var Ctor = typeof target === 'function' ? target : target.constructor;

    if (!Ctor.__decorators__) {
      Ctor.__decorators__ = [];
    }

    if (typeof index !== 'number') {
      index = undefined;
    }

    Ctor.__decorators__.push(function (options) {
      return factory(options, key, index);
    });
  };
}
function isPrimitive(value) {
  var type = _typeof(value);

  return value == null || type !== 'object' && type !== 'function';
}

function collectDataFromConstructor(vm, Component) {
  // override _init to prevent to init as Vue instance
  var originalInit = Component.prototype._init;

  Component.prototype._init = function () {
    var _this = this;

    // proxy to actual vm
    var keys = Object.getOwnPropertyNames(vm); // 2.2.0 compat (props are no longer exposed as self properties)

    if (vm.$options.props) {
      for (var key in vm.$options.props) {
        if (!vm.hasOwnProperty(key)) {
          keys.push(key);
        }
      }
    }

    keys.forEach(function (key) {
      Object.defineProperty(_this, key, {
        get: function get() {
          return vm[key];
        },
        set: function set(value) {
          vm[key] = value;
        },
        configurable: true
      });
    });
  }; // should be acquired class property values


  var data = new Component(); // restore original _init to avoid memory leak (#209)

  Component.prototype._init = originalInit; // create plain data object

  var plainData = {};
  Object.keys(data).forEach(function (key) {
    if (data[key] !== undefined) {
      plainData[key] = data[key];
    }
  });

  return plainData;
}

var $internalHooks = ['data', 'beforeCreate', 'created', 'beforeMount', 'mounted', 'beforeDestroy', 'destroyed', 'beforeUpdate', 'updated', 'activated', 'deactivated', 'render', 'errorCaptured', 'serverPrefetch' // 2.6
];
function componentFactory(Component) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  options.name = options.name || Component._componentTag || Component.name; // prototype props.

  var proto = Component.prototype;
  Object.getOwnPropertyNames(proto).forEach(function (key) {
    if (key === 'constructor') {
      return;
    } // hooks


    if ($internalHooks.indexOf(key) > -1) {
      options[key] = proto[key];
      return;
    }

    var descriptor = Object.getOwnPropertyDescriptor(proto, key);

    if (descriptor.value !== void 0) {
      // methods
      if (typeof descriptor.value === 'function') {
        (options.methods || (options.methods = {}))[key] = descriptor.value;
      } else {
        // typescript decorated data
        (options.mixins || (options.mixins = [])).push({
          data: function data() {
            return _defineProperty({}, key, descriptor.value);
          }
        });
      }
    } else if (descriptor.get || descriptor.set) {
      // computed properties
      (options.computed || (options.computed = {}))[key] = {
        get: descriptor.get,
        set: descriptor.set
      };
    }
  });
  (options.mixins || (options.mixins = [])).push({
    data: function data() {
      return collectDataFromConstructor(this, Component);
    }
  }); // decorate options

  var decorators = Component.__decorators__;

  if (decorators) {
    decorators.forEach(function (fn) {
      return fn(options);
    });
    delete Component.__decorators__;
  } // find super


  var superProto = Object.getPrototypeOf(Component.prototype);
  var Super = superProto instanceof Vue ? superProto.constructor : Vue;
  var Extended = Super.extend(options);
  forwardStaticMembers(Extended, Component, Super);

  if (reflectionIsSupported()) {
    copyReflectionMetadata(Extended, Component);
  }

  return Extended;
}
var shouldIgnore = {
  prototype: true,
  arguments: true,
  callee: true,
  caller: true
};

function forwardStaticMembers(Extended, Original, Super) {
  // We have to use getOwnPropertyNames since Babel registers methods as non-enumerable
  Object.getOwnPropertyNames(Original).forEach(function (key) {
    // Skip the properties that should not be overwritten
    if (shouldIgnore[key]) {
      return;
    } // Some browsers does not allow reconfigure built-in properties


    var extendedDescriptor = Object.getOwnPropertyDescriptor(Extended, key);

    if (extendedDescriptor && !extendedDescriptor.configurable) {
      return;
    }

    var descriptor = Object.getOwnPropertyDescriptor(Original, key); // If the user agent does not support `__proto__` or its family (IE <= 10),
    // the sub class properties may be inherited properties from the super class in TypeScript.
    // We need to exclude such properties to prevent to overwrite
    // the component options object which stored on the extended constructor (See #192).
    // If the value is a referenced value (object or function),
    // we can check equality of them and exclude it if they have the same reference.
    // If it is a primitive value, it will be forwarded for safety.

    if (!hasProto) {
      // Only `cid` is explicitly exluded from property forwarding
      // because we cannot detect whether it is a inherited property or not
      // on the no `__proto__` environment even though the property is reserved.
      if (key === 'cid') {
        return;
      }

      var superDescriptor = Object.getOwnPropertyDescriptor(Super, key);

      if (!isPrimitive(descriptor.value) && superDescriptor && superDescriptor.value === descriptor.value) {
        return;
      }
    } // Warn if the users manually declare reserved properties

    Object.defineProperty(Extended, key, descriptor);
  });
}

function Component(options) {
  if (typeof options === 'function') {
    return componentFactory(options);
  }

  return function (Component) {
    return componentFactory(Component, options);
  };
}

Component.registerHooks = function registerHooks(keys) {
  $internalHooks.push.apply($internalHooks, _toConsumableArray(keys));
};

(undefined && undefined.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};

/** @see {@link https://github.com/vuejs/vue-class-component/blob/master/src/reflect.ts} */
var reflectMetadataIsSupported = typeof Reflect !== 'undefined' && typeof Reflect.getMetadata !== 'undefined';
function applyMetadata(options, target, key) {
    if (reflectMetadataIsSupported) {
        if (!Array.isArray(options) &&
            typeof options !== 'function' &&
            !options.hasOwnProperty('type') &&
            typeof options.type === 'undefined') {
            var type = Reflect.getMetadata('design:type', target, key);
            if (type !== Object) {
                options.type = type;
            }
        }
    }
}

/**
 * decorator of a prop
 * @param  options the options for the prop
 * @return PropertyDecorator | void
 */
function Prop(options) {
    if (options === void 0) { options = {}; }
    return function (target, key) {
        applyMetadata(options, target, key);
        createDecorator(function (componentOptions, k) {
            (componentOptions.props || (componentOptions.props = {}))[k] = options;
        })(target, key);
    };
}

var emojiRegex = function () {
  // https://mths.be/emoji
  return /\uD83C\uDFF4\uDB40\uDC67\uDB40\uDC62(?:\uDB40\uDC77\uDB40\uDC6C\uDB40\uDC73|\uDB40\uDC73\uDB40\uDC63\uDB40\uDC74|\uDB40\uDC65\uDB40\uDC6E\uDB40\uDC67)\uDB40\uDC7F|(?:\uD83E\uDDD1\uD83C\uDFFF\u200D\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1|\uD83D\uDC69\uD83C\uDFFF\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69]))(?:\uD83C[\uDFFB-\uDFFE])|(?:\uD83E\uDDD1\uD83C\uDFFE\u200D\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1|\uD83D\uDC69\uD83C\uDFFE\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69]))(?:\uD83C[\uDFFB-\uDFFD\uDFFF])|(?:\uD83E\uDDD1\uD83C\uDFFD\u200D\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1|\uD83D\uDC69\uD83C\uDFFD\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69]))(?:\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])|(?:\uD83E\uDDD1\uD83C\uDFFC\u200D\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1|\uD83D\uDC69\uD83C\uDFFC\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69]))(?:\uD83C[\uDFFB\uDFFD-\uDFFF])|(?:\uD83E\uDDD1\uD83C\uDFFB\u200D\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83E\uDDD1|\uD83D\uDC69\uD83C\uDFFB\u200D\uD83E\uDD1D\u200D(?:\uD83D[\uDC68\uDC69]))(?:\uD83C[\uDFFC-\uDFFF])|\uD83D\uDC68(?:\uD83C\uDFFB(?:\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D\uD83D\uDC68(?:\uD83C[\uDFFB-\uDFFF])|\uD83D\uDC68(?:\uD83C[\uDFFB-\uDFFF]))|\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFC-\uDFFF])|[\u2695\u2696\u2708]\uFE0F|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD]))?|(?:\uD83C[\uDFFC-\uDFFF])\u200D\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D\uD83D\uDC68(?:\uD83C[\uDFFB-\uDFFF])|\uD83D\uDC68(?:\uD83C[\uDFFB-\uDFFF]))|\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83D\uDC68|(?:\uD83D[\uDC68\uDC69])\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67]))|\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFF\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFB-\uDFFE])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFE\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFB-\uDFFD\uDFFF])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFD\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFB\uDFFC\uDFFE\uDFFF])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFC\u200D(?:\uD83E\uDD1D\u200D\uD83D\uDC68(?:\uD83C[\uDFFB\uDFFD-\uDFFF])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|(?:\uD83C\uDFFF\u200D[\u2695\u2696\u2708]|\uD83C\uDFFE\u200D[\u2695\u2696\u2708]|\uD83C\uDFFD\u200D[\u2695\u2696\u2708]|\uD83C\uDFFC\u200D[\u2695\u2696\u2708]|\u200D[\u2695\u2696\u2708])\uFE0F|\u200D(?:(?:\uD83D[\uDC68\uDC69])\u200D(?:\uD83D[\uDC66\uDC67])|\uD83D[\uDC66\uDC67])|\uD83C\uDFFF|\uD83C\uDFFE|\uD83C\uDFFD|\uD83C\uDFFC)?|(?:\uD83D\uDC69(?:\uD83C\uDFFB\u200D\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D(?:\uD83D[\uDC68\uDC69])|\uD83D[\uDC68\uDC69])|(?:\uD83C[\uDFFC-\uDFFF])\u200D\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D(?:\uD83D[\uDC68\uDC69])|\uD83D[\uDC68\uDC69]))|\uD83E\uDDD1(?:\uD83C[\uDFFB-\uDFFF])\u200D\uD83E\uDD1D\u200D\uD83E\uDDD1)(?:\uD83C[\uDFFB-\uDFFF])|\uD83D\uDC69\u200D\uD83D\uDC69\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67]))|\uD83D\uDC69(?:\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D(?:\uD83D[\uDC68\uDC69])|\uD83D[\uDC68\uDC69])|\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFF\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFE\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFD\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFC\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFB\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD]))|\uD83E\uDDD1(?:\u200D(?:\uD83E\uDD1D\u200D\uD83E\uDDD1|\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFF\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFE\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFD\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFC\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD])|\uD83C\uDFFB\u200D(?:\uD83C[\uDF3E\uDF73\uDF7C\uDF84\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDAF-\uDDB3\uDDBC\uDDBD]))|\uD83D\uDC69\u200D\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC69\u200D\uD83D\uDC69\u200D(?:\uD83D[\uDC66\uDC67])|\uD83D\uDC69\u200D\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67])|(?:\uD83D\uDC41\uFE0F\u200D\uD83D\uDDE8|\uD83E\uDDD1(?:\uD83C\uDFFF\u200D[\u2695\u2696\u2708]|\uD83C\uDFFE\u200D[\u2695\u2696\u2708]|\uD83C\uDFFD\u200D[\u2695\u2696\u2708]|\uD83C\uDFFC\u200D[\u2695\u2696\u2708]|\uD83C\uDFFB\u200D[\u2695\u2696\u2708]|\u200D[\u2695\u2696\u2708])|\uD83D\uDC69(?:\uD83C\uDFFF\u200D[\u2695\u2696\u2708]|\uD83C\uDFFE\u200D[\u2695\u2696\u2708]|\uD83C\uDFFD\u200D[\u2695\u2696\u2708]|\uD83C\uDFFC\u200D[\u2695\u2696\u2708]|\uD83C\uDFFB\u200D[\u2695\u2696\u2708]|\u200D[\u2695\u2696\u2708])|\uD83D\uDE36\u200D\uD83C\uDF2B|\uD83C\uDFF3\uFE0F\u200D\u26A7|\uD83D\uDC3B\u200D\u2744|(?:(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC70\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD35\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDCD-\uDDCF\uDDD4\uDDD6-\uDDDD])(?:\uD83C[\uDFFB-\uDFFF])|\uD83D\uDC6F|\uD83E[\uDD3C\uDDDE\uDDDF])\u200D[\u2640\u2642]|(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)(?:\uFE0F|\uD83C[\uDFFB-\uDFFF])\u200D[\u2640\u2642]|\uD83C\uDFF4\u200D\u2620|(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC70\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD35\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDCD-\uDDCF\uDDD4\uDDD6-\uDDDD])\u200D[\u2640\u2642]|[\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u2328\u23CF\u23ED-\u23EF\u23F1\u23F2\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB\u25FC\u2600-\u2604\u260E\u2611\u2618\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2640\u2642\u265F\u2660\u2663\u2665\u2666\u2668\u267B\u267E\u2692\u2694-\u2697\u2699\u269B\u269C\u26A0\u26A7\u26B0\u26B1\u26C8\u26CF\u26D1\u26D3\u26E9\u26F0\u26F1\u26F4\u26F7\u26F8\u2702\u2708\u2709\u270F\u2712\u2714\u2716\u271D\u2721\u2733\u2734\u2744\u2747\u2763\u27A1\u2934\u2935\u2B05-\u2B07\u3030\u303D\u3297\u3299]|\uD83C[\uDD70\uDD71\uDD7E\uDD7F\uDE02\uDE37\uDF21\uDF24-\uDF2C\uDF36\uDF7D\uDF96\uDF97\uDF99-\uDF9B\uDF9E\uDF9F\uDFCD\uDFCE\uDFD4-\uDFDF\uDFF5\uDFF7]|\uD83D[\uDC3F\uDCFD\uDD49\uDD4A\uDD6F\uDD70\uDD73\uDD76-\uDD79\uDD87\uDD8A-\uDD8D\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA\uDECB\uDECD-\uDECF\uDEE0-\uDEE5\uDEE9\uDEF0\uDEF3])\uFE0F|\uD83C\uDFF3\uFE0F\u200D\uD83C\uDF08|\uD83D\uDC69\u200D\uD83D\uDC67|\uD83D\uDC69\u200D\uD83D\uDC66|\uD83D\uDE35\u200D\uD83D\uDCAB|\uD83D\uDE2E\u200D\uD83D\uDCA8|\uD83D\uDC15\u200D\uD83E\uDDBA|\uD83E\uDDD1(?:\uD83C\uDFFF|\uD83C\uDFFE|\uD83C\uDFFD|\uD83C\uDFFC|\uD83C\uDFFB)?|\uD83D\uDC69(?:\uD83C\uDFFF|\uD83C\uDFFE|\uD83C\uDFFD|\uD83C\uDFFC|\uD83C\uDFFB)?|\uD83C\uDDFD\uD83C\uDDF0|\uD83C\uDDF6\uD83C\uDDE6|\uD83C\uDDF4\uD83C\uDDF2|\uD83D\uDC08\u200D\u2B1B|\u2764\uFE0F\u200D(?:\uD83D\uDD25|\uD83E\uDE79)|\uD83D\uDC41\uFE0F|\uD83C\uDFF3\uFE0F|\uD83C\uDDFF(?:\uD83C[\uDDE6\uDDF2\uDDFC])|\uD83C\uDDFE(?:\uD83C[\uDDEA\uDDF9])|\uD83C\uDDFC(?:\uD83C[\uDDEB\uDDF8])|\uD83C\uDDFB(?:\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDEE\uDDF3\uDDFA])|\uD83C\uDDFA(?:\uD83C[\uDDE6\uDDEC\uDDF2\uDDF3\uDDF8\uDDFE\uDDFF])|\uD83C\uDDF9(?:\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDED\uDDEF-\uDDF4\uDDF7\uDDF9\uDDFB\uDDFC\uDDFF])|\uD83C\uDDF8(?:\uD83C[\uDDE6-\uDDEA\uDDEC-\uDDF4\uDDF7-\uDDF9\uDDFB\uDDFD-\uDDFF])|\uD83C\uDDF7(?:\uD83C[\uDDEA\uDDF4\uDDF8\uDDFA\uDDFC])|\uD83C\uDDF5(?:\uD83C[\uDDE6\uDDEA-\uDDED\uDDF0-\uDDF3\uDDF7-\uDDF9\uDDFC\uDDFE])|\uD83C\uDDF3(?:\uD83C[\uDDE6\uDDE8\uDDEA-\uDDEC\uDDEE\uDDF1\uDDF4\uDDF5\uDDF7\uDDFA\uDDFF])|\uD83C\uDDF2(?:\uD83C[\uDDE6\uDDE8-\uDDED\uDDF0-\uDDFF])|\uD83C\uDDF1(?:\uD83C[\uDDE6-\uDDE8\uDDEE\uDDF0\uDDF7-\uDDFB\uDDFE])|\uD83C\uDDF0(?:\uD83C[\uDDEA\uDDEC-\uDDEE\uDDF2\uDDF3\uDDF5\uDDF7\uDDFC\uDDFE\uDDFF])|\uD83C\uDDEF(?:\uD83C[\uDDEA\uDDF2\uDDF4\uDDF5])|\uD83C\uDDEE(?:\uD83C[\uDDE8-\uDDEA\uDDF1-\uDDF4\uDDF6-\uDDF9])|\uD83C\uDDED(?:\uD83C[\uDDF0\uDDF2\uDDF3\uDDF7\uDDF9\uDDFA])|\uD83C\uDDEC(?:\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEE\uDDF1-\uDDF3\uDDF5-\uDDFA\uDDFC\uDDFE])|\uD83C\uDDEB(?:\uD83C[\uDDEE-\uDDF0\uDDF2\uDDF4\uDDF7])|\uD83C\uDDEA(?:\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDED\uDDF7-\uDDFA])|\uD83C\uDDE9(?:\uD83C[\uDDEA\uDDEC\uDDEF\uDDF0\uDDF2\uDDF4\uDDFF])|\uD83C\uDDE8(?:\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDEE\uDDF0-\uDDF5\uDDF7\uDDFA-\uDDFF])|\uD83C\uDDE7(?:\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEF\uDDF1-\uDDF4\uDDF6-\uDDF9\uDDFB\uDDFC\uDDFE\uDDFF])|\uD83C\uDDE6(?:\uD83C[\uDDE8-\uDDEC\uDDEE\uDDF1\uDDF2\uDDF4\uDDF6-\uDDFA\uDDFC\uDDFD\uDDFF])|[#\*0-9]\uFE0F\u20E3|\u2764\uFE0F|(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC70\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD35\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDCD-\uDDCF\uDDD4\uDDD6-\uDDDD])(?:\uD83C[\uDFFB-\uDFFF])|(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)(?:\uFE0F|\uD83C[\uDFFB-\uDFFF])|\uD83C\uDFF4|(?:[\u270A\u270B]|\uD83C[\uDF85\uDFC2\uDFC7]|\uD83D[\uDC42\uDC43\uDC46-\uDC50\uDC66\uDC67\uDC6B-\uDC6D\uDC72\uDC74-\uDC76\uDC78\uDC7C\uDC83\uDC85\uDC8F\uDC91\uDCAA\uDD7A\uDD95\uDD96\uDE4C\uDE4F\uDEC0\uDECC]|\uD83E[\uDD0C\uDD0F\uDD18-\uDD1C\uDD1E\uDD1F\uDD30-\uDD34\uDD36\uDD77\uDDB5\uDDB6\uDDBB\uDDD2\uDDD3\uDDD5])(?:\uD83C[\uDFFB-\uDFFF])|(?:[\u261D\u270C\u270D]|\uD83D[\uDD74\uDD90])(?:\uFE0F|\uD83C[\uDFFB-\uDFFF])|[\u270A\u270B]|\uD83C[\uDF85\uDFC2\uDFC7]|\uD83D[\uDC08\uDC15\uDC3B\uDC42\uDC43\uDC46-\uDC50\uDC66\uDC67\uDC6B-\uDC6D\uDC72\uDC74-\uDC76\uDC78\uDC7C\uDC83\uDC85\uDC8F\uDC91\uDCAA\uDD7A\uDD95\uDD96\uDE2E\uDE35\uDE36\uDE4C\uDE4F\uDEC0\uDECC]|\uD83E[\uDD0C\uDD0F\uDD18-\uDD1C\uDD1E\uDD1F\uDD30-\uDD34\uDD36\uDD77\uDDB5\uDDB6\uDDBB\uDDD2\uDDD3\uDDD5]|\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC70\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD35\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDCD-\uDDCF\uDDD4\uDDD6-\uDDDD]|\uD83D\uDC6F|\uD83E[\uDD3C\uDDDE\uDDDF]|[\u231A\u231B\u23E9-\u23EC\u23F0\u23F3\u25FD\u25FE\u2614\u2615\u2648-\u2653\u267F\u2693\u26A1\u26AA\u26AB\u26BD\u26BE\u26C4\u26C5\u26CE\u26D4\u26EA\u26F2\u26F3\u26F5\u26FA\u26FD\u2705\u2728\u274C\u274E\u2753-\u2755\u2757\u2795-\u2797\u27B0\u27BF\u2B1B\u2B1C\u2B50\u2B55]|\uD83C[\uDC04\uDCCF\uDD8E\uDD91-\uDD9A\uDE01\uDE1A\uDE2F\uDE32-\uDE36\uDE38-\uDE3A\uDE50\uDE51\uDF00-\uDF20\uDF2D-\uDF35\uDF37-\uDF7C\uDF7E-\uDF84\uDF86-\uDF93\uDFA0-\uDFC1\uDFC5\uDFC6\uDFC8\uDFC9\uDFCF-\uDFD3\uDFE0-\uDFF0\uDFF8-\uDFFF]|\uD83D[\uDC00-\uDC07\uDC09-\uDC14\uDC16-\uDC3A\uDC3C-\uDC3E\uDC40\uDC44\uDC45\uDC51-\uDC65\uDC6A\uDC79-\uDC7B\uDC7D-\uDC80\uDC84\uDC88-\uDC8E\uDC90\uDC92-\uDCA9\uDCAB-\uDCFC\uDCFF-\uDD3D\uDD4B-\uDD4E\uDD50-\uDD67\uDDA4\uDDFB-\uDE2D\uDE2F-\uDE34\uDE37-\uDE44\uDE48-\uDE4A\uDE80-\uDEA2\uDEA4-\uDEB3\uDEB7-\uDEBF\uDEC1-\uDEC5\uDED0-\uDED2\uDED5-\uDED7\uDEEB\uDEEC\uDEF4-\uDEFC\uDFE0-\uDFEB]|\uD83E[\uDD0D\uDD0E\uDD10-\uDD17\uDD1D\uDD20-\uDD25\uDD27-\uDD2F\uDD3A\uDD3F-\uDD45\uDD47-\uDD76\uDD78\uDD7A-\uDDB4\uDDB7\uDDBA\uDDBC-\uDDCB\uDDD0\uDDE0-\uDDFF\uDE70-\uDE74\uDE78-\uDE7A\uDE80-\uDE86\uDE90-\uDEA8\uDEB0-\uDEB6\uDEC0-\uDEC2\uDED0-\uDED6]|(?:[\u231A\u231B\u23E9-\u23EC\u23F0\u23F3\u25FD\u25FE\u2614\u2615\u2648-\u2653\u267F\u2693\u26A1\u26AA\u26AB\u26BD\u26BE\u26C4\u26C5\u26CE\u26D4\u26EA\u26F2\u26F3\u26F5\u26FA\u26FD\u2705\u270A\u270B\u2728\u274C\u274E\u2753-\u2755\u2757\u2795-\u2797\u27B0\u27BF\u2B1B\u2B1C\u2B50\u2B55]|\uD83C[\uDC04\uDCCF\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01\uDE1A\uDE2F\uDE32-\uDE36\uDE38-\uDE3A\uDE50\uDE51\uDF00-\uDF20\uDF2D-\uDF35\uDF37-\uDF7C\uDF7E-\uDF93\uDFA0-\uDFCA\uDFCF-\uDFD3\uDFE0-\uDFF0\uDFF4\uDFF8-\uDFFF]|\uD83D[\uDC00-\uDC3E\uDC40\uDC42-\uDCFC\uDCFF-\uDD3D\uDD4B-\uDD4E\uDD50-\uDD67\uDD7A\uDD95\uDD96\uDDA4\uDDFB-\uDE4F\uDE80-\uDEC5\uDECC\uDED0-\uDED2\uDED5-\uDED7\uDEEB\uDEEC\uDEF4-\uDEFC\uDFE0-\uDFEB]|\uD83E[\uDD0C-\uDD3A\uDD3C-\uDD45\uDD47-\uDD78\uDD7A-\uDDCB\uDDCD-\uDDFF\uDE70-\uDE74\uDE78-\uDE7A\uDE80-\uDE86\uDE90-\uDEA8\uDEB0-\uDEB6\uDEC0-\uDEC2\uDED0-\uDED6])|(?:[#\*0-9\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u261D\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u265F\u2660\u2663\u2665\u2666\u2668\u267B\u267E\u267F\u2692-\u2697\u2699\u269B\u269C\u26A0\u26A1\u26A7\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3\u26D4\u26E9\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC04\uDCCF\uDD70\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01\uDE02\uDE1A\uDE2F\uDE32-\uDE3A\uDE50\uDE51\uDF00-\uDF21\uDF24-\uDF93\uDF96\uDF97\uDF99-\uDF9B\uDF9E-\uDFF0\uDFF3-\uDFF5\uDFF7-\uDFFF]|\uD83D[\uDC00-\uDCFD\uDCFF-\uDD3D\uDD49-\uDD4E\uDD50-\uDD67\uDD6F\uDD70\uDD73-\uDD7A\uDD87\uDD8A-\uDD8D\uDD90\uDD95\uDD96\uDDA4\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA-\uDE4F\uDE80-\uDEC5\uDECB-\uDED2\uDED5-\uDED7\uDEE0-\uDEE5\uDEE9\uDEEB\uDEEC\uDEF0\uDEF3-\uDEFC\uDFE0-\uDFEB]|\uD83E[\uDD0C-\uDD3A\uDD3C-\uDD45\uDD47-\uDD78\uDD7A-\uDDCB\uDDCD-\uDDFF\uDE70-\uDE74\uDE78-\uDE7A\uDE80-\uDE86\uDE90-\uDEA8\uDEB0-\uDEB6\uDEC0-\uDEC2\uDED0-\uDED6])\uFE0F|(?:[\u261D\u26F9\u270A-\u270D]|\uD83C[\uDF85\uDFC2-\uDFC4\uDFC7\uDFCA-\uDFCC]|\uD83D[\uDC42\uDC43\uDC46-\uDC50\uDC66-\uDC78\uDC7C\uDC81-\uDC83\uDC85-\uDC87\uDC8F\uDC91\uDCAA\uDD74\uDD75\uDD7A\uDD90\uDD95\uDD96\uDE45-\uDE47\uDE4B-\uDE4F\uDEA3\uDEB4-\uDEB6\uDEC0\uDECC]|\uD83E[\uDD0C\uDD0F\uDD18-\uDD1F\uDD26\uDD30-\uDD39\uDD3C-\uDD3E\uDD77\uDDB5\uDDB6\uDDB8\uDDB9\uDDBB\uDDCD-\uDDCF\uDDD1-\uDDDD])/g;
};

const translateTzOption = (tzOption, $t) => {
  if (!tzOption) {
    return '';
  } // gmt_offset格式示范: "GMT-08:00"、 "GMT+08:00"、 "GMT+00:00"
  // GMT+08:00 => UTC+08:00
  // NOTE: Etc/GMT+08:00 => UTC-08:00


  const utcOffset = tzOption.gmt_offset.replace('GMT', 'UTC'); // will not change origin string

  const tzTextArr = ($t(tzOption.i18n_key) || '').split(' ');
  const tzText = tzTextArr[1] || tzTextArr[0] || '';
  return `(${utcOffset}) ${tzText.trim()}`;
};
const SCRIPTS_LIST = [{
  // 全角字符(部分全角字符和Latin存在重合，需要在它之前判断。https://unicode.org/charts/PDF/UFF00.pdf
  // eslint-disable-next-line
  regex: /\u2502|[\u2590-\u2593]|\u25a0|\u25cb|[\u3001-\u3002\u300c-\u300d]|\u3099|\u309a[\u30a1-\u30ab]|\u30ad|\u30af|\u30b1|\u30b3|\u30b5|\u30b7|\u30b9|\u30bb|\u30bd|\u30bf|\u30c1|\u30c3|\u30c4|\u30c6|\u30c8|[\u30ca-\u30cf]|\u30d2|\u30d5|\u30d8|\u30db|\u30de|\u30df|[\u30e0-\u30ef\u30f2-\u30f3\u30fb-\u30fc\u3131-\u3164\uFF00-\uFF60\uFFe0-\uFFe6]/,
  length: 2,
  name: 'FullWidth',
  priority: 1
}, {
  // 半角字符，https://unicode.org/charts/PDF/UFF00.pdf
  regex: /[\u0020-\u007e\u00a2-\u00a3\u00a5-\u00a6]|\u00a9|\u00ac|\u00af|[\u2985-\u2986\uff61-\uffdc\uffe8-\uffee]/,
  length: 1,
  name: 'HalfWidth',
  priority: 1
}, {
  regex: emojiRegex(),
  length: 2,
  name: 'emoji',
  priority: 10
}];

function getCharLength(char = '') {
  const target = SCRIPTS_LIST.find(({
    regex
  }) => {
    regex.lastIndex = 0; // 重置正则表达式匹配的起始索引

    return regex.test(char);
  });
  return target ? target.length : 1;
} // babel translation of this regexp:  /(\P{Mark})(\p{Mark}+)/gu
// eslint-disable-next-line


const regexSymbolWithCombiningMarks = /((?:[\0-\u02FF\u0370-\u0482\u048A-\u0590\u05BE\u05C0\u05C3\u05C6\u05C8-\u060F\u061B-\u064A\u0660-\u066F\u0671-\u06D5\u06DD\u06DE\u06E5\u06E6\u06E9\u06EE-\u0710\u0712-\u072F\u074B-\u07A5\u07B1-\u07EA\u07F4-\u07FC\u07FE-\u0815\u081A\u0824\u0828\u082E-\u0858\u085C-\u08D2\u08E2\u0904-\u0939\u093D\u0950\u0958-\u0961\u0964-\u0980\u0984-\u09BB\u09BD\u09C5\u09C6\u09C9\u09CA\u09CE-\u09D6\u09D8-\u09E1\u09E4-\u09FD\u09FF\u0A00\u0A04-\u0A3B\u0A3D\u0A43-\u0A46\u0A49\u0A4A\u0A4E-\u0A50\u0A52-\u0A6F\u0A72-\u0A74\u0A76-\u0A80\u0A84-\u0ABB\u0ABD\u0AC6\u0ACA\u0ACE-\u0AE1\u0AE4-\u0AF9\u0B00\u0B04-\u0B3B\u0B3D\u0B45\u0B46\u0B49\u0B4A\u0B4E-\u0B55\u0B58-\u0B61\u0B64-\u0B81\u0B83-\u0BBD\u0BC3-\u0BC5\u0BC9\u0BCE-\u0BD6\u0BD8-\u0BFF\u0C05-\u0C3D\u0C45\u0C49\u0C4E-\u0C54\u0C57-\u0C61\u0C64-\u0C80\u0C84-\u0CBB\u0CBD\u0CC5\u0CC9\u0CCE-\u0CD4\u0CD7-\u0CE1\u0CE4-\u0CFF\u0D04-\u0D3A\u0D3D\u0D45\u0D49\u0D4E-\u0D56\u0D58-\u0D61\u0D64-\u0D81\u0D84-\u0DC9\u0DCB-\u0DCE\u0DD5\u0DD7\u0DE0-\u0DF1\u0DF4-\u0E30\u0E32\u0E33\u0E3B-\u0E46\u0E4F-\u0EB0\u0EB2\u0EB3\u0EBD-\u0EC7\u0ECE-\u0F17\u0F1A-\u0F34\u0F36\u0F38\u0F3A-\u0F3D\u0F40-\u0F70\u0F85\u0F88-\u0F8C\u0F98\u0FBD-\u0FC5\u0FC7-\u102A\u103F-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u1090-\u1099\u109E-\u135C\u1360-\u1711\u1715-\u1731\u1735-\u1751\u1754-\u1771\u1774-\u17B3\u17D4-\u17DC\u17DE-\u180A\u180E-\u1884\u1887-\u18A8\u18AA-\u191F\u192C-\u192F\u193C-\u1A16\u1A1C-\u1A54\u1A5F\u1A7D\u1A7E\u1A80-\u1AAF\u1ABF-\u1AFF\u1B05-\u1B33\u1B45-\u1B6A\u1B74-\u1B7F\u1B83-\u1BA0\u1BAE-\u1BE5\u1BF4-\u1C23\u1C38-\u1CCF\u1CD3\u1CE9-\u1CEC\u1CEE-\u1CF3\u1CF5\u1CF6\u1CFA-\u1DBF\u1DFA\u1E00-\u20CF\u20F1-\u2CEE\u2CF2-\u2D7E\u2D80-\u2DDF\u2E00-\u3029\u3030-\u3098\u309B-\uA66E\uA673\uA67E-\uA69D\uA6A0-\uA6EF\uA6F2-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA828-\uA87F\uA882-\uA8B3\uA8C6-\uA8DF\uA8F2-\uA8FE\uA900-\uA925\uA92E-\uA946\uA954-\uA97F\uA984-\uA9B2\uA9C1-\uA9E4\uA9E6-\uAA28\uAA37-\uAA42\uAA44-\uAA4B\uAA4E-\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2-\uAAEA\uAAF0-\uAAF4\uAAF7-\uABE2\uABEB\uABEE-\uD7FF\uE000-\uFB1D\uFB1F-\uFDFF\uFE10-\uFE1F\uFE30-\uFFFF]|\uD800[\uDC00-\uDDFC\uDDFE-\uDEDF\uDEE1-\uDF75\uDF7B-\uDFFF]|[\uD801\uD808-\uD819\uD81C-\uD82E\uD830-\uD833\uD835\uD837\uD839\uD83B-\uDB3F\uDB41-\uDBFF][\uDC00-\uDFFF]|\uD802[\uDC00-\uDE00\uDE04\uDE07-\uDE0B\uDE10-\uDE37\uDE3B-\uDE3E\uDE40-\uDEE4\uDEE7-\uDFFF]|\uD803[\uDC00-\uDD23\uDD28-\uDF45\uDF51-\uDFFF]|\uD804[\uDC03-\uDC37\uDC47-\uDC7E\uDC83-\uDCAF\uDCBB-\uDCFF\uDD03-\uDD26\uDD35-\uDD44\uDD47-\uDD72\uDD74-\uDD7F\uDD83-\uDDB2\uDDC1-\uDDC8\uDDCD-\uDE2B\uDE38-\uDE3D\uDE3F-\uDEDE\uDEEB-\uDEFF\uDF04-\uDF3A\uDF3D\uDF45\uDF46\uDF49\uDF4A\uDF4E-\uDF56\uDF58-\uDF61\uDF64\uDF65\uDF6D-\uDF6F\uDF75-\uDFFF]|\uD805[\uDC00-\uDC34\uDC47-\uDC5D\uDC5F-\uDCAF\uDCC4-\uDDAE\uDDB6\uDDB7\uDDC1-\uDDDB\uDDDE-\uDE2F\uDE41-\uDEAA\uDEB8-\uDF1C\uDF2C-\uDFFF]|\uD806[\uDC00-\uDC2B\uDC3B-\uDDD0\uDDD8\uDDD9\uDDE1-\uDDE3\uDDE5-\uDE00\uDE0B-\uDE32\uDE3A\uDE3F-\uDE46\uDE48-\uDE50\uDE5C-\uDE89\uDE9A-\uDFFF]|\uD807[\uDC00-\uDC2E\uDC37\uDC40-\uDC91\uDCA8\uDCB7-\uDD30\uDD37-\uDD39\uDD3B\uDD3E\uDD46\uDD48-\uDD89\uDD8F\uDD92\uDD98-\uDEF2\uDEF7-\uDFFF]|\uD81A[\uDC00-\uDEEF\uDEF5-\uDF2F\uDF37-\uDFFF]|\uD81B[\uDC00-\uDF4E\uDF50\uDF88-\uDF8E\uDF93-\uDFFF]|\uD82F[\uDC00-\uDC9C\uDC9F-\uDFFF]|\uD834[\uDC00-\uDD64\uDD6A-\uDD6C\uDD73-\uDD7A\uDD83\uDD84\uDD8C-\uDDA9\uDDAE-\uDE41\uDE45-\uDFFF]|\uD836[\uDC00-\uDDFF\uDE37-\uDE3A\uDE6D-\uDE74\uDE76-\uDE83\uDE85-\uDE9A\uDEA0\uDEB0-\uDFFF]|\uD838[\uDC07\uDC19\uDC1A\uDC22\uDC25\uDC2B-\uDD2F\uDD37-\uDEEB\uDEF0-\uDFFF]|\uD83A[\uDC00-\uDCCF\uDCD7-\uDD43\uDD4B-\uDFFF]|\uDB40[\uDC00-\uDCFF\uDDF0-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))((?:[\u0300-\u036F\u0483-\u0489\u0591-\u05BD\u05BF\u05C1\u05C2\u05C4\u05C5\u05C7\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06DC\u06DF-\u06E4\u06E7\u06E8\u06EA-\u06ED\u0711\u0730-\u074A\u07A6-\u07B0\u07EB-\u07F3\u07FD\u0816-\u0819\u081B-\u0823\u0825-\u0827\u0829-\u082D\u0859-\u085B\u08D3-\u08E1\u08E3-\u0903\u093A-\u093C\u093E-\u094F\u0951-\u0957\u0962\u0963\u0981-\u0983\u09BC\u09BE-\u09C4\u09C7\u09C8\u09CB-\u09CD\u09D7\u09E2\u09E3\u09FE\u0A01-\u0A03\u0A3C\u0A3E-\u0A42\u0A47\u0A48\u0A4B-\u0A4D\u0A51\u0A70\u0A71\u0A75\u0A81-\u0A83\u0ABC\u0ABE-\u0AC5\u0AC7-\u0AC9\u0ACB-\u0ACD\u0AE2\u0AE3\u0AFA-\u0AFF\u0B01-\u0B03\u0B3C\u0B3E-\u0B44\u0B47\u0B48\u0B4B-\u0B4D\u0B56\u0B57\u0B62\u0B63\u0B82\u0BBE-\u0BC2\u0BC6-\u0BC8\u0BCA-\u0BCD\u0BD7\u0C00-\u0C04\u0C3E-\u0C44\u0C46-\u0C48\u0C4A-\u0C4D\u0C55\u0C56\u0C62\u0C63\u0C81-\u0C83\u0CBC\u0CBE-\u0CC4\u0CC6-\u0CC8\u0CCA-\u0CCD\u0CD5\u0CD6\u0CE2\u0CE3\u0D00-\u0D03\u0D3B\u0D3C\u0D3E-\u0D44\u0D46-\u0D48\u0D4A-\u0D4D\u0D57\u0D62\u0D63\u0D82\u0D83\u0DCA\u0DCF-\u0DD4\u0DD6\u0DD8-\u0DDF\u0DF2\u0DF3\u0E31\u0E34-\u0E3A\u0E47-\u0E4E\u0EB1\u0EB4-\u0EBC\u0EC8-\u0ECD\u0F18\u0F19\u0F35\u0F37\u0F39\u0F3E\u0F3F\u0F71-\u0F84\u0F86\u0F87\u0F8D-\u0F97\u0F99-\u0FBC\u0FC6\u102B-\u103E\u1056-\u1059\u105E-\u1060\u1062-\u1064\u1067-\u106D\u1071-\u1074\u1082-\u108D\u108F\u109A-\u109D\u135D-\u135F\u1712-\u1714\u1732-\u1734\u1752\u1753\u1772\u1773\u17B4-\u17D3\u17DD\u180B-\u180D\u1885\u1886\u18A9\u1920-\u192B\u1930-\u193B\u1A17-\u1A1B\u1A55-\u1A5E\u1A60-\u1A7C\u1A7F\u1AB0-\u1ABE\u1B00-\u1B04\u1B34-\u1B44\u1B6B-\u1B73\u1B80-\u1B82\u1BA1-\u1BAD\u1BE6-\u1BF3\u1C24-\u1C37\u1CD0-\u1CD2\u1CD4-\u1CE8\u1CED\u1CF4\u1CF7-\u1CF9\u1DC0-\u1DF9\u1DFB-\u1DFF\u20D0-\u20F0\u2CEF-\u2CF1\u2D7F\u2DE0-\u2DFF\u302A-\u302F\u3099\u309A\uA66F-\uA672\uA674-\uA67D\uA69E\uA69F\uA6F0\uA6F1\uA802\uA806\uA80B\uA823-\uA827\uA880\uA881\uA8B4-\uA8C5\uA8E0-\uA8F1\uA8FF\uA926-\uA92D\uA947-\uA953\uA980-\uA983\uA9B3-\uA9C0\uA9E5\uAA29-\uAA36\uAA43\uAA4C\uAA4D\uAA7B-\uAA7D\uAAB0\uAAB2-\uAAB4\uAAB7\uAAB8\uAABE\uAABF\uAAC1\uAAEB-\uAAEF\uAAF5\uAAF6\uABE3-\uABEA\uABEC\uABED\uFB1E\uFE00-\uFE0F\uFE20-\uFE2F]|\uD800[\uDDFD\uDEE0\uDF76-\uDF7A]|\uD802[\uDE01-\uDE03\uDE05\uDE06\uDE0C-\uDE0F\uDE38-\uDE3A\uDE3F\uDEE5\uDEE6]|\uD803[\uDD24-\uDD27\uDF46-\uDF50]|\uD804[\uDC00-\uDC02\uDC38-\uDC46\uDC7F-\uDC82\uDCB0-\uDCBA\uDD00-\uDD02\uDD27-\uDD34\uDD45\uDD46\uDD73\uDD80-\uDD82\uDDB3-\uDDC0\uDDC9-\uDDCC\uDE2C-\uDE37\uDE3E\uDEDF-\uDEEA\uDF00-\uDF03\uDF3B\uDF3C\uDF3E-\uDF44\uDF47\uDF48\uDF4B-\uDF4D\uDF57\uDF62\uDF63\uDF66-\uDF6C\uDF70-\uDF74]|\uD805[\uDC35-\uDC46\uDC5E\uDCB0-\uDCC3\uDDAF-\uDDB5\uDDB8-\uDDC0\uDDDC\uDDDD\uDE30-\uDE40\uDEAB-\uDEB7\uDF1D-\uDF2B]|\uD806[\uDC2C-\uDC3A\uDDD1-\uDDD7\uDDDA-\uDDE0\uDDE4\uDE01-\uDE0A\uDE33-\uDE39\uDE3B-\uDE3E\uDE47\uDE51-\uDE5B\uDE8A-\uDE99]|\uD807[\uDC2F-\uDC36\uDC38-\uDC3F\uDC92-\uDCA7\uDCA9-\uDCB6\uDD31-\uDD36\uDD3A\uDD3C\uDD3D\uDD3F-\uDD45\uDD47\uDD8A-\uDD8E\uDD90\uDD91\uDD93-\uDD97\uDEF3-\uDEF6]|\uD81A[\uDEF0-\uDEF4\uDF30-\uDF36]|\uD81B[\uDF4F\uDF51-\uDF87\uDF8F-\uDF92]|\uD82F[\uDC9D\uDC9E]|\uD834[\uDD65-\uDD69\uDD6D-\uDD72\uDD7B-\uDD82\uDD85-\uDD8B\uDDAA-\uDDAD\uDE42-\uDE44]|\uD836[\uDE00-\uDE36\uDE3B-\uDE6C\uDE75\uDE84\uDE9B-\uDE9F\uDEA1-\uDEAF]|\uD838[\uDC00-\uDC06\uDC08-\uDC18\uDC1B-\uDC21\uDC23\uDC24\uDC26-\uDC2A\uDD30-\uDD36\uDEEC-\uDEEF]|\uD83A[\uDCD0-\uDCD6\uDD44-\uDD4A]|\uDB40[\uDD00-\uDDEF])+)/g;
function stringLength(str = '') {
  // 预处理组合字符，返回多个简单字符的合成字符
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
  // 目前不能识别三个或三个以上字符的合成,例如印地语
  // const normalized = str.normalize();
  //  删除任何组合字符，只留下它们所属的字符。  https://my.oschina.net/u/3375885/blog/2998185
  // 正则表达式利用了ES2018新特性 Unicode属性类。 http://es6.ruanyifeng.com/#docs/regex#Unicode-%E5%B1%9E%E6%80%A7%E7%B1%BB
  // const regexSymbolWithCombiningMarks = /(\P{Mark})(\p{Mark}+)/gu;
  const normalized = str.replace(regexSymbolWithCombiningMarks, (_$0, symbol) => symbol);
  let length = 0;

  for (const symbol of normalized) {
    length += getCharLength(symbol);
  }

  return length;
}
/**
 * JSON 转换为 queryString
 * @param obj
 * @param prefix
 * @returns {string}
 */

const jsonToQuery = (obj, prefix) => (typeof prefix === 'undefined' ? '?' : prefix) + Object.keys(obj).filter(item => typeof obj[item] !== 'undefined' && obj[item] !== null).map(item => [item, encodeURIComponent(obj[item])].join('=')).join('&');

var bc_account_num$e = "عدد الحسابات: {num}";
var bc_avartar$e = "صورة الملف الشخصي";
var bc_cancel$e = "إلغاء";
var bc_create$e = "إنشاء";
var bc_create_title$e = "إنشاء مركز أعمال";
var bc_info$e = "إدارة حسابات الإعلانات وأعضاء مركز الأعمال والأذونات. كل ذلك في مكان واحد ومجانًا.";
var bc_member_num$e = "عدد الأعضاء: {num}";
var bc_name$e = "اسم مركز الأعمال";
var bc_name_empty_hint$e = "يرجى إدخال اسم مركز الأعمال.";
var bc_name_length_hint$e = "لا يمكن أن يتجاوز اسم مركز الأعمال 200 حرف.";
var bc_name_placeholder$e = "يرجى إدخال اسم مركز الأعمال";
var bc_timezone$e = "المنطقة الزمنية";
var bc_timezone_empty_hint$e = "يرجى تحديد منطقة زمنية.";
var bc_timezone_hint$e = "سيتم استخدام المنطقة الزمنية التي تحددها عند إجراء تحليل بيانات لإعلاناتك. ونحن لا ندعم حاليا وظائف أخرى ذات صلة. يرجى تحديد المنطقة الزمنية الأفضل لعرض تحليل البيانات الإجمالي لمركز الأعمال هذا.";
var bc_timezone_placeholder$e = "حدد منطقة زمنية";
var bc_type_agency$e = "الجهة";
var ar = {
	bc_account_num: bc_account_num$e,
	bc_avartar: bc_avartar$e,
	bc_cancel: bc_cancel$e,
	bc_create: bc_create$e,
	bc_create_title: bc_create_title$e,
	bc_info: bc_info$e,
	bc_member_num: bc_member_num$e,
	bc_name: bc_name$e,
	bc_name_empty_hint: bc_name_empty_hint$e,
	bc_name_length_hint: bc_name_length_hint$e,
	bc_name_placeholder: bc_name_placeholder$e,
	bc_timezone: bc_timezone$e,
	bc_timezone_empty_hint: bc_timezone_empty_hint$e,
	bc_timezone_hint: bc_timezone_hint$e,
	bc_timezone_placeholder: bc_timezone_placeholder$e,
	bc_type_agency: bc_type_agency$e,
	"tz_key_Asia/Phnom_Penh": "توقيت آسيا \\ بنوم بنه",
	"tz_key_Etc/GMT-13": "(UTC-13:00) المحيط الهادئ/أبيا"
};

var bc_account_num$d = "Kontobetrag: {num}";
var bc_avartar$d = "Profilbild";
var bc_cancel$d = "Abbrechen";
var bc_create$d = "Erstellen";
var bc_create_title$d = "Geschäftszentrum anlegen";
var bc_info$d = "Verwalten Sie Ihre Werbekonten, Center-Mitglieder und deren Berechtigungsstatus im Geschäftszentrum. Alles in einem und frei.";
var bc_member_num$d = "Mitgliedsbetrag: {num}";
var bc_name$d = "Name des Geschäftszentrums";
var bc_name_empty_hint$d = "Geben Sie den Namen des Geschäftszentrums ein.";
var bc_name_length_hint$d = "Der Name des Geschäftszentrums darf 200 Zeichen nicht überschreiten.";
var bc_name_placeholder$d = "Geben Sie den Namen des Geschäftszentrums ein.";
var bc_timezone$d = "Zeitzone";
var bc_timezone_empty_hint$d = "Bitte wählen Sie eine Zeitzone aus.";
var bc_timezone_hint$d = "Zeitzonendaten werden für spätere Datenauswertungen benötigt. Zur Zeit unterstützen wir keine entsprechenden Funktionen. Bitte wählen Sie die Zeitzone für die allgemeine Datenansicht im Geschäftszentrum aus.";
var bc_timezone_placeholder$d = "Wählen Sie eine Zeitzone aus";
var bc_type_agency$d = "Agentur";
var de = {
	bc_account_num: bc_account_num$d,
	bc_avartar: bc_avartar$d,
	bc_cancel: bc_cancel$d,
	bc_create: bc_create$d,
	bc_create_title: bc_create_title$d,
	bc_info: bc_info$d,
	bc_member_num: bc_member_num$d,
	bc_name: bc_name$d,
	bc_name_empty_hint: bc_name_empty_hint$d,
	bc_name_length_hint: bc_name_length_hint$d,
	bc_name_placeholder: bc_name_placeholder$d,
	bc_timezone: bc_timezone$d,
	bc_timezone_empty_hint: bc_timezone_empty_hint$d,
	bc_timezone_hint: bc_timezone_hint$d,
	bc_timezone_placeholder: bc_timezone_placeholder$d,
	bc_type_agency: bc_type_agency$d,
	"tz_key_Africa/Accra": "(UTC+00:00) Afrika/Akkra",
	"tz_key_Africa/Cairo": "(UTC+02:00) Afrika/Kairo",
	"tz_key_Africa/Casablanca": "(UTC+00:00) Afrika/Casablanca",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) Afrika/Johannesburg",
	"tz_key_Africa/Lagos": "(UTC+01:00) Afrika/Lagos",
	"tz_key_Africa/Nairobi": "(UTC+03:00) Afrika/Nairobi",
	"tz_key_Africa/Tunis": "(UTC+01:00) Afrika/Tunis",
	"tz_key_America/Anchorage": "(UTC-09:00) Amerika/Anchorage",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) Amerika/Argentinien/Buenos_Aires",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) Amerika/Argentinien/Salta",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) Amerika/Argentinien/San_Luis",
	"tz_key_America/Asuncion": "(UTC-04:00) Amerika/Asunción",
	"tz_key_America/Atikokan": "(UTC-05:00) Amerika/Atikokan",
	"tz_key_America/Belem": "(UTC-03:00) Amerika/Belem",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) Amerika/Blanc-Sablon",
	"tz_key_America/Bogota": "(UTC-05:00) Amerika/Bogota",
	"tz_key_America/Campo_Grande": "(UTC-04:00) Amerika/Campo_Grande",
	"tz_key_America/Caracas": "(UTC-04:30) Amerika/Caracas",
	"tz_key_America/Chicago": "(UTC-06:00) Amerika/Chicago",
	"tz_key_America/Costa_Rica": "(UTC-06:00) Amerika/Costa_Rica",
	"tz_key_America/Dawson": "(UTC-08:00) Amerika/Dawson",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) Amerika/Dawson_Creek",
	"tz_key_America/Denver": "(UTC-07:00) Amerika/Denver",
	"tz_key_America/Edmonton": "(UTC-07:00) Amerika/Edmonton",
	"tz_key_America/El_Salvador": "(UTC-06:00) Amerika/El_Salvador",
	"tz_key_America/Guatemala": "(UTC-06:00) Amerika/Guatemala",
	"tz_key_America/Guayaquil": "(UTC-05:00) Amerika/Guayaquil",
	"tz_key_America/Halifax": "(UTC-04:00) Amerika/Halifax",
	"tz_key_America/Hermosillo": "(UTC-07:00) Amerika/Hermosillo",
	"tz_key_America/Iqaluit": "(UTC-05:00) Amerika/Iqaluit",
	"tz_key_America/Jamaica": "(UTC-05:00) Amerika/Jamaika",
	"tz_key_America/La_Paz": "(UTC-04:00) Amerika/La_Paz",
	"tz_key_America/Lima": "(UTC-05:00) Amerika/Lima",
	"tz_key_America/Los_Angeles": "(UTC-08:00) Amerika/Los_Angeles",
	"tz_key_America/Managua": "(UTC-06:00) Amerika/Managua",
	"tz_key_America/Mazatlan": "(UTC-07:00) Amerika/Mazatlan",
	"tz_key_America/Mexico_City": "(UTC-06:00) Amerika/Mexiko_Stadt",
	"tz_key_America/Montevideo": "(UTC-03:00) Amerika/Montevideo",
	"tz_key_America/Nassau": "(UTC-05:00) Amerika/Nassau",
	"tz_key_America/New_York": "(UTC-05:00) Amerika/New_York",
	"tz_key_America/Noronha": "(UTC-02:00) Amerika/Noronha",
	"tz_key_America/Panama": "(UTC-05:00) Amerika/Panama",
	"tz_key_America/Phoenix": "(UTC-07:00) Amerika/Phoenix",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) Amerika/Port_of_Spain",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) Amerika/Puerto_Rico",
	"tz_key_America/Rainy_River": "(UTC-06:00) Amerika/Rainy_River",
	"tz_key_America/Regina": "(UTC-06:00) Amerika/Regina",
	"tz_key_America/Santiago": "(UTC-04:00) Amerika/Santiago",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) Amerika/Santo_Domingo",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) Amerika/Sao_Paulo",
	"tz_key_America/St_Johns": "(UTC-03:30) Amerika/St_Johns",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) Amerika/Tegucigalpa",
	"tz_key_America/Tijuana": "(UTC-08:00) Amerika/Tijuana",
	"tz_key_America/Toronto": "(UTC-05:00) Amerika/Toronto",
	"tz_key_America/Vancouver": "(UTC-08:00) Amerika/Vancouver",
	"tz_key_Asia/Amman": "(UTC+02:00) Asien/Amman",
	"tz_key_Asia/Baghdad": "(UTC+03:00) Asien/Bagdad",
	"tz_key_Asia/Bahrain": "(UTC+03:00) Asien/Bahrain",
	"tz_key_Asia/Bangkok": "(UTC+07:00) Asien/Bangkok",
	"tz_key_Asia/Beirut": "(UTC+02:00) Asien/Beirut",
	"tz_key_Asia/Colombo": "(UTC+05:30) Asien/Colombo",
	"tz_key_Asia/Dhaka": "(UTC+06:00) Asien/Dhaka",
	"tz_key_Asia/Dubai": "(UTC+04:00) Asien/Dubai",
	"tz_key_Asia/Gaza": "(UTC+02:00) Asien/Gaza",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) Asien/Saigon",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) Asien/Hongkong",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) Asien/Irkutsk",
	"tz_key_Asia/Jakarta": "(UTC+07:00) Asien/Jakarta",
	"tz_key_Asia/Jayapura": "(UTC+09:00) Asien/Jayapura",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) Asien/Jerusalem",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) Asien/Kamtschatka",
	"tz_key_Asia/Karachi": "(UTC+05:00) Asien/Karachi",
	"tz_key_Asia/Kolkata": "(UTC+05:30) Asien/Kalkutta",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) Asien/Krasnojarsk",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) Asien/Kuala_Lumpur",
	"tz_key_Asia/Kuwait": "(UTC+03:00) Asien/Kuwait",
	"tz_key_Asia/Magadan": "(UTC+11:00) Asien/Magadan",
	"tz_key_Asia/Makassar": "(UTC+08:00) Asien/Makassar",
	"tz_key_Asia/Manila": "(UTC+08:00) Asien/Manila",
	"tz_key_Asia/Muscat": "(UTC+04:00) Asien/Muscat",
	"tz_key_Asia/Nicosia": "(UTC+02:00) Asien/Nikosia",
	"tz_key_Asia/Omsk": "(UTC+06:00) Asien/Omsk",
	"tz_key_Asia/Phnom_Penh": "Asien/Phnom Penh",
	"tz_key_Asia/Qatar": "(UTC+03:00) Asien/Katar",
	"tz_key_Asia/Riyadh": "(UTC+03:00) Asien/Riad",
	"tz_key_Asia/Seoul": "(UTC+09:00) Asien/Seoul",
	"tz_key_Asia/Shanghai": "(UTC+08:00) Asien/Peking",
	"tz_key_Asia/Singapore": "(UTC+08:00) Asien/Singapur",
	"tz_key_Asia/Taipei": "(UTC+08:00) Asien/Taipeh",
	"tz_key_Asia/Tokyo": "(UTC+09:00) Asien/Tokio",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) Asien/Wladiwostok",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) Asien/Jakutsk",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) Asien/Jekaterinburg",
	"tz_key_Atlantic/Azores": "(UTC-01:00) Atlantik/Azoren",
	"tz_key_Atlantic/Canary": "(UTC+00:00) Atlantik/Kanaren",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) Atlantik/Reykjavik",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) Australien/Broken_Hill",
	"tz_key_Australia/Perth": "(UTC+08:00) Australien/Perth",
	"tz_key_Australia/Sydney": "(UTC+10:00) Australien/Sydney",
	"tz_key_Etc/GMT": "(UTC) London",
	"tz_key_Etc/GMT+1": "(UTC-01:00) Ponta Delgada",
	"tz_key_Etc/GMT+10": "(UTC-10:00) Honolulu_Hawaii-Zeit",
	"tz_key_Etc/GMT+11": "(UTC-11:00) Pago Pago",
	"tz_key_Etc/GMT+12": "(UTC-12:00) Majuro",
	"tz_key_Etc/GMT+2": "(UTC-02:00) Noronha",
	"tz_key_Etc/GMT+3": "(UTC-03:00) Sao Paulo",
	"tz_key_Etc/GMT+4": "(UTC-04:00) Caracas",
	"tz_key_Etc/GMT+5": "(UTC-05:00) New York_Eastern Time",
	"tz_key_Etc/GMT+6": "(UTC-06:00) Mexico Stadt_Central Time",
	"tz_key_Etc/GMT+7": "(UTC-07:00) Salt Lake City_Mountain Time",
	"tz_key_Etc/GMT+8": "(UTC-08:00) Los Angeles_Pacific Time",
	"tz_key_Etc/GMT+9": "(UTC-09:00) Juno_Alaska Time",
	"tz_key_Etc/GMT-1": "(UTC+01:00) Berlin",
	"tz_key_Etc/GMT-10": "(UTC+10:00) Canberra",
	"tz_key_Etc/GMT-11": "(UTC+11:00) Honiara",
	"tz_key_Etc/GMT-12": "(UTC+12:00) Wellington",
	"tz_key_Etc/GMT-13": "Pazifik/Apia",
	"tz_key_Etc/GMT-2": "(UTC+02:00) Athen",
	"tz_key_Etc/GMT-3": "(UTC+03:00) Moskau",
	"tz_key_Etc/GMT-4": "(UTC+04:00) Abu Dhabi",
	"tz_key_Etc/GMT-5": "(UTC+05:00) Islamabad",
	"tz_key_Etc/GMT-6": "(UTC+06:00) Dhaka",
	"tz_key_Etc/GMT-7": "(UTC+07:00) Bangkok",
	"tz_key_Etc/GMT-8": "(UTC+08:00) Peking",
	"tz_key_Etc/GMT-9": "(UTC+09:00) Tokio",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) Europa/Amsterdam",
	"tz_key_Europe/Athens": "(UTC+02:00) Europa/Athen",
	"tz_key_Europe/Belgrade": "(UTC+01:00) Europa/Belgien",
	"tz_key_Europe/Berlin": "(UTC+01:00) Europa/Berlin",
	"tz_key_Europe/Bratislava": "(UTC+01:00) Europa/Bratislava",
	"tz_key_Europe/Brussels": "(UTC+01:00) Europa/Brüssel",
	"tz_key_Europe/Bucharest": "(UTC+02:00) Europa/Bukarest",
	"tz_key_Europe/Budapest": "(UTC+01:00) Europa/Budapest",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) Europa/Kopenhagen",
	"tz_key_Europe/Dublin": "(UTC+00:00) Europa/Dublin",
	"tz_key_Europe/Helsinki": "(UTC+02:00) Europa/Helsinki",
	"tz_key_Europe/Istanbul": "(UTC+02:00) Europa/Istanbul",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) Europa/Kaliningrad",
	"tz_key_Europe/Kiev": "(UTC+02:00) Europa/Kiew",
	"tz_key_Europe/Lisbon": "(UTC+00:00) Europa/Lissabon",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) Europa/Ljubljana",
	"tz_key_Europe/London": "(UTC+00:00) Europa/London",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) Europa/Luxemburg",
	"tz_key_Europe/Madrid": "(UTC+01:00) Europa/Madrid",
	"tz_key_Europe/Malta": "(UTC+01:00) Europa/Malta",
	"tz_key_Europe/Moscow": "(UTC+03:00) Europa/Moskau",
	"tz_key_Europe/Oslo": "(UTC+01:00) Europa/Oslo",
	"tz_key_Europe/Paris": "(UTC+01:00) Europa/Paris",
	"tz_key_Europe/Prague": "(UTC+01:00) Europa/Prag",
	"tz_key_Europe/Riga": "(UTC+02:00) Europa/Riga",
	"tz_key_Europe/Rome": "(UTC+01:00) Europa/Rom",
	"tz_key_Europe/Samara": "(UTC+03:00) Europa/Samara",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) Europa/Sarajevo",
	"tz_key_Europe/Skopje": "(UTC+01:00) Europa/Skopje",
	"tz_key_Europe/Sofia": "(UTC+02:00) Europa/Sofia",
	"tz_key_Europe/Stockholm": "(UTC+01:00) Europa/Stockholm",
	"tz_key_Europe/Tallinn": "(UTC+02:00) Europa/Tallinn",
	"tz_key_Europe/Vienna": "(UTC+01:00) Europa/Wien",
	"tz_key_Europe/Vilnius": "(UTC+02:00) Europa/Vilnius",
	"tz_key_Europe/Warsaw": "(UTC+01:00) Europa/Warschau",
	"tz_key_Europe/Zagreb": "(UTC+01:00) Europa/Zagreb",
	"tz_key_Europe/Zurich": "(UTC+01:00) Europa/Zürich",
	"tz_key_Indian/Maldives": "(UTC+05:00) Indianer/Malediven",
	"tz_key_Indian/Mauritius": "(UTC+04:00) Indianer/Mauritius",
	"tz_key_Pacific/Apia": "(UTC-11:00) Pazifik/Apia",
	"tz_key_Pacific/Auckland": "(UTC+12:00) Pazifik/Auckland",
	"tz_key_Pacific/Easter": "(UTC-06:00) Pazifik/Osterinsel",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) Pazifik/Galapagos",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) Pazifik/Honolulu",
	"tz_key_Pacific/Majuro": "(UTC-12:00) Pazifik/Majuro",
	"tz_key_Pacific/Midway": "(UTC-11:00) Pazifik/Midway",
	"tz_key_Pacific/Niue": "(UTC-11:00) Pazifik/Niue",
	"tz_key_Pacific/Pago Pago": "Pazifik/Pago Pago",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) Pazifik/Pago Pago"
};

var bc_account_num$c = "No. of Accounts: {num}";
var bc_avartar$c = "Profile Image";
var bc_cancel$c = "Cancel";
var bc_create$c = "Create";
var bc_create_title$c = "Create Business Center";
var bc_info$c = "Manage your ad accounts, Business Center members, and permissions. It's all in one place and it's free.";
var bc_member_num$c = "No. of Members: {num}";
var bc_name$c = "Business Center Name";
var bc_name_empty_hint$c = "Please enter the Business Center name.";
var bc_name_length_hint$c = "Business Center name can't exceed 200 characters.";
var bc_name_placeholder$c = "Please enter the Business Center name";
var bc_timezone$c = "Time Zone";
var bc_timezone_empty_hint$c = "Please select a time zone.";
var bc_timezone_hint$c = "The time zone you select will be used when carrying out data analysis of your ads. We currently do not support other related functions. Please select the time zone that is best for viewing the overall data analysis of this Business Center.";
var bc_timezone_placeholder$c = "Select a time zone";
var bc_type_agency$c = "Agency";
var en = {
	bc_account_num: bc_account_num$c,
	bc_avartar: bc_avartar$c,
	bc_cancel: bc_cancel$c,
	bc_create: bc_create$c,
	bc_create_title: bc_create_title$c,
	bc_info: bc_info$c,
	bc_member_num: bc_member_num$c,
	bc_name: bc_name$c,
	bc_name_empty_hint: bc_name_empty_hint$c,
	bc_name_length_hint: bc_name_length_hint$c,
	bc_name_placeholder: bc_name_placeholder$c,
	bc_timezone: bc_timezone$c,
	bc_timezone_empty_hint: bc_timezone_empty_hint$c,
	bc_timezone_hint: bc_timezone_hint$c,
	bc_timezone_placeholder: bc_timezone_placeholder$c,
	bc_type_agency: bc_type_agency$c,
	"tz_key_Africa/Accra": "(UTC+00:00) Africa/Accra",
	"tz_key_Africa/Cairo": "(UTC+02:00) Africa/Cairo",
	"tz_key_Africa/Casablanca": "(UTC+00:00) Africa/Casablanca",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) Africa/Johannesburg",
	"tz_key_Africa/Lagos": "(UTC+01:00) Africa/Lagos",
	"tz_key_Africa/Nairobi": "(UTC+03:00) Africa/Nairobi",
	"tz_key_Africa/Tunis": "(UTC+01:00) Africa/Tunis",
	"tz_key_America/Anchorage": "(UTC-09:00) America/Anchorage",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) America/Argentina/Buenos_Aires",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) America/Argentina/Salta",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) America/Argentina/San_Luis",
	"tz_key_America/Asuncion": "(UTC-04:00) America/Asuncion",
	"tz_key_America/Atikokan": "(UTC-05:00) America/Atikokan",
	"tz_key_America/Belem": "(UTC-03:00) America/Belem",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) America/Blanc-Sablon",
	"tz_key_America/Bogota": "(UTC-05:00) America/Bogota",
	"tz_key_America/Campo_Grande": "(UTC-04:00) America/Campo_Grande",
	"tz_key_America/Caracas": "(UTC-04:30) America/Caracas",
	"tz_key_America/Chicago": "(UTC-06:00) America/Chicago",
	"tz_key_America/Costa_Rica": "(UTC-06:00) America/Costa_Rica",
	"tz_key_America/Dawson": "(UTC-08:00) America/Dawson",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) America/Dawson_Creek",
	"tz_key_America/Denver": "(UTC-07:00) America/Denver",
	"tz_key_America/Edmonton": "(UTC-07:00) America/Edmonton",
	"tz_key_America/El_Salvador": "(UTC-06:00) America/El_Salvador",
	"tz_key_America/Guatemala": "(UTC-06:00) America/Guatemala",
	"tz_key_America/Guayaquil": "(UTC-05:00) America/Guayaquil",
	"tz_key_America/Halifax": "(UTC-04:00) America/Halifax",
	"tz_key_America/Hermosillo": "(UTC-07:00) America/Hermosillo",
	"tz_key_America/Iqaluit": "(UTC-05:00) America/Iqaluit",
	"tz_key_America/Jamaica": "(UTC-05:00) America/Jamaica",
	"tz_key_America/La_Paz": "(UTC-04:00) America/La_Paz",
	"tz_key_America/Lima": "(UTC-05:00) America/Lima",
	"tz_key_America/Los_Angeles": "(UTC-08:00) America/Los_Angeles",
	"tz_key_America/Managua": "(UTC-06:00) America/Managua",
	"tz_key_America/Mazatlan": "(UTC-07:00) America/Mazatlan",
	"tz_key_America/Mexico_City": "(UTC-06:00) America/Mexico_City",
	"tz_key_America/Montevideo": "(UTC-03:00) America/Montevideo",
	"tz_key_America/Nassau": "(UTC-05:00) America/Nassau",
	"tz_key_America/New_York": "(UTC-05:00) America/New_York",
	"tz_key_America/Noronha": "(UTC-02:00) America/Noronha",
	"tz_key_America/Panama": "(UTC-05:00) America/Panama",
	"tz_key_America/Phoenix": "(UTC-07:00) America/Phoenix",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) America/Port_of_Spain",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) America/Puerto_Rico",
	"tz_key_America/Rainy_River": "(UTC-06:00) America/Rainy_River",
	"tz_key_America/Regina": "(UTC-06:00) America/Regina",
	"tz_key_America/Santiago": "(UTC-04:00) America/Santiago",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) America/Santo_Domingo",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) America/Sao_Paulo",
	"tz_key_America/St_Johns": "(UTC-03:30) America/St_Johns",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) America/Tegucigalpa",
	"tz_key_America/Tijuana": "(UTC-08:00) America/Tijuana",
	"tz_key_America/Toronto": "(UTC-05:00) America/Toronto",
	"tz_key_America/Vancouver": "(UTC-08:00) America/Vancouver",
	"tz_key_Asia/Amman": "(UTC+02:00) Asia/Amman",
	"tz_key_Asia/Baghdad": "(UTC+03:00) Asia/Baghdad",
	"tz_key_Asia/Bahrain": "(UTC+03:00) Asia/Bahrain",
	"tz_key_Asia/Bangkok": "(UTC+07:00) Asia/Bangkok",
	"tz_key_Asia/Beirut": "(UTC+02:00) Asia/Beirut",
	"tz_key_Asia/Colombo": "(UTC+05:30) Asia/Colombo",
	"tz_key_Asia/Dhaka": "(UTC+06:00) Asia/Dhaka",
	"tz_key_Asia/Dubai": "(UTC+04:00) Asia/Dubai",
	"tz_key_Asia/Gaza": "(UTC+02:00) Asia/Gaza",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) Asia/Ho_Chi_Minh",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) Asia/Hong_Kong",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) Asia/Irkutsk",
	"tz_key_Asia/Jakarta": "(UTC+07:00) Asia/Jakarta",
	"tz_key_Asia/Jayapura": "(UTC+09:00) Asia/Jayapura",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) Asia/Jerusalem",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) Asia/Kamchatka",
	"tz_key_Asia/Karachi": "(UTC+05:00) Asia/Karachi",
	"tz_key_Asia/Kolkata": "(UTC+05:30) Asia/Kolkata",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) Asia/Krasnoyarsk",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) Asia/Kuala_Lumpur",
	"tz_key_Asia/Kuwait": "(UTC+03:00) Asia/Kuwait",
	"tz_key_Asia/Magadan": "(UTC+11:00) Asia/Magadan",
	"tz_key_Asia/Makassar": "(UTC+08:00) Asia/Makassar",
	"tz_key_Asia/Manila": "(UTC+08:00) Asia/Manila",
	"tz_key_Asia/Muscat": "(UTC+04:00) Asia/Muscat",
	"tz_key_Asia/Nicosia": "(UTC+02:00) Asia/Nicosia",
	"tz_key_Asia/Omsk": "(UTC+06:00) Asia/Omsk",
	"tz_key_Asia/Phnom_Penh": "Asia/Phnom_Penh",
	"tz_key_Asia/Qatar": "(UTC+03:00) Asia/Qatar",
	"tz_key_Asia/Riyadh": "(UTC+03:00) Asia/Riyadh",
	"tz_key_Asia/Seoul": "(UTC+09:00) Asia/Seoul",
	"tz_key_Asia/Shanghai": "(UTC+08:00) Asia/Beijing",
	"tz_key_Asia/Singapore": "(UTC+08:00) Asia/Singapore",
	"tz_key_Asia/Taipei": "(UTC+08:00) Asia/Taipei",
	"tz_key_Asia/Tokyo": "(UTC+09:00) Asia/Tokyo",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) Asia/Vladivostok",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) Asia/Yakutsk",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) Asia/Yekaterinburg",
	"tz_key_Atlantic/Azores": "(UTC-01:00) Atlantic/Azores",
	"tz_key_Atlantic/Canary": "(UTC+00:00) Atlantic/Canary",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) Atlantic/Reykjavik",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) Australia/Broken_Hill",
	"tz_key_Australia/Perth": "(UTC+08:00) Australia/Perth",
	"tz_key_Australia/Sydney": "(UTC+10:00) Australia/Sydney",
	"tz_key_Etc/GMT": "(UTC+00:00)Europe/London",
	"tz_key_Etc/GMT+0": "(UTC+00:00)Europe/London",
	"tz_key_Etc/GMT+1": "(UTC-01:00)Atlantic/Azores",
	"tz_key_Etc/GMT+10": "(UTC-10:00)Pacific/Honolulu",
	"tz_key_Etc/GMT+11": "(UTC-11:00)Pacific/Midway",
	"tz_key_Etc/GMT+12": "(UTC-12:00)Pacific/Majuro",
	"tz_key_Etc/GMT+2": "(UTC-02:00)America/Noronha",
	"tz_key_Etc/GMT+3": "(UTC-03:00)America/Sao_Paulo",
	"tz_key_Etc/GMT+4": "(UTC-04:00)America/Santiago",
	"tz_key_Etc/GMT+5": "(UTC-05:00)America/New_York",
	"tz_key_Etc/GMT+6": "(UTC-06:00)America/Mexico_City",
	"tz_key_Etc/GMT+7": "(UTC-07:00)America/Denver",
	"tz_key_Etc/GMT+8": "(UTC-08:00)America/Los_Angeles",
	"tz_key_Etc/GMT+9": "(UTC-09:00)America/Anchorage",
	"tz_key_Etc/GMT-1": "(UTC+01:00)Europe/Berlin",
	"tz_key_Etc/GMT-10": "(UTC+10:00)Australia/Sydney",
	"tz_key_Etc/GMT-11": "(UTC+11:00)Asia/Magadan",
	"tz_key_Etc/GMT-12": "(UTC+12:00)Pacific/Auckland",
	"tz_key_Etc/GMT-13": "Pacific/Apia",
	"tz_key_Etc/GMT-2": "(UTC+02:00)Europe/Athens",
	"tz_key_Etc/GMT-3": "(UTC+03:00)Europe/Moscow",
	"tz_key_Etc/GMT-4": "(UTC+04:00)Asia/Dubai",
	"tz_key_Etc/GMT-5": "(UTC+05:00)Asia/Karachi",
	"tz_key_Etc/GMT-6": "(UTC+06:00)Asia/Dhaka",
	"tz_key_Etc/GMT-7": "(UTC+07:00)Asia/Bangkok",
	"tz_key_Etc/GMT-8": "(UTC+08:00)Asia/Beijing",
	"tz_key_Etc/GMT-9": "(UTC+09:00)Asia/Tokyo",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) Europe/Amsterdam",
	"tz_key_Europe/Athens": "(UTC+02:00) Europe/Athens",
	"tz_key_Europe/Belgrade": "(UTC+01:00) Europe/Belgrade",
	"tz_key_Europe/Berlin": "(UTC+01:00) Europe/Berlin",
	"tz_key_Europe/Bratislava": "(UTC+01:00) Europe/Bratislava",
	"tz_key_Europe/Brussels": "(UTC+01:00) Europe/Brussels",
	"tz_key_Europe/Bucharest": "(UTC+02:00) Europe/Bucharest",
	"tz_key_Europe/Budapest": "(UTC+01:00) Europe/Budapest",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) Europe/Copenhagen",
	"tz_key_Europe/Dublin": "(UTC+00:00) Europe/Dublin",
	"tz_key_Europe/Helsinki": "(UTC+02:00) Europe/Helsinki",
	"tz_key_Europe/Istanbul": "(UTC+02:00) Europe/Istanbul",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) Europe/Kaliningrad",
	"tz_key_Europe/Kiev": "(UTC+02:00) Europe/Kiev",
	"tz_key_Europe/Lisbon": "(UTC+00:00) Europe/Lisbon",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) Europe/Ljubljana",
	"tz_key_Europe/London": "(UTC+00:00) Europe/London",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) Europe/Luxembourg",
	"tz_key_Europe/Madrid": "(UTC+01:00) Europe/Madrid",
	"tz_key_Europe/Malta": "(UTC+01:00) Europe/Malta",
	"tz_key_Europe/Moscow": "(UTC+03:00) Europe/Moscow",
	"tz_key_Europe/Oslo": "(UTC+01:00) Europe/Oslo",
	"tz_key_Europe/Paris": "(UTC+01:00) Europe/Paris",
	"tz_key_Europe/Prague": "(UTC+01:00) Europe/Prague",
	"tz_key_Europe/Riga": "(UTC+02:00) Europe/Riga",
	"tz_key_Europe/Rome": "(UTC+01:00) Europe/Rome",
	"tz_key_Europe/Samara": "(UTC+03:00) Europe/Samara",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) Europe/Sarajevo",
	"tz_key_Europe/Skopje": "(UTC+01:00) Europe/Skopje",
	"tz_key_Europe/Sofia": "(UTC+02:00) Europe/Sofia",
	"tz_key_Europe/Stockholm": "(UTC+01:00) Europe/Stockholm",
	"tz_key_Europe/Tallinn": "(UTC+02:00) Europe/Tallinn",
	"tz_key_Europe/Vienna": "(UTC+01:00) Europe/Vienna",
	"tz_key_Europe/Vilnius": "(UTC+02:00) Europe/Vilnius",
	"tz_key_Europe/Warsaw": "(UTC+01:00) Europe/Warsaw",
	"tz_key_Europe/Zagreb": "(UTC+01:00) Europe/Zagreb",
	"tz_key_Europe/Zurich": "(UTC+01:00) Europe/Zurich",
	"tz_key_Indian/Maldives": "(UTC+05:00) Indian/Maldives",
	"tz_key_Indian/Mauritius": "(UTC+04:00) Indian/Mauritius",
	"tz_key_Pacific/Apia": "(UTC-11:00) Pacific/Apia",
	"tz_key_Pacific/Auckland": "(UTC+12:00) Pacific/Auckland",
	"tz_key_Pacific/Easter": "(UTC-06:00) Pacific/Easter",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) Pacific/Galapagos",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) Pacific/Honolulu",
	"tz_key_Pacific/Majuro": "(UTC-12:00) Pacific/Majuro",
	"tz_key_Pacific/Midway": "(UTC-11:00) Pacific/Midway",
	"tz_key_Pacific/Niue": "(UTC-11:00) Pacific/Niue",
	"tz_key_Pacific/Pago Pago": "(UTC-11:00) Pacific/Pago Pago",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) Pacific/Pago Pago"
};

var bc_account_num$b = "Número de cuentas: {num}";
var bc_avartar$b = "Imagen de perfil";
var bc_cancel$b = "Cancelar";
var bc_create$b = "Crear";
var bc_create_title$b = "Crear Centro de negocios";
var bc_info$b = "Puedes gestionar tus cuentas de anuncios, los miembros del centro y sus permisos en el Centro de negocios. Todo ello desde un único punto y de forma gratuita.";
var bc_member_num$b = "Número de miembros: {num}";
var bc_name$b = "Nombre del Centro de negocios";
var bc_name_empty_hint$b = "Introduce el nombre del Centro de negocios.";
var bc_name_length_hint$b = "El nombre del Centro de negocios no puede rebasar los 200 caracteres.";
var bc_name_placeholder$b = "Introduce el nombre del Centro de negocios.";
var bc_timezone$b = "Huso horario";
var bc_timezone_empty_hint$b = "Selecciona el huso horario.";
var bc_timezone_hint$b = "Los datos del huso horario se usarían en un análisis posterior. En la actualidad no admitimos las funciones relacionadas. Selecciona el huso horario para la visualización de los datos generales en el Centro de negocios.";
var bc_timezone_placeholder$b = "Seleccionar el huso horario";
var bc_type_agency$b = "Agencia";
var es = {
	bc_account_num: bc_account_num$b,
	bc_avartar: bc_avartar$b,
	bc_cancel: bc_cancel$b,
	bc_create: bc_create$b,
	bc_create_title: bc_create_title$b,
	bc_info: bc_info$b,
	bc_member_num: bc_member_num$b,
	bc_name: bc_name$b,
	bc_name_empty_hint: bc_name_empty_hint$b,
	bc_name_length_hint: bc_name_length_hint$b,
	bc_name_placeholder: bc_name_placeholder$b,
	bc_timezone: bc_timezone$b,
	bc_timezone_empty_hint: bc_timezone_empty_hint$b,
	bc_timezone_hint: bc_timezone_hint$b,
	bc_timezone_placeholder: bc_timezone_placeholder$b,
	bc_type_agency: bc_type_agency$b,
	"tz_key_Africa/Accra": "(UTC+00:00) África/Acra",
	"tz_key_Africa/Cairo": "(UTC+02:00) África/El Cairo",
	"tz_key_Africa/Casablanca": "(UTC+00:00) África/Casablanca",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) África/Johannesburgo",
	"tz_key_Africa/Lagos": "(UTC+01:00) África/Lagos",
	"tz_key_Africa/Nairobi": "(UTC+03:00) África/Nairobi",
	"tz_key_Africa/Tunis": "(UTC+01:00) África/Túnez",
	"tz_key_America/Anchorage": "(UTC-09:00) América/Anchorage",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) América/Argentina/Buenos_Aires",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) América/Argentina/Salta",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) América/Argentina/San_Luis",
	"tz_key_America/Asuncion": "(UTC-04:00) América/Asunción",
	"tz_key_America/Atikokan": "(UTC-05:00) América/Atikokan",
	"tz_key_America/Belem": "(UTC-03:00) América/Belén",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) América/Blanc-Sablon",
	"tz_key_America/Bogota": "(UTC-05:00) América/Bogotá",
	"tz_key_America/Campo_Grande": "(UTC-04:00) América/Campo_Grande",
	"tz_key_America/Caracas": "(UTC-04:30) América/Caracas",
	"tz_key_America/Chicago": "(UTC-06:00) América/Chicago",
	"tz_key_America/Costa_Rica": "(UTC-06:00) América/Costa_Rica",
	"tz_key_America/Dawson": "(UTC-08:00) América/Dawson",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) América/Dawson_Creek",
	"tz_key_America/Denver": "(UTC-07:00) América/Denver",
	"tz_key_America/Edmonton": "(UTC-07:00) América/Edmonton",
	"tz_key_America/El_Salvador": "(UTC-06:00) América/El_Salvador",
	"tz_key_America/Guatemala": "(UTC-06:00) América/Guatemala",
	"tz_key_America/Guayaquil": "(UTC-05:00) América/Guayaquil",
	"tz_key_America/Halifax": "(UTC-04:00) América/Halifax",
	"tz_key_America/Hermosillo": "(UTC-07:00) América/Hermosillo",
	"tz_key_America/Iqaluit": "(UTC-05:00) América/Iqaluit",
	"tz_key_America/Jamaica": "(UTC-05:00) América/Jamaica",
	"tz_key_America/La_Paz": "(UTC-04:00) América/La_Paz",
	"tz_key_America/Lima": "(UTC-05:00) América/Lima",
	"tz_key_America/Los_Angeles": "(UTC-08:00) América/Los_Angeles",
	"tz_key_America/Managua": "(UTC-06:00) América/Managua",
	"tz_key_America/Mazatlan": "(UTC-07:00) América/Mazatlán",
	"tz_key_America/Mexico_City": "(UTC-06:00) América/Ciudad_de_México",
	"tz_key_America/Montevideo": "(UTC-03:00) América/Montevideo",
	"tz_key_America/Nassau": "(UTC-05:00) América/Nassau",
	"tz_key_America/New_York": "(UTC-05:00) América/Nueva_York",
	"tz_key_America/Noronha": "(UTC-02:00) América/Noronha",
	"tz_key_America/Panama": "(UTC-05:00) América/Panamá",
	"tz_key_America/Phoenix": "(UTC-07:00) América/Phoenix",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) América/Puerto España",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) América/Puerto_Rico",
	"tz_key_America/Rainy_River": "(UTC-06:00) América/Rainy_River",
	"tz_key_America/Regina": "(UTC-06:00) América/Regina",
	"tz_key_America/Santiago": "(UTC-04:00) América/Santiago",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) América/Santo_Domingo",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) América/Sao_Paulo",
	"tz_key_America/St_Johns": "(UTC-03:30) América/San_Juan_de_Terranova",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) América/Tegucigalpa",
	"tz_key_America/Tijuana": "(UTC-08:00) América/Tijuana",
	"tz_key_America/Toronto": "(UTC-05:00) América/Toronto",
	"tz_key_America/Vancouver": "(UTC-08:00) América/Vancouver",
	"tz_key_Asia/Amman": "(UTC+02:00) Asia/Amán",
	"tz_key_Asia/Baghdad": "(UTC+03:00) Asia/Bagdad",
	"tz_key_Asia/Bahrain": "(UTC+03:00) Asia/Bahréin",
	"tz_key_Asia/Bangkok": "(UTC+07:00) Asia/Bangkok",
	"tz_key_Asia/Beirut": "(UTC+02:00) Asia/Beirut",
	"tz_key_Asia/Colombo": "(UTC+05:30) Asia/Colombo",
	"tz_key_Asia/Dhaka": "(UTC+06:00) Asia/Daca",
	"tz_key_Asia/Dubai": "(UTC+04:00) Asia/Dubái",
	"tz_key_Asia/Gaza": "(UTC+02:00) Asia/Gaza",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) Asia/Ho_Chi_Minh",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) Asia/Hong_Kong",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) Asia/Irkutsk",
	"tz_key_Asia/Jakarta": "(UTC+07:00) Asia/Yakarta",
	"tz_key_Asia/Jayapura": "(UTC+09:00) Asia/Jayapura",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) Asia/Jerusalén",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) Asia/Kamchatka",
	"tz_key_Asia/Karachi": "(UTC+05:00) Asia/Karachi",
	"tz_key_Asia/Kolkata": "(UTC+05:30) Asia/Kolkata",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) Asia/Krasnoyarsk",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) Asia/Kuala_Lumpur",
	"tz_key_Asia/Kuwait": "(UTC+03:00) Asia/Kuwait",
	"tz_key_Asia/Magadan": "(UTC+11:00) Asia/Magadán",
	"tz_key_Asia/Makassar": "(UTC+08:00) Asia/Macasar",
	"tz_key_Asia/Manila": "(UTC+08:00) Asia/Manila",
	"tz_key_Asia/Muscat": "(UTC+04:00) Asia/Mascate",
	"tz_key_Asia/Nicosia": "(UTC+02:00) Asia/Nicosia",
	"tz_key_Asia/Omsk": "(UTC+06:00) Asia/Omsk",
	"tz_key_Asia/Phnom_Penh": "Asia/Phnom Penh",
	"tz_key_Asia/Qatar": "(UTC+03:00) Asia/Catar",
	"tz_key_Asia/Riyadh": "(UTC+03:00) Asia/Riad",
	"tz_key_Asia/Seoul": "(UTC+09:00) Asia/Seúl",
	"tz_key_Asia/Shanghai": "(UTC+08:00) Asia/Pekín",
	"tz_key_Asia/Singapore": "(UTC+08:00) Asia/Singapur",
	"tz_key_Asia/Taipei": "(UTC+08:00) Asia/Taipéi",
	"tz_key_Asia/Tokyo": "(UTC+09:00) Asia/Tokio",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) Asia/Vladivostok",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) Asia/Yakutsk",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) Asia/Yekaterinburgo",
	"tz_key_Atlantic/Azores": "(UTC-01:00) Atlántico/Azores",
	"tz_key_Atlantic/Canary": "(UTC+00:00) Atlántico/Islas Canarias",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) Atlántico/Reikiavik",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) Australia/Broken_Hill",
	"tz_key_Australia/Perth": "(UTC+08:00) Australia/Perth",
	"tz_key_Australia/Sydney": "(UTC+10:00) Australia/Sídney",
	"tz_key_Etc/GMT": "(UTC) Londres",
	"tz_key_Etc/GMT+1": "(UTC-01:00) Ponta Delgada",
	"tz_key_Etc/GMT+10": "(UTC-10:00) Hora de Honolulu_Hawaii",
	"tz_key_Etc/GMT+11": "(UTC-11:00) Pago Pago",
	"tz_key_Etc/GMT+12": "(UTC-12:00) Majuro",
	"tz_key_Etc/GMT+2": "(UTC-02:00) Noronha",
	"tz_key_Etc/GMT+3": "(UTC-03:00) Sao Paulo",
	"tz_key_Etc/GMT+4": "(UTC-04:00) Caracas",
	"tz_key_Etc/GMT+5": "(UTC-05:00) Nueva York_Hora oriental",
	"tz_key_Etc/GMT+6": "(UTC-06:00) Ciudad de México_Hora central",
	"tz_key_Etc/GMT+7": "(UTC-07:00) Salt Lake City_Hora de las Montañas Rocosas",
	"tz_key_Etc/GMT+8": "(UTC-08:00) Los Ángeles_Hora del Pacífico",
	"tz_key_Etc/GMT+9": "(UTC-09:00) Juno_Hora de Alaska",
	"tz_key_Etc/GMT-1": "(UTC+01:00) Berlín",
	"tz_key_Etc/GMT-10": "(UTC+10:00) Canberra",
	"tz_key_Etc/GMT-11": "(UTC+11:00) Honiara",
	"tz_key_Etc/GMT-12": "(UTC+12:00) Wellington",
	"tz_key_Etc/GMT-13": "Pacífico/Apia",
	"tz_key_Etc/GMT-2": "(UTC+02:00) Atenas",
	"tz_key_Etc/GMT-3": "(UTC+03:00) Moscú",
	"tz_key_Etc/GMT-4": "(UTC+04:00) Abu Dabi",
	"tz_key_Etc/GMT-5": "(UTC+05:00) Islamabad",
	"tz_key_Etc/GMT-6": "(UTC+06:00) Daca",
	"tz_key_Etc/GMT-7": "(UTC+07:00) Bangkok",
	"tz_key_Etc/GMT-8": "(UTC+08:00) Pekín",
	"tz_key_Etc/GMT-9": "(UTC+09:00) Tokio",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) Europa/Ámsterdam",
	"tz_key_Europe/Athens": "(UTC+02:00) Europa/Atenas",
	"tz_key_Europe/Belgrade": "(UTC+01:00) Europa/Belgrado",
	"tz_key_Europe/Berlin": "(UTC+01:00) Europa/Berlín",
	"tz_key_Europe/Bratislava": "(UTC+01:00) Europa/Bratislava",
	"tz_key_Europe/Brussels": "(UTC+01:00) Europa/Bruselas",
	"tz_key_Europe/Bucharest": "(UTC+02:00) Europa/Bucarest",
	"tz_key_Europe/Budapest": "(UTC+01:00) Europa/Budapest",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) Europa/Copenhague",
	"tz_key_Europe/Dublin": "(UTC+00:00) Europa/Dublín",
	"tz_key_Europe/Helsinki": "(UTC+02:00) Europa/Helsinki",
	"tz_key_Europe/Istanbul": "(UTC+02:00) Europa/Estambul",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) Europa/Kaliningrado",
	"tz_key_Europe/Kiev": "(UTC+02:00) Europa/Kiev",
	"tz_key_Europe/Lisbon": "(UTC+00:00) Europa/Lisboa",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) Europa/Liubliana",
	"tz_key_Europe/London": "(UTC+00:00) Europa/Londres",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) Europa/Luxemburgo",
	"tz_key_Europe/Madrid": "(UTC+01:00) Europa/Madrid",
	"tz_key_Europe/Malta": "(UTC+01:00) Europa/Malta",
	"tz_key_Europe/Moscow": "(UTC+03:00) Europa/Moscú",
	"tz_key_Europe/Oslo": "(UTC+01:00) Europa/Oslo",
	"tz_key_Europe/Paris": "(UTC+01:00) Europa/París",
	"tz_key_Europe/Prague": "(UTC+01:00) Europa/Praga",
	"tz_key_Europe/Riga": "(UTC+02:00) Europa/Riga",
	"tz_key_Europe/Rome": "(UTC+01:00) Europa/Roma",
	"tz_key_Europe/Samara": "(UTC+03:00) Europa/Samara",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) Europa/Sarajevo",
	"tz_key_Europe/Skopje": "(UTC+01:00) Europa/Skopie",
	"tz_key_Europe/Sofia": "(UTC+02:00) Europa/Sofía",
	"tz_key_Europe/Stockholm": "(UTC+01:00) Europa/Estocolmo",
	"tz_key_Europe/Tallinn": "(UTC+02:00) Europa/Tallin",
	"tz_key_Europe/Vienna": "(UTC+01:00) Europa/Viena",
	"tz_key_Europe/Vilnius": "(UTC+02:00) Europa/Vilna",
	"tz_key_Europe/Warsaw": "(UTC+01:00) Europa/Varsovia",
	"tz_key_Europe/Zagreb": "(UTC+01:00) Europa/Zagreb",
	"tz_key_Europe/Zurich": "(UTC+01:00) Europa/Zúrich",
	"tz_key_Indian/Maldives": "(UTC+05:00) Índico/Maldivas",
	"tz_key_Indian/Mauritius": "(UTC+04:00) Índico/Mauricio",
	"tz_key_Pacific/Apia": "(UTC-11:00) Pacífico/Apia",
	"tz_key_Pacific/Auckland": "(UTC+12:00) Pacífico/Auckland",
	"tz_key_Pacific/Easter": "(UTC-06:00) Pacífico/Isla de Pascua",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) Pacífico/Galápagos",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) Pacífico/Honolulu",
	"tz_key_Pacific/Majuro": "(UTC-12:00) Pacífico/Majuro",
	"tz_key_Pacific/Midway": "(UTC-11:00) Pacífico/Midway",
	"tz_key_Pacific/Niue": "(UTC-11:00) Pacífico/Niue",
	"tz_key_Pacific/Pago Pago": "Pacífico/Pago Pago",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) Pacífico/Pago Pago"
};

var bc_account_num$a = "Montant du compte : {num}";
var bc_avartar$a = "Photo de profil";
var bc_cancel$a = "Annuler";
var bc_create$a = "Créer";
var bc_create_title$a = "Créer un Centre d'affaires";
var bc_info$a = "Gérer vos comptes publicitaires, les membres du centre et le statut de leurs permissions dans le Centre d'affaires. Tout-en-un et gratuit.";
var bc_member_num$a = "Montant du membre : {num}";
var bc_name$a = "Nom du Centre d'affaires";
var bc_name_empty_hint$a = "Veuillez renseigner le nom du Centre d'affaires.";
var bc_name_length_hint$a = "Le nom du Centre d'affaires ne peut contenir plus de 200 caractères.";
var bc_name_placeholder$a = "Veuillez renseigner le nom du Centre d'affaires.";
var bc_timezone$a = "Fuseau horaire";
var bc_timezone_empty_hint$a = "Veuillez sélectionner le fuseau horaire.";
var bc_timezone_hint$a = "Les données relatives au fuseau horaire seront utilisées lors de l'analyse post-phase. Nous n'acceptons actuellement pas les fonctions associées. Veuillez sélectionner le fuseau horaire pour la visualisation des données générales dans le Centre d'affaires.";
var bc_timezone_placeholder$a = "Sélectionner le fuseau horaire";
var bc_type_agency$a = "Agence";
var fr = {
	bc_account_num: bc_account_num$a,
	bc_avartar: bc_avartar$a,
	bc_cancel: bc_cancel$a,
	bc_create: bc_create$a,
	bc_create_title: bc_create_title$a,
	bc_info: bc_info$a,
	bc_member_num: bc_member_num$a,
	bc_name: bc_name$a,
	bc_name_empty_hint: bc_name_empty_hint$a,
	bc_name_length_hint: bc_name_length_hint$a,
	bc_name_placeholder: bc_name_placeholder$a,
	bc_timezone: bc_timezone$a,
	bc_timezone_empty_hint: bc_timezone_empty_hint$a,
	bc_timezone_hint: bc_timezone_hint$a,
	bc_timezone_placeholder: bc_timezone_placeholder$a,
	bc_type_agency: bc_type_agency$a,
	"tz_key_Africa/Accra": "(UTC+00:00) Afrique/Accra",
	"tz_key_Africa/Cairo": "(UTC+02:00) Afrique/Le Caire",
	"tz_key_Africa/Casablanca": "(UTC+00:00) Afrique/Casablanca",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) Afrique/Johannesbourg",
	"tz_key_Africa/Lagos": "(UTC+01:00) Afrique/Lagos",
	"tz_key_Africa/Nairobi": "(UTC+03:00) Afrique/Nairobi",
	"tz_key_Africa/Tunis": "(UTC+01:00) Afrique/Tunis",
	"tz_key_America/Anchorage": "(UTC-09:00) Amérique/Anchorage",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) Amérique/Argentine/Buenos Aires",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) Amérique/Argentine/Salta",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) Amérique/Argentine/San Luis",
	"tz_key_America/Asuncion": "(UTC-04:00) Amérique/Asunción",
	"tz_key_America/Atikokan": "(UTC-05:00) Amérique/Atikokan",
	"tz_key_America/Belem": "(UTC-03:00) Amérique/Belem",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) Amérique/Blanc-Sablon",
	"tz_key_America/Bogota": "(UTC-05:00) Amérique/Bogota",
	"tz_key_America/Campo_Grande": "(UTC-04:00) Amérique/Campo Grande",
	"tz_key_America/Caracas": "(UTC-04:30) Amérique/Caracas",
	"tz_key_America/Chicago": "(UTC-06:00) Amérique/Chicago",
	"tz_key_America/Costa_Rica": "(UTC-06:00) Amérique/Costa Rica",
	"tz_key_America/Dawson": "(UTC-08:00) Amérique/Dawson",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) Amérique/Dawson Creek",
	"tz_key_America/Denver": "(UTC-07:00) Amérique/Denver",
	"tz_key_America/Edmonton": "(UTC-07:00) Amérique/Edmonton",
	"tz_key_America/El_Salvador": "(UTC-06:00) Amérique/El Salvador",
	"tz_key_America/Guatemala": "(UTC-06:00) Amérique/Guatemala",
	"tz_key_America/Guayaquil": "(UTC-05:00) Amérique/Guayaquil",
	"tz_key_America/Halifax": "(UTC-04:00) Amérique/Halifax",
	"tz_key_America/Hermosillo": "(UTC-07:00) Amérique/Hermosillo",
	"tz_key_America/Iqaluit": "(UTC-05:00) Amérique/Iqaluit",
	"tz_key_America/Jamaica": "(UTC-05:00) Amérique/Jamaïque",
	"tz_key_America/La_Paz": "(UTC-04:00) Amérique/La Paz",
	"tz_key_America/Lima": "(UTC-05:00) Amérique/Lima",
	"tz_key_America/Los_Angeles": "(UTC-08:00) Amérique/Los Angeles",
	"tz_key_America/Managua": "(UTC-06:00) Amérique/Managua",
	"tz_key_America/Mazatlan": "(UTC-07:00) Amérique/Mazatlan",
	"tz_key_America/Mexico_City": "(UTC-06:00) Amérique/Mexico City",
	"tz_key_America/Montevideo": "(UTC-03:00) Amérique/Montevideo",
	"tz_key_America/Nassau": "(UTC-05:00) Amérique/Nassau",
	"tz_key_America/New_York": "(UTC-05:00) Amérique/New York",
	"tz_key_America/Noronha": "(UTC-02:00) Amérique/Noronha",
	"tz_key_America/Panama": "(UTC-05:00) Amérique/Panama",
	"tz_key_America/Phoenix": "(UTC-07:00) Amérique/Phoenix",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) Amérique/Port-d'Espagne",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) Amérique/Porto Rico",
	"tz_key_America/Rainy_River": "(UTC-06:00) Amérique/Rainy River",
	"tz_key_America/Regina": "(UTC-06:00) Amérique/Regina",
	"tz_key_America/Santiago": "(UTC-04:00) Amérique/Santiago",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) Amérique/Saint Domingue",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) Amérique/Sao Paulo",
	"tz_key_America/St_Johns": "(UTC-03:30) Amérique/St Johns",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) Amérique/Tegucigalpa",
	"tz_key_America/Tijuana": "(UTC-08:00) Amérique/Tijuana",
	"tz_key_America/Toronto": "(UTC-05:00) Amérique/Toronto",
	"tz_key_America/Vancouver": "(UTC-08:00) Amérique/Vancouver",
	"tz_key_Asia/Amman": "(UTC+02:00) Asie/Amman",
	"tz_key_Asia/Baghdad": "(UTC+03:00) Asie/Bagdad",
	"tz_key_Asia/Bahrain": "(UTC+03:00) Asie/Bahreïn",
	"tz_key_Asia/Bangkok": "(UTC+07:00) Asie/Bangkok",
	"tz_key_Asia/Beirut": "(UTC+02:00) Asie/Beyrouth",
	"tz_key_Asia/Colombo": "(UTC+05:30) Asie/Colombo",
	"tz_key_Asia/Dhaka": "(UTC+06:00) Asie/Dacca",
	"tz_key_Asia/Dubai": "(UTC+04:00) Asie/Dubai",
	"tz_key_Asia/Gaza": "(UTC+02:00) Asie/Gaza",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) Asie/Hô Chi Minh",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) Asie/Hong Kong",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) Asie/Irkoutsk",
	"tz_key_Asia/Jakarta": "(UTC+07:00) Asie/Djakarta",
	"tz_key_Asia/Jayapura": "(UTC+09:00) Asie/Jayapura",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) Asie/Jérusalem",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) Asie/Kamchatka",
	"tz_key_Asia/Karachi": "(UTC+05:00) Asie/Karachi",
	"tz_key_Asia/Kolkata": "(UTC+05:30) Asie/Calcutta",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) Asie/Krasnoïarsk",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) Asie/Kuala Lumpur",
	"tz_key_Asia/Kuwait": "(UTC+03:00) Asie/Koweït",
	"tz_key_Asia/Magadan": "(UTC+11:00) Asie/Magadan",
	"tz_key_Asia/Makassar": "(UTC+08:00) Asie/Macassar",
	"tz_key_Asia/Manila": "(UTC+08:00) Asie/Manille",
	"tz_key_Asia/Muscat": "(UTC+04:00) Asie/Muscat",
	"tz_key_Asia/Nicosia": "(UTC+02:00) Asie/Nicosie",
	"tz_key_Asia/Omsk": "(UTC+06:00) Asie/Omsk",
	"tz_key_Asia/Phnom_Penh": "Asie/Phnom Penh",
	"tz_key_Asia/Qatar": "(UTC+03:00) Asie/Qatar",
	"tz_key_Asia/Riyadh": "(UTC+03:00) Asie/Riyad",
	"tz_key_Asia/Seoul": "(UTC+09:00) Asie/Séoul",
	"tz_key_Asia/Shanghai": "(UTC+08:00) Asie/Beijing",
	"tz_key_Asia/Singapore": "(UTC+08:00) Asie/Singapour",
	"tz_key_Asia/Taipei": "(UTC+08:00) Asie/Taipei",
	"tz_key_Asia/Tokyo": "(UTC+09:00) Asie/Tokyo",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) Asie/Vladivostok",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) Asie/Iakoutsk",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) Asie/Iekaterinbourg",
	"tz_key_Atlantic/Azores": "(UTC-01:00) Atlantique/Açores",
	"tz_key_Atlantic/Canary": "(UTC+00:00) Atlantique/Canaries",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) Atlantique/Reykjavik",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) Australie/Broken Hill",
	"tz_key_Australia/Perth": "(UTC+08:00) Australie/Perth",
	"tz_key_Australia/Sydney": "(UTC+10:00) Australie/Sydney",
	"tz_key_Etc/GMT": "(UTC) Londres",
	"tz_key_Etc/GMT+1": "(UTC-01:00) Ponta Delgada",
	"tz_key_Etc/GMT+10": "(UTC-10:00) Heure de Honolulu Hawaï",
	"tz_key_Etc/GMT+11": "(UTC-11:00) Pago Pago",
	"tz_key_Etc/GMT+12": "(UTC-12:00) Majuro",
	"tz_key_Etc/GMT+2": "(UTC-02:00) Noronha",
	"tz_key_Etc/GMT+3": "(UTC-03:00) Sao Paulo",
	"tz_key_Etc/GMT+4": "(UTC-04:00) Caracas",
	"tz_key_Etc/GMT+5": "(UTC-05:00) New York Eastern Time",
	"tz_key_Etc/GMT+6": "(UTC-06:00) Mexico City Central Time",
	"tz_key_Etc/GMT+7": "(UTC-07:00) Salt Lake City Mountain Time",
	"tz_key_Etc/GMT+8": "(UTC-08:00) Los Angeles Pacific Time",
	"tz_key_Etc/GMT+9": "(UTC-09:00) Juno Alaska Time",
	"tz_key_Etc/GMT-1": "(UTC+01:00) Berlin",
	"tz_key_Etc/GMT-10": "(UTC+10:00) Canberra",
	"tz_key_Etc/GMT-11": "(UTC+11:00) Honiara",
	"tz_key_Etc/GMT-12": "(UTC+12:00) Wellington",
	"tz_key_Etc/GMT-13": "Pacifique/Apia",
	"tz_key_Etc/GMT-2": "(UTC+02:00) Athènes",
	"tz_key_Etc/GMT-3": "(UTC+03:00) Moscou",
	"tz_key_Etc/GMT-4": "(UTC+04:00) Abu Dhabi",
	"tz_key_Etc/GMT-5": "(UTC+05:00) Islamabad",
	"tz_key_Etc/GMT-6": "(UTC+06:00) Dacca",
	"tz_key_Etc/GMT-7": "(UTC+07:00) Bangkok",
	"tz_key_Etc/GMT-8": "(UTC+08:00) Beijing",
	"tz_key_Etc/GMT-9": "(UTC+09:00) Tokyo",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) Europe/Amsterdam",
	"tz_key_Europe/Athens": "(UTC+02:00) Europe/Athènes",
	"tz_key_Europe/Belgrade": "(UTC+01:00) Europe/Belgrade",
	"tz_key_Europe/Berlin": "(UTC+01:00) Europe/Berlin",
	"tz_key_Europe/Bratislava": "(UTC+01:00) Europe/Bratislava",
	"tz_key_Europe/Brussels": "(UTC+01:00) Europe/Bruxelles",
	"tz_key_Europe/Bucharest": "(UTC+02:00) Europe/Bucarest",
	"tz_key_Europe/Budapest": "(UTC+01:00) Europe/Budapest",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) Europe/Copenhague",
	"tz_key_Europe/Dublin": "(UTC+00:00) Europe/Dublin",
	"tz_key_Europe/Helsinki": "(UTC+02:00) Europe/Helsinki",
	"tz_key_Europe/Istanbul": "(UTC+02:00) Europe/Istanbul",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) Europe/Kaliningrad",
	"tz_key_Europe/Kiev": "(UTC+02:00) Europe/Kiev",
	"tz_key_Europe/Lisbon": "(UTC+00:00) Europe/Lisbonne",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) Europe/Ljubljana",
	"tz_key_Europe/London": "(UTC+00:00) Europe/Londres",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) Europe/Luxembourg",
	"tz_key_Europe/Madrid": "(UTC+01:00) Europe/Madrid",
	"tz_key_Europe/Malta": "(UTC+01:00) Europe/Malte",
	"tz_key_Europe/Moscow": "(UTC+03:00) Europe/Moscou",
	"tz_key_Europe/Oslo": "(UTC+01:00) Europe/Oslo",
	"tz_key_Europe/Paris": "(UTC+01:00) Europe/Paris",
	"tz_key_Europe/Prague": "(UTC+01:00) Europe/Prague",
	"tz_key_Europe/Riga": "(UTC+02:00) Europe/Riga",
	"tz_key_Europe/Rome": "(UTC+01:00) Europe/Rome",
	"tz_key_Europe/Samara": "(UTC+03:00) Europe/Samara",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) Europe/Sarajevo",
	"tz_key_Europe/Skopje": "(UTC+01:00) Europe/Skopje",
	"tz_key_Europe/Sofia": "(UTC+02:00) Europe/Sofia",
	"tz_key_Europe/Stockholm": "(UTC+01:00) Europe/Stockholm",
	"tz_key_Europe/Tallinn": "(UTC+02:00) Europe/Tallinn",
	"tz_key_Europe/Vienna": "(UTC+01:00) Europe/Vienne",
	"tz_key_Europe/Vilnius": "(UTC+02:00) Europe/Vilnius",
	"tz_key_Europe/Warsaw": "(UTC+01:00) Europe/Varsovie",
	"tz_key_Europe/Zagreb": "(UTC+01:00) Europe/Zagreb",
	"tz_key_Europe/Zurich": "(UTC+01:00) Europe/Zurich",
	"tz_key_Indian/Maldives": "(UTC+05:00) Indien/Maldives",
	"tz_key_Indian/Mauritius": "(UTC+04:00) Indien/Maurice",
	"tz_key_Pacific/Apia": "(UTC-11:00) Pacifique/Apia",
	"tz_key_Pacific/Auckland": "(UTC+12:00) Pacifique/Auckland",
	"tz_key_Pacific/Easter": "(UTC-06:00) Pacifique/Est",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) Pacifique/Galapagos",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) Pacifique/Honolulu",
	"tz_key_Pacific/Majuro": "(UTC-12:00) Pacifique/Majuro",
	"tz_key_Pacific/Midway": "(UTC-11:00) Pacifique/Midway",
	"tz_key_Pacific/Niue": "(UTC-11:00) Pacifique/Niue",
	"tz_key_Pacific/Pago Pago": "Pacifique/Pago Pago",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) Pacifique/Pago Pago"
};

var bc_account_num$9 = "Jumlah Akun: {num}";
var bc_avartar$9 = "Gambar Profil";
var bc_cancel$9 = "Batalkan";
var bc_create$9 = "Buat";
var bc_create_title$9 = "Buat Pusat Bisnis";
var bc_info$9 = "Mengelola akun iklan Anda, anggota pusat, dan status izin mereka di Pusat Bisnis. Multifungsi dan Gratis.";
var bc_member_num$9 = "Jumlah Anggota: {num}";
var bc_name$9 = "Nama Pusat Bisnis";
var bc_name_empty_hint$9 = "Silakan isi nama Pusat Bisnis.";
var bc_name_length_hint$9 = "Nama Pusat Bisnis tidak boleh melebihi 200 karakter.";
var bc_name_placeholder$9 = "Silakan isi nama Pusat Bisnis.";
var bc_timezone$9 = "Zona waktu";
var bc_timezone_empty_hint$9 = "Silakan pilih zona waktu.";
var bc_timezone_hint$9 = "Data zona waktu akan digunakan dalam analisis pascafrasa. Kami saat ini tidak mendukung fungsi terkait. Silakan pilih zona waktu untuk tampilan data umum di Pusat Bisnis.";
var bc_timezone_placeholder$9 = "Pilih zona waktu";
var bc_type_agency$9 = "Agen";
var id = {
	bc_account_num: bc_account_num$9,
	bc_avartar: bc_avartar$9,
	bc_cancel: bc_cancel$9,
	bc_create: bc_create$9,
	bc_create_title: bc_create_title$9,
	bc_info: bc_info$9,
	bc_member_num: bc_member_num$9,
	bc_name: bc_name$9,
	bc_name_empty_hint: bc_name_empty_hint$9,
	bc_name_length_hint: bc_name_length_hint$9,
	bc_name_placeholder: bc_name_placeholder$9,
	bc_timezone: bc_timezone$9,
	bc_timezone_empty_hint: bc_timezone_empty_hint$9,
	bc_timezone_hint: bc_timezone_hint$9,
	bc_timezone_placeholder: bc_timezone_placeholder$9,
	bc_type_agency: bc_type_agency$9,
	"tz_key_Africa/Accra": "(UTC+00.00) Afrika/Accra",
	"tz_key_Africa/Cairo": "(UTC+02.00) Afrika/Kairo",
	"tz_key_Africa/Casablanca": "(UTC+00.00) Afrika/Casablanca",
	"tz_key_Africa/Johannesburg": "(UTC+02.00) Afrika/Johannesburg",
	"tz_key_Africa/Lagos": "(UTC+01.00) Afrika/Lagos",
	"tz_key_Africa/Nairobi": "(UTC+03.00) Afrika/Nairobi",
	"tz_key_Africa/Tunis": "(UTC+01.00) Afrika/Tunis",
	"tz_key_America/Anchorage": "(UTC-09.00) Amerika/Anchorage",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03.00) Amerika/Argentina/Buenos_Aires",
	"tz_key_America/Argentina/Salta": "(UTC-03.00) Amerika/Argentina/Salta",
	"tz_key_America/Argentina/San_Luis": "(UTC-03.00) Amerika/Argentina/San_Luis",
	"tz_key_America/Asuncion": "(UTC-04.00) Amerika/Asuncion",
	"tz_key_America/Atikokan": "(UTC-05.00) Amerika/Atikokan",
	"tz_key_America/Belem": "(UTC-03.00) Amerika/Belem",
	"tz_key_America/Blanc-Sablon": "(UTC-04.00) Amerika/Blanc-Sablon",
	"tz_key_America/Bogota": "(UTC-05.00) Amerika/Bogota",
	"tz_key_America/Campo_Grande": "(UTC-04.00) Amerika/Campo_Grande",
	"tz_key_America/Caracas": "(UTC-04.30) Amerika/Caracas",
	"tz_key_America/Chicago": "(UTC-06.00) Amerika/Chicago",
	"tz_key_America/Costa_Rica": "(UTC-06.00) Amerika/Kosta_Rika",
	"tz_key_America/Dawson": "(UTC-08.00) Amerika/Dawson",
	"tz_key_America/Dawson_Creek": "(UTC-07.00) Amerika/Dawson_Creek",
	"tz_key_America/Denver": "(UTC-07.00) Amerika/Denver",
	"tz_key_America/Edmonton": "(UTC-07.00) Amerika/Edmonton",
	"tz_key_America/El_Salvador": "(UTC-06.00) Amerika/El_Salvador",
	"tz_key_America/Guatemala": "(UTC-06.00) Amerika/Guatemala",
	"tz_key_America/Guayaquil": "(UTC-05.00) Amerika/Guayaquil",
	"tz_key_America/Halifax": "(UTC-04.00) Amerika/Halifax",
	"tz_key_America/Hermosillo": "(UTC-07.00) Amerika/Hermosillo",
	"tz_key_America/Iqaluit": "(UTC-05.00) Amerika/Iqaluit",
	"tz_key_America/Jamaica": "(UTC-05.00) Amerika/Jamaika",
	"tz_key_America/La_Paz": "(UTC-04.00) Amerika/La_Paz",
	"tz_key_America/Lima": "(UTC-05.00) Amerika/Lima",
	"tz_key_America/Los_Angeles": "(UTC-08.00) Amerika/Los_Angeles",
	"tz_key_America/Managua": "(UTC-06.00) Amerika/Managua",
	"tz_key_America/Mazatlan": "(UTC-07.00) Amerika/Mazatlan",
	"tz_key_America/Mexico_City": "(UTC-06.00) Amerika/Kota_Meksiko",
	"tz_key_America/Montevideo": "(UTC-03.00) Amerika/Montevideo",
	"tz_key_America/Nassau": "(UTC-05.00) Amerika/Nassau",
	"tz_key_America/New_York": "(UTC-05.00) Amerika/New_York",
	"tz_key_America/Noronha": "(UTC-02.00) Amerika/Noronha",
	"tz_key_America/Panama": "(UTC-05.00) Amerika/Panama",
	"tz_key_America/Phoenix": "(UTC-07.00) Amerika/Phoenix",
	"tz_key_America/Port_of_Spain": "(UTC-04.00) Amerika/Port_of_Spain",
	"tz_key_America/Puerto_Rico": "(UTC-04.00) Amerika/Puerto_Riko",
	"tz_key_America/Rainy_River": "(UTC-06.00) Amerika/Rainy_River",
	"tz_key_America/Regina": "(UTC-06.00) Amerika/Regina",
	"tz_key_America/Santiago": "(UTC-04.00) Amerika/Santiago",
	"tz_key_America/Santo_Domingo": "(UTC-04.00) Amerika/Santo_Domingo",
	"tz_key_America/Sao_Paulo": "(UTC-03.00) Amerika/Sao_Paulo",
	"tz_key_America/St_Johns": "(UTC-03.30) Amerika/St_Johns",
	"tz_key_America/Tegucigalpa": "(UTC-06.00) Amerika/Tegucigalpa",
	"tz_key_America/Tijuana": "(UTC-08.00) Amerika/Tijuana",
	"tz_key_America/Toronto": "(UTC-05.00) Amerika/Toronto",
	"tz_key_America/Vancouver": "(UTC-08.00) Amerika/Vancouver",
	"tz_key_Asia/Amman": "(UTC+02.00) Asia/Amman",
	"tz_key_Asia/Baghdad": "(UTC+03.00) Asia/Baghdad",
	"tz_key_Asia/Bahrain": "(UTC+03.00) Asia/Bahrain",
	"tz_key_Asia/Bangkok": "(UTC+07.00) Asia/Bangkok",
	"tz_key_Asia/Beirut": "(UTC+02.00) Asia/Beirut",
	"tz_key_Asia/Colombo": "(UTC+05.30) Asia/Kolombo",
	"tz_key_Asia/Dhaka": "(UTC+06.00) Asia/Dhaka",
	"tz_key_Asia/Dubai": "(UTC+04.00) Asia/Dubai",
	"tz_key_Asia/Gaza": "(UTC+02.00) Asia/Gaza",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07.00) Asia/Ho_Chi_Minh",
	"tz_key_Asia/Hong_Kong": "(UTC+08.00) Asia/Hong_Kong",
	"tz_key_Asia/Irkutsk": "(UTC+08.00) Asia/Irkutsk",
	"tz_key_Asia/Jakarta": "(UTC+07.00) Asia/Jakarta",
	"tz_key_Asia/Jayapura": "(UTC+09.00) Asia/Jayapura",
	"tz_key_Asia/Jerusalem": "(UTC+02.00) Asia/Yerusalem",
	"tz_key_Asia/Kamchatka": "(UTC+11.00) Asia/Kamchatka",
	"tz_key_Asia/Karachi": "(UTC+05.00) Asia/Karachi",
	"tz_key_Asia/Kolkata": "(UTC+05.30) Asia/Kolkata",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07.00) Asia/Krasnoyarsk",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08.00) Asia/Kuala_Lumpur",
	"tz_key_Asia/Kuwait": "(UTC+03.00) Asia/Kuwait",
	"tz_key_Asia/Magadan": "(UTC+11.00) Asia/Magadan",
	"tz_key_Asia/Makassar": "(UTC+08.00) Asia/Makassar",
	"tz_key_Asia/Manila": "(UTC+08.00) Asia/Manila",
	"tz_key_Asia/Muscat": "(UTC+04.00) Asia/Muscat",
	"tz_key_Asia/Nicosia": "(UTC+02.00) Asia/Nicosia",
	"tz_key_Asia/Omsk": "(UTC+06.00) Asia/Omsk",
	"tz_key_Asia/Phnom_Penh": "Asia/Phnom Penh",
	"tz_key_Asia/Qatar": "(UTC+03.00) Asia/Qatar",
	"tz_key_Asia/Riyadh": "(UTC+03.00) Asia/Riyadh",
	"tz_key_Asia/Seoul": "(UTC+09.00) Asia/Seoul",
	"tz_key_Asia/Shanghai": "(UTC+08.00) Asia/Beijing",
	"tz_key_Asia/Singapore": "(UTC+08.00) Asia/Singapura",
	"tz_key_Asia/Taipei": "(UTC+08.00) Asia/Taipei",
	"tz_key_Asia/Tokyo": "(UTC+09.00) Asia/Tokyo",
	"tz_key_Asia/Vladivostok": "(UTC+10.00) Asia/Vladivostok",
	"tz_key_Asia/Yakutsk": "(UTC+09.00) Asia/Yakutsk",
	"tz_key_Asia/Yekaterinburg": "(UTC+05.00) Asia/Yekaterinburg",
	"tz_key_Atlantic/Azores": "(UTC-01.00) Atlantis/Azores",
	"tz_key_Atlantic/Canary": "(UTC+00.00) Atlantis/Canaria",
	"tz_key_Atlantic/Reykjavik": "(UTC+00.00) Atlantis/Reykjavik",
	"tz_key_Australia/Broken_Hill": "(UTC+09.30) Australia/Broken_Hill",
	"tz_key_Australia/Perth": "(UTC+08.00) Australia/Perth",
	"tz_key_Australia/Sydney": "(UTC+10.00) Australia/Sidney",
	"tz_key_Etc/GMT": "(UTC) London",
	"tz_key_Etc/GMT+1": "(UTC-01.00) Ponta Delgada",
	"tz_key_Etc/GMT+10": "(UTC-10.00) Honolulu_Waktu Hawaii",
	"tz_key_Etc/GMT+11": "(UTC-11.00) Pago Pago",
	"tz_key_Etc/GMT+12": "(UTC-12.00) Majuro",
	"tz_key_Etc/GMT+2": "(UTC-02.00) Noronha",
	"tz_key_Etc/GMT+3": "(UTC-03.00) Sao Paulo",
	"tz_key_Etc/GMT+4": "(UTC-04.00) Caracas",
	"tz_key_Etc/GMT+5": "(UTC-05.00) New York_Waktu Timur",
	"tz_key_Etc/GMT+6": "(UTC-06.00) Kota Meksiko_Waktu Tengah",
	"tz_key_Etc/GMT+7": "(UTC-07.00) Kota Salt Lake_Waktu Pegunungan",
	"tz_key_Etc/GMT+8": "(UTC-08.00) Los Angeles_Waktu Pasifik",
	"tz_key_Etc/GMT+9": "(UTC-09.00) Juno_Waktu Alaska",
	"tz_key_Etc/GMT-1": "(UTC+01.00) Berlin",
	"tz_key_Etc/GMT-10": "(UTC+10.00) Canberra",
	"tz_key_Etc/GMT-11": "(UTC+11.00) Honiara",
	"tz_key_Etc/GMT-12": "(UTC+12.00) Wellington",
	"tz_key_Etc/GMT-13": "Pasifik/Apia",
	"tz_key_Etc/GMT-2": "(UTC+02.00) Athena",
	"tz_key_Etc/GMT-3": "(UTC+03.00) Moskow",
	"tz_key_Etc/GMT-4": "(UTC+04.00) Abu Dhabi",
	"tz_key_Etc/GMT-5": "(UTC+05.00) Islamabad",
	"tz_key_Etc/GMT-6": "(UTC+06.00) Dhaka",
	"tz_key_Etc/GMT-7": "(UTC+07.00) Bangkok",
	"tz_key_Etc/GMT-8": "(UTC+08.00) Beijing",
	"tz_key_Etc/GMT-9": "(UTC+09.00) Tokyo",
	"tz_key_Europe/Amsterdam": "(UTC+01.00) Eropa/Amsterdam",
	"tz_key_Europe/Athens": "(UTC+02.00) Eropa/Athena",
	"tz_key_Europe/Belgrade": "(UTC+01.00) Eropa/Belgrade",
	"tz_key_Europe/Berlin": "(UTC+01.00) Eropa/Berlin",
	"tz_key_Europe/Bratislava": "(UTC+01.00) Eropa/Bratislava",
	"tz_key_Europe/Brussels": "(UTC+01.00) Eropa/Brussels",
	"tz_key_Europe/Bucharest": "(UTC+02.00) Eropa/Bucharest",
	"tz_key_Europe/Budapest": "(UTC+01.00) Eropa/Budapest",
	"tz_key_Europe/Copenhagen": "(UTC+01.00) Eropa/Kopenhagen",
	"tz_key_Europe/Dublin": "(UTC+00.00) Eropa/Dublin",
	"tz_key_Europe/Helsinki": "(UTC+02.00) Eropa/Helsinki",
	"tz_key_Europe/Istanbul": "(UTC+02.00) Eropa/Istanbul",
	"tz_key_Europe/Kaliningrad": "(UTC+02.00) Eropa/Kaliningrad",
	"tz_key_Europe/Kiev": "(UTC+02.00) Eropa/Kiev",
	"tz_key_Europe/Lisbon": "(UTC+00.00) Eropa/Lisbon",
	"tz_key_Europe/Ljubljana": "(UTC+01.00) Eropa/Ljubljana",
	"tz_key_Europe/London": "(UTC+00.00) Eropa/London",
	"tz_key_Europe/Luxembourg": "(UTC+01.00) Eropa/Luxembourg",
	"tz_key_Europe/Madrid": "(UTC+01.00) Eropa/Madrid",
	"tz_key_Europe/Malta": "(UTC+01.00) Eropa/Malta",
	"tz_key_Europe/Moscow": "(UTC+03.00) Eropa/Moskow",
	"tz_key_Europe/Oslo": "(UTC+01.00) Eropa/Oslo",
	"tz_key_Europe/Paris": "(UTC+01.00) Eropa/Paris",
	"tz_key_Europe/Prague": "(UTC+01.00) Eropa/Praha",
	"tz_key_Europe/Riga": "(UTC+02.00) Eropa/Riga",
	"tz_key_Europe/Rome": "(UTC+01.00) Eropa/Roma",
	"tz_key_Europe/Samara": "(UTC+03.00) Eropa/Samara",
	"tz_key_Europe/Sarajevo": "(UTC+01.00) Eropa/Sarajevo",
	"tz_key_Europe/Skopje": "(UTC+01.00) Eropa/Skopje",
	"tz_key_Europe/Sofia": "(UTC+02.00) Eropa/Sofia",
	"tz_key_Europe/Stockholm": "(UTC+01.00) Eropa/Stockholm",
	"tz_key_Europe/Tallinn": "(UTC+02.00) Eropa/Tallinn",
	"tz_key_Europe/Vienna": "(UTC+01.00) Eropa/Vienna",
	"tz_key_Europe/Vilnius": "(UTC+02.00) Eropa/Vilnius",
	"tz_key_Europe/Warsaw": "(UTC+01.00) Eropa/Warsaw",
	"tz_key_Europe/Zagreb": "(UTC+01.00) Eropa/Zagreb",
	"tz_key_Europe/Zurich": "(UTC+01.00) Eropa/Zurich",
	"tz_key_Indian/Maldives": "(UTC+05.00) Hindia/Maldives",
	"tz_key_Indian/Mauritius": "(UTC+04.00) Hindia/Mauritius",
	"tz_key_Pacific/Apia": "(UTC-11.00) Pasifik/Apia",
	"tz_key_Pacific/Auckland": "(UTC+12.00) Pasifik/Auckland",
	"tz_key_Pacific/Easter": "(UTC-06.00) Pasifik/Easter",
	"tz_key_Pacific/Galapagos": "(UTC-06.00) Pasifik/Galapagos",
	"tz_key_Pacific/Honolulu": "(UTC-10.00) Pasifik/Honolulu",
	"tz_key_Pacific/Majuro": "(UTC-12.00) Pasifik/Majuro",
	"tz_key_Pacific/Midway": "(UTC-11.00) Pasifik/Midway",
	"tz_key_Pacific/Niue": "(UTC-11.00) Pasifik/Niue",
	"tz_key_Pacific/Pago Pago": "Pasifik/Pago Pago",
	"tz_key_Pacific/Pago_Pago": "(UTC-11.00) Pasifik/Pago Pago"
};

var bc_account_num$8 = "Conteggio account: {num}";
var bc_avartar$8 = "Immagine del profilo";
var bc_cancel$8 = "Annulla";
var bc_create$8 = "Crea";
var bc_create_title$8 = "Crea centro di attività";
var bc_info$8 = "Gestire gli account annunci, i membri del centro e lo stato delle loro autorizzazioni nel centro di attività. Tutto compreso e gratuito.";
var bc_member_num$8 = "Conteggio membro: {num}";
var bc_name$8 = "Nome del centro di attività";
var bc_name_empty_hint$8 = "Inserisci il nome del centro di attività.";
var bc_name_length_hint$8 = "Il nome del centro di attività non può superare i 200 caratteri.";
var bc_name_placeholder$8 = "Inserisci il nome del centro di attività.";
var bc_timezone$8 = "Fuso orario";
var bc_timezone_empty_hint$8 = "Seleziona il fuso orario.";
var bc_timezone_hint$8 = "I dati del fuso orario sarebbero utilizzati nell'analisi post-fase. Al momento non sono supportate le funzioni correlate. Seleziona il fuso orario per la visualizzazione dei dati generali nel centro di attività.";
var bc_timezone_placeholder$8 = "Seleziona fuso orario";
var bc_type_agency$8 = "Agenzia";
var it = {
	bc_account_num: bc_account_num$8,
	bc_avartar: bc_avartar$8,
	bc_cancel: bc_cancel$8,
	bc_create: bc_create$8,
	bc_create_title: bc_create_title$8,
	bc_info: bc_info$8,
	bc_member_num: bc_member_num$8,
	bc_name: bc_name$8,
	bc_name_empty_hint: bc_name_empty_hint$8,
	bc_name_length_hint: bc_name_length_hint$8,
	bc_name_placeholder: bc_name_placeholder$8,
	bc_timezone: bc_timezone$8,
	bc_timezone_empty_hint: bc_timezone_empty_hint$8,
	bc_timezone_hint: bc_timezone_hint$8,
	bc_timezone_placeholder: bc_timezone_placeholder$8,
	bc_type_agency: bc_type_agency$8,
	"tz_key_Africa/Accra": "(UTC+00:00) Africa/Accra",
	"tz_key_Africa/Cairo": "(UTC+02:00) Africa/Cairo",
	"tz_key_Africa/Casablanca": "(UTC+00:00) Africa/Casablanca",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) Africa/Johannesburg",
	"tz_key_Africa/Lagos": "(UTC+01:00) Africa/Lagos",
	"tz_key_Africa/Nairobi": "(UTC+03:00) Africa/Nairobi",
	"tz_key_Africa/Tunis": "(UTC+01:00) Africa/Tunisi",
	"tz_key_America/Anchorage": "(UTC-09:00) America/Anchorage",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) America/Argentina/Buenos_Aires",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) America/Argentina/Salta",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) America/Argentina/San_Luis",
	"tz_key_America/Asuncion": "(UTC-04:00) America/Assunzione",
	"tz_key_America/Atikokan": "(UTC-05:00) America/Atikokan",
	"tz_key_America/Belem": "(UTC-03:00) America/Belem",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) America/Blanc-Sablon",
	"tz_key_America/Bogota": "(UTC-05:00) America/Bogotà",
	"tz_key_America/Campo_Grande": "(UTC-04:00) America/Campo_Grande",
	"tz_key_America/Caracas": "(UTC-04:30) America/Caracas",
	"tz_key_America/Chicago": "(UTC-06:00) America/Chicago",
	"tz_key_America/Costa_Rica": "(UTC-06:00) America/Costa_Rica",
	"tz_key_America/Dawson": "(UTC-08:00) America/Dawson",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) America/Dawson_Creek",
	"tz_key_America/Denver": "(UTC-07:00) America/Denver",
	"tz_key_America/Edmonton": "(UTC-07:00) America/Edmonton",
	"tz_key_America/El_Salvador": "(UTC-06:00) America/El_Salvador",
	"tz_key_America/Guatemala": "(UTC-06:00) America/Guatemala",
	"tz_key_America/Guayaquil": "(UTC-05:00) America/Guayaquil",
	"tz_key_America/Halifax": "(UTC-04:00) America/Halifax",
	"tz_key_America/Hermosillo": "(UTC-07:00) America/Hermosillo",
	"tz_key_America/Iqaluit": "(UTC-05:00) America/Iqaluit",
	"tz_key_America/Jamaica": "(UTC-05:00) America/Giamaica",
	"tz_key_America/La_Paz": "(UTC-04:00) America/La_Paz",
	"tz_key_America/Lima": "(UTC-05:00) America/Lima",
	"tz_key_America/Los_Angeles": "(UTC-08:00) America/Los_Angeles",
	"tz_key_America/Managua": "(UTC-06:00) America/Managua",
	"tz_key_America/Mazatlan": "(UTC-07:00) America/Mazatlan",
	"tz_key_America/Mexico_City": "(UTC-06:00) America/Città_del_Messico",
	"tz_key_America/Montevideo": "(UTC-03:00) America/Montevideo",
	"tz_key_America/Nassau": "(UTC-05:00) America/Nassau",
	"tz_key_America/New_York": "(UTC-05:00) America/New_York",
	"tz_key_America/Noronha": "(UTC-02:00) America/Noronha",
	"tz_key_America/Panama": "(UTC-05:00) America/Panama",
	"tz_key_America/Phoenix": "(UTC-07:00) America/Phoenix",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) America/Porto_di_Spagna",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) America/Porto_Rico",
	"tz_key_America/Rainy_River": "(UTC-06:00) America/Rainy_River",
	"tz_key_America/Regina": "(UTC-06:00) America/Regina",
	"tz_key_America/Santiago": "(UTC-04:00) America/Santiago",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) America/Santo_Domingo",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) America/San_Paolo",
	"tz_key_America/St_Johns": "(UTC-03:30) America/St_Johns",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) America/Tegucigalpa",
	"tz_key_America/Tijuana": "(UTC-08:00) America/Tijuana",
	"tz_key_America/Toronto": "(UTC-05:00) America/Toronto",
	"tz_key_America/Vancouver": "(UTC-08:00) America/Vancouver",
	"tz_key_Asia/Amman": "(UTC+02:00) Asia/Amman",
	"tz_key_Asia/Baghdad": "(UTC+03:00) Asia/Baghdad",
	"tz_key_Asia/Bahrain": "(UTC+03:00) Asia/Bahrain",
	"tz_key_Asia/Bangkok": "(UTC+07:00) Asia/Bangkok",
	"tz_key_Asia/Beirut": "(UTC+02:00) Asia/Beirut",
	"tz_key_Asia/Colombo": "(UTC+05:30) Asia/Colombo",
	"tz_key_Asia/Dhaka": "(UTC+06:00) Asia/Dacca",
	"tz_key_Asia/Dubai": "(UTC+04:00) Asia/Dubai",
	"tz_key_Asia/Gaza": "(UTC+02:00) Asia/Gaza",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) Asia/Ho_Chi_Minh",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) Asia/Hong_Kong",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) Asia/Irkutsk",
	"tz_key_Asia/Jakarta": "(UTC+07:00) Asia/Giacarta",
	"tz_key_Asia/Jayapura": "(UTC+09:00) Asia/Jayapura",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) Asia/Gerusalemme",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) Asia/Kamchatka",
	"tz_key_Asia/Karachi": "(UTC+05:00) Asia/Caraci",
	"tz_key_Asia/Kolkata": "(UTC+05:30) Asia/Calcutta",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) Asia/Krasnoyarsk",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) Asia/Kuala_Lumpur",
	"tz_key_Asia/Kuwait": "(UTC+03:00) Asia/Kuwait",
	"tz_key_Asia/Magadan": "(UTC+11:00) Asia/Magadan",
	"tz_key_Asia/Makassar": "(UTC+08:00) Asia/Makassar",
	"tz_key_Asia/Manila": "(UTC+08:00) Asia/Manila",
	"tz_key_Asia/Muscat": "(UTC+04:00) Asia/Mascate",
	"tz_key_Asia/Nicosia": "(UTC+02:00) Asia/Nicosia",
	"tz_key_Asia/Omsk": "(UTC+06:00) Asia/Omsk",
	"tz_key_Asia/Phnom_Penh": "Asia/Phnom Penh",
	"tz_key_Asia/Qatar": "(UTC+03:00) Asia/Qatar",
	"tz_key_Asia/Riyadh": "(UTC+03:00) Asia/Riyadh",
	"tz_key_Asia/Seoul": "(UTC+09:00) Asia/Seoul",
	"tz_key_Asia/Shanghai": "(UTC+08:00) Asia/Pechino",
	"tz_key_Asia/Singapore": "(UTC+08:00) Asia/Singapore",
	"tz_key_Asia/Taipei": "(UTC+08:00) Asia/Taipei",
	"tz_key_Asia/Tokyo": "(UTC+09:00) Asia/Tokyo",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) Asia/Vladivostok",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) Asia/Jakutsk",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) Asia/Ekaterinburg",
	"tz_key_Atlantic/Azores": "(UTC-01:00) Atlantico/Azzorre",
	"tz_key_Atlantic/Canary": "(UTC+00:00) Atlantico/Canarie",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) Atlantico/Reykjavik",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) Australia/Broken_Hill",
	"tz_key_Australia/Perth": "(UTC+08:00) Australia/Perth",
	"tz_key_Australia/Sydney": "(UTC+10:00) Australia/Sydney",
	"tz_key_Etc/GMT": "(UTC) Londra",
	"tz_key_Etc/GMT+1": "(UTC-01:00) Ponta Delgada",
	"tz_key_Etc/GMT+10": "(UTC-10:00) Honolulu_Hawaii Time",
	"tz_key_Etc/GMT+11": "(UTC-11:00) Pago Pago",
	"tz_key_Etc/GMT+12": "(UTC-12:00) Majuro",
	"tz_key_Etc/GMT+2": "(UTC-02:00) Noronha",
	"tz_key_Etc/GMT+3": "(UTC-03:00) San Paolo",
	"tz_key_Etc/GMT+4": "(UTC-04:00) Caracas",
	"tz_key_Etc/GMT+5": "(UTC-05:00) New York_Eastern Time",
	"tz_key_Etc/GMT+6": "(UTC-06:00) Città_del_Messico_Central Time",
	"tz_key_Etc/GMT+7": "(UTC-07:00) Salt Lake City_Mountain Time",
	"tz_key_Etc/GMT+8": "(UTC-08:00) Los Angeles_Pacific Time",
	"tz_key_Etc/GMT+9": "(UTC-09:00) Juno_Alaska Time",
	"tz_key_Etc/GMT-1": "(UTC+01:00) Berlino",
	"tz_key_Etc/GMT-10": "(UTC+10:00) Canberra",
	"tz_key_Etc/GMT-11": "(UTC+11:00) Honiara",
	"tz_key_Etc/GMT-12": "(UTC+12:00) Wellington",
	"tz_key_Etc/GMT-13": "Pacifico/Apia",
	"tz_key_Etc/GMT-2": "(UTC+02:00) Atene",
	"tz_key_Etc/GMT-3": "(UTC+03:00) Mosca",
	"tz_key_Etc/GMT-4": "(UTC+04:00) Abu Dhabi",
	"tz_key_Etc/GMT-5": "(UTC+05:00) Islamabad",
	"tz_key_Etc/GMT-6": "(UTC+06:00) Dacca",
	"tz_key_Etc/GMT-7": "(UTC+07:00) Bangkok",
	"tz_key_Etc/GMT-8": "(UTC+08:00) Pechino",
	"tz_key_Etc/GMT-9": "(UTC+09:00) Tokyo",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) Europa/Amsterdam",
	"tz_key_Europe/Athens": "(UTC+02:00) Europa/Atene",
	"tz_key_Europe/Belgrade": "(UTC+01:00) Europa/Belgrado",
	"tz_key_Europe/Berlin": "(UTC+01:00) Europa/Berlino",
	"tz_key_Europe/Bratislava": "(UTC+01:00) Europa/Bratislava",
	"tz_key_Europe/Brussels": "(UTC+01:00) Europa/Bruxelles",
	"tz_key_Europe/Bucharest": "(UTC+02:00) Europa/Bucarest",
	"tz_key_Europe/Budapest": "(UTC+01:00) Europa/Budapest",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) Europa/Copenhagen",
	"tz_key_Europe/Dublin": "(UTC+00:00) Europa/Dublino",
	"tz_key_Europe/Helsinki": "(UTC+02:00) Europa/Helsinki",
	"tz_key_Europe/Istanbul": "(UTC+02:00) Europa/Istanbul",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) Europa/Kaliningrad",
	"tz_key_Europe/Kiev": "(UTC+02:00) Europa/Kiev",
	"tz_key_Europe/Lisbon": "(UTC+00:00) Europa/Lisbona",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) Europa/Lubiana",
	"tz_key_Europe/London": "(UTC+00:00) Europa/Londra",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) Europa/Lussemburgo",
	"tz_key_Europe/Madrid": "(UTC+01:00) Europa/Madrid",
	"tz_key_Europe/Malta": "(UTC+01:00) Europa/Malta",
	"tz_key_Europe/Moscow": "(UTC+03:00) Europa/Mosca",
	"tz_key_Europe/Oslo": "(UTC+01:00) Europa/Oslo",
	"tz_key_Europe/Paris": "(UTC+01:00) Europa/Parigi",
	"tz_key_Europe/Prague": "(UTC+01:00) Europa/Praga",
	"tz_key_Europe/Riga": "(UTC+02:00) Europa/Riga",
	"tz_key_Europe/Rome": "(UTC+01:00) Europa/Roma",
	"tz_key_Europe/Samara": "(UTC+03:00) Europa/Samara",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) Europa/Sarajevo",
	"tz_key_Europe/Skopje": "(UTC+01:00) Europa/Skopje",
	"tz_key_Europe/Sofia": "(UTC+02:00) Europa/Sofia",
	"tz_key_Europe/Stockholm": "(UTC+01:00) Europa/Stoccolma",
	"tz_key_Europe/Tallinn": "(UTC+02:00) Europa/Tallinn",
	"tz_key_Europe/Vienna": "(UTC+01:00) Europa/Vienna",
	"tz_key_Europe/Vilnius": "(UTC+02:00) Europa/Vilnius",
	"tz_key_Europe/Warsaw": "(UTC+01:00) Europa/Varsavia",
	"tz_key_Europe/Zagreb": "(UTC+01:00) Europa/Zagabria",
	"tz_key_Europe/Zurich": "(UTC+01:00) Europa/Zurigo",
	"tz_key_Indian/Maldives": "(UTC+05:00) Indiano/Maldive",
	"tz_key_Indian/Mauritius": "(UTC+04:00) Indiano/Mauritius",
	"tz_key_Pacific/Apia": "(UTC-11:00) Pacifico/Apia",
	"tz_key_Pacific/Auckland": "(UTC+12:00) Pacifico/Auckland",
	"tz_key_Pacific/Easter": "(UTC-06:00) Pacifico/Pasqua",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) Pacifico/Galapagos",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) Pacifico/Honolulu",
	"tz_key_Pacific/Majuro": "(UTC-12:00) Pacifico/Majuro",
	"tz_key_Pacific/Midway": "(UTC-11:00) Pacifico/Midway",
	"tz_key_Pacific/Niue": "(UTC-11:00) Pacifico/Niue",
	"tz_key_Pacific/Pago Pago": "Pacifico/Pago Pago",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) Pacifico/Pago Pago"
};

var bc_account_num$7 = "アカウント数：{num}";
var bc_avartar$7 = "プロフィール画像";
var bc_cancel$7 = "キャンセル";
var bc_create$7 = "作成";
var bc_create_title$7 = "ビジネスセンターを作成";
var bc_info$7 = "ビジネスセンターは、広告アカウント、メンバーおよび権限の管理がワンストップでできる無料のサービスです。";
var bc_member_num$7 = "メンバー数：{num}";
var bc_name$7 = "ビジネスセンター名";
var bc_name_empty_hint$7 = "ビジネスセンター名を入力してください。";
var bc_name_length_hint$7 = "ビジネスセンター名は半角200文字以内で入力してください。";
var bc_name_placeholder$7 = "ビジネスセンター名を入力してください。";
var bc_timezone$7 = "タイムゾーン";
var bc_timezone_empty_hint$7 = "タイムゾーンを選択してください。";
var bc_timezone_hint$7 = "選択されたタイムゾーンは広告のデータ分析の際に使用されます。現在、その他の関連機能に対応していません。このビジネスセンターの概要の表示に最適なタイムゾーンを選択してください。";
var bc_timezone_placeholder$7 = "タイムゾーンを選択";
var bc_type_agency$7 = "代理店";
var ja = {
	bc_account_num: bc_account_num$7,
	bc_avartar: bc_avartar$7,
	bc_cancel: bc_cancel$7,
	bc_create: bc_create$7,
	bc_create_title: bc_create_title$7,
	bc_info: bc_info$7,
	bc_member_num: bc_member_num$7,
	bc_name: bc_name$7,
	bc_name_empty_hint: bc_name_empty_hint$7,
	bc_name_length_hint: bc_name_length_hint$7,
	bc_name_placeholder: bc_name_placeholder$7,
	bc_timezone: bc_timezone$7,
	bc_timezone_empty_hint: bc_timezone_empty_hint$7,
	bc_timezone_hint: bc_timezone_hint$7,
	bc_timezone_placeholder: bc_timezone_placeholder$7,
	bc_type_agency: bc_type_agency$7,
	"tz_key_Africa/Accra": "(UTC+00:00) アフリカ/アクラ",
	"tz_key_Africa/Cairo": "(UTC+02:00) アフリカ/カイロ",
	"tz_key_Africa/Casablanca": "(UTC+00:00) アフリカ/カサブランカ",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) アフリカ/ヨハネスブルグ",
	"tz_key_Africa/Lagos": "(UTC+01:00) アフリカ/ラゴス",
	"tz_key_Africa/Nairobi": "(UTC+03:00) アフリカ/ナイロビ",
	"tz_key_Africa/Tunis": "(UTC+01:00) アフリカ/チュニス",
	"tz_key_America/Anchorage": "(UTC-09:00) アメリカ/アンカレッジ",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) アメリカ/アルゼンチン",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) アメリカ/アルゼンチン",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) アメリカ/アルゼンチン",
	"tz_key_America/Asuncion": "(UTC-04:00) アメリカ/アスンシオン",
	"tz_key_America/Atikokan": "(UTC-05:00) アメリカ/アチコカン",
	"tz_key_America/Belem": "(UTC-03:00) アメリカ/ベレン",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) アメリカ/ブランサブロン",
	"tz_key_America/Bogota": "(UTC-05:00) アメリカ/ボゴタ",
	"tz_key_America/Campo_Grande": "(UTC-04:00) アメリカ/カンポグランデ",
	"tz_key_America/Caracas": "(UTC-04:30) アメリカ/カラカス",
	"tz_key_America/Chicago": "(UTC-06:00) アメリカ/シカゴ",
	"tz_key_America/Costa_Rica": "(UTC-06:00) アメリカ/コスタリカ",
	"tz_key_America/Dawson": "(UTC-08:00) アメリカ/ドーソン",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) アメリカ/ドーソンクリーク",
	"tz_key_America/Denver": "(UTC-07:00) アメリカ/デンバー",
	"tz_key_America/Edmonton": "(UTC-07:00) アメリカ/エドモントン",
	"tz_key_America/El_Salvador": "(UTC-06:00) アメリカ/エルサルバドル",
	"tz_key_America/Guatemala": "(UTC-06:00) アメリカ/グアテマラ",
	"tz_key_America/Guayaquil": "(UTC-05:00) アメリカ/グアヤキル",
	"tz_key_America/Halifax": "(UTC-04:00) アメリカ/ハリファックス",
	"tz_key_America/Hermosillo": "(UTC-07:00) アメリカ/エルモシヨ",
	"tz_key_America/Iqaluit": "(UTC-05:00) アメリカ/イカルイト",
	"tz_key_America/Jamaica": "(UTC-05:00) アメリカ/ジャマイカ",
	"tz_key_America/La_Paz": "(UTC-04:00) アメリカ/ラパス",
	"tz_key_America/Lima": "(UTC-05:00) アメリカ/リマ",
	"tz_key_America/Los_Angeles": "(UTC-08:00) アメリカ/ロサンゼルス",
	"tz_key_America/Managua": "(UTC-06:00) アメリカ/マナグア",
	"tz_key_America/Mazatlan": "(UTC-07:00) アメリカ/マサトラン",
	"tz_key_America/Mexico_City": "(UTC-06:00) アメリカ/メキシコシティ",
	"tz_key_America/Montevideo": "(UTC-03:00) アメリカ/モンテビデオ",
	"tz_key_America/Nassau": "(UTC-05:00) アメリカ/ナッソー",
	"tz_key_America/New_York": "(UTC-05:00) アメリカ/ニューヨーク",
	"tz_key_America/Noronha": "(UTC-02:00) アメリカ/ノローニャ",
	"tz_key_America/Panama": "(UTC-05:00) アメリカ/パナマ",
	"tz_key_America/Phoenix": "(UTC-07:00) アメリカ/フェニックス",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) アメリカ/スペイン港",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) アメリカ/プエルトリコ",
	"tz_key_America/Rainy_River": "(UTC-06:00) アメリカ/レニーリバー",
	"tz_key_America/Regina": "(UTC-06:00) アメリカ/レジーナ",
	"tz_key_America/Santiago": "(UTC-04:00) アメリカ/サンティアゴ",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) アメリカ/サントドミンゴ",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) アメリカ/サンパウロ",
	"tz_key_America/St_Johns": "(UTC-03:30) アメリカ/セントジョンズ",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) アメリカ/テグシガルパ",
	"tz_key_America/Tijuana": "(UTC-08:00) アメリカ/ティファナ",
	"tz_key_America/Toronto": "(UTC-05:00) アメリカ/トロント",
	"tz_key_America/Vancouver": "(UTC-08:00) アメリカ/バンクーバー",
	"tz_key_Asia/Amman": "(UTC+02:00) アジア/アンマン",
	"tz_key_Asia/Baghdad": "(UTC+03:00) アジア/バグダッド",
	"tz_key_Asia/Bahrain": "(UTC+03:00) アジア/バーレーン",
	"tz_key_Asia/Bangkok": "(UTC+07:00) アジア/バンコク",
	"tz_key_Asia/Beirut": "(UTC+02:00) アジア/ベイルート",
	"tz_key_Asia/Colombo": "(UTC+05:30) アジア/コロンボ",
	"tz_key_Asia/Dhaka": "(UTC+06:00) アジア/ダッカ",
	"tz_key_Asia/Dubai": "(UTC+04:00) アジア/ドバイ",
	"tz_key_Asia/Gaza": "(UTC+02:00) アジア/ガザ",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) アジア/ホーチミン市",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) アジア/香港",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) アジア/イルクーツク",
	"tz_key_Asia/Jakarta": "(UTC+07:00) アジア/ジャカルタ",
	"tz_key_Asia/Jayapura": "(UTC+09:00) アジア/ジャヤプラ",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) アジア/エルサレム",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) アジア/カムチャツカ",
	"tz_key_Asia/Karachi": "(UTC+05:00) アジア/カラチ",
	"tz_key_Asia/Kolkata": "(UTC+05:30) アジア/コルカタ",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) アジア/クラスノヤルスク",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) アジア/クアラルンプール",
	"tz_key_Asia/Kuwait": "(UTC+03:00) アジア/クウェート",
	"tz_key_Asia/Magadan": "(UTC+11:00) アジア/マガダン",
	"tz_key_Asia/Makassar": "(UTC+08:00) アジア/マカッサル",
	"tz_key_Asia/Manila": "(UTC+08:00) アジア/マニラ",
	"tz_key_Asia/Muscat": "(UTC+04:00) アジア/マスカット",
	"tz_key_Asia/Nicosia": "(UTC+02:00) アジア/ニコシア",
	"tz_key_Asia/Omsk": "(UTC+06:00) アジア/オムスク",
	"tz_key_Asia/Phnom_Penh": "アジア/プノンペン",
	"tz_key_Asia/Qatar": "(UTC+03:00) アジア/カタール",
	"tz_key_Asia/Riyadh": "(UTC+03:00) アジア/リヤド",
	"tz_key_Asia/Seoul": "(UTC+09:00) アジア/ソウル",
	"tz_key_Asia/Shanghai": "(UTC+08:00) アジア/北京",
	"tz_key_Asia/Singapore": "(UTC+08:00) アジア/シンガポール",
	"tz_key_Asia/Taipei": "(UTC+08:00) アジア/台北",
	"tz_key_Asia/Tokyo": "(UTC+09:00) アジア/東京",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) アジア/ウラジオストク",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) アジア/ヤクーツク",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) アジア/エカテリンブルク",
	"tz_key_Atlantic/Azores": "(UTC-01:00) 大西洋/アゾレス",
	"tz_key_Atlantic/Canary": "(UTC+00:00) 大西洋/カナリア",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) 大西洋/レイキャビク",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) オーストラリア/ブロークンヒル",
	"tz_key_Australia/Perth": "(UTC+08:00) オーストラリア/パース",
	"tz_key_Australia/Sydney": "(UTC+10:00) オーストラリア/シドニー",
	"tz_key_Etc/GMT": "(UTC+00:00)ヨーロッパ/ロンドン",
	"tz_key_Etc/GMT+0": "(UTC+00:00)ヨーロッパ/ロンドン",
	"tz_key_Etc/GMT+1": "(UTC-01:00)大西洋/アゾレス",
	"tz_key_Etc/GMT+10": "(UTC-10:00)太平洋/ホノルル",
	"tz_key_Etc/GMT+11": "(UTC-11:00)太平洋/ミッドウェー島",
	"tz_key_Etc/GMT+12": "(UTC-12:00)太平洋/マジュロ",
	"tz_key_Etc/GMT+2": "(UTC-02:00)アメリカ/ノローニャ",
	"tz_key_Etc/GMT+3": "(UTC-03:00)アメリカ/サンパウロ",
	"tz_key_Etc/GMT+4": "(UTC-04:00)アメリカ/サンティアゴ",
	"tz_key_Etc/GMT+5": "(UTC-05:00)アメリカ/ニューヨーク",
	"tz_key_Etc/GMT+6": "(UTC-06:00)アメリカ/メキシコシティ",
	"tz_key_Etc/GMT+7": "(UTC-07:00)アメリカ/デンバー",
	"tz_key_Etc/GMT+8": "(UTC-08:00)アメリカ/ロサンゼルス",
	"tz_key_Etc/GMT+9": "(UTC-09:00)アメリカ/アンカレッジ",
	"tz_key_Etc/GMT-1": "(UTC+01:00)ヨーロッパ/ベルリン",
	"tz_key_Etc/GMT-10": "(UTC+10:00)オーストラリア/シドニー",
	"tz_key_Etc/GMT-11": "(UTC+11:00)アジア/マガダン",
	"tz_key_Etc/GMT-12": "(UTC+12:00)太平洋/オークランド",
	"tz_key_Etc/GMT-13": "太平洋/アピア",
	"tz_key_Etc/GMT-2": "(UTC+02:00)ヨーロッパ/アテネ",
	"tz_key_Etc/GMT-3": "(UTC+03:00)ヨーロッパ/モスクワ",
	"tz_key_Etc/GMT-4": "(UTC+04:00)アジア/ドバイ",
	"tz_key_Etc/GMT-5": "(UTC+05:00)アジア/カラチ",
	"tz_key_Etc/GMT-6": "(UTC+06:00)アジア/ダッカ",
	"tz_key_Etc/GMT-7": "(UTC+07:00)アジア/バンコク",
	"tz_key_Etc/GMT-8": "(UTC+08:00)アジア/北京",
	"tz_key_Etc/GMT-9": "(UTC+09:00)アジア/東京",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) ヨーロッパ/アムステルダム",
	"tz_key_Europe/Athens": "(UTC+02:00) ヨーロッパ/アテネ",
	"tz_key_Europe/Belgrade": "(UTC+01:00) ヨーロッパ/ベオグラード",
	"tz_key_Europe/Berlin": "(UTC+01:00) ヨーロッパ/ベルリン",
	"tz_key_Europe/Bratislava": "(UTC+01:00) ヨーロッパ/ブラチスラバ",
	"tz_key_Europe/Brussels": "(UTC+01:00) ヨーロッパ/ブリュッセル",
	"tz_key_Europe/Bucharest": "(UTC+02:00) ヨーロッパ/ブカレスト",
	"tz_key_Europe/Budapest": "(UTC+01:00) ヨーロッパ/ブダペスト",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) ヨーロッパ/コペンハーゲン",
	"tz_key_Europe/Dublin": "(UTC+00:00) ヨーロッパ/ダブリン",
	"tz_key_Europe/Helsinki": "(UTC+02:00) ヨーロッパ/ヘルシンキ",
	"tz_key_Europe/Istanbul": "(UTC+02:00) ヨーロッパ/イスタンブール",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) ヨーロッパ/カリーニングラード",
	"tz_key_Europe/Kiev": "(UTC+02:00) ヨーロッパ/キエフ",
	"tz_key_Europe/Lisbon": "(UTC+00:00) ヨーロッパ/リスボン",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) ヨーロッパ/リュブリャナ",
	"tz_key_Europe/London": "(UTC+00:00) ヨーロッパ/ロンドン",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) ヨーロッパ/ルクセンブルク",
	"tz_key_Europe/Madrid": "(UTC+01:00) ヨーロッパ/マドリッド",
	"tz_key_Europe/Malta": "(UTC+01:00) ヨーロッパ/マルタ",
	"tz_key_Europe/Moscow": "(UTC+03:00) ヨーロッパ/モスクワ",
	"tz_key_Europe/Oslo": "(UTC+01:00) ヨーロッパ/オスロ",
	"tz_key_Europe/Paris": "(UTC+01:00) ヨーロッパ/パリ",
	"tz_key_Europe/Prague": "(UTC+01:00) ヨーロッパ/プラハ",
	"tz_key_Europe/Riga": "(UTC+02:00) ヨーロッパ/リガ",
	"tz_key_Europe/Rome": "(UTC+01:00) ヨーロッパ/ローマ",
	"tz_key_Europe/Samara": "(UTC+03:00) ヨーロッパ/サマラ",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) ヨーロッパ/サラエボ",
	"tz_key_Europe/Skopje": "(UTC+01:00) ヨーロッパ/スコピエ",
	"tz_key_Europe/Sofia": "(UTC+02:00) ヨーロッパ/ソフィア",
	"tz_key_Europe/Stockholm": "(UTC+01:00) ヨーロッパ/ストックホルム",
	"tz_key_Europe/Tallinn": "(UTC+02:00) ヨーロッパ/タリン",
	"tz_key_Europe/Vienna": "(UTC+01:00) ヨーロッパ/ウィーン",
	"tz_key_Europe/Vilnius": "(UTC+02:00) ヨーロッパ/ビリニュス",
	"tz_key_Europe/Warsaw": "(UTC+01:00) ヨーロッパ/ワルシャワ",
	"tz_key_Europe/Zagreb": "(UTC+01:00) ヨーロッパ/ザグレブ",
	"tz_key_Europe/Zurich": "(UTC+01:00) ヨーロッパ/チューリッヒ",
	"tz_key_Indian/Maldives": "(UTC+05:00) インド/モルディブ",
	"tz_key_Indian/Mauritius": "(UTC+04:00) インド/モーリシャス",
	"tz_key_Pacific/Apia": "(UTC-11:00) 太平洋/アピア",
	"tz_key_Pacific/Auckland": "(UTC+12:00) 太平洋/オークランド",
	"tz_key_Pacific/Easter": "(UTC-06:00) 太平洋/イースター",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) 太平洋/ガラパゴス",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) 太平洋/ホノルル",
	"tz_key_Pacific/Majuro": "(UTC-12:00) 太平洋/マジュロ",
	"tz_key_Pacific/Midway": "(UTC-11:00) 太平洋/ミッドウェー島",
	"tz_key_Pacific/Niue": "(UTC-11:00) 太平洋/ニエ",
	"tz_key_Pacific/Pago Pago": "(UTC-11:00) 太平洋/パゴパゴ",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) 太平洋/パゴパゴ"
};

var bc_account_num$6 = "계정 수: {num}";
var bc_avartar$6 = "프로필 이미지";
var bc_cancel$6 = "취소";
var bc_create$6 = "생성하기";
var bc_create_title$6 = "비즈니스 센터 생성";
var bc_info$6 = "광고 계정과 센터 멤버, 비즈니스 센터 내 멤버의 권한 상태를 관리합니다. 모두 한 곳에서 무료로 처리할 수 있습니다.";
var bc_member_num$6 = "멤버 수: {num}";
var bc_name$6 = "비즈니스 센터 이름";
var bc_name_empty_hint$6 = "비즈니스 센터 이름을 입력하세요.";
var bc_name_length_hint$6 = "비즈니스 센터 이름은 최대 200자입니다.";
var bc_name_placeholder$6 = "비즈니스 센터 이름을 입력하세요.";
var bc_timezone$6 = "시간대";
var bc_timezone_empty_hint$6 = "시간대를 선택하십시오.";
var bc_timezone_hint$6 = "사후 문구 분석에는 시간대 데이터를 사용합니다. 현재는 관련 기능을 지원하지 않습니다. 비즈니스 센터의 일반 데이터 보기에 대한 시간대를 선택하십시오.";
var bc_timezone_placeholder$6 = "시간대 선택";
var bc_type_agency$6 = "대행사";
var ko = {
	bc_account_num: bc_account_num$6,
	bc_avartar: bc_avartar$6,
	bc_cancel: bc_cancel$6,
	bc_create: bc_create$6,
	bc_create_title: bc_create_title$6,
	bc_info: bc_info$6,
	bc_member_num: bc_member_num$6,
	bc_name: bc_name$6,
	bc_name_empty_hint: bc_name_empty_hint$6,
	bc_name_length_hint: bc_name_length_hint$6,
	bc_name_placeholder: bc_name_placeholder$6,
	bc_timezone: bc_timezone$6,
	bc_timezone_empty_hint: bc_timezone_empty_hint$6,
	bc_timezone_hint: bc_timezone_hint$6,
	bc_timezone_placeholder: bc_timezone_placeholder$6,
	bc_type_agency: bc_type_agency$6,
	"tz_key_Africa/Accra": "(UTC+00:00) 아프리카/아크라",
	"tz_key_Africa/Cairo": "(UTC+02:00) 아프리카/카이로",
	"tz_key_Africa/Casablanca": "(UTC+00:00) 아프리카/카사블랑카",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) 아프리카/요하네스버그",
	"tz_key_Africa/Lagos": "(UTC+01:00) 아프리카/라고스",
	"tz_key_Africa/Nairobi": "(UTC+03:00) 아프리카/나이로비",
	"tz_key_Africa/Tunis": "(UTC+01:00) 아프리카/튀니스",
	"tz_key_America/Anchorage": "(UTC-09:00) 아메리카/앵커리지",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) 아메리카/아르헨티나/부에노스아이레스",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) 아메리카/아르헨티나/살타",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) 아메리카/아르헨티나/산루이스",
	"tz_key_America/Asuncion": "(UTC-04:00) 아메리카/아순시온",
	"tz_key_America/Atikokan": "(UTC-05:00) 아메리카/아티코칸",
	"tz_key_America/Belem": "(UTC-03:00) 아메리카/벨렘",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) 아메리카/블랑 사블롱",
	"tz_key_America/Bogota": "(UTC-05:00) 아메리카/보고타",
	"tz_key_America/Campo_Grande": "(UTC-04:00) 아메리카/캄포그란데",
	"tz_key_America/Caracas": "(UTC-04:30) 아메리카/카라카스",
	"tz_key_America/Chicago": "(UTC-06:00) 아메리카/시카고",
	"tz_key_America/Costa_Rica": "(UTC-06:00) 아메리카/코스타리카",
	"tz_key_America/Dawson": "(UTC-08:00) 아메리카/도슨",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) 아메리카/도슨 크릭",
	"tz_key_America/Denver": "(UTC-07:00) 아메리카/덴버",
	"tz_key_America/Edmonton": "(UTC-07:00) 아메리카/에드먼턴",
	"tz_key_America/El_Salvador": "(UTC-06:00) 아메리카/엘살바도르",
	"tz_key_America/Guatemala": "(UTC-06:00) 아메리카/과테말라",
	"tz_key_America/Guayaquil": "(UTC-05:00) 아메리카/과야킬",
	"tz_key_America/Halifax": "(UTC-04:00) 아메리카/핼리팩스",
	"tz_key_America/Hermosillo": "(UTC-07:00) 아메리카/에르모시요",
	"tz_key_America/Iqaluit": "(UTC-05:00) 아메리카/이칼루이트",
	"tz_key_America/Jamaica": "(UTC-05:00) 아메리카/자메이카",
	"tz_key_America/La_Paz": "(UTC-04:00) 아메리카/라파스",
	"tz_key_America/Lima": "(UTC-05:00) 아메리카/리마",
	"tz_key_America/Los_Angeles": "(UTC-08:00) 아메리카/로스앤젤레스",
	"tz_key_America/Managua": "(UTC-06:00) 아메리카/마나과",
	"tz_key_America/Mazatlan": "(UTC-07:00) 아메리카/마사틀란",
	"tz_key_America/Mexico_City": "(UTC-06:00) 아메리카/멕시코시티",
	"tz_key_America/Montevideo": "(UTC-03:00) 아메리카/몬테비데오",
	"tz_key_America/Nassau": "(UTC-05:00) 아메리카/나소",
	"tz_key_America/New_York": "(UTC-05:00) 아메리카/뉴욕",
	"tz_key_America/Noronha": "(UTC-02:00) 아메리카/노로냐",
	"tz_key_America/Panama": "(UTC-05:00) 아메리카/파나마",
	"tz_key_America/Phoenix": "(UTC-07:00) 아메리카/피닉스",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) 아메리카/포트오브스페인",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) 아메리카/푸에르토리코",
	"tz_key_America/Rainy_River": "(UTC-06:00) 아메리카/레이니 리버",
	"tz_key_America/Regina": "(UTC-06:00) 아메리카/리자이나",
	"tz_key_America/Santiago": "(UTC-04:00) 아메리카/산티아고",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) 아메리카/산토도밍고",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) 아메리카/상파울루",
	"tz_key_America/St_Johns": "(UTC-03:30) 아메리카/세인트존스",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) 아메리카/테구시갈파",
	"tz_key_America/Tijuana": "(UTC-08:00) 아메리카/티후아나",
	"tz_key_America/Toronto": "(UTC-05:00) 아메리카/토론토",
	"tz_key_America/Vancouver": "(UTC-08:00) 아메리카/밴쿠버",
	"tz_key_Asia/Amman": "(UTC+02:00) 아시아/암만",
	"tz_key_Asia/Baghdad": "(UTC+03:00) 아시아/바그다드",
	"tz_key_Asia/Bahrain": "(UTC+03:00) 아시아/바레인",
	"tz_key_Asia/Bangkok": "(UTC+07:00) 아시아/방콕",
	"tz_key_Asia/Beirut": "(UTC+02:00) 아시아/베이루트",
	"tz_key_Asia/Colombo": "(UTC+05:30) 아시아/콜롬보",
	"tz_key_Asia/Dhaka": "(UTC+06:00) 아시아/다카",
	"tz_key_Asia/Dubai": "(UTC+04:00) 아시아/두바이",
	"tz_key_Asia/Gaza": "(UTC+02:00) 아시아/가자",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) 아시아/호찌민",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) 아시아/홍콩",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) 아시아/이르쿠츠크",
	"tz_key_Asia/Jakarta": "(UTC+07:00) 아시아/자카르타",
	"tz_key_Asia/Jayapura": "(UTC+09:00) 아시아/자야푸라",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) 아시아/예루살렘",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) 아시아/캄차카",
	"tz_key_Asia/Karachi": "(UTC+05:00) 아시아/카라치",
	"tz_key_Asia/Kolkata": "(UTC+05:30) 아시아/콜카타",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) 아시아/크라스노야르스크",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) 아시아/쿠알라룸푸르",
	"tz_key_Asia/Kuwait": "(UTC+03:00) 아시아/쿠웨이트",
	"tz_key_Asia/Magadan": "(UTC+11:00) 아시아/마가단",
	"tz_key_Asia/Makassar": "(UTC+08:00) 아시아/마카사르",
	"tz_key_Asia/Manila": "(UTC+08:00) 아시아/마닐라",
	"tz_key_Asia/Muscat": "(UTC+04:00) 아시아/무스카트",
	"tz_key_Asia/Nicosia": "(UTC+02:00) 아시아/니코시아",
	"tz_key_Asia/Omsk": "(UTC+06:00) 아시아/옴스크",
	"tz_key_Asia/Phnom_Penh": "아시아/프놈펜",
	"tz_key_Asia/Qatar": "(UTC+03:00) 아시아/카타르",
	"tz_key_Asia/Riyadh": "(UTC+03:00) 아시아/리야드",
	"tz_key_Asia/Seoul": "(UTC+09:00) 아시아/서울",
	"tz_key_Asia/Shanghai": "(UTC+08:00) 아시아/베이징",
	"tz_key_Asia/Singapore": "(UTC+08:00) 아시아/싱가포르",
	"tz_key_Asia/Taipei": "(UTC+08:00) 아시아/타이베이",
	"tz_key_Asia/Tokyo": "(UTC+09:00) 아시아/도쿄",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) 아시아/블라디보스토크",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) 아시아/야쿠츠크",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) 아시아/예카테린부르크",
	"tz_key_Atlantic/Azores": "(UTC-01:00) 대서양/아조레스",
	"tz_key_Atlantic/Canary": "(UTC+00:00) 대서양/카나리아",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) 대서양/레이캬비크",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) 호주/브로큰힐",
	"tz_key_Australia/Perth": "(UTC+08:00) 호주/퍼스",
	"tz_key_Australia/Sydney": "(UTC+10:00) 호주/시드니",
	"tz_key_Etc/GMT": "(UTC) 런던",
	"tz_key_Etc/GMT+1": "(UTC-01:00) 폰타델가다",
	"tz_key_Etc/GMT+10": "(UTC-10:00) 호놀룰루 하와이표준시",
	"tz_key_Etc/GMT+11": "(UTC-11:00) 파고파고",
	"tz_key_Etc/GMT+12": "(UTC-12:00) 마주로",
	"tz_key_Etc/GMT+2": "(UTC-02:00) 노로냐",
	"tz_key_Etc/GMT+3": "(UTC-03:00) 상파울루",
	"tz_key_Etc/GMT+4": "(UTC-04:00) 카라카스",
	"tz_key_Etc/GMT+5": "(UTC-05:00) 뉴욕 동부표준시",
	"tz_key_Etc/GMT+6": "(UTC-06:00) 멕시코시티 중부표준시",
	"tz_key_Etc/GMT+7": "(UTC-07:00) 솔트레이크 시티 산악표준시",
	"tz_key_Etc/GMT+8": "(UTC-08:00) 로스앤젤레스 태평양표준시",
	"tz_key_Etc/GMT+9": "(UTC-09:00) 주노 알래스카표준시",
	"tz_key_Etc/GMT-1": "(UTC+01:00) 베를린",
	"tz_key_Etc/GMT-10": "(UTC+10:00) 캔버라",
	"tz_key_Etc/GMT-11": "(UTC+11:00) 호니아라",
	"tz_key_Etc/GMT-12": "(UTC+12:00) 웰링턴",
	"tz_key_Etc/GMT-13": "태평양/아피아",
	"tz_key_Etc/GMT-2": "(UTC+02:00) 아테네",
	"tz_key_Etc/GMT-3": "(UTC+03:00) 모스크바",
	"tz_key_Etc/GMT-4": "(UTC+04:00) 아부다비",
	"tz_key_Etc/GMT-5": "(UTC+05:00) 이슬라마바드",
	"tz_key_Etc/GMT-6": "(UTC+06:00) 다카",
	"tz_key_Etc/GMT-7": "(UTC+07:00) 방콕",
	"tz_key_Etc/GMT-8": "(UTC+08:00) 베이징",
	"tz_key_Etc/GMT-9": "(UTC+09:00) 도쿄",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) 유럽/암스테르담",
	"tz_key_Europe/Athens": "(UTC+02:00) 유럽/아테네",
	"tz_key_Europe/Belgrade": "(UTC+01:00) 유럽/베오그라드",
	"tz_key_Europe/Berlin": "(UTC+01:00) 유럽/베를린",
	"tz_key_Europe/Bratislava": "(UTC+01:00) 유럽/브라티슬라바",
	"tz_key_Europe/Brussels": "(UTC+01:00) 유럽/브뤼셀",
	"tz_key_Europe/Bucharest": "(UTC+02:00) 유럽/부카레스트",
	"tz_key_Europe/Budapest": "(UTC+01:00) 유럽/부다페스트",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) 유럽/코펜하겐",
	"tz_key_Europe/Dublin": "(UTC+00:00) 유럽/더블린",
	"tz_key_Europe/Helsinki": "(UTC+02:00) 유럽/헬싱키",
	"tz_key_Europe/Istanbul": "(UTC+02:00) 유럽/이스탄불",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) 유럽/칼리닌그라드",
	"tz_key_Europe/Kiev": "(UTC+02:00) 유럽/키예프",
	"tz_key_Europe/Lisbon": "(UTC+00:00) 유럽/리스본",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) 유럽/류블랴나",
	"tz_key_Europe/London": "(UTC+00:00) 유럽/런던",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) 유럽/룩셈부르크",
	"tz_key_Europe/Madrid": "(UTC+01:00) 유럽/마드리드",
	"tz_key_Europe/Malta": "(UTC+01:00) 유럽/몰타",
	"tz_key_Europe/Moscow": "(UTC+03:00) 유럽/모스크바",
	"tz_key_Europe/Oslo": "(UTC+01:00) 유럽/오슬로",
	"tz_key_Europe/Paris": "(UTC+01:00) 유럽/파리",
	"tz_key_Europe/Prague": "(UTC+01:00) 유럽/프라하",
	"tz_key_Europe/Riga": "(UTC+02:00) 유럽/리가",
	"tz_key_Europe/Rome": "(UTC+01:00) 유럽/로마",
	"tz_key_Europe/Samara": "(UTC+03:00) 유럽/사마라",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) 유럽/사라예보",
	"tz_key_Europe/Skopje": "(UTC+01:00) 유럽/스코페",
	"tz_key_Europe/Sofia": "(UTC+02:00) 유럽/소피아",
	"tz_key_Europe/Stockholm": "(UTC+01:00) 유럽/스톡홀름",
	"tz_key_Europe/Tallinn": "(UTC+02:00) 유럽/탈린",
	"tz_key_Europe/Vienna": "(UTC+01:00) 유럽/비엔나",
	"tz_key_Europe/Vilnius": "(UTC+02:00) 유럽/빌뉴스",
	"tz_key_Europe/Warsaw": "(UTC+01:00) 유럽/바르샤바",
	"tz_key_Europe/Zagreb": "(UTC+01:00) 유럽/자그레브",
	"tz_key_Europe/Zurich": "(UTC+01:00) 유럽/취리히",
	"tz_key_Indian/Maldives": "(UTC+05:00) 인도양/몰디브",
	"tz_key_Indian/Mauritius": "(UTC+04:00) 인도양/모리셔스",
	"tz_key_Pacific/Apia": "(UTC-11:00) 태평양/아피아",
	"tz_key_Pacific/Auckland": "(UTC+12:00) 태평양/오클랜드",
	"tz_key_Pacific/Easter": "(UTC-06:00) 태평양/이스터",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) 태평양/갈라파고스",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) 태평양/호놀룰루",
	"tz_key_Pacific/Majuro": "(UTC-12:00) 태평양/마주로",
	"tz_key_Pacific/Midway": "(UTC-11:00) 태평양/미드웨이",
	"tz_key_Pacific/Niue": "(UTC-11:00) 태평양/니우에",
	"tz_key_Pacific/Pago Pago": "태평양/파고파고",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) 태평양/파고파고"
};

var bc_account_num$5 = "Amaun Akaun: {num}";
var bc_avartar$5 = "Imej Profil";
var bc_cancel$5 = "Batal";
var bc_create$5 = "Cipta";
var bc_create_title$5 = "Cipta Pusat Perniagaan";
var bc_info$5 = "Urus akaun pengiklanan anda, ahli pusat dan status keizinan mereka di Pusat Perniagaan. Semua dalam Satu dan Percuma.";
var bc_member_num$5 = "Amaun Ahli: {num}";
var bc_name$5 = "Nama Pusat Perniagaan";
var bc_name_empty_hint$5 = "Sila isikan nama Pusat Perniagaan.";
var bc_name_length_hint$5 = "Nama Pusat Perniagaan tidak boleh melebihi 200 aksara.";
var bc_name_placeholder$5 = "Sila isikan nama Pusat Perniagaan.";
var bc_timezone$5 = "Zon waktu";
var bc_timezone_empty_hint$5 = "Sila pilih zon waktu.";
var bc_timezone_hint$5 = "Data zon waktu boleh digunakan dalam analisis pasca frasa Pada masa ini kami tidak menyokong fungsi berkaitan. Sila pilih zon waktu untuk paparan data umum dalam Pusat Perniagaan.";
var bc_timezone_placeholder$5 = "Pilih zon waktu";
var bc_type_agency$5 = "Agensi";
var ms = {
	bc_account_num: bc_account_num$5,
	bc_avartar: bc_avartar$5,
	bc_cancel: bc_cancel$5,
	bc_create: bc_create$5,
	bc_create_title: bc_create_title$5,
	bc_info: bc_info$5,
	bc_member_num: bc_member_num$5,
	bc_name: bc_name$5,
	bc_name_empty_hint: bc_name_empty_hint$5,
	bc_name_length_hint: bc_name_length_hint$5,
	bc_name_placeholder: bc_name_placeholder$5,
	bc_timezone: bc_timezone$5,
	bc_timezone_empty_hint: bc_timezone_empty_hint$5,
	bc_timezone_hint: bc_timezone_hint$5,
	bc_timezone_placeholder: bc_timezone_placeholder$5,
	bc_type_agency: bc_type_agency$5,
	"tz_key_Africa/Accra": "(UTC+00:00) Afrika/Accra",
	"tz_key_Africa/Cairo": "(UTC+02:00) Afrika/Kaherah",
	"tz_key_Africa/Casablanca": "(UTC+00:00) Afrika/Casablanca",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) Afrika/Johannesburg",
	"tz_key_Africa/Lagos": "(UTC+01:00) Afrika/Lagos",
	"tz_key_Africa/Nairobi": "(UTC+03:00) Afrika/Nairobi",
	"tz_key_Africa/Tunis": "(UTC+01:00) Afrika/Tunis",
	"tz_key_America/Anchorage": "(UTC-09:00) Amerika/Anchorage",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) Amerika/Argentina/Buenos_Aires",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) Amerika/Argentina/Salta",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) Amerika/Argentina/San_Luis",
	"tz_key_America/Asuncion": "(UTC-04:00) Amerika/Asuncion",
	"tz_key_America/Atikokan": "(UTC-05:00) Amerika/Atitokan",
	"tz_key_America/Belem": "(UTC-03:00) Amerika/Belem",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) Amerika/Blanc-Sablon",
	"tz_key_America/Bogota": "(UTC-05:00) Amerika/Bogota",
	"tz_key_America/Campo_Grande": "(UTC-04:00) Amerika/Campo_Grande",
	"tz_key_America/Caracas": "(UTC-04:30) Amerika/Caracas",
	"tz_key_America/Chicago": "(UTC-06:00) Amerika/Chicago",
	"tz_key_America/Costa_Rica": "(UTC-06:00) Amerika/Costa_Rica",
	"tz_key_America/Dawson": "(UTC-08:00) Amerika/Dawson",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) Amerika/Dawson_Creek",
	"tz_key_America/Denver": "(UTC-07:00) Amerika/Denver",
	"tz_key_America/Edmonton": "(UTC-07:00) Amerika/Edmonton",
	"tz_key_America/El_Salvador": "(UTC-06:00) Amerika/El_Salvador",
	"tz_key_America/Guatemala": "(UTC-06:00) Amerika/Guatemala",
	"tz_key_America/Guayaquil": "(UTC-05:00) Amerika/Guayaquil",
	"tz_key_America/Halifax": "(UTC-04:00) Amerika/Halifax",
	"tz_key_America/Hermosillo": "(UTC-07:00) Amerika/Hermosillo",
	"tz_key_America/Iqaluit": "(UTC-05:00) Amerika/Iqaluit",
	"tz_key_America/Jamaica": "(UTC-05:00) Amerika/Jamaica",
	"tz_key_America/La_Paz": "(UTC-04:00) Amerika/La_Paz",
	"tz_key_America/Lima": "(UTC-05:00) Amerika/Lima",
	"tz_key_America/Los_Angeles": "(UTC-08:00) Amerika/Los_Angeles",
	"tz_key_America/Managua": "(UTC-06:00) Amerika/Managua",
	"tz_key_America/Mazatlan": "(UTC-07:00) Amerika/Mazatlan",
	"tz_key_America/Mexico_City": "(UTC-06:00) Amerika/Mexico_City",
	"tz_key_America/Montevideo": "(UTC-03:00) Amerika/Montevideo",
	"tz_key_America/Nassau": "(UTC-05:00) Amerika/Nassau",
	"tz_key_America/New_York": "(UTC-05:00) Amerika/New_York",
	"tz_key_America/Noronha": "(UTC-02:00) Amerika/Noronha",
	"tz_key_America/Panama": "(UTC-05:00) Amerika/Panama",
	"tz_key_America/Phoenix": "(UTC-07:00) Amerika/Phoenix",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) Amerika/Port_of_Spain",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) Amerika/Puerto_Rico",
	"tz_key_America/Rainy_River": "(UTC-06:00) Amerika/Rainy_River",
	"tz_key_America/Regina": "(UTC-06:00) Amerika/Regina",
	"tz_key_America/Santiago": "(UTC-04:00) Amerika/Santiago",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) Amerika/Santo_Domingo",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) Amerika/Sao_Paulo",
	"tz_key_America/St_Johns": "(UTC-03:30) Amerika/St_Johns",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) Amerika/Tegucigalpa",
	"tz_key_America/Tijuana": "(UTC-08:00) Amerika/Tijuana",
	"tz_key_America/Toronto": "(UTC-05:00) Amerika/Toronto",
	"tz_key_America/Vancouver": "(UTC-08:00) Amerika/Vancouver",
	"tz_key_Asia/Amman": "(UTC+02:00) Asia/Amman",
	"tz_key_Asia/Baghdad": "(UTC+03:00) Asia/Baghdad",
	"tz_key_Asia/Bahrain": "(UTC+03:00) Asia/Bahrain",
	"tz_key_Asia/Bangkok": "(UTC+07:00) Asia/Bangkok",
	"tz_key_Asia/Beirut": "(UTC+02:00) Asia/Beirut",
	"tz_key_Asia/Colombo": "(UTC+05:30) Asia/Colombo",
	"tz_key_Asia/Dhaka": "(UTC+06:00) Asia/Dhaka",
	"tz_key_Asia/Dubai": "(UTC+04:00) Asia/Dubai",
	"tz_key_Asia/Gaza": "(UTC+02:00) Asia/Gaza",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) Asia/Ho_Chi_Minh",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) Asia/Hong_Kong",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) Asia/Irkutsk",
	"tz_key_Asia/Jakarta": "(UTC+07:00) Asia/Jakarta",
	"tz_key_Asia/Jayapura": "(UTC+09:00) Asia/Jayapura",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) Asia/Baitulmuqaddis",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) Asia/Kamchatka",
	"tz_key_Asia/Karachi": "(UTC+05:00) Asia/Karachi",
	"tz_key_Asia/Kolkata": "(UTC+05:30) Asia/Kolkata",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) Asia/Krasnoyarsk",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) Asia/Kuala_Lumpur",
	"tz_key_Asia/Kuwait": "(UTC+03:00) Asia/Kuwait",
	"tz_key_Asia/Magadan": "(UTC+11:00) Asia/Magadan",
	"tz_key_Asia/Makassar": "(UTC+08:00) Asia/Makassar",
	"tz_key_Asia/Manila": "(UTC+08:00) Asia/Manila",
	"tz_key_Asia/Muscat": "(UTC+04:00) Asia/Muscat",
	"tz_key_Asia/Nicosia": "(UTC+02:00) Asia/Nicosia",
	"tz_key_Asia/Omsk": "(UTC+06:00) Asia/Omsk",
	"tz_key_Asia/Phnom_Penh": "Asia/Phnom Penh",
	"tz_key_Asia/Qatar": "(UTC+03:00) Asia/Qatar",
	"tz_key_Asia/Riyadh": "(UTC+03:00) Asia/Riyadh",
	"tz_key_Asia/Seoul": "(UTC+09:00) Asia/Seoul",
	"tz_key_Asia/Shanghai": "(UTC+08:00) Asia/Beijing",
	"tz_key_Asia/Singapore": "(UTC+08:00) Asia/Singapura",
	"tz_key_Asia/Taipei": "(UTC+08:00) Asia/Taipei",
	"tz_key_Asia/Tokyo": "(UTC+09:00) Asia/Tokyo",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) Asia/Vladivostok",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) Asia/Yakutsk",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) Asia/Yekaterinburg",
	"tz_key_Atlantic/Azores": "(UTC-01:00) Atlantik/Azores",
	"tz_key_Atlantic/Canary": "(UTC+00:00) Atlantik/Canary",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) Atlantik/Reykjavik",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) Australia/Broken_Hill",
	"tz_key_Australia/Perth": "(UTC+08:00) Australia/Perth",
	"tz_key_Australia/Sydney": "(UTC+10:00) Australia/Sydney",
	"tz_key_Etc/GMT": "(UTC) London",
	"tz_key_Etc/GMT+1": "(UTC-01:00) Ponta Delgada",
	"tz_key_Etc/GMT+10": "(UTC-10:00) Waktu Honolulu_Hawaii",
	"tz_key_Etc/GMT+11": "(UTC-11:00) Pago Pago",
	"tz_key_Etc/GMT+12": "(UTC-12:00) Majuro",
	"tz_key_Etc/GMT+2": "(UTC-02:00) Noronha",
	"tz_key_Etc/GMT+3": "(UTC-03:00) Sao Paulo",
	"tz_key_Etc/GMT+4": "(UTC-04:00) Caracas",
	"tz_key_Etc/GMT+5": "(UTC-05:00) Waktu New York_Eastern",
	"tz_key_Etc/GMT+6": "(UTC-06:00) Waktu Mexico City_Central",
	"tz_key_Etc/GMT+7": "(UTC-07:00) Waktu Salt Lake City_Mountain",
	"tz_key_Etc/GMT+8": "(UTC-08:00) Waktu Los Angeles_Pasifik",
	"tz_key_Etc/GMT+9": "(UTC-09:00) Waktu Juno_Alaska",
	"tz_key_Etc/GMT-1": "(UTC+01:00) Berlin",
	"tz_key_Etc/GMT-10": "(UTC+10:00) Canberra",
	"tz_key_Etc/GMT-11": "(UTC+11:00) Honiara",
	"tz_key_Etc/GMT-12": "(UTC+12:00) Wellington",
	"tz_key_Etc/GMT-13": "Pasifik/Apia",
	"tz_key_Etc/GMT-2": "(UTC+02:00) Athens",
	"tz_key_Etc/GMT-3": "(UTC+03:00) Moscow",
	"tz_key_Etc/GMT-4": "(UTC+04:00) Abu Dhabi",
	"tz_key_Etc/GMT-5": "(UTC+05:00) Islamabad",
	"tz_key_Etc/GMT-6": "(UTC+06:00) Dhaka",
	"tz_key_Etc/GMT-7": "(UTC+07:00) Bangkok",
	"tz_key_Etc/GMT-8": "(UTC+08:00) Beijing",
	"tz_key_Etc/GMT-9": "(UTC+09:00) Tokyo",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) Eropah/Amsterdam",
	"tz_key_Europe/Athens": "(UTC+02:00) Eropah/Athens",
	"tz_key_Europe/Belgrade": "(UTC+01:00) Eropah/Belgrade",
	"tz_key_Europe/Berlin": "(UTC+01:00) Eropah/Berlin",
	"tz_key_Europe/Bratislava": "(UTC+01:00) Eropah/Bratislava",
	"tz_key_Europe/Brussels": "(UTC+01:00) Eropah/Brussels",
	"tz_key_Europe/Bucharest": "(UTC+02:00) Eropah/Bucharest",
	"tz_key_Europe/Budapest": "(UTC+01:00) Eropah/Budapest",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) Eropah/Copenhagen",
	"tz_key_Europe/Dublin": "(UTC+00:00) Eropah/Dublin",
	"tz_key_Europe/Helsinki": "(UTC+02:00) Eropah/Helsinki",
	"tz_key_Europe/Istanbul": "(UTC+02:00) Eropah/Istanbul",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) Eropah/Kaliningrad",
	"tz_key_Europe/Kiev": "(UTC+02:00) Eropah/Kiev",
	"tz_key_Europe/Lisbon": "(UTC+00:00) Eropah/Lisbon",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) Eropah/Ljubljana",
	"tz_key_Europe/London": "(UTC+00:00) Eropah/London",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) Eropah/Luxembourg",
	"tz_key_Europe/Madrid": "(UTC+01:00) Eropah/Madrid",
	"tz_key_Europe/Malta": "(UTC+01:00) Eropah/Malta",
	"tz_key_Europe/Moscow": "(UTC+03:00) Eropah/Moscow",
	"tz_key_Europe/Oslo": "(UTC+01:00) Eropah/Oslo",
	"tz_key_Europe/Paris": "(UTC+01:00) Eropah/Paris",
	"tz_key_Europe/Prague": "(UTC+01:00) Eropah/Prague",
	"tz_key_Europe/Riga": "(UTC+02:00) Eropah/Riga",
	"tz_key_Europe/Rome": "(UTC+01:00) Eropah/Rom",
	"tz_key_Europe/Samara": "(UTC+03:00) Eropah/Samara",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) Eropah/Sarajevo",
	"tz_key_Europe/Skopje": "(UTC+01:00) Eropah/Skopje",
	"tz_key_Europe/Sofia": "(UTC+02:00) Eropah/Sofia",
	"tz_key_Europe/Stockholm": "(UTC+01:00) Eropah/Stockholm",
	"tz_key_Europe/Tallinn": "(UTC+02:00) Eropah/Tallinn",
	"tz_key_Europe/Vienna": "(UTC+01:00) Eropah/Vienna",
	"tz_key_Europe/Vilnius": "(UTC+02:00) Eropah/Vilnius",
	"tz_key_Europe/Warsaw": "(UTC+01:00) Eropah/Warsaw",
	"tz_key_Europe/Zagreb": "(UTC+01:00) Eropah/Zagreb",
	"tz_key_Europe/Zurich": "(UTC+01:00) Eropah/Zurich",
	"tz_key_Indian/Maldives": "(UTC+05:00) India/Maldives",
	"tz_key_Indian/Mauritius": "(UTC+04:00) India/Mauritius",
	"tz_key_Pacific/Apia": "(UTC-11:00) Pasifik/Apia",
	"tz_key_Pacific/Auckland": "(UTC+12:00) Pasifik/Auckland",
	"tz_key_Pacific/Easter": "(UTC-06:00) Pasifik/Easter",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) Pasifik/Galapagos",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) Pasifik/Honolulu",
	"tz_key_Pacific/Majuro": "(UTC-12:00) Pasifik/Majuro",
	"tz_key_Pacific/Midway": "(UTC-11:00) Pasifik/Midway",
	"tz_key_Pacific/Niue": "(UTC-11:00) Pasifik/Niue",
	"tz_key_Pacific/Pago Pago": "Pasifik/Pago Pago",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) Pasifik/Pago Pago"
};

var bc_account_num$4 = "Количество учетных записей: {num}";
var bc_avartar$4 = "Изображение профиля";
var bc_cancel$4 = "Отменить";
var bc_create$4 = "Создать";
var bc_create_title$4 = "Создать Бизнес-центр";
var bc_info$4 = "Управление учетными записями, участниками центра и статусом их разрешений в Бизнес-центре. Все возможности в одном месте. Бесплатно.";
var bc_member_num$4 = "Количество участников: {num}";
var bc_name$4 = "Имя Бизнес-центра";
var bc_name_empty_hint$4 = "Укажите имя Бизнес-центра.";
var bc_name_length_hint$4 = "Имя Бизнес-центра не может содержать более 200 символов.";
var bc_name_placeholder$4 = "Укажите имя Бизнес-центра.";
var bc_timezone$4 = "Часовой пояс";
var bc_timezone_empty_hint$4 = "Выберите часовой пояс";
var bc_timezone_hint$4 = "Данные часового пояса будут использоваться при пост-анализе фраз. В настоящий момент связанные функции не поддерживаются. Выберите часовой пояс для общего представления данных в Бизнес-центр.";
var bc_timezone_placeholder$4 = "Выбрать часовой пояс";
var bc_type_agency$4 = "Агентство";
var ru = {
	bc_account_num: bc_account_num$4,
	bc_avartar: bc_avartar$4,
	bc_cancel: bc_cancel$4,
	bc_create: bc_create$4,
	bc_create_title: bc_create_title$4,
	bc_info: bc_info$4,
	bc_member_num: bc_member_num$4,
	bc_name: bc_name$4,
	bc_name_empty_hint: bc_name_empty_hint$4,
	bc_name_length_hint: bc_name_length_hint$4,
	bc_name_placeholder: bc_name_placeholder$4,
	bc_timezone: bc_timezone$4,
	bc_timezone_empty_hint: bc_timezone_empty_hint$4,
	bc_timezone_hint: bc_timezone_hint$4,
	bc_timezone_placeholder: bc_timezone_placeholder$4,
	bc_type_agency: bc_type_agency$4,
	"tz_key_Africa/Accra": "(UTC+00:00) Африка/Аккра",
	"tz_key_Africa/Cairo": "(UTC+02:00) Африка/Каир",
	"tz_key_Africa/Casablanca": "(UTC+00:00) Африка/Касабланка",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) Африка/Йоханнесбург",
	"tz_key_Africa/Lagos": "(UTC+01:00) Африка/Лагос",
	"tz_key_Africa/Nairobi": "(UTC+03:00) Африка/Найроби",
	"tz_key_Africa/Tunis": "(UTC+01:00) Африка/Тунис",
	"tz_key_America/Anchorage": "(UTC-09:00) Америка/Анкоридж",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) Америка/Аргентина/Буэнос-Айрес",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) Америка/Аргентина/Сальта",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) Америка/Аргентина/Сан-Луис",
	"tz_key_America/Asuncion": "(UTC-04:00) Америка/Асунсьон",
	"tz_key_America/Atikokan": "(UTC-05:00) Америка/Атикокан",
	"tz_key_America/Belem": "(UTC-03:00) Америка/Белен",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) Америка/Блан-Саблон",
	"tz_key_America/Bogota": "(UTC-05:00) Америка/Богота",
	"tz_key_America/Campo_Grande": "(UTC-04:00) Америка/Кампу-Гранди",
	"tz_key_America/Caracas": "(UTC-04:30) Америка/Каракас",
	"tz_key_America/Chicago": "(UTC-06:00) Америка/Чикаго",
	"tz_key_America/Costa_Rica": "(UTC-06:00) Америка/Коста-Рика",
	"tz_key_America/Dawson": "(UTC-08:00) Америка/Доусон",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) Америка/Досон-Крик",
	"tz_key_America/Denver": "(UTC-07:00) Америка/Денвер",
	"tz_key_America/Edmonton": "(UTC-07:00) Америка/Эдмонтон",
	"tz_key_America/El_Salvador": "(UTC-06:00) Америка/Сальвадор",
	"tz_key_America/Guatemala": "(UTC-06:00) Америка/Гватемала",
	"tz_key_America/Guayaquil": "(UTC-05:00) Америка/Гуаякиль",
	"tz_key_America/Halifax": "(UTC-04:00) Америка/Галифакс",
	"tz_key_America/Hermosillo": "(UTC-07:00) Америка/Эрмосильо",
	"tz_key_America/Iqaluit": "(UTC-05:00) Америка/Икалуит",
	"tz_key_America/Jamaica": "(UTC-05:00) Америка/Ямайка",
	"tz_key_America/La_Paz": "(UTC-04:00) Америка/Ла-Пас",
	"tz_key_America/Lima": "(UTC-05:00) Америка/Лима",
	"tz_key_America/Los_Angeles": "(UTC-08:00) Америка/Лос-Анджелес",
	"tz_key_America/Managua": "(UTC-06:00) Америка/Манагуа",
	"tz_key_America/Mazatlan": "(UTC-07:00) Америка/Мазатлан",
	"tz_key_America/Mexico_City": "(UTC-06:00) Америка/Мехико",
	"tz_key_America/Montevideo": "(UTC-03:00) Америка/Монтевидео",
	"tz_key_America/Nassau": "(UTC-05:00) Америка/Нассау",
	"tz_key_America/New_York": "(UTC-05:00) Америка/Нью-Йорк",
	"tz_key_America/Noronha": "(UTC-02:00) Америка/Норонха",
	"tz_key_America/Panama": "(UTC-05:00) Америка/Панама",
	"tz_key_America/Phoenix": "(UTC-07:00) Америка/Финикс",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) Америка/Порт-оф-Спейн",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) Америка/Пуэрто-Рико",
	"tz_key_America/Rainy_River": "(UTC-06:00) Америка/Рейни-Ривер",
	"tz_key_America/Regina": "(UTC-06:00) Америка/Реджайна",
	"tz_key_America/Santiago": "(UTC-04:00) Америка/Сантьяго",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) Америка/Санто-Доминго",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) Америка/Сан-Паулу",
	"tz_key_America/St_Johns": "(UTC-03:30) Америка/Сент-Джонс",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) Америка/Тегусигальпа",
	"tz_key_America/Tijuana": "(UTC-08:00) Америка/Тихуана",
	"tz_key_America/Toronto": "(UTC-05:00) Америка/Торонто",
	"tz_key_America/Vancouver": "(UTC-08:00) Америка/Ванкувер",
	"tz_key_Asia/Amman": "(UTC+02:00) Азия/Амман",
	"tz_key_Asia/Baghdad": "(UTC+03:00) Азия/Багдад",
	"tz_key_Asia/Bahrain": "(UTC+03:00) Азия/Бахрейн",
	"tz_key_Asia/Bangkok": "(UTC+07:00) Азия/Бангкок",
	"tz_key_Asia/Beirut": "(UTC+02:00) Азия/Бейрут",
	"tz_key_Asia/Colombo": "(UTC+05:30) Азия/Коломбо",
	"tz_key_Asia/Dhaka": "(UTC+06:00) Азия/Дакка",
	"tz_key_Asia/Dubai": "(UTC+04:00) Азия/Дубай",
	"tz_key_Asia/Gaza": "(UTC+02:00) Азия/Газа",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) Азия/Хошимин",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) Азия/Гонконг",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) Азия/Иркутск",
	"tz_key_Asia/Jakarta": "(UTC+07:00) Азия/Джакарта",
	"tz_key_Asia/Jayapura": "(UTC+09:00) Азия/Джаяпура",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) Азия/Иерусалим",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) Азия/Камчатка",
	"tz_key_Asia/Karachi": "(UTC+05:00) Азия/Карачи",
	"tz_key_Asia/Kolkata": "(UTC+05:30) Азия/Колката",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) Азия/Красноярск",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) Азия/Куала-Лумпур",
	"tz_key_Asia/Kuwait": "(UTC+03:00) Азия/Кувейт",
	"tz_key_Asia/Magadan": "(UTC+11:00) Азия/Магадан",
	"tz_key_Asia/Makassar": "(UTC+08:00) Азия/Макасар",
	"tz_key_Asia/Manila": "(UTC+08:00) Азия/Манила",
	"tz_key_Asia/Muscat": "(UTC+04:00) Азия/Маскат",
	"tz_key_Asia/Nicosia": "(UTC+02:00) Азия/Никосия",
	"tz_key_Asia/Omsk": "(UTC+06:00) Азия/Омск",
	"tz_key_Asia/Phnom_Penh": "Азия/Пномпень",
	"tz_key_Asia/Qatar": "(UTC+03:00) Азия/Катар",
	"tz_key_Asia/Riyadh": "(UTC+03:00) Азия/Эр-Рияд",
	"tz_key_Asia/Seoul": "(UTC+09:00) Азия/Сеул",
	"tz_key_Asia/Shanghai": "(UTC+08:00) Азия/Пекин",
	"tz_key_Asia/Singapore": "(UTC+08:00) Азия/Сингапур",
	"tz_key_Asia/Taipei": "(UTC+08:00) Азия/Тайбэй",
	"tz_key_Asia/Tokyo": "(UTC+09:00) Азия/Токио",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) Азия/Владивосток",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) Азия/Якутск",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) Азия/Екатеринбург",
	"tz_key_Atlantic/Azores": "(UTC-01:00) Регион Атлантического океана/Азорские острова",
	"tz_key_Atlantic/Canary": "(UTC+00:00) Регион Атлантического океана/Канарские острова",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) Регион Атлантического океана/Рейкьявик",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) Австралия/Брокен-Хилл",
	"tz_key_Australia/Perth": "(UTC+08:00) Австралия/Перт",
	"tz_key_Australia/Sydney": "(UTC+10:00) Австралия/Сидней",
	"tz_key_Etc/GMT": "(UTC) Лондон",
	"tz_key_Etc/GMT+1": "(UTC-01:00) Понта-Дельгада",
	"tz_key_Etc/GMT+10": "(UTC-10:00) Гонолулу_Гавайское время",
	"tz_key_Etc/GMT+11": "(UTC-11:00) Паго-Паго",
	"tz_key_Etc/GMT+12": "(UTC-12:00) Маджуро",
	"tz_key_Etc/GMT+2": "(UTC-02:00) Норонха",
	"tz_key_Etc/GMT+3": "(UTC-03:00) Сан-Паулу",
	"tz_key_Etc/GMT+4": "(UTC-04:00) Каракас",
	"tz_key_Etc/GMT+5": "(UTC-05:00) Нью-Йорк_Восточное время",
	"tz_key_Etc/GMT+6": "(UTC-06:00) Мехико_Центральное время",
	"tz_key_Etc/GMT+7": "(UTC-07:00) Солт-Лейк-Сити_Горное время",
	"tz_key_Etc/GMT+8": "(UTC-08:00) Лос-Анджелес_Тихоокеанское время",
	"tz_key_Etc/GMT+9": "(UTC-09:00) Джуно_Время на Аляске",
	"tz_key_Etc/GMT-1": "(UTC+01:00) Берлин",
	"tz_key_Etc/GMT-10": "(UTC+10:00) Канберра",
	"tz_key_Etc/GMT-11": "(UTC+11:00) Хониара",
	"tz_key_Etc/GMT-12": "(UTC+12:00) Веллингтон",
	"tz_key_Etc/GMT-13": "Регион Тихого океана / Апия",
	"tz_key_Etc/GMT-2": "(UTC+02:00) Афины",
	"tz_key_Etc/GMT-3": "(UTC+03:00) Москва",
	"tz_key_Etc/GMT-4": "(UTC+04:00) Абу-Даби",
	"tz_key_Etc/GMT-5": "(UTC+05:00) Исламабад",
	"tz_key_Etc/GMT-6": "(UTC+06:00) Дакка",
	"tz_key_Etc/GMT-7": "(UTC+07:00) Бангкок",
	"tz_key_Etc/GMT-8": "(UTC+08:00) Пекин",
	"tz_key_Etc/GMT-9": "(UTC+09:00) Токио",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) Европа/Амстердам",
	"tz_key_Europe/Athens": "(UTC+02:00) Европа/Афины",
	"tz_key_Europe/Belgrade": "(UTC+01:00) Европа/Белград",
	"tz_key_Europe/Berlin": "(UTC+01:00) Европа/Берлин",
	"tz_key_Europe/Bratislava": "(UTC+01:00) Европа/Братислава",
	"tz_key_Europe/Brussels": "(UTC+01:00) Европа/Брюссель",
	"tz_key_Europe/Bucharest": "(UTC+02:00) Европа/Бухарест",
	"tz_key_Europe/Budapest": "(UTC+01:00) Европа/Будапешт",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) Европа/Копенгаген",
	"tz_key_Europe/Dublin": "(UTC+00:00) Европа/Дублин",
	"tz_key_Europe/Helsinki": "(UTC+02:00) Европа/Хельсинки",
	"tz_key_Europe/Istanbul": "(UTC+02:00) Европа/Стамбул",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) Европа/Калининград",
	"tz_key_Europe/Kiev": "(UTC+02:00) Европа/Киев",
	"tz_key_Europe/Lisbon": "(UTC+00:00) Европа/Лиссабон",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) Европа/Любляна",
	"tz_key_Europe/London": "(UTC+00:00) Европа/Лондон",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) Европа/Люксембург",
	"tz_key_Europe/Madrid": "(UTC+01:00) Европа/Мадрид",
	"tz_key_Europe/Malta": "(UTC+01:00) Европа/Мальта",
	"tz_key_Europe/Moscow": "(UTC+03:00) Европа/Москва",
	"tz_key_Europe/Oslo": "(UTC+01:00) Европа/Осло",
	"tz_key_Europe/Paris": "(UTC+01:00) Европа/Париж",
	"tz_key_Europe/Prague": "(UTC+01:00) Европа/Прага",
	"tz_key_Europe/Riga": "(UTC+02:00) Европа/Рига",
	"tz_key_Europe/Rome": "(UTC+01:00) Европа/Рим",
	"tz_key_Europe/Samara": "(UTC+03:00) Европа/Самара",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) Европа/Сараево",
	"tz_key_Europe/Skopje": "(UTC+01:00) Европа/Скопье",
	"tz_key_Europe/Sofia": "(UTC+02:00) Европа/София",
	"tz_key_Europe/Stockholm": "(UTC+01:00) Европа/Стокгольм",
	"tz_key_Europe/Tallinn": "(UTC+02:00) Европа/Таллин",
	"tz_key_Europe/Vienna": "(UTC+01:00) Европа/Вена",
	"tz_key_Europe/Vilnius": "(UTC+02:00) Европа/Вильнюс",
	"tz_key_Europe/Warsaw": "(UTC+01:00) Европа/Варшава",
	"tz_key_Europe/Zagreb": "(UTC+01:00) Европа/Загреб",
	"tz_key_Europe/Zurich": "(UTC+01:00) Европа/Цюрих",
	"tz_key_Indian/Maldives": "(UTC+05:00) Регион Индийского океана/Мальдивские острова",
	"tz_key_Indian/Mauritius": "(UTC+04:00) Регион Индийского океана/Маврикий",
	"tz_key_Pacific/Apia": "(UTC-11:00) Регион Тихого океана/Апия",
	"tz_key_Pacific/Auckland": "(UTC+12:00) Регион Тихого океана/Окленд",
	"tz_key_Pacific/Easter": "(UTC-06:00) Регион Тихого океана/о. Пасхи",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) Регион Тихого океана/Галапагос",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) Регион Тихого океана/Гонолулу",
	"tz_key_Pacific/Majuro": "(UTC-12:00) Регион Тихого океана/Маджуро",
	"tz_key_Pacific/Midway": "(UTC-11:00) Регион Тихого океана/Мидуэй",
	"tz_key_Pacific/Niue": "(UTC-11:00) Регион Тихого океана/Ниуэ",
	"tz_key_Pacific/Pago Pago": "Регион Тихого океана/Паго-Паго",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) Регион Тихого океана/Паго-Паго"
};

var bc_account_num$3 = "จำนวนบัญชี: {num}";
var bc_avartar$3 = "รูปโปรไฟล์";
var bc_cancel$3 = "ยกเลิก";
var bc_create$3 = "สร้าง";
var bc_create_title$3 = "สร้างศูนย์ธุรกิจ";
var bc_info$3 = "จัดการบัญชีโฆษณาของคุณ สมาชิกของศูนย์และสถานะการอนุญาตในศูนย์ธุรกิจ เบ็ดเสร็จและฟรี";
var bc_member_num$3 = "จำนวนสมาชิก {num}";
var bc_name$3 = "ชื่อศูนย์ธุรกิจ";
var bc_name_empty_hint$3 = "กรุณากรอกชื่อศูนย์ธุรกิจ";
var bc_name_length_hint$3 = "ชื่อศูนย์ธุรกิจต้องมีตัวอักษรไม่เกิน 200 ตัว";
var bc_name_placeholder$3 = "กรุณากรอกชื่อศูนย์ธุรกิจ";
var bc_timezone$3 = "เขตเวลา";
var bc_timezone_empty_hint$3 = "โปรดเลือกเขตเวลา";
var bc_timezone_hint$3 = "ข้อมูลเขตเวลาจะถูกใช้ในการวิเคราะห์ภายหลัง ขณะนี้ทางเราไม่รองรับฟังก์ชันที่เกี่ยวข้อง โปรดเลือกเขตเวลาที่สามารถดูข้อมูลโดยรวมในศูนย์ธุรกิจได้";
var bc_timezone_placeholder$3 = "เลือกเขตเวลา";
var bc_type_agency$3 = "ตัวแทน";
var th = {
	bc_account_num: bc_account_num$3,
	bc_avartar: bc_avartar$3,
	bc_cancel: bc_cancel$3,
	bc_create: bc_create$3,
	bc_create_title: bc_create_title$3,
	bc_info: bc_info$3,
	bc_member_num: bc_member_num$3,
	bc_name: bc_name$3,
	bc_name_empty_hint: bc_name_empty_hint$3,
	bc_name_length_hint: bc_name_length_hint$3,
	bc_name_placeholder: bc_name_placeholder$3,
	bc_timezone: bc_timezone$3,
	bc_timezone_empty_hint: bc_timezone_empty_hint$3,
	bc_timezone_hint: bc_timezone_hint$3,
	bc_timezone_placeholder: bc_timezone_placeholder$3,
	bc_type_agency: bc_type_agency$3,
	"tz_key_Africa/Accra": "(UTC+00:00) แอฟริกา/อักกรา",
	"tz_key_Africa/Cairo": "(UTC+02:00) แอฟริกา/ไคโร",
	"tz_key_Africa/Casablanca": "(UTC+00:00) แอฟริกา/คาซาบลังกา",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) แอฟริกา/โจฮันเนสเบิร์ก",
	"tz_key_Africa/Lagos": "(UTC+01:00) แอฟริกา/ลากอส",
	"tz_key_Africa/Nairobi": "(UTC+03:00) แอฟริกา/ไนโรบี",
	"tz_key_Africa/Tunis": "(UTC+01:00) แอฟริกา/ตูนิส",
	"tz_key_America/Anchorage": "(UTC-09:00) อเมริกา/แองเคอเรจ",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) อเมริกา/อาร์เจนตินา/บัวโนสไอเรส",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) อเมริกา/อาร์เจนตินา/ซัลตา",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) อเมริกา/อาร์เจนตินา/ซานลุยส์",
	"tz_key_America/Asuncion": "(UTC-04:00) อเมริกา/อะซุนซิออง",
	"tz_key_America/Atikokan": "(UTC-05:00) อเมริกา/อาติโกกัน",
	"tz_key_America/Belem": "(UTC-03:00) อเมริกา/เบเลง",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) อเมริกา/บลองช์ ซาบลอน",
	"tz_key_America/Bogota": "(UTC-05:00) อเมริกา/โบโกตา",
	"tz_key_America/Campo_Grande": "(UTC-04:00) อเมริกา/คัมโปกรันเด",
	"tz_key_America/Caracas": "(UTC-04:30) อเมริกา/การากัส",
	"tz_key_America/Chicago": "(UTC-06:00) อเมริกา/ชิคาโก",
	"tz_key_America/Costa_Rica": "(UTC-06:00) อเมริกา/คอสตาริกา",
	"tz_key_America/Dawson": "(UTC-08:00) อเมริกา/ดอว์สัน",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) อเมริกา/ดอว์สันครีก",
	"tz_key_America/Denver": "(UTC-07:00) อเมริกา/เดนเวอร์",
	"tz_key_America/Edmonton": "(UTC-07:00) อเมริกา/เอดมันตัน",
	"tz_key_America/El_Salvador": "(UTC-06:00) อเมริกา/เอลซัลวาดอร์",
	"tz_key_America/Guatemala": "(UTC-06:00) อเมริกา/กัวเตมาลา",
	"tz_key_America/Guayaquil": "(UTC-05:00) อเมริกา/กัวยากิล",
	"tz_key_America/Halifax": "(UTC-04:00) อเมริกา/แฮลิแฟกซ์",
	"tz_key_America/Hermosillo": "(UTC-07:00) อเมริกา/เออโมซิลโล",
	"tz_key_America/Iqaluit": "(UTC-05:00) อเมริกา/อีคาลูท",
	"tz_key_America/Jamaica": "(UTC-05:00) อเมริกา/จาเมกา",
	"tz_key_America/La_Paz": "(UTC-04:00) อเมริกา/ลาปาซ",
	"tz_key_America/Lima": "(UTC-05:00) อเมริกา/ลิมา",
	"tz_key_America/Los_Angeles": "(UTC-08:00) อเมริกา/ลอสแอนเจลิส",
	"tz_key_America/Managua": "(UTC-06:00) อเมริกา/มานากัว",
	"tz_key_America/Mazatlan": "(UTC-07:00) อเมริกา/มาซัตลาน",
	"tz_key_America/Mexico_City": "(UTC-06:00) อเมริกา/เม็กซิโกซิตี",
	"tz_key_America/Montevideo": "(UTC-03:00) อเมริกา/มอนเตวิเดโอ",
	"tz_key_America/Nassau": "(UTC-05:00) อเมริกา/แนสซอ",
	"tz_key_America/New_York": "(UTC-05:00) อเมริกา/นิวยอร์ก",
	"tz_key_America/Noronha": "(UTC-02:00) อเมริกา/โนรอนฮา",
	"tz_key_America/Panama": "(UTC-05:00) อเมริกา/ปานามา",
	"tz_key_America/Phoenix": "(UTC-07:00) อเมริกา/ฟีนิกซ์",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) อเมริกา/พอร์ตออฟสเปน",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) อเมริกา/เปอร์โตริโก",
	"tz_key_America/Rainy_River": "(UTC-06:00) อเมริกา/เรนนี่ริเวอร์",
	"tz_key_America/Regina": "(UTC-06:00) อเมริกา/เรจิน่า",
	"tz_key_America/Santiago": "(UTC-04:00) อเมริกา/ซันติอาโก",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) อเมริกา/ซันโตโดมิงโก",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) อเมริกา/เซาเปาโล",
	"tz_key_America/St_Johns": "(UTC-03:30) อเมริกา/เซนต์จอห์นส์",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) อเมริกา/เตกูซิกัลปา",
	"tz_key_America/Tijuana": "(UTC-08:00) อเมริกา/ติฮัวนา",
	"tz_key_America/Toronto": "(UTC-05:00) อเมริกา/โตรอนโต",
	"tz_key_America/Vancouver": "(UTC-08:00) อเมริกา/แวนคูเวอร์",
	"tz_key_Asia/Amman": "(UTC+02:00) เอเชีย/อัมมาน",
	"tz_key_Asia/Baghdad": "(UTC+03:00) เอเชีย/แบกแดด",
	"tz_key_Asia/Bahrain": "(UTC+03:00) เอเชีย/บาห์เรน",
	"tz_key_Asia/Bangkok": "(UTC+07:00) เอเชีย/กรุงเทพ",
	"tz_key_Asia/Beirut": "(UTC+02:00) เอเชีย/เบรุต",
	"tz_key_Asia/Colombo": "(UTC+05:30) เอเชีย/โคลัมโบ",
	"tz_key_Asia/Dhaka": "(UTC+06:00) เอเชีย/ธากา",
	"tz_key_Asia/Dubai": "(UTC+04:00) เอเชีย/ดูไบ",
	"tz_key_Asia/Gaza": "(UTC+02:00) เอเชีย/กาซา",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) เอเชีย/โฮจิมินห์",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) เอเชีย/ฮ่องกง",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) เอเชีย/อีร์คุตสค์",
	"tz_key_Asia/Jakarta": "(UTC+07:00) เอเชีย/จาการ์ตา",
	"tz_key_Asia/Jayapura": "(UTC+09:00) เอเชีย/จายาปุระ",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) เอเชีย/เยรูซาเล็ม",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) เอเชีย/คัมชัตคา",
	"tz_key_Asia/Karachi": "(UTC+05:00) เอเชีย/การาจี",
	"tz_key_Asia/Kolkata": "(UTC+05:30) เอเชีย/โกลกาตา",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) เอเชีย/ครัสโนยาสค์",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) เอเชีย/กัวลาลัมเปอร์",
	"tz_key_Asia/Kuwait": "(UTC+03:00) เอเชีย/คูเวต",
	"tz_key_Asia/Magadan": "(UTC+11:00) เอเชีย/มากาดาน",
	"tz_key_Asia/Makassar": "(UTC+08:00) เอเชีย/มากัสซาร์",
	"tz_key_Asia/Manila": "(UTC+08:00) เอเชีย/มะนิลา",
	"tz_key_Asia/Muscat": "(UTC+04:00) เอเชีย/มัสกัต",
	"tz_key_Asia/Nicosia": "(UTC+02:00) เอเชีย/นิโคเซีย",
	"tz_key_Asia/Omsk": "(UTC+06:00) เอเชีย/ออมสค์",
	"tz_key_Asia/Phnom_Penh": "เอเชีย/พนมเปญ",
	"tz_key_Asia/Qatar": "(UTC+03:00) เอเชีย/กาตาร์",
	"tz_key_Asia/Riyadh": "(UTC+03:00) เอเชีย/ริยาด",
	"tz_key_Asia/Seoul": "(UTC+09:00) เอเชีย/โซล",
	"tz_key_Asia/Shanghai": "(UTC+08:00) เอเชีย/ปักกิ่ง",
	"tz_key_Asia/Singapore": "(UTC+08:00) เอเชีย/สิงคโปร์",
	"tz_key_Asia/Taipei": "(UTC+08:00) เอเชีย/ไทเป",
	"tz_key_Asia/Tokyo": "(UTC+09:00) เอเชีย/โตเกียว",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) เอเชีย/วลาดิวอสต็อก",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) เอเชีย/ยาคุตสค์",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) เอเชีย/เยคาเตรินบุร์ก",
	"tz_key_Atlantic/Azores": "(UTC-01:00) แอตแลนติก/อะโซร์ส",
	"tz_key_Atlantic/Canary": "(UTC+00:00) แอตแลนติก/คะเนรี",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) แอตแลนติก/เรคยาวิก",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) ออสเตรเลีย/โบรคเคนฮิลล์",
	"tz_key_Australia/Perth": "(UTC+08:00) ออสเตรเลีย/เพิร์ธ",
	"tz_key_Australia/Sydney": "(UTC+10:00) ออสเตรเลีย/ซิดนีย์",
	"tz_key_Etc/GMT": "(UTC) ลอนดอน",
	"tz_key_Etc/GMT+1": "(UTC-01:00) ปงตาแดลกาดา",
	"tz_key_Etc/GMT+10": "(UTC-10:00) เวลาของโฮโนลูลูฮาวาย",
	"tz_key_Etc/GMT+11": "(UTC-11:00) พาโกพาโก",
	"tz_key_Etc/GMT+12": "(UTC-12:00) มาจูโร",
	"tz_key_Etc/GMT+2": "(UTC-02:00) โนรอนฮา",
	"tz_key_Etc/GMT+3": "(UTC-03:00) เซาเปาโล",
	"tz_key_Etc/GMT+4": "(UTC-04:00) การากัส",
	"tz_key_Etc/GMT+5": "(UTC-05:00) นิวยอร์ก เวลาตะวันออก",
	"tz_key_Etc/GMT+6": "(UTC-06:00) เม็กซิโกซิตี เวลากลาง",
	"tz_key_Etc/GMT+7": "(UTC-07:00) ซอลต์เลกซิตี เวลาแถบภูเขา",
	"tz_key_Etc/GMT+8": "(UTC-08:00) ลอสแอนเจลิส เวลาแปซิฟิก",
	"tz_key_Etc/GMT+9": "(UTC-09:00) จูโน เวลาอะแลสกา",
	"tz_key_Etc/GMT-1": "(UTC+01:00) เบอร์ลิน",
	"tz_key_Etc/GMT-10": "(UTC+10:00) แคนเบอร์รา",
	"tz_key_Etc/GMT-11": "(UTC+11:00) โฮนีอารา",
	"tz_key_Etc/GMT-12": "(UTC+12:00) เวลลิงตัน",
	"tz_key_Etc/GMT-13": "แปซิฟิก/อาปีอา",
	"tz_key_Etc/GMT-2": "(UTC+02:00) เอเธนส์",
	"tz_key_Etc/GMT-3": "(UTC+03:00) มอสโก",
	"tz_key_Etc/GMT-4": "(UTC+04:00) อาบูดาบี",
	"tz_key_Etc/GMT-5": "(UTC+05:00) อิสลามาบัด",
	"tz_key_Etc/GMT-6": "(UTC+06:00) ธากา",
	"tz_key_Etc/GMT-7": "(UTC+07:00) กรุงเทพ",
	"tz_key_Etc/GMT-8": "(UTC+08:00) ปักกิ่ง",
	"tz_key_Etc/GMT-9": "(UTC+09:00) โตเกียว",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) ยุโรป/อัมสเตอร์ดัม",
	"tz_key_Europe/Athens": "(UTC+02:00) ยุโรป/เอเธนส์",
	"tz_key_Europe/Belgrade": "(UTC+01:00) ยุโรป/เบลเกรด",
	"tz_key_Europe/Berlin": "(UTC+01:00) ยุโรป/เบอร์ลิน",
	"tz_key_Europe/Bratislava": "(UTC+01:00) ยุโรป/บราติสลาวา",
	"tz_key_Europe/Brussels": "(UTC+01:00) ยุโรป/บรัสเซลส์",
	"tz_key_Europe/Bucharest": "(UTC+02:00) ยุโรป/บูคาเรสต์",
	"tz_key_Europe/Budapest": "(UTC+01:00) ยุโรป/บูดาเปสต์",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) ยุโรป/โคเปนเฮเกน",
	"tz_key_Europe/Dublin": "(UTC+00:00) ยุโรป/ดับลิน",
	"tz_key_Europe/Helsinki": "(UTC+02:00) ยุโรป/เฮลซิงกิ",
	"tz_key_Europe/Istanbul": "(UTC+02:00) ยุโรป/อิสตันบูล",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) ยุโรป/คาลินินกราด",
	"tz_key_Europe/Kiev": "(UTC+02:00) ยุโรป/เคียฟ",
	"tz_key_Europe/Lisbon": "(UTC+00:00) ยุโรป/ลิสบอน",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) ยุโรป/ลูบลิยานา",
	"tz_key_Europe/London": "(UTC+00:00) ยุโรป/ลอนดอน",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) ยุโรป/ลักเซมเบิร์ก",
	"tz_key_Europe/Madrid": "(UTC+01:00) ยุโรป/มาดริด",
	"tz_key_Europe/Malta": "(UTC+01:00) ยุโรป/มอลตา",
	"tz_key_Europe/Moscow": "(UTC+03:00) ยุโรป/มอสโก",
	"tz_key_Europe/Oslo": "(UTC+01:00) ยุโรป/ออสโล",
	"tz_key_Europe/Paris": "(UTC+01:00) ยุโรป/ปารีส",
	"tz_key_Europe/Prague": "(UTC+01:00) ยุโรป/ปราก",
	"tz_key_Europe/Riga": "(UTC+02:00) ยุโรป/ริกา",
	"tz_key_Europe/Rome": "(UTC+01:00) ยุโรป/โรม",
	"tz_key_Europe/Samara": "(UTC+03:00) ยุโรป/ซามารา",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) ยุโรป/ซาราเจโว",
	"tz_key_Europe/Skopje": "(UTC+01:00) ยุโรป/สโกเปีย",
	"tz_key_Europe/Sofia": "(UTC+02:00) ยุโรป/โซเฟีย",
	"tz_key_Europe/Stockholm": "(UTC+01:00) ยุโรป/สตอกโฮล์ม",
	"tz_key_Europe/Tallinn": "(UTC+02:00) ยุโรป/ทาลลินน์",
	"tz_key_Europe/Vienna": "(UTC+01:00) ยุโรป/เวียนนา",
	"tz_key_Europe/Vilnius": "(UTC+02:00) ยุโรป/วิลนีอุส",
	"tz_key_Europe/Warsaw": "(UTC+01:00) ยุโรป/วอร์ซอว์",
	"tz_key_Europe/Zagreb": "(UTC+01:00) ยุโรป/ซาเกร็บ",
	"tz_key_Europe/Zurich": "(UTC+01:00) ยุโรป/ซูริค",
	"tz_key_Indian/Maldives": "(UTC+05:00) อินเดีย/มัลดีฟส์",
	"tz_key_Indian/Mauritius": "(UTC+04:00) อินเดีย/มอริเชียส",
	"tz_key_Pacific/Apia": "(UTC-11:00) แปซิฟิก/อาปีอา",
	"tz_key_Pacific/Auckland": "(UTC+12:00) แปซิฟิก/โอ๊คแลนด์",
	"tz_key_Pacific/Easter": "(UTC-06:00) แปซิฟิก/อีสเตอร์",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) แปซิฟิก/กาลาปากอส",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) แปซิฟิก/โฮโนลูลู",
	"tz_key_Pacific/Majuro": "(UTC-12:00) แปซิฟิก/มาจูโร",
	"tz_key_Pacific/Midway": "(UTC-11:00) แปซิฟิก/มิดเวย์",
	"tz_key_Pacific/Niue": "(UTC-11:00) แปซิฟิก/นีอูเอ",
	"tz_key_Pacific/Pago Pago": "แปซิฟิก/พาโกพาโก",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) แปซิฟิก/พาโกพาโก"
};

var bc_account_num$2 = "Hesap Miktarı: {num}";
var bc_avartar$2 = "Profil Resmi";
var bc_cancel$2 = "İptal";
var bc_create$2 = "Oluştur";
var bc_create_title$2 = "İş Merkezi Oluştur";
var bc_info$2 = "İş Merkezi’nde reklam hesaplarınızı, merkez üyelerini ve onların izin durumlarını yönetin. Hepsi Bir Arada ve Ücretsiz.";
var bc_member_num$2 = "Üye Miktarı: {num}";
var bc_name$2 = "İş Merkezi Adı";
var bc_name_empty_hint$2 = "Lütfen İş Merkezi adını girin.";
var bc_name_length_hint$2 = "İş Merkezi adı 200 karakteri geçemez.";
var bc_name_placeholder$2 = "Lütfen İş Merkezi adını girin.";
var bc_timezone$2 = "Saat dilimi";
var bc_timezone_empty_hint$2 = "Lütfen saat dilimi seçin.";
var bc_timezone_hint$2 = "Saat dilimi verisi aşama sonrası analizinde kullanılır. Şu anda mevcut fonksiyonları desteklemiyoruz. İş Merkezi’nde genel veri görünümü için lütfen saat dilimini seçin.";
var bc_timezone_placeholder$2 = "Saat dilimi seçin";
var bc_type_agency$2 = "Ajans";
var tr = {
	bc_account_num: bc_account_num$2,
	bc_avartar: bc_avartar$2,
	bc_cancel: bc_cancel$2,
	bc_create: bc_create$2,
	bc_create_title: bc_create_title$2,
	bc_info: bc_info$2,
	bc_member_num: bc_member_num$2,
	bc_name: bc_name$2,
	bc_name_empty_hint: bc_name_empty_hint$2,
	bc_name_length_hint: bc_name_length_hint$2,
	bc_name_placeholder: bc_name_placeholder$2,
	bc_timezone: bc_timezone$2,
	bc_timezone_empty_hint: bc_timezone_empty_hint$2,
	bc_timezone_hint: bc_timezone_hint$2,
	bc_timezone_placeholder: bc_timezone_placeholder$2,
	bc_type_agency: bc_type_agency$2,
	"tz_key_Africa/Accra": "(UTC+00.00) Asya/Akra",
	"tz_key_Africa/Cairo": "(UTC+02.00) Asya/Kahire",
	"tz_key_Africa/Casablanca": "(UTC+00.00) Asya/Kazablanka",
	"tz_key_Africa/Johannesburg": "(UTC+02.00) Afrika/Yohanesburg",
	"tz_key_Africa/Lagos": "(UTC+01:00) Afrika/Lagos",
	"tz_key_Africa/Nairobi": "(UTC+03.00) Asya/Nairobi",
	"tz_key_Africa/Tunis": "(UTC+01.00) Afrika/Tunus",
	"tz_key_America/Anchorage": "(UTC-09.00) Amerika/Anchorage",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03.00) Amerika/Arjantin/Buenos_Aires",
	"tz_key_America/Argentina/Salta": "(UTC-03.00) Amerika/Arjantin/Salta",
	"tz_key_America/Argentina/San_Luis": "(UTC-03.00) Amerika/Arjantin/San_Luis",
	"tz_key_America/Asuncion": "(UTC-04.00) Amerika/Asuncion",
	"tz_key_America/Atikokan": "(UTC-05.00) Amerika/Atikokan",
	"tz_key_America/Belem": "(UTC-03.00) Amerika/Belem",
	"tz_key_America/Blanc-Sablon": "(UTC-04.00) Amerika/Blanc-Sablon",
	"tz_key_America/Bogota": "(UTC-05.00) Amerika/Bogota",
	"tz_key_America/Campo_Grande": "(UTC-04.00) Amerika/Campo_Grande",
	"tz_key_America/Caracas": "(UTC-04.30) Amerika/Karakas",
	"tz_key_America/Chicago": "(UTC-06.00) Amerika/Chicago",
	"tz_key_America/Costa_Rica": "(UTC-06.00) Amerika/Kosta_Rika",
	"tz_key_America/Dawson": "(UTC-08.00) Amerika/Dawson",
	"tz_key_America/Dawson_Creek": "(UTC-07.00) Amerika/Dawson_Creek",
	"tz_key_America/Denver": "(UTC-07.00) Amerika/Denver",
	"tz_key_America/Edmonton": "(UTC-07.00) Amerika/Edmonton",
	"tz_key_America/El_Salvador": "(UTC-06.00) Amerika/El_Salvador",
	"tz_key_America/Guatemala": "(UTC-06.00) Amerika/Guatemala",
	"tz_key_America/Guayaquil": "(UTC-05.00) Amerika/Guayaquil",
	"tz_key_America/Halifax": "(UTC-04.00) Amerika/Halifax",
	"tz_key_America/Hermosillo": "(UTC-07.00) Amerika/Hermosillo",
	"tz_key_America/Iqaluit": "(UTC-05.00) Amerika/Iqaluit",
	"tz_key_America/Jamaica": "(UTC-05.00) Amerika/Jamaika",
	"tz_key_America/La_Paz": "(UTC-04.00) Amerika/La Paz",
	"tz_key_America/Lima": "(UTC-05.00) Amerika/Lima",
	"tz_key_America/Los_Angeles": "(UTC-08.00) Amerika/Los_Angeles",
	"tz_key_America/Managua": "(UTC-06.00) Amerika/Managua",
	"tz_key_America/Mazatlan": "(UTC-07.00) Amerika/Mazatlan",
	"tz_key_America/Mexico_City": "(UTC-06.00) Amerika/Meksiko",
	"tz_key_America/Montevideo": "(UTC-03.00) Amerika/Montevideo",
	"tz_key_America/Nassau": "(UTC-05.00) Amerika/Nassau",
	"tz_key_America/New_York": "(UTC-05.00) Amerika/New_York",
	"tz_key_America/Noronha": "(UTC-02.00) Amerika/Noronha",
	"tz_key_America/Panama": "(UTC-05.00) Amerika/Panama",
	"tz_key_America/Phoenix": "(UTC-07.00) Amerika/Phoenix",
	"tz_key_America/Port_of_Spain": "(UTC-04.00) Amerika/Port_of_Spain",
	"tz_key_America/Puerto_Rico": "(UTC-04.00) Amerika/Porto_Riko",
	"tz_key_America/Rainy_River": "(UTC-06.00) Amerika/Rainy_River",
	"tz_key_America/Regina": "(UTC-06.00) Amerika/Regina",
	"tz_key_America/Santiago": "(UTC-04.00) Amerika/Santiago",
	"tz_key_America/Santo_Domingo": "(UTC-04.00) Amerika/Santo_Domingo",
	"tz_key_America/Sao_Paulo": "(UTC-03.00) Amerika/Sao_Paulo",
	"tz_key_America/St_Johns": "(UTC-03.30) Amerika/St_Johns",
	"tz_key_America/Tegucigalpa": "(UTC-06.00) Amerika/Tegucigalpa",
	"tz_key_America/Tijuana": "(UTC-08.00) Amerika/Tihuana",
	"tz_key_America/Toronto": "(UTC-05.00) Amerika/Toronto",
	"tz_key_America/Vancouver": "(UTC-08.00) Amerika/Vancouver",
	"tz_key_Asia/Amman": "(UTC+02.00) Asya/Amman",
	"tz_key_Asia/Baghdad": "(UTC+03.00) Asya/Bağdat",
	"tz_key_Asia/Bahrain": "(UTC+03.00) Asya/Bahreyn",
	"tz_key_Asia/Bangkok": "(UTC+07.00) Asya/Bangkok",
	"tz_key_Asia/Beirut": "(UTC+02.00) Asya/Beyrut",
	"tz_key_Asia/Colombo": "(UTC+05.30) Asya/Colombo",
	"tz_key_Asia/Dhaka": "(UTC+06.00) Asya/Dakka",
	"tz_key_Asia/Dubai": "(UTC+04.00) Asya/Dubai",
	"tz_key_Asia/Gaza": "(UTC+02.00) Asya/Gaza",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07.00) Asya/Ho_Chi_Minh",
	"tz_key_Asia/Hong_Kong": "(UTC+08.00) Asya/Hong Kong",
	"tz_key_Asia/Irkutsk": "(UTC+08.00) Asya/Irkutsk",
	"tz_key_Asia/Jakarta": "(UTC+07.00) Asya/Jakarta",
	"tz_key_Asia/Jayapura": "(UTC+09.00) Asya/Jayapura",
	"tz_key_Asia/Jerusalem": "(UTC+02.00) Asya/Kudüs",
	"tz_key_Asia/Kamchatka": "(UTC+11.00) Asya/Kamchatka",
	"tz_key_Asia/Karachi": "(UTC+05.00) Asya/Karachi",
	"tz_key_Asia/Kolkata": "(UTC+05.30) Asya/Kolkata",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07.00) Asya/Krasnoyarsk",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08.00) Asya/Kuala_Lumpur",
	"tz_key_Asia/Kuwait": "(UTC+03.00) Asya/Kuveyt",
	"tz_key_Asia/Magadan": "(UTC+11.00) Asya/Magadan",
	"tz_key_Asia/Makassar": "(UTC+08.00) Asya/Makassar",
	"tz_key_Asia/Manila": "(UTC+08.00) Asya/Manila",
	"tz_key_Asia/Muscat": "(UTC+04.00) Asya/Muscat",
	"tz_key_Asia/Nicosia": "(UTC+02.00) Asya/Lefkoşa",
	"tz_key_Asia/Omsk": "(UTC+06.00) Asya/Omsk",
	"tz_key_Asia/Phnom_Penh": "Asya/Punom Pen",
	"tz_key_Asia/Qatar": "(UTC+03.00) Asya/Katar",
	"tz_key_Asia/Riyadh": "(UTC+03.00) Asya/Riyad",
	"tz_key_Asia/Seoul": "(UTC+09.00) Asya/Seul",
	"tz_key_Asia/Shanghai": "(UTC+08.00) Asya/Pekin",
	"tz_key_Asia/Singapore": "(UTC+08.00) Asya/Singapur",
	"tz_key_Asia/Taipei": "(UTC+08.00) Asya/Taipei",
	"tz_key_Asia/Tokyo": "(UTC+09.00) Asya/Tokyo",
	"tz_key_Asia/Vladivostok": "(UTC+10.00) Asya/Vladivostok",
	"tz_key_Asia/Yakutsk": "(UTC+09.00) Asya/Yakutsk",
	"tz_key_Asia/Yekaterinburg": "(UTC+05.00) Asya/Yekaterinburg",
	"tz_key_Atlantic/Azores": "(UTC-01.00) Atlas/Azores",
	"tz_key_Atlantic/Canary": "(UTC+00.00) Asya/Kanarya",
	"tz_key_Atlantic/Reykjavik": "(UTC+00.00) Asya/Reykjavik",
	"tz_key_Australia/Broken_Hill": "(UTC+09.30) Avustralya/Broken_Hill",
	"tz_key_Australia/Perth": "(UTC+08.00) Avustralya/Perth",
	"tz_key_Australia/Sydney": "(UTC+10.00) Avustralya/Sidney",
	"tz_key_Etc/GMT": "(UTC) Londra",
	"tz_key_Etc/GMT+1": "(UTC-01.00) Ponta Delgada",
	"tz_key_Etc/GMT+10": "(UTC-10.00) Honolulu_Hawaii Zamanı",
	"tz_key_Etc/GMT+11": "(UTC-11.00) Pago Pago",
	"tz_key_Etc/GMT+12": "(UTC-12.00) Majuro",
	"tz_key_Etc/GMT+2": "(UTC-02.00) Noronha",
	"tz_key_Etc/GMT+3": "(UTC-03.00) Sao Paulo",
	"tz_key_Etc/GMT+4": "(UTC-04.00) Karakas",
	"tz_key_Etc/GMT+5": "(UTC-05.00) New York_Doğu_Standart_Zamanı",
	"tz_key_Etc/GMT+6": "(UTC-06.00) Meksiko_Merkezi_Standart_Zamanı",
	"tz_key_Etc/GMT+7": "(UTC-07.00) Salt Lake City_Sıradağlar_Standart_Zamanı",
	"tz_key_Etc/GMT+8": "(UTC-08.00) Los Angeles_Pasifik_Standart_Zamanı",
	"tz_key_Etc/GMT+9": "(UTC-09.00) Juno_Alaska_Standart_Zamanı",
	"tz_key_Etc/GMT-1": "(UTC+01.00) Berlin",
	"tz_key_Etc/GMT-10": "(UTC+10.00) Canberra",
	"tz_key_Etc/GMT-11": "(UTC+11:00) Honiara",
	"tz_key_Etc/GMT-12": "(UTC+12:00) Wellington",
	"tz_key_Etc/GMT-13": "Pasifik/Apia",
	"tz_key_Etc/GMT-2": "(UTC+02.00) Atina",
	"tz_key_Etc/GMT-3": "(UTC+03.00) Moskova",
	"tz_key_Etc/GMT-4": "(UTC+04.00) Abu Dabi",
	"tz_key_Etc/GMT-5": "(UTC+05:00) İslamabad",
	"tz_key_Etc/GMT-6": "(UTC+06.00) Dakka",
	"tz_key_Etc/GMT-7": "(UTC+07.00) Bangkok",
	"tz_key_Etc/GMT-8": "(UTC+08.00) Pekin",
	"tz_key_Etc/GMT-9": "(UTC+09.00) Tokyo",
	"tz_key_Europe/Amsterdam": "(UTC+01.00) Avrupa/Amsterdam",
	"tz_key_Europe/Athens": "(UTC+02.00) Avrupa/Atina",
	"tz_key_Europe/Belgrade": "(UTC+01.00) Avrupa/Belgrad",
	"tz_key_Europe/Berlin": "(UTC+01.00) Avrupa/Berlin",
	"tz_key_Europe/Bratislava": "(UTC+01.00) Avrupa/Bratislava",
	"tz_key_Europe/Brussels": "(UTC+01.00) Avrupa/Brüksel",
	"tz_key_Europe/Bucharest": "(UTC+02.00) Avrupa/Bükreş",
	"tz_key_Europe/Budapest": "(UTC+01.00) Avrupa/Budapeşte",
	"tz_key_Europe/Copenhagen": "(UTC+01.00) Avrupa/Kopenhag",
	"tz_key_Europe/Dublin": "(UTC+00.00) Avrupa/Dublin",
	"tz_key_Europe/Helsinki": "(UTC+02.00) Avrupa/Helsinki",
	"tz_key_Europe/Istanbul": "(UTC+02.00) Avrupa/İstanbul",
	"tz_key_Europe/Kaliningrad": "(UTC+02.00) Avrupa/Kaliningrad",
	"tz_key_Europe/Kiev": "(UTC+02.00) Avrupa/Kiev",
	"tz_key_Europe/Lisbon": "(UTC+00.00) Avrupa/Lizbon",
	"tz_key_Europe/Ljubljana": "(UTC+01.00) Avrupa/Ljubljana",
	"tz_key_Europe/London": "(UTC+00.00) Avrupa/Londra",
	"tz_key_Europe/Luxembourg": "(UTC+01.00) Avrupa/Lüksemburg",
	"tz_key_Europe/Madrid": "(UTC+01.00) Avrupa/Madrid",
	"tz_key_Europe/Malta": "(UTC+01.00) Avrupa/Malta",
	"tz_key_Europe/Moscow": "(UTC+03.00) Avrupa/Moskova",
	"tz_key_Europe/Oslo": "(UTC+01.00) Avrupa/Oslo",
	"tz_key_Europe/Paris": "(UTC+01.00) Avrupa/Paris",
	"tz_key_Europe/Prague": "(UTC+01.00) Avrupa/Prag",
	"tz_key_Europe/Riga": "(UTC+02.00) Avrupa/Riga",
	"tz_key_Europe/Rome": "(UTC+01.00) Avrupa/Roma",
	"tz_key_Europe/Samara": "(UTC+03.00) Avrupa/Samara",
	"tz_key_Europe/Sarajevo": "(UTC+01.00) Avrupa/Saraybosna",
	"tz_key_Europe/Skopje": "(UTC+01.00) Avrupa/Üsküp",
	"tz_key_Europe/Sofia": "(UTC+02.00) Avrupa/Sofya",
	"tz_key_Europe/Stockholm": "(UTC+01.00) Avrupa/Stockholm",
	"tz_key_Europe/Tallinn": "(UTC+02.00) Avrupa/Talin",
	"tz_key_Europe/Vienna": "(UTC+01.00) Avrupa/Viyana",
	"tz_key_Europe/Vilnius": "(UTC+02.00) Avrupa/Vilnius",
	"tz_key_Europe/Warsaw": "(UTC+01.00) Avrupa/Varşova",
	"tz_key_Europe/Zagreb": "(UTC+01.00) Avrupa/Zagreb",
	"tz_key_Europe/Zurich": "(UTC+01.00) Avrupa/Zürih",
	"tz_key_Indian/Maldives": "(UTC+05.00) Hindistan/Maldives",
	"tz_key_Indian/Mauritius": "(UTC+04.00) Hindistan/Mauritius",
	"tz_key_Pacific/Apia": "(UTC-11.00) Pasifik/Apia",
	"tz_key_Pacific/Auckland": "(UTC+12.00) Pasifik/Auckland",
	"tz_key_Pacific/Easter": "(UTC-06.00) Pasifik/Easter",
	"tz_key_Pacific/Galapagos": "(UTC-06.00) Pasifik/Galapagos",
	"tz_key_Pacific/Honolulu": "(UTC-10.00) Pasifik/Honolulu",
	"tz_key_Pacific/Majuro": "(UTC-12.00) Pasifik/Majuro",
	"tz_key_Pacific/Midway": "(UTC-11.00) Pasifik/Midway",
	"tz_key_Pacific/Niue": "(UTC-11.00) Pasifik/Niue",
	"tz_key_Pacific/Pago Pago": "Pasifik/Pago Pago",
	"tz_key_Pacific/Pago_Pago": "(UTC-11.00) Pasifik/Pago Pago"
};

var bc_account_num$1 = "Số tiền tài khoản: {num}";
var bc_avartar$1 = "Ảnh hồ sơ";
var bc_cancel$1 = "Hủy";
var bc_create$1 = "Tạo";
var bc_create_title$1 = "Tạo Trung tâm doanh nghiệp";
var bc_info$1 = "Quản lý tài khoản quảng cáo, thành viên trung tâm và trạng thái quyền của họ trong Trung tâm doanh nghiệp. Tất cả trong một và miễn phí.";
var bc_member_num$1 = "Số tiền thành viên: {num}";
var bc_name$1 = "Tên Trung tâm doanh nghiệp";
var bc_name_empty_hint$1 = "Vui lòng điền tên Trung tâm doanh nghiệp.";
var bc_name_length_hint$1 = "Tên Trung tâm doanh nghiệp không được vượt quá 200 ký tự.";
var bc_name_placeholder$1 = "Vui lòng điền tên Trung tâm doanh nghiệp.";
var bc_timezone$1 = "Múi giờ";
var bc_timezone_empty_hint$1 = "Vui lòng chọn múi giờ.";
var bc_timezone_hint$1 = "Dữ liệu múi giờ sẽ được sử dụng trong khâu phân tích về sau. Hiện chúng tôi không hỗ trợ các chức năng liên quan. Vui lòng chọn múi giờ để xem dữ liệu chung trong Trung tâm doanh nghiệp.";
var bc_timezone_placeholder$1 = "Chọn múi giờ";
var bc_type_agency$1 = "Đại lý";
var vi = {
	bc_account_num: bc_account_num$1,
	bc_avartar: bc_avartar$1,
	bc_cancel: bc_cancel$1,
	bc_create: bc_create$1,
	bc_create_title: bc_create_title$1,
	bc_info: bc_info$1,
	bc_member_num: bc_member_num$1,
	bc_name: bc_name$1,
	bc_name_empty_hint: bc_name_empty_hint$1,
	bc_name_length_hint: bc_name_length_hint$1,
	bc_name_placeholder: bc_name_placeholder$1,
	bc_timezone: bc_timezone$1,
	bc_timezone_empty_hint: bc_timezone_empty_hint$1,
	bc_timezone_hint: bc_timezone_hint$1,
	bc_timezone_placeholder: bc_timezone_placeholder$1,
	bc_type_agency: bc_type_agency$1,
	"tz_key_Africa/Accra": "(UTC+00:00) Châu Phi/Accra",
	"tz_key_Africa/Cairo": "(UTC+02:00) Châu Phi/Cairo",
	"tz_key_Africa/Casablanca": "(UTC+00:00) Châu Phi/Casablanca",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) Châu Phi/Johannesburg",
	"tz_key_Africa/Lagos": "(UTC+01:00) Châu Phi/Lagos",
	"tz_key_Africa/Nairobi": "(UTC+03:00) Châu Phi/Nairobi",
	"tz_key_Africa/Tunis": "(UTC+01:00) Châu Phi/Tunis",
	"tz_key_America/Anchorage": "(UTC-09:00) Châu Mỹ/Anchorage",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) Châu Mỹ/Argentina/Buenos Aires",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) Châu Mỹ/Argentina/Salta",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) Châu Mỹ/Argentina/San Luis",
	"tz_key_America/Asuncion": "(UTC-04:00) Châu Mỹ/Asuncion",
	"tz_key_America/Atikokan": "(UTC-05:00) Châu Mỹ/Atikokan",
	"tz_key_America/Belem": "(UTC-03:00) Châu Mỹ/Belem",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) Châu Mỹ/Blanc-Sablon",
	"tz_key_America/Bogota": "(UTC-05:00) Châu Mỹ/Bogota",
	"tz_key_America/Campo_Grande": "(UTC-04:00) Châu Mỹ/Campo Grande",
	"tz_key_America/Caracas": "(UTC-04:30) Châu Mỹ/Caracas",
	"tz_key_America/Chicago": "(UTC-06:00) Châu Mỹ/Chicago",
	"tz_key_America/Costa_Rica": "(UTC-06:00) Châu Mỹ/Costa Rica",
	"tz_key_America/Dawson": "(UTC-08:00) Châu Mỹ/Dawson",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) Châu Mỹ/Dawson Creek",
	"tz_key_America/Denver": "(UTC-07:00) Châu Mỹ/Denver",
	"tz_key_America/Edmonton": "(UTC-07:00) Châu Mỹ/Edmonton",
	"tz_key_America/El_Salvador": "(UTC-06:00) Châu Mỹ/El Salvador",
	"tz_key_America/Guatemala": "(UTC-06:00) Châu Mỹ/Guatemala",
	"tz_key_America/Guayaquil": "(UTC-05:00) Châu Mỹ/Guayaquil",
	"tz_key_America/Halifax": "(UTC-04:00) Châu Mỹ/Halifax",
	"tz_key_America/Hermosillo": "(UTC-07:00) Châu Mỹ/Hermosillo",
	"tz_key_America/Iqaluit": "(UTC-05:00) Châu Mỹ/Iqaluit",
	"tz_key_America/Jamaica": "(UTC-05:00) Châu Mỹ/Jamaica",
	"tz_key_America/La_Paz": "(UTC-04:00) Châu Mỹ/La Paz",
	"tz_key_America/Lima": "(UTC-05:00) Châu Mỹ/Lima",
	"tz_key_America/Los_Angeles": "(UTC-08:00) Châu Mỹ/Los Angeles",
	"tz_key_America/Managua": "(UTC-06:00) Châu Mỹ/Managua",
	"tz_key_America/Mazatlan": "(UTC-07:00) Châu Mỹ/Mazatlan",
	"tz_key_America/Mexico_City": "(UTC-06:00) Châu Mỹ/Mexico City",
	"tz_key_America/Montevideo": "(UTC-03:00) Châu Mỹ/Montevideo",
	"tz_key_America/Nassau": "(UTC-05:00) Châu Mỹ/Nassau",
	"tz_key_America/New_York": "(UTC-05:00) Châu Mỹ/New York",
	"tz_key_America/Noronha": "(UTC-02:00) Châu Mỹ/Noronha",
	"tz_key_America/Panama": "(UTC-05:00) Châu Mỹ/Panama",
	"tz_key_America/Phoenix": "(UTC-07:00) Châu Mỹ/Phoenix",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) Châu Mỹ/Port of Spain",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) Châu Mỹ/Puerto Rico",
	"tz_key_America/Rainy_River": "(UTC-06:00) Châu Mỹ/Rainy River",
	"tz_key_America/Regina": "(UTC-06:00) Châu Mỹ/Regina",
	"tz_key_America/Santiago": "(UTC-04:00) Châu Mỹ/Santiago",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) Châu Mỹ/Santo Domingo",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) Châu Mỹ/Sao Paulo",
	"tz_key_America/St_Johns": "(UTC-03:30) Châu Mỹ/St Johns",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) Châu Mỹ/Tegucigalpa",
	"tz_key_America/Tijuana": "(UTC-08:00) Châu Mỹ/Tijuana",
	"tz_key_America/Toronto": "(UTC-05:00) Châu Mỹ/Toronto",
	"tz_key_America/Vancouver": "(UTC-08:00) Châu Mỹ/Vancouver",
	"tz_key_Asia/Amman": "(UTC+02:00) Châu Á/Amman",
	"tz_key_Asia/Baghdad": "(UTC+03:00) Châu Á/Baghdad",
	"tz_key_Asia/Bahrain": "(UTC+03:00) Châu Á/Bahrain",
	"tz_key_Asia/Bangkok": "(UTC+07:00) Châu Á/Bangkok",
	"tz_key_Asia/Beirut": "(UTC+02:00) Châu Á/Beirut",
	"tz_key_Asia/Colombo": "(UTC+05:30) Châu Á/Colombo",
	"tz_key_Asia/Dhaka": "(UTC+06:00) Châu Á/Dhaka",
	"tz_key_Asia/Dubai": "(UTC+04:00) Châu Á/Dubai",
	"tz_key_Asia/Gaza": "(UTC+02:00) Châu Á/Gaza",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) Châu Á/Ho Chi Minh",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) Châu Á/Hong Kong",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) Châu Á/Irkutsk",
	"tz_key_Asia/Jakarta": "(UTC+07:00) Châu Á/Jakarta",
	"tz_key_Asia/Jayapura": "(UTC+09:00) Châu Á/Jayapura",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) Châu Á/Jerusalem",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) Châu Á/Kamchatka",
	"tz_key_Asia/Karachi": "(UTC+05:00) Châu Á/Karachi",
	"tz_key_Asia/Kolkata": "(UTC+05:30) Châu Á/Kolkata",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) Châu Á/Krasnoyarsk",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) Châu Á/Kuala Lumpur",
	"tz_key_Asia/Kuwait": "(UTC+03:00) Châu Á/Kuwait",
	"tz_key_Asia/Magadan": "(UTC+11:00) Châu Á/Magadan",
	"tz_key_Asia/Makassar": "(UTC+08:00) Châu Á/Makassar",
	"tz_key_Asia/Manila": "(UTC+08:00) Châu Á/Manila",
	"tz_key_Asia/Muscat": "(UTC+04:00) Châu Á/Muscat",
	"tz_key_Asia/Nicosia": "(UTC+02:00) Châu Á/Nicosia",
	"tz_key_Asia/Omsk": "(UTC+06:00) Châu Á/Omsk",
	"tz_key_Asia/Phnom_Penh": "Châu Á/Phnôm Pênh",
	"tz_key_Asia/Qatar": "(UTC+03:00) Châu Á/Qatar",
	"tz_key_Asia/Riyadh": "(UTC+03:00) Châu Á/Riyadh",
	"tz_key_Asia/Seoul": "(UTC+09:00) Châu Á/Seoul",
	"tz_key_Asia/Shanghai": "(UTC+08:00) Châu Á/Bắc Kinh",
	"tz_key_Asia/Singapore": "(UTC+08:00) Châu Á/Singapore",
	"tz_key_Asia/Taipei": "(UTC+08:00) Châu Á/Đài Bắc",
	"tz_key_Asia/Tokyo": "(UTC+09:00) Châu Á/Tokyo",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) Châu Á/Vladivostok",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) Châu Á/Yakutsk",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) Châu Á/Yekaterinburg",
	"tz_key_Atlantic/Azores": "(UTC-01:00) Đại Tây Dương/Azores",
	"tz_key_Atlantic/Canary": "(UTC+00:00) Đại Tây Dương/Canary",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) Đại Tây Dương/Reykjavik",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) Châu Úc/Broken Hill",
	"tz_key_Australia/Perth": "(UTC+08:00) Châu Úc/Perth",
	"tz_key_Australia/Sydney": "(UTC+10:00) Châu Úc/Sydney",
	"tz_key_Etc/GMT": "(UTC) London",
	"tz_key_Etc/GMT+1": "(UTC-01:00) Ponta Delgada",
	"tz_key_Etc/GMT+10": "(UTC-10:00) Giờ Honolulu Hawaii",
	"tz_key_Etc/GMT+11": "(UTC-11:00) Pago Pago",
	"tz_key_Etc/GMT+12": "(UTC-12:00) Majuro",
	"tz_key_Etc/GMT+2": "(UTC-02:00) Noronha",
	"tz_key_Etc/GMT+3": "(UTC-03:00) Sao Paulo",
	"tz_key_Etc/GMT+4": "(UTC-04:00) Caracas",
	"tz_key_Etc/GMT+5": "(UTC-05:00) Múi giờ miền Đông New York",
	"tz_key_Etc/GMT+6": "(UTC-06:00) Múi giờ miền Trung Mexico City",
	"tz_key_Etc/GMT+7": "(UTC-07:00) Múi giờ miền núi Salt Lake City",
	"tz_key_Etc/GMT+8": "(UTC-08:00) Múi giờ Thái Bình Dương Los Angeles",
	"tz_key_Etc/GMT+9": "(UTC-09:00) Giờ Juno Alaska",
	"tz_key_Etc/GMT-1": "(UTC+01:00) Berlin",
	"tz_key_Etc/GMT-10": "(UTC+10:00) Canberra",
	"tz_key_Etc/GMT-11": "(UTC+11:00) Honiara",
	"tz_key_Etc/GMT-12": "(UTC+12:00) Wellington",
	"tz_key_Etc/GMT-13": "Thái Bình Dương/Apia",
	"tz_key_Etc/GMT-2": "(UTC+02:00) Athens",
	"tz_key_Etc/GMT-3": "(UTC+03:00) Moscow",
	"tz_key_Etc/GMT-4": "(UTC+04:00) Abu Dhabi",
	"tz_key_Etc/GMT-5": "(UTC+05:00) Islamabad",
	"tz_key_Etc/GMT-6": "(UTC+06:00) Dhaka",
	"tz_key_Etc/GMT-7": "(UTC+07:00) Bangkok",
	"tz_key_Etc/GMT-8": "(UTC+08:00) Bắc Kinh",
	"tz_key_Etc/GMT-9": "(UTC+09:00) Tokyo",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) Châu Âu/Amsterdam",
	"tz_key_Europe/Athens": "(UTC+02:00) Châu Âu/Athens",
	"tz_key_Europe/Belgrade": "(UTC+01:00) Châu Âu/Belgrade",
	"tz_key_Europe/Berlin": "(UTC+01:00) Châu Âu/Berlin",
	"tz_key_Europe/Bratislava": "(UTC+01:00) Châu Âu/Bratislava",
	"tz_key_Europe/Brussels": "(UTC+01:00) Châu Âu/Brussels",
	"tz_key_Europe/Bucharest": "(UTC+02:00) Châu Âu/Bucharest",
	"tz_key_Europe/Budapest": "(UTC+01:00) Châu Âu/Budapest",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) Châu Âu/Copenhagen",
	"tz_key_Europe/Dublin": "(UTC+00:00) Châu Âu/Dublin",
	"tz_key_Europe/Helsinki": "(UTC+02:00) Châu Âu/Helsinki",
	"tz_key_Europe/Istanbul": "(UTC+02:00) Châu Âu/Istanbul",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) Châu Âu/Kaliningrad",
	"tz_key_Europe/Kiev": "(UTC+02:00) Châu Âu/Kiev",
	"tz_key_Europe/Lisbon": "(UTC+00:00) Châu Âu/Lisbon",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) Châu Âu/Ljubljana",
	"tz_key_Europe/London": "(UTC+00:00) Châu Âu/London",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) Châu Âu/Luxembourg",
	"tz_key_Europe/Madrid": "(UTC+01:00) Châu Âu/Madrid",
	"tz_key_Europe/Malta": "(UTC+01:00) Châu Âu/Malta",
	"tz_key_Europe/Moscow": "(UTC+03:00) Châu Âu/Moscow",
	"tz_key_Europe/Oslo": "(UTC+01:00) Châu Âu/Oslo",
	"tz_key_Europe/Paris": "(UTC+01:00) Châu Âu/Paris",
	"tz_key_Europe/Prague": "(UTC+01:00) Châu Âu/Prague",
	"tz_key_Europe/Riga": "(UTC+02:00) Châu Âu/Riga",
	"tz_key_Europe/Rome": "(UTC+01:00) Châu Âu/Rome",
	"tz_key_Europe/Samara": "(UTC+03:00) Châu Âu/Samara",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) Châu Âu/Sarajevo",
	"tz_key_Europe/Skopje": "(UTC+01:00) Châu Âu/Skopje",
	"tz_key_Europe/Sofia": "(UTC+02:00) Châu Âu/Sofia",
	"tz_key_Europe/Stockholm": "(UTC+01:00) Châu Âu/Stockholm",
	"tz_key_Europe/Tallinn": "(UTC+02:00) Châu Âu/Tallinn",
	"tz_key_Europe/Vienna": "(UTC+01:00) Châu Âu/Viên",
	"tz_key_Europe/Vilnius": "(UTC+02:00) Châu Âu/Vilnius",
	"tz_key_Europe/Warsaw": "(UTC+01:00) Châu Âu/Warsaw",
	"tz_key_Europe/Zagreb": "(UTC+01:00) Châu Âu/Zagreb",
	"tz_key_Europe/Zurich": "(UTC+01:00) Châu Âu/Zurich",
	"tz_key_Indian/Maldives": "(UTC+05:00) Ấn Độ Dương/Maldives",
	"tz_key_Indian/Mauritius": "(UTC+04:00) Ấn Độ Dương/Mauritius",
	"tz_key_Pacific/Apia": "(UTC-11:00) Thái Bình Dương/Apia",
	"tz_key_Pacific/Auckland": "(UTC+12:00) Thái Bình Dương/Auckland",
	"tz_key_Pacific/Easter": "(UTC-06:00) Thái Bình Dương/Easter",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) Thái Bình Dương/Galapagos",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) Thái Bình Dương/Honolulu",
	"tz_key_Pacific/Majuro": "(UTC-12:00) Thái Bình Dương/Majuro",
	"tz_key_Pacific/Midway": "(UTC-11:00) Thái Bình Dương/Midway",
	"tz_key_Pacific/Niue": "(UTC-11:00) Thái Bình Dương/Niue",
	"tz_key_Pacific/Pago Pago": "Thái Bình Dương/Pago Pago",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) Thái Bình Dương/Pago Pago"
};

var bc_account_num = "广告账号数：{num}";
var bc_avartar = "头像";
var bc_cancel = "取消";
var bc_create = "创建";
var bc_create_title = "新建商务中心";
var bc_info = "商务中心一站式地管理广告帐户、商务中心成员和其权限，而且免费供您使用。";
var bc_member_num = "成员数：{num}";
var bc_name = "商务中心名称";
var bc_name_empty_hint = "请填写商务中心名称";
var bc_name_length_hint = "商务中心名称不超过200个字符。";
var bc_name_placeholder = "请填写商务中心名称";
var bc_timezone = "时区";
var bc_timezone_empty_hint = "请选择时区";
var bc_timezone_hint = "用于后期数据统计，目前暂不支持相关功能。建议选择便于查看商务中心整体数据时所用的时区。";
var bc_timezone_placeholder = "选择时区";
var bc_type_agency = "代理商";
var zh = {
	bc_account_num: bc_account_num,
	bc_avartar: bc_avartar,
	bc_cancel: bc_cancel,
	bc_create: bc_create,
	bc_create_title: bc_create_title,
	bc_info: bc_info,
	bc_member_num: bc_member_num,
	bc_name: bc_name,
	bc_name_empty_hint: bc_name_empty_hint,
	bc_name_length_hint: bc_name_length_hint,
	bc_name_placeholder: bc_name_placeholder,
	bc_timezone: bc_timezone,
	bc_timezone_empty_hint: bc_timezone_empty_hint,
	bc_timezone_hint: bc_timezone_hint,
	bc_timezone_placeholder: bc_timezone_placeholder,
	bc_type_agency: bc_type_agency,
	"tz_key_Africa/Accra": "(UTC+00:00) 非洲/阿克拉",
	"tz_key_Africa/Cairo": "(UTC+02:00) 非洲/开罗",
	"tz_key_Africa/Casablanca": "(UTC+00:00) 非洲/卡萨布兰卡",
	"tz_key_Africa/Johannesburg": "(UTC+02:00) 非洲/约翰内斯堡",
	"tz_key_Africa/Lagos": "(UTC+01:00) 非洲/拉各斯",
	"tz_key_Africa/Nairobi": "(UTC+03:00) 非洲/内罗毕",
	"tz_key_Africa/Tunis": "(UTC+01:00) 非洲/突尼斯",
	"tz_key_America/Anchorage": "(UTC-09:00) 美洲/安克雷奇",
	"tz_key_America/Argentina/Buenos_Aires": "(UTC-03:00) 美洲/阿根廷/布宜诺斯艾利斯",
	"tz_key_America/Argentina/Salta": "(UTC-03:00) 美洲/阿根廷/萨尔塔",
	"tz_key_America/Argentina/San_Luis": "(UTC-03:00) 美洲/阿根廷/圣路易斯",
	"tz_key_America/Asuncion": "(UTC-04:00) 美洲/亚松森",
	"tz_key_America/Atikokan": "(UTC-05:00) 美国/阿提科坎",
	"tz_key_America/Belem": "(UTC-03:00) 美洲/贝伦",
	"tz_key_America/Blanc-Sablon": "(UTC-04:00) 美洲/白朗萨布隆",
	"tz_key_America/Bogota": "(UTC-05:00) 美洲/波哥大",
	"tz_key_America/Campo_Grande": "(UTC-04:00) 美洲/大坎普",
	"tz_key_America/Caracas": "(UTC-04:30) 美洲/加拉加斯",
	"tz_key_America/Chicago": "(UTC-06:00) 美洲/芝加哥",
	"tz_key_America/Costa_Rica": "(UTC-06:00) 美洲/哥斯达黎加",
	"tz_key_America/Dawson": "(UTC-08:00) 美洲/道森",
	"tz_key_America/Dawson_Creek": "(UTC-07:00) 美洲/道森河",
	"tz_key_America/Denver": "(UTC-07:00) 美洲/丹佛",
	"tz_key_America/Edmonton": "(UTC-07:00) 美洲/艾德蒙顿",
	"tz_key_America/El_Salvador": "(UTC-06:00) 美洲/萨尔瓦多",
	"tz_key_America/Guatemala": "(UTC-06:00) 美洲/危地马拉",
	"tz_key_America/Guayaquil": "(UTC-05:00) 美洲/瓜亚基尔",
	"tz_key_America/Halifax": "(UTC-04:00) 美洲/哈利法克斯",
	"tz_key_America/Hermosillo": "(UTC-07:00) 美洲/赫莫西洛",
	"tz_key_America/Iqaluit": "(UTC-05:00) 美洲/伊卡鲁伊特",
	"tz_key_America/Jamaica": "(UTC-05:00) 美洲/牙买加",
	"tz_key_America/La_Paz": "(UTC-04:00) 美洲/拉巴斯",
	"tz_key_America/Lima": "(UTC-05:00) 美洲/利马",
	"tz_key_America/Los_Angeles": "(UTC-08:00) 美洲/洛杉矶",
	"tz_key_America/Managua": "(UTC-06:00) 美洲/马那瓜",
	"tz_key_America/Mazatlan": "(UTC-07:00) 美洲/馬薩特蘭",
	"tz_key_America/Mexico_City": "(UTC-06:00) 美洲/墨西哥城",
	"tz_key_America/Montevideo": "(UTC-03:00) 美洲/蒙得维的亚",
	"tz_key_America/Nassau": "(UTC-05:00) 美洲/拿骚港",
	"tz_key_America/New_York": "(UTC-05:00) 美洲/纽约",
	"tz_key_America/Noronha": "(UTC-02:00) 美洲/诺隆尼亚",
	"tz_key_America/Panama": "(UTC-05:00) 美洲/巴拿马",
	"tz_key_America/Phoenix": "(UTC-07:00) 美洲/凤凰城",
	"tz_key_America/Port_of_Spain": "(UTC-04:00) 美洲/西班牙港口",
	"tz_key_America/Puerto_Rico": "(UTC-04:00) 美洲/波多黎各",
	"tz_key_America/Rainy_River": "(UTC-06:00) 美洲/雷尼里弗",
	"tz_key_America/Regina": "(UTC-06:00) 美洲/里贾纳",
	"tz_key_America/Santiago": "(UTC-04:00) 美洲/圣地亚哥",
	"tz_key_America/Santo_Domingo": "(UTC-04:00) 美洲/桑托多明哥",
	"tz_key_America/Sao_Paulo": "(UTC-03:00) 美洲/圣保罗",
	"tz_key_America/St_Johns": "(UTC-03:30) 美洲/圣约翰",
	"tz_key_America/Tegucigalpa": "(UTC-06:00) 美洲/特古西加尔巴",
	"tz_key_America/Tijuana": "(UTC-08:00) 美洲/蒂华纳",
	"tz_key_America/Toronto": "(UTC-05:00) 美国/多伦多",
	"tz_key_America/Vancouver": "(UTC-08:00) 美洲/温哥华",
	"tz_key_Asia/Amman": "(UTC+02:00) 亚洲/安曼",
	"tz_key_Asia/Baghdad": "(UTC+03:00) 亚洲/巴格达",
	"tz_key_Asia/Bahrain": "(UTC+03:00) 亚洲/巴林",
	"tz_key_Asia/Bangkok": "(UTC+07:00) 亚洲/曼谷",
	"tz_key_Asia/Beirut": "(UTC+02:00) 亚洲/贝鲁特",
	"tz_key_Asia/Colombo": "(UTC+05:30) 亚洲/科伦坡",
	"tz_key_Asia/Dhaka": "(UTC+06:00) 亚洲/达卡",
	"tz_key_Asia/Dubai": "(UTC+04:00) 亚洲/迪拜",
	"tz_key_Asia/Gaza": "(UTC+02:00) 亚洲/加沙",
	"tz_key_Asia/Ho_Chi_Minh": "(UTC+07:00) 亚洲/胡志明市",
	"tz_key_Asia/Hong_Kong": "(UTC+08:00) 亚洲/香港",
	"tz_key_Asia/Irkutsk": "(UTC+08:00) 亚洲/伊尔库茨克",
	"tz_key_Asia/Jakarta": "(UTC+07:00) 亚洲/雅加达",
	"tz_key_Asia/Jayapura": "(UTC+09:00) 亚洲/贾亚普拉",
	"tz_key_Asia/Jerusalem": "(UTC+02:00) 亚洲/耶路撒冷",
	"tz_key_Asia/Kamchatka": "(UTC+11:00) 亚洲/堪察加半岛",
	"tz_key_Asia/Karachi": "(UTC+05:00) 亚洲/卡拉奇",
	"tz_key_Asia/Kolkata": "(UTC+05:30) 亚洲/加尔各答",
	"tz_key_Asia/Krasnoyarsk": "(UTC+07:00) 亚洲/克拉斯诺亚尔斯克",
	"tz_key_Asia/Kuala_Lumpur": "(UTC+08:00) 亚洲/吉隆坡",
	"tz_key_Asia/Kuwait": "(UTC+03:00) 亚洲/科威特",
	"tz_key_Asia/Magadan": "(UTC+11:00) 亚洲/马加丹",
	"tz_key_Asia/Makassar": "(UTC+08:00) 亚洲/马卡萨尔",
	"tz_key_Asia/Manila": "(UTC+08:00) 亚洲/马尼拉",
	"tz_key_Asia/Muscat": "(UTC+04:00) 亚洲/麝香葡萄酒",
	"tz_key_Asia/Nicosia": "(UTC+02:00) 亚洲/尼科西亚",
	"tz_key_Asia/Omsk": "(UTC+06:00) 亚洲/鄂木斯克",
	"tz_key_Asia/Phnom_Penh": "亚洲/金边",
	"tz_key_Asia/Qatar": "(UTC+03:00) 亚洲/卡塔尔",
	"tz_key_Asia/Riyadh": "(UTC+03:00) 亚洲/利雅得",
	"tz_key_Asia/Seoul": "(UTC+09:00) 亚洲/首尔",
	"tz_key_Asia/Shanghai": "(UTC+08:00) 亚洲/北京",
	"tz_key_Asia/Singapore": "(UTC+08:00) 亚洲/新加坡",
	"tz_key_Asia/Taipei": "(UTC+08:00) 亚洲/台北",
	"tz_key_Asia/Tokyo": "(UTC+09:00) 亚洲/东京",
	"tz_key_Asia/Vladivostok": "(UTC+10:00) 亚洲/海参崴",
	"tz_key_Asia/Yakutsk": "(UTC+09:00) 亚洲/雅库茨克",
	"tz_key_Asia/Yekaterinburg": "(UTC+05:00) 亚洲/叶卡捷琳堡",
	"tz_key_Atlantic/Azores": "(UTC-01:00) 大西洋/亚速尔群岛",
	"tz_key_Atlantic/Canary": "(UTC+00:00) 大西洋/加那利群岛",
	"tz_key_Atlantic/Reykjavik": "(UTC+00:00) 大西洋/雷克雅未克",
	"tz_key_Australia/Broken_Hill": "(UTC+09:30) 澳洲/布罗肯希尔",
	"tz_key_Australia/Perth": "(UTC+08:00) 澳洲/珀斯",
	"tz_key_Australia/Sydney": "(UTC+10:00) 澳洲/悉尼",
	"tz_key_Etc/GMT": "(UTC+00:00)欧洲/伦敦",
	"tz_key_Etc/GMT+0": "(UTC+00:00)欧洲/伦敦",
	"tz_key_Etc/GMT+1": "(UTC-01:00)大西洋/亚速尔群岛",
	"tz_key_Etc/GMT+10": "(UTC-10:00)太平洋/火奴鲁鲁",
	"tz_key_Etc/GMT+11": "(UTC-11:00)太平洋/中途岛",
	"tz_key_Etc/GMT+12": "(UTC-12:00)太平洋/马朱罗",
	"tz_key_Etc/GMT+2": "(UTC-02:00)美洲/诺隆尼亚",
	"tz_key_Etc/GMT+3": "(UTC-03:00)美洲/圣保罗",
	"tz_key_Etc/GMT+4": "(UTC-04:00)美洲/圣地亚哥",
	"tz_key_Etc/GMT+5": "(UTC-05:00)美洲/纽约",
	"tz_key_Etc/GMT+6": "(UTC-06:00)美洲/墨西哥城",
	"tz_key_Etc/GMT+7": "(UTC-07:00)美洲/丹佛",
	"tz_key_Etc/GMT+8": "(UTC-08:00)美洲/洛杉矶",
	"tz_key_Etc/GMT+9": "(UTC-09:00)美洲/安克雷奇",
	"tz_key_Etc/GMT-1": "(UTC+01:00)欧洲/柏林",
	"tz_key_Etc/GMT-10": "(UTC+10:00)澳洲/悉尼",
	"tz_key_Etc/GMT-11": "(UTC+11:00)亚洲/马加丹",
	"tz_key_Etc/GMT-12": "(UTC+12:00)太平洋/奥克兰",
	"tz_key_Etc/GMT-13": "太平洋/阿皮亚",
	"tz_key_Etc/GMT-2": "(UTC+02:00)欧洲/雅典",
	"tz_key_Etc/GMT-3": "(UTC+03:00)欧洲/莫斯科",
	"tz_key_Etc/GMT-4": "(UTC+04:00)亚洲/迪拜",
	"tz_key_Etc/GMT-5": "(UTC+05:00)亚洲/卡拉奇",
	"tz_key_Etc/GMT-6": "(UTC+06:00)亚洲/达卡",
	"tz_key_Etc/GMT-7": "(UTC+07:00)亚洲/曼谷",
	"tz_key_Etc/GMT-8": "(UTC+08:00)亚洲/北京",
	"tz_key_Etc/GMT-9": "(UTC+09:00)亚洲/东京",
	"tz_key_Europe/Amsterdam": "(UTC+01:00) 欧洲/阿姆斯特丹",
	"tz_key_Europe/Athens": "(UTC+02:00) 欧洲/雅典",
	"tz_key_Europe/Belgrade": "(UTC+01:00) 欧洲/贝尔格莱德",
	"tz_key_Europe/Berlin": "(UTC+01:00) 欧洲/柏林",
	"tz_key_Europe/Bratislava": "(UTC+01:00) 欧洲/布拉迪斯拉发",
	"tz_key_Europe/Brussels": "(UTC+01:00) 欧洲/布鲁塞尔",
	"tz_key_Europe/Bucharest": "(UTC+02:00) 欧洲/布加勒斯特",
	"tz_key_Europe/Budapest": "(UTC+01:00) 欧洲/布达佩斯",
	"tz_key_Europe/Copenhagen": "(UTC+01:00) 欧洲/哥本哈根",
	"tz_key_Europe/Dublin": "(UTC+00:00) 欧洲/都柏林",
	"tz_key_Europe/Helsinki": "(UTC+02:00) 欧洲/赫尔辛基",
	"tz_key_Europe/Istanbul": "(UTC+02:00) 欧洲/伊斯坦布尔",
	"tz_key_Europe/Kaliningrad": "(UTC+02:00) 欧洲/加里宁格勒",
	"tz_key_Europe/Kiev": "(UTC+02:00) 欧洲/基辅",
	"tz_key_Europe/Lisbon": "(UTC+00:00) 欧洲/里斯本",
	"tz_key_Europe/Ljubljana": "(UTC+01:00) 欧洲/卢布尔雅那",
	"tz_key_Europe/London": "(UTC+00:00) 欧洲/伦敦",
	"tz_key_Europe/Luxembourg": "(UTC+01:00) 欧洲/卢森堡",
	"tz_key_Europe/Madrid": "(UTC+01:00) 欧洲/马德里",
	"tz_key_Europe/Malta": "(UTC+01:00) 欧洲/马耳他",
	"tz_key_Europe/Moscow": "(UTC+03:00) 欧洲/莫斯科",
	"tz_key_Europe/Oslo": "(UTC+01:00) 欧洲/奥斯陆",
	"tz_key_Europe/Paris": "(UTC+01:00) 欧洲/巴黎",
	"tz_key_Europe/Prague": "(UTC+01:00) 欧洲/布拉格",
	"tz_key_Europe/Riga": "(UTC+02:00) 欧洲/里加",
	"tz_key_Europe/Rome": "(UTC+01:00) 欧洲/罗马",
	"tz_key_Europe/Samara": "(UTC+03:00) 欧洲/萨马拉",
	"tz_key_Europe/Sarajevo": "(UTC+01:00) 欧洲/萨拉热窝",
	"tz_key_Europe/Skopje": "(UTC+01:00) 欧洲/斯科普里",
	"tz_key_Europe/Sofia": "(UTC+02:00) 欧洲/索非亚",
	"tz_key_Europe/Stockholm": "(UTC+01:00) 欧洲/斯德哥尔摩",
	"tz_key_Europe/Tallinn": "(UTC+02:00) 欧洲/塔林",
	"tz_key_Europe/Vienna": "(UTC+01:00) 欧洲/维也纳",
	"tz_key_Europe/Vilnius": "(UTC+02:00) 欧洲/维尔纽斯",
	"tz_key_Europe/Warsaw": "(UTC+01:00) 欧洲/华沙",
	"tz_key_Europe/Zagreb": "(UTC+01:00) 欧洲/萨格勒布",
	"tz_key_Europe/Zurich": "(UTC+01:00) 欧洲/苏黎世",
	"tz_key_Indian/Maldives": "(UTC+05:00) 印度洋/马尔代夫",
	"tz_key_Indian/Mauritius": "(UTC+04:00) 印度洋/毛里求斯",
	"tz_key_Pacific/Apia": "(UTC-11:00) 太平洋/阿皮亚",
	"tz_key_Pacific/Auckland": "(UTC+12:00) 太平洋/奥克兰",
	"tz_key_Pacific/Easter": "(UTC-06:00) 太平洋/复活节岛",
	"tz_key_Pacific/Galapagos": "(UTC-06:00) 太平洋/加拉帕戈斯群岛",
	"tz_key_Pacific/Honolulu": "(UTC-10:00) 太平洋/火奴鲁鲁",
	"tz_key_Pacific/Majuro": "(UTC-12:00) 太平洋/马朱罗",
	"tz_key_Pacific/Midway": "(UTC-11:00) 太平洋/中途岛",
	"tz_key_Pacific/Niue": "(UTC-11:00) 太平洋/纽埃",
	"tz_key_Pacific/Pago Pago": "(UTC-11:00) 太平洋/帕果帕果",
	"tz_key_Pacific/Pago_Pago": "(UTC-11:00) 太平洋/帕果帕果"
};

const protocal = window.location.protocol;
const IS_BOE = /boe/.test(window.location.hostname);
const BC_BOE_ORIGIN = `${protocal}//${window.location.host}`;
const BC_PROD_ORIGIN = `${protocal}//${window.location.host}`;
const BC_ORIGIN = IS_BOE ? BC_BOE_ORIGIN : BC_PROD_ORIGIN;
const BC_API_PREFIX_OLD = `${BC_ORIGIN}/api/v1/bm`;
const BC_API_PREFIX = `${BC_ORIGIN}/api/v2/bm`;
const paths = {
  timezoneList: {
    path: '/timezones/',
    old: true
  },
  createNormalBc: {
    path: '/organization/',
    old: true
  }
};
const apis = Object.keys(paths).reduce((res, key) => {
  const item = paths[key];
  res[key] = `${item.old ? BC_API_PREFIX_OLD : BC_API_PREFIX}${item.path}`;
  return res;
}, {}); // 语言包根据语言环境分类

const messages = {
  en,
  zh,
  ar,
  de,
  es,
  fr,
  id,
  it,
  ja,
  ko,
  ms,
  ru,
  th,
  tr,
  vi
};

let TzSelect = class TzSelect extends Vue {
  constructor() {
    super(...arguments);
    this.translateTzOption = translateTzOption;
    this.tzList = [];
  }

  get locales() {
    return messages[this.lang] || messages.en;
  }

  translate(key, _options = {}) {
    return this.locales[key];
  }

  getTimezoneList() {
    if (this.tzList && this.tzList.length) return;
    fetch(`${apis.timezoneList}${this.attrQuery}`, {
      credentials: 'include'
    }).then(res => {
      if (res.ok) {
        return res.json();
      } else {
        throw Error(`Request rejected with status ${res.status}`);
      }
    }).then(wrapper => {
      this.tzList = wrapper.data.timezone;
    }).catch(err => {
      console.log(err.message);
    });
  }

  update(value) {
    this.$emit('input', value);
  }

  mounted() {
    this.getTimezoneList();
  }

};

__decorate([Prop({
  type: String,
  default: ''
})], TzSelect.prototype, "attrQuery", void 0);

__decorate([Prop({
  type: String,
  default: 'en'
})], TzSelect.prototype, "lang", void 0);

__decorate([Prop({
  type: String,
  default: ''
})], TzSelect.prototype, "value", void 0);

__decorate([Prop({
  type: String,
  default: ''
})], TzSelect.prototype, "placeholder", void 0);

TzSelect = __decorate([Component], TzSelect);
var script$1 = TzSelect;

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__$1 = script$1;
/* template */

var __vue_render__$1 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('byted-select', {
    attrs: {
      "value": _vm.value,
      "placeholder": _vm.placeholder,
      "filter-placeholder": " ",
      "no-data-text": " ",
      "filterable": "",
      "filled": true
    },
    on: {
      "change": _vm.update,
      "visible-change": _vm.getTimezoneList
    }
  }, _vm._l(_vm.tzList, function (tzOption, key) {
    return _c('byted-option', {
      key: key,
      attrs: {
        "value": tzOption.timezone,
        "label": _vm.translateTzOption(tzOption, _vm.translate)
      }
    });
  }), 1);
};

var __vue_staticRenderFns__$1 = [];
/* style */

const __vue_inject_styles__$1 = undefined;
/* scoped */

const __vue_scope_id__$1 = undefined;
/* module identifier */

const __vue_module_identifier__$1 = undefined;
/* functional template */

const __vue_is_functional_template__$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1,
  staticRenderFns: __vue_staticRenderFns__$1
}, __vue_inject_styles__$1, __vue_script__$1, __vue_scope_id__$1, __vue_is_functional_template__$1, __vue_module_identifier__$1, false, undefined, undefined, undefined);

/*!
 * JavaScript Cookie v2.2.1
 * https://github.com/js-cookie/js-cookie
 *
 * Copyright 2006, 2015 Klaus Hartl & Fagner Brack
 * Released under the MIT license
 */

var js_cookie = createCommonjsModule(function (module, exports) {
(function (factory) {
	var registeredInModuleLoader;
	{
		module.exports = factory();
		registeredInModuleLoader = true;
	}
	if (!registeredInModuleLoader) {
		var OldCookies = window.Cookies;
		var api = window.Cookies = factory();
		api.noConflict = function () {
			window.Cookies = OldCookies;
			return api;
		};
	}
}(function () {
	function extend () {
		var i = 0;
		var result = {};
		for (; i < arguments.length; i++) {
			var attributes = arguments[ i ];
			for (var key in attributes) {
				result[key] = attributes[key];
			}
		}
		return result;
	}

	function decode (s) {
		return s.replace(/(%[0-9A-Z]{2})+/g, decodeURIComponent);
	}

	function init (converter) {
		function api() {}

		function set (key, value, attributes) {
			if (typeof document === 'undefined') {
				return;
			}

			attributes = extend({
				path: '/'
			}, api.defaults, attributes);

			if (typeof attributes.expires === 'number') {
				attributes.expires = new Date(new Date() * 1 + attributes.expires * 864e+5);
			}

			// We're using "expires" because "max-age" is not supported by IE
			attributes.expires = attributes.expires ? attributes.expires.toUTCString() : '';

			try {
				var result = JSON.stringify(value);
				if (/^[\{\[]/.test(result)) {
					value = result;
				}
			} catch (e) {}

			value = converter.write ?
				converter.write(value, key) :
				encodeURIComponent(String(value))
					.replace(/%(23|24|26|2B|3A|3C|3E|3D|2F|3F|40|5B|5D|5E|60|7B|7D|7C)/g, decodeURIComponent);

			key = encodeURIComponent(String(key))
				.replace(/%(23|24|26|2B|5E|60|7C)/g, decodeURIComponent)
				.replace(/[\(\)]/g, escape);

			var stringifiedAttributes = '';
			for (var attributeName in attributes) {
				if (!attributes[attributeName]) {
					continue;
				}
				stringifiedAttributes += '; ' + attributeName;
				if (attributes[attributeName] === true) {
					continue;
				}

				// Considers RFC 6265 section 5.2:
				// ...
				// 3.  If the remaining unparsed-attributes contains a %x3B (";")
				//     character:
				// Consume the characters of the unparsed-attributes up to,
				// not including, the first %x3B (";") character.
				// ...
				stringifiedAttributes += '=' + attributes[attributeName].split(';')[0];
			}

			return (document.cookie = key + '=' + value + stringifiedAttributes);
		}

		function get (key, json) {
			if (typeof document === 'undefined') {
				return;
			}

			var jar = {};
			// To prevent the for loop in the first place assign an empty array
			// in case there are no cookies at all.
			var cookies = document.cookie ? document.cookie.split('; ') : [];
			var i = 0;

			for (; i < cookies.length; i++) {
				var parts = cookies[i].split('=');
				var cookie = parts.slice(1).join('=');

				if (!json && cookie.charAt(0) === '"') {
					cookie = cookie.slice(1, -1);
				}

				try {
					var name = decode(parts[0]);
					cookie = (converter.read || converter)(cookie, name) ||
						decode(cookie);

					if (json) {
						try {
							cookie = JSON.parse(cookie);
						} catch (e) {}
					}

					jar[name] = cookie;

					if (key === name) {
						break;
					}
				} catch (e) {}
			}

			return key ? jar[key] : jar;
		}

		api.set = set;
		api.get = function (key) {
			return get(key, false /* read as raw */);
		};
		api.getJSON = function (key) {
			return get(key, true /* read as json */);
		};
		api.remove = function (key, attributes) {
			set(key, '', extend(attributes, {
				expires: -1
			}));
		};

		api.defaults = {};

		api.withConverter = init;

		return api;
	}

	return init(function () {});
}));
});

let SelectOrg = class SelectOrg extends Vue {
  constructor() {
    super(...arguments);
    this.loading = false;
    this.formData = {
      name: '',
      timezone: ''
    };
  }

  get locale() {
    return messages[this.lang] || messages.en;
  }

  translate(key, _options = {}) {
    return this.locale[key] || key;
  }

  get rules() {
    const {
      translate
    } = this;
    return {
      name: [{
        required: true,
        message: translate('bc_name_empty_hint'),
        trigger: 'blur'
      }, {
        validator: this.nameLengthValidator,
        trigger: 'blur'
      }],
      timezone: [{
        required: true,
        message: translate('bc_timezone_empty_hint'),
        trigger: 'change'
      }]
    };
  }

  nameLengthValidator(_rule, value = '', callback) {
    const MAX_LEN = 200;

    if (stringLength(value) > MAX_LEN) {
      callback(new Error(this.translate('bc_name_length_hint', {
        maxLength: MAX_LEN
      })));
    }

    callback(void 0);
  }

  get attrQuery() {
    return jsonToQuery({
      attr_source: this.sourceId,
      source_biz_id: this.sourceBizId,
      attr_type: 'SDK'
    });
  }

  createBc(data = {}) {
    this.loading = true;
    return fetch(`${apis.createNormalBc}${this.attrQuery}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'X-CSRFToken': js_cookie.get('csrftoken') || ''
      },
      body: JSON.stringify(data)
    }).finally(() => {
      this.loading = false;
    });
  }

  onCreateClick() {
    this.$refs.createForm.validate(valid => {
      if (!valid) return;
      this.createBc(this.formData).then(res => {
        if (res.ok) {
          return res.json();
        } else {
          throw Error(`Request rejected with status ${res.status}`);
        }
      }).then(res => {
        if (res.code === 0) {
          this.$emit('created', res.data);
        } else {
          this.$emit('error', res.msg);
        }
      }).catch(err => {
        this.$emit('error', err.message);
      });
    });
  }

  onCancelClick() {
    this.$emit('cancel');
  }

  mounted() {}

};

__decorate([Prop({
  type: String,
  default: 'none'
})], SelectOrg.prototype, "sourceId", void 0);

__decorate([Prop({
  type: String,
  default: 'none'
})], SelectOrg.prototype, "sourceBizId", void 0);

__decorate([Prop({
  type: String,
  default: js_cookie.get('lang_type') || 'en'
})], SelectOrg.prototype, "lang", void 0);

__decorate([Prop({
  type: Boolean,
  default: true
})], SelectOrg.prototype, "hasCancel", void 0);

__decorate([Prop({
  type: String,
  default: '600px'
})], SelectOrg.prototype, "width", void 0);

__decorate([Prop({
  type: String,
  default: 'vertical'
})], SelectOrg.prototype, "layoutType", void 0);

__decorate([Prop({
  type: String,
  default: '200px'
})], SelectOrg.prototype, "labelWidth", void 0);

SelectOrg = __decorate([Component({
  components: {
    TzSelect: __vue_component__$1
  }
})], SelectOrg);
var script = SelectOrg;

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__ = script;
/* template */

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "create-org-container",
    style: {
      width: _vm.width
    },
    attrs: {
      "id": "create-normal-bc"
    }
  }, [_c('byted-form', {
    ref: "createForm",
    staticClass: "create-normal-bc-form",
    attrs: {
      "model": _vm.formData,
      "layoutType": _vm.layoutType,
      "labelWidth": _vm.layoutType === 'horizontal' ? _vm.labelWidth : ''
    }
  }, [_c('byted-form-item', {
    attrs: {
      "required": "",
      "label": _vm.translate('bc_name'),
      "prop": "name",
      "rules": _vm.rules.name
    }
  }, [_c('byted-input', {
    attrs: {
      "filled": true,
      "placeholder": _vm.translate('bc_name_placeholder')
    },
    model: {
      value: _vm.formData.name,
      callback: function ($$v) {
        _vm.$set(_vm.formData, "name", typeof $$v === 'string' ? $$v.trim() : $$v);
      },
      expression: "formData.name"
    }
  })], 1), _vm._v(" "), _c('byted-form-item', {
    attrs: {
      "required": "",
      "prop": "timezone",
      "rules": _vm.rules.timezone
    }
  }, [_c('label', {
    attrs: {
      "slot": "label"
    },
    slot: "label"
  }, [_c('span', [_vm._v(_vm._s(_vm.translate('bc_timezone')))]), _vm._v(" "), _c('byted-tooltip', {
    attrs: {
      "popper-class": "bc-hint-tooltip",
      "max-width": "300",
      "placement": "top",
      "trigger": "hover"
    }
  }, [_c('span', [_vm._v(_vm._s(_vm.translate('bc_timezone_hint')))]), _vm._v(" "), _c('byted-icon', {
    staticClass: "hover-tip",
    attrs: {
      "slot": "reference",
      "name": "question-circle"
    },
    slot: "reference"
  })], 1)], 1), _vm._v(" "), _c('tz-select', {
    staticClass: "tz-select",
    attrs: {
      "attr-query": _vm.attrQuery,
      "lang": _vm.lang,
      "placeholder": _vm.translate('bc_timezone_placeholder')
    },
    model: {
      value: _vm.formData.timezone,
      callback: function ($$v) {
        _vm.$set(_vm.formData, "timezone", $$v);
      },
      expression: "formData.timezone"
    }
  })], 1), _vm._v(" "), _c('byted-form-item', {
    staticClass: "op-btns"
  }, [_vm.hasCancel ? _c('byted-button', {
    staticClass: "op-btn",
    attrs: {
      "disabled": _vm.loading
    },
    on: {
      "click": _vm.onCancelClick
    }
  }, [_vm._v("\n        " + _vm._s(_vm.translate('bc_cancel')) + "\n      ")]) : _vm._e(), _vm._v(" "), _c('byted-button', {
    staticClass: "op-btn",
    attrs: {
      "loading": _vm.loading,
      "type": "primary"
    },
    on: {
      "click": _vm.onCreateClick
    }
  }, [_vm._v("\n        " + _vm._s(_vm.translate('bc_create')) + "\n      ")])], 1)], 1)], 1);
};

var __vue_staticRenderFns__ = [];
/* style */

const __vue_inject_styles__ = function (inject) {
  if (!inject) return;
  inject("data-v-003328aa_0", {
    source: ".create-normal-bc-form,.create-org-container .op-btns{width:100%}.create-normal-bc-form .tz-select{display:block;text-align:left}.create-org-container .op-btns{text-align:right}.create-org-container .op-btn{margin-left:10px}.create-normal-bc-form .form-item-hint{color:#999;font-size:12px;line-height:20px;margin-top:4px}.create-normal-bc-form .hover-tip{margin-left:4px;vertical-align:middle;cursor:pointer;fill:#999}#create-normal-bc .bui-btn{font-weight:400}#create-normal-bc .bui-btn-default:focus,#create-normal-bc .bui-btn-default:hover{border-color:#2a4d8f;color:#2a4d8f;background-color:#fff}#create-normal-bc .bui-btn.bui-btn-primary,#create-normal-bc .bui-btn.bui-btn-primary:focus,#create-normal-bc .bui-btn.bui-btn-primary:hover{background:#2a4d8f;border-color:transparent}#create-normal-bc .bui-btn.bui-btn-primary:hover{opacity:.9}#create-normal-bc .bui-input:focus,#create-normal-bc .bui-select-wrapper.active,#create-normal-bc input:hover{border-color:#2a4d8f}#create-normal-bc .bui-input:focus,#create-normal-bc .bui-select-wrapper.active,#create-normal-bc input:hover{border-color:#2a4d8f}#create-normal-bc .byted-option-selected{color:#2a4d8f}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__ = undefined;
/* module identifier */

const __vue_module_identifier__ = undefined;
/* functional template */

const __vue_is_functional_template__ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, createInjector, undefined, undefined);

Vue.use(_Button);
Vue.use(_Select);
Vue.use(_Form);
Vue.use(_FormItem);
Vue.use(_Input);
Vue.use(_Tooltip);
Vue.use(_Icon);

var components = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CreateNormalBc: __vue_component__
});

const install = function installBcSdk(_Vue) {
  Object.entries(components).forEach(([componentName, component]) => {
    _Vue.component(componentName, component);
  });
}; // Create module definition for Vue.use()
let createNormalBc = function (container, options) {
  if (!container) {
    console.error('@byted/bc-sdk: the first argument(container) is required and it should be CSS selector string or actual HTMLElement.');
    return;
  }

  const {
    on = {},
    ...props
  } = options || {};
  new Vue({
    render: h => h(__vue_component__, {
      props: { ...(props || {})
      },
      on: { ...on
      }
    })
  }).$mount(container);
};

export default install;
export { __vue_component__ as CreateNormalBc, createNormalBc };
